import React, { useState, useEffect, useCallback, useRef, useMemo, memo, Fragment } from "react";
import { initializeApp } from "firebase/app";
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword,
         signOut, onAuthStateChanged, sendPasswordResetEmail } from "firebase/auth";
import { getFirestore, doc, getDoc, setDoc, updateDoc, collection,
         getDocs, deleteDoc, serverTimestamp, query, orderBy, addDoc } from "firebase/firestore";
import { getMessaging, getToken, onMessage } from "firebase/messaging";

// ── GLOBAL THEME — Bloomberg dark ─────────────────────────────────────────────
// Inject global CSS vars once
if(typeof document!=="undefined" && !document.getElementById("rabt-theme")){
  const s=document.createElement("style");
  s.id="rabt-theme";
  s.textContent=`
    :root {
      --d: 1;
      --row-h: calc(28px * var(--d));
      --bg:      #07101c;
      --bg2:     #0b1624;
      --card:    #0f1d30;
      --card2:   #132539;
      --panel:   #0c1826;
      --border:  #1a2c44;
      --border2: #23405f;
      --rule:    #0e1a2a;
      --text:    #e7edf5;
      --text2:   #8ea2bf;
      --muted:   #5a728f;
      --accent:  #e8b339;
      --accent2: #49c2e4;
      --buy:     #2ed47a;
      --sell:    #ff5b6c;
      --hold:    #7c8ea6;
      --buyBg:   rgba(46,212,122,0.08);
      --sellBg:  rgba(255,91,108,0.08);
      --buyBorder: rgba(46,212,122,0.35);
      --sellBorder:rgba(255,91,108,0.35);
      --amber:   #e8b339;
      --cyan:    #49c2e4;
      --violet:  #8c7cf0;
    }
    body { background:#07101c!important; color:#e7edf5!important; font-family:"IBM Plex Sans",-apple-system,BlinkMacSystemFont,sans-serif!important; }
    * { box-sizing:border-box; }
    ::-webkit-scrollbar { width:6px; height:6px; }
    ::-webkit-scrollbar-track { background:transparent; }
    ::-webkit-scrollbar-thumb { background:#1a2c44; border-radius:0; }
    ::-webkit-scrollbar-thumb:hover { background:#23405f; }
    .rabt-card { background:#0f1d30; border:1px solid #1a2c44; border-radius:0; }
    .rabt-buy  { color:#2ed47a!important; }
    .rabt-sell { color:#ff5b6c!important; }
    .sig-buy   { background:rgba(46,212,122,0.08); color:#2ed47a; border:1px solid rgba(46,212,122,0.35); border-radius:0; padding:1px 5px; font-size:10px; font-weight:700; font-family:"JetBrains Mono",monospace; }
    .sig-sell  { background:rgba(255,91,108,0.08); color:#ff5b6c; border:1px solid rgba(255,91,108,0.35); border-radius:0; padding:1px 5px; font-size:10px; font-weight:700; font-family:"JetBrains Mono",monospace; }
  `;
  document.head.appendChild(s);
}

// Build marker — verify the deployed build in DevTools console: window.__RAB_BUILD__
// (side-effecting so it survives minification and changes the bundle content-hash).
window.__RAB_BUILD__ = "dynamic-tp-strong-2026-10-21a";
try { console.log("[RAB] build", window.__RAB_BUILD__); } catch (e) {}

// ── Convenience constants pointing to CSS vars ────────────────────────────────
const N = {
  bg:"#07101c", bg2:"#0b1624", card:"#0f1d30", card2:"#132539",
  panel:"#0c1826", border:"#1a2c44", border2:"#23405f", rule:"#0e1a2a",
  text:"#e7edf5", text2:"#8ea2bf", muted:"#5a728f",
  accent:"#e8b339", accent2:"#49c2e4", amber:"#e8b339",
  buy:"#2ed47a", sell:"#ff5b6c", hold:"#7c8ea6",
  buyBg:"rgba(46,212,122,0.08)", sellBg:"rgba(255,91,108,0.08)",
  buyBorder:"rgba(46,212,122,0.35)", sellBorder:"rgba(255,91,108,0.35)",
  font:"'IBM Plex Sans',-apple-system,sans-serif",
  mono:"'JetBrains Mono',ui-monospace,monospace",
  serif:"'Instrument Serif',Georgia,serif",
};


// ── Firebase Configuration ────────────────────────────────────────────────────
// REPLACE with your Firebase project config from Firebase Console
const firebaseConfig = {
  apiKey:            "AIzaSyBwwwl4FMgoPIr5kjhXUo4C11-d2-yGRS8",
  authDomain:        "rab-trade.firebaseapp.com",
  projectId:         "rab-trade",
  storageBucket:     "rab-trade.firebasestorage.app",
  messagingSenderId: "840000180766",
  appId:             "1:840000180766:web:c31e20c12983f80fdf540f",
};
const _fbApp = initializeApp(firebaseConfig);
const _auth  = getAuth(_fbApp);
const _db    = getFirestore(_fbApp, "rab-trade");

// ── Firebase Cloud Messaging (push notifications) ─────────────────────────────
// VAPID key from Firebase Console → Project Settings → Cloud Messaging → Web Push
// "Generate key pair" under Web Push certificates section
const FCM_VAPID_KEY = "BGdNEbivnN-EK6NOSXR0OWyeGUMtEgZEZXwvMT5npT1A8iA3WOpQEFwVlhFySyuanAbj452gcq9mbgS0c4uZR8Y"; // ← replace with your VAPID public key
let _messaging = null;
try { _messaging = getMessaging(_fbApp); } catch(e) { /* not supported in all envs */ }

async function _registerPush(uid) {
  // Skip if FCM not available, VAPID key is placeholder, or browser doesn't support
  if (!_messaging) return;
  if (!FCM_VAPID_KEY || FCM_VAPID_KEY.includes("YOUR_VAPID")) return;
  if (!("Notification" in window) || !("serviceWorker" in navigator)) return;
  // Only register if permission already granted — don't auto-prompt on login
  // The Settings tab has an explicit "Enable Push" button for first-time setup
  if (Notification.permission !== "granted") return;
  try {
    const swReg = window._swRegistration ||
      await navigator.serviceWorker.getRegistration('/') ||
      await navigator.serviceWorker.ready;
    if (!swReg) return;
    const token = await getToken(_messaging, {
      vapidKey: FCM_VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });
    if (!token) return;
    const prev = localStorage.getItem("fcm_token");
    if (prev === token) return;   // already registered this token
    const res = await fetch("/push/register", {
      method: "POST",
      headers: await _authHeadersFor(uid, { "Content-Type": "application/json" }),
      body: JSON.stringify({ token }),
    });
    if (res.ok) {
      localStorage.setItem("fcm_token", token);
      console.log("[FCM] Push token registered");
    }
  } catch(e) {
    // Non-fatal — push works on next reload once VAPID key is corrected
    console.warn("[FCM] Push registration skipped:", e.message);
  }
}

// ── Firebase helpers ──────────────────────────────────────────────────────────
async function fbGetUser(uid) {
  const snap = await getDoc(doc(_db, "users", uid));
  return snap.exists() ? snap.data() : null;
}
async function fbGetAllUsers() {
  // No orderBy — avoids requiring a Firestore composite index
  // Sort by createdAt in JavaScript instead
  const snap = await getDocs(collection(_db, "users"));
  const users = snap.docs.map(d=>({id:d.id,...d.data()}));
  // Sort newest first — handle both Timestamp and string formats
  return users.sort((a,b)=>{
    const ta = a.createdAt?.toDate?.() || new Date(a.createdAt||0);
    const tb = b.createdAt?.toDate?.() || new Date(b.createdAt||0);
    return tb - ta;
  });
}
async function fbCreateUser(email, password, displayName, expiresAt, role="user") {
  // SECURITY (23 Oct 2026): created on the SERVER with the Admin SDK. The old
  // browser flow signed the admin out (it logged in as the new user) and relied on
  // users being allowed to write their own role / expiry, which let anyone make
  // themselves admin or extend their own subscription.
  const tok = _auth.currentUser ? await _auth.currentUser.getIdToken() : "";
  const r = await fetch(`${API_BASE}/admin/users/create`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Authorization": "Bearer " + tok },
    body: JSON.stringify({ email, password, displayName, expiresAt: expiresAt || null, role }),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || !j.uid) throw new Error(j.detail || "Could not create user");
  return j.uid;
}
async function fbUpdateUser(uid, updates) {
  await updateDoc(doc(_db,"users",uid), updates);
}

// ── Device fingerprint — stable ID per browser/device ────────────────────────
// Combines multiple signals to create a fingerprint that persists across
// email changes. Stored in localStorage so it survives tab closes.
function getDeviceId() {
  const key = 'nse_pro_device_id';
  let id = localStorage.getItem(key);
  if(id) return id;
  // Generate once and persist
  const nav = window.navigator;
  const raw = [
    nav.userAgent,
    nav.language,
    nav.platform || nav.oscpu || '',
    (window.screen?.width||0) + 'x' + (window.screen?.height||0),
    window.screen?.colorDepth||0,
    new Date().getTimezoneOffset(),
    nav.hardwareConcurrency || '',
    nav.deviceMemory || '',
  ].join('|');
  // Simple hash
  let h = 0;
  for(let i = 0; i < raw.length; i++) {
    h = (Math.imul(31, h) + raw.charCodeAt(i)) | 0;
  }
  id = 'dev_' + Math.abs(h).toString(36) + '_' + Date.now().toString(36);
  localStorage.setItem(key, id);
  return id;
}

// Check if this device already used demo (Firestore lookup)
async function checkDeviceDemoUsed(deviceId) {
  try {
    const snap = await getDoc(doc(_db, "device_demos", deviceId));
    return snap.exists();
  } catch { return false; }
}

// Mark this device as demo used
async function markDeviceDemoUsed(deviceId, uid, email) {
  try {
    await setDoc(doc(_db, "device_demos", deviceId), {
      uid, email,
      usedAt: serverTimestamp(),
    });
  } catch(e) { console.warn('Device demo mark failed:', e); }
}

// ── Razorpay helper ──────────────────────────────────────────────────────────
function loadRazorpay() {
  return new Promise(resolve=>{
    if(window.Razorpay){ resolve(true); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload  = ()=>resolve(true);
    s.onerror = ()=>resolve(false);
    document.body.appendChild(s);
  });
}

// ── Subscription helpers ──────────────────────────────────────────────────────
function isExpired(rec) {
  if(!rec?.expiresAt) return true;
  return new Date(rec.expiresAt) < new Date();
}
function isDemo(rec) {
  return rec?.plan === 'demo' || rec?.plan === 'Demo';
}

// ── Scan Results Firestore sync ──────────────────────────────────────────────
async function fbSaveScanResults(uid, results, activeList, timeframe) {
  const ref = doc(_db, "users", uid, "sync", "scanResults");
  await setDoc(ref, {
    results: results.slice(0,50), // cap at 50 to stay within Firestore limits
    activeList, timeframe,
    updatedAt: serverTimestamp(),
    deviceId: navigator.userAgent.slice(0,50),
  });
}
async function fbGetScanResults(uid) {
  try {
    const snap = await getDoc(doc(_db, "users", uid, "sync", "scanResults"));
    return snap.exists() ? snap.data() : null;
  } catch { return null; }
}

// ── Virtual (paper) trades Firestore sync ─────────────────────────────────────
// localStorage alone is unreliable on mobile webviews/PWAs (evicted between
// sessions), which made open paper trades vanish overnight. Mirror them to
// Firestore so they persist across days and devices.
async function fbSaveVirtualTrades(uid, trades) {
  if(!uid) return;
  try {
    await setDoc(doc(_db, "users", uid, "sync", "virtualTrades"), {
      trades: (trades||[]).slice(0, 300),   // cap for Firestore doc-size safety
      updatedAt: serverTimestamp(),
      deviceId: navigator.userAgent.slice(0,50),
    });
  } catch(e) { /* non-fatal — localStorage still holds a copy */ }
}
async function fbGetVirtualTrades(uid) {
  if(!uid) return null;
  try {
    const snap = await getDoc(doc(_db, "users", uid, "sync", "virtualTrades"));
    return snap.exists() ? (snap.data().trades || []) : null;
  } catch { return null; }
}

// ── Trade History Firestore helpers ──────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
// FIREBASE TRADE JOURNAL — persistent cross-device trade log
// Captures: entry (any source) + exit (TP/SL/manual) + P&L
// Collection: users/{uid}/journal/{tradeId}
//
// ⚠ FIRESTORE RULES — add this to your Firebase Console → Firestore → Rules:
//   match /users/{uid}/journal/{doc} {
//     allow read, write: if request.auth.uid == uid;
//   }
// ══════════════════════════════════════════════════════════════════════════════
const JOURNAL_COL = (uid) => collection(_db, "users", uid, "journal");
const JOURNAL_DOC = (uid, id) => doc(_db, "users", uid, "journal", id);

// Save or update a single journal entry
async function fbJournalSave(uid, entry) {
  if(!uid || !entry?.id) return;
  try {
    await setDoc(JOURNAL_DOC(uid, entry.id), {
      ...entry,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch(e) { console.warn("fbJournalSave failed:", e.message); }
}

// Load all journal entries (all time, newest first)
async function fbJournalLoad(uid, limitN=500) {
  try {
    const q = query(JOURNAL_COL(uid), orderBy("entryTime","desc"));
    const snap = await getDocs(q);
    return snap.docs.map(d=>({...d.data(), id:d.id}));
  } catch { return []; }
}

// Mark an open entry as closed (TP/SL/manual)
async function fbJournalClose(uid, tradeId, exitData) {
  if(!uid || !tradeId) return;
  try {
    await setDoc(JOURNAL_DOC(uid, tradeId), {
      ...exitData,
      status:   "CLOSED",
      exitTime: exitData.exitTime || new Date().toISOString(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch(e) { console.warn("fbJournalClose failed:", e.message); }
}

// Build a journal entry from an order placement event
function buildJournalEntry({ orderId, symbol, exchange, side, qty, price,
  product, source, tpPrice, slPrice, tpPct, slPct, tabMode, confidence,
  tag, instrumentType, notes, lotSize, strike, expiry,
  signals="",        // human-readable string  e.g. "TSI Bullish Cross · RSI Oversold"
  signalDetails=null, // structured array [{scan,type,label}, ...] — for analytics
  taSnapshot=null,    // full TA indicator snapshot {rsi, tsi, macd, ema9, ema50, stoch_k, stoch_d, bb_*, vol*}
  tradeReason=""      // manual reason/notes for external trades (admin)
}) {
  const id = `j_${orderId||Date.now()}_${symbol||""}`.replace(/[^a-zA-Z0-9_]/g,"_");

  // lotSize is critical for MCX P&L calculation
  // NSE F&O: Kite sends total qty (qty=50 for 1 lot of NIFTY), lotSize=1
  // MCX: Kite sends lots (qty=1 for 1 lot CRUDEOIL), lotSize=100
  // Delta: qty=contracts, lotSize=1
  const exch2 = (exchange||"").toUpperCase();
  let ls = parseFloat(lotSize||0);
  if(exch2 === "MCX" && (!ls || ls <= 1)) {
    const baseMatch = (symbol||"").toUpperCase().match(/^([A-Z]+)/);
    const baseSym   = baseMatch ? baseMatch[1] : symbol||"";
    ls = MCX_LOT_SIZE[baseSym] || MCX_LOT_SIZE[baseSym.replace(/M$/,"")] || 1;
  } else if(!ls || ls < 1) {
    ls = 1;
  }

  return {
    id,
    orderId:    orderId||null,
    symbol:     symbol||"",
    exchange:   exchange||"NSE",
    side:       side||"BUY",
    qty:        parseInt(qty||1),
    lotSize:    ls,              // MCX: actual lot size; NSE F&O: 1; Equity: 1
    entryPrice: parseFloat(price||0),
    product:    product||"MIS",
    source:     source||"manual",
    tabMode:    tabMode||"equity",
    tpPrice:    tpPrice ? parseFloat(tpPrice) : null,
    slPrice:    slPrice ? parseFloat(slPrice) : null,
    tpPct:      tpPct||null,
    slPct:      slPct||null,
    confidence: confidence||null,
    tag:        tag||"",
    instrumentType: instrumentType||"EQ",
    strike:     strike ? parseFloat(strike) : null,
    expiry:     expiry||null,
    // signals: human-readable string for display
    signals:       signals || (signalDetails?.length ? signalDetails.map(s=>s.label).join(" · ") : "no signal"),
    // signalDetails: structured array for analytics — which scans fired, direction, etc.
    signalDetails: signalDetails || (signals ? [{label: signals, scan: null, type: side}] : [{label:"no signal",scan:null,type:null}]),
    // taSnapshot: full indicator values at time of trade (RSI, TSI, MACD, EMA, BB, Stoch, Volume)
    taSnapshot:    taSnapshot||null,
    // tradeReason: manual reason/signal for external trades or self-journaling notes
    tradeReason:   tradeReason||"",
    notes:      notes||"",
    status:     "OPEN",
    entryTime:  new Date().toISOString(),
    exitTime:   null,
    exitPrice:  null,
    exitReason: null,
    pnl:        null,
    roi:        null,
  };
}

// Calculate P&L when closing — correctly handles lot size for F&O and MCX
function calcJournalPnL(entry, exitPrice) {
  const ep = parseFloat(exitPrice||0);
  const en = parseFloat(entry.entryPrice||0);
  const q  = parseInt(entry.qty||1);

  // Determine lot size multiplier:
  // 1. Use explicitly stored lotSize (new entries have this)
  // 2. For MCX: look up from MCX_LOT_SIZE table using base symbol
  // 3. For NSE F&O: qty is already total units (Kite sends total qty), lotSize=1
  // 4. Equity: lotSize=1
  let ls = parseFloat(entry.lotSize||0);

  if(!ls || ls <= 1) {
    const exch  = (entry.exchange||"").toUpperCase();
    const itype = (entry.instrumentType||"").toUpperCase();

    if(exch === "MCX") {
      // Extract base symbol from futures symbol e.g. "CRUDEOILM25APRFUT" → "CRUDEOIL"
      const baseMatch = (entry.symbol||"").toUpperCase().match(/^([A-Z]+)/);
      const baseSym   = baseMatch ? baseMatch[1] : entry.symbol||"";
      // Look up actual lot size
      const mcxLot = MCX_LOT_SIZE[baseSym] || MCX_LOT_SIZE[baseSym.replace(/M$/,"")] || 1;
      ls = mcxLot;
    } else {
      // NSE F&O: Kite returns total qty (already includes lot size)
      // Delta/PERP: qty = contracts, each worth 1 unit
      ls = 1;
    }
  }

  if(!ep || !en) return { pnl: null, roi: null };
  const pnl     = entry.side==="BUY" ? (ep-en)*q*ls : (en-ep)*q*ls;
  const capital = en * q * ls;
  const roi     = capital > 0 ? (pnl/capital*100) : 0;
  return { pnl: parseFloat(pnl.toFixed(2)), roi: parseFloat(roi.toFixed(2)) };
}

async function fbSaveTrade(uid, trade) {
  const ref = doc(_db, "users", uid, "trades", trade.id || `trade_${Date.now()}`);
  await setDoc(ref, {...trade, savedAt: serverTimestamp()});
}
async function fbGetTrades(uid) {
  try {
    const q = query(collection(_db, "users", uid, "trades"), orderBy("savedAt","desc"));
    const snap = await getDocs(q);
    return snap.docs.map(d=>({...d.data(), id:d.id}));
  } catch { return []; }
}

// ── Watcher Firestore sync ────────────────────────────────────────────────────
// ── User Settings Firestore sync ──────────────────────────────────────────────
// Saves all AT configs, signal params, Telegram etc to Firestore per user
async function fbSaveSettings(uid, settings) {
  try {
    const ref = doc(_db, "users", uid, "sync", "settings");
    await setDoc(ref, { ...settings, updatedAt: serverTimestamp() });
  } catch(e) { console.warn("fbSaveSettings failed:", e.message); }
}
async function fbGetSettings(uid) {
  try {
    const snap = await getDoc(doc(_db, "users", uid, "sync", "settings"));
    return snap.exists() ? snap.data() : null;
  } catch { return null; }
}

async function fbSaveWatchers(uid, watchers) {
  const ref = doc(_db, "users", uid, "sync", "watchers");
  // Save WATCHING + recently fired (last 24h) so positions tab shows history
  const cutoff = Date.now() - 86400000;
  const toSave = watchers.filter(w=>
    w.status==="WATCHING" ||
    (w.triggeredAt && new Date(w.triggeredAt).getTime() > cutoff)
  ).slice(0, 100); // cap at 100
  await setDoc(ref, {
    watchers:  toSave,
    updatedAt: serverTimestamp(),
    savedAt:   Date.now(),
  }, { merge: false });
}
async function fbGetWatchers(uid) {
  try {
    const snap = await getDoc(doc(_db, "users", uid, "sync", "watchers"));
    return snap.exists() ? (snap.data().watchers || []) : [];
  } catch { return []; }
}

// ── Trade Firestore bulk save/load ────────────────────────────────────────────
async function fbSaveAllTrades(uid, trades) {
  // Save all trades in one Firestore document for fast cross-device load
  const ref = doc(_db, "users", uid, "sync", "allTrades");
  await setDoc(ref, {
    trades: trades.slice(0, 500),  // cap at 500
    updatedAt: serverTimestamp(),
  });
}
async function fbGetAllTrades(uid) {
  try {
    const snap = await getDoc(doc(_db, "users", uid, "sync", "allTrades"));
    if(snap.exists() && snap.data().trades?.length > 0) return snap.data().trades;
    // Fallback: load from individual trade docs
    return await fbGetTrades(uid);
  } catch { return []; }
}

async function fbDeleteTrade(uid, tradeId) {
  await deleteDoc(doc(_db, "users", uid, "trades", tradeId));
}
async function fbDeleteUser(uid) {
  await deleteDoc(doc(_db,"users",uid));
}
function isAccessValid(rec) {
  if(!rec||!rec.active) return false;
  if(!rec.expiresAt) return true;
  return new Date(rec.expiresAt) > new Date();
}
function daysRemaining(rec) {
  if(!rec?.expiresAt) return null;
  return Math.ceil((new Date(rec.expiresAt)-new Date())/(864e5));
}

// ── Backend URL — permanent custom domain via Cloudflare Tunnel ──────────────
// api.rabtrade.in → Cloudflare Tunnel → VPS port 8000 (never changes)
const API_BASE       = "https://api.rabtrade.in";
const DELTA_API_BASE = "https://api.rabtrade.in";

// SECURITY (23 Oct 2026): the backend now trusts ONLY a verified Firebase ID token.
// A few older calls (AI chat, broker key registration, Kotak login…) used plain
// fetch() without one; this wrapper adds the signed-in user's token to every
// request to our API that doesn't already carry it.
if (typeof window !== "undefined" && !window.__rabAuthFetch) {
  window.__rabAuthFetch = true;
  const _origFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    let opts = init || {};
    try {
      const url = typeof input === "string" ? input : ((input && input.url) || String(input || ""));
      if (url.startsWith(API_BASE) || url.startsWith(DELTA_API_BASE)) {
        const h = new Headers(opts.headers || (typeof input !== "string" && input && input.headers) || {});
        if (!h.has("Authorization") && _auth && _auth.currentUser) {
          h.set("Authorization", "Bearer " + await _auth.currentUser.getIdToken());
          opts = { ...opts, headers: h };
        }
      }
    } catch (e) { /* fall through with original options */ }
    return _origFetch(input, opts);
  };
}
// Bump this whenever you ship. Shown in the VPS panel + header so you can tell
// at a glance which build a device is actually running (helps catch stale PWA).
const APP_BUILD      = "vps-v5.1-signal-tags · 2026-08-25";

// ── Margin helper — cash + pledged stock collateral ───────────────────────────
// Kite equity.available = cash only. equity.collateral = pledged stock margin.
// Total usable = available + collateral (haircut already applied by Kite).
// For MIS, use available_mis which Kite computes with leverage.
function getMargin(margins, mode="nrml") {
  const eq = margins?.equity || {};
  const cash  = parseFloat(eq.available    || 0);
  const coll  = parseFloat(eq.collateral   || 0);
  const mis   = parseFloat(eq.available_mis|| 0);
  const net   = parseFloat(eq.net          || 0);
  if(mode==="mis") return mis>0 ? mis : cash+coll;
  return net>0 ? net : cash+coll;
}
function getMarginBreakdown(margins) {
  const eq = margins?.equity || {};
  return {
    cash:       parseFloat(eq.available    ||0),
    collateral: parseFloat(eq.collateral   ||0),
    mis:        parseFloat(eq.available_mis||0),
    used:       parseFloat(eq.used         ||0),
    net:        parseFloat(eq.net          ||0),
    total:      getMargin(margins,"nrml"),
  };
}

// ── Price rounding helper — rounds to nearest tick size ──────────────────────
function roundToTick(price, tickSize) {
  if(!tickSize || tickSize<=0) return parseFloat(price.toFixed(2));
  const t = parseFloat(tickSize);
  const decimals = t < 1 ? String(t).split(".")[1]?.length || 2 : 0;
  const rounded = Math.round(price / t) * t;
  return parseFloat(rounded.toFixed(Math.max(decimals, 2)));
}

// ── Product type helper — F&O orders must be NRML, equity MIS/CNC ────────────
function getProduct(exchange, instrumentType, timeframe) {
  const ex     = (exchange||"").toUpperCase();
  const itype  = (instrumentType||"").toUpperCase();
  const tf     = (timeframe||"").toLowerCase();
  // F&O exchanges always NRML
  if(ex==="NFO" || ex==="MCX" || ex==="CDS" || ex==="BFO") return "NRML";
  // F&O instrument types always NRML regardless of exchange
  if(["FUT","CE","PE","OPT"].includes(itype)) return "NRML";
  // Weekly/Daily timeframe = positional/swing trade → CNC (delivery)
  if(tf==="1week" || tf==="1day") return "CNC";
  // Intraday timeframes → MIS
  return "MIS";
}

// ── Daily trade counter — returns today's count, resets at midnight ──────────
function getTodayStr() { return new Date().toISOString().slice(0,10); }

function checkDailyLimit(dailyRef, limit) {
  // Returns {ok, reached, count, remaining}
  if(!limit || limit === 0) return {ok:true, reached:false, count:0, remaining:999};
  const today = getTodayStr();
  const rec   = dailyRef.current;
  const count = rec.date === today ? rec.count : 0;
  const ok = count < limit;
  return {ok, reached: !ok, count, remaining: limit - count};
}

function incrementDailyTrades(setDailyTrades, dailyRef) {
  const today = getTodayStr();
  const prev  = dailyRef.current;
  const count = (prev.date === today ? prev.count : 0) + 1;
  setDailyTrades({date: today, count});
}

// Alias used by EquityTab
const incrementDailyCount = incrementDailyTrades;

// ── Smart position size — scales between minCapital and maxCapital ─────────────
// availableMargin: free margin in account currency
// minCapital:      minimum trade size (user-set floor)
// maxTradesPct:    max % of available margin for one trade (e.g. 50%)
// price:           asset price (for qty calculation)
function calcPositionSize(availableMargin, minCapital, maxTradesPct, price) {
  if(!availableMargin || availableMargin <= 0) return minCapital;
  const maxCapital = availableMargin * (maxTradesPct / 100);
  const scaled = Math.min(maxCapital, Math.max(minCapital, maxCapital * 0.5));
  return Math.max(minCapital, Math.floor(scaled));
}

// ── Kite "Place Order" deeplink ────────────────────────────────────────────────
// Opens Kite web with order pre-filled — no API key or static IP needed
// Works for trader/viewer users who place orders manually in their own Kite account
// ── Multi-broker order deeplink ────────────────────────────────────────────────
// Opens broker web/app with order pre-filled — no API key or static IP needed
// Works for all users regardless of broker

// ── Shared broker order overlay — professional order detail popup ────────────
function _showBrokerOrderOverlay({ symbol, exchange, side, qty, price, orderType, product, brokerName, brokerLogo, brokerColor, onOpen, connectLabel }) {
  const details = `${side==="BUY"?"BUY":"SELL"} ${qty} × ${symbol} @ ₹${price} (${orderType||"LIMIT"} ${product||"MIS"})`;
  robustCopy(details);
  const isBuy = side==="BUY";
  const sideColor = isBuy ? "#2ed47a" : "#ff5b6c";
  const ov = document.createElement('div');
  ov.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.85);z-index:999999;display:flex;align-items:center;justify-content:center;font-family:"IBM Plex Sans",sans-serif';
  ov.innerHTML = `
    <div style="background:#0b1624;border:1px solid #1a2c44;width:420px;max-width:95vw;box-shadow:0 20px 60px rgba(0,0,0,0.8)">
      <div style="background:${sideColor}15;border-bottom:1px solid ${sideColor}40;padding:16px 20px;display:flex;align-items:center;justify-content:space-between">
        <div style="font-size:18px;font-weight:700;color:${sideColor};font-family:'JetBrains Mono',monospace">${side==="BUY"?"BUY":"SELL"} ORDER</div>
        <button id="_bo_close" style="background:none;border:none;color:#5a728f;font-size:22px;cursor:pointer;padding:0 4px;line-height:1">✕</button>
      </div>
      <div style="padding:20px">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px">
          <div><div style="font-size:9px;text-transform:uppercase;letter-spacing:0.12em;color:#5a728f;margin-bottom:3px">Symbol</div><div style="font-size:16px;font-weight:700;color:#e7edf5;font-family:'JetBrains Mono',monospace">${symbol}</div></div>
          <div><div style="font-size:9px;text-transform:uppercase;letter-spacing:0.12em;color:#5a728f;margin-bottom:3px">Exchange</div><div style="font-size:16px;font-weight:700;color:#e7edf5;font-family:'JetBrains Mono',monospace">${exchange||"NSE"}</div></div>
          <div><div style="font-size:9px;text-transform:uppercase;letter-spacing:0.12em;color:#5a728f;margin-bottom:3px">Quantity</div><div style="font-size:16px;font-weight:700;color:#e7edf5;font-family:'JetBrains Mono',monospace">${qty}</div></div>
          <div><div style="font-size:9px;text-transform:uppercase;letter-spacing:0.12em;color:#5a728f;margin-bottom:3px">Price</div><div style="font-size:16px;font-weight:700;color:#e8b339;font-family:'JetBrains Mono',monospace">₹${price}</div></div>
          <div><div style="font-size:9px;text-transform:uppercase;letter-spacing:0.12em;color:#5a728f;margin-bottom:3px">Type</div><div style="font-size:13px;font-weight:600;color:#8ea2bf">${orderType||"LIMIT"}</div></div>
          <div><div style="font-size:9px;text-transform:uppercase;letter-spacing:0.12em;color:#5a728f;margin-bottom:3px">Product</div><div style="font-size:13px;font-weight:600;color:#8ea2bf">${product||"MIS"}</div></div>
        </div>
        <div style="background:#07101c;border:1px solid #1a2c44;padding:10px 14px;margin-bottom:16px;display:flex;align-items:center;gap:8px">
          <span style="color:#2ed47a;font-size:14px">✓</span>
          <span style="font-size:11px;color:#8ea2bf">Order details copied to clipboard</span>
        </div>
        <button id="_bo_open" style="width:100%;padding:14px;background:${brokerColor};border:none;color:#fff;font-size:14px;font-weight:700;cursor:pointer;font-family:inherit;letter-spacing:0.5px;margin-bottom:8px">
          ${brokerLogo} OPEN IN ${brokerName.toUpperCase()} — PASTE & PLACE
        </button>
        ${connectLabel ? `<div style="text-align:center;padding:10px 0 4px;border-top:1px solid #1a2c44;margin-top:8px">
          <div style="font-size:10px;color:#5a728f;margin-bottom:8px">Want 1-click order placement? Connect your Kite API</div>
          <button id="_bo_connect" style="background:rgba(232,179,57,0.1);border:1px solid rgba(232,179,57,0.3);color:#e8b339;padding:8px 16px;font-size:11px;font-weight:700;cursor:pointer;font-family:inherit">
            ⚡ CONNECT KITE API — PLACE ORDERS DIRECTLY
          </button>
        </div>` : ''}
      </div>
    </div>`;
  document.body.appendChild(ov);
  ov.querySelector('#_bo_close').onclick = () => ov.remove();
  ov.onclick = (e) => { if(e.target===ov) ov.remove(); };
  ov.querySelector('#_bo_open').onclick = () => { if(onOpen) onOpen(); ov.remove(); };
  const conn = ov.querySelector('#_bo_connect');
  if(conn) conn.onclick = () => { ov.remove(); window.dispatchEvent(new CustomEvent("rabt-show-settings")); };
}

const BROKER_CONFIGS = {
  zerodha: {
    name: "Zerodha Kite",
    logo: "🟠",
    color: "#ff6b35",
    openOrder: (p) => {
      // Use Kite Publisher basket URL — opens order placement popup directly in Kite web
      // Requires the user to be logged into Kite web in their browser.
      const apiKey = sessionStorage.getItem("kite_api_key") || sessionStorage.getItem("kite_key");
      if(apiKey && p.symbol) {
        const orderType = p.orderType || (p.price && parseFloat(p.price) > 0 ? "LIMIT" : "MARKET");
        const items = JSON.stringify([{
          tradingsymbol: p.symbol,
          exchange:      p.exchange || "NSE",
          transaction_type: p.side === "SELL" ? "SELL" : "BUY",
          order_type:    orderType,
          quantity:      parseInt(p.qty) || 1,
          product:       p.product || "MIS",
          price:         orderType === "LIMIT" ? parseFloat(p.price || 0) : 0,
          trigger_price: parseFloat(p.triggerPrice || 0),
        }]);
        const url = `https://kite.zerodha.com/connect/basket?api_key=${encodeURIComponent(apiKey)}&items=${encodeURIComponent(items)}`;
        window.open(url, "_blank");
      } else {
        // No api_key stored — fall back to overlay with manual instructions
        _showBrokerOrderOverlay({
          ...p, brokerName:"Zerodha Kite", brokerLogo:"🟠", brokerColor:"#ff6b35", connectLabel:true,
          onOpen: ()=> {
            // Open Kite buy/sell page for the symbol
            const sym = (p.symbol||"").toUpperCase();
            const exch = (p.exchange||"NSE").toUpperCase();
            window.open(`https://kite.zerodha.com/dashboard#${p.side==="SELL"?"s":"b"}:${exch}:${sym}`,"_blank");
          },
        });
      }
    },
  },
  groww: {
    name: "Groww",
    logo: "🌱",
    color: "#00d09c",
    openOrder: (p) => {
      const sym = (p.symbol||"").toUpperCase();
      const exch = (p.exchange||"NSE").toUpperCase();
      // Groww trade page: groww.in/trade/nse-{symbol} or groww.in/trade/bse-{symbol}
      const exchPrefix = exch === "BSE" ? "bse" : "nse";
      const url = `https://groww.in/trade/${exchPrefix}-${sym.toLowerCase()}`;
      _showBrokerOrderOverlay({
        ...p, brokerName:"Groww", brokerLogo:"🌱", brokerColor:"#00d09c", connectLabel:false,
        onOpen: ()=> window.open(url, "_blank"),
      });
    },
  },
  upstox: {
    name: "Upstox",
    logo: "🔵",
    color: "#5C6BC0",
    openOrder: ({ symbol, exchange, side, qty, price }) => {
      // Upstox web order page
      const params = new URLSearchParams({
        symbol: `${exchange}_EQ|${symbol}`,
        qty, price: price||0,
        transaction_type: side,
      });
      window.open(`https://upstox.com/trading/order-page/?${params}`, "_blank");
    }
  },
  angelone: {
    name: "AngelOne",
    logo: "😇",
    color: "#e8392b",
    openOrder: (p) => _showBrokerOrderOverlay({
      ...p, brokerName:"AngelOne", brokerLogo:"😇", brokerColor:"#e8392b", connectLabel:true,
      onOpen: ()=> window.open(`https://trade.angelone.in/trade-desk/${p.exchange||"NSE"}/${p.symbol}`,"_blank"),
    }),
  },
  fyers: {
    name: "Fyers",
    logo: "🟢",
    color: "#1a8c4e",
    openOrder: ({ symbol, exchange, side, qty, price }) => {
      window.open(`https://web.fyers.in/trade?symbol=${exchange}:${symbol}&side=${side}&qty=${qty}&price=${price||0}`, "_blank");
    }
  },
  dhan: {
    name: "Dhan",
    logo: "🟣",
    color: "#7c3aed",
    openOrder: (p) => _showBrokerOrderOverlay({
      ...p, brokerName:"Dhan", brokerLogo:"🟣", brokerColor:"#7c3aed", connectLabel:true,
      onOpen: ()=> window.open("https://web.dhan.co","_blank"),
    }),
  },
  kotak: {
    name: "Kotak Neo",
    logo: "🔴",
    color: "#dc2626",
    openOrder: (p) => _showBrokerOrderOverlay({
      ...p, brokerName:"Kotak Neo", brokerLogo:"🔴", brokerColor:"#dc2626", connectLabel:true,
      onOpen: ()=> window.open("https://trade.kotakneo.com/Login/","_blank"),
    }),
  },
  hdfcsky: {
    name: "HDFC SKY",
    logo: "🔷",
    color: "#004C8F",
    openOrder: (p) => _showBrokerOrderOverlay({
      ...p, brokerName:"HDFC SKY", brokerLogo:"🔷", brokerColor:"#004C8F", connectLabel:true,
      onOpen: ()=> window.open("https://hdfcsky.com/trading","_blank"),
    }),
  },
  hdfcsec: {
    name: "HDFC Securities",
    logo: "🏦",
    color: "#1a56a0",
    openOrder: (p) => _showBrokerOrderOverlay({
      ...p, brokerName:"HDFC Securities", brokerLogo:"🏦", brokerColor:"#1a56a0", connectLabel:true,
      onOpen: ()=> window.open("https://investright.hdfcsec.com","_blank"),
    }),
  },
};

// User's chosen broker stored in localStorage
let _userBroker = localStorage.getItem("rabt_broker") || "zerodha";
function setUserBroker(b) { _userBroker = b; localStorage.setItem("rabt_broker", b); }
function getUserBroker() { return _userBroker; }

function openInBroker(orderParams) {
  const broker = BROKER_CONFIGS[_userBroker] || BROKER_CONFIGS.zerodha;
  broker.openOrder(orderParams);
}

// Legacy alias
function openInKite(params) { openInBroker(params); }

// ── Robust clipboard copy — tries modern API first, falls back to execCommand ──
function robustCopy(text) {
  if(navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).catch(()=>{
      // Clipboard API rejected (e.g. no focus / permissions) — use fallback
      _execCommandCopy(text);
    });
  } else {
    _execCommandCopy(text);
  }
}
function _execCommandCopy(text) {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0";
    document.body.appendChild(ta);
    ta.focus(); ta.select();
    document.execCommand("copy");
    ta.remove();
  } catch(e) { /* silent — user will see order details in the toast anyway */ }
}

// ── GTT (Good Till Triggered) helper ─────────────────────────────────────────
// Creates TP/SL via Zerodha's broker-native GTT system.
// Legal for ALL users — GTT is placed by Zerodha, not our VPS.
// OCO = One Cancels Other: TP + SL simultaneously.
async function createGTT({ symbol, exchange="NSE", side="BUY", qty=1,
                           entryPrice=0, tpPrice=null, slPrice=null,
                           tpEnabled=true, slEnabled=true }) {
  const exitSide = side==="BUY" ? "SELL" : "BUY";

  if(tpEnabled && slEnabled && tpPrice && slPrice) {
    // OCO: two-leg GTT — TP + SL, one cancels the other
    const sorted = side==="BUY"
      ? [slPrice, tpPrice]           // BUY: SL below, TP above
      : [tpPrice, slPrice];          // SELL: TP below, SL above
    const limits  = side==="BUY"
      ? [slPrice*0.995, tpPrice*1.005]   // slight slippage buffer
      : [tpPrice*0.995, slPrice*1.005];

    return apiFetch("/gtt/create", {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({
        tradingsymbol:    symbol,
        exchange,
        transaction_type: exitSide,
        quantity:         qty,
        last_price:       entryPrice,
        trigger_type:     "two-leg",
        trigger_values:   sorted.map(p=>parseFloat(p.toFixed(2))),
        limit_prices:     limits.map(p=>parseFloat(p.toFixed(2))),
      })
    });
  } else if(tpEnabled && tpPrice) {
    // Single TP
    return apiFetch("/gtt/create", {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({
        tradingsymbol: symbol, exchange,
        transaction_type: exitSide, quantity: qty,
        last_price: entryPrice, trigger_type: "single",
        trigger_values: [parseFloat(tpPrice.toFixed(2))],
        limit_prices:   [parseFloat((tpPrice*1.005).toFixed(2))],
      })
    });
  } else if(slEnabled && slPrice) {
    // Single SL
    return apiFetch("/gtt/create", {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({
        tradingsymbol: symbol, exchange,
        transaction_type: exitSide, quantity: qty,
        last_price: entryPrice, trigger_type: "single",
        trigger_values: [parseFloat(slPrice.toFixed(2))],
        limit_prices:   [parseFloat((slPrice*0.995).toFixed(2))],
      })
    });
  }
  return null;
}

// ── Place OCO GTT + register trailing SL ─────────────────────────────────────
async function placeGTTWithTrail({ symbol, exchange="NSE", side="BUY", qty=1,
                                   entryPrice=0, tpPrice=null, slPrice=null,
                                   trailPct=1.5 }) {
  // 1. Place OCO GTT (TP + SL)
  const result = await createGTT({
    symbol, exchange, side, qty, entryPrice, tpPrice, slPrice,
    tpEnabled: !!tpPrice, slEnabled: !!slPrice,
  });
  if(!result?.ok) return result;

  // 2. Register for trailing SL
  if(result.gtt_id && trailPct > 0) {
    try {
      await apiFetch("/gtt/trail/register", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          gtt_id: result.gtt_id,
          entry_price: entryPrice,
          side,
          trail_pct: trailPct,
          symbol,
          exchange,
        })
      });
    } catch(e) { console.warn("Trail register failed:", e); }
  }
  return result;
}


// Delta-specific: calculate qty from available balance using contract margin
function calcDeltaQty(availBal, marginPct, price, initialMarginPct, contractValue) {
  if(!availBal || availBal <= 0 || !price || price <= 0) return 1;
  const im     = (initialMarginPct > 1 ? initialMarginPct / 100 : initialMarginPct) || 0.05;
  const cv     = contractValue || 1;
  const marginPerContract = price * cv * im;
  if(marginPerContract <= 0) return 1;
  const capital = availBal * (marginPct / 100);
  const qty     = Math.max(1, Math.floor(capital / marginPerContract));
  const maxQty  = Math.max(1, Math.floor((availBal * 0.30) / marginPerContract));
  return Math.min(qty, maxQty);
}

// ── PWA Service Worker registration (self-updating) ──────────────────────────
// A cache-first service worker will keep serving an OLD JS bundle after a deploy
// until it's manually cleared. To stop that: check for updates on load + hourly,
// tell any waiting worker to activate, and reload once when it takes control so
// the newest build (see window.__RAB_BUILD__) loads without a manual cache clear.
if('serviceWorker' in navigator) {
  let _swReloaded = false;
  try {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if(_swReloaded) return; _swReloaded = true;
      window.location.reload();
    });
  } catch(e) {}
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then(reg => {
      try { reg.update(); } catch(e) {}
      try { setInterval(() => { try { reg.update(); } catch(e) {} }, 60*60*1000); } catch(e) {}
      const _promote = (w) => { try { w && w.postMessage && w.postMessage({type:'SKIP_WAITING'}); } catch(e) {} };
      if(reg.waiting) _promote(reg.waiting);
      reg.addEventListener('updatefound', () => {
        const nw = reg.installing;
        nw && nw.addEventListener('statechange', () => {
          if(nw.state === 'installed' && navigator.serviceWorker.controller) _promote(nw);
        });
      });
    }).catch(()=>{});
  });
}

// Fetch a fresh Firebase ID token for the signed-in user. getIdToken() returns
// a cached token and auto-refreshes when it's near expiry, so it's safe to call
// on every request. Returns "" when signed out.
async function _idToken() {
  try {
    if (_auth && _auth.currentUser) return await _auth.currentUser.getIdToken();
  } catch (e) {}
  return "";
}

// Build the standard auth headers (verified Bearer token + legacy X-User-ID).
async function _authHeaders(extra={}) {
  return _authHeadersFor(_currentUID, extra);
}

// Same, but with an explicit uid — for calls made during login before the
// module-level _currentUID has been set (e.g. auth-status polling).
async function _authHeadersFor(uid, extra={}) {
  const tok = await _idToken();
  return {
    ...extra,
    ...(uid ? {"X-User-ID": uid} : {}),
    ...(tok ? {"Authorization": "Bearer " + tok} : {}),
  };
}

async function deltaFetch(path, opts={}) {
  // Telegram is restricted (see apiFetch) — block Delta's telegram sends too.
  if(path.includes("alert/telegram") && !path.includes("/test") && !opts._tgAllow){
    return { ok:true, suppressed:"tg-restricted" };
  }
  const headers = await _authHeaders(opts.headers||{});
  const res = await fetch(`${DELTA_API_BASE}${path}`, {...opts, headers});
  if (!res.ok) {
    const e = await res.json().catch(()=>({detail:res.statusText}));
    // e.detail can be a string or an object — normalise to string
    const msg = typeof e.detail === "string" ? e.detail
      : typeof e.detail === "object" ? JSON.stringify(e.detail)
      : e.error || e.message || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return res.json();
}

// ── Constants ─────────────────────────────────────────────────────────────────
const SCAN_LABELS = { 1:"TSI Cross", 2:"RSI Extreme", 3:"Vol+RSI", 4:"EMA 9/50 Cross", 5:"📐 Patterns", 6:"🔀 MTF", 7:"ROC", 8:"Price Action" };

// ── Trade Reason options (shown when no scan signal attached) ────────────────
const TRADE_REASONS = [
  "Technical breakout",
  "Support/Resistance level",
  "News/Event based",
  "Momentum/Trend following",
  "Mean reversion",
  "Hedging existing position",
  "Sector rotation",
  "Options premium decay",
  "Scalping opportunity",
];

// ── Auto-Virtual: add scan results with clear signals to Virtual Tab ─────────
// activeScan: only count signals from the user's SELECTED scans (null = all)
function autoVirtualFromScan(results, addVirtualFn, minSignals, tabMode, exchange, showToast, activeScan=null) {
  if(!addVirtualFn || !results?.length) return 0;
  let count = 0;
  for(const r of results) {
    if(r.error) continue;
    // Filter to signals from the selected scan toggles only
    const sigs = (r.signals || []).filter(s=>!activeScan || activeScan.includes(s.scan));
    if(sigs.length < minSignals) continue;
    const hasBuy  = sigs.some(s=>s.type==="BUY");
    const hasSell = sigs.some(s=>s.type==="SELL");
    if(!hasBuy && !hasSell) continue;
    const side = hasBuy && !hasSell ? "BUY" : hasSell && !hasBuy ? "SELL" : "BUY";
    const ltp  = parseFloat(r.ltp || r.price || 0);
    if(ltp <= 0) continue;
    // Default TP/SL based on tab mode
    const tpPct = tabMode==="fo" ? 3 : tabMode==="mcx" ? 2 : 2;
    const slPct = tabMode==="fo" ? 2 : tabMode==="mcx" ? 1.5 : 1;
    const added = addVirtualFn({
      symbol:    r.symbol,
      exchange:  exchange || r.exchange || "NSE",
      side,
      entry:     ltp,
      price:     ltp,
      qty:       r.lot_size || 1,
      product:   tabMode==="fo"?"NRML":tabMode==="mcx"?"NRML":"MIS",
      tp:        ltp * (side==="BUY" ? 1+tpPct/100 : 1-tpPct/100),
      sl:        ltp * (side==="BUY" ? 1-slPct/100 : 1+slPct/100),
      tab_mode:  tabMode,
      source:    "auto_virtual",
      signals:   sigs.map(s=>s.label).join(" · "),
      confidence:r.confidence || null,
      score:     r.score || 0,
      note:      `Auto-virtual · ${sigs.length} signals: ${sigs.map(s=>s.label).join(", ")}`,
    });
    if(added) count++;   // null = duplicate skipped (already OPEN in Virtual)
  }
  if(count > 0 && showToast) showToast(`🎮 ${count} stock${count>1?"s":""} added to Virtual Tab (${minSignals}+ signals)`);
  return count;
}

// ── Auto-Virtual toggle UI component ─────────────────────────────────────────
function AutoVirtualToggle({enabled, setEnabled, minSigs, setMinSigs}) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:6,padding:"4px 0"}}>
      <label style={{display:"flex",alignItems:"center",gap:4,cursor:"pointer"}}>
        <input type="checkbox" checked={enabled} onChange={e=>setEnabled(e.target.checked)}
          style={{accentColor:"#60a5fa",width:12,height:12}}/>
        <span style={{fontSize:10,color:enabled?"#60a5fa":"#5a728f",fontWeight:enabled?700:400}}>
          🎮 Auto-Virtual
        </span>
      </label>
      {enabled && (
        <span style={{fontSize:10,color:"#8ea2bf",display:"flex",alignItems:"center",gap:3}}>
          min
          <input type="number" min={1} max={6} value={minSigs}
            onChange={e=>setMinSigs(Math.max(1,Math.min(6,parseInt(e.target.value)||1)))}
            style={{width:30,padding:"1px 3px",fontSize:10,background:"#0a1628",
              border:"1px solid #23405f",borderRadius:3,color:"#e7edf5",textAlign:"center"}}/>
          signals
        </span>
      )}
    </div>
  );
}

// ── Strategy Engine Constants ─────────────────────────────────────────────────
const STRATEGIES = {
  AUTO:     { label:"🤖 Auto",     desc:"IV-based auto select",          color:"#e8b339" },
  NAKED:    { label:"🎯 Naked",    desc:"Simple CE/PE buy",              color:"#6b7280" },
  SPREAD:   { label:"📊 Spread",   desc:"Bull Call / Bear Put Spread",   color:"#e8b339" },
  STRADDLE: { label:"⚡ Straddle", desc:"ATM CE + PE (same strike)",     color:"#49c2e4" },
  STRANGLE: { label:"🔀 Strangle", desc:"OTM CE + OTM PE",              color:"#ff5b6c" },
};

const IV_COLORS = { HIGH:"#ff5b6c", MEDIUM:"#e8b339", LOW:"#2ed47a", UNKNOWN:"#8ea2bf" };

// Shared strategy builder — calls /fo/strategy endpoint
async function buildStrategy(symbol, exchange, signalSide, cfg) {
  const body = {
    symbol,
    exchange,
    signal_side:       signalSide,    // BUY / SELL / MIXED
    min_days:          cfg.minDays || 15,
    iv_override:       cfg.ivOverride || null,
    strategy_override: cfg.strategyOverride === "AUTO" ? null : (cfg.strategyOverride || null),
    spread_steps:      cfg.spreadSteps || 2,
    signal_count:      cfg.signalCount || 1,
  };
  return apiFetch("/fo/strategy", {
    method:"POST", headers:{"Content-Type":"application/json"},
    body: JSON.stringify(body)
  });
}

// Place all legs of a strategy
async function placeStrategyLegs(legs, capital, product="NRML") {
  const results = [];
  for (const leg of legs) {
    const price = parseFloat(leg.option_ltp || 0);
    if (!price) { results.push({leg, error:"LTP=0"}); continue; }
    const qty = Math.max(
      leg.lot_size,
      Math.floor(capital / price / leg.lot_size) * leg.lot_size
    );
    try {
      const res = await apiFetch("/order/place", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          tradingsymbol:    leg.tradingsymbol,
          exchange:         leg.exchange,
          transaction_type: leg.action,   // BUY or SELL
          quantity:         qty,
          order_type:       "LIMIT",
          price:            price,
          trigger_price:    0,
          product,
          tag: "RABT-AUTO-FO",
        })
      });
      results.push({ leg, qty, price, orderId: res.order_id, ok: true });
    } catch(e) {
      results.push({ leg, qty, price, error: e.message, ok: false });
    }
  }
  return results;
}


const INDICES = ["NIFTY","BANKNIFTY","FINNIFTY","MIDCPNIFTY","SENSEX","BANKEX","NIFTYIT"];
// Map user-friendly index names → backend will resolve to NFO futures
const INDEX_NAMES = new Set([
  "NIFTY","NIFTY 50","NIFTY50",
  "BANKNIFTY","BANK NIFTY",
  "FINNIFTY","NIFTY FIN SERVICE",
  "MIDCPNIFTY","MIDCAP NIFTY",
  "NIFTY IT","NIFTYIT",
  "SENSEX",
]);
// MCX symbols use exchange prefix for Kite — stored separately
const MCX_BASE = {
  "CRUDEOIL":   {name:"Crude Oil",    unit:"bbl",  color:"#e8b339"},
  "GOLD":       {name:"Gold",         unit:"10g",  color:"#f0c040"},
  "SILVER":     {name:"Silver",       unit:"kg",   color:"#a0b0c0"},
  "NATURALGAS": {name:"Natural Gas",  unit:"mmbtu",color:"#6b7280"},
  "COPPER":     {name:"Copper",       unit:"kg",   color:"#e8b339"},
  "ZINC":       {name:"Zinc",         unit:"kg",   color:"#8ea2bf"},
  "LEAD":       {name:"Lead",         unit:"kg",   color:"#8ea2bf"},
  "ALUMINIUM":  {name:"Aluminium",    unit:"kg",   color:"#8ea2bf"},
  "NICKEL":     {name:"Nickel",       unit:"kg",   color:"#8ea2bf"},
  "CRUDEOILM":  {name:"Crude Oil Mini",unit:"bbl", color:"#e8b339"},
  "GOLDM":      {name:"Gold Mini",    unit:"g",    color:"#f0c040"},
  "SILVERM":    {name:"Silver Mini",  unit:"kg",   color:"#a0b0c0"},
  "GOLDPETAL":  {name:"Gold Petal",   unit:"g",    color:"#f0c040"},
  "COTTONCNDY": {name:"Cotton",       unit:"bale", color:"#2ed47a"},
};

// MCX lot sizes — number of units per lot (for P&L calculation)
// Kite order quantity = number of lots, P&L = price_diff × qty × lot_size
const MCX_LOT_SIZE = {
  "CRUDEOIL":   100,   // 100 barrels/lot
  "CRUDEOILM":  10,    // 10 barrels/lot (mini)
  "GOLD":       1,     // 1 unit = 10g
  "GOLDM":      1,     // 1g
  "GOLDPETAL":  1,     // 1g
  "SILVER":     30,    // 30 kg/lot
  "SILVERM":    5,     // 5 kg/lot
  "NATURALGAS": 1250,  // 1250 mmbtu/lot
  "COPPER":     2500,  // 2500 kg/lot
  "ZINC":       5000,  // 5000 kg/lot
  "LEAD":       5000,  // 5000 kg/lot
  "ALUMINIUM":  5000,  // 5000 kg/lot
  "NICKEL":     1500,  // 1500 kg/lot
  "COTTONCNDY": 25,    // 25 bales/lot
};

// Delta India uses BTCUSD, ETHUSD for inverse perps
// and SOLUSD, BNBUSD etc — verify exact names via /delta/products
const DELTA_WATCHLISTS = {
  "TOP 5":        ["BTCUSD","ETHUSD","SOLUSD","BNBUSD","XRPUSD"],
  "PERP MAJORS":  ["BTCUSD","ETHUSD","SOLUSD","BNBUSD","XRPUSD","ADAUSD","DOGEUSD","AVAXUSD"],
  "PERP ALT":     ["MATICUSD","DOTUSD","LTCUSD","ATOMUSD","UNIUSD","LINKUSD","TRXUSD"],
  "DEFI":         ["UNIUSD","AAVEUSD","SUSHIUSD","COMPUSD","MKRUSD","SNXUSD"],
};


// ── Default signal thresholds (passed to /scan as signal_params) ─────────────
const DEFAULT_SIGNAL_PARAMS = {
  // TSI 25/13/13 — matches TradingView / Kite default "True Strength Index 25 13 13".
  // (Signal was 7, which crosses earlier than TV and produced false cross signals.)
  tsi_long: 25, tsi_short: 13, tsi_signal: 13,
  rsi_period: 14, rsi_ob: 80, rsi_os: 20,
  vol_high: 1.5, vol_low: 0.6,
  rsi_vol_high: 65, rsi_vol_low: 35,
  ema_fast: 9, ema_slow: 50,
  vol_lookback: 5,    // candles to measure buy/sell volume
  vol_dom_pct:  60,   // % dominance threshold for buy/sell side
  // MACD
  macd_fast: 12, macd_slow: 26, macd_signal: 9,
  // Bollinger Bands
  bb_period: 20, bb_std: 2.0,
  // Stochastic
  stoch_k: 14, stoch_d: 3, stoch_smooth: 3,
  stoch_ob: 80, stoch_os: 20,
};

const DELTA_SCAN_LABELS = {1:"TSI Cross",2:"RSI Extreme",3:"Vol+RSI",4:"EMA Cross",5:"📐 Patterns",6:"🔀 MTF"};
const SCAN_LABELS_MAP   = {1:"TSI Cross",2:"RSI Extreme",3:"Vol+RSI",4:"EMA Cross",5:"📐 Patterns",6:"🔀 MTF",8:"D/S Zone"};

const MCX_SYMBOLS = ["CRUDEOIL","GOLD","SILVER","NATURALGAS","COPPER","ZINC","LEAD","ALUMINIUM","NICKEL","CRUDEOILM","GOLDM","SILVERM","GOLDPETAL","COTTONCNDY"];

const WATCHLISTS = {
  "NIFTY 50":    ["RELIANCE","TCS","HDFCBANK","INFY","ICICIBANK","HINDUNILVR","SBIN","BAJFINANCE","BHARTIARTL","KOTAKBANK","LT","AXISBANK","ASIANPAINT","MARUTI","TITAN","SUNPHARMA","WIPRO","ULTRACEMCO","NESTLEIND","TECHM","POWERGRID","NTPC","ONGC","COALINDIA","TATAMOTORS","TATASTEEL","JSWSTEEL","ADANIPORTS","INDUSINDBK","BAJAJFINSV","HCLTECH","DRREDDY","DIVISLAB","CIPLA","EICHERMOT","HEROMOTOCO","BPCL","IOC","GRASIM","HINDALCO","BRITANNIA","APOLLOHOSP","TATACONSUM","BAJAJ-AUTO","UPL","SBILIFE","HDFCLIFE","ADANIENT"],
  "NSE F&O ALL": ["RELIANCE","TCS","HDFCBANK","INFY","ICICIBANK","HINDUNILVR","SBIN","BAJFINANCE","BHARTIARTL","KOTAKBANK","LT","AXISBANK","ASIANPAINT","MARUTI","TITAN","SUNPHARMA","WIPRO","ULTRACEMCO","NESTLEIND","TECHM","POWERGRID","NTPC","ONGC","COALINDIA","TATAMOTORS","TATASTEEL","JSWSTEEL","ADANIPORTS","INDUSINDBK","BAJAJFINSV","HCLTECH","DRREDDY","DIVISLAB","CIPLA","EICHERMOT","HEROMOTOCO","BPCL","IOC","GRASIM","HINDALCO","BRITANNIA","APOLLOHOSP","TATACONSUM","BAJAJ-AUTO","UPL","SBILIFE","HDFCLIFE","ADANIENT","ITC","M&M","AARTIIND","ABBOTINDIA","ABCAPITAL","ABFRL","ACC","ALKEM","AMBUJACEM","APOLLOTYRE","ASHOKLEY","ASTRAL","ATGL","AUROPHARMA","BALKRISIND","BANDHANBNK","BANKBARODA","BATAINDIA","BEL","BERGEPAINT","BHARATFORG","BHEL","BIOCON","CANBK","CANFINHOME","CHOLAFIN","COFORGE","COLPAL","CONCOR","CROMPTON","CUMMINSIND","DABUR","DEEPAKNTR","DELTACORP","DIXON","DLF","EMAMILTD","ESCORTS","FEDERALBNK","GAIL","GLENMARK","GMRINFRA","GNFC","GODREJCP","GODREJPROP","GRANULES","GSPL","HDFCAMC","HINDCOPPER","HINDPETRO","ICICIGI","ICICIPRULI","IDFCFIRSTB","IEX","INDHOTEL","INDIAMART","INDUSTOWER","IPCALAB","IRB","JINDALSTEL","JUBLFOOD","L&TFH","LICHSGFIN","LTIM","LTTS","LUPIN","M&MFIN","MANAPPURAM","MARICO","MCX","METROPOLIS","MFSL","MGL","MOTHERSON","MPHASIS","MRF","MUTHOOTFIN","NATIONALUM","NAUKRI","NMDC","OBEROIRLTY","OFSS","PAGEIND","PEL","PERSISTENT","PETRONET","PFC","PIDILITIND","PIIND","PNB","POLYCAB","PVRINOX","RAMCOCEM","RBLBANK","RECLTD","SAIL","SBICARD","SHREECEM","SIEMENS","SRF","SUNTV","SYNGENE","TATACHEM","TATACOMM","TATAPOWER","TORNTPHARM","TORNTPOWER","TRENT","TVSMOTORS","UBL","UNIONBANK","VEDL","VOLTAS","YESBANK","ZEEL","ZYDUSLIFE"],
  "NIFTY BANK":  ["HDFCBANK","ICICIBANK","SBIN","KOTAKBANK","AXISBANK","INDUSINDBK","BANDHANBNK","FEDERALBNK","IDFCFIRSTB","PNB","BANKBARODA","CANBK","AUBANK","RBLBANK","YESBANK"],
  "NIFTY IT":    ["TCS","INFY","HCLTECH","WIPRO","TECHM","LTIM","MPHASIS","COFORGE","PERSISTENT","OFSS","LTTS","KPITTECH"],
  "NIFTY MIDCAP":["PIIND","AARTIIND","ABCAPITAL","ABFRL","ACC","APLAPOLLO","ASTRAL","BALKRISIND","BATAINDIA","BHARATFORG","BIOCON","CANFINHOME","CHOLAFIN","CONCOR","CROMPTON","CUMMINSIND","DEEPAKNTR","DIXON","GNFC","GODREJPROP"],
  "NIFTY PHARMA":["SUNPHARMA","DRREDDY","CIPLA","DIVISLAB","BIOCON","AUROPHARMA","TORNTPHARM","LUPIN","ALKEM","IPCALAB","ABBOTINDIA"],
  "NIFTY AUTO":  ["MARUTI","TATAMOTORS","M&M","BAJAJ-AUTO","EICHERMOT","HEROMOTOCO","TVSMOTORS","ASHOKLEY","BHARATFORG","MOTHERSON","BALKRISIND","MRF","APOLLOTYRE","BOSCHLTD"],
  "NIFTY FMCG":  ["HINDUNILVR","NESTLEIND","BRITANNIA","TATACONSUM","DABUR","MARICO","GODREJCP","EMAMILTD","COLPAL","VBL","RADICO","UBL","MCDOWELL-N"],
  "NIFTY METAL": ["TATASTEEL","JSWSTEEL","HINDALCO","VEDL","COALINDIA","NMDC","SAILSTEEL","JINDALSTEL","NATIONALUM","WELCORP","RATNAMANI","APL"],
  "MCX":         MCX_SYMBOLS,
};

// ── F&O Tradeable Index Watchlists ───────────────────────────────────────────
// Only indices/stocks that have liquid F&O contracts on NSE
const FO_WATCHLISTS = {
  "NSE F&O ALL": WATCHLISTS["NSE F&O ALL"],  // full ~182 stock F&O list
  "INDEX FUTURES": [
    "NIFTY 50","BANKNIFTY","FINNIFTY","MIDCPNIFTY","SENSEX","BANKEX",
  ],
  "NIFTY F&O":  [
    "NIFTY 50","RELIANCE","TCS","HDFCBANK","INFY","ICICIBANK","SBIN",
    "BAJFINANCE","BHARTIARTL","KOTAKBANK","LT","AXISBANK","TATAMOTORS",
    "TATASTEEL","WIPRO","SUNPHARMA","MARUTI","TITAN","ADANIPORTS","ONGC",
  ],
  "BANK F&O": [
    "BANKNIFTY","HDFCBANK","ICICIBANK","SBIN","KOTAKBANK","AXISBANK",
    "INDUSINDBK","BANDHANBNK","FEDERALBNK","IDFCFIRSTB","PNB","BANKBARODA",
  ],
  "FIN F&O": [
    "FINNIFTY","BAJFINANCE","BAJAJFINSV","HDFCLIFE","SBILIFE","ICICIPRULI",
    "MUTHOOTFIN","CHOLAFIN","M&MFIN","LICHSGFIN","ABCAPITAL",
  ],
  "MIDCAP F&O": [
    "MIDCPNIFTY","ABFRL","ACC","ASTRAL","BALKRISIND","BHARATFORG",
    "BIOCON","CANFINHOME","CROMPTON","DEEPAKNTR","DIXON","GODREJPROP",
  ],
  "NSE F&O ALL": [
    // Nifty 50
    "RELIANCE","TCS","HDFCBANK","INFY","ICICIBANK","HINDUNILVR","SBIN","BAJFINANCE",
    "BHARTIARTL","KOTAKBANK","LT","AXISBANK","ASIANPAINT","MARUTI","TITAN","SUNPHARMA",
    "WIPRO","ULTRACEMCO","NESTLEIND","TECHM","POWERGRID","NTPC","ONGC","COALINDIA",
    "TATAMOTORS","TATASTEEL","JSWSTEEL","ADANIPORTS","INDUSINDBK","BAJAJFINSV",
    "HCLTECH","DRREDDY","DIVISLAB","CIPLA","EICHERMOT","HEROMOTOCO","BPCL","IOC",
    "GRASIM","HINDALCO","BRITANNIA","APOLLOHOSP","TATACONSUM","BAJAJ-AUTO","UPL",
    "SBILIFE","HDFCLIFE","ADANIENT","ITC","M&M",
    // Additional F&O eligible stocks
    "AARTIIND","ABBOTINDIA","ABCAPITAL","ABFRL","ACC","ALKEM","AMBUJACEM",
    "APOLLOTYRE","ASHOKLEY","ASTRAL","ATGL","AUROPHARMA","BALKRISIND","BANDHANBNK",
    "BANKBARODA","BATAINDIA","BEL","BERGEPAINT","BHARATFORG","BHEL","BIOCON",
    "CANBK","CANFINHOME","CHOLAFIN","COFORGE","COLPAL","CONCOR","CROMPTON",
    "CUMMINSIND","DABUR","DEEPAKNTR","DELTACORP","DIXON","DLF",
    "EMAMILTD","ESCORTS","FEDERALBNK","GAIL","GLENMARK","GMRINFRA","GNFC",
    "GODREJCP","GODREJPROP","GRANULES","GSPL","HDFCAMC","HINDCOPPER","HINDPETRO",
    "ICICIGI","ICICIPRULI","IDFCFIRSTB","IEX","INDHOTEL","INDIAMART","INDUSTOWER",
    "IPCALAB","IRB","JINDALSTEL","JUBLFOOD","L&TFH","LICHSGFIN","LTIM","LTTS",
    "LUPIN","M&MFIN","MANAPPURAM","MARICO","MCX","METROPOLIS","MFSL","MGL",
    "MOTHERSON","MPHASIS","MRF","MUTHOOTFIN","NATIONALUM","NAUKRI",
    "NMDC","OBEROIRLTY","OFSS","PAGEIND","PEL","PERSISTENT","PETRONET","PFC",
    "PIDILITIND","PIIND","PNB","POLYCAB","PVRINOX","RAMCOCEM","RBLBANK","RECLTD",
    "SAIL","SBICARD","SHREECEM","SIEMENS","SRF","SUNTV","SYNGENE",
    "TATACHEM","TATACOMM","TATAPOWER","TORNTPHARM","TORNTPOWER","TRENT",
    "TVSMOTORS","UBL","UNIONBANK","VEDL","VOLTAS","YESBANK","ZEEL","ZYDUSLIFE",
  ],
  "STOCK F&O":  [
    "RELIANCE","TCS","HDFCBANK","INFY","ICICIBANK","SBIN","BAJFINANCE",
    "BHARTIARTL","KOTAKBANK","LT","AXISBANK","MARUTI","TITAN","SUNPHARMA",
    "WIPRO","TATAMOTORS","TATASTEEL","ADANIPORTS","ONGC","NTPC","COALINDIA",
    "JSWSTEEL","HINDALCO","BPCL","IOC","DRREDDY","CIPLA","DIVISLAB",
    "EICHERMOT","HEROMOTOCO","BAJAJ-AUTO","TECHM","HCLTECH","ULTRACEMCO",
  ],
};

// Per-list exchange override (MCX needs different exchange in orders)
const LIST_EXCHANGE = {"MCX":"MCX"};
const DEFAULT_EXCHANGE = "NSE";
const INDEX_QUOTES_MAP = {
  "NIFTY 50":    "NIFTY 50",
  "BANKNIFTY":   "NIFTY BANK",
  "FINNIFTY":    "NIFTY FIN SERVICE",
  "MIDCPNIFTY":  "NIFTY MIDCAP SELECT",
  "NIFTY IT":    "NIFTY IT",
  "NIFTY AUTO":  "NIFTY AUTO",
};
// BSE indices fetched separately
const BSE_INDEX_MAP = {
  "SENSEX":  "BSE:SENSEX",
  "BANKEX":  "BSE:BANKEX",
};
// ══════════════════════════════════════════════════════════════════════════════
// DAILY / WEEKLY ALERT STATUS SECTION (embedded inside VpsScannerPanel)
// ══════════════════════════════════════════════════════════════════════════════
function DailyWeeklyScanStatus({ authed }) {
  const [bgStatus, setBgStatus]   = useState(null);
  const [toggling, setToggling]   = useState(null);   // "daily"|"weekly"
  const [running,  setRunning]    = useState(null);   // "daily"|"weekly"

  const fetchStatus = async () => {
    try { const d = await apiFetch("/bg-scan/status"); setBgStatus(d); }
    catch {}
  };

  useEffect(()=>{
    if(!authed) return;
    fetchStatus();
    const t = setInterval(fetchStatus, 30000);  // refresh every 30s
    return ()=>clearInterval(t);
  },[authed]);

  const toggle = async (type) => {
    if(!bgStatus) return;
    const curEnabled = type==="daily" ? bgStatus.daily_enabled : bgStatus.weekly_enabled;
    setToggling(type);
    try {
      await apiFetch("/bg-scan/toggle",{
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({type, enabled: !curEnabled})
      });
      await fetchStatus();
    } catch(e) { alert("Toggle failed: "+e.message); }
    finally { setToggling(null); }
  };

  const runNow = async (type) => {
    if(!window.confirm(`Trigger ${type} alert scan now? This will scan all watchlist symbols and send Telegram alerts.`)) return;
    setRunning(type);
    try {
      await apiFetch("/bg-scan/run-now",{
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({type})
      });
      await fetchStatus();
    } catch(e) { alert("Trigger failed: "+e.message); }
    finally { setRunning(null); }
  };

  const fmtTime = (iso) => {
    if(!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-IN",{
        month:"short", day:"numeric",
        hour:"2-digit", minute:"2-digit", hour12:false,
        timeZone:"Asia/Kolkata"
      }) + " IST";
    } catch { return iso; }
  };

  const fmtNext = (iso) => {
    if(!iso) return "—";
    try {
      const diff = Math.round((new Date(iso) - Date.now()) / 60000);
      if(diff < 0) return "soon";
      if(diff < 60) return `in ${diff}m`;
      const h = Math.floor(diff/60), m = diff%60;
      if(h < 24) return `in ${h}h${m?` ${m}m`:""}`;
      return `in ${Math.floor(h/24)}d`;
    } catch { return "—"; }
  };

  if(!authed) return null;

  const Row = ({type, label, enabled, running: isRunning, lastRun, nextRun, alertsSent}) => {
    const isToggling = toggling===type;
    const isRunNow   = running===type;
    const dotColor   = isRunning ? "#f59e0b"
                     : enabled   ? "#2ed47a"
                     :             "#ff5b6c";
    const dotLabel   = isRunning ? "⟳ SCANNING…"
                     : enabled   ? "● ACTIVE"
                     :             "○ PAUSED";
    return (
      <div style={{
        background:"#0d1117", borderRadius:0, padding:"10px 12px",
        border:`1px solid ${enabled?"rgba(34,197,94,0.25)":"rgba(100,116,139,0.2)"}`,
      }}>
        {/* Title row */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:14}}>{type==="daily"?"📅":"📆"}</span>
            <div>
              <div style={{fontSize:12,fontWeight:700,color:"#e7edf5"}}>{label}</div>
              <div style={{fontSize:9,color:"#5a728f",marginTop:1}}>
                {type==="daily"?"Fires Mon–Fri at 3:35 PM IST":"Fires every Friday at 3:35 PM IST"}
              </div>
            </div>
          </div>
          {/* Status badge */}
          <span style={{fontSize:9,fontWeight:700,color:dotColor,
            background:`${dotColor}18`,border:`1px solid ${dotColor}44`,
            borderRadius:0,padding:"2px 8px",letterSpacing:0.5}}>
            {dotLabel}
          </span>
        </div>

        {/* Stats row */}
        <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:8,fontSize:9,color:"#5a728f"}}>
          <span>Last run: <b style={{color:"#8ea2bf"}}>{fmtTime(lastRun)}</b></span>
          <span>Next: <b style={{color: enabled?"#49c2e4":"#5a728f"}}>{enabled?fmtNext(nextRun):"—"}</b></span>
          {alertsSent > 0 && <span>Last alerts: <b style={{color:"#2ed47a"}}>{alertsSent}</b></span>}
        </div>

        {/* Action buttons */}
        <div style={{display:"flex",gap:6}}>
          <button
            onClick={()=>toggle(type)}
            disabled={!!isToggling||!!isRunning}
            style={{
              flex:1, fontFamily:"inherit", fontSize:10, fontWeight:700,
              cursor:(isToggling||isRunning)?"default":"pointer",
              borderRadius:0, padding:"5px 0",
              background: enabled?"rgba(239,68,68,0.12)":"rgba(34,197,94,0.12)",
              border:`1px solid ${enabled?"#ff5b6c":"#2ed47a"}`,
              color: enabled?"#ff5b6c":"#2ed47a",
              opacity:(isToggling||isRunning)?0.5:1,
            }}>
            {isToggling?"…":enabled?"⏸ Pause":"▶ Enable"}
          </button>
          <button
            onClick={()=>runNow(type)}
            disabled={!!isRunNow||!!isRunning}
            style={{
              fontFamily:"inherit", fontSize:10, fontWeight:600,
              cursor:(isRunNow||isRunning)?"default":"pointer",
              borderRadius:0, padding:"5px 12px",
              background:"rgba(232,179,57,0.1)",
              border:"1px solid #e8b339",
              color:"#49c2e4",
              opacity:(isRunNow||isRunning)?0.5:1,
            }}>
            {isRunNow?"⟳ Running…":"▷ Run Now"}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:10}}>

      {/* Section header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:12,fontWeight:700,color:"#f59e0b"}}>
            📣 Daily & Weekly Telegram Alerts
          </div>
          <div style={{fontSize:10,color:"#5a728f",marginTop:2}}>
            Auto-scans all NSE + MCX watchlists after market close
          </div>
        </div>
        {bgStatus && (
          <span style={{fontSize:9,
            color:bgStatus.telegram_ok?"#2ed47a":"#ff5b6c",
            background:bgStatus.telegram_ok?"rgba(34,197,94,0.1)":"rgba(239,68,68,0.1)",
            border:`1px solid ${bgStatus.telegram_ok?"rgba(34,197,94,0.3)":"rgba(239,68,68,0.3)"}`,
            borderRadius:0, padding:"2px 7px"}}>
            {bgStatus.telegram_ok?"✓ Telegram OK":"⚠ No Telegram"}
          </span>
        )}
      </div>

      {!bgStatus ? (
        <div style={{fontSize:10,color:"#5a728f",padding:"8px 0",textAlign:"center"}}>
          Loading status…
        </div>
      ) : (
        <>
          <Row
            type="daily"
            label="Daily Alert Scan (1D)"
            enabled={bgStatus.daily_enabled}
            running={bgStatus.daily_running}
            lastRun={bgStatus.last_daily_run}
            nextRun={bgStatus.next_daily_run}
            alertsSent={bgStatus.daily_alerts_sent}
          />
          <Row
            type="weekly"
            label="Weekly Alert Scan (1W)"
            enabled={bgStatus.weekly_enabled}
            running={bgStatus.weekly_running}
            lastRun={bgStatus.last_weekly_run}
            nextRun={bgStatus.next_weekly_run}
            alertsSent={bgStatus.weekly_alerts_sent}
          />
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// VPS SCANNER PANEL — configure and monitor background scanner running on VPS
// ══════════════════════════════════════════════════════════════════════════════
function VpsScannerPanel({ cfg, setCfg, status, tgToken, tgChat, authed,
                           currentWatchlist, onClose }) {
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [tgTest,  setTgTest]  = useState(null);
  const [tgTesting, setTgTesting] = useState(false);
  const doTgTest = async()=>{
    setTgTesting(true); setTgTest(null);
    try{
      const r = await apiFetch("/vps-scanner/tg-test",{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({bot_token:tgToken||"", chat_id:tgChat||""})});
      setTgTest(r.ok?`✅ Sent via ${r.source} — check Telegram`:`⚠ ${r.error||"failed"}`);
    }catch(e){ setTgTest("⚠ "+(e.message||"failed")); }
    finally{ setTgTesting(false); }
  };

  const save = async()=>{
    if(!authed) return;
    setSaving(true);
    try {
      await apiFetch("/vps-scanner/config", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          ...cfg,
          kite_tg_token: tgToken,
          kite_tg_chat:  tgChat,
        })
      });
      setSaved(true);
      setTimeout(()=>setSaved(false), 2000);
    } catch(e) {
      alert("Save failed: " + e.message);
    } finally { setSaving(false); }
  };

  // Toggle + persist to the VPS immediately (so mobile toggles like Auto-Trade
  // take effect on the server without a separate Save tap — this was why
  // "Auto-Trade ON from mobile" wasn't actually trading).
  const savePatch = async(patch)=>{
    const next = {...cfg, ...patch};
    setCfg(next);
    if(!authed) return;
    try {
      await apiFetch("/vps-scanner/config", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ ...next, kite_tg_token: tgToken, kite_tg_chat: tgChat })
      });
      setSaved(true); setTimeout(()=>setSaved(false), 1500);
    } catch(e) { /* keep local state; user can hit Save to retry */ }
  };

  const kiteWlStr = (cfg.kite_watchlist||[]).join(", ");

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",
      display:"flex",alignItems:"center",justifyContent:"center",
      zIndex:500,padding:16}}>
      <div style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:12,
        width:"min(480px,95vw)",maxHeight:"85vh",overflow:"auto",
        WebkitOverflowScrolling:"touch"}}>

        {/* Header */}
        <div style={{padding:"14px 16px",borderBottom:"1px solid #1a2c44",
          display:"flex",justifyContent:"space-between",alignItems:"center",
          position:"sticky",top:0,background:"#0f1d30",zIndex:1}}>
          <div>
            <div style={{fontSize:14,fontWeight:700,color:"#e7edf5"}}>🖥️ VPS Background Scanner</div>
            <div style={{fontSize:10,color:"#8ea2bf",marginTop:2}}>
              Runs on your VPS 24/7 — even when browser is closed
            </div>
            <div style={{fontSize:9,color:"#49c2e4",marginTop:2}}>build {APP_BUILD}</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",
            color:"#8ea2bf",fontSize:18,cursor:"pointer"}}>✕</button>
        </div>

        <div style={{padding:"14px 16px",display:"flex",flexDirection:"column",gap:14}}>

          {/* Status bar */}
          {status&&(
            <div style={{background:"rgba(0,212,170,0.06)",border:"1px solid rgba(0,212,170,0.2)",
              borderRadius:0,padding:"8px 12px",fontSize:10}}>
              <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
                <span style={{color: cfg.auto_trade?"#ff5b6c":"#5a728f",fontWeight:700}}>
                  🤖 Auto-Trade: {cfg.auto_trade?"ON":"OFF"}
                </span>
                <span style={{color: cfg.kite_enabled?"#2ed47a":"#5a728f"}}>
                  📡 Equity: {cfg.kite_enabled?"RUNNING":"OFF"}
                </span>
                <span style={{color: cfg.mcx_enabled?"#e8b339":"#5a728f"}}>
                  🛢️ MCX: {cfg.mcx_enabled?"RUNNING":"OFF"}
                </span>
                <span style={{color: cfg.delta_enabled?"#49c2e4":"#5a728f"}}>
                  Δ Delta: {cfg.delta_enabled?"RUNNING":"OFF"}
                  {cfg.delta_enabled&&status.delta_mode&&(
                    <b style={{marginLeft:5,color:status.delta_mode==="auto"?"#ff5b6c":status.delta_mode==="paper"?"#60a5fa":"#8ea2bf"}}>
                      · {status.delta_mode==="paper"?"📝 PAPER":status.delta_mode==="auto"?"🤖 LIVE":"alerts"}
                      {status.delta_paper_open>0?` · ${status.delta_paper_open} open`:""}
                    </b>
                  )}
                </span>
                {(()=>{
                  const fmt=(ts)=> (ts&&ts>0)? new Date(ts*1000).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"}) : "—";
                  const ago=(ts)=>{ if(!ts||ts<=0||!status.server_now) return ""; const m=Math.max(0,Math.round((status.server_now-ts)/60)); return m<1?" (now)":` (${m}m ago)`; };
                  const parts=[];
                  if(cfg.kite_enabled)  parts.push(`NSE ${fmt(status.last_kite_scan)}`);
                  if(cfg.mcx_enabled)   parts.push(`MCX ${fmt(status.last_mcx_scan)}`);
                  if(cfg.delta_enabled) parts.push(`Δ ${fmt(status.delta_last_scan)}${ago(status.delta_last_scan)}`);
                  return parts.length? <span style={{color:"#8ea2bf"}}>Last scan — {parts.join(" · ")}</span> : null;
                })()}
                {cfg.auto_trade&&(
                  <span style={{color:"#e8b339"}}>
                    Trades today — Eq {status.eq_trades_today||0} · MCX {status.mcx_trades_today||0} · Δ {status.delta_trades_today||0}
                  </span>
                )}
              </div>
              {status.kite_session_ok===false&&(
                <div style={{marginTop:6,padding:"6px 8px",background:"rgba(255,91,108,0.12)",
                  border:"1px solid #ff5b6c",color:"#ff5b6c",fontWeight:700,fontSize:11}}>
                  ⚠️ Zerodha session expired — scanner is idle. Log in to Kite (in the app) to resume today's auto-scan / auto-trade.
                </div>
              )}
            </div>
          )}

          {/* ── MASTER AUTO-TRADE + VALIDATION PIPELINES ── */}
          <div style={{background: cfg.auto_trade?"rgba(248,81,73,0.08)":"#0b1624",
            border:`1px solid ${cfg.auto_trade?"rgba(248,81,73,0.4)":"#1a2c44"}`,borderRadius:0,padding:"12px 14px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:13,fontWeight:800,color:cfg.auto_trade?"#ff8a95":"#e7edf5"}}>
                  🤖 AUTO-TRADE (all markets)
                </div>
                <div style={{fontSize:11,color:"#8ea2bf",marginTop:2}}>
                  {cfg.auto_trade
                    ? "VPS places REAL orders on validated GO signals — even with the app closed"
                    : "Scan + alert only — no orders are placed"}
                </div>
              </div>
              <button onClick={()=>savePatch({auto_trade:!cfg.auto_trade})}
                style={{background:cfg.auto_trade?"rgba(248,81,73,0.2)":"rgba(232,179,57,0.07)",
                  border:`1px solid ${cfg.auto_trade?"#ff5b6c":"#23405f"}`,borderRadius:20,padding:"5px 16px",
                  color:cfg.auto_trade?"#ff5b6c":"#8ea2bf",fontFamily:"inherit",fontSize:11,fontWeight:800,cursor:"pointer"}}>
                {cfg.auto_trade?"● TRADING ON":"○ OFF"}
              </button>
            </div>
            <div style={{display:"flex",gap:8,marginTop:10,flexWrap:"wrap"}}>
              <button onClick={()=>savePatch({tech_validate:!(cfg.tech_validate!==false)})}
                style={{flex:1,minWidth:140,background:(cfg.tech_validate!==false)?"rgba(46,212,122,0.12)":"rgba(90,114,143,0.08)",
                  border:`1px solid ${(cfg.tech_validate!==false)?"#2ed47a":"#23405f"}`,borderRadius:0,padding:"6px 8px",
                  color:(cfg.tech_validate!==false)?"#2ed47a":"#8ea2bf",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                {(cfg.tech_validate!==false)?"✓ ":""}Technical + Validate
              </button>
              <button onClick={()=>savePatch({ai_validate:!cfg.ai_validate})}
                style={{flex:1,minWidth:140,background:cfg.ai_validate?"rgba(163,113,247,0.15)":"rgba(90,114,143,0.08)",
                  border:`1px solid ${cfg.ai_validate?"#a78bfa":"#23405f"}`,borderRadius:0,padding:"6px 8px",
                  color:cfg.ai_validate?"#a78bfa":"#8ea2bf",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                {cfg.ai_validate?"✓ ":""}AI Signal + Validate
              </button>
            </div>
            <div style={{fontSize:9,color:"#8ea2bf",marginTop:8,lineHeight:1.5}}>
              Every signal is re-checked by the validation engine; only <b style={{color:"#2ed47a"}}>GO</b> entries alert{(cfg.auto_trade||cfg.paper_trade)?" and trade":""}. Applies to Equity &amp; MCX. <b style={{color:"#49c2e4"}}>Delta has its own Paper / Auto-Trade switch</b> below.
            </div>
            <div style={{fontSize:9,color:"#2ed47a",marginTop:6,padding:"6px 8px",background:"rgba(46,212,122,0.06)",border:"1px solid rgba(46,212,122,0.25)",lineHeight:1.5}}>
              🔬 <b>Signal Engine v2</b> (equity · MCX · delta): a trade fires only when a <b>volume spike</b> + candle direction + <b>order-book imbalance</b> all agree, plus a context — <b>D/S zone</b>, <b>TSI cross</b>, or <b>RSI extreme</b>. Entry is pulled to the strongest bid/ask level in the depth. Tunable via config (vol_spike_thr, vol_dom_pct, rsi bands, entry_depth_pct).
            </div>

            {/* SELL CE/PE daily suggestions — alerts only, never auto-trades */}
            <div style={{marginTop:10,padding:"8px 10px",background:"#0b1624",border:"1px solid #1a2c44",borderLeft:"3px solid #e8b339"}}>
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                <span style={{fontSize:11,fontWeight:700,color:"#e8b339"}}>💰 Sell CE/PE Daily Ideas</span>
                <button onClick={()=>savePatch({sell_alerts_enabled:!cfg.sell_alerts_enabled})}
                  style={{background:cfg.sell_alerts_enabled?"rgba(232,179,57,0.18)":"rgba(90,114,143,0.08)",
                    border:`1px solid ${cfg.sell_alerts_enabled?"#e8b339":"#23405f"}`,borderRadius:0,padding:"4px 12px",
                    color:cfg.sell_alerts_enabled?"#e8b339":"#8ea2bf",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                  {cfg.sell_alerts_enabled?"✓ ON":"OFF"}
                </button>
                <button onClick={()=>{apiFetch("/vps-scanner/sell-suggestions?send_telegram=true").catch(()=>{});}}
                  style={{marginLeft:"auto",background:"transparent",border:"1px solid #23405f",borderRadius:0,padding:"4px 10px",
                    color:"#8ea2bf",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}
                  title="Run the sell-suggestion scan now and push the ideas to your Telegram">
                  Run now →
                </button>
              </div>
              <div style={{fontSize:9,color:"#8ea2bf",marginTop:6,lineHeight:1.5}}>
                Daily at 09:40 IST: for NIFTY, BANKNIFTY, FINNIFTY, MIDCPNIFTY & SENSEX, suggests an OTM strike to <b>sell</b> for the nearest weekly — beyond the IV expected-move, targeting ~{cfg.sell_min_roi_pct||3}% ROI/week on margin, with IV, OI walls & an AI news read. <b style={{color:"#e8b339"}}>Alert only — never auto-trades.</b>
              </div>
              <VpsSellIdeas />
            </div>

            {/* Scan selector — choose which primary scans run on the VPS */}
            <div style={{marginTop:10,padding:"8px 10px",background:"#0b1624",border:"1px solid #1a2c44"}}>
              <div style={{fontSize:10,fontWeight:700,color:"#8ea2bf",marginBottom:6}}>ACTIVE SCANS · choose signals</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {[[1,"TSI cross"],[2,"RSI"],[3,"Vol+RSI"],[4,"EMA"]].map(([id,label])=>{
                  const cur = cfg.active_scans||[1,2,3,4];
                  const on = cur.includes(id);
                  return (
                    <button key={id} onClick={()=>{
                      const next = on ? cur.filter(x=>x!==id) : [...cur,id].sort();
                      if(next.length===0) return;  // keep at least one scan on
                      savePatch({active_scans:next});
                    }} style={{flex:"1 0 64px",background:on?"rgba(46,212,122,0.15)":"rgba(90,114,143,0.08)",
                      border:`1px solid ${on?"#2ed47a":"#23405f"}`,borderRadius:0,padding:"6px 4px",
                      color:on?"#2ed47a":"#8ea2bf",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                      {on?"✓ ":""}{label}
                    </button>
                  );
                })}
              </div>
              <div style={{display:"flex",gap:6,alignItems:"center",marginTop:8}}>
                <span style={{fontSize:10,color:"#8ea2bf",flex:1}}>Demand/Supply zone</span>
                {[["off","Off"],["support","Support only"]].map(([m,label])=>(
                  <button key={m} onClick={()=>savePatch({ds_zone_mode:m})}
                    style={{background:(cfg.ds_zone_mode||"off")===m?"rgba(167,139,250,0.18)":"rgba(90,114,143,0.08)",
                      border:`1px solid ${(cfg.ds_zone_mode||"off")===m?"#a78bfa":"#23405f"}`,borderRadius:0,padding:"4px 10px",
                      color:(cfg.ds_zone_mode||"off")===m?"#a78bfa":"#8ea2bf",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                    {label}
                  </button>
                ))}
              </div>
              <div style={{fontSize:9,color:"#5a728f",marginTop:6,lineHeight:1.5}}>
                Needs {cfg.kite_min_signals||2}+ of the selected scans to agree. D/S zone never counts as a signal — "Support only" just adds confirmation strength (default Off, as it produced false signals).
              </div>
            </div>
            {/* Telegram test — confirms VPS alerts actually send */}
            <div style={{display:"flex",gap:8,alignItems:"center",marginTop:10}}>
              <button onClick={doTgTest} disabled={tgTesting}
                style={{background:"rgba(73,194,228,0.1)",border:"1px solid rgba(73,194,228,0.4)",borderRadius:0,
                  padding:"6px 12px",color:"#49c2e4",fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                {tgTesting?"Sending…":"🔔 Send VPS Telegram test"}
              </button>
              {tgTest&&<span style={{fontSize:10,color:tgTest.startsWith("✅")?"#2ed47a":"#ff5b6c"}}>{tgTest}</span>}
            </div>

            {/* Paper mode — VPS records paper trades, no real orders */}
            <div onClick={()=>savePatch({paper_trade:!cfg.paper_trade})}
              title="Paper mode: the VPS records paper trades (tracked + closed server-side) instead of placing real orders. Paper runs unlimited and never uses the real daily trade cap."
              style={{display:"flex",alignItems:"center",gap:8,marginTop:10,padding:"7px 10px",cursor:"pointer",
                background:cfg.paper_trade?"rgba(96,165,250,0.1)":"#0b1624",
                border:`1px solid ${cfg.paper_trade?"#60a5fa":"#1a2c44"}`,borderRadius:0}}>
              <span style={{fontSize:13}}>{cfg.paper_trade?"📝":"💵"}</span>
              <span style={{fontSize:11,fontWeight:700,color:cfg.paper_trade?"#60a5fa":"#e7edf5"}}>
                {cfg.paper_trade?"PAPER MODE — ON (no real orders)":"PAPER MODE — OFF (live orders)"}
              </span>
              <span style={{fontSize:9,color:"#8ea2bf",marginLeft:"auto"}}>
                {cfg.paper_trade?"unlimited · doesn't use daily cap":"real orders when Auto-Trade on"}
              </span>
            </div>
            {/* ── SPREAD MODE (equity/index options → defined-risk verticals) ── */}
            <div onClick={()=>savePatch({nse_spread_mode:!cfg.nse_spread_mode})}
              title="Spread mode: equity & index option auto-trades use a 2-leg vertical spread chosen by signal + IV regime instead of a naked CE/PE buy."
              style={{display:"flex",alignItems:"center",gap:8,marginTop:8,padding:"7px 10px",cursor:"pointer",
                background:cfg.nse_spread_mode?"rgba(163,113,247,0.1)":"#0b1624",
                border:`1px solid ${cfg.nse_spread_mode?"#a371f7":"#1a2c44"}`,borderRadius:0}}>
              <span style={{fontSize:13}}>{cfg.nse_spread_mode?"🎚️":"🎯"}</span>
              <span style={{fontSize:11,fontWeight:700,color:cfg.nse_spread_mode?"#a371f7":"#e7edf5"}}>
                {cfg.nse_spread_mode?"SPREAD MODE — ON (bull/bear call/put spreads)":"SPREAD MODE — OFF (naked CE/PE buy)"}
              </span>
              <span style={{fontSize:9,color:"#8ea2bf",marginLeft:"auto"}}>
                {cfg.nse_spread_mode?"IV-aware · defined risk":"click to enable"}
              </span>
            </div>
            {cfg.nse_spread_mode&&(
              <div style={{marginTop:6,padding:"7px 10px",background:"#0b1624",border:"1px solid #1a2c44"}}>
                <div style={{fontSize:9,color:"#8ea2bf",lineHeight:1.6,marginBottom:6}}>
                  Bullish + <b>low IV</b> → <b style={{color:"#2ed47a"}}>Bull Call</b> (debit, gain on move+IV↑) · Bullish + <b>high IV</b> → <b style={{color:"#2ed47a"}}>Bull Put</b> (credit, decay)<br/>
                  Bearish + <b>high IV</b> → <b style={{color:"#ff8a95"}}>Bear Call</b> (credit, decay) · Bearish + <b>low IV</b> → <b style={{color:"#ff8a95"}}>Bear Put</b> (debit, gain on move+IV↑)<br/>
                  Buys the long leg first &amp; <b>waits for the fill</b> before selling the short (margin benefit). Spread-mode is strict — no naked CE/PE fallback. Respects Paper / Auto-Trade. <b style={{color:"#e8b339"}}>Test in Paper first.</b>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:10,color:"#8ea2bf"}}>Width (strikes apart):</span>
                  {[1,2,3,4,5].map(n=>(
                    <button key={n} onClick={()=>savePatch({nse_spread_strikes:n})}
                      style={{width:26,height:24,background:(cfg.nse_spread_strikes||3)===n?"rgba(163,113,247,0.2)":"#0f1d30",
                        border:`1px solid ${(cfg.nse_spread_strikes||3)===n?"#a371f7":"#23405f"}`,borderRadius:0,
                        color:(cfg.nse_spread_strikes||3)===n?"#a371f7":"#8ea2bf",fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                      {n}
                    </button>
                  ))}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6,marginTop:6,flexWrap:"wrap"}}>
                  <span style={{fontSize:10,color:"#8ea2bf"}}>Fill wait (per leg):</span>
                  {[[60,"1m"],[120,"2m"],[150,"2.5m"],[240,"4m"],[300,"5m"]].map(([s,l])=>(
                    <button key={s} onClick={()=>savePatch({nse_spread_fill_wait:s})}
                      style={{padding:"3px 8px",background:(Number(cfg.nse_spread_fill_wait)||150)===s?"rgba(163,113,247,0.2)":"#0f1d30",
                        border:`1px solid ${(Number(cfg.nse_spread_fill_wait)||150)===s?"#a371f7":"#23405f"}`,borderRadius:0,
                        color:(Number(cfg.nse_spread_fill_wait)||150)===s?"#a371f7":"#8ea2bf",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                      {l}
                    </button>
                  ))}
                </div>
                <div style={{fontSize:9,color:"#5a728f",marginTop:5}}>Legs are placed as <b>marketable limits</b> (cross the bid/ask by ~2%) so they fill instead of resting — far fewer cancellations. If a leg still doesn't fill within the wait, the trade is cancelled/unwound cleanly.</div>
              </div>
            )}
            {/* ── Min signal strength gate (Equity + MCX) ── */}
            <div style={{marginTop:8,padding:"7px 10px",background:"#0b1624",border:"1px solid #1a2c44"}}>
              <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                <span style={{fontSize:10,color:"#8ea2bf"}}>Min signal strength (Equity + MCX) — trade only ⭐≥</span>
                {[7,8,9,10].map(n=>(
                  <button key={n} onClick={()=>savePatch({min_signal_strength:n})}
                    style={{width:26,height:24,background:(Number(cfg.min_signal_strength)||8)===n?"rgba(46,212,122,0.18)":"#0f1d30",
                      border:`1px solid ${(Number(cfg.min_signal_strength)||8)===n?"#2ed47a":"#23405f"}`,borderRadius:0,
                      color:(Number(cfg.min_signal_strength)||8)===n?"#2ed47a":"#8ea2bf",fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                    {n}
                  </button>
                ))}
              </div>
              <div style={{fontSize:9,color:"#5a728f",marginTop:4}}>Score-based: <b>1 scan is enough to fire a signal</b>; the trade needs total ⭐ ≥ this. ⭐ = sum of scan ratings (TSI 5, RSI 4, Vol 5) + D/S zone (2). 8 ⇒ ~2 scans agreeing. Weaker signals still alert.</div>
            </div>
            {/* ── Dynamic TP for STRONG signals (Equity + MCX + Delta) ── */}
            <div style={{marginTop:8,padding:"7px 10px",background:"#0b1624",border:"1px solid #1a2c44"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:6,flexWrap:"wrap"}}>
                <span style={{fontSize:10,color:"#8ea2bf"}}>🚀 Dynamic TP on strong signals — let winners run</span>
                <button onClick={()=>savePatch({dynamic_tp_strong:!(cfg.dynamic_tp_strong!==false)})}
                  style={{background:(cfg.dynamic_tp_strong!==false)?"rgba(46,212,122,0.18)":"#0f1d30",
                    border:`1px solid ${(cfg.dynamic_tp_strong!==false)?"#2ed47a":"#23405f"}`,borderRadius:20,padding:"4px 14px",
                    color:(cfg.dynamic_tp_strong!==false)?"#2ed47a":"#8ea2bf",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                  {(cfg.dynamic_tp_strong!==false)?"● ON":"○ OFF"}
                </button>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",marginTop:6}}>
                <span style={{fontSize:10,color:"#8ea2bf"}}>Treat as STRONG when ⭐≥</span>
                {[8,9,10,11].map(n=>(
                  <button key={n} onClick={()=>savePatch({strong_signal_score:n})}
                    style={{width:26,height:24,background:(Number(cfg.strong_signal_score)||9)===n?"rgba(163,113,247,0.2)":"#0f1d30",
                      border:`1px solid ${(Number(cfg.strong_signal_score)||9)===n?"#a371f7":"#23405f"}`,borderRadius:0,
                      color:(Number(cfg.strong_signal_score)||9)===n?"#a371f7":"#8ea2bf",fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                    {n}
                  </button>
                ))}
              </div>
              <div style={{fontSize:9,color:"#5a728f",marginTop:4}}>When a trade's originating signal is ⭐≥ this, the fixed TP is <b>not</b> booked — the TP level is locked in as a hard floor and the <b>trailing SL</b> rides the move for maximum profit (exits only when the trail gives back). Weaker signals keep the fixed TP. Covers Equity (MIS), MCX, naked options &amp; Delta; GTT/CNC delivery trades keep the fixed TP.</div>
            </div>
          </div>

          {/* ── MCX scanner ── */}
          <div style={{background:"#0b1624",borderRadius:0,padding:"12px 14px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:cfg.mcx_enabled?10:0}}>
              <div style={{fontSize:12,fontWeight:700,color:"#e8b339"}}>🛢️ MCX Scanner (scan + validate{cfg.auto_trade?" + trade":""})</div>
              <button onClick={()=>setCfg(p=>({...p,mcx_enabled:!p.mcx_enabled}))}
                style={{background:cfg.mcx_enabled?"rgba(22,163,74,0.15)":"rgba(232,179,57,0.07)",
                  border:`1px solid ${cfg.mcx_enabled?"#2ed47a":"#23405f"}`,borderRadius:20,padding:"4px 14px",
                  color:cfg.mcx_enabled?"#2ed47a":"#8ea2bf",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                {cfg.mcx_enabled?"● ON":"○ OFF"}
              </button>
            </div>
            {cfg.mcx_enabled&&(
              <textarea value={(cfg.mcx_watchlist||[]).join(", ")}
                onChange={e=>setCfg(p=>({...p,mcx_watchlist:e.target.value.split(",").map(s=>s.trim().toUpperCase()).filter(Boolean)}))}
                placeholder="CRUDEOIL, GOLD, SILVER, NATURALGAS, COPPER..." rows={2}
                style={{width:"100%",background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,padding:"6px 8px",color:"#e7edf5",fontFamily:"inherit",fontSize:11,resize:"vertical",boxSizing:"border-box"}}/>
            )}
          </div>

          {/* ── LIVE TP/SL TRACKER ── */}
          <div style={{background:"#0b1624",borderRadius:0,padding:"12px 14px"}}>
            <VpsTpSlTracker authed={authed} />
          </div>

          {/* ── VPS PAPER TRADES LOG ── */}
          <div style={{background:"#0b1624",borderRadius:0,padding:"12px 14px"}}>
            <VpsPaperLog authed={authed} />
          </div>

          {/* ── DAILY / WEEKLY ALERT STATUS ── */}
          <div style={{background:"#0b1624",borderRadius:0,padding:"12px 14px"}}>
            <DailyWeeklyScanStatus authed={authed} />
          </div>

          {/* ── KITE SCANNER section ── */}
          <div style={{background:"#0b1624",borderRadius:0,padding:"12px 14px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:"#e8b339"}}>
                  📡 Equity NSE Scanner
                </div>
                <div style={{fontSize:12,color:"#8ea2bf",marginTop:2}}>
                  Scan + validate → Telegram alerts.{cfg.auto_trade?" Places real orders on GO (Auto-Trade ON).":" Enable Auto-Trade above to place orders."}
                </div>
              </div>
              <button onClick={()=>setCfg(p=>({...p,kite_enabled:!p.kite_enabled}))}
                style={{background:cfg.kite_enabled?"rgba(22,163,74,0.15)":"rgba(232,179,57,0.07)",
                  border:`1px solid ${cfg.kite_enabled?"#2ed47a":"#23405f"}`,
                  borderRadius:20,padding:"4px 14px",
                  color:cfg.kite_enabled?"#2ed47a":"#8ea2bf",
                  fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                {cfg.kite_enabled?"● ON":"○ OFF"}
              </button>
            </div>

            {cfg.kite_enabled&&(
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                <div>
                  <div style={{fontSize:12,color:"#8ea2bf",marginBottom:4}}>
                    WATCHLIST (comma separated symbols)
                  </div>
                  <textarea
                    value={kiteWlStr}
                    onChange={e=>setCfg(p=>({...p,
                      kite_watchlist: e.target.value.split(",").map(s=>s.trim().toUpperCase()).filter(Boolean)
                    }))}
                    placeholder="RELIANCE, TCS, INFY, NIFTY 50..."
                    rows={3}
                    style={{width:"100%",background:"#0f1d30",border:"1px solid #23405f",
                      borderRadius:0,padding:"7px 10px",color:"#e7edf5",
                      fontFamily:"inherit",fontSize:11,outline:"none",
                      resize:"vertical",boxSizing:"border-box"}}/>
                  {currentWatchlist?.length>0&&(
                    <button onClick={()=>setCfg(p=>({...p,kite_watchlist:currentWatchlist}))}
                      style={{marginTop:4,background:"none",border:"none",color:"#6b7280",
                        fontFamily:"inherit",fontSize:12,cursor:"pointer",padding:0}}>
                      ← Use current scanner watchlist ({currentWatchlist.length} symbols)
                    </button>
                  )}
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                  <span style={{fontSize:12,color:"#8ea2bf"}}>Timeframe:</span>
                  {["5min","15min","30min","1hour","1day"].map(tf=>(
                    <button key={tf} onClick={()=>setCfg(p=>({...p,kite_timeframe:tf,mcx_timeframe:tf}))}
                      style={{background:cfg.kite_timeframe===tf?"rgba(0,212,170,0.12)":"transparent",
                        border:`1px solid ${cfg.kite_timeframe===tf?"#e8b339":"#23405f"}`,
                        borderRadius:0,padding:"2px 8px",color:cfg.kite_timeframe===tf?"#e8b339":"#8ea2bf",
                        fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
                      {tf}
                    </button>
                  ))}
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                  <span style={{fontSize:12,color:"#8ea2bf"}}>Min signals to trade:</span>
                  {[1,2,3,4].map(n=>(
                    <button key={n} onClick={()=>setCfg(p=>({...p,kite_min_signals:n}))}
                      style={{background:(cfg.kite_min_signals||2)===n?"rgba(232,179,57,0.15)":"transparent",
                        border:`1px solid ${(cfg.kite_min_signals||2)===n?"#e8b339":"#23405f"}`,
                        borderRadius:0,padding:"2px 10px",color:(cfg.kite_min_signals||2)===n?"#e8b339":"#8ea2bf",
                        fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                      {n}
                    </button>
                  ))}
                  <span style={{fontSize:10,color:"#5a728f"}}>lower = more trades</span>
                </div>
                <div style={{fontSize:12,color:tgToken?"#2ed47a":"#ff5b6c"}}>
                  {tgToken
                    ? `✓ Telegram configured — alerts will send to chat ${tgChat}`
                    : "⚠ No Telegram token set — configure in Settings to receive alerts"}
                </div>
              </div>
            )}
          </div>

          {/* ── DELTA AI SCANNER section ── */}
          <div style={{background:"#0b1624",borderRadius:0,padding:"12px 14px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:"#49c2e4"}}>
                  Δ Delta AI Scanner
                </div>
                <div style={{fontSize:12,color:"#8ea2bf",marginTop:2}}>
                  Own Paper / Auto-Trade switch (separate from NSE/MCX). Max {cfg.delta_max_trades} trades/day.
                </div>
              </div>
              <button onClick={()=>setCfg(p=>({...p,delta_enabled:!p.delta_enabled}))}
                style={{background:cfg.delta_enabled?"rgba(163,113,247,0.15)":"rgba(232,179,57,0.07)",
                  border:`1px solid ${cfg.delta_enabled?"#49c2e4":"#23405f"}`,
                  borderRadius:20,padding:"4px 14px",
                  color:cfg.delta_enabled?"#49c2e4":"#8ea2bf",
                  fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                {cfg.delta_enabled?"● ON":"○ OFF"}
              </button>
            </div>

            {cfg.delta_enabled&&(
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {/* Delta trade mode — SEPARATE from the NSE/MCX master switches.
                    Paper = track the strategy with no real orders; Auto = live orders. */}
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  <button onClick={()=>setCfg(p=>({...p,delta_paper_trade:!p.delta_paper_trade,
                      delta_auto_trade:!p.delta_paper_trade?false:p.delta_auto_trade}))}
                    style={{flex:1,minWidth:150,background:cfg.delta_paper_trade?"rgba(96,165,250,0.14)":"#0f1d30",
                      border:`1px solid ${cfg.delta_paper_trade?"#60a5fa":"#23405f"}`,borderRadius:0,padding:"6px 10px",
                      color:cfg.delta_paper_trade?"#60a5fa":"#8ea2bf",fontFamily:"inherit",fontSize:11,fontWeight:800,cursor:"pointer"}}>
                    {cfg.delta_paper_trade?"📝 PAPER — ON":"📝 Paper — OFF"}
                  </button>
                  <button onClick={()=>setCfg(p=>({...p,delta_auto_trade:!p.delta_auto_trade,
                      delta_paper_trade:!p.delta_auto_trade?false:p.delta_paper_trade}))}
                    style={{flex:1,minWidth:150,background:cfg.delta_auto_trade?"rgba(248,81,73,0.18)":"#0f1d30",
                      border:`1px solid ${cfg.delta_auto_trade?"#ff5b6c":"#23405f"}`,borderRadius:0,padding:"6px 10px",
                      color:cfg.delta_auto_trade?"#ff5b6c":"#8ea2bf",fontFamily:"inherit",fontSize:11,fontWeight:800,cursor:"pointer"}}>
                    {cfg.delta_auto_trade?"🤖 AUTO-TRADE — ON":"🤖 Auto-Trade — OFF"}
                  </button>
                </div>
                {/* Mode-aware status line */}
                {cfg.delta_auto_trade ? (
                  <div style={{background:"rgba(248,81,73,0.07)",border:"1px solid rgba(248,81,73,0.2)",
                    borderRadius:0,padding:"7px 10px",fontSize:12,color:"#ff5b6c"}}>
                    ⚠ LIVE — real orders will be placed on Delta Exchange automatically on GO signals.
                  </div>
                ) : cfg.delta_paper_trade ? (
                  <div style={{background:"rgba(96,165,250,0.07)",border:"1px solid rgba(96,165,250,0.25)",
                    borderRadius:0,padding:"7px 10px",fontSize:12,color:"#60a5fa"}}>
                    📝 PAPER — <b>every</b> validated GO signal is tracked with TP/SL (the ⭐strength gate applies to LIVE only, so paper captures all setups). Runs <b>unlimited</b>, never uses the live cap. Track the strategy here, then flip to Auto-Trade.
                  </div>
                ) : (
                  <div style={{background:"#0f1d30",border:"1px solid #23405f",
                    borderRadius:0,padding:"7px 10px",fontSize:12,color:"#8ea2bf"}}>
                    Alerts only — Delta scans + validates to Telegram, but neither paper nor live trades are recorded. Turn on Paper to start tracking.
                  </div>
                )}

                {/* Watchlist — top 5 crypto */}
                <div>
                  <div style={{fontSize:12,color:"#8ea2bf",marginBottom:4}}>WATCHLIST (top 5)</div>
                  <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                    {["BTCUSD","ETHUSD","SOLUSD","BNBUSD","XRPUSD"].map(sym=>{
                      const active=(cfg.delta_watchlist||[]).includes(sym);
                      return (
                        <button key={sym} onClick={()=>setCfg(p=>({...p,
                          delta_watchlist: active
                            ? p.delta_watchlist.filter(s=>s!==sym)
                            : [...(p.delta_watchlist||[]),sym]
                        }))} style={{background:active?"rgba(163,113,247,0.15)":"#0f1d30",
                          border:`1px solid ${active?"#49c2e4":"#23405f"}`,
                          borderRadius:0,padding:"3px 9px",
                          color:active?"#49c2e4":"#8ea2bf",
                          fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
                          {sym.replace("USD","")}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Delta timeframe — 5m / 15m / 1h */}
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <span style={{fontSize:12,color:"#8ea2bf"}}>Timeframe:</span>
                  {["5m","15m","1h"].map(tf=>(
                    <button key={tf} onClick={()=>setCfg(p=>({...p,delta_timeframe:tf}))}
                      style={{background:(cfg.delta_timeframe||"15m")===tf?"rgba(163,113,247,0.15)":"transparent",
                        border:`1px solid ${(cfg.delta_timeframe||"15m")===tf?"#a371f7":"#23405f"}`,
                        borderRadius:0,padding:"2px 10px",color:(cfg.delta_timeframe||"15m")===tf?"#a371f7":"#8ea2bf",
                        fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                      {tf}
                    </button>
                  ))}
                </div>

                {/* Buy options vs futures + expiry */}
                <div onClick={()=>setCfg(p=>({...p,delta_trade_options:!(p.delta_trade_options!==false)}))}
                  style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"6px 8px",
                    background:(cfg.delta_trade_options!==false)?"rgba(46,212,122,0.08)":"rgba(255,91,108,0.06)",
                    border:`1px solid ${(cfg.delta_trade_options!==false)?"rgba(46,212,122,0.4)":"rgba(255,91,108,0.35)"}`}}>
                  <span style={{fontSize:12}}>{(cfg.delta_trade_options!==false)?"🎯":"⚠️"}</span>
                  <span style={{fontSize:11,fontWeight:700,color:(cfg.delta_trade_options!==false)?"#2ed47a":"#ff8a95"}}>
                    {(cfg.delta_trade_options!==false)?"BUY OPTIONS (no liquidation)":"BUY FUTURES (leverage/liquidation risk)"}
                  </span>
                  <span style={{fontSize:9,color:"#8ea2bf",marginLeft:"auto"}}>
                    {(cfg.delta_trade_options!==false)?"BUY→call, SELL→put · BTC/ETH":"click to switch"}
                  </span>
                </div>
                {(cfg.delta_trade_options!==false)&&(
                  <div style={{display:"flex",gap:6,alignItems:"center"}}>
                    <span style={{fontSize:12,color:"#8ea2bf"}}>Option expiry:</span>
                    {[[0,"Nearest"],[7,"7d+"],[14,"14d+"]].map(([d,l])=>(
                      <button key={d} onClick={()=>setCfg(p=>({...p,delta_option_min_days:d}))}
                        style={{background:(cfg.delta_option_min_days||0)===d?"rgba(163,113,247,0.15)":"transparent",
                          border:`1px solid ${(cfg.delta_option_min_days||0)===d?"#a371f7":"#23405f"}`,
                          borderRadius:0,padding:"2px 10px",color:(cfg.delta_option_min_days||0)===d?"#a371f7":"#8ea2bf",
                          fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer"}}>{l}</button>
                    ))}
                  </div>
                )}

                {/* Delta min signal strength gate */}
                <div style={{marginBottom:2}}>
                  <div style={{fontSize:12,color:"#8ea2bf",marginBottom:4}}>MIN SIGNAL STRENGTH — trade only ⭐≥</div>
                  <div style={{display:"flex",gap:4}}>
                    {[6,7,8,9].map(n=>(
                      <button key={n} onClick={()=>setCfg(p=>({...p,delta_min_strength:n}))}
                        style={{flex:1,background:(Number(cfg.delta_min_strength)||7)===n?"rgba(73,194,228,0.18)":"#0f1d30",
                          border:`1px solid ${(Number(cfg.delta_min_strength)||7)===n?"#49c2e4":"#23405f"}`,borderRadius:0,padding:"4px 0",
                          color:(Number(cfg.delta_min_strength)||7)===n?"#49c2e4":"#8ea2bf",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Max trades & min confidence */}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  <div>
                    <div style={{fontSize:12,color:"#8ea2bf",marginBottom:4}}>MAX TRADES/DAY</div>
                    <div style={{display:"flex",gap:4}}>
                      {[5,10,20,30].map(n=>(
                        <button key={n} onClick={()=>setCfg(p=>({...p,delta_max_trades:n}))}
                          style={{flex:1,background:(cfg.delta_max_trades||10)===n?"rgba(220,38,38,0.15)":"#0f1d30",
                            border:`1px solid ${(cfg.delta_max_trades||10)===n?"#ff5b6c":"#23405f"}`,
                            borderRadius:0,padding:"4px 0",
                            color:(cfg.delta_max_trades||10)===n?"#ff5b6c":"#8ea2bf",
                            fontFamily:"inherit",fontSize:10,cursor:"pointer"}}>
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize:12,color:"#8ea2bf",marginBottom:4}}>
                      MIN CONFIDENCE: {cfg.delta_min_confidence||80}%
                    </div>
                    <input type="range" min={70} max={95} step={5}
                      value={cfg.delta_min_confidence||80}
                      onChange={e=>setCfg(p=>({...p,delta_min_confidence:parseInt(e.target.value)}))}
                      style={{width:"100%",accentColor:"#49c2e4"}}/>
                  </div>
                </div>

                {/* Margin allocation + min trade */}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  <div>
                    <div style={{fontSize:12,color:"#8ea2bf",marginBottom:4}}>
                      MARGIN PER TRADE: {cfg.delta_margin_pct||10}% of balance
                    </div>
                    <input type="range" min={5} max={30} step={5}
                      value={cfg.delta_margin_pct||10}
                      onChange={e=>setCfg(p=>({...p,delta_margin_pct:parseInt(e.target.value)}))}
                      style={{width:"100%",accentColor:"#49c2e4"}}/>
                    <div style={{fontSize:11,color:"#5a728f",marginTop:2}}>
                      Size auto-calculated from balance × this %
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize:12,color:"#8ea2bf",marginBottom:4}}>MIN TRADE (USD)</div>
                    <div style={{display:"flex",gap:4}}>
                      {[5,10,20,50].map(n=>(
                        <button key={n} onClick={()=>setCfg(p=>({...p,delta_min_trade_usd:n}))}
                          style={{flex:1,background:(cfg.delta_min_trade_usd||10)===n?"rgba(163,113,247,0.2)":"#0f1d30",
                            border:`1px solid ${(cfg.delta_min_trade_usd||10)===n?"#49c2e4":"#23405f"}`,
                            borderRadius:0,padding:"4px 0",
                            color:(cfg.delta_min_trade_usd||10)===n?"#49c2e4":"#8ea2bf",
                            fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
                          ${n}
                        </button>
                      ))}
                    </div>
                    <div style={{fontSize:11,color:"#5a728f",marginTop:2}}>
                      Skip if margin &lt; this amount
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Save button */}
          <button onClick={save} disabled={saving}
            style={{background:saved
              ?"rgba(63,185,80,0.2)"
              :"linear-gradient(135deg,rgba(0,212,170,0.25),rgba(0,112,243,0.2))",
              border:`1px solid ${saved?"#2ed47a":"rgba(0,212,170,0.4)"}`,
              borderRadius:0,padding:"10px 0",
              color:saved?"#2ed47a":"#e8b339",
              fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer"}}>
            {saving?"Saving to VPS...":saved?"✓ Saved — VPS scanner updated":"💾 Save & Activate on VPS"}
          </button>

          <div style={{fontSize:12,color:"#5a728f",textAlign:"center",lineHeight:1.5}}>
            Scans every 15 minutes · Mon–Fri 9:15 AM – 3:30 PM IST only<br/>
            VPS continues running even when this browser is closed
          </div>
        </div>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// SUBSCRIPTION PAGE — shown when user has no active subscription
// ══════════════════════════════════════════════════════════════════════════════
function SubscriptionPage({ fbUser, fbRecord, onActivated, onLogout }) {
  const [plans,     setPlans]    = useState([]);
  const [loading,   setLoading]  = useState(true);
  const [paying,    setPaying]   = useState(false);
  const [msg,       setMsg]      = useState(null);
  const [demoUsed,  setDemoUsed] = useState(false);

  useEffect(()=>{
    apiFetch('/payment/plans').then(d=>{
      setPlans(d.plans||[]);
      setLoading(false);
    }).catch(()=>setLoading(false));
    // Check if demo already used
    if(fbRecord?.demoUsed) setDemoUsed(true);
  },[]);

  const activatePlan = async(plan)=>{
    setPaying(true); setMsg(null);
    try {
      if(plan.price===0) {
        // Check both account-level AND device-level demo usage
        const deviceId   = getDeviceId();
        const devUsed    = await checkDeviceDemoUsed(deviceId);
        if(demoUsed || devUsed) {
          setMsg({type:'error',
            text:'Demo already used on this account or device. Please choose a paid plan.'});
          return;
        }
        const res = await apiFetch('/payment/activate-demo', {method:'POST'});
        if(res.ok) {
          await markDeviceDemoUsed(deviceId, fbUser.uid, fbUser.email);
          // expiresAt / plan / demoUsed are written by the server now (protected fields);
          // only the device id is ours to record.
          await fbUpdateUser(fbUser.uid, { deviceId, updatedAt: new Date().toISOString() }).catch(()=>{});
          setMsg({type:'ok', text:'✓ Demo activated! Enjoy 1 day of free access.'});
          setTimeout(()=>onActivated(), 1500);
        }
        return;
      }

      // Paid plan — create Razorpay order
      const loaded = await loadRazorpay();
      if(!loaded) { setMsg({type:'error', text:'Payment gateway failed to load. Check internet connection.'}); return; }

      const order = await apiFetch('/payment/create-order', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({plan_id:plan.id, email:fbUser.email})
      });
      if(!order.ok) throw new Error(order.error||'Order creation failed');

      // Open Razorpay checkout
      await new Promise((resolve, reject)=>{
        const rzp = new window.Razorpay({
          key:         order.key_id,
          amount:      order.amount,
          currency:    order.currency,
          name:        'RABTRADE',
          description: plan.name + ' — ' + plan.desc,
          order_id:    order.order_id,
          prefill:     {email: fbUser.email, name: fbRecord?.displayName||''},
          theme:       {color:'#e8b339'},
          handler: async(response)=>{
            try {
              // Verify payment on server
              const verify = await apiFetch('/payment/verify', {
                method:'POST', headers:{'Content-Type':'application/json'},
                body: JSON.stringify({
                  razorpay_order_id:   response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature:  response.razorpay_signature,
                  plan_id: plan.id,
                })
              });
              if(!verify.ok) throw new Error('Verification failed');

              // The server has already written expiresAt / plan / lastPayment
              // (protected fields — users can no longer write them themselves).

              if(verify.type==='pertrade') {
                setMsg({type:'ok', text:`✓ ${verify.credits_added} trade credits added! Balance: ${verify.credits} trades`});
              } else {
                setMsg({type:'ok', text:`✓ Payment successful! Access activated until ${verify.expires}`});
              }
              setTimeout(()=>onActivated(), 2000);
              resolve();
            } catch(e){ reject(e); }
          },
          modal: { ondismiss: ()=>reject(new Error('dismissed')) }
        });
        rzp.open();
      });
    } catch(e){
      if(e.message!=='dismissed')
        setMsg({type:'error', text:'Payment failed: '+e.message});
    } finally { setPaying(false); }
  };

  const days = daysRemaining(fbRecord);
  const expired = isExpired(fbRecord);

  return (
    <div style={{minHeight:"100vh",background:"#07101c",display:"flex",alignItems:"center",
      justifyContent:"center",padding:16,fontFamily:"inherit"}}>
      <div style={{width:"min(520px,100%)",display:"flex",flexDirection:"column",gap:16}}>

        {/* Header */}
        <div style={{textAlign:"center",marginBottom:8}}>
          <div style={{fontSize:28,marginBottom:8}}>📈</div>
          <div style={{fontSize:20,fontWeight:700,color:"#e7edf5"}}>RABTRADE</div>
          <div style={{fontSize:12,color:"#8ea2bf",marginTop:4}}>
            AI-powered trading signals · F&O analytics · Auto-trade
          </div>
          {!expired&&days!==null&&(
            <div style={{marginTop:8,padding:"4px 12px",display:"inline-block",
              background:"rgba(220,38,38,0.12)",border:"1px solid rgba(248,81,73,0.3)",
              borderRadius:20,fontSize:11,color:"#ff5b6c"}}>
              ⏰ Your subscription expires in {days} day{days!==1?"s":""}
            </div>
          )}
          {expired&&fbRecord&&(
            <div style={{marginTop:8,padding:"4px 12px",display:"inline-block",
              background:"rgba(220,38,38,0.15)",border:"1px solid #f85149",
              borderRadius:20,fontSize:11,color:"#ff5b6c",fontWeight:700}}>
              ✗ Subscription expired — renew to continue
            </div>
          )}
        </div>

        {/* Message */}
        {msg&&(
          <div style={{padding:"10px 14px",borderRadius:0,fontSize:11,
            background:msg.type==="ok"?"rgba(63,185,80,0.1)":"rgba(248,81,73,0.1)",
            border:"1px solid "+(msg.type==="ok"?"#2ed47a":"#ff5b6c"),
            color:msg.type==="ok"?"#2ed47a":"#ff5b6c"}}>
            {msg.text}
          </div>
        )}

        {/* Plans */}
        {/* Current credits balance for per-trade users */}
        {fbRecord?.plan==="pertrade"&&(
          <div style={{padding:"12px 16px",background:"rgba(0,212,170,0.06)",
            border:"1px solid rgba(0,212,170,0.3)",borderRadius:0,
            display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:"#e7edf5"}}>⚡ Trade Credits</div>
              <div style={{fontSize:11,color:"#8ea2bf",marginTop:2}}>
                Each order placed costs 1 credit · Exit orders are free
              </div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:24,fontWeight:700,color:"#e8b339"}}>{fbRecord?.credits??'—'}</div>
              <div style={{fontSize:12,color:"#8ea2bf"}}>remaining</div>
            </div>
          </div>
        )}

        {loading
          ? <div style={{textAlign:"center",color:"#8ea2bf",padding:40}}>⟳ Loading plans...</div>
          : <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {plans.map(plan=>{
                const isDemo = plan.price===0;
                const disabled = paying || (isDemo && demoUsed);
                return (
                  <div key={plan.id}
                    style={{background:plan.popular?"rgba(0,212,170,0.06)":"#0f1d30",
                      border:"1.5px solid "+(plan.popular?"#e8b339":"#1a2c44"),
                      borderRadius:0,padding:"14px 16px",position:"relative"}}>
                    {plan.popular&&(
                      <div style={{position:"absolute",top:-10,right:16,
                        background:"#e8b339",borderRadius:0,padding:"2px 10px",
                        fontSize:12,fontWeight:700,color:"#07101c"}}>POPULAR</div>
                    )}
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <div>
                        <div style={{fontSize:14,fontWeight:700,color:"#e7edf5"}}>{plan.name}</div>
                        <div style={{fontSize:11,color:"#8ea2bf",marginTop:2}}>{plan.desc}</div>
                        {plan.type==="pertrade"&&(
                          <div style={{fontSize:10,color:"#e8b339",marginTop:3}}>
                            ₹{plan.price} charged per order placed
                          </div>
                        )}
                      </div>
                      <div style={{textAlign:"right"}}>
                        <div style={{fontSize:18,fontWeight:700,color:plan.popular?"#e8b339":"#e7edf5"}}>
                          {plan.price===0?"FREE":"₹"+plan.price}
                        </div>
                        {plan.price>0&&plan.type==="fixed"&&(
                          <div style={{fontSize:12,color:"#8ea2bf"}}>{plan.days} days</div>
                        )}
                      </div>
                    </div>
                    <button onClick={()=>activatePlan(plan)} disabled={disabled}
                      style={{marginTop:10,width:"100%",
                        background:disabled?"rgba(232,179,57,0.06)":plan.popular
                          ?"linear-gradient(135deg,rgba(0,212,170,0.3),rgba(0,112,243,0.25))"
                          :"rgba(96,165,250,0.1)",
                        border:"1px solid "+(disabled?"#1a2c44":plan.popular?"#e8b339":"rgba(96,165,250,0.4)"),
                        borderRadius:7,padding:"9px 0",
                        color:disabled?"#5a728f":plan.popular?"#e8b339":"#6b7280",
                        fontFamily:"inherit",fontSize:11,fontWeight:700,
                        cursor:disabled?"default":"pointer"}}>
                      {disabled&&isDemo?"✓ Demo already used"
                        :paying?"⟳ Processing..."
                        :plan.price===0?"▶ Activate Free Demo"
                        :"💳 Pay ₹"+plan.price+" — Subscribe"}
                    </button>
                  </div>
                );
              })}
            </div>
        }

        {/* Footer */}
        <div style={{textAlign:"center",fontSize:10,color:"#5a728f",lineHeight:1.8}}>
          Payments secured by Razorpay · Instant activation<br/>
          Questions? Contact support · Logged in as {fbUser?.email}
        </div>
        <button onClick={onLogout}
          style={{background:"none",border:"1px solid #1a2c44",borderRadius:0,
            padding:"6px 0",color:"#8ea2bf",fontFamily:"inherit",fontSize:10,cursor:"pointer"}}>
          Logout
        </button>
      </div>
    </div>
  );
}

const TABS = [
  { id:"dashboard",  icon:"📊", label:"Dashboard"  },
  { id:"equity",     icon:"⚡", label:"Equity"     },
  { id:"mcx",        icon:"🏭", label:"MCX F&O"    },
  { id:"positions",  icon:"📋", label:"Positions"  },
  // Strategy tab removed
  { id:"customscan", icon:"🔬", label:"My Scans"   },
  { id:"delta",      icon:"Δ",  label:"Delta"      },
  { id:"virtual",    icon:"🎮", label:"Virtual"    },
  { id:"vps",        icon:"🖥️", label:"VPS"        },
  { id:"vpstrade",   icon:"📈", label:"VPS Trade"  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
// Module-level UID ref — updated by App component
let _currentUID = "";
function setCurrentUID(uid) { _currentUID = uid; }

async function apiFetch(path, opts={}) {
  // ── Telegram is restricted ─────────────────────────────────────────────────
  // Only allow /alert/telegram sends explicitly marked _tgAllow — i.e. the
  // pre-entry validate scan and position TP/SL exit alerts. Everything else
  // (raw scan signals, AI scan, custom/mobile scan) is dropped. The /test
  // endpoint is exempt so the settings "Test Telegram" button still works.
  if(path.includes("alert/telegram") && !path.includes("/test") && !opts._tgAllow){
    return { ok:true, suppressed:"tg-restricted" };
  }
  const headers = await _authHeaders(opts.headers||{});
  // Long ops: any scan (equity /scan loops 50 symbols × historical data), AI
  // scans, and option-chain resolution — these routinely exceed 60s.
  const isLongOp = path.includes("/scan") || path.includes("/ai/") || path.includes("/fo/find_atm") || path.includes("/fo/futures-symbol");
  const timeoutMs = isLongOp ? 180000 : 60000;
  const controller = new AbortController();
  const tid = setTimeout(()=>controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${API_BASE}${path}`, {...opts, headers, signal: controller.signal});
    clearTimeout(tid);
    if (!res.ok) { const e = await res.json().catch(()=>({detail:res.statusText})); throw new Error(e.detail||"API error"); }
    return res.json();
  } catch(e) {
    clearTimeout(tid);
    if(e.name === 'AbortError') throw new Error(`Request timed out (${isLongOp?'180':'60'}s) — ${path.includes('/scan/investment')?'Fundamental data fetch took too long. Try a smaller watchlist (< 20 symbols) or retry.':'server may be slow'}`);
    throw e;
  }
}

function isMarketOpen() {
  const now = new Date();
  const day = now.getDay(); // 0=Sun,6=Sat
  if(day===0||day===6) return false;
  const h = now.getHours(), m = now.getMinutes();
  const mins = h*60+m;
  return mins >= 555 && mins <= 930; // 9:15 - 15:30 IST
}

function fmt(val) {
  const n = parseFloat(val)||0;
  return new Intl.NumberFormat("en-IN",{style:"currency",currency:"INR",maximumFractionDigits:0}).format(n);
}

function fmtPct(val) { return `${val>=0?"+":""}${parseFloat(val||0).toFixed(2)}%`; }
function todayKey() { return new Date().toLocaleDateString("en-IN"); }
function dedupKey(sym,label) { return `${sym}||${label}||${todayKey()}`; }

// ── Extract nearest non-overlapping D/S zones for a given entry price ──────────
// Handles two formats:
//   New (pivot-based): zones = { demand:[low,high]|null, supply:[low,high]|null }
//   Old (scan-based):  zones = { demand:[[low,high,strength],...], supply:[[...]] }
// Returns { demandZone:{low,high}|null, supplyZone:{low,high}|null }
function extractNearestZones(zones, entryPrice) {
  if(!zones || !entryPrice) return { demandZone:null, supplyZone:null };
  const ep = parseFloat(entryPrice);

  // ── New format: flat [low, high] array ─────────────────────────────────────
  const dFlat = zones.demand;
  const sFlat = zones.supply;
  if(Array.isArray(dFlat) && !Array.isArray(dFlat[0]) && dFlat.length === 2) {
    // Pivot-zone format: demand=[low,high], supply=[low,high]
    const dz = dFlat[0] && dFlat[1] && dFlat[1] > dFlat[0] ? {low:dFlat[0],high:dFlat[1]} : null;
    const sz = sFlat && sFlat[0] && sFlat[1] && sFlat[1] > sFlat[0] ? {low:sFlat[0],high:sFlat[1]} : null;
    return { demandZone: dz, supplyZone: sz };
  }

  // ── Old format: array of [low, high, strength] tuples ─────────────────────
  const valid = arr => (arr||[]).filter(z=>z[0]&&z[1]&&z[1]>z[0]);
  const nearDemand = valid(zones.demand)
    .filter(z => z[1] < ep * 1.005)
    .sort((a,b) => b[0]-a[0])[0];
  const nearSupply = valid(zones.supply)
    .filter(z => z[0] > ep * 0.995)
    .sort((a,b) => a[0]-b[0])[0];

  // Overlap check — keep the stronger zone
  const overlap = nearDemand && nearSupply && nearDemand[1] >= nearSupply[0];
  if(overlap) {
    const useD = (nearDemand[2]||1) >= (nearSupply[2]||1);
    return {
      demandZone: useD ? {low:nearDemand[0],high:nearDemand[1]} : null,
      supplyZone: useD ? null : {low:nearSupply[0],high:nearSupply[1]},
    };
  }
  return {
    demandZone: nearDemand ? {low:nearDemand[0],high:nearDemand[1]} : null,
    supplyZone: nearSupply ? {low:nearSupply[0],high:nearSupply[1]} : null,
  };
}

function cardColor(r, activeScan) {
  if (!r||r.error) return { bg:"#0f1d30", border:"#23405f", dot:"#8ea2bf", label:"—" };
  const sigs = (r.signals||[]).filter(s=>activeScan.includes(s.scan));
  const hasBuy=sigs.some(s=>s.type==="BUY"), hasSell=sigs.some(s=>s.type==="SELL");
  if (hasBuy&&hasSell) return { bg:"rgba(96,165,250,0.12)", border:"#e8b339", dot:"#e8b339", label:"MIXED" };
  if (hasBuy)          return { bg:"rgba(63,185,80,0.1)",  border:"#2ed47a", dot:"#2ed47a", label:"BUY"   };
  if (hasSell)         return { bg:"rgba(248,81,73,0.1)",  border:"#ff5b6c", dot:"#ff5b6c", label:"SELL"  };
  return { bg:"rgba(187,162,0,0.07)", border:"#bbaa00", dot:"#bbaa00", label:"NEUTRAL" };
}

const pnlColor = (v) => parseFloat(v)>=0 ? "#2ed47a" : "#ff5b6c";

function PatternBadge({signal}) {
  const isBuy = signal.type === "BUY";
  return (
    <span style={{display:"inline-flex",alignItems:"center",gap:3,fontSize:12,fontWeight:700,
      padding:"2px 7px",borderRadius:0,
      background:isBuy?"rgba(240,192,64,0.12)":"rgba(220,38,38,0.12)",
      color:isBuy?"#f0c040":"#ff5b6c",
      border:"1px solid "+(isBuy?"rgba(240,192,64,0.35)":"rgba(248,81,73,0.3)")}}>
      📐 {signal.pattern||signal.label}
      {signal.confidence&&<span style={{fontSize:11,opacity:0.7}}>{signal.confidence}</span>}
    </span>
  );
}

function Badge({type,children}) {
  const color = type==="BUY"?"#2ed47a":type==="SELL"?"#ff5b6c":"#6b7280";
  return <span style={{padding:"2px 7px",borderRadius:20,fontSize:12,fontWeight:700,background:`${color}22`,color,border:`1px solid ${color}44`,whiteSpace:"nowrap"}}>{children}</span>;
}


// ── Persistent localStorage hook ─────────────────────────────────────────────
function useLocalStorage(key, initial) {
  const [val, setVal] = useState(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored === null) return initial;
      const parsed = JSON.parse(stored);
      // If both stored and initial are objects (not arrays), merge to fill missing keys
      if(parsed && typeof parsed === 'object' && !Array.isArray(parsed) &&
         initial && typeof initial === 'object' && !Array.isArray(initial)) {
        return { ...initial, ...parsed };
      }
      return parsed;
    } catch { return initial; }
  });
  const setValue = useCallback((v) => {
    setVal(prev => {
      const next = typeof v === 'function' ? v(prev) : v;
      try { localStorage.setItem(key, JSON.stringify(next)); } catch {}
      return next;
    });
  }, [key]);
  return [val, setValue];
}

// ── Shared layout ─────────────────────────────────────────────────────────────
function Card({children, style={}}) {
  return <div style={{background:"#0f1d30",border:"1px solid #1a2c44",borderRadius:0,padding:"14px 16px",...style}}>{children}</div>;
}

// ══════════════════════════════════════════════════════════════════════════════
// STRATEGY POPUP — inline trade strategy suggester for F&O and MCX scan rows
// Shown when user clicks on a scan result row trade cell
// Recommends: BUY CE/PE, SELL CE/PE (option writing), BUY/SELL FUT
// ══════════════════════════════════════════════════════════════════════════════
function StrategyPopup({ symbol, exchange, ltp, hasBuy, hasSell, signals=[],
  lotSize=1, onTrade, onClose, canTrade=false, mode="fo" }) {

  const isMCX   = mode==="mcx" || exchange==="MCX";
  const isDelta  = mode==="delta" || exchange==="DELTA";
  const curr     = isDelta ? "$" : "₹";
  const isBullish = hasBuy && !hasSell;
  const isBearish = hasSell && !hasBuy;
  const isMixed   = hasBuy && hasSell;

  // ── Demand/Supply zones ──────────────────────────────────────────────────
  const [dsZones, setDsZones] = useState(null);
  const [selectedStrategy, setSelectedStrategy] = useState(null);

  // Fetch D/S zones from VPS when popup opens
  useEffect(()=>{
    if(!ltp||ltp<=0) return;
    apiFetch('/chart/candles',{
      method:'POST',headers:{'Content-Type':'application/json'},
      body:JSON.stringify({symbol,exchange:exchange==='MCX'?'MCX':'NSE',timeframe:'1hour',days:30})
    }).then(d=>{ if(d?.zones) setDsZones(d.zones); }).catch(()=>{});
  },[symbol,exchange]);

  // Signal-driven strategy logic
  const tsiSig   = signals.find(s=>s.label?.toLowerCase().includes("tsi"));
  const emaSig   = signals.find(s=>s.label?.toLowerCase().includes("ema"));
  const clearBull = tsiSig?.type==="BUY" || emaSig?.type==="BUY";
  const clearBear = tsiSig?.type==="SELL" || emaSig?.type==="SELL";

  // OTM % and strike rounding by market type
  const otmPct = isMCX ? 1.0 : isDelta ? 2.0 : 1.5;
  // Delta strikes are round numbers (500/1000 increments for BTC, 50 for ETH etc)
  const strikeRound = isDelta
    ? (ltp > 10000 ? 500 : ltp > 1000 ? 50 : ltp > 100 ? 10 : 1)
    : 50;
  const ceStrike  = ltp>0 ? Math.round(ltp*(1+otmPct/100)/strikeRound)*strikeRound : 0;
  const peStrike  = ltp>0 ? Math.round(ltp*(1-otmPct/100)/strikeRound)*strikeRound : 0;
  const atmStrike = ltp>0 ? Math.round(ltp/strikeRound)*strikeRound : 0;

  // Format price with right currency and decimals
  const fmtPrice = (p) => {
    if(!p) return "?";
    if(isDelta) return `$${parseFloat(p).toLocaleString("en-US",{maximumFractionDigits:0})}`;
    return `₹${parseFloat(p).toLocaleString("en-IN",{maximumFractionDigits:0})}`;
  };

  const strategies = [
    // ── STRADDLE — recommended when mixed signals ─────────────────────────────
    ...(isMixed ? [{
      id:"sell_straddle",
      label: "SELL OTM STRADDLE (Mixed Signal)",
      sublabel: `SELL CE @ ~${fmtPrice(ceStrike)} + SELL PE @ ~${fmtPrice(peStrike)} · collect both premiums`,
      action:"SELL", type:"STRADDLE", strike:atmStrike,
      color:"#f59e0b", bg:"rgba(245,158,11,0.12)",
      recommended: true,
      tag:"✅ Mixed signals — ideal for premium selling (market neutral)",
      riskLabel:"Profit if price stays between strikes. Loss if big move either side.",
    }] : []),
    // Option SELL strategies (premium collection — primary preference)
    {
      id:"sell_pe",
      label: isDelta?"SELL OTM PUT (Write)": isMCX?"SELL PUT (Futures OTM)":"SELL OTM PUT",
      sublabel: `Strike ~${fmtPrice(peStrike)} · collect premium if price stays above`,
      action:"SELL", type:"PE", strike:peStrike,
      color:"#2ed47a", bg:"rgba(22,163,74,0.12)",
      recommended: isBullish || clearBull,
      tag: isBullish?"✅ Bullish signal — best match":clearBull?"TSI/EMA bullish cross":"",
      riskLabel:"Max loss: large fall. Max profit: premium collected.",
    },
    {
      id:"sell_ce",
      label: isDelta?"SELL OTM CALL (Write)":isMCX?"SELL CALL (Futures OTM)":"SELL OTM CALL",
      sublabel: `Strike ~${fmtPrice(ceStrike)} · collect premium if price stays below`,
      action:"SELL", type:"CE", strike:ceStrike,
      color:"#ff5b6c", bg:"rgba(220,38,38,0.12)",
      recommended: isBearish || clearBear,
      tag: isBearish?"✅ Bearish signal — best match":clearBear?"TSI/EMA bearish cross":"",
      riskLabel:"Max loss: large rise. Max profit: premium collected.",
    },
    // Option BUY strategies (directional, limited risk)
    {
      id:"buy_ce",
      label: isDelta?"BUY CALL OPTION":isMCX?"BUY CALL":"BUY ATM CALL",
      sublabel: `Strike ~${fmtPrice(atmStrike)} · profits if price rises`,
      action:"BUY", type:"CE", strike:atmStrike,
      color:"#2ed47a", bg:"rgba(22,163,74,0.06)",
      recommended: false,
      tag:"Limited risk bullish — pay premium",
      riskLabel:"Max loss: premium paid. Unlimited upside.",
    },
    {
      id:"buy_pe",
      label: isDelta?"BUY PUT OPTION":isMCX?"BUY PUT":"BUY ATM PUT",
      sublabel: `Strike ~${fmtPrice(atmStrike)} · profits if price falls`,
      action:"BUY", type:"PE", strike:atmStrike,
      color:"#ff5b6c", bg:"rgba(220,38,38,0.06)",
      recommended: false,
      tag:"Limited risk bearish — pay premium",
      riskLabel:"Max loss: premium paid. Unlimited downside capture.",
    },
    // Futures / Perp — directional, leveraged
    {
      id:"buy_fut",
      label: isDelta?"BUY PERPETUAL (Long)":isMCX?"BUY FUTURES (Long)":"BUY FUTURES (Long)",
      sublabel: `${isDelta?"Long PERP":"Buy futures"} @ ${fmtPrice(ltp)}`,
      action:"BUY", type:"FUT", strike:null,
      color:"#2ed47a", bg:"rgba(22,163,74,0.04)",
      recommended: false,
      tag:"Directional leveraged long",
      riskLabel:"Full exposure — use stop-loss.",
    },
    {
      id:"sell_fut",
      label: isDelta?"SELL PERPETUAL (Short)":isMCX?"SELL FUTURES (Short)":"SELL FUTURES (Short)",
      sublabel: `${isDelta?"Short PERP":"Short futures"} @ ${fmtPrice(ltp)}`,
      action:"SELL", type:"FUT", strike:null,
      color:"#ff5b6c", bg:"rgba(220,38,38,0.04)",
      recommended: false,
      tag:"Directional leveraged short",
      riskLabel:"Full exposure — use stop-loss.",
    },
  ];

  const [resolving, setResolving] = useState(false);
  const [resolveErr, setResolveErr] = useState(null);
  // Expiry selector — populated after first successful /fo/find_atm call
  const [availExpiries, setAvailExpiries] = useState([]); // ["2026-07-03","2026-07-30",...]
  const [selectedExpiry, setSelectedExpiry] = useState(""); // "" = auto (backend default)
  const [lastContract, setLastContract] = useState(null);  // last resolved contract

  const handleTrade = async (s) => {
    if(!canTrade || !onTrade || resolving) return;
    setResolveErr(null);

    // Futures / Perp — resolve to actual futures contract first
    if(s.type==="FUT") {
      setResolving(true);
      try {
        const exch = isMCX?"MCX":"NSE";
        const futRes = await apiFetch("/fo/futures-symbol",{method:"POST",
          headers:{"Content-Type":"application/json"},
          body:JSON.stringify({symbol, exchange:exch})});
        if(!futRes.found) {
          setResolveErr(`No futures contract found: ${futRes.reason||"check symbol"}`);
          setResolving(false);
          return;
        }
        onTrade({
          symbol:         futRes.tradingsymbol,
          exchange:       futRes.exchange || "NFO",
          side:           s.action,
          price:          futRes.ltp || ltp || 0,
          qty:            futRes.lot_size || lotSize || 1,
          product:        "NRML",
          instrumentType: "FUT",
          suggestedStrike:null,
          source:         "scan_strategy",
        });
        onClose();
      } catch(e){ setResolveErr(`Futures lookup failed: ${e.message}`); }
      finally{ setResolving(false); }
      return;
    }

    // Straddle — place SELL CE + SELL PE sequentially (one by one)
    if(s.type==="STRADDLE") {
      setResolving(true);
      try {
        const exch = isMCX?"MCX":"NSE";
        // Find CE leg — OTM at resistance for SELL
        const ceRes = await apiFetch("/fo/find_atm",{method:"POST",
          headers:{"Content-Type":"application/json"},
          body:JSON.stringify({symbol,exchange:exch,signal_side:"SELL",option_type:"CE",min_days:7,
            action:"SELL",target_expiry:selectedExpiry||""})});
        // Find PE leg — OTM at support for SELL
        const peRes = await apiFetch("/fo/find_atm",{method:"POST",
          headers:{"Content-Type":"application/json"},
          body:JSON.stringify({symbol,exchange:exch,signal_side:"BUY",option_type:"PE",min_days:7,
            action:"SELL",target_expiry:selectedExpiry||""})});
        // Populate expiry list from straddle response
        const anyRes = ceRes.found?ceRes:peRes;
        if(anyRes.found && anyRes.contract?.all_expiries?.length) {
          setAvailExpiries(anyRes.contract.all_expiries);
          if(!selectedExpiry) setSelectedExpiry(anyRes.contract.selected_expiry||"");
        }

        const legs = [];
        if(ceRes.found) legs.push({...ceRes.contract, leg:"CE"});
        if(peRes.found) legs.push({...peRes.contract, leg:"PE"});

        if(!legs.length){ setResolveErr("No straddle contracts found"); setResolving(false); return; }

        // Place each leg ONE BY ONE — wait for first order before placing second
        for(const leg of legs){
          await new Promise((resolve, reject) => {
            try {
              onTrade({
                symbol:         leg.tradingsymbol,
                exchange:       leg.exchange||exch,
                side:           "SELL",
                price:          leg.option_ltp||0,
                qty:            leg.lot_size||lotSize||1,
                product:        "NRML",
                instrumentType: leg.leg,
                source:         "straddle",
              });
              // Wait 2s between legs to ensure first order is submitted before second
              setTimeout(resolve, 2000);
            } catch(e){ reject(e); }
          });
        }
        onClose();
      } catch(e){ setResolveErr(`Straddle failed: ${e.message}`); }
      finally{ setResolving(false); }
      return;
    }

    // Delta — pass through directly, openDeltaTicket handles lookup
    if(isDelta) {
      onTrade({ symbol, exchange, side:s.action, price:ltp,
        qty:1, product:"NRML", instrumentType:s.type,
        suggestedStrike:s.strike, source:"scan_strategy" });
      onClose();
      return;
    }

    // NSE / MCX — resolve actual option contract via /fo/find_atm
    setResolving(true);
    setResolveErr(null);
    try {
      const exch = isMCX ? "MCX" : "NSE";
      // signal_side: for SELL PE (bullish) pass "BUY", for SELL CE (bearish) pass "SELL"
      // for BUY CE (bullish) pass "BUY", for BUY PE (bearish) pass "SELL"
      const signalSide = (s.type==="PE" && s.action==="SELL") || (s.type==="CE" && s.action==="BUY")
        ? "BUY" : "SELL";
      const res = await apiFetch("/fo/find_atm", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          symbol,
          exchange: exch,
          signal_side: signalSide,
          option_type: s.type,
          min_days: 7,
          preferred_strike: s.strike || null,
          action: s.action,
          target_expiry: selectedExpiry || "",  // user-selected expiry override
        })
      });
      if(!res.found) {
        setResolveErr(`No ${s.type} contract found: ${res.reason||"check expiry dates"}`);
        setResolving(false);
        return;
      }
      const contract = res.contract;
      // Populate expiry selector from backend response
      if(contract.all_expiries?.length) {
        setAvailExpiries(contract.all_expiries);
        if(!selectedExpiry) setSelectedExpiry(contract.selected_expiry || "");
      }
      setLastContract({...contract, _strategy: s});
      const optLtp = parseFloat(contract.option_ltp || 0);
      const optOI  = parseInt(contract.option_oi || 0);
      // Warn if no price found — likely illiquid strike
      if(optLtp <= 0 && optOI <= 0) {
        setResolveErr(`${contract.tradingsymbol} has no price/liquidity. Strike ${contract.strike} may be too far OTM. Try a different strategy.`);
        setResolving(false);
        return;
      }
      onTrade({
        symbol:         contract.tradingsymbol,
        exchange:       contract.exchange || exch,
        side:           s.action,
        price:          optLtp,
        qty:            contract.lot_size || lotSize || 1,
        product:        "NRML",
        instrumentType: s.type,
        suggestedStrike:contract.strike,
        contractInfo:   contract,   // full contract for display
        source:         "scan_strategy",
        srLevel:        contract.sr_level || null,
        srType:         contract.sr_type || null,
      });
      onClose();
    } catch(e) {
      setResolveErr(`Failed to find contract: ${e.message}`);
    } finally {
      setResolving(false);
    }
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",
      zIndex:900,display:"flex",alignItems:"center",justifyContent:"center",padding:12}}
      onClick={onClose}>
      <div style={{background:"#0f1d30",border:"1px solid #c8b494",borderRadius:12,
        width:"min(420px,96vw)",maxHeight:"88vh",overflowY:"auto",
        boxShadow:"0 8px 40px rgba(0,0,0,0.3)",fontFamily:"inherit"}}
        onClick={e=>e.stopPropagation()}>

        {/* Header */}
        <div style={{padding:"12px 16px",borderBottom:"1px solid #d4c4a8",
          display:"flex",justifyContent:"space-between",alignItems:"center",
          background:"#0b1624",borderRadius:"12px 12px 0 0"}}>
          <div>
            <div style={{fontSize:14,fontWeight:800,color:"#e7edf5"}}>
              {isBullish?"▲":isBearish?"▼":"◈"} {symbol}
              <span style={{fontSize:11,color:"#8ea2bf",fontWeight:400,marginLeft:8}}>
                {isDelta?"₿ Crypto Strategy":isMCX?"MCX F&O Strategy":"NSE F&O Strategy"}
              </span>
            </div>
            <div style={{fontSize:11,color:"#8ea2bf",marginTop:2}}>
              LTP {fmtPrice(ltp)} ·
              {isBullish?" Bullish signal":isBearish?" Bearish signal":isMixed?" Mixed signals":" No clear signal"}
              {signals.length>0&&<span style={{marginLeft:6,color:isBullish?"#2ed47a":"#ff5b6c",fontWeight:700}}>
                ({signals.map(s=>s.label).join(", ")})
              </span>}
            </div>
          </div>
          <button onClick={onClose}
            style={{background:"none",border:"none",color:"#8ea2bf",fontSize:18,cursor:"pointer"}}>✕</button>
        </div>

        <div style={{padding:"12px 14px",display:"flex",flexDirection:"column",gap:8}}>

          {/* Section: Option SELL (top — premium collection) */}
          <div style={{fontSize:10,color:"#e8b339",fontWeight:700,letterSpacing:1,marginBottom:2}}>
            📈 OPTION SELLING (PREMIUM COLLECTION)
          </div>
          {strategies.filter(s=>s.action==="SELL"&&s.type!=="FUT").map(s=>(
            <div key={s.id} style={{background:s.bg,border:`1.5px solid ${s.recommended?s.color:s.color+"44"}`,
              borderRadius:0,padding:"10px 12px",
              boxShadow:s.recommended?"0 2px 8px rgba(0,0,0,0.08)":"none"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                    {s.recommended&&<span style={{fontSize:9,background:s.color,color:"#fff",
                      borderRadius:0,padding:"1px 6px",fontWeight:700,flexShrink:0}}>
                      AI PICK
                    </span>}
                    <span style={{fontSize:12,fontWeight:700,color:s.color}}>{s.label}</span>
                  </div>
                  <div style={{fontSize:10,color:"#8ea2bf"}}>{s.sublabel}</div>
                  {s.tag&&<div style={{fontSize:10,color:"#e8b339",marginTop:2,fontStyle:"italic"}}>{s.tag}</div>}
                  <div style={{fontSize:10,color:"#5a728f",marginTop:3}}>{s.riskLabel}</div>
                  {/* Margin estimate for SELL options — manual trade info */}
                  {!isDelta&&ltp>0&&(
                    <div style={{marginTop:5,padding:"4px 8px",
                      background:"rgba(232,179,57,0.08)",borderRadius:0,
                      border:"1px solid rgba(232,179,57,0.2)"}}>
                      <div style={{fontSize:9,color:"#e8b339",fontWeight:700,marginBottom:2}}>
                        💰 MARGIN REQUIRED (est.) — Manual trade only
                      </div>
                      <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
                        {[
                          ["NRML ~15%", `₹${Math.round(ltp*s.strike/100*0.15*(isMCX?1:50)).toLocaleString("en-IN")}`],
                          ["SPAN+Exposure", "Check broker"],
                          ["Lot size", isMCX?"varies":"25-50 qty"],
                        ].map(([l,v])=>(
                          <div key={l} style={{fontSize:9}}>
                            <span style={{color:"#5a728f"}}>{l}: </span>
                            <span style={{color:"#e7edf5",fontWeight:700}}>{v}</span>
                          </div>
                        ))}
                      </div>
                      <div style={{fontSize:9,color:"#5a728f",marginTop:3,fontStyle:"italic"}}>
                        Actual margin varies by broker. Check Zerodha Margin Calculator before placing.
                      </div>
                    </div>
                  )}
                </div>
                {canTrade?(
                  <button onClick={()=>handleTrade(s)} disabled={resolving}
                    style={{background:s.color,border:"none",borderRadius:7,
                      padding:"8px 12px",color:"#fff",fontFamily:"inherit",
                      fontSize:11,fontWeight:700,cursor:resolving?"default":"pointer",
                      flexShrink:0, minWidth:64,textAlign:"center",
                      opacity:resolving?0.6:1}}>
                    {resolving?"⟳":s.action+" "+s.type}
                  </button>
                ):(
                  <span style={{fontSize:10,color:"#5a728f",padding:"4px 6px",
                    background:"rgba(0,0,0,0.05)",borderRadius:0,flexShrink:0}}>
                    Signal only
                  </span>
                )}
              </div>
            </div>
          ))}

          {/* Section: Option BUY */}
          <div style={{fontSize:10,color:"#6b7280",fontWeight:700,letterSpacing:1,marginTop:4,marginBottom:2}}>
            🎯 OPTION BUYING (LIMITED RISK)
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
            {strategies.filter(s=>s.action==="BUY"&&s.type!=="FUT").map(s=>(
              <div key={s.id} style={{background:s.bg,border:`1px solid ${s.color}33`,
                borderRadius:7,padding:"8px 10px"}}>
                <div style={{fontSize:11,fontWeight:700,color:s.color,marginBottom:2}}>{s.label}</div>
                <div style={{fontSize:10,color:"#8ea2bf",marginBottom:6}}>{s.sublabel}</div>
                {canTrade?(
                  <button onClick={()=>handleTrade(s)}
                    style={{width:"100%",background:`${s.color}22`,border:`1px solid ${s.color}66`,
                      borderRadius:0,padding:"5px",color:s.color,fontFamily:"inherit",
                      fontSize:11,fontWeight:700,cursor:"pointer"}}>
                    {s.action} {s.type}
                  </button>
                ):(
                  <span style={{fontSize:10,color:"#5a728f"}}>Signal only</span>
                )}
              </div>
            ))}
          </div>

          {/* Section: Futures */}
          <div style={{fontSize:10,color:"#6b7280",fontWeight:700,letterSpacing:1,marginTop:4,marginBottom:2}}>
            ⚡ {isDelta?"PERPETUAL / FUTURES (DIRECTIONAL)":"FUTURES (DIRECTIONAL, LEVERAGED)"}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
            {strategies.filter(s=>s.type==="FUT").map(s=>(
              <div key={s.id} style={{background:s.bg,border:`1px solid ${s.color}22`,
                borderRadius:7,padding:"8px 10px"}}>
                <div style={{fontSize:11,fontWeight:700,color:s.color,marginBottom:2}}>{s.label}</div>
                <div style={{fontSize:10,color:"#8ea2bf",marginBottom:6}}>{s.tag}</div>
                {canTrade?(
                  <button onClick={()=>handleTrade(s)}
                    style={{width:"100%",background:`${s.color}18`,border:`1px solid ${s.color}44`,
                      borderRadius:0,padding:"5px",color:s.color,fontFamily:"inherit",
                      fontSize:11,fontWeight:700,cursor:"pointer"}}>
                    {s.action} FUT
                  </button>
                ):(
                  <span style={{fontSize:10,color:"#5a728f"}}>Signal only</span>
                )}
              </div>
            ))}
          </div>

          {/* ── Expiry Selector — shown after first contract is resolved ── */}
          {availExpiries.length > 0 && (
            <div style={{display:"flex",alignItems:"center",gap:8,
              padding:"8px 10px",background:"rgba(73,194,228,0.06)",
              border:"1px solid rgba(73,194,228,0.2)",borderRadius:6}}>
              <span style={{fontSize:11,color:"#49c2e4",fontWeight:700,whiteSpace:"nowrap"}}>
                📅 Expiry
              </span>
              <div style={{display:"flex",gap:5,flexWrap:"wrap",flex:1}}>
                {availExpiries.map(exp=>{
                  const d = new Date(exp);
                  const label = d.toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"2-digit"});
                  const daysLeft = Math.round((d - new Date())/(1000*60*60*24));
                  const isSelected = exp === selectedExpiry;
                  return (
                    <button key={exp} onClick={()=>setSelectedExpiry(exp)}
                      style={{fontSize:10,padding:"3px 8px",borderRadius:4,cursor:"pointer",
                        fontFamily:"inherit",fontWeight:isSelected?700:400,
                        background:isSelected?"rgba(73,194,228,0.2)":"rgba(11,22,36,0.8)",
                        border:`1px solid ${isSelected?"#49c2e4":"rgba(73,194,228,0.25)"}`,
                        color:isSelected?"#49c2e4":"#8ea2bf"}}>
                      {label}
                      <span style={{fontSize:9,marginLeft:4,color:isSelected?"#6ee7b7":"#5a728f"}}>
                        {daysLeft}d
                      </span>
                    </button>
                  );
                })}
              </div>
              {lastContract && (
                <span style={{fontSize:10,color:"#5a728f",whiteSpace:"nowrap"}}>
                  {lastContract.tradingsymbol}
                </span>
              )}
            </div>
          )}

          {/* Resolving state / error */}
          {resolving&&(
            <div style={{padding:"10px 12px",background:"rgba(232,179,57,0.08)",
              border:"1px solid rgba(232,179,57,0.3)",borderRadius:7,
              fontSize:11,color:"#e8b339",textAlign:"center"}}>
              ⟳ Finding option contract from {isMCX?"MCX":"NSE"} chain...
            </div>
          )}
          {resolveErr&&(
            <div style={{padding:"10px 12px",background:"rgba(220,38,38,0.08)",
              border:"1px solid rgba(220,38,38,0.25)",borderRadius:7,fontSize:11,color:"#ff5b6c"}}>
              ✗ {resolveErr}
              <div style={{fontSize:10,color:"#8ea2bf",marginTop:4,lineHeight:1.5}}>
                Try selecting a different expiry above, or check your VPS backend.
              </div>
            </div>
          )}

          {/* ── PAYOFF CHART ── */}
          {ltp>0&&(()=>{
            const strat = selectedStrategy || strategies.find(s=>s.recommended) || strategies[0];
            if(!strat) return null;
            const legs = buildPayoffLegs({
              strategyId: strat.id,
              strike:     strat.strike || (strat.type==="PE"?peStrike:ceStrike),
              premium:    strat.estimatedPremium || ltp*0.015,
              lotSize,
              qty:        1,
              spotPrice:  ltp,
            });
            return (
              <div style={{background:"#0b1624",border:"1px solid #d4c4a8",borderRadius:0,padding:10,marginTop:4}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <div style={{fontSize:10,fontWeight:700,color:"#e8b339",letterSpacing:1}}>
                    📊 PAYOFF AT EXPIRY — {strat.label.toUpperCase()}
                  </div>
                  </div>
                {/* Strategy selector pills */}
                <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:8}}>
                  {strategies.map(s=>(
                    <button key={s.id} onClick={()=>setSelectedStrategy(s)}
                      style={{background:selectedStrategy?.id===s.id?`${s.color}22`:"#0f1d30",
                        border:`1px solid ${selectedStrategy?.id===s.id?s.color:"#23405f"}`,
                        borderRadius:0,padding:"2px 8px",color:selectedStrategy?.id===s.id?s.color:"#8ea2bf",
                        fontFamily:"inherit",fontSize:10,fontWeight:selectedStrategy?.id===s.id?700:400,cursor:"pointer"}}>
                      {s.id.replace("_"," ").toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* GTT is now auto-created via OrderTicket after main order fills */}

          <div style={{fontSize:10,color:"#5a728f",lineHeight:1.6,
            borderTop:"1px solid #d4c4a8",paddingTop:8,marginTop:2}}>
            {isDelta
              ? "₿ Crypto options: ticket opens after contract lookup. Premium auto-fetched from Delta."
              : `Option contracts resolved via /fo/find_atm from your VPS. Actual tradingsymbol (e.g. NIFTY24DEC19800PE) fetched before placing order.`}
            <br/>⚠ Option selling has unlimited loss risk. Always use a stop-loss or hedge.
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiBox({label,value,color="#e7edf5",sub}) {
  return (
    <div style={{background:"#0b1624",padding:"12px 16px",borderRadius:0,border:"1px solid #1a2c44"}}>
      <div style={{fontSize:12,color:"#8ea2bf",letterSpacing:2,marginBottom:2}}>{label}</div>
      <div style={{fontSize:22,fontWeight:700,color,lineHeight:1}}>{value}</div>
      {sub && <div style={{fontSize:10,color:"#8ea2bf",marginTop:2}}>{sub}</div>}
    </div>
  );
}




// ══════════════════════════════════════════════════════════════════════════════
// PAYOFF CHART — Sensibull-style P&L diagram for F&O options & futures
// Shows: breakeven points, profit/loss zones, current price marker
// ══════════════════════════════════════════════════════════════════════════════
function PayoffChart({ legs=[], spotPrice=0, currency="₹", compact=false }) {
  const canvasRef = useRef(null);

  function legPnl(leg, expPrice) {
    const { type, strike, premium=0, qty=1, lotSize=1, side="BUY" } = leg;
    const dir = side==="BUY" ? 1 : -1;
    const contracts = qty * lotSize;
    if(type==="CE")   return dir * (Math.max(0, expPrice - strike) - premium) * contracts;
    if(type==="PE")   return dir * (Math.max(0, strike - expPrice) - premium) * contracts;
    if(type==="FUT"||type==="PERP") return dir * (expPrice - strike) * contracts;
    return 0;
  }
  const totalPnl = p => legs.reduce((s,l)=>s+legPnl(l,p), 0);

  useEffect(()=>{
    if(!canvasRef.current||!legs.length||!spotPrice) return;
    const canvas=canvasRef.current, ctx=canvas.getContext("2d");
    const W=canvas.width, H=canvas.height;
    const PAD=compact?{l:38,r:8,t:14,b:26}:{l:52,r:12,t:20,b:36};
    ctx.clearRect(0,0,W,H);

    const range=spotPrice*0.25;
    const priceMin=spotPrice-range, priceMax=spotPrice+range;
    const N=120;
    const prices=Array.from({length:N+1},(_,i)=>priceMin+(priceMax-priceMin)*i/N);
    const pnls=prices.map(p=>totalPnl(p));
    const pnlMax=Math.max(...pnls,0), pnlMin=Math.min(...pnls,0);
    const pad=(pnlMax-pnlMin)*0.15||Math.abs(pnlMax||pnlMin)*0.2||100;
    const pnlHi=pnlMax+pad, pnlLo=pnlMin-pad;
    const toX=p=>PAD.l+(p-priceMin)/(priceMax-priceMin)*(W-PAD.l-PAD.r);
    const toY=v=>PAD.t+(1-(v-pnlLo)/(pnlHi-pnlLo))*(H-PAD.t-PAD.b);
    const zeroY=toY(0);

    // Background
    ctx.fillStyle="#07101c"; ctx.fillRect(0,0,W,H);

    // Zero line
    ctx.strokeStyle="#1a2c44"; ctx.lineWidth=1; ctx.setLineDash([4,4]);
    ctx.beginPath(); ctx.moveTo(PAD.l,zeroY); ctx.lineTo(W-PAD.r,zeroY); ctx.stroke();
    ctx.setLineDash([]);

    // Zone fills
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(toX(prices[0]),zeroY);
    prices.forEach((p,i)=>ctx.lineTo(toX(p),toY(pnls[i])));
    ctx.lineTo(toX(prices[N]),zeroY); ctx.closePath(); ctx.clip();
    ctx.fillStyle="rgba(22,163,74,0.10)"; ctx.fillRect(PAD.l,PAD.t,W-PAD.l-PAD.r,zeroY-PAD.t);
    ctx.fillStyle="rgba(220,38,38,0.10)"; ctx.fillRect(PAD.l,zeroY,W-PAD.l-PAD.r,H-PAD.b-zeroY);
    ctx.restore();

    // P&L curve
    const gr=ctx.createLinearGradient(0,PAD.t,0,H-PAD.b);
    const gs=pnlHi!==pnlLo?Math.min(Math.max(1-(0-pnlLo)/(pnlHi-pnlLo),0),1):0.5;
    gr.addColorStop(0,"#2ed47a"); gr.addColorStop(gs,"#2ed47a");
    gr.addColorStop(Math.min(gs+0.001,1),"#ff5b6c"); gr.addColorStop(1,"#ff5b6c");
    ctx.strokeStyle=gr; ctx.lineWidth=compact?2:2.5; ctx.lineJoin="round";
    ctx.beginPath();
    prices.forEach((p,i)=>{ const x=toX(p),y=toY(pnls[i]); i===0?ctx.moveTo(x,y):ctx.lineTo(x,y); });
    ctx.stroke();

    // Spot line
    const spotX=toX(spotPrice);
    ctx.strokeStyle="#e8b339"; ctx.lineWidth=1.5; ctx.setLineDash([3,3]);
    ctx.beginPath(); ctx.moveTo(spotX,PAD.t); ctx.lineTo(spotX,H-PAD.b); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle="#e8b339"; ctx.font=`bold ${compact?9:10}px monospace`;
    ctx.textAlign="center"; ctx.fillText("NOW",spotX,PAD.t-3);

    // Breakeven markers
    let prevP=pnls[0];
    prices.forEach((p,i)=>{
      if(i===0){prevP=pnls[0];return;}
      const curP=pnls[i];
      if((prevP<0&&curP>=0)||(prevP>=0&&curP<0)){
        const be=prices[i-1]+(prices[i]-prices[i-1])*Math.abs(prevP)/(Math.abs(prevP)+Math.abs(curP));
        const bx=toX(be);
        ctx.fillStyle="#0b1624"; ctx.strokeStyle="#e8b339"; ctx.lineWidth=1.5;
        ctx.beginPath(); ctx.arc(bx,zeroY,compact?4:5,0,Math.PI*2); ctx.fill(); ctx.stroke();
        ctx.fillStyle="#e8b339"; ctx.font=`bold ${compact?8:9}px monospace`; ctx.textAlign="center";
        const bl=currency+(be>=1000?(be/1000).toFixed(1)+"K":be.toFixed(0));
        ctx.fillText("BE "+bl, bx, zeroY+(compact?13:16));
      }
      prevP=curP;
    });

    // Y-axis
    const nL=compact?3:4;
    for(let i=0;i<=nL;i++){
      const v=pnlLo+(pnlHi-pnlLo)*i/nL;
      const y=toY(v);
      ctx.fillStyle=v>0?"#2ed47a":v<0?"#ff5b6c":"#8ea2bf";
      ctx.font=`${compact?8:9}px monospace`; ctx.textAlign="right";
      const lbl=v===0?"0":(Math.abs(v)>=1000?(v/1000).toFixed(1)+"K":v.toFixed(0));
      ctx.fillText(lbl,PAD.l-4,y+3);
    }
    // X-axis
    ctx.fillStyle="#8ea2bf"; ctx.font=`${compact?8:9}px monospace`; ctx.textAlign="center";
    const xT=compact?3:5;
    for(let i=0;i<=xT;i++){
      const p=priceMin+(priceMax-priceMin)*i/xT;
      ctx.fillText(p>=1000?(p/1000).toFixed(1)+"K":p.toFixed(0), toX(p), H-PAD.b+12);
    }

    // Current P&L dot
    const cpnl=totalPnl(spotPrice);
    ctx.fillStyle=cpnl>=0?"#2ed47a":"#ff5b6c";
    ctx.beginPath(); ctx.arc(spotX,toY(cpnl),compact?4:5,0,Math.PI*2); ctx.fill();
    const cpnlLbl=(cpnl>=0?"+":"")+currency+(Math.abs(cpnl)>=1000?(Math.abs(cpnl)/1000).toFixed(1)+"K":Math.abs(cpnl).toFixed(0));
    ctx.font=`bold ${compact?9:10}px monospace`; ctx.textAlign=spotX>W*0.6?"right":"left";
    ctx.fillText(cpnlLbl, spotX+(spotX>W*0.6?-8:8), toY(cpnl)-6);

  },[legs,spotPrice,compact]);

  if(!legs.length||!spotPrice) return null;

  // Stats
  const samplePrices=Array.from({length:201},(_,i)=>spotPrice*0.75+spotPrice*0.5*i/200);
  const samplePnls=samplePrices.map(p=>totalPnl(p));
  const pnlNow=totalPnl(spotPrice);
  const maxP=Math.max(...samplePnls), minP=Math.min(...samplePnls);
  const breakevens=[];
  let pr=samplePnls[0];
  samplePrices.forEach((p,i)=>{ if(i===0) return; const cr=samplePnls[i];
    if((pr<0&&cr>=0)||(pr>=0&&cr<0)){ const be=samplePrices[i-1]+(samplePrices[i]-samplePrices[i-1])*Math.abs(pr)/(Math.abs(pr)+Math.abs(cr)); breakevens.push(be); } pr=cr; });
  const fmt2=v=>{const a=Math.abs(v);const s=a>=1e5?`${currency}${(a/1e5).toFixed(1)}L`:a>=1000?`${currency}${(a/1000).toFixed(1)}K`:`${currency}${a.toFixed(0)}`;return (v>=0?"+":"-")+s;};

  return (
    <div style={{fontFamily:"inherit"}}>
      {!compact&&(
        <div style={{display:"flex",gap:6,marginBottom:8,flexWrap:"wrap"}}>
          {[["P&L NOW",fmt2(pnlNow),pnlNow>=0?"#2ed47a":"#ff5b6c"],
            ["MAX PROFIT",maxP>spotPrice*0.4?"∞":fmt2(maxP),"#2ed47a"],
            ["MAX LOSS",minP<-(spotPrice*0.4)?"∞":fmt2(minP),"#ff5b6c"],
            ["BREAKEVEN",breakevens.length===0?"—":breakevens.map(be=>currency+(be>=1000?(be/1000).toFixed(1)+"K":be.toFixed(0))).join(" / "),"#e8b339"],
          ].map(([l,v,c])=>(
            <div key={l} style={{flex:1,minWidth:72,background:"#0b1624",border:"1px solid #d4c4a8",borderRadius:0,padding:"5px 8px",textAlign:"center"}}>
              <div style={{fontSize:9,color:"#8ea2bf",letterSpacing:1,marginBottom:2}}>{l}</div>
              <div style={{fontSize:12,fontWeight:700,color:c}}>{v}</div>
            </div>
          ))}
        </div>
      )}
      <canvas ref={canvasRef} width={compact?280:340} height={compact?100:160}
        style={{width:"100%",height:compact?100:160,borderRadius:0,border:"1px solid #d4c4a8",background:"#07101c",display:"block"}}/>
      {compact&&breakevens.length>0&&(
        <div style={{fontSize:9,color:"#e8b339",textAlign:"center",marginTop:3}}>
          BE: {breakevens.map(be=>currency+(be>=1000?(be/1000).toFixed(1)+"K":be.toFixed(0))).join(" · ")}
          {" "}· Now: <span style={{color:pnlNow>=0?"#2ed47a":"#ff5b6c"}}>{fmt2(pnlNow)}</span>
        </div>
      )}
    </div>
  );
}

function buildPayoffLegs({strategyId,strike,premium=0,lotSize=1,qty=1,spotPrice=0}){
  switch(strategyId){
    case "sell_pe":  return [{type:"PE",side:"SELL",strike,premium,qty,lotSize}];
    case "sell_ce":  return [{type:"CE",side:"SELL",strike,premium,qty,lotSize}];
    case "buy_ce":   return [{type:"CE",side:"BUY", strike,premium,qty,lotSize}];
    case "buy_pe":   return [{type:"PE",side:"BUY", strike,premium,qty,lotSize}];
    case "buy_fut":  return [{type:"FUT",side:"BUY", strike:spotPrice,premium:0,qty,lotSize}];
    case "sell_fut": return [{type:"FUT",side:"SELL",strike:spotPrice,premium:0,qty,lotSize}];
    default:         return [];
  }
}

// ══════════════════════════════════════════════════════════════════════════════



// ══════════════════════════════════════════════════════════════════════════════
// TAB: AI SCANNER — Claude-powered multi-parameter signal engine
// ══════════════════════════════════════════════════════════════════════════════
const AI_SCAN_DEFAULTS = "NIFTY 50,BANKNIFTY,FINNIFTY,RELIANCE,TCS,HDFCBANK,INFY,ICICIBANK,SBIN,BAJFINANCE";

const SIGNAL_COLORS = {
  BUY:  { bg:"rgba(22,163,74,0.08)",  border:"rgba(63,185,80,0.4)",  dot:"#2ed47a", label:"▲ BUY"  },
  SELL: { bg:"rgba(220,38,38,0.08)",  border:"rgba(248,81,73,0.4)",  dot:"#ff5b6c", label:"▼ SELL" },
  HOLD: { bg:"rgba(139,148,158,0.06)",border:"rgba(139,148,158,0.2)",dot:"#8ea2bf", label:"◆ HOLD" },
};

function ConfidenceBar({ value }) {
  const color = value>=70?"#2ed47a":value>=50?"#e8b339":"#ff5b6c";
  return (
    <div style={{display:"flex",alignItems:"center",gap:6}}>
      <div style={{flex:1,height:4,background:"#1a2c44",borderRadius:2,overflow:"hidden"}}>
        <div style={{height:"100%",width:`${value}%`,background:color,borderRadius:2,
          transition:"width 0.5s ease"}}/>
      </div>
      <span style={{fontSize:10,fontWeight:700,color,minWidth:32}}>{value}%</span>
    </div>
  );
}

// ── AI Signal Box Card ────────────────────────────────────────────────────────
// tabMode: "equity" | "fo" | "mcx" | "delta"
function AISignalCard({ result, onTrade, onShowToast, tabMode="equity", autoFired=false, onAddVirtual=null, canTrade=false, setStrategyPopup=null }) {
  const [expanded,  setExpanded]  = useState(false);
  const [trading,   setTrading]   = useState(false);
  const [traded,    setTraded]    = useState(false);
  const [liveLtp,   setLiveLtp]   = useState(null);  // live price override
  const [priceDir,  setPriceDir]  = useState(null);  // "up" | "down"
  const prevPriceRef = useRef(null);

  // ── Live price refresh every 5s ───────────────────────────────────────────
  useEffect(()=>{
    if(!result?.symbol) return;
    let cancelled = false;
    const fetchLtp = async()=>{
      try {
        // FO underlyings (NIFTY 50, BANKNIFTY) live on NSE not NFO
        // MCX futures use resolved_symbol (e.g. CRUDEOILM25APRFUT)
        const isMCX = tabMode==="mcx";
        const sym   = result.resolved_symbol || result.symbol;
        const exch  = isMCX ? "MCX" : tabMode==="delta" ? "DELTA" : "NSE";
        // Delta prices come from Delta exchange directly via the scan — no NSE lookup
        if(exch === "DELTA") {
          // Try Delta price from scan result directly (already live from Delta)
          const deltaPrice = parseFloat(result.ltp || result.entry_price || 0);
          if(deltaPrice > 0) { setLiveLtp(deltaPrice); prevPriceRef.current = deltaPrice; }
          return;
        }
        // Strip space for indices: "NIFTY 50" → "NIFTY 50" (Kite accepts with space)
        const res = await apiFetch(`/quotes?symbols=${encodeURIComponent(`${exch}:${sym}`)}`);
        // quotes endpoint strips exchange prefix from key
        const q = res[sym] || res[sym.replace(" ","")] || res[result.symbol] || res[result.resolved_symbol];
        const price = parseFloat(q?.ltp || q?.last_price || 0);
        if(!cancelled && price > 0) {
          const prev = prevPriceRef.current;
          setPriceDir(prev ? (price > prev ? "up" : price < prev ? "down" : null) : null);
          prevPriceRef.current = price;
          setLiveLtp(price);
        }
      } catch {}
    };
    fetchLtp(); // immediate fetch
    const t = setInterval(fetchLtp, 5000); // 5s — same as scanner
    return ()=>{ cancelled=true; clearInterval(t); };
  },[result?.symbol, result?.resolved_symbol, tabMode]);

  if(!result) return null;

  const sig = result.signal || "HOLD";
  const isDelta = tabMode==="delta";
  const curr = isDelta?"$":"₹";
  const col = SIGNAL_COLORS[sig] || SIGNAL_COLORS.HOLD;
  if(sig==="ERROR") return (
    <div style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,
      padding:"8px 12px",fontSize:10,color:"#ff5b6c"}}>
      {result.symbol}: {result.error?.includes("IndexError")||result.error?.includes("list index")?'Insufficient candle data — try longer timeframe':result.error}
    </div>
  );

  const rr    = result.risk_reward?.toFixed(1);
  const ltp   = liveLtp || result.ltp || result.entry_price || 0;
  const conf  = result.confidence || 0;
  const instr = result.best_instrument || "STOCK";

  // ── Resolve trade params based on tab type ─────────────────────────────
  const getTradeParams = ()=>{
    // Always use live price for order — never stale AI scan price
    const tradePrice = liveLtp || result.ltp || result.entry_price || 0;
    // Build signal details for position tracking
    const aiReasons = (result.key_reasons||[]).slice(0,3);
    const aiSignalLabel = aiReasons.length>0 ? aiReasons.join(" · ") : (result.summary||`AI ${sig}`);
    const aiSignals = aiReasons.map(r=>({label:r, type:sig, scan:"ai"}));
    if(aiSignals.length===0) aiSignals.push({label:result.summary||`AI ${sig}`, type:sig, scan:"ai"});

    const base = {
      side:        sig,
      price:       tradePrice,
      tpPrice:     result.target_price,
      slPrice:     result.stop_loss,
      signals:     aiSignals,
      signalLabel: aiSignalLabel,
      confidence:  result.confidence||0,
      source:      "ai_scan",
    };

    if(tabMode==="equity") {
      // NSE intraday MIS — trade stock directly
      return { ...base,
        symbol:   result.symbol,
        exchange: "NSE",
        qty:      1,
        product:  "MIS",
        instrumentType: "EQ",
      };
    }
    if(tabMode==="fo") {
      // F&O — AI decides CE/PE/FUT based on best_instrument + margin
      const isOpt = instr==="CE"||instr==="PE";
      return { ...base,
        symbol:   isOpt ? `${result.symbol}${instr}` : `${result.symbol}FUT`,
        exchange: "NFO",
        qty:      result.lot_size || 1,
        product:  "NRML",
        instrumentType: isOpt ? instr : "FUT",
        suggestedStrike: result.suggested_strike,
      };
    }
    if(tabMode==="mcx") {
      return { ...base,
        symbol:   result.resolved_symbol || result.symbol,
        exchange: "MCX",
        qty:      result.lot_size || 1,
        product:  "NRML",
        instrumentType: "FUT",
      };
    }
    if(tabMode==="delta") {
      const isBuy = sig==="BUY";
      // Option selling strategy:
      // BUY signal (bullish) → SELL OTM PUT (PE)
      // SELL signal (bearish) → SELL OTM CALL (CE)
      const ltp = liveLtp || parseFloat(result.ltp || result.entry_price || 0);
      const otmPct = 1.5; // 1.5% OTM
      const peStrike = ltp > 0 ? parseFloat((ltp*(1-otmPct/100)).toFixed(2)) : 0;
      const ceStrike = ltp > 0 ? parseFloat((ltp*(1+otmPct/100)).toFixed(2)) : 0;
      return { ...base,
        symbol:          result.symbol,
        exchange:        "DELTA",
        qty:             1,
        product:         "DELTA",
        // Selling options = SELL side regardless of signal direction
        side:            "SELL",
        instrumentType:  isBuy ? "PE" : "CE",
        suggestedAction: isBuy ? "SELL OTM PUT" : "SELL OTM CALL",
        suggestedStrike: isBuy ? peStrike : ceStrike,
        suggestedInstr:  isBuy ? "Put Option (OTM)" : "Call Option (OTM)",
        // Alternative: directional perp trade
        altAction:       isBuy ? "BUY PERP" : "SELL PERP",
        altPrice:        ltp,
      };
    }
    return { ...base, symbol:result.symbol, exchange:"NSE", qty:1 };
  };

  const handleTrade = async()=>{
    if(trading || traded) return;
    setTrading(true);
    try {
      onTrade(getTradeParams());
      setTraded(true);
    } finally {
      setTrading(false);
    }
  };

  const confColor = conf>=75?"#2ed47a":conf>=55?"#e8b339":"#ff5b6c";
  const instrLabel = tabMode==="equity"?"MIS (Equity)":tabMode==="fo"?"F&O (NRML)":tabMode==="delta"?(sig==="BUY"?"SELL PE":"SELL CE"):instr;

  return (
    <div style={{
      background: traded ? "rgba(22,163,74,0.06)" : col.bg,
      border: `1.5px solid ${autoFired?"#e8b339":traded?"#2ed47a":col.border}`,
      borderLeft: `4px solid ${col.dot}`,
      borderRadius:0, overflow:"hidden",
      opacity: sig==="HOLD" ? 0.7 : 1,
    }}>

      {/* ── BOX TOP — symbol + signal + confidence ── */}
      <div style={{padding:"10px 12px",cursor:"pointer"}}
        onClick={()=>setExpanded(p=>!p)}>

        {/* Row 1: symbol + signal badge + price */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
          <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
            <span style={{fontSize:13,fontWeight:700,color:"#e7edf5"}}>{result.symbol}</span>
            <span style={{fontSize:10,padding:"2px 8px",borderRadius:20,fontWeight:800,
              background: sig==="BUY"?"#15803d":sig==="SELL"?"#991b1b":"#1a2c44",
              color: sig==="BUY"?"#2ed47a":sig==="SELL"?"#ff5b6c":"#8ea2bf",
              border:`1px solid ${col.border}`}}>
              {sig==="BUY"?"▲ BUY":sig==="SELL"?"▼ SELL":"◆ HOLD"}
            </span>
            <span style={{fontSize:12,padding:"1px 6px",borderRadius:0,
              background:"rgba(163,113,247,0.12)",border:"1px solid rgba(163,113,247,0.3)",
              color:"#49c2e4",fontWeight:700}}>{instrLabel}</span>
            {autoFired&&<span style={{fontSize:11,color:"#e8b339",fontWeight:700}}>⚡ AUTO</span>}
            {traded&&<span style={{fontSize:11,color:"#2ed47a",fontWeight:700}}>✓ PLACED</span>}
          </div>
          <div style={{textAlign:"right",flexShrink:0}}>
            <div style={{fontSize:12,fontWeight:700,
              color: liveLtp ? (priceDir==="up"?"#2ed47a":priceDir==="down"?"#ff5b6c":"#e7edf5") : "#5a728f",
              display:"flex",alignItems:"center",gap:3,justifyContent:"flex-end"}}>
              {liveLtp&&(priceDir==="up"?"▲":priceDir==="down"?"▼":"")}
              {liveLtp
                ? `${curr}${isDelta?liveLtp.toFixed(4):liveLtp.toLocaleString("en-IN",{maximumFractionDigits:2})}`
                : result.ltp || result.entry_price
                  ? <span style={{fontSize:10}}>₹{(result.ltp||result.entry_price)?.toLocaleString("en-IN",{maximumFractionDigits:2})} <span style={{fontSize:7,color:"#ff5b6c"}}>STALE</span></span>
                  : <span style={{fontSize:12,color:"#5a728f"}}>fetching…</span>
              }
            </div>
            <div style={{fontSize:11,color:liveLtp?"#e8b339":"#ff5b6c"}}>
              {liveLtp
                ? "🟢 LIVE · 5s"
                : `⚠ scan price · ${result.analyzed_at ? new Date(result.analyzed_at).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"}) : "old"}`}
            </div>
            <div style={{fontSize:11,color:confColor,fontWeight:700}}>{conf}% conf</div>
          </div>
        </div>

        {/* Confidence bar */}
        <div style={{height:3,background:"#1a2c44",borderRadius:2,overflow:"hidden",marginBottom:8}}>
          <div style={{height:"100%",width:`${conf}%`,background:confColor,borderRadius:2}}/>
        </div>

        {/* Row 2: Entry / Target / SL / R:R boxes */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:4,marginBottom:6}}>
          {[
            ["Entry",  liveLtp ? `${curr}${isDelta?liveLtp.toFixed(4):liveLtp.toLocaleString("en-IN",{maximumFractionDigits:2})}` : `₹${result.entry_price||"?"}`, liveLtp?"#e8b339":"#5c3d2e"],
            ["Target", `₹${result.target_price||"?"}`, "#2ed47a"],
            ["SL",     `₹${result.stop_loss||"?"}`,    "#ff5b6c"],
            ["R:R",    rr?`1:${rr}`:"?",               rr>=2?"#2ed47a":rr>=1?"#e8b339":"#ff5b6c"],
          ].map(([l,v,c])=>(
            <div key={l} style={{background:"rgba(0,0,0,0.08)",borderRadius:0,
              padding:"4px 6px",textAlign:"center"}}>
              <div style={{fontSize:7,color:"#5a728f",letterSpacing:0.5}}>{l}</div>
              <div style={{fontSize:10,fontWeight:700,color:c}}>{v}</div>
            </div>
          ))}
        </div>

        {/* Signal clarity + parameter votes */}
        {result.signal_clarity&&(
          <div style={{display:"flex",gap:4,alignItems:"center",marginBottom:5,flexWrap:"wrap"}}>
            {/* Clarity badge */}
            <span style={{fontSize:12,padding:"1px 7px",borderRadius:0,fontWeight:800,
              background:
                result.signal_clarity==="CLEAR"  ?"rgba(63,185,80,0.2)":
                result.signal_clarity==="STRONG" ?"rgba(0,212,170,0.15)":
                result.signal_clarity==="MODERATE"?"rgba(96,165,250,0.15)":
                "rgba(220,38,38,0.15)",
              color:
                result.signal_clarity==="CLEAR"  ?"#2ed47a":
                result.signal_clarity==="STRONG" ?"#e8b339":
                result.signal_clarity==="MODERATE"?"#e8b339":
                "#ff5b6c",
              border:`1px solid ${
                result.signal_clarity==="CLEAR"  ?"rgba(63,185,80,0.4)":
                result.signal_clarity==="STRONG" ?"rgba(0,212,170,0.3)":
                result.signal_clarity==="MODERATE"?"rgba(96,165,250,0.3)":
                "rgba(248,81,73,0.4)"}`}}>
              {result.signal_clarity==="CLEAR"?"✓ CLEAR":
               result.signal_clarity==="STRONG"?"✓ STRONG":
               result.signal_clarity==="MODERATE"?"⚠ MODERATE":
               "✗ MIXED"}
            </span>
            {/* Parameter votes */}
            {result.parameters_aligned!=null&&(
              <span style={{fontSize:11,color:"#5a728f"}}>
                {result.parameters_aligned}/5 agree
              </span>
            )}
            {/* Individual parameter vote dots */}
            {result.parameter_votes&&Object.entries(result.parameter_votes).map(([k,v])=>{
              const bullish = v==="BULLISH"||v==="CONFIRMING"||v==="BUYING";
              const bearish = v==="BEARISH"||v==="WEAK"||v==="SELLING";
              const labels = {technical:"T",volume:"V",news:"N",institutional:"I",fo_data:"F"};
              return (
                <div key={k} title={`${k}: ${v}`}
                  style={{width:16,height:16,borderRadius:"50%",
                    background:bullish?"#2ed47a":bearish?"#ff5b6c":"#23405f",
                    display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <span style={{fontSize:7,color:"#fff",fontWeight:800}}>{labels[k]}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Row 3: RSI / Vol / IV / PCR / OI / Delivery / News mini stats */}
        <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
          {[
            ["RSI",  result.rsi,                              result.rsi>70?"#ff5b6c":result.rsi<30?"#2ed47a":"#8ea2bf"],
            ["Vol",  result.vol_ratio?`${result.vol_ratio}x`:null, result.vol_ratio>=2?"#2ed47a":"#8ea2bf"],
            ["IV",   result.iv?`${result.iv}%`:null,          result.iv>40?"#e8b339":result.iv>25?"#8ea2bf":"#49c2e4"],
            ["PCR",  result.pcr,                              result.pcr>1.2?"#2ed47a":result.pcr<0.8?"#ff5b6c":"#8ea2bf"],
            ["OI",   result.oi?`${(result.oi/1000).toFixed(0)}K`:null, "#8ea2bf"],
            ["MP",   result.max_pain?`₹${result.max_pain.toLocaleString("en-IN")}`:null, "#e8b339"],
            ["Delv", result.delivery_pct?`${result.delivery_pct}%`:null, result.delivery_pct>=60?"#2ed47a":"#8ea2bf"],
            ["FII",  result.fii_net!=null?`${result.fii_net>0?"+":""}${result.fii_net}Cr`:null, result.fii_net>0?"#2ed47a":"#ff5b6c"],
            ["News", result.news_sentiment,                   result.news_sentiment==="BULLISH"?"#2ed47a":result.news_sentiment==="BEARISH"?"#ff5b6c":"#8ea2bf"],
          ].filter(([,v])=>v!=null).map(([l,v,c])=>(
            <div key={l} style={{background:"rgba(232,179,57,0.06)",borderRadius:0,
              padding:"2px 6px",display:"flex",gap:3,alignItems:"center"}}>
              <span style={{fontSize:11,color:"#5a728f"}}>{l}</span>
              <span style={{fontSize:12,fontWeight:700,color:c}}>{v}</span>
            </div>
          ))}
        </div>

        {/* Scan timestamp */}
        {result.analyzed_at && (
          <div style={{fontSize:9,color:"#484f58",marginTop:3}}>
            Scanned: {new Date(result.analyzed_at).toLocaleString("en-IN",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"})}
          </div>
        )}

        {/* S/R level quick view */}
        {result.sr && (result.sr.nearest_support || result.sr.nearest_resistance) && (
          <div style={{display:"flex",gap:6,marginTop:5,padding:"5px 8px",
            background:"rgba(232,179,57,0.04)",borderRadius:0,
            border:"1px solid rgba(255,255,255,0.06)"}}>
            {result.sr.nearest_resistance && (
              <div style={{display:"flex",alignItems:"center",gap:3}}>
                <span style={{fontSize:11,color:"#ff5b6c",fontWeight:800}}>R</span>
                <span style={{fontSize:10,color:"#ff5b6c",fontWeight:700}}>
                  {isDelta?`$${parseFloat(result.sr.nearest_resistance).toFixed(2)}`:`₹${parseFloat(result.sr.nearest_resistance).toLocaleString("en-IN",{maximumFractionDigits:1})}`}
                </span>
                {result.sr.dist_to_resistance!=null&&(
                  <span style={{fontSize:11,color:"#5a728f"}}>{result.sr.dist_to_resistance}%↑</span>
                )}
                {(result.sr.supply||[]).length>0&&(
                  <span style={{fontSize:7,padding:"0 3px",borderRadius:2,
                    background:"rgba(220,38,38,0.15)",color:"#ff5b6c",
                    border:"1px solid rgba(248,81,73,0.3)"}}>SUPPLY</span>
                )}
              </div>
            )}
            {result.sr.nearest_resistance && result.sr.nearest_support && (
              <span style={{color:"#1a2c44",fontSize:12}}>|</span>
            )}
            {result.sr.nearest_support && (
              <div style={{display:"flex",alignItems:"center",gap:3}}>
                <span style={{fontSize:11,color:"#2ed47a",fontWeight:800}}>S</span>
                <span style={{fontSize:10,color:"#2ed47a",fontWeight:700}}>
                  {isDelta?`$${parseFloat(result.sr.nearest_support).toFixed(2)}`:`₹${parseFloat(result.sr.nearest_support).toLocaleString("en-IN",{maximumFractionDigits:1})}`}
                </span>
                {result.sr.dist_to_support!=null&&(
                  <span style={{fontSize:11,color:"#5a728f"}}>{result.sr.dist_to_support}%↓</span>
                )}
                {(result.sr.demand||[]).length>0&&(
                  <span style={{fontSize:7,padding:"0 3px",borderRadius:2,
                    background:"rgba(22,163,74,0.15)",color:"#2ed47a",
                    border:"1px solid rgba(63,185,80,0.3)"}}>DEMAND</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Dynamic Demand/Supply zone range — always visible */}
        {(result.demandLow != null || result.supplyLow != null) && (
          <div style={{display:"flex",gap:8,marginTop:5,padding:"4px 8px",
            background:"rgba(0,0,0,0.15)",borderRadius:0,
            border:"1px solid rgba(255,255,255,0.06)"}}>
            {result.demandLow != null && result.demandHigh != null && (
              <div style={{display:"flex",alignItems:"center",gap:3}}>
                <span style={{fontSize:7,padding:"0 3px",borderRadius:2,
                  background:"rgba(0,200,120,0.15)",color:"#00c878",fontWeight:700,
                  border:"1px solid rgba(0,200,120,0.3)"}}>DEMAND</span>
                <span style={{fontSize:10,color:"#00c878",fontWeight:600}}>
                  {isDelta?`$${result.demandLow.toFixed(1)}–${result.demandHigh.toFixed(1)}`:`₹${parseFloat(result.demandLow).toLocaleString("en-IN",{maximumFractionDigits:1})}–${parseFloat(result.demandHigh).toLocaleString("en-IN",{maximumFractionDigits:1})}`}
                </span>
              </div>
            )}
            {result.supplyLow != null && result.supplyHigh != null && (
              <div style={{display:"flex",alignItems:"center",gap:3}}>
                <span style={{fontSize:7,padding:"0 3px",borderRadius:2,
                  background:"rgba(255,80,80,0.15)",color:"#ff5050",fontWeight:700,
                  border:"1px solid rgba(255,80,80,0.3)"}}>SUPPLY</span>
                <span style={{fontSize:10,color:"#ff5050",fontWeight:600}}>
                  {isDelta?`$${result.supplyLow.toFixed(1)}–${result.supplyHigh.toFixed(1)}`:`₹${parseFloat(result.supplyLow).toLocaleString("en-IN",{maximumFractionDigits:1})}–${parseFloat(result.supplyHigh).toLocaleString("en-IN",{maximumFractionDigits:1})}`}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Summary line */}
        {result.summary&&(
          <div style={{marginTop:6,fontSize:10,color:"#8ea2bf",lineHeight:1.4,
            fontStyle:"italic",borderTop:"1px solid rgba(255,255,255,0.04)",paddingTop:6}}>
            {result.summary}
          </div>
        )}
      </div>

      {/* ── ONE-CLICK TRADE BUTTON ── always visible, not behind expand */}
      {sig!=="HOLD"&&(
        <div style={{padding:"0 10px 10px",display:"flex",gap:6,flexDirection:"column"}} onClick={e=>e.stopPropagation()}>
          {result.signal_clarity==="MIXED"&&(
            <div style={{fontSize:12,color:"#ff5b6c",padding:"4px 8px",
              background:"rgba(220,38,38,0.08)",borderRadius:0,
              border:"1px solid rgba(248,81,73,0.2)",textAlign:"center"}}>
              ✗ Mixed signals — trade blocked. Parameters conflicting ({result.parameters_aligned||0}/5 agree)
            </div>
          )}
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {/* F&O Strategy button — shown for equity AND fo tabs */}
          {canTrade && setStrategyPopup && (tabMode==="equity"||tabMode==="fo") && (liveLtp||result.ltp||result.entry_price)>0 && (
            <button onClick={()=>setStrategyPopup({
                symbol:  result.symbol,
                exchange:"NSE",
                ltp:     liveLtp||result.ltp||result.entry_price||0,
                hasBuy:  sig==="BUY",
                hasSell: sig==="SELL",
                signals: result.key_reasons ? result.key_reasons.map(r=>({label:r,type:sig})) : [],
                lotSize: result.lot_size||50,
                mode:    "fo",
              })}
              style={{background: tabMode==="fo" ? "rgba(163,113,247,0.15)" : "rgba(232,179,57,0.1)",
                border: tabMode==="fo" ? "1px solid rgba(163,113,247,0.45)" : "1px solid rgba(232,179,57,0.35)",
                borderRadius:0,padding:"9px 10px",color:"#49c2e4",fontFamily:"inherit",
                fontSize:11,fontWeight:700,cursor:"pointer",minHeight:40,whiteSpace:"nowrap",
                flex: tabMode==="fo" ? 1 : "none"}}>
              🎯 F&O Strategy
            </button>
          )}
          {/* GTT is now auto-created via OrderTicket after main order fills */}
          {/* In F&O mode, hide direct equity trade button — user should use F&O Strategy */}
          {canTrade && tabMode!=="fo" ? (
            <button onClick={handleTrade} disabled={trading||traded||result.signal_clarity==="MIXED"}
              style={{flex:1,
                background: traded
                  ? "rgba(22,163,74,0.15)"
                  : sig==="BUY"
                    ? "linear-gradient(135deg,#1a7f37,#3fb950)"
                    : "linear-gradient(135deg,#8b1a1a,#f85149)",
                border:"none",borderRadius:0,padding:"9px 12px",
                color: traded?"#2ed47a":"#fff",
                fontFamily:"inherit",fontSize:11,fontWeight:700,
                cursor:trading||traded?"default":"pointer",
                minHeight:40,letterSpacing:0.5,
                opacity:trading?0.7:1}}>
              {trading ? "⟳ Placing..." :
               traded  ? "✓ Order placed" :
               result.signal_clarity==="MIXED" ? "✗ Mixed — blocked" :
               `⚡ ${sig} ${instrLabel} — ${curr}${isDelta?(liveLtp||result.entry_price||ltp)?.toFixed(4):(liveLtp||result.entry_price||ltp)?.toLocaleString("en-IN",{maximumFractionDigits:2})}`}
            </button>
          ) : !canTrade ? (
            /* Viewer — show signal summary, no trade button */
            <div style={{flex:1,background:"rgba(96,165,250,0.06)",
              border:"1px solid #1a2c44",borderRadius:0,
              padding:"9px 12px",minHeight:40,display:"flex",
              alignItems:"center",gap:8}}>
              <span style={{fontSize:10,fontWeight:700,
                color:sig==="BUY"?"#2ed47a":sig==="SELL"?"#ff5b6c":"#8ea2bf"}}>
                {sig==="BUY"?"▲ BUY signal":sig==="SELL"?"▼ SELL signal":"◆ HOLD"}
              </span>
              {result.entry_price>0&&(
                <span style={{fontSize:12,color:"#8ea2bf"}}>
                  Entry ₹{parseFloat(result.entry_price).toLocaleString("en-IN",{maximumFractionDigits:2})}
                </span>
              )}
              {result.target_price>0&&(
                <span style={{fontSize:12,color:"#2ed47a"}}>
                  TP ₹{parseFloat(result.target_price).toLocaleString("en-IN",{maximumFractionDigits:2})}
                </span>
              )}
              {result.stop_loss>0&&(
                <span style={{fontSize:12,color:"#ff5b6c"}}>
                  SL ₹{parseFloat(result.stop_loss).toLocaleString("en-IN",{maximumFractionDigits:2})}
                </span>
              )}
            </div>
          ) : null /* F&O mode — strategy button above is primary */}
          <button onClick={()=>setExpanded(p=>!p)}
            style={{background:"rgba(232,179,57,0.07)",border:"1px solid #1a2c44",
              borderRadius:0,padding:"9px 10px",color:"#8ea2bf",
              fontFamily:"inherit",fontSize:10,cursor:"pointer",minHeight:40}}>
            {expanded?"▲":"▼"}
          </button>
          </div>
          {/* Add to Virtual button */}
          {sig!=="HOLD"&&onAddVirtual&&(
            <button onClick={()=>onAddVirtual({
              symbol:  result.symbol,
              side:    sig,
              entry:   result.entry_price||ltp,
              tp:      result.target_price,
              sl:      result.stop_loss,
              qty:     1,
              tab_mode: tabMode,
              source:  "ai",
              signal:  sig,
              confidence: result.confidence,
              clarity:    result.signal_clarity,
              key_reasons: result.key_reasons,
            })}
              style={{width:"100%",background:"rgba(96,165,250,0.08)",
                border:"1px solid rgba(96,165,250,0.2)",borderRadius:7,
                padding:"6px 0",color:"#6b7280",fontFamily:"inherit",
                fontSize:10,cursor:"pointer",marginTop:4}}>
              🎮 Add to Virtual
            </button>
          )}
        </div>
      )}

      {/* ── EXPANDED DETAIL ── */}
      {expanded&&(
        <div style={{padding:"0 12px 12px",borderTop:"1px solid rgba(255,255,255,0.05)"}}>
          {/* ── Delta/Crypto Strategy Recommendation ── */}
          {tabMode==="delta"&&sig!=="HOLD"&&(
            <div style={{marginTop:8,padding:"10px 12px",
              background:"rgba(232,179,57,0.06)",borderRadius:0,
              border:"1px solid rgba(232,179,57,0.3)"}}>
              <div style={{fontSize:10,color:"#e8b339",letterSpacing:1,fontWeight:700,marginBottom:8}}>
                ₿ OPTION SELLING STRATEGY
              </div>
              {(()=>{
                const isBuy = sig==="BUY";  // BUY signal = bullish = SELL PE
                // ── Determine strategy from signal clarity + S/R levels ─────
                const votes   = result.parameter_votes || {};
                const techDir = votes.technical || "";
                const clarity = result.signal_clarity || "MODERATE";
                const hasSupport    = result.sr?.nearest_support;
                const hasResistance = result.sr?.nearest_resistance;
                const distToSup = parseFloat(result.sr?.dist_to_support || 99);
                const distToRes = parseFloat(result.sr?.dist_to_resistance || 99);
                const ltp = liveLtp || parseFloat(result.ltp || result.entry_price || 0);

                // ── Rule 1: Price near support + bullish signal → SELL OTM PE ─
                const nearSupport    = hasSupport && distToSup <= 2.5;
                // ── Rule 2: Price near resistance + bearish signal → SELL OTM CE
                const nearResistance = hasResistance && distToRes <= 2.5;

                // ── Rule 3: Single clear signal (TSI or EMA cross) ────────────
                const tsiSignal = techDir==="BULLISH"||techDir==="BEARISH";
                const clearSignal = clarity==="CLEAR"||clarity==="STRONG";
                const singleSignal = clearSignal && tsiSignal;

                // ── Determine primary recommendation ─────────────────────────
                // Option selling logic:
                // BUY signal (bullish) → SELL OTM PE (collect put premium)
                // SELL signal (bearish) → SELL OTM CE (collect call premium)
                const sellPE = isBuy;
                const sellCE = !isBuy;

                // OTM strike suggestion: 1–2% OTM from current price
                const otmPct = 1.5;
                const peStrike  = ltp > 0 ? (ltp * (1 - otmPct/100)) : null;
                const ceStrike  = ltp > 0 ? (ltp * (1 + otmPct/100)) : null;
                const recStrike = sellPE ? peStrike : ceStrike;

                // Reason why this trade makes sense
                const reasonParts = [];
                if(nearSupport && sellPE)    reasonParts.push(`Price within ${distToSup.toFixed(1)}% of support $${parseFloat(hasSupport).toFixed(2)} — bounce likely`);
                if(nearResistance && sellCE) reasonParts.push(`Price within ${distToRes.toFixed(1)}% of resistance $${parseFloat(hasResistance).toFixed(2)} — rejection likely`);
                if(singleSignal)             reasonParts.push(`${techDir} ${clarity.toLowerCase()} signal — ${isBuy?"TSI/EMA crossed above":"TSI/EMA crossed below"}`);
                if(!reasonParts.length)      reasonParts.push(`${clarity} ${sig} signal from technical analysis`);

                // Max profit = premium collected | Max loss = large adverse move
                // Break-even = strike ± premium
                const premiumEst = ltp > 0 ? (ltp * 0.015).toFixed(4) : "?";  // ~1.5% of spot as rough premium
                const breakEven  = ltp > 0 && recStrike
                  ? sellPE
                    ? (recStrike - parseFloat(premiumEst)).toFixed(4)
                    : (recStrike + parseFloat(premiumEst)).toFixed(4)
                  : "?";

                return (
                  <div>
                    {/* Primary recommendation */}
                    <div style={{background:sellPE?"rgba(22,163,74,0.12)":"rgba(220,38,38,0.12)",
                      border:`2px solid ${sellPE?"#2ed47a":"#ff5b6c"}`,
                      borderRadius:0,padding:"10px 12px",marginBottom:8}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                        <div>
                          <div style={{fontSize:13,fontWeight:800,color:sellPE?"#2ed47a":"#ff5b6c"}}>
                            {sellPE?"📈 SELL OTM PUT":"📉 SELL OTM CALL"}
                          </div>
                          <div style={{fontSize:10,color:"#8ea2bf",marginTop:2}}>
                            {sellPE
                              ? "Collect premium — profitable if price stays above strike"
                              : "Collect premium — profitable if price stays below strike"}
                          </div>
                        </div>
                        <div style={{textAlign:"right",flexShrink:0}}>
                          <div style={{fontSize:9,color:"#5a728f"}}>SUGGESTED STRIKE</div>
                          <div style={{fontSize:13,fontWeight:800,color:"#e7edf5"}}>
                            {recStrike ? `$${recStrike.toFixed(2)}` : "~1.5% OTM"}
                          </div>
                          <div style={{fontSize:9,color:"#8ea2bf"}}>{otmPct}% OTM</div>
                        </div>
                      </div>

                      {/* Trade metrics */}
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:5,marginBottom:6}}>
                        {[
                          ["Max Profit", `~$${premiumEst}`, "#2ed47a"],
                          ["Break-even",  `$${breakEven}`,   "#e8b339"],
                          ["Max Loss",   "Unlimited*",      "#ff5b6c"],
                        ].map(([l,v,c])=>(
                          <div key={l} style={{background:"rgba(0,0,0,0.06)",borderRadius:0,padding:"4px 6px",textAlign:"center"}}>
                            <div style={{fontSize:9,color:"#5a728f"}}>{l}</div>
                            <div style={{fontSize:10,fontWeight:700,color:c}}>{v}</div>
                          </div>
                        ))}
                      </div>

                      {/* Reason */}
                      <div style={{fontSize:10,color:"#5c3d2e",lineHeight:1.5}}>
                        <b>Why:</b> {reasonParts[0]}
                      </div>
                    </div>

                    {/* Signal trigger details */}
                    <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:6}}>
                      {nearSupport&&sellPE&&(
                        <div style={{fontSize:10,padding:"3px 8px",borderRadius:0,
                          background:"rgba(22,163,74,0.1)",border:"1px solid rgba(22,163,74,0.3)",color:"#2ed47a"}}>
                          📍 Support @ ${parseFloat(hasSupport).toFixed(2)} · {distToSup.toFixed(1)}% away
                        </div>
                      )}
                      {nearResistance&&sellCE&&(
                        <div style={{fontSize:10,padding:"3px 8px",borderRadius:0,
                          background:"rgba(220,38,38,0.1)",border:"1px solid rgba(220,38,38,0.3)",color:"#ff5b6c"}}>
                          📍 Resistance @ ${parseFloat(hasResistance).toFixed(2)} · {distToRes.toFixed(1)}% away
                        </div>
                      )}
                      {singleSignal&&(
                        <div style={{fontSize:10,padding:"3px 8px",borderRadius:0,
                          background:"rgba(232,179,57,0.1)",border:"1px solid rgba(232,179,57,0.3)",color:"#e8b339"}}>
                          {isBuy?"TSI/EMA ↑ Cross":"TSI/EMA ↓ Cross"} · {clarity}
                        </div>
                      )}
                      {result.vol_ratio&&(
                        <div style={{fontSize:10,padding:"3px 8px",borderRadius:0,
                          background:"rgba(107,114,128,0.08)",border:"1px solid rgba(107,114,128,0.2)",color:"#6b7280"}}>
                          Vol {result.vol_ratio}× avg
                        </div>
                      )}
                    </div>

                    {/* Alternative — directional futures trade */}
                    <div style={{background:"rgba(107,114,128,0.06)",border:"1px solid rgba(107,114,128,0.2)",
                      borderRadius:0,padding:"6px 8px",marginBottom:4}}>
                      <div style={{fontSize:10,fontWeight:700,color:"#6b7280",marginBottom:2}}>
                        Alternative: {isBuy?"BUY PERP (Long)":"SELL PERP (Short)"}
                      </div>
                      <div style={{fontSize:10,color:"#8ea2bf"}}>
                        Directional futures trade if you prefer leverage over premium collection.
                        {ltp>0&&result.target_price&&` TP $${parseFloat(result.target_price).toFixed(2)} · SL $${parseFloat(result.stop_loss||0).toFixed(2)}`}
                      </div>
                    </div>

                    {/* Risk warning */}
                    <div style={{fontSize:9,color:"#5a728f",lineHeight:1.5,borderTop:"1px solid #d4c4a8",paddingTop:5,marginTop:2}}>
                      ⚠ Option selling has unlimited loss risk. Always set a stop-loss or hedge. 
                      Premium collected if option expires worthless. Close early at 50% profit.
                      *Max loss = large adverse move beyond strike ± premium.
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {result.key_reasons?.length>0&&(
            <div style={{marginTop:8}}>
              <div style={{fontSize:11,color:"#e8b339",letterSpacing:1,fontWeight:700,marginBottom:5}}>
                ✓ WHY {sig}
              </div>
              {result.key_reasons.map((r,i)=>(
                <div key={i} style={{fontSize:10,color:"#5c3d2e",marginBottom:3,
                  paddingLeft:10,borderLeft:"2px solid #92400e44",lineHeight:1.4}}>
                  {r}
                </div>
              ))}
            </div>
          )}
          {result.risk_factors?.length>0&&(
            <div style={{marginTop:8}}>
              <div style={{fontSize:11,color:"#ff5b6c",letterSpacing:1,fontWeight:700,marginBottom:5}}>
                ⚠ RISKS
              </div>
              {result.risk_factors.map((r,i)=>(
                <div key={i} style={{fontSize:10,color:"#5c3d2e",marginBottom:3,
                  paddingLeft:10,borderLeft:"2px solid #f8514944",lineHeight:1.4}}>
                  {r}
                </div>
              ))}
            </div>
          )}

          {/* ── S/R Full Detail ── */}
          {result.sr && (
            (result.sr.resistance||[]).length>0 ||
            (result.sr.support||[]).length>0 ||
            (result.sr.supply||[]).length>0 ||
            (result.sr.demand||[]).length>0
          ) && (
            <div style={{marginTop:10}}>
              <div style={{fontSize:11,color:"#6b7280",letterSpacing:1,fontWeight:700,marginBottom:6}}>
                📊 S/R & DEMAND/SUPPLY ZONES
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                {/* Resistance zones */}
                {(result.sr.resistance||[]).length>0&&(
                  <div style={{background:"rgba(248,81,73,0.05)",borderRadius:0,padding:"8px 10px",
                    border:"1px solid rgba(248,81,73,0.15)"}}>
                    <div style={{fontSize:11,color:"#ff5b6c",fontWeight:700,marginBottom:5}}>
                      RESISTANCE
                    </div>
                    {(result.sr.resistance||[]).slice(0,4).map((z,i)=>(
                      <div key={i} style={{display:"flex",justifyContent:"space-between",
                        marginBottom:3,fontSize:12}}>
                        <span style={{color:"#ff5b6c",fontWeight:700}}>
                          ₹{parseFloat(z.price||z.zone_high||0).toLocaleString("en-IN",{maximumFractionDigits:1})}
                        </span>
                        <span style={{color:"#5a728f",fontSize:11}}>
                          {z.touches>1?`${z.touches}x`:""}
                          {z.strength?" "+z.strength:""}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Support zones */}
                {(result.sr.support||[]).length>0&&(
                  <div style={{background:"rgba(63,185,80,0.05)",borderRadius:0,padding:"8px 10px",
                    border:"1px solid rgba(63,185,80,0.15)"}}>
                    <div style={{fontSize:11,color:"#2ed47a",fontWeight:700,marginBottom:5}}>
                      SUPPORT
                    </div>
                    {(result.sr.support||[]).slice(0,4).map((z,i)=>(
                      <div key={i} style={{display:"flex",justifyContent:"space-between",
                        marginBottom:3,fontSize:12}}>
                        <span style={{color:"#2ed47a",fontWeight:700}}>
                          ₹{parseFloat(z.price||z.zone_low||0).toLocaleString("en-IN",{maximumFractionDigits:1})}
                        </span>
                        <span style={{color:"#5a728f",fontSize:11}}>
                          {z.touches>1?`${z.touches}x`:""}
                          {z.strength?" "+z.strength:""}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Supply zones */}
                {(result.sr.supply||[]).length>0&&(
                  <div style={{background:"rgba(248,81,73,0.04)",borderRadius:0,padding:"8px 10px",
                    border:"1px solid rgba(248,81,73,0.12)"}}>
                    <div style={{fontSize:11,color:"#ff5b6c",fontWeight:700,marginBottom:5}}>
                      📦 SUPPLY ZONE
                    </div>
                    {(result.sr.supply||[]).slice(0,2).map((z,i)=>(
                      <div key={i} style={{fontSize:12,color:"#ff5b6c",marginBottom:3}}>
                        ₹{parseFloat(z.zone_low||0).toLocaleString("en-IN",{maximumFractionDigits:1})}
                        {" – "}
                        ₹{parseFloat(z.zone_high||0).toLocaleString("en-IN",{maximumFractionDigits:1})}
                        <span style={{color:"#5a728f",fontSize:11,marginLeft:4}}>
                          (vol {z.volume_ratio?.toFixed(1)||"?"}x)
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Demand zones */}
                {(result.sr.demand||[]).length>0&&(
                  <div style={{background:"rgba(63,185,80,0.04)",borderRadius:0,padding:"8px 10px",
                    border:"1px solid rgba(63,185,80,0.12)"}}>
                    <div style={{fontSize:11,color:"#2ed47a",fontWeight:700,marginBottom:5}}>
                      📦 DEMAND ZONE
                    </div>
                    {(result.sr.demand||[]).slice(0,2).map((z,i)=>(
                      <div key={i} style={{fontSize:12,color:"#2ed47a",marginBottom:3}}>
                        ₹{parseFloat(z.zone_low||0).toLocaleString("en-IN",{maximumFractionDigits:1})}
                        {" – "}
                        ₹{parseFloat(z.zone_high||0).toLocaleString("en-IN",{maximumFractionDigits:1})}
                        <span style={{color:"#5a728f",fontSize:11,marginLeft:4}}>
                          (vol {z.volume_ratio?.toFixed(1)||"?"}x)
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Context: is price near support or resistance? */}
              {result.sr.nearest_support && result.sr.nearest_resistance && ltp > 0 && (
                <div style={{marginTop:6,fontSize:12,color:"#8ea2bf",
                  padding:"4px 8px",background:"rgba(232,179,57,0.04)",borderRadius:0}}>
                  {(() => {
                    const distS = Math.abs(ltp - result.sr.nearest_support) / ltp * 100;
                    const distR = Math.abs(result.sr.nearest_resistance - ltp) / ltp * 100;
                    if(distS < 1) return <span style={{color:"#2ed47a"}}>⚠ Price near SUPPORT — watch for bounce</span>;
                    if(distR < 1) return <span style={{color:"#ff5b6c"}}>⚠ Price near RESISTANCE — watch for rejection</span>;
                    const rr = (distR/distS).toFixed(1);
                    return <span>Risk/Reward to levels: <b style={{color:"#e7edf5"}}>{rr}:1</b> (S {distS.toFixed(1)}% away · R {distR.toFixed(1)}% away)</span>;
                  })()}
                </div>
              )}
            </div>
          )}

          <div style={{fontSize:11,color:"#5a728f",marginTop:8}}>
            {result.analyzed_at&&`Analyzed: ${new Date(result.analyzed_at).toLocaleTimeString("en-IN")}`}
            {result.cached&&" · cached"}
          </div>
        </div>
      )}
    </div>
  );
}

function AIScannerTab({ onTrade, onShowToast, authed, positions, indexQuotes }) {
  const [symbols,    setSymbols]    = useLocalStorage("ai_scan_symbols", AI_SCAN_DEFAULTS);
  const [timeframe,  setTimeframe]  = useState("15min");
  const [exchange,   setExchange]   = useState("NSE");
  const [results,    setResults]    = useState([]);
  const [scanning,   setScanning]   = useState(false);
  const [progress,   setProgress]   = useState(0);   // 0-100
  const [error,      setError]      = useState(null);
  const [lastScan,   setLastScan]   = useState(null);
  const [autoMode,   setAutoMode]   = useState(false);
  const [interval,   setInterval_]  = useState(15);   // minutes
  const [filterSig,  setFilterSig]  = useState("ALL");
  const [showConfig, setShowConfig] = useState(false);
  const [minConf,    setMinConf]    = useState(60);   // min confidence to show
  const autoRef = useRef(null);

  const symList = useMemo(()=>
    symbols.split(",").map(s=>s.trim().toUpperCase()).filter(Boolean)
  ,[symbols]);

  const filteredResults = useMemo(()=>
    results
      .filter(r=>filterSig==="ALL"||r.signal===filterSig)
      .filter(r=>r.signal==="ERROR"||(r.confidence||0)>=minConf)
  ,[results, filterSig, minConf]);

  const runScan = useCallback(async()=>{
    if(!authed||scanning) return;
    setScanning(true); setError(null); setProgress(0);
    try {
      // Async job pattern — avoids Cloudflare 100s timeout
      setProgress(10);
      const startResp = await apiFetch("/ai/scan/start", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({symbols:symList, exchange, timeframe})
      });
      const aiJobId = startResp?.job_id;
      if(!aiJobId) throw new Error("Server did not return a job ID");
      const _aiPollStart = Date.now();
      let aiData = null;
      while(Date.now() - _aiPollStart < 300_000) { // 5 min max
        await new Promise(r=>setTimeout(r,3000));
        const poll = await apiFetch(`/ai/scan/result/${aiJobId}`);
        if(poll?.status==="done") { aiData = poll.result; break; }
        if(poll?.status==="error") throw new Error(poll.error||"AI scan failed");
        if(poll?.progress) setProgress(p=>Math.min(p+5,90));
      }
      if(!aiData) throw new Error("AI scan timed out");
      setProgress(100);
      setResults(aiData.results||[]);
      setLastScan(new Date());
    } catch(e){
      setError(e.message);
    } finally {
      setScanning(false);
      setTimeout(()=>setProgress(0),1000);
    }
  },[authed, scanning, symList, exchange, timeframe]);

  // Auto scan loop
  useEffect(()=>{
    if(autoMode&&authed){
      runScan();
      autoRef.current = setInterval(runScan, interval*60*1000);
    } else {
      if(autoRef.current){ clearInterval(autoRef.current); autoRef.current=null; }
    }
    return()=>{ if(autoRef.current) clearInterval(autoRef.current); };
  },[autoMode]);

  const buyCount  = results.filter(r=>r.signal==="BUY").length;
  const sellCount = results.filter(r=>r.signal==="SELL").length;
  const holdCount = results.filter(r=>r.signal==="HOLD").length;

  return (
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:"#07101c"}}>

      {/* Header */}
      <div style={{padding:"10px 14px",borderBottom:"1px solid #1a2c44",flexShrink:0,
        background:"linear-gradient(135deg,rgba(163,113,247,0.06),rgba(0,212,170,0.04))"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:"#e7edf5"}}>🤖 AI Signal Scanner</div>
            <div style={{fontSize:12,color:"#8ea2bf"}}>Claude analyzes Technical + Volume + News + Fundamentals + F&O + Institutional</div>
          </div>
          <button onClick={()=>setShowConfig(p=>!p)}
            style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,
              padding:"5px 10px",color:"#8ea2bf",fontFamily:"inherit",fontSize:10,cursor:"pointer"}}>
            ⚙️
          </button>
        </div>

        {/* Signal summary counts */}
        {results.length>0&&(
          <div style={{display:"flex",gap:8,marginBottom:8}}>
            {[["BUY",buyCount,"#2ed47a"],["SELL",sellCount,"#ff5b6c"],["HOLD",holdCount,"#8ea2bf"]].map(([s,c,col])=>(
              <div key={s} style={{background:`${col}18`,border:`1px solid ${col}44`,
                borderRadius:0,padding:"3px 10px",cursor:"pointer",
                outline:filterSig===s?`1px solid ${col}`:"none"}}
                onClick={()=>setFilterSig(p=>p===s?"ALL":s)}>
                <span style={{fontSize:10,fontWeight:700,color:col}}>{s}: {c}</span>
              </div>
            ))}
            <div style={{marginLeft:"auto",fontSize:12,color:"#5a728f",display:"flex",alignItems:"center"}}>
              {lastScan&&`Last: ${lastScan.toLocaleTimeString("en-IN")}`}
            </div>
          </div>
        )}

        {/* Controls */}
        <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
          <button onClick={runScan} disabled={scanning||!authed}
            style={{background:scanning?"#1a2c44":"linear-gradient(135deg,#a16207,#92400e)",
              border:"none",borderRadius:7,padding:"8px 16px",color:"#fff",
              fontFamily:"inherit",fontSize:11,fontWeight:700,
              cursor:scanning?"default":"pointer",opacity:scanning?0.6:1,minHeight:36}}>
            {scanning?`⟳ Analyzing ${symList.length} symbols...`:"🤖 Run AI Scan"}
          </button>
          <button onClick={()=>setAutoMode(p=>!p)}
            style={{background:autoMode?"rgba(220,38,38,0.12)":"rgba(22,163,74,0.08)",
              border:`1px solid ${autoMode?"rgba(248,81,73,0.4)":"rgba(63,185,80,0.3)"}`,
              borderRadius:7,padding:"8px 12px",
              color:autoMode?"#ff5b6c":"#2ed47a",fontFamily:"inherit",
              fontSize:10,fontWeight:700,cursor:"pointer",minHeight:36}}>
            {autoMode?`⏹ Stop (${interval}m)`:`⏰ Auto`}
          </button>
          {[["15min","15m"],["30min","30m"],["1hour","1H"],["1day","1D"]].map(([v,l])=>(
            <button key={v} onClick={()=>setTimeframe(v)}
              style={{background:timeframe===v?"rgba(0,212,170,0.1)":"transparent",
                border:`1px solid ${timeframe===v?"#e8b339":"#1a2c44"}`,
                borderRadius:0,padding:"5px 8px",
                color:timeframe===v?"#e8b339":"#8ea2bf",
                fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>{l}</button>
          ))}
          {/* Filter by min confidence */}
          <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:4}}>
            <span style={{fontSize:12,color:"#8ea2bf"}}>Min conf:</span>
            <input type="range" min={0} max={90} step={5} value={minConf}
              onChange={e=>setMinConf(parseInt(e.target.value))}
              style={{width:60,accentColor:"#49c2e4"}}/>
            <span style={{fontSize:12,color:"#49c2e4",fontWeight:700,minWidth:24}}>{minConf}%</span>
          </div>
        </div>

        {/* Progress bar */}
        {scanning&&(
          <div style={{marginTop:8,height:2,background:"#1a2c44",borderRadius:1,overflow:"hidden"}}>
            <div style={{height:"100%",width:`${progress}%`,
              background:"linear-gradient(90deg,#a16207,#92400e)",
              borderRadius:1,transition:"width 0.3s ease",
              animation:progress<100?"pulse 1.5s ease-in-out infinite":"none"}}/>
          </div>
        )}
      </div>

      {/* Config panel */}
      {showConfig&&(
        <div style={{padding:"12px 14px",borderBottom:"1px solid #1a2c44",
          background:"#0b1624",flexShrink:0}}>
          <div style={{fontSize:12,color:"#8ea2bf",marginBottom:6}}>
            SYMBOLS TO ANALYZE (comma separated, max 10)
          </div>
          <textarea value={symbols} onChange={e=>setSymbols(e.target.value)} rows={3}
            style={{width:"100%",background:"#0f1d30",border:"1px solid #23405f",
              borderRadius:0,padding:"8px 10px",color:"#e7edf5",fontFamily:"inherit",
              fontSize:10,resize:"none",boxSizing:"border-box"}}
            placeholder="RELIANCE,TCS,NIFTY 50,BANKNIFTY,CRUDEOIL..."/>
          <div style={{display:"flex",gap:8,marginTop:8,alignItems:"center",flexWrap:"wrap"}}>
            <span style={{fontSize:12,color:"#8ea2bf"}}>Auto interval:</span>
            {[5,10,15,30].map(m=>(
              <button key={m} onClick={()=>setInterval_(m)}
                style={{background:interval===m?"rgba(0,212,170,0.1)":"#0f1d30",
                  border:`1px solid ${interval===m?"#e8b339":"#23405f"}`,
                  borderRadius:0,padding:"3px 8px",color:interval===m?"#e8b339":"#8ea2bf",
                  fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>{m}m</button>
            ))}
            <span style={{fontSize:12,color:"#5a728f",marginLeft:"auto"}}>
              ⚠ Each symbol uses 1 Claude API call
            </span>
          </div>
        </div>
      )}

      {/* Results */}
      <div style={{flex:1,overflowY:"auto",padding:"10px 12px",
        display:"flex",flexDirection:"column",gap:10,WebkitOverflowScrolling:"touch"}}>

        {error&&(
          <div style={{background:"rgba(248,81,73,0.1)",border:"1px solid rgba(248,81,73,0.3)",
            borderRadius:0,padding:"12px 14px",fontSize:11,color:"#ff5b6c"}}>
            ⚠ {error}
            {error.includes("GEMINI_API_KEY")&&(
              <div style={{marginTop:6,fontSize:10,color:"#8ea2bf"}}>
                Add your API key on VPS: <code style={{color:"#e8b339"}}>
                export GEMINI_API_KEY=sk-ant-... && systemctl restart rabtrade</code>
              </div>
            )}
          </div>
        )}

        {!scanning&&results.length===0&&!error&&(
          <div style={{textAlign:"center",padding:60}}>
            <div style={{fontSize:40,marginBottom:12}}>🤖</div>
            <div style={{color:"#8ea2bf",fontSize:13,marginBottom:6}}>
              AI Scanner ready
            </div>
            <div style={{color:"#5a728f",fontSize:11,lineHeight:1.6}}>
              Claude will analyze {symList.length} symbols using<br/>
              Technical + Volume + News + Fundamentals + F&O + Institutional data
            </div>
          </div>
        )}

        {filteredResults.map((r,i)=>(
          <AISignalCard key={r.symbol||i} result={r} onTrade={onTrade} onShowToast={onShowToast}/>
        ))}

        {results.length>0&&filteredResults.length===0&&(
          <div style={{textAlign:"center",padding:40,color:"#8ea2bf",fontSize:12}}>
            No signals match current filter (min confidence: {minConf}%)
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: VIRTUAL TRADING — Paper trade to test scanner performance
// ══════════════════════════════════════════════════════════════════════════════
const VIRTUAL_CAPITAL = 500000; // ₹5,00,000 starting balance

function VirtualTab({ scanResults, authed, signalParams, indexQuotes, aiResults, addVirtualRef, uid="" }) {
  const VIRTUAL_START = 500000;
  const [vTrades,   setVTrades]   = useLocalStorage('nse_pro_virtual_trades2', []);
  const [vCapital,  setVCapital]  = useLocalStorage('nse_pro_virtual_capital2', VIRTUAL_START);
  const [tab,        setTab]        = useState("open");
  const [showAdd,    setShowAdd]    = useState(false);
  const [showCfg,    setShowCfg]    = useState(false);
  const [aiSugg,     setAiSugg]     = useState(null);
  const [loadingSugg,setLoadingSugg]= useState(false);
  const [aiPerf,     setAiPerf]     = useState([]);

  // Manual add form
  const [form, setForm] = useState({
    symbol:"", side:"BUY", entry:"", qty:"1",
    tp:"", sl:"", tab_mode:"equity", note:""
  });

  const openTrades   = vTrades.filter(t=>t.status==="OPEN");
  const closedTrades = vTrades.filter(t=>t.status==="CLOSED");

  const getPnl = t => {
    if(t.status==="CLOSED") return parseFloat(t.pnl)||0;
    const ltp = t.currentPrice || t.entry;
    return t.side==="BUY" ? (ltp-t.entry)*t.qty : (t.entry-ltp)*t.qty;
  };

  const stats = useMemo(()=>{
    const pnls   = closedTrades.map(t=>parseFloat(t.pnl)||0);
    const total  = pnls.reduce((a,b)=>a+b,0);
    const wins   = pnls.filter(p=>p>0).length;
    const losses = pnls.filter(p=>p<=0).length;
    const avgWin = wins>0   ? pnls.filter(p=>p>0).reduce((a,b)=>a+b,0)/wins   : 0;
    const avgLoss= losses>0 ? pnls.filter(p=>p<=0).reduce((a,b)=>a+b,0)/losses: 0;
    const openPnl= openTrades.reduce((a,t)=>a+(t.unrealised||0),0);
    const rr     = avgLoss!==0 ? Math.abs(avgWin/avgLoss).toFixed(2) : "∞";
    return {
      total, wins, losses, openPnl,
      winRate: closedTrades.length>0?(wins/closedTrades.length*100).toFixed(0):0,
      rr, avgWin, avgLoss,
      currentCapital: vCapital + total,
      roi: ((vCapital + total - VIRTUAL_START)/VIRTUAL_START*100).toFixed(2),
    };
  },[closedTrades, openTrades, vCapital]);

  // Add virtual trade from any source
  const addVirtualTrade = useCallback((params)=>{
    // DEDUP: auto-added trades (auto-virtual on every scan cycle) must not
    // re-add a symbol+side that already has an OPEN virtual trade.
    const _sym  = (params.symbol||"").toUpperCase();
    const _side = params.side||"BUY";
    const _isAuto = (params.source||"")==="auto_virtual";
    if(_isAuto && (vTradesRef.current||[]).some(t=>
        t.status==="OPEN" && t.symbol===_sym && t.side===_side))
      return null;
    // unique id even when many trades are added in the same millisecond
    const id = `vt_${Date.now()}_${Math.random().toString(36).slice(2,7)}`;
    const trade = {
      id,
      symbol:     (params.symbol||"").toUpperCase(),
      exchange:   params.exchange||"NSE",
      side:       params.side||"BUY",
      entry:      parseFloat(params.entry||params.price||0),
      currentPrice: parseFloat(params.entry||params.price||0),
      qty:        parseInt(params.qty||1),
      product:    params.product||"MIS",
      // accept both key styles: tp/sl (scan cards) and tpPrice/slPrice (order ticket)
      tp:         parseFloat(params.tp||params.tpPrice||0)||null,
      sl:         parseFloat(params.sl||params.slPrice||0)||null,
      trailEnabled: !!params.trailEnabled,
      trailPct:     parseFloat(params.trailPct||0)||0,
      highWaterMark: parseFloat(params.entry||params.price||0),
      lowWaterMark:  parseFloat(params.entry||params.price||0),
      tab_mode:   params.tab_mode||"equity",
      source:     params.source||"manual",
      signal:     params.signal||params.signals||null,  // accept both
      confidence: params.confidence||null,
      clarity:    params.clarity||null,
      key_reasons:params.key_reasons||[],
      note:       params.note||params.signals||"",
      status:     "OPEN",
      openedAt:   new Date().toISOString(),
      pnl:        0,
      unrealised: 0,
    };
    setVTrades(prev=>[trade,...prev]);
    return id;
  },[]);

  // Register into App ref so EquityTab/McxTab can add virtual trades
  useEffect(()=>{
    if(addVirtualRef) addVirtualRef.current = addVirtualTrade;
    return ()=>{ if(addVirtualRef) addVirtualRef.current = null; };
  },[addVirtualTrade, addVirtualRef]);

  // ── Firebase persistence — survive localStorage eviction (mobile/PWA) ────────
  // On mount, merge whatever Firestore has with local (never drop a trade that
  // exists in either; a CLOSED copy always wins so nothing resurrects as open).
  const vLoadedRef = useRef(false);
  useEffect(()=>{
    if(!authed || !uid || vLoadedRef.current) return;
    let cancelled = false;
    (async()=>{
      const cloud = await fbGetVirtualTrades(uid);
      if(cancelled){ return; }
      if(Array.isArray(cloud)){
        setVTrades(local=>{
          const byId = {};
          (cloud||[]).forEach(t=>{ if(t&&t.id) byId[t.id]=t; });
          (local||[]).forEach(t=>{
            if(!t||!t.id) return;
            const ex = byId[t.id];
            if(!ex) byId[t.id]=t;
            else byId[t.id] = (ex.status==="CLOSED") ? ex : t;   // keep closed final; else local (live)
          });
          return Object.values(byId).sort((a,b)=> new Date(b.openedAt||0) - new Date(a.openedAt||0));
        });
      }
      vLoadedRef.current = true;
    })();
    return ()=>{ cancelled = true; };
  },[authed, uid]);

  // Debounced save to Firestore whenever trades change (after initial load)
  const vSaveTimer = useRef(null);
  useEffect(()=>{
    if(!authed || !uid || !vLoadedRef.current) return;
    if(vSaveTimer.current) clearTimeout(vSaveTimer.current);
    vSaveTimer.current = setTimeout(()=>{ fbSaveVirtualTrades(uid, vTrades); }, 1500);
    return ()=>{ if(vSaveTimer.current) clearTimeout(vSaveTimer.current); };
  },[vTrades, authed, uid]);

  // Close virtual trade
  const closeVTrade = (id, exitPrice, reason="MANUAL")=>{
    setVTrades(prev=>prev.map(t=>{
      if(t.id!==id) return t;
      const ep = parseFloat(exitPrice||t.currentPrice||t.entry);
      const pnl= t.side==="BUY" ? (ep-t.entry)*t.qty : (t.entry-ep)*t.qty;
      const roi= t.entry>0 ? ((ep-t.entry)/t.entry*(t.side==="BUY"?1:-1)*100).toFixed(2) : 0;
      return {...t, status:"CLOSED", exitPrice:ep, exitAt:new Date().toISOString(),
              exitReason:reason, pnl:parseFloat(pnl.toFixed(2)), roi:parseFloat(roi)};
    }));
  };

  // ── AUTO-CLOSE ENGINE ──────────────────────────────────────────────────────
  // Polls LTP every 20s for open virtual trades:
  //   • updates currentPrice + unrealised P&L live
  //   • fires TP/SL like a GTT order (exit AT trigger level)
  //   • if a derivative gets no quote ~5 min (contract expired) → close at last LTP
  const vTradesRef = useRef(vTrades);
  useEffect(()=>{ vTradesRef.current = vTrades; },[vTrades]);
  const vMissRef = useRef({});
  useEffect(()=>{
    if(!authed) return;
    let stopped = false;
    const tick = async()=>{
      const open = (vTradesRef.current||[]).filter(t=>t.status==="OPEN");
      if(open.length===0 || stopped) return;
      const syms = [...new Set(open.map(t=>`${t.exchange||"NSE"}:${t.symbol}`))];
      let quotes = null;
      try { quotes = await apiFetch(`/quotes?symbols=${encodeURIComponent(syms.join(","))}`); }
      catch(e) { quotes = null; }
      if(!quotes || stopped) return;
      setVTrades(prev=>prev.map(t=>{
        if(t.status!=="OPEN") return t;
        const q   = quotes[t.symbol] || quotes[(t.symbol||"").toUpperCase()];
        const ltp = q ? parseFloat(q.ltp) : null;
        const mkClose = (ep, reason, cp)=>{
          const pnl = t.side==="BUY" ? (ep-t.entry)*t.qty : (t.entry-ep)*t.qty;
          delete vMissRef.current[t.id];
          return {...t, status:"CLOSED", exitPrice:ep, exitAt:new Date().toISOString(),
                  exitReason:reason, currentPrice:cp!=null?cp:ep,
                  pnl:parseFloat(pnl.toFixed(2)),
                  roi:t.entry>0?parseFloat(((ep-t.entry)/t.entry*(t.side==="BUY"?1:-1)*100).toFixed(2)):0};
        };
        if(!ltp || ltp<=0){
          // No quote — possibly an EXPIRED F&O/MCX contract
          const looksDerivative = /(FUT|CE|PE)$/.test(t.symbol||"") ||
                                  /\d{2}[A-Z]{3}/.test(t.symbol||"") ||
                                  ["fo","mcx"].includes(t.tab_mode);
          const miss = (vMissRef.current[t.id]||0)+1;
          vMissRef.current[t.id] = miss;
          if(looksDerivative && miss>=15)  // ~5 min without quotes → expired: close at last LTP
            return mkClose(parseFloat(t.currentPrice||t.entry), "EXPIRED — closed at last LTP", null);
          return t;
        }
        vMissRef.current[t.id] = 0;
        // Trailing SL — ratchet a stop behind the best price seen
        const hwm = Math.max(parseFloat(t.highWaterMark||t.entry), ltp);
        const lwm = Math.min(parseFloat(t.lowWaterMark ||t.entry), ltp);
        let effSL = t.sl ? parseFloat(t.sl) : null;
        // Trail activates only once price is trail% in profit (same as watchers)
        if(t.trailEnabled && t.trailPct>0){
          if(t.side==="BUY" && hwm >= t.entry*(1 + t.trailPct/100)){
            const trailSL = hwm*(1 - t.trailPct/100);
            effSL = effSL!=null ? Math.max(effSL, trailSL) : trailSL;
          } else if(t.side==="SELL" && lwm <= t.entry*(1 - t.trailPct/100)){
            const trailSL = lwm*(1 + t.trailPct/100);
            effSL = effSL!=null ? Math.min(effSL, trailSL) : trailSL;
          }
          if(effSL!=null) effSL = parseFloat(effSL.toFixed(2));
        }
        // GTT-style TP/SL check — exit AT the trigger level
        if(t.side==="BUY"){
          if(effSL && ltp<=effSL) return mkClose(effSL, effSL>(t.sl||0)?"TRAIL SL HIT":"SL HIT", ltp);
          if(t.tp && ltp>=t.tp)   return mkClose(parseFloat(t.tp), "TP HIT", ltp);
        } else {
          if(effSL && ltp>=effSL) return mkClose(effSL, (t.sl==null||effSL<t.sl)?"TRAIL SL HIT":"SL HIT", ltp);
          if(t.tp && ltp<=t.tp)   return mkClose(parseFloat(t.tp), "TP HIT", ltp);
        }
        const upnl = t.side==="BUY" ? (ltp-t.entry)*t.qty : (t.entry-ltp)*t.qty;
        return {...t, currentPrice:ltp, unrealised:parseFloat(upnl.toFixed(2)),
                highWaterMark:hwm, lowWaterMark:lwm,
                ...(t.trailEnabled&&t.trailPct>0?{trailSL:effSL}:{})};
      }));
    };
    tick();
    const iv = setInterval(tick, 20000);
    return ()=>{ stopped=true; clearInterval(iv); };
  },[authed]);

  // Load AI performance log
  useEffect(()=>{
    if(!authed) return;
    apiFetch("/ai/trade-log").then(d=>setAiPerf(d.trades||[])).catch(()=>{});
  },[authed]);

  // Get AI suggestions
  const getSuggestions = async()=>{
    setLoadingSugg(true);
    try {
      const res = await apiFetch("/ai/suggest-improvements",{method:"POST",
        headers:{"Content-Type":"application/json"}, body:JSON.stringify({})});
      setAiSugg(res);
    } catch(e){ setAiSugg({ok:false, reason:e.message}); }
    finally { setLoadingSugg(false); }
  };

  const pnlColor = v => parseFloat(v)>=0?"#2ed47a":"#ff5b6c";
  const fmt = v => v>=0?`+₹${Math.abs(v).toLocaleString("en-IN",{maximumFractionDigits:0})}`:`-₹${Math.abs(v).toLocaleString("en-IN",{maximumFractionDigits:0})}`;

  const sourceColor = s => s==="ai"?"#49c2e4":s==="scanner"?"#e8b339":s==="auto"?"#ff5b6c":"#8ea2bf";
  const sourceName  = s => s==="ai"?"🤖 AI":s==="scanner"?"📡 Scanner":s==="auto"?"⚡ Auto":"✏️ Manual";

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>

      {/* ── Header ── */}
      <div style={{background:"#0b1624",borderBottom:"1px solid #1a2c44",
        padding:"10px 14px",flexShrink:0}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:13,fontWeight:700,color:"#e7edf5"}}>🎮 Virtual Trading</span>
            <span style={{fontSize:12,padding:"1px 7px",borderRadius:0,
              background:"rgba(96,165,250,0.1)",color:"#6b7280",border:"1px solid rgba(96,165,250,0.2)"}}>
              Paper Money
            </span>
          </div>
          <div style={{display:"flex",gap:6}}>
            <button onClick={()=>setShowAdd(true)}
              style={{background:"rgba(63,185,80,0.1)",border:"1px solid rgba(63,185,80,0.3)",
                borderRadius:0,padding:"4px 10px",color:"#2ed47a",fontFamily:"inherit",
                fontSize:10,fontWeight:700,cursor:"pointer"}}>
              + Add Trade
            </button>
            <button onClick={()=>{ if(window.confirm("Reset all virtual trades and capital?")){
                setVTrades([]); setVCapital(VIRTUAL_START);
              }}}
              style={{background:"none",border:"1px solid #23405f",borderRadius:0,
                padding:"4px 10px",color:"#5a728f",fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
              Reset
            </button>
          </div>
        </div>

        {/* KPI strip */}
        <div style={{display:"flex",gap:10,overflowX:"auto",scrollbarWidth:"none"}}>
          {[
            ["Capital",`₹${(vCapital+stats.total).toLocaleString("en-IN",{maximumFractionDigits:0})}`,pnlColor(stats.total)],
            ["Total P&L", fmt(stats.total), pnlColor(stats.total)],
            ["Open P&L",  fmt(stats.openPnl), pnlColor(stats.openPnl)],
            ["Win Rate",  `${stats.winRate}%`, stats.winRate>=50?"#2ed47a":"#ff5b6c"],
            ["R:R",       `1:${stats.rr}`, parseFloat(stats.rr)>=1.5?"#2ed47a":"#e8b339"],
            ["ROI",       `${stats.roi}%`, pnlColor(stats.roi)],
          ].map(([l,v,c])=>(
            <div key={l} style={{flexShrink:0,textAlign:"center"}}>
              <div style={{fontSize:11,color:"#8ea2bf"}}>{l}</div>
              <div style={{fontSize:11,fontWeight:700,color:c}}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Tabs ── */}
      <div style={{display:"flex",borderBottom:"1px solid #1a2c44",background:"#0b1624",flexShrink:0}}>
        {[["open",`Open (${openTrades.length})`],["closed",`Closed (${closedTrades.length})`],
          ["ailog","AI Log"],["suggest","💡 AI Coach"]
        ].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)}
            style={{background:"none",border:"none",
              borderBottom:`2px solid ${tab===id?"#e8b339":"transparent"}`,
              padding:"8px 12px",color:tab===id?"#e8b339":"#8ea2bf",
              fontFamily:"inherit",fontSize:10,fontWeight:tab===id?700:400,
              cursor:"pointer",whiteSpace:"nowrap"}}>
            {label}
          </button>
        ))}
      </div>

      <div style={{flex:1,overflowY:"auto",WebkitOverflowScrolling:"touch"}}>

        {/* ══ VPS PAPER TRADES (server-side, from Paper mode) ══ */}
        <div style={{margin:"10px 12px",padding:"10px 12px",background:"#0f1d30",
          border:"1px solid #23405f",borderRadius:8}}>
          <VpsPaperLog authed={authed} />
        </div>

        {/* ══ OPEN TRADES ══ */}
        {tab==="open"&&(
          <div style={{padding:"10px 12px"}}>
            {openTrades.length===0?(
              <div style={{textAlign:"center",padding:40,color:"#8ea2bf",fontSize:12}}>
                <div style={{fontSize:32,marginBottom:8}}>🎮</div>
                No open virtual trades.<br/>
                <span style={{fontSize:10,color:"#5a728f"}}>
                  Tap 🎮 on any scan result to paper trade, or add manually below.
                </span>
              </div>
            ):openTrades.map(t=>{
              const upnl = t.side==="BUY"
                ? ((t.currentPrice||t.entry)-t.entry)*t.qty
                : (t.entry-(t.currentPrice||t.entry))*t.qty;
              return (
                <div key={t.id} style={{background:"#0f1d30",
                  borderLeft:`3px solid ${sourceColor(t.source)}`,
                  border:"1px solid #1a2c44",borderRadius:0,
                  padding:"10px 12px",marginBottom:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                    <div>
                      <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                        <span style={{fontSize:12,fontWeight:700,color:"#e7edf5"}}>{t.symbol}</span>
                        <span style={{fontSize:12,padding:"1px 5px",borderRadius:0,fontWeight:700,
                          background:t.side==="BUY"?"rgba(22,163,74,0.12)":"rgba(220,38,38,0.12)",
                          color:t.side==="BUY"?"#2ed47a":"#ff5b6c"}}>{t.side}</span>
                        <span style={{fontSize:11,color:sourceColor(t.source)}}>{sourceName(t.source)}</span>
                        {t.clarity&&<span style={{fontSize:11,color:"#5a728f"}}>{t.clarity}</span>}
                      </div>
                      <div style={{fontSize:12,color:"#8ea2bf",marginTop:2}}>
                        Entry ₹{t.entry} · Qty {t.qty}
                        {t.exchange&&<span style={{color:"#5a728f"}}> · {t.exchange}</span>}
                        {t.product&&<span style={{color:"#5a728f"}}> · {t.product}</span>}
                        {t.tp&&<span style={{color:"#2ed47a"}}> · TP ₹{t.tp}</span>}
                        {t.sl&&<span style={{color:"#ff5b6c"}}> · SL ₹{t.sl}</span>}
                        {t.trailEnabled&&t.trailPct>0&&<span style={{color:"#e8b339"}}> · TR {t.trailPct}%{t.trailSL?` @₹${t.trailSL}`:""}</span>}
                      </div>
                      {t.note&&<div style={{fontSize:12,color:"#5a728f",marginTop:2,fontStyle:"italic"}}>{t.note}</div>}
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:13,fontWeight:700,color:pnlColor(upnl)}}>
                        {upnl>=0?"+":""}₹{Math.abs(upnl).toFixed(0)}
                      </div>
                      <div style={{fontSize:11,color:"#5a728f"}}>unrealised</div>
                    </div>
                  </div>
                  {t.key_reasons?.length>0&&(
                    <div style={{fontSize:12,color:"#8ea2bf",marginBottom:6,
                      paddingLeft:8,borderLeft:"2px solid rgba(0,212,170,0.2)"}}>
                      {t.key_reasons[0]}
                    </div>
                  )}
                  <div style={{display:"flex",gap:6}}>
                    <input placeholder="Exit price"
                      id={`exit_${t.id}`}
                      style={{flex:1,background:"#0b1624",border:"1px solid #23405f",
                        borderRadius:0,padding:"5px 8px",color:"#e7edf5",
                        fontFamily:"inherit",fontSize:11,outline:"none"}}/>
                    <button onClick={()=>{
                      const el=document.getElementById(`exit_${t.id}`);
                      const ep=parseFloat(el?.value||t.currentPrice||t.entry);
                      if(ep>0) closeVTrade(t.id, ep);
                    }} style={{background:"rgba(220,38,38,0.12)",border:"1px solid rgba(248,81,73,0.3)",
                      borderRadius:0,padding:"5px 10px",color:"#ff5b6c",fontFamily:"inherit",
                      fontSize:10,fontWeight:700,cursor:"pointer"}}>
                      Exit
                    </button>
                    <button onClick={()=>setVTrades(p=>p.filter(x=>x.id!==t.id))}
                      style={{background:"none",border:"1px solid #1a2c44",borderRadius:0,
                        padding:"5px 8px",color:"#5a728f",fontFamily:"inherit",
                        fontSize:10,cursor:"pointer"}}>✕</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ══ CLOSED TRADES ══ */}
        {tab==="closed"&&(
          <div style={{padding:"10px 12px"}}>
            {closedTrades.length===0?(
              <div style={{textAlign:"center",padding:40,color:"#8ea2bf",fontSize:12}}>
                No closed trades yet
              </div>
            ):closedTrades.slice().reverse().map(t=>(
              <div key={t.id} style={{background:"#0f1d30",
                borderLeft:`3px solid ${(t.pnl||0)>=0?"#2ed47a":"#ff5b6c"}`,
                border:"1px solid #1a2c44",borderRadius:0,
                padding:"10px 12px",marginBottom:8}}>
                <div style={{display:"flex",justifyContent:"space-between"}}>
                  <div>
                    <div style={{display:"flex",gap:5,alignItems:"center",flexWrap:"wrap"}}>
                      <span style={{fontSize:12,fontWeight:700,color:"#e7edf5"}}>{t.symbol}</span>
                      <span style={{fontSize:12,padding:"1px 5px",borderRadius:0,fontWeight:700,
                        background:t.side==="BUY"?"rgba(22,163,74,0.12)":"rgba(220,38,38,0.12)",
                        color:t.side==="BUY"?"#2ed47a":"#ff5b6c"}}>{t.side}</span>
                      <span style={{fontSize:11,color:sourceColor(t.source)}}>{sourceName(t.source)}</span>
                    </div>
                    <div style={{fontSize:12,color:"#8ea2bf",marginTop:2}}>
                      {t.entry} → {t.exitPrice} · Qty {t.qty}
                      {t.exitAt&&<span> · {new Date(t.exitAt).toLocaleDateString("en-IN")}</span>}
                      {t.exitReason&&<span style={{marginLeft:5,fontSize:9,fontWeight:700,padding:"1px 4px",
                        color:t.exitReason==="TP HIT"?"#2ed47a":t.exitReason==="SL HIT"?"#ff5b6c":"#e8b339",
                        background:t.exitReason==="TP HIT"?"rgba(46,212,122,0.08)":t.exitReason==="SL HIT"?"rgba(255,91,108,0.08)":"rgba(232,179,57,0.08)"}}>
                        {t.exitReason}</span>}
                    </div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:13,fontWeight:700,color:pnlColor(t.pnl||0)}}>
                      {(t.pnl||0)>=0?"+":""}₹{Math.abs(t.pnl||0).toFixed(0)}
                    </div>
                    <div style={{fontSize:12,color:pnlColor(t.roi||0)}}>
                      {(t.roi||0)>=0?"+":""}{parseFloat(t.roi||0).toFixed(1)}%
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ══ AI TRADE LOG ══ */}
        {tab==="ailog"&&(
          <div style={{padding:"10px 12px"}}>
            <div style={{fontSize:12,color:"#8ea2bf",marginBottom:8}}>
              Real AI auto-trades — actual P&L when closed
            </div>
            {aiPerf.length===0?(
              <div style={{textAlign:"center",padding:40,color:"#8ea2bf",fontSize:12}}>
                No AI trades logged yet.<br/>
                <span style={{fontSize:10,color:"#5a728f"}}>AI auto-trade places real orders and logs them here.</span>
              </div>
            ):aiPerf.map((t,i)=>(
              <div key={i} style={{background:"#0f1d30",
                borderLeft:`3px solid ${t.status==="OPEN"?"#49c2e4":(t.pnl||0)>=0?"#2ed47a":"#ff5b6c"}`,
                border:"1px solid #1a2c44",borderRadius:0,padding:"9px 12px",marginBottom:7}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                  <div>
                    <div style={{display:"flex",gap:5,alignItems:"center",flexWrap:"wrap"}}>
                      <span style={{fontSize:11,fontWeight:700,color:"#e7edf5"}}>{t.symbol}</span>
                      <span style={{fontSize:12,padding:"1px 5px",borderRadius:0,fontWeight:700,
                        background:t.side==="BUY"?"rgba(22,163,74,0.12)":"rgba(220,38,38,0.12)",
                        color:t.side==="BUY"?"#2ed47a":"#ff5b6c"}}>{t.side}</span>
                      <span style={{fontSize:11,padding:"1px 5px",borderRadius:0,
                        background:"rgba(163,113,247,0.1)",color:"#49c2e4"}}>
                        {t.confidence}% {t.clarity}
                      </span>
                      <span style={{fontSize:11,color:t.status==="OPEN"?"#e8b339":"#8ea2bf",fontWeight:700}}>
                        {t.status}
                      </span>
                    </div>
                    <div style={{fontSize:12,color:"#8ea2bf",marginTop:2}}>
                      @ ₹{t.entry_price} · {t.tab_mode} · {t.timeframe}
                    </div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    {t.pnl!=null?(
                      <div style={{fontSize:12,fontWeight:700,color:pnlColor(t.pnl||0)}}>
                        {(t.pnl||0)>=0?"+":""}₹{Math.abs(t.pnl||0).toFixed(0)}
                      </div>
                    ):<div style={{fontSize:12,color:"#5a728f"}}>open</div>}
                    <div style={{fontSize:11,color:"#5a728f"}}>
                      {new Date(t.timestamp).toLocaleDateString("en-IN")}
                    </div>
                  </div>
                </div>
                {t.key_reasons?.[0]&&(
                  <div style={{fontSize:12,color:"#8ea2bf",
                    paddingLeft:8,borderLeft:"2px solid rgba(163,113,247,0.3)"}}>
                    {t.key_reasons[0]}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ══ AI COACH / SUGGESTIONS ══ */}
        {tab==="suggest"&&(
          <div style={{padding:"10px 12px"}}>
            <div style={{marginBottom:10}}>
              <button onClick={getSuggestions} disabled={loadingSugg||!authed}
                style={{width:"100%",background:"linear-gradient(135deg,rgba(163,113,247,0.2),rgba(0,112,243,0.15))",
                  border:"1px solid rgba(163,113,247,0.4)",borderRadius:0,
                  padding:"10px 0",color:"#49c2e4",fontFamily:"inherit",
                  fontSize:12,fontWeight:700,cursor:"pointer",
                  opacity:loadingSugg?0.6:1}}>
                {loadingSugg?"🤖 Analyzing your trades...":"🤖 Analyze Performance & Get Suggestions"}
              </button>
              <div style={{fontSize:12,color:"#5a728f",marginTop:4,textAlign:"center"}}>
                Requires at least 3 closed AI trades in the log
              </div>
            </div>

            {aiSugg&&!aiSugg.ok&&(
              <div style={{background:"rgba(220,38,38,0.08)",border:"1px solid rgba(248,81,73,0.2)",
                borderRadius:0,padding:"10px 12px",fontSize:11,color:"#ff5b6c"}}>
                {aiSugg.reason}
              </div>
            )}

            {aiSugg?.ok&&(
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {/* Summary */}
                <div style={{background:"rgba(163,113,247,0.06)",border:"1px solid rgba(163,113,247,0.2)",
                  borderRadius:0,padding:"10px 12px"}}>
                  <div style={{fontSize:12,color:"#49c2e4",fontWeight:700,marginBottom:4,letterSpacing:1}}>
                    📊 PERFORMANCE SUMMARY
                  </div>
                  <div style={{fontSize:11,color:"#5c3d2e"}}>{aiSugg.overall_assessment}</div>
                  <div style={{display:"flex",gap:10,marginTop:6,flexWrap:"wrap"}}>
                    <span style={{fontSize:12,color:"#8ea2bf"}}>
                      Win rate: <span style={{color:aiSugg.win_rate>=50?"#2ed47a":"#ff5b6c",fontWeight:700}}>
                        {aiSugg.win_rate}%</span>
                    </span>
                    <span style={{fontSize:12,color:"#8ea2bf"}}>
                      P&L: <span style={{color:pnlColor(aiSugg.total_pnl),fontWeight:700}}>
                        ₹{aiSugg.total_pnl?.toFixed(0)}</span>
                    </span>
                    <span style={{fontSize:12,color:"#8ea2bf"}}>
                      Trades: {aiSugg.trades_analyzed}
                    </span>
                  </div>
                </div>

                {/* Suggestions */}
                {aiSugg.suggestions?.length>0&&(
                  <div>
                    <div style={{fontSize:12,color:"#e8b339",fontWeight:700,letterSpacing:1,marginBottom:6}}>
                      💡 RECOMMENDATIONS
                    </div>
                    {aiSugg.suggestions.map((s,i)=>(
                      <div key={i} style={{background:"#0f1d30",border:"1px solid #1a2c44",
                        borderRadius:7,padding:"8px 10px",marginBottom:6}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                          <span style={{fontSize:10,fontWeight:700,color:"#e7edf5",textTransform:"capitalize"}}>
                            {s.type?.replace(/_/g," ")}
                          </span>
                          <div style={{display:"flex",gap:6,fontSize:12}}>
                            <span style={{color:"#ff5b6c"}}>Now: {s.current}</span>
                            <span style={{color:"#2ed47a"}}>→ {s.recommended}</span>
                          </div>
                        </div>
                        <div style={{fontSize:10,color:"#8ea2bf"}}>{s.reason}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Win/Loss patterns */}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  <div style={{background:"rgba(63,185,80,0.05)",border:"1px solid rgba(63,185,80,0.15)",
                    borderRadius:7,padding:"8px 10px"}}>
                    <div style={{fontSize:11,color:"#2ed47a",fontWeight:700,marginBottom:5}}>✓ WIN PATTERNS</div>
                    {aiSugg.win_patterns?.map((p,i)=>(
                      <div key={i} style={{fontSize:12,color:"#5c3d2e",marginBottom:3}}>▸ {p}</div>
                    ))}
                  </div>
                  <div style={{background:"rgba(248,81,73,0.05)",border:"1px solid rgba(248,81,73,0.15)",
                    borderRadius:7,padding:"8px 10px"}}>
                    <div style={{fontSize:11,color:"#ff5b6c",fontWeight:700,marginBottom:5}}>✗ LOSS PATTERNS</div>
                    {aiSugg.loss_patterns?.map((p,i)=>(
                      <div key={i} style={{fontSize:12,color:"#5c3d2e",marginBottom:3}}>▸ {p}</div>
                    ))}
                  </div>
                </div>

                {/* Best performing */}
                {aiSugg.best_performing&&(
                  <div style={{background:"rgba(0,212,170,0.05)",border:"1px solid rgba(0,212,170,0.15)",
                    borderRadius:7,padding:"8px 10px"}}>
                    <div style={{fontSize:11,color:"#e8b339",fontWeight:700,marginBottom:6}}>
                      🎯 BEST SETUP
                    </div>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                      {Object.entries(aiSugg.best_performing).map(([k,v])=>(
                        <div key={k} style={{background:"rgba(0,0,0,0.2)",borderRadius:0,padding:"3px 8px"}}>
                          <div style={{fontSize:7,color:"#5a728f",textTransform:"capitalize"}}>{k.replace(/_/g," ")}</div>
                          <div style={{fontSize:10,fontWeight:700,color:"#e8b339"}}>{v}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Avoid */}
                {aiSugg.avoid?.length>0&&(
                  <div style={{background:"rgba(248,81,73,0.04)",border:"1px solid rgba(248,81,73,0.15)",
                    borderRadius:7,padding:"8px 10px"}}>
                    <div style={{fontSize:11,color:"#ff5b6c",fontWeight:700,marginBottom:4}}>⚠ AVOID</div>
                    <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                      {aiSugg.avoid.map((a,i)=>(
                        <span key={i} style={{fontSize:12,padding:"2px 7px",borderRadius:0,
                          background:"rgba(248,81,73,0.1)",color:"#ff5b6c",border:"1px solid rgba(248,81,73,0.2)"}}>
                          {a}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{fontSize:12,color:"#5a728f",textAlign:"center",fontStyle:"italic"}}>
                  "{aiSugg.summary}"
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Manual Add Trade Modal ── */}
      {showAdd&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",
          display:"flex",alignItems:"center",justifyContent:"center",zIndex:500,padding:16}}>
          <div style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:12,
            width:"min(400px,95vw)",padding:18}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <span style={{fontSize:13,fontWeight:700,color:"#e7edf5"}}>➕ Add Virtual Trade</span>
              <button onClick={()=>setShowAdd(false)}
                style={{background:"none",border:"none",color:"#8ea2bf",fontSize:18,cursor:"pointer"}}>✕</button>
            </div>

            {/* Source type tabs */}
            <div style={{display:"flex",gap:0,marginBottom:12,borderRadius:0,overflow:"hidden",
              border:"1px solid #1a2c44"}}>
              {[["manual","✏️ Manual"],["scanner","📡 From Scanner"],["ai","🤖 From AI"]].map(([s,l])=>(
                <button key={s} onClick={()=>setForm(p=>({...p,source:s}))}
                  style={{flex:1,background:(form.source||"manual")===s?"rgba(0,212,170,0.12)":"#0b1624",
                    border:"none",padding:"6px 0",color:(form.source||"manual")===s?"#e8b339":"#8ea2bf",
                    fontFamily:"inherit",fontSize:12,cursor:"pointer",fontWeight:(form.source||"manual")===s?700:400}}>
                  {l}
                </button>
              ))}
            </div>

            {/* If scanner/AI, show signal cards to pick from */}
            {(form.source==="scanner"||form.source==="ai")&&scanResults?.length>0&&(
              <div style={{marginBottom:10}}>
                <div style={{fontSize:12,color:"#8ea2bf",marginBottom:5}}>Pick from latest scan:</div>
                <div style={{maxHeight:120,overflowY:"auto",display:"flex",flexDirection:"column",gap:4}}>
                  {scanResults.filter(r=>(r.signals||[]).length>0).slice(0,8).map(r=>(
                    <button key={r.symbol} onClick={()=>setForm(p=>({
                      ...p,
                      symbol:r.symbol,
                      entry:String(r.price||r.ltp||""),
                      side:(r.signals||[])[0]?.type||"BUY",
                      tp:  String(r.price ? (r.price*1.02).toFixed(2) : ""),
                      sl:  String(r.price ? (r.price*0.99).toFixed(2) : ""),
                    }))}
                      style={{background:"#0b1624",border:"1px solid #1a2c44",borderRadius:0,
                        padding:"5px 10px",color:"#e7edf5",fontFamily:"inherit",fontSize:10,
                        cursor:"pointer",textAlign:"left",display:"flex",gap:8,alignItems:"center"}}>
                      <span style={{fontWeight:700}}>{r.symbol}</span>
                      <span style={{color:"#8ea2bf"}}>₹{r.price||r.ltp}</span>
                      <span style={{color:(r.signals||[])[0]?.type==="BUY"?"#2ed47a":"#ff5b6c",fontSize:12}}>
                        {(r.signals||[]).length} signals
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Form fields */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
              {[
                ["Symbol","symbol","text","e.g. RELIANCE"],
                ["Side","side","select",""],
                ["Entry Price","entry","number","₹"],
                ["Quantity","qty","number","1"],
                ["Target (TP)","tp","number","Optional"],
                ["Stop Loss (SL)","sl","number","Optional"],
              ].map(([label,key,type,ph])=>(
                <div key={key}>
                  <div style={{fontSize:11,color:"#8ea2bf",marginBottom:3}}>{label}</div>
                  {type==="select"?(
                    <select value={form[key]} onChange={e=>setForm(p=>({...p,[key]:e.target.value}))}
                      style={{width:"100%",background:"#0b1624",border:"1px solid #23405f",
                        borderRadius:0,padding:"6px 8px",color:"#e7edf5",fontFamily:"inherit",
                        fontSize:11,outline:"none"}}>
                      <option value="BUY">BUY ▲</option>
                      <option value="SELL">SELL ▼</option>
                    </select>
                  ):(
                    <input type={type} value={form[key]}
                      onChange={e=>setForm(p=>({...p,[key]:e.target.value}))}
                      placeholder={ph}
                      style={{width:"100%",background:"#0b1624",border:"1px solid #23405f",
                        borderRadius:0,padding:"6px 8px",color:"#e7edf5",fontFamily:"inherit",
                        fontSize:11,outline:"none",boxSizing:"border-box"}}/>
                  )}
                </div>
              ))}
            </div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,color:"#8ea2bf",marginBottom:3}}>Note (optional)</div>
              <input value={form.note} onChange={e=>setForm(p=>({...p,note:e.target.value}))}
                placeholder="Why are you taking this trade?"
                style={{width:"100%",background:"#0b1624",border:"1px solid #23405f",
                  borderRadius:0,padding:"6px 8px",color:"#e7edf5",fontFamily:"inherit",
                  fontSize:11,outline:"none",boxSizing:"border-box"}}/>
            </div>

            <button onClick={()=>{
              if(!form.symbol||!form.entry) return;
              addVirtualTrade({...form, source:form.source||"manual"});
              setForm({symbol:"",side:"BUY",entry:"",qty:"1",tp:"",sl:"",tab_mode:"equity",note:"",source:"manual"});
              setShowAdd(false);
            }} style={{width:"100%",background:"linear-gradient(135deg,#1a7f37,#3fb950)",
              border:"none",borderRadius:0,padding:"10px 0",color:"#fff",
              fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer"}}>
              Add Virtual Trade
            </button>
          </div>
        </div>
      )}
    </div>
  );
}



function AITab({ scanResults, indexQuotes, positions, signalParams, setSignalParams,
                 isAdmin=false, userRole="viewer", journal=[], hasAIAccess=false, watchers=[] }) {
  const STRATEGY_PRESETS = {
    "Scalping":    { tsi_long:10, tsi_short:5,  tsi_signal:3,  rsi_period:7,  rsi_ob:75, rsi_os:25, ema_fast:5,  ema_slow:20,  vol_lookback:3, vol_dom_pct:55, rsi_vol_high:60 },
    "Intraday":    { tsi_long:25, tsi_short:13, tsi_signal:13, rsi_period:14, rsi_ob:70, rsi_os:30, ema_fast:9,  ema_slow:50,  vol_lookback:5, vol_dom_pct:60, rsi_vol_high:65 },
    "Swing":       { tsi_long:40, tsi_short:20, tsi_signal:10, rsi_period:14, rsi_ob:75, rsi_os:25, ema_fast:9,  ema_slow:50,  vol_lookback:7, vol_dom_pct:60, rsi_vol_high:65 },
    "Positional":  { tsi_long:50, tsi_short:25, tsi_signal:13, rsi_period:21, rsi_ob:80, rsi_os:20, ema_fast:20, ema_slow:200, vol_lookback:10, vol_dom_pct:65, rsi_vol_high:70 },
    "Conservative":{ tsi_long:50, tsi_short:25, tsi_signal:13, rsi_period:21, rsi_ob:85, rsi_os:15, ema_fast:20, ema_slow:100, vol_lookback:10, vol_dom_pct:70, rsi_vol_high:75 },
    "Aggressive":  { tsi_long:15, tsi_short:8,  tsi_signal:5,  rsi_period:9,  rsi_ob:65, rsi_os:35, ema_fast:5,  ema_slow:21,  vol_lookback:3, vol_dom_pct:55, rsi_vol_high:60 },
  };

  const canEdit     = userRole !== "viewer";
  const canUseAI    = isAdmin || hasAIAccess;  // AI features gated by access flag
  const [aiTab, setAiTab] = useState("chat");  // chat | backtest | analysis | scan_builder | positions

  const [messages,    setMessages]  = useState([{
    role:"assistant",
    content:`Hey! I'm your AI Strategy Advisor 🧠\n\nI can:\n• Chat — adjust signal settings, explain strategy\n• Backtest — check success ratio of your past signals\n• Analysis — AI learns from your trade history & gives predictions\n${canUseAI?"\n• Scan Builder — describe what you want to scan, I'll build the params\n• Position Analysis — deep-dive your open & closed trades":""}\n\n${canEdit?"Just describe what you want — I'll update settings live.":"Viewer mode — can explain but can't change settings."}\n\nTry: "Make it more aggressive" or "Switch to swing trading"`
  }]);
  const [input,       setInput]     = useState("");
  const [loading,     setLoading]   = useState(false);
  const [activePreset,setActivePreset] = useState(null);
  const [btLoading,   setBtLoading] = useState(false);
  const [btResult,    setBtResult]  = useState(null);
  const [anLoading,   setAnLoading] = useState(false);
  const [anResult,    setAnResult]  = useState(null);
  const messagesEndRef  = useRef(null);
  useEffect(()=>{ messagesEndRef.current?.scrollIntoView({behavior:"smooth"}); },[messages]);

  // ── Scan Builder state ────────────────────────────────────────────────────
  const [sbMessages,  setSbMessages] = useState([{role:"assistant",
    content:"Describe the scan you want in plain English.\n\nExamples:\n• \"Find stocks where RSI is below 35 and MACD is crossing up\"\n• \"Oversold stocks on 15min with above-average volume\"\n• \"Stocks near lower Bollinger Band with bullish stochastic\"\n\nI'll translate it into scan parameters you can apply instantly."}]);
  const [sbInput,     setSbInput]    = useState("");
  const [sbLoading,   setSbLoading]  = useState(false);
  const [sbPending,   setSbPending]  = useState(null); // parsed params waiting for apply
  const sbEndRef = useRef(null);
  useEffect(()=>{ sbEndRef.current?.scrollIntoView({behavior:"smooth"}); },[sbMessages]);

  // ── Position Analysis state ───────────────────────────────────────────────
  const [posMessages, setPosMessages] = useState([]);
  const [posInput,    setPosInput]    = useState("");
  const [posLoading,  setPosLoading]  = useState(false);
  const [posAnalysed, setPosAnalysed] = useState(false);
  const posEndRef = useRef(null);
  useEffect(()=>{ posEndRef.current?.scrollIntoView({behavior:"smooth"}); },[posMessages]);

  // ── Load journal — prefer prop (Firebase), fall back to localStorage ─────
  const getJournal = () => {
    // Use Firebase journal passed as prop if available
    if(journal && journal.length > 0) {
      return journal.filter(t=>t&&t.symbol&&t.side);
    }
    // Fallback to localStorage
    try {
      const keys = ['nse_pro_trades', 'rabt_journal', 'rabtrade_journal'];
      for(const k of keys) {
        const raw = localStorage.getItem(k);
        if(raw) {
          const parsed = JSON.parse(raw);
          if(Array.isArray(parsed) && parsed.length > 0)
            return parsed.filter(t=>t&&t.symbol);
        }
      }
      return [];
    } catch { return []; }
  };

  const applyPreset = (name) => {
    if(!canEdit) return;
    const preset = STRATEGY_PRESETS[name];
    if(!preset) return;
    setSignalParams(prev=>({...prev,...preset}));
    setActivePreset(name);
    setMessages(prev=>[...prev,{role:"assistant",
      content:`✅ Applied **${name}** strategy!\nTSI ${preset.tsi_long}/${preset.tsi_short}/${preset.tsi_signal} · RSI ${preset.rsi_period} (OB:${preset.rsi_ob}/OS:${preset.rsi_os}) · EMA ${preset.ema_fast}/${preset.ema_slow}`,
      isSystem:true}]);
  };

  // ── BACKTEST — calculate signal success ratios from journal ───────────────
  const runBacktest = async () => {
    setBtLoading(true); setBtResult(null);
    const rawJournal = getJournal();
    const closedTrades = rawJournal.filter(t=>t.pnl!=null||t.exitPrice);
    if(closedTrades.length < 2) {
      setBtResult({error:`Only ${closedTrades.length} closed trades in your journal.\n\nThis backtest analyses your actual trade history. Trades are recorded when you place orders and marked complete when TP/SL hits.\n\n💡 For a signal backtest without needing trade history, use the 📈 Chart tab → Backtest — it simulates signals on historical candles.`});
      setBtLoading(false); return;
    }

    // Enrich trades: calculate pnl from exitPrice if not stored
    const journal = rawJournal.map(t=>{
      if(t.pnl!=null) return t;
      if(t.exitPrice && t.entryPrice) {
        const { pnl } = calcJournalPnL(t, t.exitPrice);
        return {...t, pnl};
      }
      return t;
    });

    try {
      const tradesWithSignals = journal.filter(t=>t.signals&&t.pnl!=null);
      const tradesWithPnl     = journal.filter(t=>t.pnl!=null);
      const totalAll          = journal.length;
      
      // Overall win rate
      const wins   = tradesWithPnl.filter(t=>parseFloat(t.pnl||0)>0).length;
      const losses = tradesWithPnl.filter(t=>parseFloat(t.pnl||0)<0).length;
      const total  = tradesWithPnl.length;
      const winRate = total>0 ? ((wins/total)*100).toFixed(1) : 0;
      const avgWin  = wins>0
        ? (tradesWithPnl.filter(t=>parseFloat(t.pnl||0)>0).reduce((s,t)=>s+parseFloat(t.pnl),0)/wins).toFixed(0)
        : 0;
      const avgLoss = losses>0
        ? Math.abs(tradesWithPnl.filter(t=>parseFloat(t.pnl||0)<0).reduce((s,t)=>s+parseFloat(t.pnl),0)/losses).toFixed(0)
        : 0;
      const rr = avgLoss>0 ? (avgWin/avgLoss).toFixed(2) : "∞";

      // Per signal type success
      const sigMap = {};
      tradesWithSignals.forEach(t=>{
        const sigs = (t.signals||"").split('·').map(s=>s.trim()).filter(Boolean);
        sigs.forEach(sig=>{
          const key = sig.replace(/\s+(BUY|SELL)/i,'').trim().slice(0,30);
          if(!sigMap[key]) sigMap[key]={wins:0,losses:0,total:0,pnl:0};
          const p = parseFloat(t.pnl||0);
          sigMap[key].total++;
          sigMap[key].pnl += p;
          if(p>0) sigMap[key].wins++;
          else if(p<0) sigMap[key].losses++;
        });
      });
      const sigStats = Object.entries(sigMap)
        .filter(([,v])=>v.total>=2)
        .map(([sig,v])=>({
          sig, total:v.total,
          rate: ((v.wins/v.total)*100).toFixed(0),
          pnl:  v.pnl.toFixed(0),
          wins: v.wins, losses: v.losses,
        }))
        .sort((a,b)=>parseFloat(b.rate)-parseFloat(a.rate));

      // Per instrument type
      const instrMap = {};
      tradesWithPnl.forEach(t=>{
        const k = t.instrumentType||t.tabMode||"EQ";
        if(!instrMap[k]) instrMap[k]={wins:0,total:0,pnl:0};
        instrMap[k].total++;
        instrMap[k].pnl += parseFloat(t.pnl||0);
        if(parseFloat(t.pnl||0)>0) instrMap[k].wins++;
      });

      // Best/worst symbols
      const symMap = {};
      tradesWithPnl.forEach(t=>{
        if(!symMap[t.symbol]) symMap[t.symbol]={wins:0,total:0,pnl:0};
        symMap[t.symbol].total++;
        symMap[t.symbol].pnl += parseFloat(t.pnl||0);
        if(parseFloat(t.pnl||0)>0) symMap[t.symbol].wins++;
      });
      const symList = Object.entries(symMap)
        .filter(([,v])=>v.total>=2)
        .map(([sym,v])=>({sym,total:v.total,pnl:parseFloat(v.pnl.toFixed(0)),rate:((v.wins/v.total)*100).toFixed(0)}))
        .sort((a,b)=>b.pnl-a.pnl);

      setBtResult({winRate,wins,losses,total,avgWin,avgLoss,rr,sigStats,instrMap,symList,
        totalPnl:tradesWithPnl.reduce((s,t)=>s+parseFloat(t.pnl||0),0).toFixed(0),
        totalAll, openCount: totalAll - total});
    } catch(e) {
      setBtResult({error:e.message});
    }
    setBtLoading(false);
  };

  // ── ANALYSIS — AI reads history and gives smart prediction ───────────────
  const runAnalysis = async () => {
    setAnLoading(true); setAnResult(null);
    const journal = getJournal();
    if(journal.length < 5) {
      setAnResult({error:"Need at least 5 trades to analyse. Place more trades from the app."});
      setAnLoading(false); return;
    }
    try {
      const recent = journal.slice(-30); // last 30 trades
      const summary = {
        total: journal.length,
        recent_trades: recent.map(t=>({
          symbol:t.symbol, side:t.side, pnl:parseFloat(t.pnl||0).toFixed(0),
          signals:t.signals||"", tabMode:t.tabMode||"equity",
          instrumentType:t.instrumentType||"EQ",
          source:t.source||"manual",
        })),
        current_params: signalParams,
        market_context: Object.entries(indexQuotes||{}).slice(0,4)
          .map(([k,v])=>`${k}:₹${v?.last_price||0}`).join(", "),
        open_positions: (positions?.net||[]).slice(0,5).map(p=>({
          symbol:p.tradingsymbol, pnl:p.pnl, qty:p.quantity
        })),
      };

      const resp = await fetch(`${API_BASE}/ai/chat`, {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          max_tokens:1000,
          system:`You are an AI trading analyst for RAB Trade — an Indian trading terminal. 
Analyse the trader's history and give actionable insights.

FORMAT YOUR RESPONSE IN SECTIONS:
1. 🧠 PATTERN ANALYSIS (2-3 lines: what signals/setups work best for this trader)
2. 📊 SUCCESS RATIO (mention win rate, best signal type, best instrument)
3. ⚠️ WEAK SPOTS (what to avoid based on history)
4. 🎯 NEXT TRADE RECOMMENDATION (specific actionable advice for next trade)
5. ⚙️ SETTINGS SUGGESTION (if you recommend param changes, use <PARAMS>{...}</PARAMS> at the end)

Keep it SHORT — max 15 lines total. Use ₹ for prices. Focus on Indian market context.`,
          messages:[{role:"user",content:`Analyse my trading history and give insights:\n${JSON.stringify(summary,null,2)}`}]
        })
      });
      const data  = await resp.json();
      const text  = data.content?.[0]?.text || "Could not get analysis.";

      // Extract params if AI suggests changes
      const paramMatch = text.match(/<PARAMS>([\s\S]*?)<\/PARAMS>/);
      const display    = text.replace(/<PARAMS>[\s\S]*?<\/PARAMS>/g,"").trim();
      let appliedParams = null;
      if(paramMatch && canEdit) {
        try {
          const changes = JSON.parse(paramMatch[1]);
          setSignalParams(prev=>({...prev,...changes}));
          appliedParams = changes;
        } catch {}
      }
      setAnResult({text:display, appliedParams});
    } catch(e) {
      setAnResult({error:e.message});
    }
    setAnLoading(false);
  };

  // ── CHAT sendMessage ─────────────────────────────────────────────────────
  const sendMessage = async () => {
    if(!input.trim()||loading) return;
    const userMsg = input.trim();
    setInput("");
    const newMessages = [...messages,{role:"user",content:userMsg}];
    setMessages(newMessages);
    setLoading(true);
    try {
      const current = signalParams || DEFAULT_SIGNAL_PARAMS;
      const journal = getJournal().slice(-10);
      const systemPrompt = `You are an AI trading strategy advisor for RAB Trade (Indian market terminal).

CURRENT SIGNAL PARAMS: ${JSON.stringify(current)}
PRESETS AVAILABLE: ${Object.keys(STRATEGY_PRESETS).join(", ")}
USER ROLE: ${userRole} ${canEdit?"(CAN edit settings)":"(viewer)"}
MARKET: ${Object.entries(indexQuotes||{}).slice(0,4).map(([k,v])=>`${k}:₹${v?.last_price||0}`).join(", ")||"not loaded"}
RECENT TRADES (last 10): ${JSON.stringify(journal.map(t=>({sym:t.symbol,side:t.side,pnl:t.pnl,signals:t.signals})))}

RULES:
- Keep responses SHORT (3-5 lines max unless explaining).
- If recommending param changes AND user can edit, add <PARAMS>{...}</PARAMS> at END.
- Only include params you're changing. Explain in plain English.
- Indian context: intraday=5/15min, swing=1h/daily, positional=weekly.`;

      const resp = await fetch(`${API_BASE}/ai/chat`,{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({max_tokens:500,system:systemPrompt,
          messages:newMessages.map(m=>({role:m.role==="assistant"?"assistant":"user",content:m.content}))})
      });
      if(!resp.ok) throw new Error(`Server error ${resp.status}`);
      const data = await resp.json();
      const raw  = data.content?.[0]?.text || "Sorry, no response.";
      const paramMatch = raw.match(/<PARAMS>([\s\S]*?)<\/PARAMS>/);
      let display = raw.replace(/<PARAMS>[\s\S]*?<\/PARAMS>/g,"").trim();
      let applied = null;
      if(paramMatch && canEdit) {
        try {
          const ch = JSON.parse(paramMatch[1]);
          setSignalParams(prev=>({...prev,...ch}));
          applied = ch;
          display += `\n\n✅ Applied: ${Object.entries(ch).map(([k,v])=>`${k}:${v}`).join(" · ")}`;
        } catch {}
      }
      setMessages(prev=>[...prev,{role:"assistant",content:display,appliedParams:applied}]);
    } catch(e) {
      setMessages(prev=>[...prev,{role:"assistant",content:`⚠ ${e.message}`}]);
    }
    setLoading(false);
  };

  const currentParams = signalParams || DEFAULT_SIGNAL_PARAMS;

  // ── SCAN BUILDER send ───────────────────────────────────────────────────
  const sendScanBuilder = async () => {
    if(!sbInput.trim() || sbLoading) return;
    const userMsg = sbInput.trim();
    setSbInput("");
    const newMsgs = [...sbMessages, {role:"user", content:userMsg}];
    setSbMessages(newMsgs);
    setSbLoading(true);
    setSbPending(null);
    try {
      const sysPrompt = `You are a trading scan parameter expert for RAB Trade (Indian NSE/MCX terminal).

AVAILABLE SCAN PARAMS (JSON keys and ranges):
- tsi_long (5-50), tsi_short (3-25), tsi_signal (3-20)  — TSI crossover sensitivity
- rsi_period (5-30), rsi_ob (60-100), rsi_os (0-40)      — RSI overbought/oversold levels
- ema_fast (3-25), ema_slow (20-200)                      — EMA crossover periods
- macd_fast (3-30), macd_slow (8-60), macd_signal (3-20)  — MACD line periods
- bb_period (5-50), bb_std (1-4)                          — Bollinger Bands width
- stoch_k (3-30), stoch_d (1-10), stoch_smooth (1-10)    — Stochastic periods
- stoch_ob (60-100), stoch_os (0-40)                      — Stochastic zones
- vol_lookback (2-20), vol_dom_pct (40-90)               — Volume analysis sensitivity

ACTIVE SCAN GROUPS:
- Scan 1: TSI cross, Scan 2: RSI Extreme, Scan 3: Vol+RSI, Scan 4: EMA Cross
- Scan 5: Patterns, Scan 8: D/S Zone, Scan 9: MACD, Scan 10: BB Bands, Scan 11: Stochastic

CURRENT PARAMS: ${JSON.stringify(currentParams)}

RULES:
1. Understand the user's intent, explain which scan groups are most relevant.
2. Recommend parameter values tuned to their description.
3. Always end your response with a JSON block tagged: <SCAN_PARAMS>{...}</SCAN_PARAMS>
4. Only include params that differ from defaults. Keep the JSON minimal.
5. Indian market context: NSE intraday = 5/15min, swing = 1h/daily.
6. If the user wants a specific indicator combo, explain how to enable both scans together.`;

      const resp = await fetch(`${API_BASE}/ai/chat`,{
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({max_tokens:600, system:sysPrompt,
          messages:newMsgs.map(m=>({role:m.role==="assistant"?"assistant":"user",content:m.content}))})
      });
      if(!resp.ok) throw new Error(`Server error ${resp.status}`);
      const data = await resp.json();
      const raw  = data.content?.[0]?.text || "No response.";
      const pMatch = raw.match(/<SCAN_PARAMS>([\s\S]*?)<\/SCAN_PARAMS>/);
      const display = raw.replace(/<SCAN_PARAMS>[\s\S]*?<\/SCAN_PARAMS>/g,"").trim();
      let parsedParams = null;
      if(pMatch) { try { parsedParams = JSON.parse(pMatch[1]); } catch {} }
      setSbMessages(prev=>[...prev,{role:"assistant",content:display,params:parsedParams}]);
      if(parsedParams) setSbPending(parsedParams);
    } catch(e) {
      setSbMessages(prev=>[...prev,{role:"assistant",content:`⚠ ${e.message}`}]);
    }
    setSbLoading(false);
  };

  const applyScanParams = (params) => {
    if(!params || !canEdit) return;
    setSignalParams(prev=>({...prev,...params}));
    setSbPending(null);
    setSbMessages(prev=>[...prev,{role:"assistant",
      content:`✅ Applied! Params updated:\n${Object.entries(params).map(([k,v])=>`  ${k}: ${v}`).join("\n")}\n\nRun a scan now to see results with these settings.`}]);
  };

  // ── POSITION ANALYSIS ───────────────────────────────────────────────────
  const buildPositionContext = () => {
    const openPos  = (watchers||[]).filter(w=>w.status==="ACTIVE"||w.status==="active"||w.status==="WATCHING");
    const trades   = getJournal().slice(-30);
    const closedW  = (watchers||[]).filter(w=>w.status?.startsWith("EXITED")||w.status==="STALE_NO_POSITION");

    const openSummary = openPos.map(w=>({
      symbol: w.symbol, side: w.side, entry: w.entry_price,
      ltp: w.ltp||w.current_price, tp: w.tp_price, sl: w.sl_price,
      pnl: w.pnl_est||0, product: w.product, tf: w.timeframe,
    }));
    const closedSummary = [...closedW.map(w=>({
      symbol:w.symbol, side:w.side, entry:w.entry_price, exit:w.exit_price,
      pnl:w.pnl_est||0, hit:w.status?.replace("EXITED_",""),
    })), ...trades.map(t=>({
      symbol:t.symbol, side:t.side, entry:t.price||t.entryPrice,
      exit:t.exitPrice, pnl:t.pnl||0,
    }))].slice(0,30);

    return { openPos: openSummary, closedTrades: closedSummary };
  };

  const runPositionAnalysis = async (followUp=null) => {
    if(posLoading) return;
    const ctx = buildPositionContext();
    const userContent = followUp || `Analyse my positions and give me actionable insights.`;
    const newMsgs = [...posMessages, {role:"user", content:userContent}];
    setPosMessages(newMsgs);
    setPosInput("");
    setPosLoading(true);

    try {
      const sysPrompt = `You are a professional trading analyst for RAB Trade (Indian NSE/MCX terminal).

OPEN POSITIONS (${ctx.openPos.length}):
${JSON.stringify(ctx.openPos, null, 2)}

RECENT CLOSED TRADES (last 30):
${JSON.stringify(ctx.closedTrades, null, 2)}

MARKET: ${Object.entries(indexQuotes||{}).slice(0,4).map(([k,v])=>`${k}:₹${v?.last_price||0}`).join(", ")||"not loaded"}
CURRENT SCAN PARAMS: ${JSON.stringify(currentParams)}

ANALYSIS RULES:
1. For OPEN positions: assess each one — risk/reward, how far from TP/SL, should they hold or exit.
2. For CLOSED trades: identify patterns — what's working, what's not, win rate by signal type.
3. Give specific, actionable advice (not generic). Use ₹ for prices.
4. Note any concentration risk (too many positions in same sector/side).
5. Keep responses focused. Use plain language.
6. If user asks about a specific symbol, give focused analysis on that.`;

      const resp = await fetch(`${API_BASE}/ai/chat`,{
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({max_tokens:800, system:sysPrompt,
          messages:newMsgs.map(m=>({role:m.role==="assistant"?"assistant":"user",content:m.content}))})
      });
      if(!resp.ok) throw new Error(`Server error ${resp.status}`);
      const data = await resp.json();
      const text = data.content?.[0]?.text || "No response.";
      setPosMessages(prev=>[...prev,{role:"assistant",content:text}]);
      setPosAnalysed(true);
    } catch(e) {
      setPosMessages(prev=>[...prev,{role:"assistant",content:`⚠ ${e.message}`}]);
    }
    setPosLoading(false);
  };

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",fontFamily:"inherit",background:"#07101c"}}>

      {/* Header */}
      <div style={{background:"#0b1624",borderBottom:"1px solid #1a2c44",padding:"8px 14px",flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
          <span style={{fontSize:14,fontWeight:700,color:"#49c2e4"}}>🧠 AI Advisor · Gemini</span>
          <div style={{fontSize:11,color:"#5a728f"}}>
            RSI {currentParams.rsi_ob}/{currentParams.rsi_os} · EMA {currentParams.ema_fast}/{currentParams.ema_slow}
          </div>
        </div>
        {/* Sub-tabs */}
        <div style={{display:"flex",gap:4,marginBottom:6,flexWrap:"wrap"}}>
          {[
            ["chat","💬 Chat"],
            ["backtest","📊 Backtest"],
            ["analysis","🔍 Analysis"],
            ...(canUseAI ? [["scan_builder","🛠 Scan Builder"],["positions","📊 Positions"]] : []),
          ].map(([id,lbl])=>(
            <button key={id} onClick={()=>setAiTab(id)}
              style={{background:aiTab===id
                ?"rgba(232,179,57,0.2)"
                :(id==="scan_builder"||id==="positions")?"rgba(0,212,170,0.05)":"transparent",
                border:`1px solid ${aiTab===id?"#e8b339":(id==="scan_builder"||id==="positions")?"rgba(0,212,170,0.3)":"#1a2c44"}`,
                borderRadius:0,padding:"3px 10px",
                color:aiTab===id?"#49c2e4":(id==="scan_builder"||id==="positions")?"#34d399":"#5a728f",
                fontFamily:"inherit",fontSize:11,fontWeight:aiTab===id?700:400,cursor:"pointer"}}>
              {lbl}
            </button>
          ))}
          {!canEdit&&<span style={{fontSize:10,color:"#23405f",marginLeft:"auto",alignSelf:"center"}}>👁 view only</span>}
        </div>
        {/* Preset pills — only in chat tab */}
        {aiTab==="chat"&&(
          <div style={{display:"flex",gap:3,flexWrap:"wrap"}}>
            {Object.keys(STRATEGY_PRESETS).map(name=>(
              <button key={name} onClick={()=>applyPreset(name)}
                style={{background:activePreset===name?"rgba(96,165,250,0.2)":"rgba(232,179,57,0.05)",
                  border:`1px solid ${activePreset===name?"#49c2e4":"#1a2c44"}`,
                  borderRadius:0,padding:"2px 7px",color:activePreset===name?"#49c2e4":"#8ea2bf",
                  fontFamily:"inherit",fontSize:11,cursor:"pointer",fontWeight:activePreset===name?700:400}}>
                {name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── CHAT TAB ── */}
      {aiTab==="chat"&&(
        <>
          <div style={{flex:1,overflowY:"auto",padding:"10px 14px",display:"flex",flexDirection:"column",gap:8,WebkitOverflowScrolling:"touch"}}>
            {messages.map((m,i)=>(
              <div key={i} style={{alignSelf:m.role==="user"?"flex-end":"flex-start",maxWidth:"88%"}}>
                {m.role==="user"?(
                  <div style={{background:"rgba(163,113,247,0.15)",border:"1px solid rgba(163,113,247,0.3)",
                    borderRadius:"12px 12px 3px 12px",padding:"8px 12px",fontSize:12,color:"#e7edf5",lineHeight:1.5}}>
                    {m.content}
                  </div>
                ):(
                  <div style={{background:"#0f1d30",border:`1px solid ${m.appliedParams?"rgba(34,197,94,0.3)":"#1a2c44"}`,
                    borderLeft:`3px solid ${m.appliedParams?"#2ed47a":"#49c2e4"}`,
                    borderRadius:"3px 12px 12px 12px",padding:"10px 12px",
                    fontSize:11,color:"#e7edf5",lineHeight:1.7,whiteSpace:"pre-wrap"}}>
                    {m.content}
                    {m.appliedParams&&(
                      <div style={{marginTop:5,padding:"3px 7px",background:"rgba(34,197,94,0.06)",
                        borderRadius:0,fontSize:11,color:"#2ed47a"}}>
                        ✅ {Object.entries(m.appliedParams).map(([k,v])=>`${k}:${v}`).join(" · ")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            {loading&&(
              <div style={{alignSelf:"flex-start",background:"#0f1d30",border:"1px solid #1a2c44",
                borderLeft:"3px solid #49c2e4",borderRadius:"3px 12px 12px 12px",
                padding:"10px 14px",fontSize:11,color:"#49c2e4"}}>
                ⟳ Thinking...
              </div>
            )}
            <div ref={messagesEndRef}/>
          </div>
          <div style={{padding:"5px 14px",borderTop:"1px solid #161b22",display:"flex",gap:4,flexWrap:"wrap",flexShrink:0}}>
            {["Explain my settings","More aggressive","Fewer false signals","Best for 15min","Best for Bank Nifty"].map((q,i)=>(
              <button key={i} onClick={()=>setInput(q)}
                style={{background:"rgba(232,179,57,0.04)",border:"1px solid #1a2c44",
                  borderRadius:0,padding:"2px 8px",color:"#5a728f",
                  fontFamily:"inherit",fontSize:11,cursor:"pointer",whiteSpace:"nowrap"}}>
                {q}
              </button>
            ))}
            <button onClick={()=>setMessages([{role:"assistant",content:"Chat cleared!"}])}
              style={{marginLeft:"auto",background:"none",border:"none",color:"#23405f",fontSize:11,cursor:"pointer"}}>
              clear
            </button>
          </div>
          <div style={{padding:"8px 14px 12px",borderTop:"1px solid #1a2c44",display:"flex",gap:8,flexShrink:0}}>
            <input value={input} onChange={e=>setInput(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&sendMessage()}
              placeholder="Ask Gemini AI anything about strategy..."
              style={{flex:1,background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,
                padding:"9px 12px",color:"#e7edf5",fontFamily:"inherit",fontSize:12,outline:"none"}}/>
            <button onClick={sendMessage} disabled={loading||!input.trim()}
              style={{background:"linear-gradient(135deg,#0f1d30,#e8b339)",border:"none",
                borderRadius:0,padding:"9px 14px",color:"#fff",fontFamily:"inherit",
                fontSize:12,fontWeight:700,cursor:loading||!input.trim()?"default":"pointer",
                opacity:loading||!input.trim()?0.5:1}}>
              {loading?"⟳":"→"}
            </button>
          </div>
        </>
      )}

      {/* ── BACKTEST TAB ── */}
      {aiTab==="backtest"&&(
        <div style={{flex:1,overflowY:"auto",padding:14}}>
          <div style={{marginBottom:12,fontSize:11,color:"#5a728f",lineHeight:1.6}}>
            Analyses your trade history from the journal. Calculates win rate, success ratio per signal type, best/worst symbols.
          </div>
          <button onClick={runBacktest} disabled={btLoading}
            style={{width:"100%",background:"linear-gradient(135deg,#0f1d30,#2563eb)",border:"none",
              borderRadius:0,padding:"12px",color:"#fff",fontFamily:"inherit",fontSize:13,
              fontWeight:700,cursor:btLoading?"default":"pointer",marginBottom:14}}>
            {btLoading?"⟳ Analysing your trades...":"📊 Run Backtest"}
          </button>

          {btResult?.error&&(
            <div style={{background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.25)",
              borderRadius:0,padding:14,color:"#ff5b6c",fontSize:12}}>
              ⚠ {btResult.error}
            </div>
          )}

          {btResult&&!btResult.error&&(
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {/* Overall */}
              <div style={{background:"#0f1d30",border:"1px solid #1a2c44",borderRadius:0,padding:14}}>
                <div style={{fontSize:11,color:"#49c2e4",fontWeight:700,letterSpacing:1,marginBottom:10}}>OVERALL PERFORMANCE</div>
                <div style={{fontSize:10,color:"#5a728f",marginBottom:8}}>
                  {btResult.totalAll} total trades · {btResult.total} closed · {btResult.openCount} still open
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:8}}>
                  {[
                    ["Win Rate",`${btResult.winRate}%`, parseFloat(btResult.winRate)>=50?"#2ed47a":"#ff5b6c"],
                    ["Trades",btResult.total,"#e7edf5"],
                    ["Total P&L",`₹${parseInt(btResult.totalPnl).toLocaleString("en-IN")}`,parseInt(btResult.totalPnl)>=0?"#2ed47a":"#ff5b6c"],
                    ["Avg Win",`₹${btResult.avgWin}`,"#2ed47a"],
                    ["Avg Loss",`₹${btResult.avgLoss}`,"#ff5b6c"],
                    ["R:R",btResult.rr,parseFloat(btResult.rr)>=1?"#2ed47a":"#f59e0b"],
                  ].map(([l,v,c])=>(
                    <div key={l} style={{background:"#0b1624",borderRadius:0,padding:"8px 10px",textAlign:"center"}}>
                      <div style={{fontSize:9,color:"#5a728f",marginBottom:3,letterSpacing:1}}>{l}</div>
                      <div style={{fontSize:14,fontWeight:700,color:c}}>{v}</div>
                    </div>
                  ))}
                </div>
                {/* Win/Loss bar */}
                <div style={{height:6,background:"#ff5b6c",borderRadius:0,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${btResult.winRate}%`,background:"#2ed47a",transition:"width 1s"}}/>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:"#5a728f",marginTop:3}}>
                  <span>✓ {btResult.wins} wins</span><span>✗ {btResult.losses} losses</span>
                </div>
              </div>

              {/* Signal stats */}
              {btResult.sigStats?.length>0&&(
                <div style={{background:"#0f1d30",border:"1px solid #1a2c44",borderRadius:0,padding:14}}>
                  <div style={{fontSize:11,color:"#49c2e4",fontWeight:700,letterSpacing:1,marginBottom:10}}>SIGNAL SUCCESS RATE</div>
                  {btResult.sigStats.map(s=>(
                    <div key={s.sig} style={{marginBottom:8}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                        <span style={{fontSize:11,color:"#e7edf5",flex:1}}>{s.sig}</span>
                        <span style={{fontSize:11,fontWeight:700,color:parseFloat(s.rate)>=50?"#2ed47a":"#ff5b6c"}}>{s.rate}%</span>
                        <span style={{fontSize:10,color:"#5a728f",marginLeft:8}}>{s.total} trades</span>
                      </div>
                      <div style={{height:4,background:"#0b1624",borderRadius:2,overflow:"hidden"}}>
                        <div style={{height:"100%",width:`${s.rate}%`,background:parseFloat(s.rate)>=60?"#2ed47a":parseFloat(s.rate)>=40?"#f59e0b":"#ff5b6c"}}/>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Best/Worst symbols */}
              {btResult.symList?.length>0&&(
                <div style={{background:"#0f1d30",border:"1px solid #1a2c44",borderRadius:0,padding:14}}>
                  <div style={{fontSize:11,color:"#49c2e4",fontWeight:700,letterSpacing:1,marginBottom:10}}>SYMBOL PERFORMANCE</div>
                  {btResult.symList.slice(0,8).map(s=>(
                    <div key={s.sym} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                      padding:"5px 0",borderBottom:"1px solid #1a2c44"}}>
                      <span style={{fontSize:12,fontWeight:700,color:"#e7edf5",flex:1}}>{s.sym}</span>
                      <span style={{fontSize:11,color:"#5a728f",marginRight:12}}>{s.rate}% · {s.total} trades</span>
                      <span style={{fontSize:12,fontWeight:700,color:s.pnl>=0?"#2ed47a":"#ff5b6c"}}>
                        {s.pnl>=0?"+":""}{s.pnl.toLocaleString("en-IN")}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── ANALYSIS TAB ── */}
      {aiTab==="analysis"&&(
        <div style={{flex:1,overflowY:"auto",padding:14}}>

          {/* Open positions widget */}
          {(positions?.net||[]).filter(p=>parseInt(p.quantity)!==0).length>0&&(
            <div style={{background:"#0f1d30",border:"1px solid #1a2c44",borderRadius:0,padding:12,marginBottom:12}}>
              <div style={{fontSize:10,color:"#49c2e4",fontWeight:700,letterSpacing:1,marginBottom:8}}>📋 OPEN POSITIONS</div>
              {(positions?.net||[]).filter(p=>parseInt(p.quantity)!==0).map((p,i)=>{
                const pnl = parseFloat(p.pnl||0);
                return(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                    padding:"4px 0",borderBottom:"1px solid #1a2c44"}}>
                    <div>
                      <span style={{fontSize:11,fontWeight:700,color:"#e7edf5"}}>{p.tradingsymbol}</span>
                      <span style={{fontSize:10,color:parseInt(p.quantity)>0?"#2ed47a":"#ff5b6c",marginLeft:6}}>
                        {parseInt(p.quantity)>0?"▲ LONG":"▼ SHORT"} {Math.abs(parseInt(p.quantity))}
                      </span>
                    </div>
                    <span style={{fontSize:11,fontWeight:700,color:pnl>=0?"#2ed47a":"#ff5b6c"}}>
                      {pnl>=0?"+":""}{pnl.toFixed(0)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{marginBottom:12,fontSize:11,color:"#5a728f",lineHeight:1.6}}>
            AI reads your full trade history + open positions and gives pattern analysis, weak spots, and next trade recommendation.
          </div>
          <button onClick={runAnalysis} disabled={anLoading}
            style={{width:"100%",background:"linear-gradient(135deg,#4c1d95,#7c3aed)",border:"none",
              borderRadius:0,padding:"12px",color:"#fff",fontFamily:"inherit",fontSize:13,
              fontWeight:700,cursor:anLoading?"default":"pointer",marginBottom:14}}>
            {anLoading?"⟳ AI is analysing your history...":"🔍 Run AI Analysis"}
          </button>

          {anResult?.error&&(
            <div style={{background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.25)",
              borderRadius:0,padding:14,color:"#ff5b6c",fontSize:12}}>
              ⚠ {anResult.error}
            </div>
          )}

          {anResult&&!anResult.error&&(
            <div style={{background:"#0f1d30",border:"1px solid rgba(124,58,237,0.3)",
              borderLeft:"3px solid #7c3aed",borderRadius:0,padding:16}}>
              <div style={{fontSize:11,color:"#a78bfa",fontWeight:700,letterSpacing:1,marginBottom:10}}>
                🧠 AI ANALYSIS
              </div>
              <div style={{fontSize:12,color:"#e7edf5",lineHeight:1.8,whiteSpace:"pre-wrap"}}>
                {anResult.text}
              </div>
              {anResult.appliedParams&&(
                <div style={{marginTop:10,padding:"6px 10px",background:"rgba(34,197,94,0.08)",
                  border:"1px solid rgba(34,197,94,0.25)",borderRadius:0,fontSize:11,color:"#2ed47a"}}>
                  ✅ Settings updated: {Object.entries(anResult.appliedParams).map(([k,v])=>`${k}:${v}`).join(" · ")}
                </div>
              )}
              <button onClick={runAnalysis} disabled={anLoading}
                style={{marginTop:10,background:"rgba(124,58,237,0.1)",border:"1px solid rgba(124,58,237,0.3)",
                  borderRadius:0,padding:"5px 12px",color:"#a78bfa",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>
                ↻ Refresh Analysis
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── SCAN BUILDER TAB ── */}
      {aiTab==="scan_builder"&&canUseAI&&(
        <div style={{display:"flex",flexDirection:"column",height:"100%"}}>
          <div style={{padding:"8px 14px",background:"rgba(0,212,170,0.04)",borderBottom:"1px solid #1a2c44",flexShrink:0}}>
            <div style={{fontSize:11,color:"#34d399",fontWeight:700,marginBottom:2}}>🛠 Custom Scan Builder</div>
            <div style={{fontSize:10,color:"#5a728f"}}>
              Describe your scan in plain English → AI generates parameters → Apply with one click
            </div>
          </div>

          {/* Chat messages */}
          <div style={{flex:1,overflowY:"auto",padding:"10px 14px",display:"flex",flexDirection:"column",gap:8}}>
            {sbMessages.map((m,i)=>(
              <div key={i} style={{alignSelf:m.role==="user"?"flex-end":"flex-start",maxWidth:"90%"}}>
                {m.role==="user"?(
                  <div style={{background:"rgba(52,211,153,0.12)",border:"1px solid rgba(52,211,153,0.25)",
                    borderRadius:"12px 12px 3px 12px",padding:"8px 12px",fontSize:12,color:"#e7edf5",lineHeight:1.5}}>
                    {m.content}
                  </div>
                ):(
                  <div style={{background:"#0f1d30",border:"1px solid #1a2c44",borderLeft:"3px solid #34d399",
                    borderRadius:"3px 12px 12px 12px",padding:"10px 12px",fontSize:11,color:"#e7edf5",
                    lineHeight:1.7,whiteSpace:"pre-wrap"}}>
                    {m.content}
                    {/* Show Apply button if this message has params */}
                    {m.params&&canEdit&&(
                      <div style={{marginTop:8,padding:"6px 10px",background:"rgba(0,212,170,0.06)",
                        border:"1px solid rgba(0,212,170,0.3)",borderRadius:0}}>
                        <div style={{fontSize:10,color:"#34d399",marginBottom:4,fontWeight:700}}>
                          Suggested params: {Object.keys(m.params).join(", ")}
                        </div>
                        <div style={{fontSize:10,color:"#5a728f",marginBottom:6,fontFamily:"monospace"}}>
                          {JSON.stringify(m.params,null,1).slice(0,200)}
                        </div>
                        <button onClick={()=>applyScanParams(m.params)}
                          style={{background:"linear-gradient(135deg,#059669,#34d399)",border:"none",
                            borderRadius:0,padding:"5px 14px",color:"#fff",fontFamily:"inherit",
                            fontSize:11,fontWeight:700,cursor:"pointer"}}>
                          ✅ Apply to Signal Settings
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            {sbLoading&&(
              <div style={{alignSelf:"flex-start",background:"#0f1d30",border:"1px solid #1a2c44",
                borderLeft:"3px solid #34d399",borderRadius:"3px 12px 12px 12px",
                padding:"10px 14px",fontSize:11,color:"#34d399"}}>
                ⟳ Building scan params...
              </div>
            )}
            <div ref={sbEndRef}/>
          </div>

          {/* Quick prompts */}
          <div style={{padding:"5px 14px",borderTop:"1px solid #161b22",display:"flex",gap:3,flexWrap:"wrap",flexShrink:0}}>
            {["Oversold RSI + strong volume","MACD bullish cross on 15min","Near lower Bollinger Band","Stochastic oversold with trend","Breakout above resistance"].map((q,i)=>(
              <button key={i} onClick={()=>setSbInput(q)}
                style={{background:"rgba(0,212,170,0.04)",border:"1px solid #1a2c44",
                  borderRadius:0,padding:"2px 8px",color:"#5a728f",
                  fontFamily:"inherit",fontSize:11,cursor:"pointer",whiteSpace:"nowrap"}}>
                {q}
              </button>
            ))}
            <button onClick={()=>setSbMessages([{role:"assistant",content:"New conversation. Describe your scan."}])}
              style={{marginLeft:"auto",background:"none",border:"none",color:"#23405f",fontSize:11,cursor:"pointer"}}>
              clear
            </button>
          </div>

          {/* Input */}
          <div style={{padding:"8px 14px 12px",borderTop:"1px solid #1a2c44",display:"flex",gap:8,flexShrink:0}}>
            <input value={sbInput} onChange={e=>setSbInput(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&sendScanBuilder()}
              placeholder="e.g. Find stocks oversold on RSI below 30 with MACD crossing up..."
              style={{flex:1,background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,
                padding:"9px 12px",color:"#e7edf5",fontFamily:"inherit",fontSize:12,outline:"none"}}/>
            <button onClick={sendScanBuilder} disabled={sbLoading||!sbInput.trim()}
              style={{background:"linear-gradient(135deg,#059669,#10b981)",border:"none",
                borderRadius:0,padding:"9px 14px",color:"#fff",fontFamily:"inherit",
                fontSize:12,fontWeight:700,cursor:sbLoading||!sbInput.trim()?"default":"pointer",
                opacity:sbLoading||!sbInput.trim()?0.5:1}}>
              {sbLoading?"⟳":"→"}
            </button>
          </div>
        </div>
      )}

      {/* ── POSITION ANALYSIS TAB ── */}
      {aiTab==="positions"&&canUseAI&&(
        <div style={{display:"flex",flexDirection:"column",height:"100%"}}>
          <div style={{padding:"8px 14px",background:"rgba(245,158,11,0.04)",borderBottom:"1px solid #1a2c44",flexShrink:0}}>
            <div style={{fontSize:11,color:"#fbbf24",fontWeight:700,marginBottom:2}}>📊 Position Analysis</div>
            <div style={{fontSize:10,color:"#5a728f"}}>
              AI analyses your open positions + trade history and gives risk/reward insights
            </div>
          </div>

          {/* Run button on first load */}
          {!posAnalysed&&posMessages.length===0&&(
            <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:20,gap:14}}>
              {/* Open position count */}
              {(()=>{
                const openCount = (watchers||[]).filter(w=>w.status==="ACTIVE"||w.status==="active"||w.status==="WATCHING").length;
                const closedCount = getJournal().length;
                return(
                  <div style={{textAlign:"center"}}>
                    <div style={{fontSize:28,marginBottom:8}}>📊</div>
                    <div style={{fontSize:12,color:"#e7edf5",marginBottom:4,fontWeight:700}}>
                      Ready to analyse your portfolio
                    </div>
                    <div style={{fontSize:11,color:"#5a728f",lineHeight:1.8}}>
                      {openCount} open position{openCount!==1?"s":""} · {closedCount} trade{closedCount!==1?"s":""} in journal
                    </div>
                  </div>
                );
              })()}
              <button onClick={()=>runPositionAnalysis()}
                style={{background:"linear-gradient(135deg,#b45309,#f59e0b)",border:"none",
                  borderRadius:0,padding:"12px 28px",color:"#fff",fontFamily:"inherit",
                  fontSize:13,fontWeight:700,cursor:"pointer"}}>
                🔍 Analyse My Positions
              </button>
              <div style={{fontSize:10,color:"#23405f",textAlign:"center",maxWidth:280,lineHeight:1.6}}>
                AI will review each open trade for risk/reward, check your closed trade patterns, and suggest what to watch.
              </div>
            </div>
          )}

          {/* Chat messages */}
          {posMessages.length>0&&(
            <div style={{flex:1,overflowY:"auto",padding:"10px 14px",display:"flex",flexDirection:"column",gap:8}}>
              {posMessages.map((m,i)=>(
                <div key={i} style={{alignSelf:m.role==="user"?"flex-end":"flex-start",maxWidth:"92%"}}>
                  {m.role==="user"?(
                    <div style={{background:"rgba(245,158,11,0.12)",border:"1px solid rgba(245,158,11,0.25)",
                      borderRadius:"12px 12px 3px 12px",padding:"8px 12px",fontSize:12,color:"#e7edf5",lineHeight:1.5}}>
                      {m.content}
                    </div>
                  ):(
                    <div style={{background:"#0f1d30",border:"1px solid #1a2c44",borderLeft:"3px solid #f59e0b",
                      borderRadius:"3px 12px 12px 12px",padding:"10px 12px",fontSize:11,color:"#e7edf5",
                      lineHeight:1.8,whiteSpace:"pre-wrap"}}>
                      {m.content}
                    </div>
                  )}
                </div>
              ))}
              {posLoading&&(
                <div style={{alignSelf:"flex-start",background:"#0f1d30",border:"1px solid #1a2c44",
                  borderLeft:"3px solid #f59e0b",borderRadius:"3px 12px 12px 12px",
                  padding:"10px 14px",fontSize:11,color:"#f59e0b"}}>
                  ⟳ Analysing positions...
                </div>
              )}
              <div ref={posEndRef}/>
            </div>
          )}

          {/* Follow-up input — shown after first analysis */}
          {posAnalysed&&(
            <>
              <div style={{padding:"5px 14px",borderTop:"1px solid #161b22",display:"flex",gap:3,flexWrap:"wrap",flexShrink:0}}>
                {["Which position has most risk?","What's my win rate?","Should I exit any now?","What signals are working best?"].map((q,i)=>(
                  <button key={i} onClick={()=>setPosInput(q)}
                    style={{background:"rgba(245,158,11,0.04)",border:"1px solid #1a2c44",
                      borderRadius:0,padding:"2px 8px",color:"#5a728f",
                      fontFamily:"inherit",fontSize:11,cursor:"pointer",whiteSpace:"nowrap"}}>
                    {q}
                  </button>
                ))}
                <button onClick={()=>{setPosMessages([]);setPosAnalysed(false);}}
                  style={{marginLeft:"auto",background:"none",border:"none",color:"#23405f",fontSize:11,cursor:"pointer"}}>
                  clear
                </button>
              </div>
              <div style={{padding:"8px 14px 12px",borderTop:"1px solid #1a2c44",display:"flex",gap:8,flexShrink:0}}>
                <input value={posInput} onChange={e=>setPosInput(e.target.value)}
                  onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&runPositionAnalysis(posInput||undefined)}
                  placeholder="Ask about a specific position, trade, or risk..."
                  style={{flex:1,background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,
                    padding:"9px 12px",color:"#e7edf5",fontFamily:"inherit",fontSize:12,outline:"none"}}/>
                <button onClick={()=>runPositionAnalysis(posInput||undefined)} disabled={posLoading}
                  style={{background:"linear-gradient(135deg,#b45309,#f59e0b)",border:"none",
                    borderRadius:0,padding:"9px 14px",color:"#fff",fontFamily:"inherit",
                    fontSize:12,fontWeight:700,cursor:posLoading?"default":"pointer",opacity:posLoading?0.5:1}}>
                  {posLoading?"⟳":"→"}
                </button>
              </div>
            </>
          )}
        </div>
      )}

    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// CUSTOM SCAN TAB
// Users build their own scan conditions, save per-user in Firestore,
// run them against any watchlist, see results.
// ══════════════════════════════════════════════════════════════════════════════

// Available fields the user can filter on (mapped to analyze() return keys)
const CS_FIELDS = [
  // Momentum
  {key:"rsi",        label:"RSI",              unit:"",   hint:"0–100 · oversold<30 overbought>70"},
  {key:"stoch_k",    label:"Stoch %K",         unit:"",   hint:"0–100 · oversold<20 overbought>80"},
  {key:"stoch_d",    label:"Stoch %D",         unit:"",   hint:"0–100"},
  // TSI (True Strength Index)
  {key:"tsi",        label:"TSI",              unit:"",   hint:"-1 to +1 · >0=bullish <0=bearish"},
  {key:"tsi_signal", label:"TSI Signal",       unit:"",   hint:"TSI signal line for cross detection"},
  // MACD
  {key:"macd",       label:"MACD Line",        unit:"",   hint:"positive=above zero=bullish"},
  {key:"macd_signal",label:"MACD Signal",      unit:"",   hint:""},
  {key:"macd_hist",  label:"MACD Histogram",   unit:"",   hint:"positive=bullish momentum"},
  // Bollinger Bands
  {key:"bb_upper",   label:"BB Upper Band",    unit:"₹",  hint:"price level"},
  {key:"bb_lower",   label:"BB Lower Band",    unit:"₹",  hint:"price level"},
  {key:"bb_mid",     label:"BB Mid (SMA20)",   unit:"₹",  hint:""},
  {key:"bb_width",   label:"BB Width %",       unit:"%",  hint:"<3=squeeze >8=expansion"},
  // EMAs & Price
  {key:"ema9",       label:"EMA 9",            unit:"₹",  hint:""},
  {key:"ema50",      label:"EMA 50",           unit:"₹",  hint:""},
  {key:"price",      label:"LTP / Price",      unit:"₹",  hint:""},
  // Demand / Supply Zones
  {key:"demandLow",  label:"Demand Zone Low",  unit:"₹",  hint:"nearest demand zone lower bound"},
  {key:"demandHigh", label:"Demand Zone High", unit:"₹",  hint:"nearest demand zone upper bound"},
  {key:"supplyLow",  label:"Supply Zone Low",  unit:"₹",  hint:"nearest supply zone lower bound"},
  {key:"supplyHigh", label:"Supply Zone High", unit:"₹",  hint:"nearest supply zone upper bound"},
  // Volume
  {key:"volRatio",   label:"Volume Ratio",     unit:"x",  hint:"vs 5-bar avg · >1.5=high"},
  {key:"buyVolPct",  label:"Buy Vol %",        unit:"%",  hint:"0–100 · >60=buying pressure"},
  {key:"sellVolPct", label:"Sell Vol %",       unit:"%",  hint:"0–100"},
  // Derived relational (above/below operators)
  {key:"_ema9_vs_ema50",     label:"EMA9 vs EMA50",        unit:"", hint:"above=bullish trend", derived:true},
  {key:"_price_vs_ema50",    label:"Price vs EMA50",       unit:"", hint:"above=price>EMA50",   derived:true},
  {key:"_price_vs_ema9",     label:"Price vs EMA9",        unit:"", hint:"above=price>EMA9",    derived:true},
  {key:"_macd_vs_signal",    label:"MACD vs Signal",       unit:"", hint:"above=bullish cross",  derived:true},
  {key:"_tsi_vs_signal",     label:"TSI vs TSI Signal",    unit:"", hint:"above=bullish cross",  derived:true},
  {key:"_price_vs_demand",   label:"Price vs Demand Zone", unit:"", hint:"above=price above demand zone (bullish bounce)",  derived:true},
  {key:"_price_vs_supply",   label:"Price vs Supply Zone", unit:"", hint:"above=price broke above supply (bullish breakout)", derived:true},
  {key:"_price_in_demand",   label:"Price IN Demand Zone", unit:"", hint:"above=price is inside demand zone (potential bounce)", derived:true},
  {key:"_price_in_supply",   label:"Price IN Supply Zone", unit:"", hint:"above=price is inside supply zone (potential rejection)", derived:true},
  // Signal counts
  {key:"_buy_count",  label:"Buy Signals #",   unit:"",  hint:"number of BUY signals fired"},
  {key:"_sell_count", label:"Sell Signals #",  unit:"",  hint:"number of SELL signals fired"},
  {key:"_total_sigs", label:"Total Signals #", unit:"",  hint:""},
  // ── Fundamental fields (Investment mode) ──
  {key:"f_pe",              label:"PE Ratio",           unit:"",  hint:"Price/Earnings · <15=value <25=fair", group:"fundamental"},
  {key:"f_pb",              label:"PB Ratio",           unit:"",  hint:"Price/Book · <1=undervalued <3=fair", group:"fundamental"},
  {key:"f_roe",             label:"ROE %",              unit:"%", hint:"Return on Equity · >15=good >25=excellent", group:"fundamental"},
  {key:"f_roce",            label:"ROCE %",             unit:"%", hint:"Return on Capital Employed · >15=good", group:"fundamental"},
  {key:"f_debt_equity",     label:"Debt/Equity",        unit:"",  hint:"<0.5=low debt <1=moderate >2=high", group:"fundamental"},
  {key:"f_sales_growth",    label:"Sales Growth %",     unit:"%", hint:"Revenue growth · >10=good >20=high", group:"fundamental"},
  {key:"f_profit_growth",   label:"Profit Growth %",    unit:"%", hint:"Profit growth · >15=good >25=high", group:"fundamental"},
  {key:"f_market_cap",      label:"Market Cap (Cr)",    unit:"₹Cr", hint:"in Crores · <5000=small <25000=mid", group:"fundamental"},
  {key:"f_promoter_holding",label:"Promoter Holding %", unit:"%", hint:">50=good >70=high conviction", group:"fundamental"},
  {key:"f_dividend_yield",  label:"Dividend Yield %",   unit:"%", hint:">2=decent >5=high yield", group:"fundamental"},
  {key:"f_eps",             label:"EPS",                unit:"₹", hint:"Earnings Per Share", group:"fundamental"},
  {key:"f_book_value",      label:"Book Value",         unit:"₹", hint:"Net asset value per share", group:"fundamental"},
  {key:"f_industry_pe",     label:"Industry PE",        unit:"",  hint:"Sector average PE for comparison", group:"fundamental"},
  {key:"f_cash_flow",       label:"Cash Flow (Cr)",     unit:"₹Cr", hint:"Operating cash flow · >0=positive >500=strong", group:"fundamental"},
  {key:"f_current_ratio",   label:"Current Ratio",      unit:"",  hint:"Liquidity · >1.5=healthy <1=risk", group:"fundamental"},
];
const CS_OPS_NUM     = [{v:"gt",l:"> Greater than"},{v:"gte",l:"≥ At least"},{v:"lt",l:"< Less than"},{v:"lte",l:"≤ At most"},{v:"between",l:"↔ Between"}];
const CS_OPS_DERIVED = [{v:"above",l:"↑ Above / Bullish"},{v:"below",l:"↓ Below / Bearish"}];

function csGetValue(r, field) {
  if(!r) return null;
  // EMA relational
  if(field === "_ema9_vs_ema50")  return (r.ema9!=null && r.ema50!=null) ? (r.ema9>r.ema50?1:-1) : null;
  if(field === "_price_vs_ema50") return (r.price!=null && r.ema50!=null) ? (r.price>r.ema50?1:-1) : null;
  if(field === "_price_vs_ema9")  return (r.price!=null && r.ema9!=null)  ? (r.price>r.ema9?1:-1)  : null;
  // MACD relational
  if(field === "_macd_vs_signal") return (r.macd!=null && r.macd_signal!=null) ? (r.macd>r.macd_signal?1:-1) : null;
  // TSI relational — above = TSI crossed above its signal line (bullish)
  if(field === "_tsi_vs_signal")  return (r.tsi!=null && r.tsi_signal!=null) ? (r.tsi>r.tsi_signal?1:-1) : null;
  // Demand/Supply zone relational
  if(field === "_price_vs_demand") {
    const p=parseFloat(r.price||r.ltp||0), dh=r.demandHigh;
    return (p && dh!=null) ? (p>dh?1:-1) : null; // above=bounced above demand
  }
  if(field === "_price_vs_supply") {
    const p=parseFloat(r.price||r.ltp||0), sh=r.supplyHigh;
    return (p && sh!=null) ? (p>sh?1:-1) : null; // above=broke above supply (bullish breakout)
  }
  if(field === "_price_in_demand") {
    const p=parseFloat(r.price||r.ltp||0), dl=r.demandLow, dh=r.demandHigh;
    return (p && dl!=null && dh!=null) ? (p>=dl && p<=dh?1:-1) : null; // above=inside demand zone
  }
  if(field === "_price_in_supply") {
    const p=parseFloat(r.price||r.ltp||0), sl=r.supplyLow, sh=r.supplyHigh;
    return (p && sl!=null && sh!=null) ? (p>=sl && p<=sh?1:-1) : null; // above=inside supply zone
  }
  // Signal counts
  if(field === "_buy_count")   return (r.signals||[]).filter(s=>s.type==="BUY").length;
  if(field === "_sell_count")  return (r.signals||[]).filter(s=>s.type==="SELL").length;
  if(field === "_total_sigs")  return (r.signals||[]).length;
  // Derived computed fields
  if(field === "macd_hist") return (r.macd!=null && r.macd_signal!=null) ? parseFloat((r.macd - r.macd_signal).toFixed(4)) : null;
  if(field === "bb_width")  return (r.bb_upper!=null && r.bb_lower!=null && r.bb_mid!=null && r.bb_mid!==0)
    ? parseFloat(((r.bb_upper - r.bb_lower) / r.bb_mid * 100).toFixed(2)) : null;
  // price is stored as r.price or r.ltp
  if(field === "price") return parseFloat(r.price||r.ltp||0)||null;
  // Fundamental fields — look in r.fundamentals
  if(field.startsWith("f_") && r.fundamentals) {
    const fKey = field.substring(2); // strip "f_" prefix
    const v = r.fundamentals[fKey];
    return v!=null ? parseFloat(v) : null;
  }
  return r[field]!=null ? parseFloat(r[field]) : null;
}

function csEval(r, cond) {
  const v = csGetValue(r, cond.field);
  if(v == null) return false;
  const isDerived = cond.field.startsWith("_ema")||cond.field.startsWith("_price")||cond.field==="_macd_vs_signal"||cond.field==="_tsi_vs_signal";
  if(isDerived) return cond.op==="above" ? v>0 : v<0;
  const n = parseFloat(cond.value);
  switch(cond.op){
    case "gt":      return v > n;
    case "gte":     return v >= n;
    case "lt":      return v < n;
    case "lte":     return v <= n;
    case "between": return v >= n && v <= parseFloat(cond.value2||n);
    default: return false;
  }
}

function csMatch(r, scan) {
  if(!r || r.error) return false;
  if(!scan.conditions?.length) return true;
  const results = scan.conditions.map(c=>csEval(r,c));
  return scan.logic==="OR" ? results.some(Boolean) : results.every(Boolean);
}

function CustomScanTab({ signalParams, authed, onShowToast, fbUser, isAdmin, userRole, indexQuotes, onTrade, canTrade,
  scanResults, positions, setSignalParams, journal, hasAIAccess, watchers, addVirtualRef, sendTgAlert=null }) {
  const uid = fbUser?.uid;
  const canEdit = userRole !== "viewer";

  // ── Saved scans (Firestore) ───────────────────────────────────────────────
  const [savedScans, setSavedScans] = useState([]);
  const [loadingScans, setLoadingScans] = useState(false);
  const [savingMsg, setSavingMsg] = useState("");

  // ── Scan mode: Trading (TA only) vs Investment (Fundamental + TA) ────────
  const [csScanMode, setCsScanMode] = useLocalStorage('cs_scan_mode', 'trading');

  // Prefetch fundamentals into server cache when Investment mode is activated
  const prefetchFundamentals = useCallback((symbols)=>{
    if(!symbols?.length) return;
    apiFetch("/fundamentals/prefetch",{
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({symbols: symbols.slice(0,50)}),
    }).catch(()=>{});
  },[]);
  const [smallCapSymbols, setSmallCapSymbols] = useState([]);
  const [allNseSymbols, setAllNseSymbols] = useState([]);
  const [loadingWl, setLoadingWl] = useState(false);

  // Fetch dynamic watchlists on demand
  const fetchSmallCaps = useCallback(async()=>{
    if(smallCapSymbols.length) return;
    setLoadingWl(true);
    try {
      const d = await apiFetch("/watchlists/smallcap");
      if(d?.symbols?.length) setSmallCapSymbols(d.symbols);
    } catch(e){ if(onShowToast) onShowToast("Failed to load small caps","error"); }
    setLoadingWl(false);
  },[smallCapSymbols]);

  const fetchAllNse = useCallback(async()=>{
    if(allNseSymbols.length) return;
    setLoadingWl(true);
    try {
      const d = await apiFetch("/watchlists/all-nse-eq");
      if(d?.symbols?.length) setAllNseSymbols(d.symbols);
    } catch(e){ if(onShowToast) onShowToast("Failed to load NSE stocks","error"); }
    setLoadingWl(false);
  },[allNseSymbols]);

  // ── Builder state ──────────────────────────────────────────────────────────
  const [view, setView] = useState("list"); // "list"|"builder"|"results"
  const [editId, setEditId] = useState(null); // null=new
  const [scanName, setScanName]   = useState("");
  const [scanDesc, setScanDesc]   = useState("");
  const [logic,    setLogic]      = useState("AND");
  const [conditions, setConditions] = useState([{field:"rsi",op:"lt",value:"35",value2:""}]);

  // ── Runner state ──────────────────────────────────────────────────────────
  const [running,      setRunning]     = useState(false);
  const [runProgress,  setRunProgress] = useState(""); // live status while async scan runs
  const [runResults,   setRunResults]  = useState(null); // {scan, matched, total, ts}
  const [runError,     setRunError]    = useState("");
  const [activeScanId, setActiveScanId]= useState(null);
  // Run-time options (picked when clicking Run — not saved with scan)
  const [runTf,        setRunTf]       = useState("15min");
  const [runWl,        setRunWl]       = useState("NIFTY 50");
  const [runCustomSym, setRunCustomSym]= useState(""); // comma-separated custom symbols
  const [runOptScan,   setRunOptScan]  = useState(null); // scan waiting for options
  const [runOptOpen,   setRunOptOpen]  = useState(false);

  // ── Auto-Virtual for Custom Scans ─────────────────────────────────────────
  const [autoVirtual,    setAutoVirtual]    = useLocalStorage('cs_auto_virtual', false);
  const [autoVirtualMin, setAutoVirtualMin] = useLocalStorage('cs_auto_virtual_min', 2);

  // ── AI Builder state ───────────────────────────────────────────────────────
  const [builderMode, setBuilderMode] = useState("ai"); // "ai" | "manual"
  const [aiMsgs,  setAiMsgs]  = useState([{role:"assistant",_uiOnly:true,content:"Describe your scan in plain English and I'll build the conditions for you.\n\nExamples:\n• \"RSI below 30 with MACD turning bullish\"\n• \"Stocks near lower Bollinger Band with high volume surge\"\n• \"EMA 9 crossed above EMA 50 with RSI between 45 and 65\"\n• \"Stochastic oversold, buy volume pressure, price above EMA 50\"\n• \"Nifty 50 stocks with PE below 20 and good ROE\"\n• \"Low debt, high promoter holding, price above EMA 50\""}]);
  const [aiInput, setAiInput] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiPending, setAiPending] = useState(null); // parsed scan object from last AI response
  const aiEndRef = useRef(null);
  useEffect(()=>{ aiEndRef.current?.scrollIntoView({behavior:"smooth"}); },[aiMsgs]);

  // ── Load scans from Firestore ─────────────────────────────────────────────
  const loadScans = useCallback(async()=>{
    if(!uid) return;
    setLoadingScans(true);
    try {
      const colRef = collection(_db,"users",uid,"custom_scans");
      const snap = await getDocs(query(colRef, orderBy("created_at","desc")));
      setSavedScans(snap.docs.map(d=>({id:d.id,...d.data()})));
    } catch(e){ onShowToast?.("Failed to load custom scans","error"); }
    setLoadingScans(false);
  },[uid]);

  useEffect(()=>{ loadScans(); },[loadScans]);

  // ── Open builder for new scan ─────────────────────────────────────────────
  const startNew = () => {
    setEditId(null); setScanName(""); setScanDesc(""); setLogic("AND");
    setConditions([{field:"rsi",op:"lt",value:"35",value2:""}]);
    setBuilderMode("ai"); setAiPending(null);
    setView("builder");
  };

  // ── Open builder to edit existing scan ───────────────────────────────────
  const startEdit = (s) => {
    setEditId(s.id); setScanName(s.name||""); setScanDesc(s.description||"");
    setLogic(s.logic||"AND");
    setConditions((s.conditions||[]).map(c=>({...c,value:String(c.value??""),value2:String(c.value2??"")})));
    setBuilderMode("manual"); setAiPending(null);
    setView("builder");
  };

  // ── Save scan to Firestore ────────────────────────────────────────────────
  const saveScan = async () => {
    if(!scanName.trim()) { onShowToast?.("Enter a scan name","error"); return; }
    if(!conditions.length) { onShowToast?.("Add at least one condition","error"); return; }
    setSavingMsg("Saving...");
    // Build conditions — never store undefined (Firestore rejects it)
    const cleanConditions = conditions
      .filter(c=>c.field&&c.op)
      .map(c=>{
        const base = { field:c.field, op:c.op };
        if(!isDerived(c.field)) {
          base.value = isNaN(parseFloat(c.value)) ? (c.value||0) : parseFloat(c.value);
          if(c.op==="between" && c.value2!=="" && !isNaN(parseFloat(c.value2))) {
            base.value2 = parseFloat(c.value2);
          }
        }
        return base;
      });
    const data = {
      name: scanName.trim(),
      description: scanDesc.trim() || "",
      logic,
      conditions: cleanConditions,
      updated_at: new Date().toISOString(),
    };
    try {
      if(editId) {
        await updateDoc(doc(_db,"users",uid,"custom_scans",editId), data);
        setSavingMsg("✓ Updated");
      } else {
        data.created_at = new Date().toISOString();
        await addDoc(collection(_db,"users",uid,"custom_scans"), data);
        setSavingMsg("✓ Saved");
      }
      await loadScans();
      setTimeout(()=>{ setSavingMsg(""); setView("list"); },800);
    } catch(e){ setSavingMsg(""); onShowToast?.(`Save failed: ${e.message}`,"error"); }
  };

  // ── Delete scan ───────────────────────────────────────────────────────────
  const deleteScan = async (id, name) => {
    if(!window.confirm(`Delete "${name}"?`)) return;
    try {
      await deleteDoc(doc(_db,"users",uid,"custom_scans",id));
      setSavedScans(p=>p.filter(s=>s.id!==id));
      if(activeScanId===id){ setRunResults(null); setActiveScanId(null); }
      onShowToast?.(`"${name}" deleted`,"success");
    } catch(e){ onShowToast?.("Delete failed","error"); }
  };

  // ── Open run-options panel then execute ───────────────────────────────────
  const openRunOptions = (s) => {
    setRunOptScan(s); setRunOptOpen(true);
    // If scan has fundamental conditions, start prefetching into cache immediately
    // so by the time user clicks Run, most data is already cached
    const hasFund = (s.conditions||[]).some(c=>c.field?.startsWith("f_"));
    if(hasFund) {
      const wlSymbols = WATCHLISTS[runWl] || WATCHLISTS["NIFTY 50"];
      prefetchFundamentals(wlSymbols);
    }
  };

  const runScan = async (s, opts={}) => {
    const tf       = opts.tf  || runTf  || "15min";
    const exchange = opts.exchange || "NSE";
    // Resolve symbols: custom list > named watchlist > fallback
    let symbols;
    const customRaw = (opts.customSym ?? runCustomSym).trim();
    if(customRaw) {
      symbols = customRaw.split(/[\s,]+/).map(x=>x.trim().toUpperCase()).filter(Boolean);
    } else {
      const wl = opts.wl || runWl || "NIFTY 50";
      if(wl === "SMALL CAP (<5000Cr)") {
        if(!smallCapSymbols.length) await fetchSmallCaps();
        symbols = smallCapSymbols.length ? smallCapSymbols : WATCHLISTS["NIFTY 50"];
      } else if(wl === "ALL NSE EQUITY") {
        if(!allNseSymbols.length) await fetchAllNse();
        symbols = allNseSymbols.length ? allNseSymbols : WATCHLISTS["NIFTY 50"];
      } else {
        symbols = WATCHLISTS[wl] || WATCHLISTS["NIFTY 50"];
      }
    }
    setRunOptOpen(false);
    setRunning(true); setRunError(""); setRunProgress(""); setActiveScanId(s.id);
    setRunResults(null); setView("results");

    // Check if scan has fundamental conditions → use investment scan endpoint
    const hasFundamental = (s.conditions||[]).some(c => c.field?.startsWith("f_"));

    try {
      let all;
      if(hasFundamental) {
        // Split conditions into fundamental filters and TA filters
        const fundFilters = (s.conditions||[]).filter(c=>c.field?.startsWith("f_")).map(c=>({
          field: c.field.substring(2), op: c.op, value: c.value, value2: c.value2,
        }));
        // Use async job pattern to avoid Cloudflare's 100s timeout (error 524).
        // Step 1: kick off scan — returns job_id immediately (< 1s)
        const symsCapped = symbols.slice(0, 40);
        const startResp = await apiFetch("/scan/investment/start", {
          method:"POST", headers:{"Content-Type":"application/json"},
          body: JSON.stringify({
            symbols: symsCapped, exchange, timeframe:tf,
            fundamental_filters: fundFilters,
            signal_params:{...DEFAULT_SIGNAL_PARAMS,...(signalParams||{})},
          })
        });
        const jobId = startResp?.job_id;
        if(!jobId) throw new Error("Server did not return a job ID");

        // Step 2: poll until done (max 3 minutes, every 3s)
        const _pollStart = Date.now();
        let pollData = null;
        while(Date.now() - _pollStart < 180_000) {
          await new Promise(r => setTimeout(r, 3000));
          const pollResp = await apiFetch(`/scan/investment/result/${jobId}`);
          if(pollResp?.status === "done") { pollData = pollResp.result; break; }
          if(pollResp?.status === "error") throw new Error(pollResp.error || "Scan failed on server");
          // still running — show live progress
          if(pollResp?.progress) setRunProgress(pollResp.progress);
        }
        if(!pollData) throw new Error("Scan timed out after 3 minutes");
        all = pollData?.results || [];
        if(pollData?.partial && pollData?.warning) {
          onShowToast?.(pollData.warning, "warn");
        }
      } else {
        // Use async job pattern for TA scan too — avoids Cloudflare 100s timeout
        setRunProgress("Starting scan…");
        const startResp = await apiFetch("/scan/start", {
          method:"POST", headers:{"Content-Type":"application/json"},
          body: JSON.stringify({
            symbols, timeframe:tf, exchange,
            tech_weight:1.0, news_enabled:false,
            signal_params:{...DEFAULT_SIGNAL_PARAMS,...(signalParams||{})},
            min_score:0,
          })
        });
        const taJobId = startResp?.job_id;
        if(!taJobId) throw new Error("Server did not return a job ID");
        const _taPollStart = Date.now();
        let taData = null;
        while(Date.now() - _taPollStart < 120_000) {
          await new Promise(r => setTimeout(r, 2000));
          const pollResp = await apiFetch(`/scan/result/${taJobId}`);
          if(pollResp?.status === "done") { taData = pollResp.result; break; }
          if(pollResp?.status === "error") throw new Error(pollResp.error || "Scan failed on server");
        }
        if(!taData) throw new Error("Scan timed out — try a smaller watchlist");
        all = taData?.results || [];
      }
      // Apply TA conditions client-side (fundamental already filtered server-side)
      const taConditions = (s.conditions||[]).filter(c=>!c.field?.startsWith("f_"));
      const taScan = {...s, conditions: taConditions};
      const matched = hasFundamental
        ? (taConditions.length ? all.filter(r=>csMatch(r,taScan)) : all)
        : all.filter(r=>csMatch(r,s));
      setRunResults({
        scan:s, matched, total:all.length,
        ts:new Date().toLocaleTimeString("en-IN"),
        tf, symbols: customRaw ? `${symbols.length} symbols` : (opts.wl||runWl),
        hasFundamental,
        partial: hasFundamental && symbols.length > 40,
      });

      // ── Auto-Virtual: add matched stocks with enough signals to Virtual Tab ──
      if(autoVirtual && addVirtualRef?.current) {
        autoVirtualFromScan(matched, addVirtualRef.current, autoVirtualMin, tf==="1day"||tf==="1week"?"investment":"intraday", exchange, onShowToast);
      }
    } catch(e){ setRunError(e.message||"Scan failed"); }
    setRunning(false);
  };

  // ── Condition row helpers ─────────────────────────────────────────────────
  const addCondition    = ()=>setConditions(p=>[...p,{field:"rsi",op:"lt",value:"30",value2:""}]);
  const removeCondition = i=>setConditions(p=>p.filter((_,j)=>j!==i));
  const updateCondition = (i,key,val)=>setConditions(p=>p.map((c,j)=>j===i?{...c,[key]:val}:c));

  const fieldDef = (f) => CS_FIELDS.find(x=>x.key===f)||{};
  const isDerived= (f) => f?.startsWith("_ema")||f?.startsWith("_price")||f==="_macd_vs_signal"||f==="_tsi_vs_signal";
  const opsFor   = (f) => isDerived(f) ? CS_OPS_DERIVED : CS_OPS_NUM;

  const rowStyle = {background:"#0b1624",border:"1px solid #1a2c44",borderRadius:7,padding:"8px 10px",
    marginBottom:7,display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"};
  const sel = {background:"#07101c",border:"1px solid #1a2c44",borderRadius:0,
    padding:"5px 7px",color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none"};
  const inp = {background:"#07101c",border:"1px solid #1a2c44",borderRadius:0,
    padding:"5px 8px",color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none",width:72};

  // ── AI Scan Builder ─────────────────────────────────────────────────────────
  const AI_SCAN_SYSTEM = `You are a professional scan condition builder for Rab Trade, an Indian stock market trading terminal.
Your job: translate the user's plain-English description into structured scan conditions and respond conversationally.
You can create ANY scan the user describes — there are no limitations. If the user asks for TSI, demand zones, supply zones, momentum, volume, or any combination, you build it.

AVAILABLE FIELDS — provide "field" key exactly as listed:

Numeric fields (operators: gt, gte, lt, lte, between):
  rsi          — RSI 0-100. Oversold<30, Neutral 30-70, Overbought>70
  stoch_k      — Stochastic %K 0-100. Oversold<20, Overbought>80
  stoch_d      — Stochastic %D 0-100 (signal line of stoch_k)
  tsi          — True Strength Index, range roughly -1 to +1. >0=bullish momentum, <0=bearish. Strong signal >0.2 or <-0.2
  tsi_signal   — TSI signal line (smoothed TSI). Compare tsi vs tsi_signal for crossover detection
  macd         — MACD line value (positive = above zero = bullish bias)
  macd_signal  — MACD signal line value
  macd_hist    — MACD Histogram = macd minus macd_signal (positive = bullish momentum building)
  bb_upper     — Bollinger Upper Band in ₹ (overbought zone)
  bb_lower     — Bollinger Lower Band in ₹ (oversold zone)
  bb_mid       — Bollinger Middle Band / 20-SMA in ₹
  bb_width     — BB Width % = (upper-lower)/mid×100. Low<3=squeeze (breakout pending), High>8=high volatility
  ema9         — 9-period EMA price in ₹
  ema50        — 50-period EMA price in ₹
  price        — Current LTP/price in ₹
  demandLow    — Nearest Demand Zone lower bound in ₹ (support area)
  demandHigh   — Nearest Demand Zone upper bound in ₹ (support area)
  supplyLow    — Nearest Supply Zone lower bound in ₹ (resistance area)
  supplyHigh   — Nearest Supply Zone upper bound in ₹ (resistance area)
  volRatio     — Volume vs 5-bar average (1.0=normal, 1.5=high, 2.0=very high)
  buyVolPct    — Buy volume % 0-100 (>60 = strong buying pressure)
  sellVolPct   — Sell volume % 0-100 (>60 = strong selling pressure)
  _buy_count   — Number of buy signals currently fired (integer, 0-10+)
  _sell_count  — Number of sell signals currently fired (integer, 0-10+)
  _total_sigs  — Total number of signals fired (buy + sell)

Relational fields (operators: above OR below — no value needed):
  _ema9_vs_ema50   — above=EMA9>EMA50 (bullish short-term trend), below=EMA9<EMA50 (bearish)
  _price_vs_ema50  — above=price>EMA50 (bullish), below=price<EMA50 (bearish)
  _price_vs_ema9   — above=price>EMA9 (momentum up), below=price<EMA9 (momentum down)
  _macd_vs_signal  — above=MACD line above signal (bullish crossover), below=bearish crossover
  _tsi_vs_signal   — above=TSI above its signal line (bullish TSI cross), below=bearish TSI cross
  _price_vs_demand — above=price bounced above demand zone (bullish), below=price below demand (weak)
  _price_vs_supply — above=price broke above supply zone (bullish breakout), below=rejected at supply
  _price_in_demand — above=price is currently inside demand zone (potential bounce setup)
  _price_in_supply — above=price is currently inside supply zone (potential rejection setup)

Fundamental fields (operators: gt, gte, lt, lte, between — available in Investment mode):
  f_pe              — PE Ratio (Price/Earnings). <15=deep value, <25=fair, >40=expensive
  f_pb              — Price/Book ratio. <1=potentially undervalued, <3=fair, >5=expensive
  f_roe             — Return on Equity %. >15=good, >25=excellent
  f_roce            — Return on Capital Employed %. >15=good, >25=excellent
  f_debt_equity     — Debt to Equity ratio. <0.5=low debt, <1=moderate, >2=high debt
  f_sales_growth    — Revenue/Sales growth % (YoY). >10=growing, >25=fast growth
  f_profit_growth   — Net Profit growth % (YoY). >15=good, >30=strong
  f_market_cap      — Market Capitalization in ₹ Crores. <5000=small cap, <20000=mid cap, >20000=large cap
  f_promoter_holding — Promoter holding %. >50=high conviction, >70=very strong promoter control
  f_dividend_yield  — Dividend Yield %. >1=decent, >3=high yield
  f_eps             — Earnings Per Share in ₹
  f_book_value      — Book Value per share in ₹
  f_industry_pe     — Industry average PE for comparison (compare with f_pe to find under/overvalued vs peers)
  f_cash_flow       — Operating Cash Flow in ₹ Crores. >0=cash generating, >500=strong, <0=cash burn
  f_current_ratio   — Current Assets / Current Liabilities. >1.5=healthy, <1=liquidity risk

OPERATORS GUIDE:
  gt      → strictly greater than   (e.g. volRatio gt 1.5)
  gte     → greater than or equal   (e.g. _buy_count gte 3)
  lt      → strictly less than      (e.g. rsi lt 30)
  lte     → less than or equal      (e.g. stoch_k lte 20)
  between → between two values      (e.g. rsi between 40 and 60) — requires value AND value2
  above   → relational bullish only (e.g. _macd_vs_signal above)
  below   → relational bearish only (e.g. _ema9_vs_ema50 below)

STRATEGY EXAMPLES (use as reference when user describes similar setups):
- "TSI bullish cross with volume" → _tsi_vs_signal above + tsi gt 0 + volRatio gt 1.5
- "Price near demand zone" → _price_in_demand above (or _price_vs_demand above for bounced)
- "Supply breakout with momentum" → _price_vs_supply above + rsi gt 50 + volRatio gt 1.5
- "Oversold bounce near demand" → rsi lt 35 + _price_vs_demand above + _tsi_vs_signal above
- "BB squeeze with TSI bullish" → bb_width lt 3 + _tsi_vs_signal above + _macd_vs_signal above
- "Strong momentum buy" → _tsi_vs_signal above + _macd_vs_signal above + _ema9_vs_ema50 above + volRatio gt 1.3
- "Value stocks with good fundamentals" → f_pe lt 15 + f_roe gt 15 + f_debt_equity lt 1
- "High growth small caps" → f_market_cap lt 5000 + f_sales_growth gt 20 + f_profit_growth gt 20
- "Undervalued with momentum" → f_pe lt 20 + f_roe gt 15 + _tsi_vs_signal above + rsi gt 50
- "Dividend stocks near demand" → f_dividend_yield gt 2 + f_promoter_holding gt 50 + _price_vs_demand above
- "Quality at reasonable price" → f_roe gt 18 + f_roce gt 15 + f_debt_equity lt 0.5 + f_pe lt 25
- "Cash rich Nifty 50 type stocks" → f_cash_flow gt 500 + f_current_ratio gt 1.5 + f_debt_equity lt 0.5
- "Good cash flow last year" → f_cash_flow gt 100 + f_profit_growth gt 10 + f_roe gt 12

RESPONSE RULES:
1. First, briefly acknowledge what the user wants in 1-2 sentences.
2. Explain the conditions you chose and why (2-3 sentences).
3. Then output the JSON block inside <SCAN_CONDITIONS> tags.
4. If the user refines or asks to tweak, output a new <SCAN_CONDITIONS> block with the updated conditions.

JSON FORMAT (output exactly inside <SCAN_CONDITIONS> tags):
<SCAN_CONDITIONS>
{"name":"Short descriptive name","description":"One-line description of what it finds","logic":"AND","conditions":[{"field":"rsi","op":"lt","value":35},{"field":"_tsi_vs_signal","op":"above"}]}
</SCAN_CONDITIONS>

IMPORTANT:
- Use AND logic for precise setups (all conditions required)
- Use OR logic for broad watchlists (any condition triggers)
- 2–5 conditions is ideal; never exceed 6
- For "between": include both "value" and "value2" fields in the JSON
- For relational fields (_xxx): NEVER include "value" in the JSON
- Keep it practical for live Indian market scanning
- If user describes a famous strategy (e.g. "oversold bounce", "golden cross", "BB squeeze breakout", "demand zone bounce", "supply breakout"), implement it correctly using the full field set
- You have access to ALL indicators including TSI, demand/supply zones, Bollinger Bands, MACD, Stochastic — use whatever the user's logic requires
- You ALSO have access to fundamental fields (f_pe, f_pb, f_roe, f_roce, f_debt_equity, f_sales_growth, f_profit_growth, f_market_cap, f_promoter_holding, f_dividend_yield, f_eps, f_book_value, f_industry_pe, f_cash_flow, f_current_ratio) — use these for investment/value scans
- You CAN combine fundamental + technical conditions in a single scan (e.g. "low PE + RSI oversold" or "high ROE with TSI bullish cross")
- When user asks about fundamentals like cash ratio, debt, PE, growth, profitability — map to the closest available f_ field and build the scan`;

  const sendAIBuild = async () => {
    const text = aiInput.trim();
    if(!text || aiLoading) return;
    const newMsgs = [...aiMsgs, {role:"user", content:text}];
    setAiMsgs(newMsgs);
    setAiInput("");
    setAiLoading(true);
    try {
      const resp = await apiFetch("/ai/chat", {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          // Strip the initial UI-only greeting (role:"assistant") — it must not be sent
          // to Gemini because two consecutive model turns break the conversation structure
          // and cause the model to ignore our system prompt.
          messages: newMsgs
            .filter(m => !(m._uiOnly))                 // drop UI-only messages
            .filter((m,i,arr) => {                      // also drop leading assistant turns
              if(i===0 && m.role==="assistant") return false;
              return true;
            })
            .map(m=>({role:m.role, content:m.content})),
          system: AI_SCAN_SYSTEM,
          max_tokens: 1500,
        })
      });
      const reply = (resp?.content?.[0]?.text || resp?.text || "Sorry, I couldn't generate conditions.").trim();
      setAiMsgs(p=>[...p, {role:"assistant", content:reply}]);
      // Parse embedded <SCAN_CONDITIONS> JSON block
      const match = reply.match(/<SCAN_CONDITIONS>([\s\S]*?)<\/SCAN_CONDITIONS>/);
      if(match) {
        try {
          const parsed = JSON.parse(match[1].trim());
          if(parsed.conditions?.length) setAiPending(parsed);
        } catch(e) { /* malformed JSON from AI, ignore */ }
      }
    } catch(e) {
      setAiMsgs(p=>[...p, {role:"assistant", content:`⚠ Error connecting to AI: ${e.message}`}]);
    }
    setAiLoading(false);
  };

  const applyAIConditions = () => {
    if(!aiPending) return;
    if(aiPending.name)        setScanName(n => n || aiPending.name);
    if(aiPending.description) setScanDesc(d => d || aiPending.description);
    if(aiPending.logic)       setLogic(aiPending.logic);
    if(aiPending.conditions?.length) {
      setConditions(aiPending.conditions.map(c=>({
        field:  c.field  || "rsi",
        op:     c.op     || "lt",
        value:  c.value  != null ? String(c.value)  : "",
        value2: c.value2 != null ? String(c.value2) : "",
      })));
    }
    setAiPending(null);
    setBuilderMode("manual"); // switch so user can review/tweak
  };

  const [showAIPanel, setShowAIPanel] = useState(false);

  return (
    <div style={{display:"flex",height:"100%",fontFamily:"inherit",background:"#07101c",position:"relative"}}>

    {/* ── Left: Scan Builder ── */}
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",minWidth:0}}>

      {/* Header */}
      <div style={{background:"#0b1624",borderBottom:"1px solid #1a2c44",padding:"10px 14px",
        display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
        <div>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:13,fontWeight:700,color:"#e7edf5"}}>🔬 Custom Scans</span>
            {/* Trading / Investment mode toggle */}
            <div style={{display:"flex",gap:0,borderRadius:0,overflow:"hidden",border:"1px solid #1a2c44"}}>
              {[["trading","⚡ Trading"],["investment","📈 Investment"]].map(([m,lbl])=>(
                <button key={m} onClick={()=>setCsScanMode(m)}
                  style={{background:csScanMode===m?"rgba(232,179,57,0.2)":"#07101c",
                    border:"none",padding:"3px 10px",
                    color:csScanMode===m?"#e8b339":"#5a728f",
                    fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          <div style={{fontSize:10,color:"#5a728f",marginTop:1}}>
            {csScanMode==="investment"
              ? "Combine fundamental (PE, ROE, debt) + technical filters for investment screening"
              : "Build condition-based scans, save them, run on any watchlist"}
          </div>
        </div>
        <div style={{display:"flex",gap:6,alignItems:"center"}}>
          <button onClick={()=>setShowAIPanel(p=>!p)}
            style={{background:showAIPanel?"rgba(73,194,228,0.15)":"transparent",
              border:`1px solid ${showAIPanel?"#49c2e4":"#1a2c44"}`,borderRadius:0,
              padding:"5px 10px",color:showAIPanel?"#49c2e4":"#8ea2bf",
              fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer"}}>
            🧠 AI {showAIPanel?"◂":"▸"}
          </button>
          {view!=="list"&&(
            <button onClick={()=>setView("list")}
              style={{background:"transparent",border:"1px solid #1a2c44",borderRadius:0,
                padding:"5px 10px",color:"#8ea2bf",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>
              ← Back
            </button>
          )}
          {view==="list"&&canEdit&&(
            <button onClick={startNew}
              style={{background:"linear-gradient(135deg,#0f1d30,#e8b339)",border:"none",
                borderRadius:7,padding:"6px 14px",color:"#fff",fontFamily:"inherit",
                fontSize:11,fontWeight:700,cursor:"pointer"}}>
              + New Scan
            </button>
          )}
        </div>
      </div>

      {/* ── LIST VIEW ─────────────────────────────────────────────────────── */}
      {view==="list"&&(
        <div style={{flex:1,overflowY:"auto",padding:14}}>
          {loadingScans&&(
            <div style={{textAlign:"center",padding:40,color:"#5a728f",fontSize:12}}>⟳ Loading...</div>
          )}
          {!loadingScans&&savedScans.length===0&&(
            <div style={{textAlign:"center",padding:"50px 20px"}}>
              <div style={{fontSize:32,marginBottom:12}}>🔬</div>
              <div style={{fontSize:13,color:"#e7edf5",marginBottom:6,fontWeight:700}}>No custom scans yet</div>
              <div style={{fontSize:11,color:"#5a728f",marginBottom:18,lineHeight:1.7}}>
                Build scans using your own conditions — RSI, MACD, Stochastic,<br/>
                Bollinger Bands, volume, EMA cross, and more.
              </div>
              {canEdit&&(
                <button onClick={startNew}
                  style={{background:"linear-gradient(135deg,#0f1d30,#e8b339)",border:"none",
                    borderRadius:0,padding:"10px 24px",color:"#fff",fontFamily:"inherit",
                    fontSize:12,fontWeight:700,cursor:"pointer"}}>
                  + Create First Scan
                </button>
              )}
            </div>
          )}
          {savedScans.map(s=>(
            <div key={s.id} style={{background:"#0f1d30",border:"1px solid #1a2c44",
              borderRadius:9,padding:"12px 14px",marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:"#e7edf5"}}>{s.name}</div>
                  {s.description&&<div style={{fontSize:10,color:"#5a728f",marginTop:1}}>{s.description}</div>}
                  <div style={{display:"flex",gap:6,marginTop:4,flexWrap:"wrap"}}>
                    <span style={{fontSize:10,color:"#5a728f",background:"#0b1624",padding:"1px 6px",borderRadius:0}}>
                      {(s.conditions||[]).length} condition{(s.conditions||[]).length!==1?"s":""} · {s.logic||"AND"}
                    </span>
                  </div>
                </div>
                <div style={{display:"flex",gap:5,flexShrink:0}}>
                  <button onClick={()=>openRunOptions(s)} disabled={running}
                    style={{background:"linear-gradient(135deg,#059669,#10b981)",border:"none",
                      borderRadius:0,padding:"5px 12px",color:"#fff",fontFamily:"inherit",
                      fontSize:11,fontWeight:700,cursor:running?"default":"pointer",opacity:running?0.6:1}}>
                    {running&&activeScanId===s.id?"⟳":"▶ Run"}
                  </button>
                  {canEdit&&(
                    <>
                      <button onClick={()=>startEdit(s)}
                        style={{background:"transparent",border:"1px solid #1a2c44",borderRadius:0,
                          padding:"5px 9px",color:"#8ea2bf",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>
                        ✏
                      </button>
                      <button onClick={()=>deleteScan(s.id,s.name)}
                        style={{background:"transparent",border:"1px solid #1a2c44",borderRadius:0,
                          padding:"5px 9px",color:"#ff5b6c",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>
                        🗑
                      </button>
                    </>
                  )}
                </div>
              </div>
              {/* Condition pills */}
              <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:4}}>
                {(s.conditions||[]).slice(0,6).map((c,i)=>{
                  const fd = fieldDef(c.field);
                  const isD = isDerived(c.field);
                  return(
                    <span key={i} style={{fontSize:10,padding:"1px 7px",borderRadius:0,
                      background:"rgba(232,179,57,0.08)",border:"1px solid #1a2c44",color:"#8ea2bf"}}>
                      {fd.label||c.field} {isD?(c.op==="above"?"↑":"↓"):`${c.op} ${c.value}${c.value2?`–${c.value2}`:""}`}
                    </span>
                  );
                })}
                {(s.conditions||[]).length>6&&(
                  <span style={{fontSize:10,color:"#5a728f"}}>+{(s.conditions||[]).length-6} more</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── BUILDER VIEW ──────────────────────────────────────────────────── */}
      {view==="builder"&&(
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>

          {/* Scan meta: name / desc / timeframe / watchlist — always visible */}
          <div style={{background:"#0b1624",borderBottom:"1px solid #1a2c44",padding:"10px 14px",flexShrink:0}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
              <div>
                <div style={{fontSize:10,color:"#8ea2bf",marginBottom:3}}>Scan Name *</div>
                <input value={scanName} onChange={e=>setScanName(e.target.value)}
                  placeholder="e.g. Oversold Bounce"
                  style={{...sel,width:"100%",padding:"7px 10px",fontSize:12}}/>
              </div>
              <div>
                <div style={{fontSize:10,color:"#8ea2bf",marginBottom:3}}>Description (optional)</div>
                <input value={scanDesc} onChange={e=>setScanDesc(e.target.value)}
                  placeholder="What does this scan look for?"
                  style={{...sel,width:"100%",padding:"7px 10px",fontSize:12}}/>
              </div>
            </div>
            <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
              <div>
                <div style={{fontSize:10,color:"#8ea2bf",marginBottom:3}}>Match Logic</div>
                <div style={{display:"flex",gap:4}}>
                  {["AND","OR"].map(l=>(
                    <button key={l} onClick={()=>setLogic(l)}
                      style={{background:logic===l?"rgba(232,179,57,0.2)":"transparent",
                        border:`1px solid ${logic===l?"#e8b339":"#1a2c44"}`,
                        borderRadius:0,padding:"5px 14px",
                        color:logic===l?"#49c2e4":"#5a728f",
                        fontFamily:"inherit",fontSize:11,fontWeight:logic===l?700:400,cursor:"pointer"}}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{fontSize:10,color:"#23405f",marginTop:14}}>
                {logic==="AND"?"All conditions must match":"Any condition triggers"}
              </div>
              {/* Save + Cancel */}
              <div style={{marginLeft:"auto",display:"flex",gap:6,alignItems:"center"}}>
                {savingMsg?.includes("✓")&&(
                  <span style={{fontSize:11,color:"#2ed47a"}}>{savingMsg}</span>
                )}
                <button onClick={()=>setView("list")}
                  style={{background:"transparent",border:"1px solid #1a2c44",borderRadius:7,
                    padding:"7px 14px",color:"#8ea2bf",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>
                  Cancel
                </button>
                <button onClick={saveScan} disabled={!!savingMsg}
                  style={{background:"linear-gradient(135deg,#0f1d30,#e8b339)",border:"none",
                    borderRadius:7,padding:"7px 18px",color:"#fff",fontFamily:"inherit",
                    fontSize:11,fontWeight:700,cursor:savingMsg?"default":"pointer",opacity:savingMsg?0.7:1}}>
                  {savingMsg&&!savingMsg.includes("✓")?"⟳ Saving...":"💾 Save Scan"}
                </button>
              </div>
            </div>
          </div>

          {/* Mode tab bar */}
          <div style={{display:"flex",background:"#07101c",borderBottom:"1px solid #1a2c44",flexShrink:0}}>
            {[["ai","🤖 AI Build"],["manual","⚙ Manual"]].map(([m,label])=>(
              <button key={m} onClick={()=>setBuilderMode(m)}
                style={{padding:"8px 18px",background:"transparent",border:"none",
                  borderBottom:`2px solid ${builderMode===m?"#e8b339":"transparent"}`,
                  color:builderMode===m?"#49c2e4":"#5a728f",fontFamily:"inherit",
                  fontSize:11,fontWeight:builderMode===m?700:400,cursor:"pointer"}}>
                {label}
              </button>
            ))}
            {conditions.length>0&&(
              <span style={{marginLeft:"auto",display:"flex",alignItems:"center",
                paddingRight:12,fontSize:10,color:"#5a728f"}}>
                {conditions.length} condition{conditions.length!==1?"s":""} · {logic}
              </span>
            )}
          </div>

          {/* ── AI BUILD MODE ─────────────────────────────────────────────── */}
          {builderMode==="ai"&&(
            <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>

              {/* Chat messages */}
              <div style={{flex:1,overflowY:"auto",padding:"12px 14px",display:"flex",flexDirection:"column",gap:10}}>
                {aiMsgs.map((m,i)=>{
                  const isUser = m.role==="user";
                  // Strip <SCAN_CONDITIONS> block from display
                  const displayText = m.content.replace(/<SCAN_CONDITIONS>[\s\S]*?<\/SCAN_CONDITIONS>/g,"").trim();
                  const hasConditions = /<SCAN_CONDITIONS>/.test(m.content);
                  return (
                    <div key={i} style={{display:"flex",flexDirection:"column",
                      alignItems:isUser?"flex-end":"flex-start",gap:4}}>
                      <div style={{
                        maxWidth:"85%",padding:"10px 13px",borderRadius:isUser?"10px 10px 3px 10px":"10px 10px 10px 3px",
                        background:isUser?"linear-gradient(135deg,#0f1d30,#e8b339)":"#0f1d30",
                        border:isUser?"none":"1px solid #1a2c44",
                        fontSize:12,color:"#e7edf5",lineHeight:1.6,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>
                        {displayText}
                      </div>
                      {/* Apply button appears under AI messages that generated conditions */}
                      {!isUser&&hasConditions&&aiPending&&i===aiMsgs.length-1&&(
                        <button onClick={applyAIConditions}
                          style={{background:"linear-gradient(135deg,#065f46,#10b981)",border:"none",
                            borderRadius:7,padding:"7px 16px",color:"#fff",fontFamily:"inherit",
                            fontSize:11,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
                          ✓ Apply {aiPending.conditions?.length} Condition{aiPending.conditions?.length!==1?"s":""}
                          {aiPending.name&&<span style={{opacity:0.8,fontWeight:400}}>→ "{aiPending.name}"</span>}
                        </button>
                      )}
                    </div>
                  );
                })}
                {aiLoading&&(
                  <div style={{alignSelf:"flex-start",padding:"10px 13px",borderRadius:"10px 10px 10px 3px",
                    background:"#0f1d30",border:"1px solid #1a2c44",fontSize:12,color:"#5a728f"}}>
                    ⟳ Generating conditions...
                  </div>
                )}
                <div ref={aiEndRef}/>
              </div>

              {/* Chat input */}
              <div style={{borderTop:"1px solid #1a2c44",padding:"10px 12px",flexShrink:0,
                display:"flex",gap:8,alignItems:"flex-end",background:"#0b1624"}}>
                <textarea value={aiInput} onChange={e=>setAiInput(e.target.value)}
                  onKeyDown={e=>{ if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendAIBuild();} }}
                  placeholder="Describe your scan... e.g. 'RSI oversold below 30, MACD turning bullish, above EMA 50'"
                  rows={2}
                  style={{flex:1,background:"#07101c",border:"1px solid #1a2c44",borderRadius:0,
                    padding:"9px 12px",color:"#e7edf5",fontFamily:"inherit",fontSize:12,
                    outline:"none",resize:"none",lineHeight:1.5}}/>
                <button onClick={sendAIBuild} disabled={!aiInput.trim()||aiLoading}
                  style={{background:"linear-gradient(135deg,#0f1d30,#e8b339)",border:"none",
                    borderRadius:0,padding:"10px 16px",color:"#fff",fontFamily:"inherit",
                    fontSize:12,fontWeight:700,cursor:(!aiInput.trim()||aiLoading)?"default":"pointer",
                    opacity:(!aiInput.trim()||aiLoading)?0.5:1,flexShrink:0,height:52}}>
                  {aiLoading?"⟳":"Send"}
                </button>
              </div>

              {/* Helper chips */}
              <div style={{borderTop:"1px solid #1a2c44",padding:"7px 12px",flexShrink:0,
                background:"#0a0f1a",display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
                <span style={{fontSize:10,color:"#23405f",flexShrink:0}}>Try:</span>
                {["RSI oversold + MACD bullish cross","EMA golden cross with high volume",
                  "BB squeeze breakout setup","Stochastic oversold with buying pressure",
                  "3+ buy signals with price above EMA50"].map(ex=>(
                  <button key={ex} onClick={()=>setAiInput(ex)}
                    style={{background:"rgba(232,179,57,0.06)",border:"1px solid #1a2c44",
                      borderRadius:12,padding:"3px 9px",color:"#5a728f",fontFamily:"inherit",
                      fontSize:10,cursor:"pointer"}}>
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── MANUAL BUILD MODE ─────────────────────────────────────────── */}
          {builderMode==="manual"&&(
            <div style={{flex:1,overflowY:"auto",padding:14}}>

              {/* Conditions list */}
              <div style={{fontSize:10,fontWeight:700,color:"#8ea2bf",letterSpacing:1,marginBottom:8,
                display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span>CONDITIONS</span>
                <span style={{fontSize:9,color:"#23405f",fontWeight:400}}>
                  {logic==="AND"?"All must match":"Any one triggers"}
                </span>
              </div>

              {conditions.map((cond,i)=>{
                const isD = isDerived(cond.field);
                const ops = opsFor(cond.field);
                return(
                  <div key={i} style={rowStyle}>
                    <div style={{fontSize:10,color:"#5a728f",width:22,textAlign:"right",flexShrink:0,fontWeight:700}}>
                      {i===0?"IF":(logic==="AND"?"AND":"OR")}
                    </div>
                    {/* Field selector */}
                    <select value={cond.field}
                      onChange={e=>{
                        const newOp = isDerived(e.target.value)?"above":
                          ["rsi","stoch_k","stoch_d"].includes(e.target.value)?"lt":"gt";
                        updateCondition(i,"field",e.target.value);
                        updateCondition(i,"op",newOp);
                      }}
                      style={{...sel,flex:1,minWidth:120}}>
                      {csScanMode==="investment"&&<optgroup label="── Fundamental ──">
                        {CS_FIELDS.filter(f=>f.group==="fundamental").map(f=>(
                          <option key={f.key} value={f.key}>{f.label}</option>
                        ))}
                      </optgroup>}
                      <optgroup label="── Technical ──">
                        {CS_FIELDS.filter(f=>!f.group).map(f=>(
                          <option key={f.key} value={f.key}>{f.label}</option>
                        ))}
                      </optgroup>
                    </select>
                    {/* Operator */}
                    <select value={cond.op} onChange={e=>updateCondition(i,"op",e.target.value)}
                      style={{...sel,minWidth:110}}>
                      {ops.map(o=>(<option key={o.v} value={o.v}>{o.l}</option>))}
                    </select>
                    {/* Value inputs — hidden for relational derived fields */}
                    {!isD&&(
                      <>
                        <input value={cond.value} onChange={e=>updateCondition(i,"value",e.target.value)}
                          placeholder="value" style={inp} type="number"/>
                        {cond.op==="between"&&(
                          <>
                            <span style={{fontSize:10,color:"#5a728f"}}>–</span>
                            <input value={cond.value2} onChange={e=>updateCondition(i,"value2",e.target.value)}
                              placeholder="max" style={inp} type="number"/>
                          </>
                        )}
                        <span style={{fontSize:10,color:"#5a728f",minWidth:12}}>
                          {fieldDef(cond.field).unit}
                        </span>
                      </>
                    )}
                    {isD&&(
                      <span style={{fontSize:10,color:cond.op==="above"?"#2ed47a":"#ff5b6c",flex:1}}>
                        {cond.op==="above"?"↑ bullish crossover":"↓ bearish crossover"}
                      </span>
                    )}
                    <button onClick={()=>removeCondition(i)}
                      style={{background:"none",border:"none",color:"#ff5b6c",fontSize:14,cursor:"pointer",
                        padding:"0 4px",flexShrink:0,lineHeight:1}}>✕</button>
                  </div>
                );
              })}

              <button onClick={addCondition}
                style={{background:"rgba(232,179,57,0.06)",border:"1px dashed #1a2c44",
                  borderRadius:0,padding:"7px 14px",color:"#5a728f",fontFamily:"inherit",
                  fontSize:11,cursor:"pointer",width:"100%",marginBottom:14}}>
                + Add Condition
              </button>

              {/* Condition summary preview */}
              {conditions.length>0&&(
                <div style={{background:"rgba(232,179,57,0.04)",border:"1px solid #1a2c44",
                  borderRadius:0,padding:"10px 14px",marginBottom:6}}>
                  <div style={{fontSize:10,color:"#5a728f",fontWeight:700,letterSpacing:1,marginBottom:6}}>
                    SCAN SUMMARY
                  </div>
                  <div style={{fontSize:11,color:"#8ea2bf",lineHeight:2}}>
                    Find stocks where <strong style={{color:"#49c2e4"}}>{logic==="AND"?"ALL":"ANY"}</strong> of:
                    {conditions.map((c,i)=>{
                      const fd = fieldDef(c.field);
                      const isD = isDerived(c.field);
                      return (
                        <div key={i} style={{marginLeft:10,color:"#e7edf5"}}>
                          • {fd.label||c.field}{" "}
                          {isD?(c.op==="above"?"↑ is Bullish":"↓ is Bearish")
                           :c.op==="between"?`is between ${c.value} – ${c.value2}`
                           :`${CS_OPS_NUM.find(o=>o.v===c.op)?.l?.split(" ")[0]||c.op} ${c.value}${fd.unit}`}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quick tip to switch to AI */}
              <div style={{textAlign:"center",padding:"8px 0"}}>
                <button onClick={()=>setBuilderMode("ai")}
                  style={{background:"transparent",border:"none",color:"#23405f",fontFamily:"inherit",
                    fontSize:10,cursor:"pointer",textDecoration:"underline"}}>
                  🤖 Let AI build conditions for you
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── RUN OPTIONS OVERLAY ───────────────────────────────────────────── */}
      {runOptOpen&&runOptScan&&(
        <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.7)",zIndex:50,
          display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#0f1d30",border:"1px solid #e8b339",borderRadius:12,
            padding:24,width:380,maxWidth:"90vw",boxShadow:"0 20px 60px rgba(0,0,0,0.5)"}}>
            <div style={{fontSize:13,fontWeight:700,color:"#e7edf5",marginBottom:4}}>
              ▶ Run: <span style={{color:"#49c2e4"}}>{runOptScan.name}</span>
            </div>
            <div style={{fontSize:10,color:"#5a728f",marginBottom:4}}>
              Choose what to scan — you can change this each time you run
            </div>
            {(runOptScan.conditions||[]).some(c=>c.field?.startsWith("f_"))&&(
              <div style={{fontSize:10,color:"#e8b339",background:"rgba(232,179,57,0.07)",
                border:"1px solid rgba(232,179,57,0.2)",borderRadius:6,padding:"5px 10px",
                marginBottom:14,display:"flex",alignItems:"center",gap:6}}>
                <span>⚡</span>
                <span>Fundamental scan — data fetches from Yahoo Finance in background. Tip: try &lt;20 symbols for fastest results.</span>
              </div>
            )}

            {/* Timeframe */}
            <div style={{marginBottom:14}}>
              <div style={{fontSize:10,color:"#8ea2bf",marginBottom:4,fontWeight:700,letterSpacing:1}}>TIMEFRAME</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {["1min","5min","10min","15min","30min","1hour","1day"].map(t=>(
                  <button key={t} onClick={()=>setRunTf(t)}
                    style={{background:runTf===t?"rgba(232,179,57,0.2)":"transparent",
                      border:`1px solid ${runTf===t?"#e8b339":"#1a2c44"}`,
                      borderRadius:0,padding:"4px 10px",
                      color:runTf===t?"#49c2e4":"#5a728f",
                      fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Watchlist */}
            <div style={{marginBottom:10}}>
              <div style={{fontSize:10,color:"#8ea2bf",marginBottom:4,fontWeight:700,letterSpacing:1}}>WATCHLIST</div>
              <select value={runWl} onChange={e=>{
                const v=e.target.value;
                setRunWl(v); setRunCustomSym("");
                if(v==="SMALL CAP (<5000Cr)") fetchSmallCaps();
                if(v==="ALL NSE EQUITY") fetchAllNse();
              }}
                style={{background:"#07101c",border:"1px solid #1a2c44",borderRadius:0,
                  padding:"7px 10px",color:"#e7edf5",fontFamily:"inherit",fontSize:11,
                  outline:"none",width:"100%"}}>
                {Object.keys(WATCHLISTS).filter(k=>k!=="MCX").map(k=>(
                  <option key={k} value={k}>{k} ({WATCHLISTS[k]?.length} symbols)</option>
                ))}
                <option value="SMALL CAP (<5000Cr)">SMALL CAP &lt;₹5000Cr ({smallCapSymbols.length||"loading..."} stocks)</option>
                <option value="ALL NSE EQUITY">ALL NSE EQUITY ({allNseSymbols.length||"loading..."} stocks)</option>
              </select>
            </div>

            {/* Custom symbols override */}
            <div style={{marginBottom:18}}>
              <div style={{fontSize:10,color:"#8ea2bf",marginBottom:4}}>
                Or enter custom symbols <span style={{color:"#23405f"}}>(comma/space separated — overrides watchlist)</span>
              </div>
              <input value={runCustomSym} onChange={e=>setRunCustomSym(e.target.value)}
                placeholder="e.g. RELIANCE, TCS, INFY, HDFC"
                style={{background:"#07101c",border:"1px solid #1a2c44",borderRadius:0,
                  padding:"7px 10px",color:"#e7edf5",fontFamily:"inherit",fontSize:11,
                  outline:"none",width:"100%"}}/>
              {runCustomSym.trim()&&(
                <div style={{fontSize:10,color:"#49c2e4",marginTop:3}}>
                  ✓ Scanning {runCustomSym.split(/[\s,]+/).filter(Boolean).length} custom symbols
                </div>
              )}
            </div>

            {/* Auto-Virtual toggle */}
            <div style={{marginBottom:14,padding:"8px 10px",background:"rgba(96,165,250,0.06)",
              border:"1px solid #1a2c44",borderRadius:7}}>
              <AutoVirtualToggle enabled={autoVirtual} setEnabled={setAutoVirtual}
                minSigs={autoVirtualMin} setMinSigs={setAutoVirtualMin}/>
            </div>

            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button onClick={()=>setRunOptOpen(false)}
                style={{background:"transparent",border:"1px solid #1a2c44",borderRadius:7,
                  padding:"8px 16px",color:"#8ea2bf",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>
                Cancel
              </button>
              <button onClick={()=>runScan(runOptScan)}
                style={{background:"linear-gradient(135deg,#059669,#10b981)",border:"none",
                  borderRadius:7,padding:"8px 20px",color:"#fff",fontFamily:"inherit",
                  fontSize:12,fontWeight:700,cursor:"pointer"}}>
                ▶ Run Scan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── RESULTS VIEW ──────────────────────────────────────────────────── */}
      {view==="results"&&(
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
          {running&&(
            <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",
              flexDirection:"column",gap:12,color:"#5a728f"}}>
              <div style={{fontSize:24,animation:"spin 1s linear infinite"}}>⟳</div>
              <div style={{fontSize:12}}>{runProgress||"Starting scan…"}</div>
              {runProgress&&runProgress.includes("Fetching")&&(
                <div style={{fontSize:10,color:"#3a5270",maxWidth:260,textAlign:"center"}}>
                  Fetching via Yahoo Finance — usually done in 5–10s
                </div>
              )}
            </div>
          )}
          {!running&&runError&&(
            <div style={{padding:20}}>
              <div style={{background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.25)",
                borderRadius:0,padding:14,color:"#ff5b6c",fontSize:12}}>
                ⚠ {runError}
              </div>
            </div>
          )}
          {!running&&runResults&&!runError&&(
            <>
              {/* Partial scan warning */}
              {runResults.partial&&(
                <div style={{background:"rgba(245,158,11,0.1)",borderBottom:"1px solid rgba(245,158,11,0.25)",
                  padding:"6px 14px",fontSize:10,color:"#f59e0b",display:"flex",alignItems:"center",gap:6}}>
                  ⚠ Fundamental scan capped at 40 symbols to avoid timeout.
                  Use a custom symbol list with &lt;20 stocks for best results.
                </div>
              )}
              {/* Results header */}
              <div style={{padding:"10px 14px",borderBottom:"1px solid #1a2c44",
                display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
                <div>
                  <span style={{fontSize:12,fontWeight:700,color:"#e7edf5"}}>{runResults.scan.name}</span>
                  <span style={{fontSize:11,color:"#2ed47a",marginLeft:8,fontWeight:700}}>
                    {runResults.matched.length} match{runResults.matched.length!==1?"es":""}
                  </span>
                  <span style={{fontSize:10,color:"#5a728f",marginLeft:6}}>of {runResults.total} scanned</span>
                  <span style={{fontSize:10,color:"#23405f",marginLeft:6}}>
                    · {runResults.tf} · {runResults.symbols} · {runResults.ts}
                  </span>
                </div>
                <div style={{display:"flex",gap:6}}>
                  {sendTgAlert&&runResults.matched.length>0&&(
                    <button onClick={()=>{
                      const time=new Date().toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"});
                      const lines=runResults.matched.slice(0,20).map(r=>{
                        const buys=(r.signals||[]).filter(s=>s.type==="BUY");
                        const sells=(r.signals||[]).filter(s=>s.type==="SELL");
                        const dir=buys.length?"🟢 BUY":sells.length?"🔴 SELL":"⚪";
                        const ltp=parseFloat(r.price||r.ltp||0);
                        return `${dir} <b>${r.symbol}</b> @ ₹${ltp||"–"}`;
                      });
                      const msg=`<b>🔬 Custom Scan: ${runResults.scan.name}</b>\n⏱ ${time} · ${runResults.tf} · ${runResults.symbols}\n${runResults.matched.length} match${runResults.matched.length!==1?"es":""}\n\n${lines.join("\n")}`;
                      sendTgAlert(msg);
                      onShowToast?.("📤 Sent to Telegram");
                    }}
                    style={{background:"rgba(0,136,204,0.15)",border:"1px solid rgba(0,136,204,0.4)",
                      borderRadius:0,padding:"4px 10px",color:"#29b6d5",fontFamily:"inherit",fontSize:11,cursor:"pointer",fontWeight:700}}>
                    📤 Telegram
                  </button>
                  )}
                  <button onClick={()=>openRunOptions(runResults.scan)} disabled={running}
                    style={{background:"transparent",border:"1px solid #1a2c44",borderRadius:0,
                      padding:"4px 10px",color:"#8ea2bf",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>
                    ↻ Re-run
                  </button>
                </div>
              </div>

              {runResults.matched.length===0&&(
                <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",
                  flexDirection:"column",gap:10,color:"#5a728f"}}>
                  <div style={{fontSize:24}}>🔍</div>
                  <div style={{fontSize:12}}>No stocks matched all conditions</div>
                  <div style={{fontSize:10,color:"#23405f"}}>Try relaxing conditions or switching to OR logic</div>
                </div>
              )}

              {/* Result cards */}
              <div style={{flex:1,overflowY:"auto",padding:10}}>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(175px,1fr))",gap:8}}>
                  {runResults.matched.map((r,i)=>{
                    const buys  = (r.signals||[]).filter(s=>s.type==="BUY");
                    const sells = (r.signals||[]).filter(s=>s.type==="SELL");
                    const mixed = buys.length&&sells.length;
                    const color = mixed?"#f59e0b":buys.length?"#2ed47a":sells.length?"#ff5b6c":"#5a728f";
                    const ltp = parseFloat(r.price||r.ltp||0);
                    // Check which conditions matched for this result
                    const condMatches = runResults.scan.conditions.map(c=>csEval(r,c));
                    return(
                      <div key={r.symbol||i} style={{background:"#0f1d30",
                        border:`1px solid ${color}44`,borderLeft:`3px solid ${color}`,
                        borderRadius:0,padding:"10px 11px"}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                          <div style={{display:"flex",flexDirection:"column",gap:2}}>
                            <span style={{fontSize:12,fontWeight:700,color:"#e7edf5"}}>{r.symbol}</span>
                            {r.earnings?.alert&&(
                              <span style={{fontSize:9,padding:"1px 5px",borderRadius:3,
                                background: r.earnings.days_to!=null&&Math.abs(r.earnings.days_to)<=1
                                  ?"rgba(239,68,68,0.15)":"rgba(245,158,11,0.12)",
                                border: `1px solid ${r.earnings.days_to!=null&&Math.abs(r.earnings.days_to)<=1
                                  ?"rgba(239,68,68,0.4)":"rgba(245,158,11,0.3)"}`,
                                color: r.earnings.days_to!=null&&Math.abs(r.earnings.days_to)<=1
                                  ?"#ff5b6c":"#f59e0b",
                                width:"fit-content"}}>
                                📅 {r.earnings.alert}
                              </span>
                            )}
                          </div>
                          <span style={{fontSize:12,fontWeight:700,color:"#e7edf5"}}>
                            {ltp>0?`₹${ltp.toLocaleString("en-IN",{maximumFractionDigits:ltp<100?2:0})}`:"—"}
                          </span>
                        </div>
                        {/* Condition match badges */}
                        <div style={{display:"flex",gap:3,flexWrap:"wrap",marginBottom:6}}>
                          {runResults.scan.conditions.map((c,ci)=>{
                            const fd = fieldDef(c.field);
                            const v  = csGetValue(r,c.field);
                            const matched = condMatches[ci];
                            return(
                              <span key={ci} style={{fontSize:9,padding:"1px 5px",borderRadius:0,
                                background:matched?"rgba(34,197,94,0.1)":"rgba(100,116,139,0.08)",
                                border:`1px solid ${matched?"rgba(34,197,94,0.3)":"#1a2c44"}`,
                                color:matched?"#2ed47a":"#5a728f"}}>
                                {fd.label||c.field}
                                {v!=null&&!isDerived(c.field)&&`: ${typeof v==="number"?v.toFixed(1):v}`}
                                {v!=null&&isDerived(c.field)&&(v>0?" ↑":" ↓")}
                              </span>
                            );
                          })}
                        </div>
                        {/* Indicator values */}
                        <div style={{display:"flex",gap:5,flexWrap:"wrap",fontSize:9,color:"#5a728f"}}>
                          {r.rsi!=null&&<span style={{color:r.rsi>=70?"#ff5b6c":r.rsi<=30?"#2ed47a":"#8ea2bf"}}>RSI {r.rsi?.toFixed(0)}</span>}
                          {r.macd!=null&&<span style={{color:r.macd>0?"#06b6d4":"#8ea2bf"}}>MACD {r.macd>0?"+":""}{r.macd?.toFixed(2)}</span>}
                          {r.stoch_k!=null&&<span style={{color:r.stoch_k>=80?"#ff5b6c":r.stoch_k<=20?"#2ed47a":"#e879f9"}}>K{r.stoch_k?.toFixed(0)}</span>}
                          {r.volRatio!=null&&<span style={{color:r.volRatio>=1.5?"#f59e0b":"#5a728f"}}>Vol {r.volRatio?.toFixed(1)}x</span>}
                        </div>
                        {/* Fundamental data — shown in investment mode */}
                        {r.fundamentals&&Object.keys(r.fundamentals).length>0&&(
                          <div style={{display:"flex",gap:4,flexWrap:"wrap",fontSize:8,marginTop:4,
                            padding:"4px 6px",background:"rgba(232,179,57,0.06)",border:"1px solid rgba(232,179,57,0.15)",borderRadius:0}}>
                            {r.fundamentals.pe!=null&&<span style={{color:r.fundamentals.pe<15?"#2ed47a":r.fundamentals.pe>40?"#ff5b6c":"#e8b339"}}>PE {r.fundamentals.pe}</span>}
                            {r.fundamentals.pb!=null&&<span style={{color:r.fundamentals.pb<1?"#2ed47a":"#8ea2bf"}}>PB {r.fundamentals.pb}</span>}
                            {r.fundamentals.roe!=null&&<span style={{color:r.fundamentals.roe>15?"#2ed47a":"#8ea2bf"}}>ROE {r.fundamentals.roe}%</span>}
                            {r.fundamentals.roce!=null&&<span style={{color:r.fundamentals.roce>15?"#2ed47a":"#8ea2bf"}}>ROCE {r.fundamentals.roce}%</span>}
                            {r.fundamentals.debt_equity!=null&&<span style={{color:r.fundamentals.debt_equity<0.5?"#2ed47a":r.fundamentals.debt_equity>2?"#ff5b6c":"#f59e0b"}}>D/E {r.fundamentals.debt_equity}</span>}
                            {r.fundamentals.sales_growth!=null&&<span style={{color:r.fundamentals.sales_growth>10?"#2ed47a":"#8ea2bf"}}>Rev {r.fundamentals.sales_growth>0?"+":""}{r.fundamentals.sales_growth}%</span>}
                            {r.fundamentals.profit_growth!=null&&<span style={{color:r.fundamentals.profit_growth>15?"#2ed47a":"#8ea2bf"}}>Profit {r.fundamentals.profit_growth>0?"+":""}{r.fundamentals.profit_growth}%</span>}
                            {r.fundamentals.promoter_holding!=null&&<span style={{color:r.fundamentals.promoter_holding>50?"#2ed47a":"#f59e0b"}}>Prom {r.fundamentals.promoter_holding}%</span>}
                            {r.fundamentals.market_cap!=null&&<span style={{color:"#8ea2bf"}}>MCap ₹{r.fundamentals.market_cap>=100?Math.round(r.fundamentals.market_cap).toLocaleString("en-IN"):r.fundamentals.market_cap}Cr</span>}
                            {r.fundamentals.cash_flow!=null&&<span style={{color:r.fundamentals.cash_flow>0?"#2ed47a":"#ff5b6c"}}>CF {r.fundamentals.cash_flow>0?"+":""}{Math.round(r.fundamentals.cash_flow).toLocaleString("en-IN")}Cr</span>}
                            {r.fundamentals.current_ratio!=null&&<span style={{color:r.fundamentals.current_ratio>=1.5?"#2ed47a":r.fundamentals.current_ratio<1?"#ff5b6c":"#f59e0b"}}>CR {r.fundamentals.current_ratio?.toFixed(1)}</span>}
                          </div>
                        )}
                        {/* ── Trade + Virtual buttons ── */}
                        {ltp>0&&(
                          <div style={{display:"flex",gap:4,marginTop:6,flexWrap:"wrap"}}>
                            {canTrade&&onTrade&&buys.length>0&&(
                              <button onClick={()=>onTrade({symbol:r.symbol,exchange:r.exchange||"NSE",side:"BUY",price:ltp,qty:1,product:runTf==="1day"||runTf==="1week"?"CNC":"MIS",signals:r.signals||[],signalLabel:(r.signals||[]).map(s=>s.label).join(" · ")})}
                                style={{flex:1,background:"rgba(34,197,94,0.12)",border:"1px solid rgba(34,197,94,0.35)",
                                  borderRadius:0,padding:"4px 0",color:"#2ed47a",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer",textAlign:"center"}}>
                                ▲ BUY {runTf==="1day"||runTf==="1week"?"CNC":"MIS"}
                              </button>
                            )}
                            {canTrade&&onTrade&&sells.length>0&&(
                              <button onClick={()=>onTrade({symbol:r.symbol,exchange:r.exchange||"NSE",side:"SELL",price:ltp,qty:1,product:runTf==="1day"||runTf==="1week"?"CNC":"MIS",signals:r.signals||[],signalLabel:(r.signals||[]).map(s=>s.label).join(" · ")})}
                                style={{flex:1,background:"rgba(239,68,68,0.12)",border:"1px solid rgba(239,68,68,0.35)",
                                  borderRadius:0,padding:"4px 0",color:"#ff5b6c",fontFamily:'inherit',fontSize:10,fontWeight:700,cursor:"pointer",textAlign:"center"}}>
                                ▼ SELL {runTf==="1day"||runTf==="1week"?"CNC":"MIS"}
                              </button>
                            )}
                            {canTrade&&onTrade&&!buys.length&&!sells.length&&(
                              <>
                                <button onClick={()=>onTrade({symbol:r.symbol,exchange:r.exchange||"NSE",side:"BUY",price:ltp,qty:1,product:"CNC"})}
                                  style={{flex:1,background:"rgba(34,197,94,0.08)",border:"1px solid rgba(34,197,94,0.25)",
                                    borderRadius:0,padding:"4px 0",color:"#2ed47a",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer",textAlign:"center"}}>
                                  ▲ BUY
                                </button>
                                <button onClick={()=>onTrade({symbol:r.symbol,exchange:r.exchange||"NSE",side:"SELL",price:ltp,qty:1,product:"CNC"})}
                                  style={{flex:1,background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.25)",
                                    borderRadius:0,padding:"4px 0",color:"#ff5b6c",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer",textAlign:"center"}}>
                                  ▼ SELL
                                </button>
                              </>
                            )}
                            {/* Virtual trade buttons — always visible */}
                            {addVirtualRef&&(
                              <div style={{display:"flex",gap:3,width:"100%",marginTop:2}}>
                                <button onClick={()=>{if(addVirtualRef.current)addVirtualRef.current({symbol:r.symbol,exchange:r.exchange||"NSE",side:"BUY",qty:1,price:ltp,source:"scan"});}}
                                  style={{flex:1,background:"rgba(73,194,228,0.08)",border:"1px solid rgba(73,194,228,0.25)",
                                    borderRadius:0,padding:"3px 0",color:"#49c2e4",fontFamily:"inherit",fontSize:9,fontWeight:700,cursor:"pointer",textAlign:"center"}}>
                                  🎮 V-BUY
                                </button>
                                <button onClick={()=>{if(addVirtualRef.current)addVirtualRef.current({symbol:r.symbol,exchange:r.exchange||"NSE",side:"SELL",qty:1,price:ltp,source:"scan"});}}
                                  style={{flex:1,background:"rgba(73,194,228,0.08)",border:"1px solid rgba(73,194,228,0.25)",
                                    borderRadius:0,padding:"3px 0",color:"#49c2e4",fontFamily:"inherit",fontSize:9,fontWeight:700,cursor:"pointer",textAlign:"center"}}>
                                  🎮 V-SELL
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      )}

    </div>{/* end left scan panel */}

    {/* ── Right: AI Advisor (collapsible) ── */}
    {showAIPanel&&(
      <div style={{width:380,minWidth:340,maxWidth:420,borderLeft:"1px solid #1a2c44",
        display:"flex",flexDirection:"column",overflow:"hidden",flexShrink:0}}>
        <AITab scanResults={scanResults} indexQuotes={indexQuotes} positions={positions}
          signalParams={signalParams} setSignalParams={setSignalParams}
          isAdmin={isAdmin} userRole={userRole} journal={journal}
          hasAIAccess={hasAIAccess} watchers={watchers}/>
      </div>
    )}

    </div>
  );
}


// TAB: DASHBOARD
// ══════════════════════════════════════════════════════════════════════════════
function DashboardTab({positions,holdings,margins,scanResults,alerts,indexQuotes}) {
  const posNet = useMemo(()=>{
    const net = positions?.net||[];
    return {
      unrealised: net.reduce((a,p)=>a+(p.unrealised||0),0),
      realised:   net.reduce((a,p)=>a+(p.realised||0),0),
      total:      net.reduce((a,p)=>a+(p.pnl||0),0),
    };
  },[positions]);

  const holdPnl = useMemo(()=>
    (holdings||[]).reduce((a,h)=>a+(h.pnl||0),0)
  ,[holdings]);

  const openPositions = (positions?.net||[]).filter(p=>p.quantity!==0);
  const signalStocks  = scanResults.filter(r=>(r.signals||[]).length>0);
  const todayAlerts   = alerts.filter(a=>a.date===todayKey());



  return (
    <div style={{padding:16,display:"flex",flexDirection:"column",gap:14}}>
      {/* KPI grid */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:8}}>
        <KpiBox label="TODAY P&L (F&O)" value={fmt(posNet.total)} color={pnlColor(posNet.total)} sub={`Unreal: ${fmt(posNet.unrealised)}`}/>
        <KpiBox label="HOLDINGS P&L"    value={fmt(holdPnl)}      color={pnlColor(holdPnl)}/>
        <KpiBox label="TOTAL MARGIN" value={fmt(getMargin(margins,"nrml"))} color="#2ed47a"
          sub={(()=>{const b=getMarginBreakdown(margins);const parts=[];if(b.cash>0)parts.push(`Cash ₹${Math.round(b.cash).toLocaleString("en-IN")}`);if(b.collateral>0)parts.push(`Pledge ₹${Math.round(b.collateral).toLocaleString("en-IN")}`);return parts.join(" + ")||undefined;})()}/>
        <KpiBox label="MARGIN USED" value={fmt(margins?.equity?.used||0)} color="#e8b339"
          sub={margins?.equity?.available_mis>0?`MIS avail: ${fmt(margins.equity.available_mis)}`:undefined}/>
      </div>

      {/* Index strip */}
      <Card>
        <div style={{fontSize:10,color:"#8ea2bf",letterSpacing:2,marginBottom:10}}>MARKET OVERVIEW</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:8}}>
          {Object.entries(INDEX_QUOTES_MAP).map(([name,key])=>{
            const q=indexQuotes[key];
            return (
              <div key={name} style={{background:"#0b1624",padding:"10px 12px",borderRadius:0,border:"1px solid #1a2c44"}}>
                <div style={{fontSize:12,color:"#8ea2bf",marginBottom:3}}>{name}</div>
                {q ? (
                  <div style={{fontSize:14,fontWeight:700,color:q.pct>=0?"#2ed47a":"#ff5b6c"}}>
                    {q.ltp?.toLocaleString("en-IN")}
                    <span style={{fontSize:10,marginLeft:6}}>{q.pct>=0?"▲":"▼"}{Math.abs(q.pct||0).toFixed(2)}%</span>
                  </div>
                ):<div style={{color:"#23405f",fontSize:11}}>loading...</div>}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Open positions summary */}
      <Card>
        <div style={{fontSize:10,color:"#8ea2bf",letterSpacing:2,marginBottom:10}}>OPEN POSITIONS ({openPositions.length})</div>
        {openPositions.length===0
          ? <div style={{color:"#23405f",fontSize:12,textAlign:"center",padding:"12px 0"}}>No open positions</div>
          : openPositions.slice(0,5).map((p,i)=>(
            <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:"1px solid #1a2c44"}}>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:"#e7edf5"}}>{p.tradingsymbol}</div>
                <div style={{fontSize:10,color:"#8ea2bf"}}>Qty: {p.quantity} · Avg: ₹{p.average_price}</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:13,fontWeight:700,color:pnlColor(p.pnl)}}>{fmt(p.pnl)}</div>
                <div style={{fontSize:10,color:"#8ea2bf"}}>LTP: ₹{p.last_price}</div>
              </div>
            </div>
          ))
        }
      </Card>

      {/* Scanner signals summary */}
      <Card>
        <div style={{fontSize:10,color:"#8ea2bf",letterSpacing:2,marginBottom:10}}>TODAY SIGNALS ({todayAlerts.length})</div>
        {todayAlerts.slice(0,6).map((a,i)=>(
          <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:"1px solid #1a2c44"}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <Badge type={a.signal.type}>{a.signal.type==="BUY"?"▲":"▼"} {a.signal.type}</Badge>
              <span style={{fontSize:12,fontWeight:700,color:"#e7edf5"}}>{a.symbol}</span>
              {a.signal.scan===5
                ? <PatternBadge signal={a.signal}/>
                : <span style={{fontSize:10,color:"#8ea2bf"}}>{a.signal.label}</span>
              }
            </div>
            <span style={{fontSize:10,color:"#8ea2bf"}}>₹{a.price}</span>
          </div>
        ))}
        {todayAlerts.length===0 && <div style={{color:"#23405f",fontSize:12,textAlign:"center",padding:"12px 0"}}>Run a scan to see signals</div>}
      </Card>


    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: SCANNER (grid/table)
// ══════════════════════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════════════════════
// STRATEGY CONFIG PANEL — shared by Scanner AT, AutoFO, MCX tabs
// ══════════════════════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════════════════════
// BACKGROUND ENGINE — runs all tab scan+AT loops regardless of active tab
// Each engine: { enabled, cfg, fired } managed at App level
// ══════════════════════════════════════════════════════════════════════════════
function useBackgroundEngines({
  authed, deltaAuthed,
  signalParams,
  onAddAutoTP, onShowToast,
  // MCX
  mcxEnabled, mcxCfg, mcxAtMode, mcxFired, setMcxFired, setMcxAtLog,
  // Delta
  deltaEnabled, deltaCfg, deltaFired, setDeltaFired, setDeltaLog,
}) {
  // ── Refs so interval callbacks always see latest values ───────────────────
  const mcxCfgRef       = useRef(mcxCfg);
  const mcxAtModeRef    = useRef(mcxAtMode);
  const mcxFiredRef     = useRef(mcxFired);
  const deltaCfgRef     = useRef(deltaCfg);
  const deltaFiredRef   = useRef(deltaFired);
  const signalParamsRef = useRef(signalParams);

  useEffect(()=>{ mcxCfgRef.current       = mcxCfg;       },[mcxCfg]);
  useEffect(()=>{ mcxAtModeRef.current    = mcxAtMode;    },[mcxAtMode]);
  useEffect(()=>{ mcxFiredRef.current     = mcxFired;     },[mcxFired]);
  useEffect(()=>{ deltaCfgRef.current     = deltaCfg;     },[deltaCfg]);
  useEffect(()=>{ deltaFiredRef.current   = deltaFired;   },[deltaFired]);
  useEffect(()=>{ signalParamsRef.current = signalParams; },[signalParams]);

  // ── MCX engine ────────────────────────────────────────────────────────────
  useEffect(()=>{
    if(!mcxEnabled || !authed) return;
    const run = async()=>{
      const cfg=mcxCfgRef.current;
      const mode=mcxAtModeRef.current;
      const scanList=cfg.scanList||[];
      if(!scanList.length) return;
      try{
        const {results}=await apiFetch("/scan",{method:"POST",headers:{"Content-Type":"application/json"},
          body:JSON.stringify({symbols:scanList,timeframe:cfg.timeframe||"1hour",exchange:"MCX",
            tech_weight:0.7,news_enabled:false,signal_params:signalParamsRef.current||DEFAULT_SIGNAL_PARAMS})});
        // Batch guards — prevent race condition where multiple stocks fire in same scan
        let mcxBatchCount = 0;
        const mcxBatchFired = new Set();
        const mcxDailyLimit = cfg.dailyLimit || 5;

        for(const stock of results){
          if(!stock.price||stock.error) continue;
          if(mcxBatchCount >= mcxDailyLimit) break; // hard stop within batch
          if(mcxBatchFired.has(stock.symbol)) continue;
          const now=Date.now();
          if((now-(mcxFiredRef.current[stock.symbol]||0))/1000 < (cfg.cooldown||300)) continue;
          const sigs=(stock.signals||[]).filter(s=>(cfg.conditions||[1,2,3,4,5,8]).includes(s.scan));
          if(sigs.length < (cfg.minSignals||2)) continue;
          const buys=sigs.filter(s=>s.type==="BUY").length;
          const sells=sigs.filter(s=>s.type==="SELL").length;
          const side=buys>=sells?"BUY":"SELL";
          mcxBatchFired.add(stock.symbol);
          mcxBatchCount++;
          setMcxFired(p=>({...p,[stock.symbol]:now}));
          mcxFiredRef.current={...mcxFiredRef.current,[stock.symbol]:now};
          try{
            // lot_size comes from scan result (resolved from instrument map)
            const lotSize = stock.lot_size || cfg.lotSize || 1;
            const tradeSym = stock.resolved_symbol || stock.symbol;
            if(mode==="options"){
              const actualSide=buys>0&&sells>0?"MIXED":side;
              const stratRes=await buildStrategy(tradeSym,"MCX",actualSide,{
                minDays:cfg.minDays||15,ivOverride:cfg.ivOverride,strategyOverride:cfg.strategyOverride,
                spreadSteps:cfg.spreadSteps||2,signalCount:sigs.length});
              if(!stratRes.found) continue;
              const orderResults=await placeStrategyLegs(stratRes.legs,cfg.capital,"NRML");
              for(const r of orderResults){
                if(!r.ok||r.leg.action!=="BUY") continue;
                const tp=parseFloat((r.price*(1+(cfg.optTpPct||30)/100)).toFixed(2));
                const sl=parseFloat((r.price*(1-(cfg.optSlPct||20)/100)).toFixed(2));
                onAddAutoTP&&onAddAutoTP({id:`atp-mcxopt-bg-${Date.now()}`,
                  symbol:r.leg.tradingsymbol,exchange:"MCX",side:"BUY",
                  entryPrice:r.price,qty:r.qty,product:"NRML",
                  tpEnabled:true,tpPrice:tp,tpPct:cfg.optTpPct||30,
                  slEnabled:true,slPrice:sl,slPct:cfg.optSlPct||20,
                  trailEnabled:false,highWaterMark:r.price,
                  status:"WATCHING",orderId:r.orderId,
                  createdAt:new Date().toISOString(),source:"MCX BG Auto",
                  // FIX: no zones on option-leg watchers — underlying-scale zones
                  // vs option premium force-fired SL right after entry.
                  demandZone:null, supplyZone:null,
                  zoneTouched:'none',
                });
              }
              setMcxAtLog(p=>[{id:`at-bg-${Date.now()}`,time:new Date().toLocaleTimeString("en-IN"),
                symbol:stock.symbol,mode:"OPT",strategy:stratRes.strategy,ivRank:stratRes.iv_rank,
                legs:orderResults.length,side:actualSide,
                status:orderResults.every(r=>r.ok)?"PLACED":"PARTIAL",
                signals:sigs.map(s=>s.label).join(", ")},...p].slice(0,200));
              onShowToast?.(`🏭 MCX ${stratRes.strategy}: ${stock.symbol}`);
            } else {
              // Futures — use lot_size from scan result
              const qty = Math.max(lotSize, Math.floor((cfg.capital||50000)/stock.price/lotSize)*lotSize);
              const entryP = parseFloat(stock.price||0);
              const tpP = parseFloat((entryP*(side==="BUY"?1+(cfg.tpPct||2)/100:1-(cfg.tpPct||2)/100)).toFixed(2));
              const slP = parseFloat((entryP*(side==="BUY"?1-(cfg.slPct||1)/100:1+(cfg.slPct||1)/100)).toFixed(2));

              const res=await apiFetch("/order/place",{method:"POST",headers:{"Content-Type":"application/json"},
                body:JSON.stringify({tradingsymbol:tradeSym,exchange:"MCX",
                  transaction_type:side,quantity:qty,order_type:"MARKET",
                  trigger_price:0,product:"NRML",tag:"RABT-AUTO-MCX"})});

              // Register TP/SL watcher via onAddAutoTP (tracked in frontend + backend)
              onAddAutoTP&&onAddAutoTP({
                id:`atp-mcx-bg-${Date.now()}-${tradeSym}`,
                symbol:tradeSym, exchange:"MCX", side,
                entryPrice:entryP, qty, product:"NRML",
                tpEnabled:true, tpPrice:tpP, tpPct:cfg.tpPct||2,
                slEnabled:true, slPrice:slP, slPct:cfg.slPct||1,
                trailEnabled:false, highWaterMark:entryP,
                status:"WATCHING", orderId:res.order_id,
                createdAt:new Date().toISOString(), source:"MCX BG Auto",
                // Zone-aware TP/SL
                ...extractNearestZones(stock.zones, entryP),
                zoneTouched:'none',
              });
              setMcxAtLog(p=>[{id:`at-bg-${Date.now()}`,time:new Date().toLocaleTimeString("en-IN"),
                symbol:stock.symbol,contract:tradeSym,mode:"FUT",side,qty,
                price:entryP,tp:tpP,sl:slP,status:"PLACED",orderId:res.order_id,
                signals:sigs.map(s=>s.label).join(", ")},...p].slice(0,200));
              onShowToast?.(`🏭 MCX FUT: ${side} ${tradeSym} ×${qty} | TP ₹${tpP} SL ₹${slP}`);
            }
          }catch(e){ onShowToast?.(`MCX AT failed ${stock.symbol}: ${e.message}`,"error"); }
        }
      }catch{}
    };
    run();
    const t=setInterval(run,60000);
    return()=>clearInterval(t);
  },[mcxEnabled,authed]);

  // ── Delta engine ──────────────────────────────────────────────────────────
  useEffect(()=>{
    if(!deltaEnabled || !deltaAuthed) return;
    // Local daily trade counter for BG engine (resets at midnight)
    const deltaDailyRef = {current: {date:'', count:0}};
    const run = async()=>{
      const cfg = deltaCfgRef.current;
      const symbols = cfg.symbols||[];
      if(!symbols.length) return;

      // ── Pre-flight: check balance + margin usage ───────────────────────────
      let availBal = 0;
      let marginUsagePct = 0;
      try {
        const risk = await deltaFetch("/delta/risk");
        availBal      = parseFloat(risk.available_balance||0);
        marginUsagePct= parseFloat(risk.margin_usage_pct||0);
        // Block if margin usage too high or balance too low
        if(marginUsagePct >= (cfg.maxMarginUsagePct||80)) {
          onShowToast?.(`⚠ Δ AT blocked — margin ${marginUsagePct.toFixed(1)}% (limit ${cfg.maxMarginUsagePct||80}%)`, "warn");
          return;
        }
        // Check for any positions near liquidation
        const liqWarnings = risk.liq_warnings||[];
        if(liqWarnings.length>0){
          const danger = liqWarnings.filter(w=>parseFloat(w.liq_dist_pct||100) < (cfg.liqWarningPct||15));
          if(danger.length>0){
            onShowToast?.(`🚨 Δ AT blocked — ${danger.length} position(s) near liquidation! Add margin first.`, "error");
            return;
          }
        }
      } catch{ /* risk fetch failed — use cfg.capital as fallback */ }

      try{
        const data = await deltaFetch("/delta/scan",{method:"POST",headers:{"Content-Type":"application/json"},
          body:JSON.stringify({symbols, resolution:cfg.timeframe||"1h", tech_weight:0.7,
            signal_params:signalParamsRef.current||DEFAULT_SIGNAL_PARAMS})});
        const results = data.results||[];

        for(const stock of results){
          if(!stock.price) continue;
          const now = Date.now();
          if((now-(deltaFiredRef.current[stock.symbol]||0))/1000 < cfg.cooldown) continue;
          const sigs  = (stock.signals||[]).filter(s=>cfg.conditions.includes(s.scan));
          if(sigs.length < cfg.minSignals) continue;
          const buys  = sigs.filter(s=>s.type==="BUY").length;
          const sells = sigs.filter(s=>s.type==="SELL").length;

          // Skip mixed signals — must all agree
          if(buys > 0 && sells > 0) continue;

          const side = buys >= sells ? "buy" : "sell";

          // Check daily limit
          const dl = checkDailyLimit(deltaDailyRef, cfg.dailyLimit||99);
          if(dl.reached){
            onShowToast?.(`⚠ Δ daily limit reached (${dl.count}/${cfg.dailyLimit})`, "warn");
            break;
          }

          setDeltaFired(p=>({...p,[stock.symbol]:now}));
          deltaFiredRef.current = {...deltaFiredRef.current,[stock.symbol]:now};

          try{
            // ── Get product info for margin calculation ──────────────────────
            let pid  = cfg.pidCache?.[stock.symbol]||null;
            let initialMarginPct = 0.05;  // 5% default
            let contractValue    = 1;

            if(!pid){
              const pd = await deltaFetch(`/delta/products?symbol=${stock.symbol}&state=live`);
              const prod = (pd.products||[])[0];
              if(!prod) continue;
              pid = prod.id;
              initialMarginPct = parseFloat(prod.initial_margin||"0.05");
              if(initialMarginPct > 1) initialMarginPct /= 100;
              contractValue = parseFloat(prod.contract_value||"1");
              const u = {...(cfg.pidCache||{}), [stock.symbol]:pid};
              deltaCfgRef.current = {...deltaCfgRef.current, pidCache:u};
            }

            // ── Live price ───────────────────────────────────────────────────
            const ticker = await deltaFetch(`/delta/ticker/${stock.symbol}`);
            const price  = parseFloat(side==="buy"
              ? (ticker.ask||ticker.last_price||ticker.mark_price)
              : (ticker.bid||ticker.last_price||ticker.mark_price));
            if(!price || price<=0) continue;

            // ── Margin-aware sizing ──────────────────────────────────────────
            // margin_per_contract = price × contract_value × initial_margin_pct
            const marginPerContract = price * contractValue * initialMarginPct;
            let size = 1;
            if(cfg.autoSize && availBal>0 && marginPerContract>0){
              // Capital = availBal × maxTradesPct%
              const capital = availBal * ((cfg.maxTradesPct||20)/100);
              size = Math.max(1, Math.floor(capital / marginPerContract));
              // Safety cap: never use >30% balance on one trade
              const maxSize = Math.max(1, Math.floor((availBal*0.30)/marginPerContract));
              size = Math.min(size, maxSize);
            } else {
              // Manual: use capital setting as USD target
              const capital = cfg.capital||10;
              size = Math.max(1, Math.floor(capital / marginPerContract));
            }

            // ── Fee-inclusive TP/SL ──────────────────────────────────────────
            // Delta taker fee: 0.05% per side = 0.10% round trip
            // TP must clear fees by at least 0.10% buffer → minimum TP = 0.20%
            const TAKER_FEE = 0.0005;
            const minTpPct  = (TAKER_FEE*2 + 0.001) * 100; // 0.20%
            const rawTpPct  = cfg.tpPct||3.0;
            const effectiveTpPct = Math.max(rawTpPct, minTpPct);

            const tp = parseFloat((price*(1+(side==="buy"?effectiveTpPct:-effectiveTpPct)/100)).toFixed(6));
            const sl = cfg.slPct>0
              ? parseFloat((price*(1+(side==="buy"?-cfg.slPct:cfg.slPct)/100)).toFixed(6))
              : null;

            // ── Verify margin adequacy before placing ────────────────────────
            // Require 1.5× margin buffer to keep position safe from liquidation
            const totalMarginNeeded = marginPerContract * size * 1.5;
            if(availBal > 0 && totalMarginNeeded > availBal){
              onShowToast?.(`⚠ Δ ${stock.symbol}: need $${totalMarginNeeded.toFixed(2)} but only $${availBal.toFixed(2)} — skipping`, "warn");
              continue;
            }

            // ── Build + place order ──────────────────────────────────────────
            const bgTF      = cfg.timeframe||"1h";
            const isScalp   = bgTF==="3m" || bgTF==="5m";
            const isBTC     = (stock.symbol||"").toUpperCase().includes("BTC");
            const leverage  = isScalp ? (cfg.scalpLeverage||50) : (cfg.leverage||10);
            const body = {
              product_id:    pid,
              side,
              size,
              order_type:    cfg.orderType||"limit_order",
              time_in_force: "gtc",
              reduce_only:   false,
              ...(leverage > 1 ? {leverage} : {}),
            };
            if(cfg.orderType!=="market_order") body.limit_price = String(price);
            if(effectiveTpPct>0) body.bracket_take_profit_limit_price = String(tp);
            if(sl)               body.bracket_stop_loss_limit_price   = String(sl);

            const res = await deltaFetch("/delta/order/place",{
              method:"POST",headers:{"Content-Type":"application/json"},
              body:JSON.stringify(body)
            });

            const feeEst = (price*size*contractValue*TAKER_FEE*2).toFixed(4);
            setDeltaLog(p=>[{
              id:`dt-bg-${Date.now()}`,
              time: new Date().toLocaleTimeString("en-IN"),
              symbol:stock.symbol, side:side.toUpperCase(), price, size,
              tp, sl:sl||null, marginUsed:(marginPerContract*size).toFixed(2),
              feeEst, effectiveTpPct: effectiveTpPct.toFixed(2),
              status:"PLACED", orderId:res.order_id,
              signals:sigs.map(s=>s.label).join(", ")
            },...p].slice(0,200));

            onShowToast?.(`Δ AT: ${side.toUpperCase()} ${size}× ${stock.symbol} @ $${price} | TP $${tp} | Fee ~$${feeEst}`);

            // Update available balance estimate to prevent over-trading in same run
            availBal = Math.max(0, availBal - marginPerContract*size);
            // Increment daily counter
            incrementDailyCount(v=>{ deltaDailyRef.current = v; }, deltaDailyRef);

          }catch(e){ onShowToast?.(`Δ AT failed ${stock.symbol}: ${e.message}`, "error"); }
        }
      }catch{}
    };
    run();
    const t = setInterval(run, 60000);
    return()=>clearInterval(t);
  },[deltaEnabled,deltaAuthed]);
}


// ══════════════════════════════════════════════════════════════════════════════
// SIGNAL SETTINGS MODAL — adjust RSI/TSI/EMA/Vol thresholds
// ══════════════════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════════════════
// SUPPORT / RESISTANCE / DEMAND / SUPPLY ZONE PANEL
// Users can add manual zones + view auto-detected zones from scan
// ══════════════════════════════════════════════════════════════════════════════
function SRZonePanel({ scanResults, onClose }) {
  const [manualZones, setManualZones] = useLocalStorage("nse_pro_sr_zones", []);
  const [form, setForm] = useState({
    symbol:"", type:"support", price:"", zone_low:"", zone_high:"", note:"", source:"manual"
  });
  const [tab, setTab] = useState("auto"); // auto | manual

  const typeColors = {
    support:    "#2ed47a", resistance: "#ff5b6c",
    demand:     "#e8b339", supply:     "#ff5b6c",
  };

  function addManual() {
    if (!form.symbol || !form.price) return;
    const price = parseFloat(form.price);
    const zone  = {
      id:        `sr-${Date.now()}`,
      symbol:    form.symbol.toUpperCase(),
      type:      form.type,
      price,
      zone_low:  parseFloat(form.zone_low) || parseFloat((price * 0.99).toFixed(2)),
      zone_high: parseFloat(form.zone_high) || parseFloat((price * 1.01).toFixed(2)),
      note:      form.note,
      source:    "manual",
      addedAt:   new Date().toISOString(),
    };
    setManualZones(p => [zone, ...p]);
    setForm({ symbol:"", type:"support", price:"", zone_low:"", zone_high:"", note:"", source:"manual" });
  }

  // Collect auto-detected zones from scan results
  const autoZones = [];
  (scanResults||[]).forEach(r => {
    const sr = r.sr || {};
    const sym = r.symbol;
    (sr.support||[]).forEach(z => autoZones.push({...z, symbol:sym, type:"support", source:"auto"}));
    (sr.resistance||[]).forEach(z => autoZones.push({...z, symbol:sym, type:"resistance", source:"auto"}));
    (sr.demand||[]).forEach(z => autoZones.push({...z, symbol:sym, type:"demand", source:"auto"}));
    (sr.supply||[]).forEach(z => autoZones.push({...z, symbol:sym, type:"supply", source:"auto"}));
  });

  const ZoneRow = ({z, onDelete}) => {
    const col = typeColors[z.type] || "#8ea2bf";
    return (
      <div style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",
        borderBottom:"1px solid #161b22",background:"#0b1624"}}>
        <div style={{width:3,height:32,background:col,borderRadius:2,flexShrink:0}}/>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
            <span style={{fontSize:11,fontWeight:700,color:"#e7edf5"}}>{z.symbol}</span>
            <span style={{fontSize:11,padding:"1px 5px",borderRadius:0,
              background:`${col}22`,color:col,border:`1px solid ${col}44`,fontWeight:700,
              textTransform:"uppercase"}}>{z.type}</span>
            {z.source==="auto"&&<span style={{fontSize:7,color:"#5a728f"}}>AUTO</span>}
            {z.strength&&<span style={{fontSize:11,color:"#8ea2bf"}}>★{z.strength}</span>}
          </div>
          <div style={{display:"flex",gap:10,marginTop:2,flexWrap:"wrap"}}>
            <span style={{fontSize:10,color:col,fontWeight:700}}>₹{z.price}</span>
            {(z.zone_low||z.zone_high) && (
              <span style={{fontSize:12,color:"#5a728f"}}>
                Zone ₹{z.zone_low}–₹{z.zone_high}
              </span>
            )}
            {z.note&&<span style={{fontSize:12,color:"#8ea2bf",fontStyle:"italic"}}>{z.note}</span>}
          </div>
        </div>
        {onDelete&&(
          <button onClick={onDelete}
            style={{background:"none",border:"none",color:"#5a728f",cursor:"pointer",fontSize:12,padding:"0 4px"}}>✕</button>
        )}
      </div>
    );
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:9999,
      display:"flex",alignItems:"flex-start",justifyContent:"center",paddingTop:40}}>
      <div style={{background:"#0b1624",border:"1px solid #23405f",borderRadius:12,
        width:"min(680px,95vw)",maxHeight:"80vh",display:"flex",flexDirection:"column",overflow:"hidden"}}>

        {/* Header */}
        <div style={{padding:"14px 16px",borderBottom:"1px solid #1a2c44",
          display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
          <div>
            <div style={{fontSize:14,fontWeight:800,color:"#e7edf5"}}>📊 S/R & Demand/Supply Zones</div>
            <div style={{fontSize:10,color:"#8ea2bf",marginTop:2}}>
              Auto-detected from scans + manual zones
            </div>
          </div>
          <button onClick={onClose}
            style={{background:"none",border:"none",color:"#8ea2bf",fontSize:18,cursor:"pointer"}}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{display:"flex",borderBottom:"1px solid #1a2c44",flexShrink:0}}>
          {[["auto",`Auto (${autoZones.length})`],["manual",`Manual (${manualZones.length})`],["add","+ Add Zone"]].map(([v,l])=>(
            <button key={v} onClick={()=>setTab(v)}
              style={{flex:1,background:"none",border:"none",
                borderBottom:`2px solid ${tab===v?"#e8b339":"transparent"}`,
                padding:"9px 0",color:tab===v?"#e8b339":"#8ea2bf",
                fontFamily:"inherit",fontSize:11,fontWeight:tab===v?700:400,cursor:"pointer"}}>
              {l}
            </button>
          ))}
        </div>

        <div style={{flex:1,overflowY:"auto"}}>
          {/* Auto zones */}
          {tab==="auto"&&(
            <div>
              {autoZones.length===0
                ? <div style={{padding:32,textAlign:"center",color:"#5a728f",fontSize:12}}>
                    Run a scan first to see auto-detected zones
                  </div>
                : autoZones.sort((a,b)=>a.symbol.localeCompare(b.symbol)||b.strength-a.strength)
                    .map((z,i)=><ZoneRow key={i} z={z}/>)
              }
            </div>
          )}

          {/* Manual zones */}
          {tab==="manual"&&(
            <div>
              {manualZones.length===0
                ? <div style={{padding:32,textAlign:"center",color:"#5a728f",fontSize:12}}>
                    No manual zones added yet.<br/>Go to "+ Add Zone" to add one.
                  </div>
                : manualZones.map((z,i)=>(
                    <ZoneRow key={z.id} z={z}
                      onDelete={()=>setManualZones(p=>p.filter(x=>x.id!==z.id))}/>
                  ))
              }
            </div>
          )}

          {/* Add zone form */}
          {tab==="add"&&(
            <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <div style={{flex:2,minWidth:100}}>
                  <div style={{fontSize:12,color:"#8ea2bf",marginBottom:4}}>SYMBOL *</div>
                  <input value={form.symbol} onChange={e=>setForm(p=>({...p,symbol:e.target.value.toUpperCase()}))}
                    placeholder="RELIANCE"
                    style={{width:"100%",background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,
                      padding:"8px 10px",color:"#e7edf5",fontFamily:"inherit",fontSize:12,outline:"none",boxSizing:"border-box"}}/>
                </div>
                <div style={{flex:1,minWidth:80}}>
                  <div style={{fontSize:12,color:"#8ea2bf",marginBottom:4}}>TYPE *</div>
                  <select value={form.type} onChange={e=>setForm(p=>({...p,type:e.target.value}))}
                    style={{width:"100%",background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,
                      padding:"8px 10px",color:"#e7edf5",fontFamily:"inherit",fontSize:12,outline:"none"}}>
                    <option value="support">Support</option>
                    <option value="resistance">Resistance</option>
                    <option value="demand">Demand Zone</option>
                    <option value="supply">Supply Zone</option>
                  </select>
                </div>
              </div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <div style={{flex:1,minWidth:80}}>
                  <div style={{fontSize:12,color:"#8ea2bf",marginBottom:4}}>PRICE * (₹)</div>
                  <input type="number" value={form.price} onChange={e=>setForm(p=>({...p,price:e.target.value}))}
                    placeholder="2450"
                    style={{width:"100%",background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,
                      padding:"8px 10px",color:"#e7edf5",fontFamily:"inherit",fontSize:12,outline:"none",boxSizing:"border-box"}}/>
                </div>
                <div style={{flex:1,minWidth:80}}>
                  <div style={{fontSize:12,color:"#8ea2bf",marginBottom:4}}>ZONE LOW (₹)</div>
                  <input type="number" value={form.zone_low} onChange={e=>setForm(p=>({...p,zone_low:e.target.value}))}
                    placeholder="auto (−1%)"
                    style={{width:"100%",background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,
                      padding:"8px 10px",color:"#e7edf5",fontFamily:"inherit",fontSize:12,outline:"none",boxSizing:"border-box"}}/>
                </div>
                <div style={{flex:1,minWidth:80}}>
                  <div style={{fontSize:12,color:"#8ea2bf",marginBottom:4}}>ZONE HIGH (₹)</div>
                  <input type="number" value={form.zone_high} onChange={e=>setForm(p=>({...p,zone_high:e.target.value}))}
                    placeholder="auto (+1%)"
                    style={{width:"100%",background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,
                      padding:"8px 10px",color:"#e7edf5",fontFamily:"inherit",fontSize:12,outline:"none",boxSizing:"border-box"}}/>
                </div>
              </div>
              <div>
                <div style={{fontSize:12,color:"#8ea2bf",marginBottom:4}}>NOTE (optional)</div>
                <input value={form.note} onChange={e=>setForm(p=>({...p,note:e.target.value}))}
                  placeholder="e.g. Strong weekly support, multiple touches"
                  style={{width:"100%",background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,
                    padding:"8px 10px",color:"#e7edf5",fontFamily:"inherit",fontSize:12,outline:"none",boxSizing:"border-box"}}/>
              </div>
              <button onClick={addManual}
                style={{background:"rgba(0,212,170,0.1)",border:"1px solid #92400e",borderRadius:0,
                  padding:"11px 0",color:"#e8b339",fontFamily:"inherit",fontSize:13,fontWeight:700,
                  cursor:"pointer"}}>
                + Add Zone
              </button>

              {/* Legend */}
              <div style={{background:"#0f1d30",borderRadius:0,padding:12,marginTop:4}}>
                <div style={{fontSize:12,color:"#8ea2bf",fontWeight:700,marginBottom:8}}>ZONE TYPES</div>
                {[
                  ["Support",    "#2ed47a", "Price bounced up multiple times. Buy near support."],
                  ["Resistance", "#ff5b6c", "Price rejected multiple times. Sell near resistance."],
                  ["Demand Zone","#e8b339", "High-volume support — institutional buying likely."],
                  ["Supply Zone","#ff5b6c", "High-volume resistance — institutional selling likely."],
                ].map(([t,c,d])=>(
                  <div key={t} style={{display:"flex",gap:8,marginBottom:6,alignItems:"flex-start"}}>
                    <div style={{width:10,height:10,borderRadius:2,background:c,flexShrink:0,marginTop:1}}/>
                    <div>
                      <span style={{fontSize:10,color:c,fontWeight:700}}>{t}: </span>
                      <span style={{fontSize:10,color:"#8ea2bf"}}>{d}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SignalSettingsModal({ params, onChange, onClose }) {
  const [local, setLocal] = useState({...DEFAULT_SIGNAL_PARAMS, ...params});
  const set = (k,v) => setLocal(p=>({...p,[k]:v}));
  const num = (k, label, min, max, step=1, color="#e7edf5") => (
    <div style={{marginBottom:8}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
        <span style={{fontSize:10,color:"#8ea2bf"}}>{label}</span>
        <span style={{fontSize:11,fontWeight:700,color}}>{local[k]}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={local[k]}
        onChange={e=>set(k, parseFloat(e.target.value))}
        style={{width:"100%",accentColor:color}}/>
      <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"#23405f"}}>
        <span>{min}</span><span>{max}</span>
      </div>
    </div>
  );

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",display:"flex",
      alignItems:"center",justifyContent:"center",zIndex:2000,fontFamily:"inherit"}}>
      <div style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:12,
        padding:20,width:460,maxHeight:"88vh",overflowY:"auto"}}>

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <span style={{fontSize:14,fontWeight:700,color:"#e7edf5"}}>⚙ Signal Settings</span>
          <button onClick={onClose} style={{background:"none",border:"none",color:"#8ea2bf",fontSize:18,cursor:"pointer"}}>✕</button>
        </div>

        {/* TSI */}
        <div style={{background:"#0b1624",borderRadius:0,padding:12,marginBottom:10}}>
          <div style={{fontSize:10,fontWeight:700,color:"#e8b339",letterSpacing:1,marginBottom:10}}>
            📈 TSI — True Strength Index
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
            {num("tsi_long",   "Long Period",   5,  50, 1, "#e8b339")}
            {num("tsi_short",  "Short Period",  3,  25, 1, "#e8b339")}
            {num("tsi_signal", "Signal Period", 3,  20, 1, "#e8b339")}
          </div>
          <div style={{fontSize:12,color:"#8ea2bf",marginTop:4}}>
            Default: 25 / 13 / 7 · Lower = more sensitive, more signals · Higher = smoother, fewer signals
          </div>
        </div>

        {/* RSI */}
        <div style={{background:"#0b1624",borderRadius:0,padding:12,marginBottom:10}}>
          <div style={{fontSize:10,fontWeight:700,color:"#6b7280",letterSpacing:1,marginBottom:10}}>
            📊 RSI — Relative Strength Index
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
            {num("rsi_period", "RSI Period",   5,  30, 1,  "#6b7280")}
            {num("rsi_ob",     "Overbought",  60, 100, 1,  "#ff5b6c")}
            {num("rsi_os",     "Oversold",     0,  40, 1,  "#2ed47a")}
          </div>
          <div style={{fontSize:12,color:"#8ea2bf",marginTop:4}}>
            Default: period 14 · OB 80 · OS 20 · Tighten thresholds (e.g. OB 70/OS 30) for more signals
          </div>
        </div>

        {/* Volume + RSI divergence */}
        <div style={{background:"#0b1624",borderRadius:0,padding:12,marginBottom:10}}>
          <div style={{fontSize:10,fontWeight:700,color:"#e8b339",letterSpacing:1,marginBottom:10}}>
            📦 Volume × RSI Divergence
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            {num("vol_lookback",  "Lookback Candles", 2,  20, 1, "#e8b339")}
            {num("vol_dom_pct",   "Dominance % (BUY/SELL side)", 40, 90, 5, "#e8b339")}
            {num("rsi_vol_high",  "High RSI (SELL zone)", 50, 90, 1, "#ff5b6c")}
          </div>
          <div style={{fontSize:12,color:"#8ea2bf",marginTop:4}}>
            4 rules: RSI≥50+BuyVol→BUY · RSI≥HighRSI+SellVol→SELL · RSI&lt;40+SellVol→SELL · RSI&lt;20+BuyVol→BUY (capitulation)
          </div>
        </div>

        {/* EMA */}
        <div style={{background:"#0b1624",borderRadius:0,padding:12,marginBottom:10}}>
          <div style={{fontSize:10,fontWeight:700,color:"#49c2e4",letterSpacing:1,marginBottom:10}}>
            📉 EMA Crossover
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            {num("ema_fast", "Fast EMA",  3,  25, 1, "#49c2e4")}
            {num("ema_slow", "Slow EMA", 20, 200, 5, "#49c2e4")}
          </div>
          <div style={{fontSize:12,color:"#8ea2bf",marginTop:4}}>
            Default: 9 / 50 · Common: 9/21 · 20/50 · 50/200 (golden cross)
          </div>
        </div>

        {/* Pattern Detection */}
        <div style={{background:"#0b1624",borderRadius:0,padding:12,marginBottom:10}}>
          <div style={{fontSize:10,fontWeight:700,color:"#f0c040",letterSpacing:1,marginBottom:10}}>
            📐 Chart Pattern Detection
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            {num("breakout_bars",  "Breakout Lookback",  5, 50, 1, "#f0c040")}
            {num("pivot_window",   "Pivot Window",        2, 15, 1, "#f0c040")}
            {num("dbl_tolerance",  "Double Top/Bot %",   0.01, 0.10, 0.01, "#f0c040")}
            {num("flag_move_pct",  "Flag Pole Min %",    0.01, 0.15, 0.01, "#f0c040")}
            {num("flag_bars",      "Flag Bars",           5,  20, 1, "#f0c040")}
          </div>
          <div style={{fontSize:12,color:"#8ea2bf",marginTop:4}}>
            Detects: Breakout · Breakdown · Double Top · Double Bottom · Bull/Bear Flag · Cup & Handle
          </div>
        </div>

        {/* MACD, BB, Stochastic config panels removed — scans 9/10/11 disabled */}

        {/* Trailing SL note */}
        <div style={{background:"#0b1624",borderRadius:0,padding:12,marginBottom:16,border:"1px solid #92400e33"}}>
          <div style={{fontSize:10,fontWeight:700,color:"#e8b339",marginBottom:4}}>
            🎯 Trailing SL (NAKED strategy only)
          </div>
          <div style={{fontSize:12,color:"#8ea2bf",lineHeight:1.8}}>
            Set Trail % in the Auto-Trade config panel of each tab.<br/>
            The SL moves up automatically as price rises, locking in profits.<br/>
            <span style={{color:"#2ed47a"}}>Entry ₹100 · Trail 2% → SL starts at ₹98 → price hits ₹110 → SL moves to ₹107.8</span>
          </div>
        </div>

        {/* Buttons */}
        <div style={{display:"flex",gap:8}}>
          <button onClick={()=>{ setLocal({...DEFAULT_SIGNAL_PARAMS}); }}
            style={{background:"transparent",border:"1px solid #23405f",borderRadius:0,
              padding:"8px 16px",color:"#8ea2bf",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>
            Reset Defaults
          </button>
          <button onClick={onClose}
            style={{background:"transparent",border:"1px solid #23405f",borderRadius:0,
              padding:"8px 16px",color:"#8ea2bf",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>
            Cancel
          </button>
          <button onClick={()=>{ onChange(local); onClose(); }}
            style={{flex:1,background:"linear-gradient(135deg,#92400e,#92400e)",border:"none",
              borderRadius:0,padding:"8px",color:"#fff",fontFamily:"inherit",
              fontSize:12,fontWeight:700,cursor:"pointer"}}>
            ✓ Apply Settings
          </button>
        </div>
      </div>
    </div>
  );
}

function StrategyConfigPanel({ cfg, onChange, compact=false }) {
  const s = cfg.strategyOverride || "AUTO";
  const instrMode = cfg.instrumentMode || "AUTO";
  return (
    <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
      {/* Instrument mode — FUTURES / OPTIONS / AUTO */}
      <div style={{display:"flex",gap:3,alignItems:"center"}}>
        <span style={{fontSize:12,color:"#8ea2bf",flexShrink:0}}>Instrument:</span>
        <div style={{display:"flex",background:"#07101c",border:"1px solid #1a2c44",borderRadius:0,overflow:"hidden"}}>
          {[["AUTO","🤖 Auto"],["FUTURES","📈 Futures"],["OPTIONS","🎯 Options"]].map(([k,l])=>(
            <button key={k} onClick={()=>onChange({...cfg,instrumentMode:k})}
              style={{background:instrMode===k?"rgba(0,212,170,0.15)":"transparent",border:"none",
                borderRight:"1px solid #1a2c44",padding:"3px 8px",
                color:instrMode===k?"#e8b339":"#8ea2bf",fontFamily:"inherit",
                fontSize:12,fontWeight:instrMode===k?700:400,cursor:"pointer",whiteSpace:"nowrap"}}>
              {l}
            </button>
          ))}
        </div>
      </div>
      {/* Strategy — only show when OPTIONS mode */}
      {instrMode!=="FUTURES"&&(
      <div style={{display:"flex",gap:3,alignItems:"center"}}>
        <span style={{fontSize:12,color:"#8ea2bf",flexShrink:0}}>Strategy:</span>
        <div style={{display:"flex",background:"#07101c",border:"1px solid #1a2c44",borderRadius:0,overflow:"hidden"}}>
          {Object.entries(STRATEGIES).map(([k,v])=>(
            <button key={k} onClick={()=>onChange({...cfg,strategyOverride:k})}
              style={{background:s===k?`${v.color}22`:"transparent",border:"none",
                borderRight:"1px solid #1a2c44",padding:"3px 8px",
                color:s===k?v.color:"#8ea2bf",fontFamily:"inherit",
                fontSize:12,fontWeight:s===k?700:400,cursor:"pointer",whiteSpace:"nowrap"}}>
              {v.label}
            </button>
          ))}
        </div>
      </div>
      )}
      {instrMode!=="FUTURES"&&s==="AUTO"&&(
        <div style={{display:"flex",gap:3,alignItems:"center"}}>
          <span style={{fontSize:12,color:"#8ea2bf"}}>IV:</span>
          {[null,"LOW","MEDIUM","HIGH"].map(v=>(
            <button key={String(v)} onClick={()=>onChange({...cfg,ivOverride:v})}
              style={{background:cfg.ivOverride===v?"rgba(0,212,170,0.15)":"transparent",
                border:"1px solid "+(cfg.ivOverride===v?"#e8b339":"#1a2c44"),
                borderRadius:0,padding:"2px 6px",
                color:cfg.ivOverride===v?"#e8b339":IV_COLORS[v||"UNKNOWN"],
                fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
              {v||"🤖 Detect"}
            </button>
          ))}
        </div>
      )}
      {instrMode!=="FUTURES"&&(s==="SPREAD"||s==="STRANGLE")&&(
        <div style={{display:"flex",gap:3,alignItems:"center"}}>
          <span style={{fontSize:12,color:"#8ea2bf"}}>OTM steps:</span>
          {[1,2,3,4].map(n=>(
            <button key={n} onClick={()=>onChange({...cfg,spreadSteps:n})}
              style={{background:(cfg.spreadSteps||2)===n?"rgba(96,165,250,0.2)":"transparent",
                border:"1px solid "+((cfg.spreadSteps||2)===n?"#e8b339":"#1a2c44"),
                borderRadius:0,padding:"2px 6px",color:(cfg.spreadSteps||2)===n?"#e8b339":"#8ea2bf",
                fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
              {n}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StrategyPreviewCard({ result, onConfirm, onCancel, placing }) {
  if (!result) return null;
  const { strategy, iv_rank, hv, underlying_ltp, atm_strike, expiry,
          legs, net_premium, max_loss, max_profit, symbol } = result;
  const stratInfo = STRATEGIES[strategy] || STRATEGIES.NAKED;
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",display:"flex",
      alignItems:"center",justifyContent:"center",zIndex:2000,fontFamily:"inherit"}}>
      <div style={{background:"#0f1d30",border:`1px solid ${stratInfo.color}44`,
        borderRadius:12,padding:20,width:420,maxHeight:"85vh",overflowY:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <div>
            <div style={{fontSize:14,fontWeight:700,color:stratInfo.color}}>{stratInfo.label} — {symbol}</div>
            <div style={{fontSize:10,color:"#8ea2bf",marginTop:2}}>{stratInfo.desc}</div>
          </div>
          <button onClick={onCancel} style={{background:"none",border:"none",color:"#8ea2bf",fontSize:18,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:6,marginBottom:14}}>
          {[["Underlying",`₹${underlying_ltp}`,"#e7edf5"],["ATM Strike",`₹${atm_strike}`,"#6b7280"],
            ["IV Rank",iv_rank||"—",IV_COLORS[iv_rank||"UNKNOWN"]],["HV",hv?`${hv}%`:"—","#8ea2bf"],
          ].map(([l,v,c])=>(
            <div key={l} style={{background:"#0b1624",borderRadius:0,padding:"7px 8px"}}>
              <div style={{fontSize:11,color:"#8ea2bf",marginBottom:2}}>{l}</div>
              <div style={{fontSize:11,fontWeight:700,color:c}}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{marginBottom:14}}>
          <div style={{fontSize:12,color:"#8ea2bf",letterSpacing:1,marginBottom:6}}>ORDER LEGS</div>
          {(legs||[]).map((leg,i)=>(
            <div key={i} style={{background:"#0b1624",borderRadius:0,padding:"8px 10px",
              marginBottom:5,display:"flex",justifyContent:"space-between",alignItems:"center",
              borderLeft:`3px solid ${leg.action==="BUY"?"#2ed47a":"#ff5b6c"}`}}>
              <div>
                <span style={{fontSize:11,fontWeight:700,color:leg.action==="BUY"?"#2ed47a":"#ff5b6c",marginRight:8}}>{leg.action}</span>
                <span style={{fontSize:11,color:"#e7edf5"}}>{leg.tradingsymbol}</span>
                <span style={{fontSize:12,color:"#8ea2bf",marginLeft:6}}>
                  {leg.instrument_type} K{leg.strike} {leg.expiry} ({leg.days_to_expiry}d)
                </span>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:12,fontWeight:700,color:"#e7edf5"}}>₹{leg.option_ltp||"—"}</div>
                <div style={{fontSize:12,color:"#8ea2bf"}}>lot {leg.lot_size}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,marginBottom:16}}>
          {[["Net Premium",`₹${net_premium}`,"#6b7280"],
            ["Max Loss",typeof max_loss==="number"?`₹${max_loss}`:"—","#ff5b6c"],
            ["Max Profit",max_profit==="Unlimited"?"∞ Unlimited":`₹${max_profit}`,"#2ed47a"],
          ].map(([l,v,c])=>(
            <div key={l} style={{background:"#0b1624",borderRadius:0,padding:"8px 10px",textAlign:"center"}}>
              <div style={{fontSize:11,color:"#8ea2bf",marginBottom:3}}>{l}</div>
              <div style={{fontSize:11,fontWeight:700,color:c}}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{fontSize:12,color:"#8ea2bf",marginBottom:14,textAlign:"center"}}>
          Expiry: {expiry} · {(legs||[])[0]?.days_to_expiry||"?"} days left
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={onCancel} disabled={placing}
            style={{flex:1,background:"transparent",border:"1px solid #23405f",borderRadius:0,
              padding:"10px",color:"#8ea2bf",fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={placing||!(legs&&legs.length>0)}
            style={{flex:2,background:`linear-gradient(135deg,${stratInfo.color}88,${stratInfo.color})`,
              border:"none",borderRadius:0,padding:"10px",color:"#fff",
              fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:placing?"default":"pointer"}}>
            {placing?"Placing...":"✓ Place "+strategy+" ("+(legs||[]).length+" legs)"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// INSTITUTIONAL DATA — hook + badge component for scan results
// ══════════════════════════════════════════════════════════════════════════════

// Cache institutional data globally so it's fetched once per session
const _instCache = { data: null, fetchedAt: null };

function useInstitutionalData() {
  const [instData,  setInstData]  = useState(null);
  const [fiiDii,    setFiiDii]    = useState(null);
  const [partOI,    setPartOI]    = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [lastFetch, setLastFetch] = useState(null);

  const fetchAll = useCallback(async(force=false)=>{
    // Use in-memory cache (avoid re-fetching on every tab switch)
    if(!force && _instCache.data && _instCache.fetchedAt &&
       (Date.now() - _instCache.fetchedAt) < 3600000) {
      setInstData(_instCache.data);
      setFiiDii(_instCache.data.fiiDii);
      setPartOI(_instCache.data.partOI);
      return;
    }
    setLoading(true);
    try {
      const [fii, bulk, block, poi] = await Promise.all([
        apiFetch("/nse/fii-dii"),
        apiFetch("/nse/bulk-deals"),
        apiFetch("/nse/block-deals"),
        apiFetch("/nse/participant-oi"),
      ]);
      // Build per-symbol lookup from bulk+block deals
      const dealMap = {};
      const addDeal = (list, type) => {
        (list?.data||[]).forEach(d=>{
          const sym = d.symbol?.toUpperCase();
          if(!sym) return;
          if(!dealMap[sym]) dealMap[sym] = { bulk:[], block:[] };
          dealMap[sym][type].push(d);
        });
      };
      addDeal(bulk, "bulk");
      addDeal(block, "block");
      const compiled = { fiiDii: fii, partOI: poi, dealMap };
      _instCache.data = compiled;
      _instCache.fetchedAt = Date.now();
      setInstData(compiled);
      setFiiDii(fii);
      setPartOI(poi);
      setLastFetch(new Date());
    } catch(e){ console.warn("Inst data fetch failed:", e.message); }
    finally{ setLoading(false); }
  },[]);

  // Fetch delivery % for a specific symbol on demand
  const fetchDelivery = useCallback(async(symbol)=>{
    try {
      const d = await apiFetch(`/nse/delivery?symbol=${symbol}`);
      return d?.delivery_pct ?? null;
    } catch { return null; }
  },[]);

  return { instData, fiiDii, partOI, loading, lastFetch, fetchAll, fetchDelivery };
}

// ── Institutional badge shown in scan result card ─────────────────────────
function InstBadge({ symbol, instData }) {
  const [delivPct, setDelivPct] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const deals    = instData?.dealMap?.[symbol?.toUpperCase()] || {};
  const bulkList = deals.bulk  || [];
  const blockList= deals.block || [];
  const hasDeal  = bulkList.length > 0 || blockList.length > 0;

  // Fetch delivery % lazily when user expands
  useEffect(()=>{
    if(!expanded || delivPct!==null || !symbol) return;
    apiFetch(`/nse/delivery?symbol=${symbol}`)
      .then(d=>setDelivPct(d?.delivery_pct ?? "—"))
      .catch(()=>setDelivPct("—"));
  },[expanded, symbol]);

  if(!instData) return null;

  return (
    <div style={{marginTop:4}}>
      {/* Summary badges */}
      <div style={{display:"flex",gap:4,flexWrap:"wrap",alignItems:"center"}}>
        {hasDeal&&(
          <span style={{fontSize:11,padding:"1px 5px",borderRadius:0,fontWeight:700,
            background:"rgba(163,113,247,0.15)",border:"1px solid rgba(163,113,247,0.4)",
            color:"#49c2e4",cursor:"pointer"}}
            onClick={()=>setExpanded(p=>!p)}>
            🏦 {bulkList.length>0?`${bulkList.length} Bulk`:""}{blockList.length>0?` ${blockList.length} Block`:""}
          </span>
        )}
        {!hasDeal&&(
          <span style={{fontSize:11,color:"#5a728f"}}>No bulk/block deals</span>
        )}
        <span onClick={()=>setExpanded(p=>!p)}
          style={{fontSize:11,color:"#5a728f",cursor:"pointer",marginLeft:2}}>
          {expanded?"▲":"▼ details"}
        </span>
      </div>

      {/* Expanded institutional detail */}
      {expanded&&(
        <div style={{marginTop:6,background:"#0b1624",borderRadius:0,padding:"8px 10px",
          border:"1px solid #1a2c44",fontSize:12}}>

          {/* Delivery % */}
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:6,
            paddingBottom:6,borderBottom:"1px solid #1a2c44"}}>
            <span style={{color:"#8ea2bf"}}>Delivery %</span>
            <span style={{fontWeight:700,
              color:delivPct>=60?"#2ed47a":delivPct>=40?"#e8b339":delivPct!=null?"#ff5b6c":"#8ea2bf"}}>
              {delivPct!=null?`${delivPct}%`:"Loading..."}
            </span>
          </div>

          {/* Bulk deals */}
          {bulkList.length>0&&(
            <div style={{marginBottom:6}}>
              <div style={{color:"#49c2e4",fontWeight:700,marginBottom:3}}>BULK DEALS</div>
              {bulkList.slice(0,3).map((d,i)=>(
                <div key={i} style={{display:"flex",justifyContent:"space-between",
                  color:"#5c3d2e",marginBottom:2}}>
                  <span style={{color:"#8ea2bf",maxWidth:120,overflow:"hidden",
                    textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{d.client}</span>
                  <span style={{color:d.side==="B"?"#2ed47a":"#ff5b6c",fontWeight:700,marginLeft:4}}>
                    {d.side==="B"?"BUY":"SELL"} {d.qty}@₹{d.price}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Block deals */}
          {blockList.length>0&&(
            <div style={{marginBottom:6}}>
              <div style={{color:"#6b7280",fontWeight:700,marginBottom:3}}>BLOCK DEALS</div>
              {blockList.slice(0,3).map((d,i)=>(
                <div key={i} style={{display:"flex",justifyContent:"space-between",
                  color:"#5c3d2e",marginBottom:2}}>
                  <span style={{color:"#8ea2bf",maxWidth:120,overflow:"hidden",
                    textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{d.client}</span>
                  <span style={{color:d.side==="B"?"#2ed47a":"#ff5b6c",fontWeight:700,marginLeft:4}}>
                    {d.side==="B"?"BUY":"SELL"} {d.qty}@₹{d.price}
                  </span>
                </div>
              ))}
            </div>
          )}

          {!hasDeal&&<div style={{color:"#5a728f"}}>No institutional deals today</div>}
        </div>
      )}
    </div>
  );
}

// ── Delivery % table cell (lazy loaded) ─────────────────────────────────
function DelivCell({ symbol, instData }) {
  const [pct, setPct] = useState(null);
  const [tried, setTried] = useState(false);

  useEffect(()=>{
    if(tried || !symbol) return;
    setTried(true);
    apiFetch(`/nse/delivery?symbol=${symbol.toUpperCase()}`)
      .then(d=>setPct(d?.delivery_pct ?? "—"))
      .catch(()=>setPct("—"));
  },[symbol]);

  if(pct===null) return <span style={{fontSize:12,color:"#5a728f"}}>...</span>;
  const num = parseFloat(pct);
  const color = isNaN(num)?"#5a728f":num>=60?"#2ed47a":num>=40?"#e8b339":"#ff5b6c";
  return <span style={{fontSize:10,fontWeight:700,color}}>{isNaN(num)?pct:`${num}%`}</span>;
}

// ── Market-wide FII/DII + Participant OI bar ──────────────────────────────
function FiiDiiBar({ fiiDii, partOI, loading, lastFetch, onRefresh }) {
  if(!fiiDii && !loading) return (
    <div style={{padding:"8px 14px",background:"#0b1624",borderBottom:"1px solid #1a2c44",
      display:"flex",alignItems:"center",gap:8}}>
      <span style={{fontSize:10,color:"#8ea2bf"}}>📊 Institutional data not loaded</span>
      <button onClick={onRefresh}
        style={{background:"rgba(0,212,170,0.1)",border:"1px solid rgba(0,212,170,0.3)",
          borderRadius:0,padding:"3px 10px",color:"#e8b339",fontFamily:"inherit",
          fontSize:12,fontWeight:700,cursor:"pointer"}}>
        Load Data
      </button>
    </div>
  );

  const fnet = fiiDii?.fii_net;
  const dnet = fiiDii?.dii_net;
  const fiiUp = fnet >= 0;
  const diiUp = dnet >= 0;

  return (
    <div style={{padding:"6px 12px",background:"#090d13",borderBottom:"1px solid #161b22",
      display:"flex",gap:0,overflowX:"auto",alignItems:"center",flexShrink:0,scrollbarWidth:"none"}}>

      {loading&&<span style={{fontSize:12,color:"#8ea2bf",marginRight:8}}>⟳ Loading...</span>}

      {fiiDii&&!fiiDii.error&&[
        ["FII Net", fnet, fiiUp],
        ["DII Net", dnet, diiUp],
      ].map(([label,val,up])=>(
        <div key={label} style={{padding:"2px 12px",borderRight:"1px solid #161b22",flexShrink:0}}>
          <div style={{fontSize:11,color:"#8ea2bf",letterSpacing:1}}>{label}</div>
          <div style={{fontSize:11,fontWeight:700,color:up?"#2ed47a":"#ff5b6c"}}>
            {val!=null?`${up?"+":""}₹${Math.abs(val/100).toFixed(0)}Cr`:"—"}
          </div>
        </div>
      ))}

      {partOI&&!partOI.error&&(
        <div style={{padding:"2px 12px",borderRight:"1px solid #161b22",flexShrink:0}}>
          <div style={{fontSize:11,color:"#8ea2bf",letterSpacing:1}}>FII F&O NET</div>
          <div style={{fontSize:11,fontWeight:700,
            color:(partOI.fii?.net||0)>=0?"#2ed47a":"#ff5b6c"}}>
            {partOI.fii?.net!=null?`${partOI.fii.net>=0?"+":""}${partOI.fii.net}`:  "—"}
          </div>
        </div>
      )}

      {fiiDii?.date&&(
        <div style={{padding:"2px 10px",flexShrink:0}}>
          <div style={{fontSize:11,color:"#5a728f"}}>as of {fiiDii.date}</div>
        </div>
      )}

      <button onClick={onRefresh}
        style={{marginLeft:"auto",background:"none",border:"none",
          color:"#5a728f",fontSize:12,cursor:"pointer",flexShrink:0,padding:"2px 6px"}}>
        ↻
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// QUICK OPTIONS MODAL — opens from scan result for index symbols
// ══════════════════════════════════════════════════════════════════════════════
function QuickOptionsModal({ symbol, ltp, onTrade, onClose }) {
  const [chain,       setChain]       = useState([]);
  const [quotes,      setQuotes]      = useState({});
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [expiry,      setExpiry]      = useState("");
  const [expiries,    setExpiries]    = useState([]);
  const [strikes,     setStrikes]     = useState([]);
  const [quoteLoading,setQL]          = useState(false);

  // Normalize index name for chain fetch (NIFTY 50 → NIFTY, BANKNIFTY → BANKNIFTY)
  const chainSym = useMemo(()=>{
    const map = {
      "NIFTY 50":"NIFTY","NIFTY50":"NIFTY","NIFTY":"NIFTY",
      "BANKNIFTY":"BANKNIFTY","BANK NIFTY":"BANKNIFTY",
      "FINNIFTY":"FINNIFTY","NIFTY FIN SERVICE":"FINNIFTY",
      "MIDCPNIFTY":"MIDCPNIFTY","NIFTY IT":"NIFTYIT","NIFTYIT":"NIFTYIT",
      "SENSEX":"SENSEX",
    };
    return map[symbol.toUpperCase().trim()] || symbol.toUpperCase().trim();
  },[symbol]);

  // Fetch chain on mount
  useEffect(()=>{
    setLoading(true); setError(null);
    apiFetch(`/fo/chain/NSE/${chainSym}?min_days=0`)
      .then(data=>{
        const rows = data.chain || [];
        setChain(rows);
        // Get sorted expiries
        const exps = [...new Set(rows.map(r=>r.expiry))].sort();
        setExpiries(exps);
        if(exps.length>0) setExpiry(exps[0]);
      })
      .catch(e=>setError(e.message))
      .finally(()=>setLoading(false));
  },[chainSym]);

  // Filter by expiry, find ATM ±7 strikes
  useEffect(()=>{
    if(!expiry || chain.length===0) return;
    const filtered = chain.filter(r=>r.expiry===expiry && (r.instrument_type==="CE"||r.instrument_type==="PE"));
    const allStrikes = [...new Set(filtered.map(r=>r.strike))].sort((a,b)=>a-b);
    // ATM = strike closest to LTP
    const atm = allStrikes.reduce((best,s)=>Math.abs(s-ltp)<Math.abs(best-ltp)?s:best, allStrikes[0]||0);
    const atmIdx = allStrikes.indexOf(atm);
    const range = allStrikes.slice(Math.max(0,atmIdx-7), atmIdx+8);
    setStrikes(range);
    // Fetch quotes for these strikes
    const contracts = filtered.filter(r=>range.includes(r.strike));
    if(contracts.length===0) return;
    setQL(true);
    // Batch into chunks of 10 to avoid URL length limit / connection drops
    const allSyms = contracts.slice(0,40).map(c=>`NFO:${c.tradingsymbol}`);
    const chunks  = [];
    for(let i=0;i<allSyms.length;i+=10) chunks.push(allSyms.slice(i,i+10));
    Promise.all(
      chunks.map(chunk=>apiFetch(`/quotes?symbols=${encodeURIComponent(chunk.join(','))}`).catch(()=>({})))
    ).then(results=>{
      const merged = Object.assign({}, ...results);
      setQuotes(merged);
    }).catch(()=>{}).finally(()=>setQL(false));
  },[expiry, chain, ltp]);

  const getQ = (type, strike)=>{
    const row = chain.find(r=>r.expiry===expiry&&r.instrument_type===type&&r.strike===strike);
    if(!row) return null;
    return quotes[`NFO:${row.tradingsymbol}`] || null;
  };
  const getLot = ()=> chain.find(r=>r.expiry===expiry)?.lot_size || 50;

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:600,
      display:"flex",alignItems:"flex-end",justifyContent:"center"}}
      onClick={onClose}>
      <div style={{background:"#0b1624",border:"1px solid #23405f",borderRadius:"16px 16px 0 0",
        width:"100%",maxWidth:640,maxHeight:"85vh",display:"flex",flexDirection:"column",
        paddingBottom:"env(safe-area-inset-bottom,0)"}}
        onClick={e=>e.stopPropagation()}>

        {/* Header */}
        <div style={{padding:"16px 16px 12px",borderBottom:"1px solid #1a2c44",
          display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
          <div>
            <div style={{fontSize:14,fontWeight:700,color:"#e7edf5"}}>{symbol} Options</div>
            <div style={{fontSize:10,color:"#8ea2bf"}}>Spot: <span style={{color:"#e7edf5",fontWeight:600}}>₹{ltp?.toLocaleString("en-IN",{maximumFractionDigits:2})}</span> · Lot: {getLot()}</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:"#8ea2bf",fontSize:20,cursor:"pointer",padding:"4px 8px"}}>✕</button>
        </div>

        {/* Expiry selector */}
        <div style={{padding:"10px 16px",borderBottom:"1px solid #1a2c44",
          display:"flex",gap:6,overflowX:"auto",flexShrink:0,scrollbarWidth:"none"}}>
          {expiries.slice(0,6).map(exp=>(
            <button key={exp} onClick={()=>setExpiry(exp)}
              style={{background:expiry===exp?"rgba(0,212,170,0.15)":"#0f1d30",
                border:`1px solid ${expiry===exp?"#e8b339":"#23405f"}`,
                borderRadius:0,padding:"5px 10px",color:expiry===exp?"#e8b339":"#8ea2bf",
                fontFamily:"inherit",fontSize:10,fontWeight:600,cursor:"pointer",
                whiteSpace:"nowrap",flexShrink:0}}>
              {exp}
            </button>
          ))}
        </div>

        {/* Chain header */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 80px 80px 80px 1fr",
          padding:"6px 16px",background:"#090d13",flexShrink:0}}>
          <div style={{fontSize:12,color:"#2ed47a",fontWeight:700,textAlign:"center"}}>CALL (CE)</div>
          <div style={{fontSize:12,color:"#8ea2bf",textAlign:"center"}}>IV</div>
          <div style={{fontSize:12,color:"#8ea2bf",textAlign:"center",fontWeight:700}}>STRIKE</div>
          <div style={{fontSize:12,color:"#8ea2bf",textAlign:"center"}}>IV</div>
          <div style={{fontSize:12,color:"#ff5b6c",fontWeight:700,textAlign:"center"}}>PUT (PE)</div>
        </div>

        {/* Chain body */}
        <div style={{flex:1,overflowY:"auto",WebkitOverflowScrolling:"touch"}}>
          {loading && <div style={{textAlign:"center",padding:40,color:"#8ea2bf",fontSize:12}}>Loading chain...</div>}
          {error   && <div style={{textAlign:"center",padding:20,color:"#ff5b6c",fontSize:11}}>{error}</div>}
          {!loading && !error && strikes.map(strike=>{
            const ce = getQ("CE",strike);
            const pe = getQ("PE",strike);
            const ceLtp = ce?.last_price || 0;
            const peLtp = pe?.last_price || 0;
            const ceChg = ce?.net_change || 0;
            const peChg = pe?.net_change || 0;
            const isATM = Math.abs(strike - ltp) < (strikes[1]-strikes[0])/2;
            const ceRow = chain.find(r=>r.expiry===expiry&&r.instrument_type==="CE"&&r.strike===strike);
            const peRow = chain.find(r=>r.expiry===expiry&&r.instrument_type==="PE"&&r.strike===strike);

            return (
              <div key={strike} style={{
                display:"grid",gridTemplateColumns:"1fr 80px 80px 80px 1fr",
                padding:"7px 16px",
                borderBottom:"1px solid #161b22",
                background:isATM?"rgba(0,212,170,0.05)":"transparent",
                alignItems:"center"}}>

                {/* CE side */}
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,fontWeight:700,color:"#2ed47a"}}>
                      {ceLtp ? `₹${ceLtp}` : quoteLoading ? "..." : "—"}
                    </div>
                    <div style={{fontSize:12,color:ceChg>=0?"#2ed47a":"#ff5b6c"}}>
                      {ceChg>=0?"+":""}{ceChg?.toFixed(1)||""}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:3}}>
                    <button onClick={()=>{
                      if(!ceRow) return;
                      onTrade({symbol:ceRow.tradingsymbol,exchange:"NFO",side:"BUY",
                        price:ceLtp||0,qty:ceRow.lot_size||50,instrumentType:"CE"});
                      onClose();
                    }} style={{background:"rgba(22,163,74,0.15)",border:"1px solid rgba(63,185,80,0.4)",
                      borderRadius:0,padding:"3px 7px",color:"#2ed47a",fontFamily:"inherit",
                      fontSize:12,fontWeight:700,cursor:"pointer",minHeight:28,minWidth:32}}>B</button>
                    <button onClick={()=>{
                      if(!ceRow) return;
                      onTrade({symbol:ceRow.tradingsymbol,exchange:"NFO",side:"SELL",
                        price:ceLtp||0,qty:ceRow.lot_size||50,instrumentType:"CE"});
                      onClose();
                    }} style={{background:"rgba(248,81,73,0.1)",border:"1px solid rgba(248,81,73,0.3)",
                      borderRadius:0,padding:"3px 7px",color:"#ff5b6c",fontFamily:"inherit",
                      fontSize:12,fontWeight:700,cursor:"pointer",minHeight:28,minWidth:32}}>S</button>
                  </div>
                </div>

                {/* CE IV */}
                <div style={{textAlign:"center",fontSize:12,color:"#8ea2bf"}}>
                  {ce?.oi ? `${(ce.oi/1000).toFixed(0)}K` : "—"}
                </div>

                {/* Strike — ATM highlighted */}
                <div style={{textAlign:"center",fontWeight:700,
                  fontSize:isATM?13:11,
                  color:isATM?"#e8b339":"#5c3d2e",
                  background:isATM?"rgba(0,212,170,0.12)":"transparent",
                  borderRadius:0,padding:"2px 0"}}>
                  {strike.toLocaleString("en-IN")}
                  {isATM&&<div style={{fontSize:7,color:"#e8b339",letterSpacing:1}}>ATM</div>}
                </div>

                {/* PE IV */}
                <div style={{textAlign:"center",fontSize:12,color:"#8ea2bf"}}>
                  {pe?.oi ? `${(pe.oi/1000).toFixed(0)}K` : "—"}
                </div>

                {/* PE side */}
                <div style={{display:"flex",alignItems:"center",gap:6,justifyContent:"flex-end"}}>
                  <div style={{display:"flex",gap:3}}>
                    <button onClick={()=>{
                      if(!peRow) return;
                      onTrade({symbol:peRow.tradingsymbol,exchange:"NFO",side:"BUY",
                        price:peLtp||0,qty:peRow.lot_size||50,instrumentType:"PE"});
                      onClose();
                    }} style={{background:"rgba(22,163,74,0.15)",border:"1px solid rgba(63,185,80,0.4)",
                      borderRadius:0,padding:"3px 7px",color:"#2ed47a",fontFamily:"inherit",
                      fontSize:12,fontWeight:700,cursor:"pointer",minHeight:28,minWidth:32}}>B</button>
                    <button onClick={()=>{
                      if(!peRow) return;
                      onTrade({symbol:peRow.tradingsymbol,exchange:"NFO",side:"SELL",
                        price:peLtp||0,qty:peRow.lot_size||50,instrumentType:"PE"});
                      onClose();
                    }} style={{background:"rgba(248,81,73,0.1)",border:"1px solid rgba(248,81,73,0.3)",
                      borderRadius:0,padding:"3px 7px",color:"#ff5b6c",fontFamily:"inherit",
                      fontSize:12,fontWeight:700,cursor:"pointer",minHeight:28,minWidth:32}}>S</button>
                  </div>
                  <div style={{flex:1,textAlign:"right"}}>
                    <div style={{fontSize:12,fontWeight:700,color:"#ff5b6c"}}>
                      {peLtp ? `₹${peLtp}` : quoteLoading ? "..." : "—"}
                    </div>
                    <div style={{fontSize:12,color:peChg>=0?"#2ed47a":"#ff5b6c"}}>
                      {peChg>=0?"+":""}{peChg?.toFixed(1)||""}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer hint */}
        <div style={{padding:"8px 16px",borderTop:"1px solid #1a2c44",flexShrink:0,
          display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontSize:12,color:"#5a728f"}}>OI shown in thousands · ATM highlighted in teal</span>
          {quoteLoading&&<span style={{fontSize:12,color:"#8ea2bf"}}>⟳ Loading quotes...</span>}
        </div>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// AI PANEL — reusable component embedded in any tab
// ══════════════════════════════════════════════════════════════════════════════
function useAIPanel({ authed, signalParams, exchange="NSE" }) {
  const [aiResults,   setAiResults]   = useState([]);
  const [aiLoading,   setAiLoading]   = useState(false);
  const [aiError,     setAiError]     = useState(null);
  const [aiVisible,   setAiVisible]   = useState(false);
  const [lastAiScan,  setLastAiScan]  = useState(null);
  const [autoTrade,   setAutoTrade]   = useState(false);
  const [autoFired,   setAutoFired]   = useState({});
  const autoFiredRef = useRef({});
  useEffect(()=>{ autoFiredRef.current = autoFired; },[autoFired]);

  // ── Live LTP refresh for AI scan results every 5s ─────────────────────────
  useEffect(()=>{
    if(!authed || !aiResults.length) return;
    const MCX_SYMS = new Set(["CRUDEOIL","CRUDEOILM","GOLD","GOLDM","SILVER","SILVERM",
      "NATURALGAS","NATURALGASM","COPPER","ZINC","LEAD","ALUMINIUM","NICKEL"]);
    const refresh = async()=>{
      try {
        if(!aiResults.length) return;
        const quoteSyms = [...new Set(aiResults.map(r=>r.resolved_symbol||r.symbol).filter(Boolean))].slice(0,20);

        // Key rule: FO underlyings (NIFTY, BANKNIFTY etc) live on NSE not NFO
        // MCX futures use MCX: with resolved_symbol
        // Options/futures contracts (CE/PE/FUT suffix) use NFO:
        const keys = quoteSyms.map(s => {
          const su = s.toUpperCase();
          const base = su.replace(/\d+[A-Z]{3}(FUT|CE|PE)$/,""); // strip expiry suffix
          if(exchange==="MCX" || MCX_SYMS.has(base)) return `MCX:${s}`;
          if(su.match(/(CE|PE|FUT)$/) && !MCX_SYMS.has(base)) return `NFO:${s}`;
          return `NSE:${s}`;
        }).join(",");

        const q = await apiFetch(`/quotes?symbols=${encodeURIComponent(keys)}`);

        setAiResults(prev=>prev.map(r=>{
          const sym1 = r.resolved_symbol || r.symbol;
          const sym2 = r.symbol;
          // Try multiple key formats — quotes strips exchange prefix
          const live = q[sym1] || q[sym2]
                    || q[sym1?.replace(" ","")] || q[sym2?.replace(" ","")];
          if(!live) return r;
          const livePrice = parseFloat(live.ltp || live.last_price || 0);
          if(!livePrice) return r;
          return {...r, ltp: livePrice, price: livePrice};
        }));
      } catch {}
    };
    refresh();
    const t = setInterval(refresh, 5000); // 5s — matches scanner refresh rate
    return ()=>clearInterval(t);
  },[authed, aiResults.length, exchange]);
  useEffect(()=>{
    const today  = new Date().toISOString().slice(0,10);
    const key    = 'nse_pro_ai_fired_date';
    const stored = sessionStorage.getItem(key);
    if(stored !== today) {
      sessionStorage.setItem(key, today);
      setAutoFired({});
    }
  },[]);
  const [minAutoConf, setMinAutoConf] = useLocalStorage('nse_pro_ai_min_conf', 75);
  const [marginPct,   setMarginPct]   = useLocalStorage('nse_pro_ai_margin_pct', 10);
  const [maxDaily,    setMaxDaily]    = useLocalStorage('nse_pro_ai_max_daily', 5);
  const [maxScans,    setMaxScans]    = useLocalStorage('nse_pro_ai_max_scans', 0);
  const [scanCount,   setScanCount]   = useLocalStorage('nse_pro_ai_scan_count',
    {date:'', count:0});                                  // daily scan counter
  const [balance,     setBalance]     = useState(null);   // {equity, commodity} from Kite

  // Fetch balance on mount when authed
  useEffect(()=>{
    if(!authed) return;
    if(exchange==="DELTA") {
      // Use backend proxy (deltaFetch equivalent) — Delta needs session auth
      apiFetch('/delta/risk')
        .then(d=>setBalance({_delta:true, available_balance:d.available_balance||0}))
        .catch(()=>{
          // Fallback: try direct deltaFetch path
          fetch(`${API_BASE}/delta/risk`)
            .then(r=>r.json())
            .then(d=>setBalance({_delta:true, available_balance:d.available_balance||0}))
            .catch(()=>{});
        });
    } else {
      apiFetch('/user/margins').then(d=>setBalance(d)).catch(()=>{});
    }
  },[authed, exchange]);

  // Increment scan counter (returns false if limit reached)
  const checkScanLimit = useCallback(()=>{
    if(!maxScans || maxScans === 0) return true; // unlimited
    const today = new Date().toISOString().slice(0,10);
    const rec   = scanCount.date === today ? scanCount : {date:today, count:0};
    if(rec.count >= maxScans) return false;
    setScanCount({date:today, count: rec.count + 1});
    return true;
  },[maxScans, scanCount, setScanCount]);
  const autoTradeRef  = useRef(false);
  const marginPctRef  = useRef(10);
  const maxDailyRef   = useRef(5);
  useEffect(()=>{ autoTradeRef.current = autoTrade; },[autoTrade]);
  useEffect(()=>{ marginPctRef.current = marginPct; },[marginPct]);
  useEffect(()=>{ maxDailyRef.current  = maxDaily;  },[maxDaily]);

  const runAIScan = useCallback(async(symbols, exchange="NSE", timeframe="15min", onTrade=null, tabMode="equity", onShowToast=null, onAddNote=null, onJournalEntry=null, sendTgAlert=null, isAdmin=false)=>{
    if(!authed || !symbols?.length) return;
    // Check daily scan limit
    if(!checkScanLimit()) {
      if(onShowToast) onShowToast(`Daily scan limit reached (${maxScans}/day). Change limit in AI settings.`, "error");
      return;
    }
    // Refresh balance before scan so margin check is fresh
    if(exchange==="DELTA") {
      apiFetch('/delta/risk').then(d=>setBalance({_delta:true,available_balance:d.available_balance||0})).catch(()=>{});
    } else {
      apiFetch('/user/margins').then(d=>setBalance(d)).catch(()=>{});
    }
    setAiLoading(true); setAiError(null); setAiVisible(true);
    try {
      // Async job pattern — avoids Cloudflare 100s timeout
      const startR = await apiFetch("/ai/scan/start", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          symbols, exchange, timeframe,
          signal_params: signalParams || DEFAULT_SIGNAL_PARAMS,
        })
      });
      const aiJob = startR?.job_id;
      if(!aiJob) throw new Error("Server did not return a job ID");
      // Poll until done (max 5 minutes)
      const _aPollStart = Date.now();
      let aiResp = null;
      while(Date.now() - _aPollStart < 300_000) {
        await new Promise(r=>setTimeout(r,3000));
        const p = await apiFetch(`/ai/scan/result/${aiJob}`);
        if(p?.status==="done") { aiResp = p.result; break; }
        if(p?.status==="error") throw new Error(p.error||"AI scan failed on server");
        // Show live progress from backend
        if(p?.progress && onShowToast) onShowToast(`⟳ ${p.progress}`, "info");
      }
      if(!aiResp) throw new Error("AI scan timed out after 5 minutes");
      const results = aiResp.results || [];
      setAiResults(results);
      setLastAiScan(new Date());

      // ── Telegram alert for AI scan BUY/SELL results ───────────────────────
      if(sendTgAlert && results.length > 0) {
        const actionable = results.filter(r=>r.signal==="BUY"||r.signal==="SELL");
        if(actionable.length > 0) {
          const time = new Date().toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"});
          const isFO = tabMode==="fo";

          // Helper: fetch ATM option for F&O mode
          const _fetchATM = async(symbol, ltp, optType) => {
            try {
              const res = await apiFetch(`/fo/find_atm`, {
                method:'POST', headers:{'Content-Type':'application/json'},
                body: JSON.stringify({ symbol, exchange:'NSE', option_type:optType, signal_side:optType==='CE'?'BUY':'SELL', min_days:3 })
              });
              if(!res||res.error||!res.found) return null;
              const c = res.contract;
              if(!c||!c.tradingsymbol) return null;
              const optLtp = parseFloat(c.option_ltp||0);
              if(optLtp<0.5) return null;
              const tp = optLtp>0 ? (optLtp*1.20).toFixed(1) : null;
              const sl = optLtp>0 ? (optLtp*0.80).toFixed(1) : null;
              return { sym:c.tradingsymbol, ltp:optLtp, strike:c.strike, expiry:c.expiry||"", tp, sl };
            } catch { return null; }
          };

          const lines = await Promise.all(actionable.slice(0,10).map(async(r)=>{
            const emoji = r.signal==="BUY"?"🟢":"🔴";
            const dir   = r.signal==="BUY"?"▲ BUY":"▼ SELL";
            const conf  = r.confidence ? ` · ${r.confidence}%` : "";
            const reasons = (r.key_reasons||[]).slice(0,3).map((k,i)=>`   ${i+1}. ${k}`).join("\n");
            const ivLine = r.iv ? `\n   📊 IV: ${r.iv}%` : "";
            const oiLine = (r.total_ce_oi||r.total_pe_oi) ? `\n   📈 CE OI: ${((r.total_ce_oi||0)/1000).toFixed(0)}K · PE OI: ${((r.total_pe_oi||0)/1000).toFixed(0)}K` : "";
            const mpLine = r.max_pain ? `\n   💎 Max Pain: ₹${r.max_pain}` : "";
            const srLine = r.sr?.demand_zone ? `\n   📐 Demand ₹${r.sr.demand_zone[0]}–₹${r.sr.demand_zone[1]}` : "";

            // F&O mode: show option strike + option TP/SL instead of stock TP/SL
            let tpSlLine = "";
            let optLine = "";
            if(isFO && isAdmin && r.ltp > 0) {
              const optType = r.signal==="BUY"?"CE":"PE";
              const optLabel = r.signal==="BUY"?"BUY CE":"BUY PE";
              const atm = await _fetchATM(r.symbol, r.ltp, optType);
              if(atm && atm.ltp > 0) {
                optLine = `\n\n   📋 <b>${optLabel}: ${atm.sym}</b>\n   Strike ₹${atm.strike} · Option LTP ₹${atm.ltp}`;
                if(atm.tp) tpSlLine = `\n   🎯 TP ₹${atm.tp} · 🛑 SL ₹${atm.sl}`;
              } else {
                // Fallback to stock TP/SL if option not found
                tpSlLine = (r.target_price||r.stop_loss) ? `\n   🎯 TP ₹${r.target_price||"–"} · 🛑 SL ₹${r.stop_loss||"–"} (stock)` : "";
              }
            } else {
              tpSlLine = (r.target_price||r.stop_loss) ? `\n   🎯 TP ₹${r.target_price||"–"} · 🛑 SL ₹${r.stop_loss||"–"}` : "";
            }

            const base = isAdmin
              ? `${emoji} <b>${dir} ${r.symbol}</b> @ ₹${r.ltp} [${timeframe}]${conf}\n${reasons}${optLine}${tpSlLine}${ivLine}${oiLine}${mpLine}${srLine}`
              : `📊 <b>${r.symbol}</b> @ ₹${r.ltp} [${timeframe}]${conf}\n${reasons}${tpSlLine}${ivLine}\n   <i>AI signal — analyse before acting</i>`;
            return base;
          }));
          const msg = `<b>🤖 AI Scan</b> · ${time} · ${tabMode.toUpperCase()}\n${actionable.length} signal${actionable.length>1?"s":""}\n\n${lines.join("\n\n")}`;
          sendTgAlert(msg);
        }
      }

      // ── Auto-trade: call backend directly — no order window ────────────────
      if(autoTradeRef.current) {
        const highConf = results.filter(r=>
          r.signal!=="HOLD" && r.signal!=="ERROR" &&
          r.signal_clarity!=="MIXED" &&
          (r.confidence||0) >= minAutoConf &&
          !autoFiredRef.current[r.symbol]
        );
        if(highConf.length > 0) {
          if(onShowToast) onShowToast(`🤖 AI Auto: analyzing ${highConf.length} signal(s)...`);
          // Mark as fired immediately to prevent duplicate trades
          highConf.forEach(r => {
            autoFiredRef.current[r.symbol] = {status:"PENDING", firedAt: Date.now()};
          });
          setAutoFired(prev => {
            const next = {...prev};
            highConf.forEach(r => { next[r.symbol] = {status:"PENDING", firedAt: Date.now()}; });
            return next;
          });
          apiFetch("/ai/autotrade/batch", {
            method:"POST",
            headers:{"Content-Type":"application/json"},
            body: JSON.stringify({
              symbols:          highConf.map(r=>r.symbol),
              exchange:         tabMode==="mcx"?"MCX":tabMode==="delta"?"DELTA":"NSE",
              timeframe,
              tab_mode:         tabMode,
              product:          tabMode==="investment"?"CNC":tabMode==="fo"||tabMode==="mcx"?"NRML":"MIS",
              margin_pct:       marginPctRef.current || 10,
              min_confidence:   minAutoConf,
              max_daily_trades: maxDailyRef.current || 5,
              liq_check:        tabMode!=="equity",
            })
          }).then(res=>{
            // Show top-level error if present (Kite not authed etc)
            if(res.error) {
              if(onShowToast) onShowToast(`⚠ Auto-trade: ${res.error}`, "error");
              return;
            }
            if(res.placed_count > 0) {
              res.placed?.forEach(r=>{
                // ── Record in Firebase journal with signal info ──────────────
                const _aiSig = (results||[]).find(x=>x.symbol===r.symbol);
                const _sigLabel = _aiSig?.key_reasons?.join(" · ") || _aiSig?.summary || r.signal?.key_reasons?.join(" · ") || "";
                const _sigDetails = (_aiSig?.signals || r.signal?.signals || []).map(s=>({
                  label: s.label||"", type: s.type||r.side, scan: s.scan||null
                }));
                if(typeof onJournalEntry === "function") {
                  onJournalEntry({
                    orderId:  r.order_id,
                    symbol:   r.symbol,
                    exchange: r.exchange||"NSE",
                    side:     r.side,
                    qty:      r.qty,
                    price:    r.price,
                    product:  r.product||"NRML",
                    source:   "ai_auto",
                    tabMode:  tabMode,
                    tpPrice:  r.tp||null,
                    slPrice:  r.sl||null,
                    signals:       _sigLabel,
                    signalDetails: _sigDetails,
                    confidence:    r.confidence || _aiSig?.confidence || 0,
                    tag:      "RABT-AI-AUTO",
                  });
                }
                setAutoFired(prev=>({...prev,[r.symbol]:{
                  status:"PENDING", order_id:r.order_id,
                  side:r.side, qty:r.qty, price:r.price,
                  symbol:r.symbol, exchange:r.exchange,
                }}));

                // ── GTT is auto-created by backend after order fill ──────────
                // Backend _register_gtt_when_filled() polls order status and
                // creates OCO GTT + registers trailing SL once COMPLETE.
                const tpPrice = r.tp || 0;
                const slPrice = r.sl || 0;

                if(onAddNote) {
                  const aiSig = (results||[]).find(x=>x.symbol===r.symbol);
                  if(aiSig) onAddNote(r.symbol, {
                    source:"AI Auto", tab:tabMode,
                    signal:aiSig.signal, confidence:aiSig.confidence,
                    summary:aiSig.summary, entry:r.price,
                    tp:aiSig.target_price, sl:aiSig.stop_loss,
                    reasons:aiSig.key_reasons||[], clarity:aiSig.signal_clarity,
                    votes:aiSig.parameter_votes,
                  });
                }
                if(onShowToast) onShowToast(
                  `✓ AI Order: ${r.side} ${r.symbol} x${r.qty} @ ₹${r.price}`
                  + (tpPrice ? ` | TP ₹${tpPrice}` : '')
                  + (slPrice ? ` | SL ₹${slPrice}` : '')
                );
              });
            }
            // Show skip reasons for debugging
            res.skipped?.forEach(r=>{
              if(!r.reason) return;
              const skipReason = r.reason||"";
              // Only show important skips (not routine HOLD/low conf)
              if(!skipReason.includes("Signal is HOLD") &&
                 !skipReason.includes("below threshold") &&
                 !skipReason.includes("Open position") &&
                 !skipReason.includes("Daily limit")) {
                if(onShowToast) onShowToast(`⚠ ${r.symbol||''}: ${skipReason}`);
              }
            });
          }).catch(e=>{
            console.error("Auto-trade batch error:", e);
            if(onShowToast) onShowToast(`⚠ Auto-trade failed: ${e.message}`);
          });
        }
      }
    } catch(e) {
      setAiError(e.message);
    } finally {
      setAiLoading(false);
    }
  },[authed, minAutoConf]);

  return { aiResults, aiLoading, aiError, aiVisible, setAiVisible, lastAiScan,
           runAIScan, autoTrade, setAutoTrade, minAutoConf, setMinAutoConf, autoFired,
           marginPct, setMarginPct, maxDaily, setMaxDaily,
           maxScans, setMaxScans, scanCount, balance, setBalance };
  // Note: onAddNote is passed in via runAIScan call — not stored in hook
}

// ══════════════════════════════════════════════════════════════════════════════
// CHART MODAL — TradingView Lightweight Charts + Backtest
// ══════════════════════════════════════════════════════════════════════════════
function ChartModal({ symbol, exchange="NSE", timeframe="15min", onClose,
                      signalParams={}, atCfgIntra={}, onShowToast, authed,
                      scanZones=null, isAdmin=false, parentActiveScan=null }) { // zones from scan result — overrides chart API zones
  const [chartTab,    setChartTab]    = useState("chart");
  const [tf,          setTf]          = useState(timeframe);
  const [days,        setDays]        = useState(180); // fixed 180d — TradingView scrollable
  const [loading,     setLoading]     = useState(false);
  const [candles,     setCandles]     = useState(null);
  const [chartError,  setChartError]  = useState(null);
  const [showSignals, setShowSignals] = useState(true);
  // Indicator toggles (MACD/BB/Stoch removed — scans disabled)
  const showBB = false, showMACD = false, showStoch = false;
  // Scan filter — which signal types to show on chart (synced from scan tab if provided)
  const [activeScanFilter, setActiveScanFilter] = useState(
    parentActiveScan ? new Set(parentActiveScan) : new Set([1,2,3,4,5,8])
  );
  const [showPatternLabels, setShowPatternLabels] = useState(true);
  const chartRef = useRef(null);
  const tvRef    = useRef(null);
  const seriesRef= useRef({});

  // Backtest state
  const [btRunning,   setBtRunning]   = useState(false);
  const [btResult,    setBtResult]    = useState(null);
  const [btMode,      setBtMode]      = useState("single");
  const [btDays,      setBtDays]      = useState(90);
  const [btTpPct,     setBtTpPct]     = useState(atCfgIntra.tpPct||1.5);
  const [btSlPct,     setBtSlPct]     = useState(atCfgIntra.slPct||0.8);
  const [btTrailPct,  setBtTrailPct]  = useState(atCfgIntra.trailPct||0.5);
  const [btTrailEn,   setBtTrailEn]   = useState(true);
  const [btTf,        setBtTf]        = useState(tf);
  const [btSymbols,   setBtSymbols]   = useState(symbol);

  // Load candles — POST with signalParams so VPS uses same strategy
  const loadChart = useCallback(async()=>{
    if(!symbol) return;
    setLoading(true);
    setChartError(null);
    try {
      const data = await apiFetch(`/chart/candles`, {
        method: "POST",
        headers: {"Content-Type":"application/json"},
        body: JSON.stringify({
          symbol, exchange, timeframe: tf, days,
          signal_params: signalParams,
        })
      });
      setCandles(data);
    } catch(e) {
      setChartError(e.message||"Failed to load chart data");
      if(onShowToast) onShowToast(`Chart error: ${e.message}`, "error");
    } finally { setLoading(false); }
  },[symbol,exchange,tf,days,signalParams]);

  useEffect(()=>{ if(chartTab==="chart") loadChart(); },[chartTab,tf,days]);

  // Render TradingView Lightweight Charts
  useEffect(()=>{
    if(chartTab!=="chart"||!candles||!chartRef.current) return;

    // Load library dynamically
    const scriptId = "tv-lwc-script";
    const init = () => {
      if(!window.LightweightCharts) return;
      // Destroy previous chart
      if(tvRef.current) { tvRef.current.remove(); tvRef.current=null; seriesRef.current={}; }
      // ── Sub-pane height allocation (bottom-up) ───────────────────────────────
      // vol:   bottom 8%     → always shown
      // stoch: next 17%      → if showStoch
      // macd:  next 20%      → if showMACD
      // main:  rest (top)    → candles + BB + D/S zones
      const volH    = 0.08;
      const stochH  = showStoch ? 0.17 : 0;
      const macdH   = showMACD  ? 0.20 : 0;
      const mainBottom = volH + stochH + macdH;   // compressed bottom of main chart
      // Volume  [volStart .. 1.00]
      const volStart   = 1 - volH;
      // Stoch   [stochStart .. volStart]
      const stochStart = volStart - stochH;
      // MACD    [macdStart .. stochStart]
      const macdStart  = stochStart - macdH;

      // ── Gap-free time mapping for intraday charts ──────────────────────────
      // Non-trading hours (overnight, weekends) create huge empty spaces.
      // Solution: remap to uniform spacing; show real IST time via formatter.
      const isIntraday = ["1min","3min","5min","10min","15min","30min","1hour"].includes(tf);
      const IST_OFFSET = 5.5 * 3600; // UTC+5:30 in seconds
      const rawCandles = candles.candles || [];
      // Build mapping: original time → new uniform time, and reverse lookup
      const timeMap = {};      // original → uniform
      const reverseMap = {};   // uniform → original
      let mappedCandles = rawCandles;
      if(isIntraday && rawCandles.length > 1) {
        // Detect median bar step from actual data
        const steps = [];
        for(let i=1;i<Math.min(rawCandles.length,50);i++) {
          const diff = rawCandles[i].time - rawCandles[i-1].time;
          if(diff > 0 && diff < 86400) steps.push(diff);
        }
        steps.sort((a,b)=>a-b);
        const barStep = steps.length ? steps[Math.floor(steps.length/2)] : 900;
        // Start from first candle's time, increment by barStep for each subsequent candle
        const baseTime = rawCandles[0].time;
        rawCandles.forEach((c,i) => {
          const uniformT = baseTime + i * barStep;
          timeMap[c.time] = uniformT;
          reverseMap[uniformT] = c.time;
        });
        mappedCandles = rawCandles.map((c,i) => ({...c, time: baseTime + i * barStep}));
      }
      // Helper: map any original timestamp to uniform
      const mapTime = (t) => {
        if(!isIntraday) return t;
        if(timeMap[t] !== undefined) return timeMap[t];
        // Find nearest mapped time for timestamps not in candles (e.g. signal/zone times)
        const origTimes = rawCandles.map(c=>c.time);
        let best = origTimes[0], bestDist = Math.abs(t - origTimes[0]);
        for(let i=1;i<origTimes.length;i++){
          const d = Math.abs(t - origTimes[i]);
          if(d < bestDist){ bestDist=d; best=origTimes[i]; }
        }
        return timeMap[best] || t;
      };
      // IST formatter for time axis labels
      const formatIST = (ut) => {
        const origT = reverseMap[ut] || ut;
        const istSec = origT + IST_OFFSET;
        const d = new Date(istSec * 1000);
        const hh = d.getUTCHours().toString().padStart(2,"0");
        const mm = d.getUTCMinutes().toString().padStart(2,"0");
        const dd = d.getUTCDate();
        const mon = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][d.getUTCMonth()];
        return `${dd} ${mon} ${hh}:${mm}`;
      };

      const chart = window.LightweightCharts.createChart(chartRef.current, {
        width:  chartRef.current.clientWidth,
        height: chartRef.current.clientHeight || 420,
        layout: { background:{type:"solid",color:"#0d1117"}, textColor:"#c9d1d9" },
        grid:   { vertLines:{color:"#161b22"}, horzLines:{color:"#161b22"} },
        crosshair: { mode: window.LightweightCharts.CrosshairMode.Normal },
        rightPriceScale: { borderColor:"#1a2c44" },
        timeScale: {
          borderColor:"#1a2c44", timeVisible:true, secondsVisible:false, rightOffset:10,
          ...(isIntraday ? {
            tickMarkFormatter: (t) => formatIST(t),
          } : {}),
        },
        localization: isIntraday ? {
          timeFormatter: (t) => formatIST(t),
        } : undefined,
      });
      tvRef.current = chart;

      // Candlestick series — RSI gradient coloring on ALL candles
      // Smooth color gradient: blue/teal (oversold) → orange (neutral) → red/crimson (overbought)
      // Every candle body shows its RSI level — no "normal" fallback
      const cs = chart.addCandlestickSeries({
        upColor:"#f97316", downColor:"#f97316",
        borderUpColor:"#f97316", borderDownColor:"#f97316",
        wickUpColor:"#f97316", wickDownColor:"#f97316",
      });
      // RSI color interpolation — smooth gradient across full 0-100 range
      const rsiToColor = (rv) => {
        // Clamp RSI to 0-100
        const v = Math.max(0, Math.min(100, rv));
        let r, g, b;
        if (v <= 20) {
          // 0-20: deep teal → bright teal  (extreme oversold)
          const t = v / 20;
          r = Math.round(13 + t * 7);    // 13→20
          g = Math.round(128 + t * 56);  // 128→184
          b = Math.round(128 + t * 38);  // 128→166
        } else if (v <= 35) {
          // 20-35: teal → green  (oversold)
          const t = (v - 20) / 15;
          r = Math.round(20 + t * 14);   // 20→34
          g = Math.round(184 + t * 13);  // 184→197
          b = Math.round(166 - t * 72);  // 166→94
        } else if (v <= 50) {
          // 35-50: green → amber/orange  (neutral low)
          const t = (v - 35) / 15;
          r = Math.round(34 + t * 215);  // 34→249
          g = Math.round(197 - t * 82);  // 197→115
          b = Math.round(94 - t * 72);   // 94→22
        } else if (v <= 65) {
          // 50-65: orange → dark orange  (neutral high)
          const t = (v - 50) / 15;
          r = Math.round(249 - t * 17);  // 249→232
          g = Math.round(115 - t * 35);  // 115→80
          b = Math.round(22 - t * 2);    // 22→20
        } else if (v <= 80) {
          // 65-80: dark orange → red  (overbought)
          const t = (v - 65) / 15;
          r = Math.round(232 - t * 17);  // 232→215
          g = Math.round(80 - t * 48);   // 80→32
          b = Math.round(20 + t * 12);   // 20→32
        } else {
          // 80-100: red → deep crimson  (extreme overbought)
          const t = (v - 80) / 20;
          r = Math.round(215 - t * 88);  // 215→127
          g = Math.round(32 - t * 3);    // 32→29
          b = Math.round(32 - t * 3);    // 32→29
        }
        return `rgb(${r},${g},${b})`;
      };
      const rsiArr = candles.rsi || [];
      const coloredCandles = mappedCandles.map((c, i) => {
        const rv = rsiArr[i] != null ? rsiArr[i] : 50;
        const isBull = c.close >= c.open;
        const clr = rsiToColor(rv);
        // Preserve bullish/bearish distinction: green tint for up, red tint for down
        // Blend RSI color with green (bullish) or red (bearish) at 40% weight
        const parseRGB = (s) => { const m=s.match(/\d+/g); return m?[+m[0],+m[1],+m[2]]:[249,115,22]; };
        const [rr,rg,rb] = parseRGB(clr);
        const [tr,tg,tb] = isBull ? [34,197,94] : [239,68,68]; // green / red tint
        const mix = 0.35; // 35% tint blend
        const mr = Math.round(rr*(1-mix) + tr*mix);
        const mg = Math.round(rg*(1-mix) + tg*mix);
        const mb = Math.round(rb*(1-mix) + tb*mix);
        const blended = `rgb(${mr},${mg},${mb})`;
        return { ...c, color: blended, borderColor: blended, wickColor: blended };
      });
      cs.setData(coloredCandles);
      seriesRef.current.candles = cs;

      // EMA9 — thin blue
      if(candles.ema9?.length) {
        const e9 = chart.addLineSeries({ color:"#e8b339", lineWidth:1, priceLineVisible:false, lastValueVisible:false });
        e9.setData(candles.ema9.map(c=>({time:mapTime(c.time),value:c.value})));
        seriesRef.current.ema9 = e9;
      }
      // EMA50 — thin orange
      if(candles.ema50?.length) {
        const e50 = chart.addLineSeries({ color:"#f59e0b", lineWidth:1.5, priceLineVisible:false, lastValueVisible:false });
        e50.setData(candles.ema50.map(c=>({time:mapTime(c.time),value:c.value})));
        seriesRef.current.ema50 = e50;
      }

      // Apply main chart margins AFTER chart creation (LWC v3.8 API)
      chart.priceScale("right").applyOptions({
        scaleMargins: { top: 0.02, bottom: mainBottom },
      });

      // Volume histogram — priceScaleId:"" = unnamed overlay, scaleMargins positions it
      const vs = chart.addHistogramSeries({
        color:"#26a69a", priceFormat:{type:"volume"},
        priceScaleId:"",
        scaleMargins:{ top: volStart, bottom: 0 },
      });
      vs.setData(mappedCandles.map(c=>({
        time:c.time, value:c.volume,
        color: c.close>=c.open?"rgba(249,115,22,0.35)":"rgba(249,115,22,0.18)",
      })));

      // ── Bollinger Bands — overlay on main price scale ────────────────────────
      if(showBB && candles.bb_chart?.length) {
        const bbU = chart.addLineSeries({
          color:"rgba(14,165,233,0.75)", lineWidth:1, lineStyle:2,
          priceLineVisible:false, lastValueVisible:false, crosshairMarkerVisible:false, title:"BB↑",
        });
        const bbL = chart.addLineSeries({
          color:"rgba(14,165,233,0.75)", lineWidth:1, lineStyle:2,
          priceLineVisible:false, lastValueVisible:false, crosshairMarkerVisible:false, title:"BB↓",
        });
        const bbM = chart.addLineSeries({
          color:"rgba(14,165,233,0.3)", lineWidth:1, lineStyle:0,
          priceLineVisible:false, lastValueVisible:false, crosshairMarkerVisible:false,
        });
        bbU.setData(candles.bb_chart.map(b=>({time:mapTime(b.time),value:b.upper})));
        bbL.setData(candles.bb_chart.map(b=>({time:mapTime(b.time),value:b.lower})));
        bbM.setData(candles.bb_chart.map(b=>({time:mapTime(b.time),value:b.mid})));
      }

      // ── MACD sub-pane ────────────────────────────────────────────────────────
      // In LWC v3.8, named priceScaleId creates an independent scale.
      // scaleMargins MUST be applied via chart.priceScale(id).applyOptions() — not inline.
      if(showMACD && candles.macd_chart?.length) {
        const mHist = chart.addHistogramSeries({
          priceScaleId: "macd",
          priceLineVisible: false, lastValueVisible: false, base: 0,
        });
        mHist.setData(candles.macd_chart.map(m=>({
          time: mapTime(m.time), value: m.histogram,
          color: m.histogram >= 0 ? "rgba(34,197,94,0.6)" : "rgba(239,68,68,0.6)",
        })));
        const mLine = chart.addLineSeries({
          color: "#38bdf8", lineWidth: 1,
          priceScaleId: "macd",
          priceLineVisible: false, lastValueVisible: true, crosshairMarkerVisible: false, title: "MACD",
        });
        mLine.setData(candles.macd_chart.map(m=>({time:mapTime(m.time),value:m.macd})));
        const mSig = chart.addLineSeries({
          color: "#fb923c", lineWidth: 1,
          priceScaleId: "macd",
          priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false, title: "Sig",
        });
        mSig.setData(candles.macd_chart.map(m=>({time:mapTime(m.time),value:m.signal})));
        // Apply scale margins AFTER creating all MACD series
        chart.priceScale("macd").applyOptions({
          scaleMargins: { top: macdStart, bottom: 1 - macdStart - macdH },
        });
      }

      // ── Stochastic sub-pane ──────────────────────────────────────────────────
      if(showStoch && candles.stoch_chart?.length) {
        const skLine = chart.addLineSeries({
          color: "#a78bfa", lineWidth: 1.5,
          priceScaleId: "stoch",
          priceLineVisible: false, lastValueVisible: true, crosshairMarkerVisible: false, title: "%K",
        });
        skLine.setData(candles.stoch_chart.map(s=>({time:mapTime(s.time),value:s.k})));
        const sdLine = chart.addLineSeries({
          color: "#fb923c", lineWidth: 1,
          priceScaleId: "stoch",
          priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false, title: "%D",
        });
        sdLine.setData(candles.stoch_chart.map(s=>({time:mapTime(s.time),value:s.d})));
        // OB/OS reference lines
        if(candles.stoch_chart.length > 1) {
          const t0 = mapTime(candles.stoch_chart[0].time);
          const t1 = mapTime(candles.stoch_chart[candles.stoch_chart.length-1].time);
          [[80,"rgba(239,68,68,0.45)"],[20,"rgba(34,197,94,0.45)"]].forEach(([lvl,clr])=>{
            const rl = chart.addLineSeries({
              color: clr, lineWidth: 1, lineStyle: 2,
              priceScaleId: "stoch",
              priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
            });
            rl.setData([{time:t0,value:lvl},{time:t1,value:lvl}]);
          });
        }
        // Apply scale margins AFTER creating all Stoch series
        chart.priceScale("stoch").applyOptions({
          scaleMargins: { top: stochStart, bottom: 1 - stochStart - stochH },
        });
      }

      // ── Signal markers — show recent signals (last 5 bars window) ────────────
      if(showSignals && candles.signals?.length) {
        const sigAllT = rawCandles.map(c=>c.time);
        // Show signals from the last 5 bars so current + recent patterns are visible
        const cutoffIdx = Math.max(0, sigAllT.length - 5);
        const sigCutoff = sigAllT[cutoffIdx] || 0;
        const filtered = candles.signals.filter(m => {
          if(m.time < sigCutoff) return false; // only recent signals
          if(!m.scans || m.scans.length === 0) return true;
          return m.scans.some(s => activeScanFilter.has(s));
        });
        const sorted = [...filtered].map(m=>({...m, time:mapTime(m.time)})).sort((a,b)=>a.time-b.time);
        cs.setMarkers(sorted);
        seriesRef.current.markers = sorted;
      }

      // ── D/S Zone lines on chart (tied to scan 8 "Zone" filter) ──
      if(activeScanFilter.has(8) && candles.dynamic_zones?.length && rawCandles.length >= 15) {
        const allT = mappedCandles.map(c=>c.time);
        // Use uniform bar step from mapped candles (gaps removed)
        const mStep = allT.length > 1 ? allT[1] - allT[0] : 900;
        const defaultFwd = allT[allT.length-1] + mStep * 7;

        const nearestDemand = candles.dynamic_zones.find(z=>z.type==="demand");
        const nearestSupply = candles.dynamic_zones.find(z=>z.type==="supply");
        const zonesToDraw = [nearestDemand, nearestSupply].filter(Boolean);

        const drawZoneBox = (zl, zh, isDemand, tStart, tEnd, strength) => {
          if(zl == null || zh == null || zl >= zh) return;
          const rgb     = isDemand ? "0,200,120" : "255,80,80";
          const lineClr = `rgba(${rgb},0.85)`;
          const lbl     = isDemand ? `D ₹${zl.toFixed(0)}–${zh.toFixed(0)} (${strength}x)` : `S ₹${zl.toFixed(0)}–${zh.toFixed(0)} (${strength}x)`;

          const timePoints = [];
          let t = tStart;
          while(t <= tEnd) { timePoints.push(t); t += mStep; }
          if(timePoints[timePoints.length-1] < tEnd) timePoints.push(tEnd);

          const topLine = chart.addLineSeries({
            color:lineClr, lineWidth:2, lineStyle:2,
            priceLineVisible:false, lastValueVisible:true, title:lbl,
            crosshairMarkerVisible:false,
          });
          topLine.setData(timePoints.map(t=>({time:t,value:zh})));
          const botLine = chart.addLineSeries({
            color:lineClr, lineWidth:2, lineStyle:2,
            priceLineVisible:false, lastValueVisible:false,
            crosshairMarkerVisible:false,
          });
          botLine.setData(timePoints.map(t=>({time:t,value:zl})));
        };

        zonesToDraw.forEach(z => {
          const isDemand = z.type === "demand";
          const tStart = mapTime(z.time_start || rawCandles[rawCandles.length-1]?.time || 0);
          const tEnd   = defaultFwd; // always extend to forward projection
          drawZoneBox(z.low, z.high, isDemand, tStart, tEnd, z.strength||1);
        });
      }

      // ── Signal markers on chart — arrows + pattern name labels ──
      if(showSignals && candles.signals?.length && seriesRef.current.candles) {
        const allMarkers = [];
        candles.signals.forEach(m => {
          if(m.scans?.length && !m.scans.some(s=>activeScanFilter.has(s))) return;
          // Extract short pattern/signal name for label
          let label = "";
          if(m.text) {
            // Backend sends "▲ Label1 + Label2" — strip the arrow prefix, keep short
            label = m.text.replace(/^[▲▼]\s*/, "").split(" + ")[0];
            // Truncate long labels to keep chart clean
            if(label.length > 20) label = label.substring(0, 18) + "…";
          }
          allMarkers.push({
            time: mapTime(m.time),
            position: m.position || (m.color==="#2ed47a" ? "belowBar" : "aboveBar"),
            color: m.color || "#8ea2bf",
            shape: m.shape || "circle",
            text: label,   // show pattern name below/above candle
            size: m.size || 1,
          });
        });
        // Sort by time (required by LWC)
        allMarkers.sort((a,b) => a.time - b.time);
        // Dedupe — one marker per time+position
        const seen = new Set();
        const deduped = allMarkers.filter(m => {
          const key = `${m.time}_${m.position}`;
          if(seen.has(key)) return false;
          seen.add(key); return true;
        });
        seriesRef.current.candles.setMarkers(deduped);
      }

      // Extend time scale right so the 10-candle zone projection is visible
      if(mappedCandles.length >= 35) {
        const allT2 = mappedCandles.map(c=>c.time);
        const lastT2 = allT2[allT2.length-1];
        const mStep2 = allT2.length > 1 ? allT2[1] - allT2[0] : 900;
        const zoneEndT = lastT2 + mStep2 * 11;
        try { chart.timeScale().setVisibleRange({from: allT2[Math.max(0,allT2.length-80)], to: zoneEndT}); } catch(e) {}
      } else {
        chart.timeScale().fitContent();
      }
      const ro = new ResizeObserver(()=>{
        if(chartRef.current) chart.applyOptions({width:chartRef.current.clientWidth});
      });
      ro.observe(chartRef.current);
      tvRef.current._ro = ro;
    };

    if(!document.getElementById(scriptId)) {
      const s = document.createElement("script");
      s.id  = scriptId;
      s.src = "https://unpkg.com/lightweight-charts@3.8.0/dist/lightweight-charts.standalone.production.js";
      s.onload = init;
      s.onerror = ()=>{ if(onShowToast) onShowToast("Chart library failed to load — check internet connection","error"); };
      document.head.appendChild(s);
    } else if(window.LightweightCharts) {
      // Check version — v4+ doesn't have addCandlestickSeries directly
      if(window.LightweightCharts.version && parseInt(window.LightweightCharts.version()) >= 4) {
        // Wrong version cached — force reload with pinned version
        const old = document.getElementById(scriptId);
        if(old) old.remove();
        const s2 = document.createElement("script");
        s2.id  = scriptId;
        s2.src = "https://unpkg.com/lightweight-charts@3.8.0/dist/lightweight-charts.standalone.production.js";
        s2.onload = init;
        document.head.appendChild(s2);
      } else {
        init();
      }
    } else {
      const wait = setInterval(()=>{ if(window.LightweightCharts){ clearInterval(wait); init(); }},100);
    }
    return ()=>{
      if(tvRef.current?._ro) tvRef.current._ro.disconnect();
      if(chartRef.current) chartRef.current.querySelectorAll('.zone-box').forEach(el=>el.remove());
    };
  },[candles, chartTab, showSignals, activeScanFilter, showPatternLabels, tf]);

  // ── Admin-only: TSI + Pivot S/R overlay (Pine Script logic in JS) ────────────
  // Rendered as an HTML overlay on top of the chart div (not inside LWC canvas)
  const [tsiOverlay, setTsiOverlay] = useState(null); // {tradeStatus,entryPrice,tp,sl,tsiVal,signals:[]}
  useEffect(()=>{
    if(!isAdmin || !candles || chartTab!=="chart" || !tvRef.current) return;
    const bars = candles.candles;
    if(bars.length < 40) return;

    // ── EMA helper ──────────────────────────────────────────────────────────
    const ema = (data, period) => {
      const k = 2/(period+1);
      const out = new Array(data.length).fill(null);
      let val = null;
      for(let i=0; i<data.length; i++){
        if(data[i]===null||data[i]===undefined){ out[i]=null; continue; }
        val = val===null ? data[i] : data[i]*k + val*(1-k);
        out[i] = val;
      }
      return out;
    };

    // ── TSI calculation ──────────────────────────────────────────────────────
    const closes = bars.map(b=>b.close);
    const moms   = closes.map((c,i)=>i===0?0:c-closes[i-1]);
    const ema1   = ema(moms, 13);
    const ema2   = ema(ema1.map(v=>v??0), 25);
    const momAbs = moms.map(m=>Math.abs(m));
    const ema1A  = ema(momAbs, 13);
    const ema2A  = ema(ema1A.map(v=>v??0), 25);
    const tsiArr = ema2.map((e,i)=>ema2A[i]?100*e/ema2A[i]:null);
    // fill nulls for signal smoothing
    const tsiClean = tsiArr.map(v=>v??0);
    const tsiSig   = ema(tsiClean, 7);

    // ── Pivot high/low ──────────────────────────────────────────────────────
    const lb = 20; // lookback=20 — matches Pine Script for distinct S/R
    let pivotHigh = null, pivotLow = null;
    const highs = bars.map(b=>b.high);
    const lows  = bars.map(b=>b.low);
    for(let i=lb; i<bars.length-lb; i++){
      let isH=true, isL=true;
      for(let j=i-lb; j<=i+lb; j++){
        if(j!==i && highs[j]>=highs[i]) isH=false;
        if(j!==i && lows[j] <=lows[i])  isL=false;
      }
      if(isH) pivotHigh = {idx:i, price:highs[i]};
      if(isL) pivotLow  = {idx:i, price:lows[i]};
    }

    // ── ATR(14) ─────────────────────────────────────────────────────────────
    const trArr = bars.map((b,i)=>{
      if(i===0) return b.high-b.low;
      const prev = bars[i-1].close;
      return Math.max(b.high-b.low, Math.abs(b.high-prev), Math.abs(b.low-prev));
    });
    const atrArr = ema(trArr,14);
    const atr = atrArr[atrArr.length-1] || 1;

    // ── Detect TSI crosses ──────────────────────────────────────────────────
    const n = tsiArr.length-1;
    const tsiNow  = tsiArr[n]   || 0;
    const tsiPrev = tsiArr[n-1] || 0;
    const sigNow  = tsiSig[n]   || 0;
    const sigPrev = tsiSig[n-1] || 0;
    const tsiBull = tsiPrev<=sigPrev && tsiNow>sigNow;  // cross above
    const tsiBear = tsiPrev>=sigPrev && tsiNow<sigNow;  // cross below

    // ── Collect recent TSI signals for BUY/SELL markers ────────────────────
    const signalMarkers = [];
    for(let i=1; i<bars.length; i++){
      const tNow=tsiArr[i]??0, tPrv=tsiArr[i-1]??0;
      const sNow=tsiSig[i]??0, sPrv=tsiSig[i-1]??0;
      if(tPrv<=sPrv && tNow>sNow) signalMarkers.push({time:bars[i].time,type:'BUY',price:bars[i].close});
      if(tPrv>=sPrv && tNow<sNow) signalMarkers.push({time:bars[i].time,type:'SELL',price:bars[i].close});
    }

    // ── Current trade state ─────────────────────────────────────────────────
    let tradeStatus='None', entryPrice=null, tpPrice=null, slPrice=null;
    for(let i=1; i<bars.length; i++){
      const tN=tsiArr[i]??0, tP=tsiArr[i-1]??0;
      const sN=tsiSig[i]??0, sP=tsiSig[i-1]??0;
      const atrI = atrArr[i]||atr;
      if(tradeStatus==='None'){
        if(tP<=sP && tN>sN){ tradeStatus='Buy'; entryPrice=bars[i].close; tpPrice=bars[i].high+atrI*3; slPrice=bars[i].low-atrI*1.5; }
        if(tP>=sP && tN<sN){ tradeStatus='Sell'; entryPrice=bars[i].close; tpPrice=bars[i].low-atrI*3; slPrice=bars[i].high+atrI*1.5; }
      } else if(tradeStatus==='Buy'){
        if(bars[i].low<=slPrice||bars[i].high>=tpPrice){ tradeStatus='None'; entryPrice=null; tpPrice=null; slPrice=null; }
      } else if(tradeStatus==='Sell'){
        if(bars[i].high>=slPrice||bars[i].low<=tpPrice){ tradeStatus='None'; entryPrice=null; tpPrice=null; slPrice=null; }
      }
    }

    setTsiOverlay({
      tradeStatus, entryPrice, tpPrice, slPrice,
      tsiNow: tsiNow.toFixed(2), sigNow: sigNow.toFixed(2),
      tsiBull, tsiBear,
      pivotHigh, pivotLow, atr: atr.toFixed(2),
      signalMarkers,
    });

    // NOTE: Pivot S/R zone boxes are drawn by the main chart useEffect for all users.
    // Admin TSI overlay only adds TSI BUY/SELL signal markers on top.

    // Add TSI BUY/SELL signal markers (last 10 bars) to chart as shapes
    const _barTimes = bars.map(b=>b.time);
    const recentSigs = signalMarkers.filter(m=>m.time >= _barTimes[Math.max(0,_barTimes.length-10)]);
    if(recentSigs.length > 0) {
      const existingMarkers = seriesRef.current.markers || [];
      const cs = seriesRef.current.candles;
      if(cs) {
        const tsiCsMkrs = recentSigs.map(s=>({
          time:  s.time,
          position: s.type==='BUY'?'belowBar':'aboveBar',
          color:  s.type==='BUY'?'#2ed47a':'#ff5b6c',
          shape:  s.type==='BUY'?'arrowUp':'arrowDown',
          text:   s.type==='BUY'?'TSI▲':'TSI▼',
          size:   1,
        }));
        const merged = [...existingMarkers, ...tsiCsMkrs]
          .sort((a,b)=>a.time-b.time);
        cs.setMarkers(merged);
      }
    }
  },[isAdmin, candles, chartTab]);

  // Run TA backtest
  const runBacktest = async() => {
    if(!authed) return;
    setBtRunning(true); setBtResult(null);
    try {
      const syms = btMode==="single"
        ? [symbol]
        : btSymbols.split(/[,\n]+/).map(s=>s.trim().toUpperCase()).filter(Boolean);
      const res = await apiFetch("/backtest/run", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          symbols:       syms,
          exchange,
          timeframe:     btTf,
          days:          btDays,
          tp_pct:        btTpPct,
          sl_pct:        btSlPct,
          trail_pct:     btTrailPct,
          trail_enabled: btTrailEn,
          signal_params: signalParams,
        })
      });
      setBtResult(res);
    } catch(e) {
      if(onShowToast) onShowToast(`Backtest error: ${e.message}`, "error");
    } finally { setBtRunning(false); }
  };

  const inp = {background:"#0d1117",border:"1px solid #1a2c44",borderRadius:0,
               color:"#e7edf5",padding:"3px 6px",fontFamily:"inherit"};

  const TF_LIST = ["5min","15min","30min","1hour","1day"];

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:9100,
      display:"flex",flexDirection:"column"}}>
      {/* Header */}
      <div style={{background:"#0b1624",borderBottom:"1px solid #1a2c44",
        padding:"10px 16px",display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
        <span style={{fontSize:15,fontWeight:700,color:"#e7edf5"}}>{symbol}</span>
        <span style={{fontSize:11,color:"#5a728f"}}>{exchange}</span>

        {/* Tab */}
        {["chart","backtest"].map(t=>(
          <button key={t} onClick={()=>setChartTab(t)}
            style={{background:chartTab===t?"rgba(232,179,57,0.2)":"transparent",
              border:`1px solid ${chartTab===t?"#e8b339":"#23405f"}`,
              borderRadius:0,padding:"4px 12px",color:chartTab===t?"#49c2e4":"#5a728f",
              fontFamily:"inherit",fontSize:11,fontWeight:chartTab===t?700:400,cursor:"pointer"}}>
            {t==="chart"?"📈 Chart":"🔬 Backtest"}
          </button>
        ))}

        {chartTab==="chart" && (
          <div style={{display:"flex",gap:4,marginLeft:8}}>
            {TF_LIST.map(t=>(
              <button key={t} onClick={()=>setTf(t)}
                style={{background:tf===t?"rgba(34,197,94,0.15)":"transparent",
                  border:`1px solid ${tf===t?"#2ed47a":"#23405f"}`,
                  borderRadius:0,padding:"3px 8px",color:tf===t?"#2ed47a":"#5a728f",
                  fontFamily:"inherit",fontSize:10,cursor:"pointer"}}>
                {t}
              </button>
            ))}

            {/* Signals toggle */}
            <button onClick={()=>setShowSignals(p=>!p)}
              style={{background:showSignals?"rgba(163,113,247,0.2)":"transparent",
                border:`1px solid ${showSignals?"#a78bfa":"#23405f"}`,
                borderRadius:0,padding:"3px 8px",
                color:showSignals?"#a78bfa":"#5a728f",
                fontFamily:"inherit",fontSize:10,cursor:"pointer",
                display:"flex",alignItems:"center",gap:3}}>
              {showSignals?"▲▼":"◇"} Signals
              {showSignals&&candles?.signals?.length>0&&(
                <span style={{background:"rgba(163,113,247,0.3)",borderRadius:0,
                  padding:"0 5px",fontSize:9,color:"#c4b5fd"}}>
                  {candles.signals.length}
                </span>
              )}
            </button>
            {/* RSI Color legend — gradient from teal (oversold) to red (overbought) */}
            <span style={{fontSize:9,color:"#5a728f",display:"flex",alignItems:"center",gap:2,
              padding:"2px 6px",border:"1px solid #1a2c44",borderRadius:0}}>
              <span style={{fontSize:8,color:"#8ea2bf",fontWeight:600}}>RSI</span>
              <span style={{width:7,height:7,borderRadius:1,background:"rgb(13,148,148)",display:"inline-block"}}/>
              <span title="RSI 0-20 Oversold">20</span>
              <span style={{width:7,height:7,borderRadius:1,background:"rgb(34,197,94)",display:"inline-block",marginLeft:1}}/>
              <span title="RSI 20-35">35</span>
              <span style={{width:7,height:7,borderRadius:1,background:"rgb(249,115,22)",display:"inline-block",marginLeft:1}}/>
              <span title="RSI 50 Neutral">50</span>
              <span style={{width:7,height:7,borderRadius:1,background:"rgb(232,80,20)",display:"inline-block",marginLeft:1}}/>
              <span title="RSI 65">65</span>
              <span style={{width:7,height:7,borderRadius:1,background:"rgb(215,32,32)",display:"inline-block",marginLeft:1}}/>
              <span title="RSI 80+ Overbought">80+</span>
            </span>
            {/* Refresh */}
            <button onClick={loadChart} disabled={loading}
              style={{background:"transparent",border:"1px solid #23405f",
                borderRadius:0,padding:"3px 7px",color:"#5a728f",
                fontFamily:"inherit",fontSize:10,cursor:"pointer",opacity:loading?0.5:1}}>
              {loading?"⟳":"↻"}
            </button>
          </div>
        )}

        <button onClick={onClose}
          style={{marginLeft:"auto",background:"none",border:"none",color:"#5a728f",
            fontSize:20,cursor:"pointer",padding:"0 4px"}}>✕</button>
      </div>

      {/* Chart Tab */}
      {chartTab==="chart" && (
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>

          {/* ── Scan filter bar ─────────────────────────────────────────── */}
          {showSignals && (
            <div style={{background:"#0d1117",borderBottom:"1px solid #1a2c44",
              padding:"5px 12px",display:"flex",gap:4,flexWrap:"wrap",alignItems:"center"}}>
              <span style={{fontSize:9,color:"#5a728f",letterSpacing:1,marginRight:2}}>FILTER:</span>
              {[
                {id:1,  label:"TSI",     color:"#49c2e4"},
                {id:2,  label:"RSI",     color:"#a78bfa"},
                {id:3,  label:"Vol",     color:"#34d399"},
                {id:4,  label:"EMA",     color:"#f59e0b"},
                {id:5,  label:"📐 Pat",  color:"#fb923c"},
                {id:8,  label:"Zone",    color:"#f472b6"},
              ].map(({id,label,color})=>{
                const on = activeScanFilter.has(id);
                const cnt = candles?.signals?.filter(m=>m.scans?.includes(id)).length||0;
                return (
                  <button key={id} onClick={()=>setActiveScanFilter(prev=>{
                    const n = new Set(prev);
                    n.has(id)?n.delete(id):n.add(id);
                    return n;
                  })}
                    style={{background:on?`${color}22`:"transparent",
                      border:`1px solid ${on?color:"#1a2c44"}`,
                      borderRadius:12,padding:"2px 8px",
                      color:on?color:"#5a728f",
                      fontFamily:"inherit",fontSize:9,cursor:"pointer",
                      display:"flex",alignItems:"center",gap:4}}>
                    {label}
                    {cnt>0&&<span style={{background:on?`${color}33`:"#1a2c44",
                      borderRadius:0,padding:"0 4px",fontSize:8}}>{cnt}</span>}
                  </button>
                );
              })}
              <button onClick={()=>setActiveScanFilter(new Set([1,2,3,4,5,8]))}
                style={{background:"transparent",border:"none",color:"#e8b339",
                  fontSize:9,cursor:"pointer",padding:"0 4px"}}>all</button>
              <button onClick={()=>setActiveScanFilter(new Set())}
                style={{background:"transparent",border:"none",color:"#5a728f",
                  fontSize:9,cursor:"pointer",padding:"0 4px"}}>none</button>
              <span style={{marginLeft:"auto",fontSize:9,color:"#5a728f"}}>
                {candles?.signals?.filter(m=>!m.scans||m.scans.some(s=>activeScanFilter.has(s))).length||0} signals shown
              </span>
            </div>
          )}
          <div style={{flex:1,position:"relative",background:"#0d1117"}}>
            {loading && (
              <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",
                justifyContent:"center",color:"#5a728f",fontSize:13,zIndex:2,flexDirection:"column",gap:8}}>
                <div style={{fontSize:22,animation:"spin 1s linear infinite"}}>⟳</div>
                <span>Loading candles from VPS...</span>
                <style>{"@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}"}</style>
              </div>
            )}
            {!loading && !candles && (
              <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",
                justifyContent:"center",color:"#5a728f",fontSize:13,zIndex:2,flexDirection:"column",gap:8}}>
                <span style={{fontSize:28}}>📊</span>
                {chartError
                  ? <span style={{color:"#ff5b6c",fontSize:12,textAlign:"center",maxWidth:280}}>
                      ⚠ {chartError}
                    </span>
                  : <span>No chart data — tap Retry below</span>
                }
                <button onClick={loadChart}
                  style={{background:"rgba(232,179,57,0.1)",border:"1px solid #e8b339",
                    borderRadius:0,padding:"5px 14px",color:"#e8b339",fontFamily:"inherit",
                    fontSize:11,cursor:"pointer"}}>
                  ↻ Retry
                </button>
              </div>
            )}
            <div ref={chartRef} style={{width:"100%",height:"100%"}}/>

            {/* ── Pattern Detection Panel — right-side overlay ────────────────── */}
            {candles && (()=>{
              // Collect all pattern signals from chart data
              const patSigs = (candles.signals||[]).filter(m=>
                m.scans?.includes(5) && m.patterns?.length
              );
              // Unique patterns (latest occurrence)
              const seen = new Set();
              const uniquePats = [];
              for(const m of [...patSigs].reverse()){
                const key = m.patterns[0];
                if(!seen.has(key)){ seen.add(key); uniquePats.push(m); }
              }
              if(!uniquePats.length) return null;
              return (
                <div style={{
                  position:"absolute", top:8, right: (isAdmin&&tsiOverlay) ? 200 : 8,
                  zIndex:210, display:"flex", flexDirection:"column", gap:5,
                  maxWidth:220, pointerEvents:"none",
                }}>
                  <div style={{fontSize:9,color:"#5a728f",letterSpacing:1,
                    fontWeight:700,textAlign:"right",marginBottom:2}}>
                    📐 PATTERNS DETECTED
                  </div>
                  {uniquePats.slice(0,6).map((m,i)=>{
                    const pat = m.patterns[0];
                    const isBull = m.color==="#2ed47a";
                    const isDB   = pat==="Double Bottom";
                    const isDT   = pat==="Double Top";
                    const isCup  = pat==="Cup & Handle";
                    const isFlag = pat?.includes("Flag");
                    const isBrk  = pat?.includes("Breakout")||pat?.includes("Breakdown");
                    // Pattern type emoji
                    const emoji = isDB||isCup ? "🟢"
                      : isDT ? "🔴"
                      : isFlag&&isBull ? "🏴"
                      : isFlag ? "🚩"
                      : isBrk&&isBull ? "⬆️"
                      : isBrk ? "⬇️" : isBull ? "▲" : "▼";
                    return (
                      <div key={i} style={{
                        background:"rgba(10,14,20,0.93)",
                        border:`1px solid ${isBull?"rgba(34,197,94,0.5)":"rgba(239,68,68,0.5)"}`,
                        borderLeft:`3px solid ${isBull?"#2ed47a":"#ff5b6c"}`,
                        borderRadius:0, padding:"6px 9px",
                        fontFamily:"inherit",
                      }}>
                        <div style={{fontSize:11,fontWeight:700,
                          color: isBull?"#2ed47a":"#ff5b6c", marginBottom:2}}>
                          {emoji} {pat}
                        </div>
                        <div style={{fontSize:9,color:"#8ea2bf",lineHeight:1.35}}>
                          {m.text?.replace(/^[▲▼]\s*/,"")||""}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* ── Admin TSI Signal Table (top-right overlay, like TradingView) ── */}
            {isAdmin && tsiOverlay && (
              <div style={{position:"absolute",top:10,right:10,zIndex:200,
                background:"rgba(10,14,20,0.96)",
                border:`2px solid ${tsiOverlay.tradeStatus==='Buy'?"#2ed47a":tsiOverlay.tradeStatus==='Sell'?"#ff5b6c":"#23405f"}`,
                borderRadius:0,padding:"10px 14px",minWidth:170,fontFamily:"inherit",
                boxShadow:"0 4px 24px rgba(0,0,0,0.6)"}}>
                {/* Header label */}
                <div style={{fontSize:10,fontWeight:700,color:"#5a728f",
                  letterSpacing:1,marginBottom:6,textTransform:"uppercase"}}>
                  TSI Signal · Admin
                </div>
                {/* Trade status — big and clear */}
                <div style={{
                  background: tsiOverlay.tradeStatus==='Buy'?"rgba(34,197,94,0.20)"
                    : tsiOverlay.tradeStatus==='Sell'?"rgba(239,68,68,0.20)"
                    : "rgba(100,116,139,0.12)",
                  border:`1px solid ${tsiOverlay.tradeStatus==='Buy'?"#2ed47a44":tsiOverlay.tradeStatus==='Sell'?"#ff5b6c44":"#23405f44"}`,
                  borderRadius:0,padding:"5px 10px",marginBottom:8,textAlign:"center",
                  color: tsiOverlay.tradeStatus==='Buy'?"#2ed47a"
                    : tsiOverlay.tradeStatus==='Sell'?"#ff5b6c":"#5a728f",
                  fontWeight:800,fontSize:14,letterSpacing:0.5,
                }}>
                  {tsiOverlay.tradeStatus==='Buy'?"▲  BUY"
                    : tsiOverlay.tradeStatus==='Sell'?"▼  SELL"
                    : "— No Active Trade"}
                  {tsiOverlay.entryPrice && (
                    <div style={{fontWeight:500,fontSize:11,marginTop:2,opacity:0.85}}>
                      @ ₹{parseFloat(tsiOverlay.entryPrice).toFixed(2)}
                    </div>
                  )}
                </div>
                {/* TP / SL / Entry grid */}
                {tsiOverlay.tradeStatus!=='None' && (
                  <div style={{display:"grid",gridTemplateColumns:"36px 1fr",
                    gap:"4px 10px",marginBottom:8,fontSize:12}}>
                    <span style={{color:"#5a728f",alignSelf:"center"}}>TP</span>
                    <span style={{color:"#2ed47a",fontWeight:700,fontSize:13}}>
                      ₹{parseFloat(tsiOverlay.tpPrice||0).toFixed(1)}
                    </span>
                    <span style={{color:"#5a728f",alignSelf:"center"}}>SL</span>
                    <span style={{color:"#ff5b6c",fontWeight:700,fontSize:13}}>
                      ₹{parseFloat(tsiOverlay.slPrice||0).toFixed(1)}
                    </span>
                    <span style={{color:"#5a728f",alignSelf:"center"}}>Entry</span>
                    <span style={{color:"#49c2e4",fontWeight:700,fontSize:13}}>
                      ₹{parseFloat(tsiOverlay.entryPrice||0).toFixed(1)}
                    </span>
                  </div>
                )}
                {/* Divider + TSI values */}
                <div style={{borderTop:"1px solid #1a2c44",paddingTop:6,
                  display:"flex",flexWrap:"wrap",gap:"6px 12px",alignItems:"center"}}>
                  <span style={{color:"#0d9488",fontSize:11,fontWeight:600}}>
                    TSI {tsiOverlay.tsiNow}
                  </span>
                  <span style={{color:"#f97316",fontSize:11,fontWeight:600}}>
                    Sig {tsiOverlay.sigNow}
                  </span>
                  {tsiOverlay.tsiBull&&(
                    <span style={{color:"#2ed47a",fontSize:11,fontWeight:800,
                      background:"rgba(34,197,94,0.15)",borderRadius:0,padding:"1px 5px"}}>
                      ▲ Bull Cross
                    </span>
                  )}
                  {tsiOverlay.tsiBear&&(
                    <span style={{color:"#ff5b6c",fontSize:11,fontWeight:800,
                      background:"rgba(239,68,68,0.15)",borderRadius:0,padding:"1px 5px"}}>
                      ▼ Bear Cross
                    </span>
                  )}
                </div>
                <div style={{marginTop:4,fontSize:10,color:"#475569"}}>
                  ATR(14) {tsiOverlay.atr}
                  {tsiOverlay.pivotHigh&&` · R ₹${tsiOverlay.pivotHigh.price.toFixed(1)}`}
                  {tsiOverlay.pivotLow &&` · S ₹${tsiOverlay.pivotLow.price.toFixed(1)}`}
                </div>
              </div>
            )}
          </div>
          {/* Legend */}
          <div style={{background:"#0b1624",borderTop:"1px solid #1a2c44",
            padding:"5px 14px",display:"flex",gap:14,flexShrink:0,flexWrap:"wrap",alignItems:"center"}}>
            <span style={{fontSize:10,color:"#5a728f"}}>
              <span style={{color:"#e8b339",marginRight:4}}>―</span>EMA{signalParams.ema_fast||9}
            </span>
            <span style={{fontSize:10,color:"#5a728f"}}>
              <span style={{color:"#f59e0b",marginRight:4}}>―</span>EMA{signalParams.ema_slow||50}
            </span>
            {showSignals&&candles?.signals?.length>0&&(
              <>
                <span style={{fontSize:10,color:"#5a728f"}}>
                  <span style={{color:"#2ed47a",marginRight:3}}>▲</span>
                  {candles.signals.filter(s=>s.color==="#2ed47a").length} BUY
                </span>
                <span style={{fontSize:10,color:"#5a728f"}}>
                  <span style={{color:"#ff5b6c",marginRight:3}}>▼</span>
                  {candles.signals.filter(s=>s.color==="#ff5b6c").length} SELL
                </span>
                <span style={{fontSize:10,color:"#5a728f"}}>
                  TSI {signalParams.tsi_long||25}/{signalParams.tsi_short||13} · RSI {signalParams.rsi_period||14}
                </span>
              </>
            )}
            {candles?.zones && candles.signals?.some(m=>m.scans?.includes(8))&&(
              <>
                {candles.zones.demand?.length>0&&(
                  <span style={{fontSize:10,color:"#2ed47a"}}>
                    🟢 {candles.zones.demand.length} Demand
                  </span>
                )}
                {candles.zones.supply?.length>0&&(
                  <span style={{fontSize:10,color:"#ff5b6c"}}>
                    🔴 {candles.zones.supply.length} Supply
                  </span>
                )}
              </>
            )}
            <span style={{fontSize:10,color:"#23405f",marginLeft:"auto"}}>
              {candles?.candles?.length||0} candles · {tf} · {exchange}
            </span>
          </div>
        </div>
      )}

      {/* Backtest Tab */}
      {chartTab==="backtest" && (
        <div style={{flex:1,overflowY:"auto",padding:16}}>
          {/* Config */}
          <div style={{background:"#0b1624",border:"1px solid #1a2c44",borderRadius:0,
            padding:"12px 16px",marginBottom:12}}>
            <div style={{fontSize:12,fontWeight:700,color:"#e7edf5",marginBottom:10}}>
              🔬 Strategy Backtest Config
            </div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center",marginBottom:8}}>
              {/* Mode */}
              <label style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"#8ea2bf"}}>
                Mode
                <select value={btMode} onChange={e=>setBtMode(e.target.value)} style={{...inp,fontSize:11}}>
                  <option value="single">Single symbol ({symbol})</option>
                  <option value="bulk">Multiple symbols</option>
                </select>
              </label>
              {/* TF */}
              <label style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"#8ea2bf"}}>
                Timeframe
                <select value={btTf} onChange={e=>setBtTf(e.target.value)} style={{...inp,fontSize:11}}>
                  {TF_LIST.map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              {/* Days */}
              <label style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"#8ea2bf"}}>
                History
                <select value={btDays} onChange={e=>setBtDays(parseInt(e.target.value))} style={{...inp,fontSize:11}}>
                  {[30,60,90,180,365].map(d=><option key={d} value={d}>{d} days</option>)}
                </select>
              </label>
            </div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center",marginBottom:8}}>
              {[["TP %",btTpPct,setBtTpPct,0.1],["SL %",btSlPct,setBtSlPct,0.1],
                ["Trail %",btTrailPct,setBtTrailPct,0.1]].map(([l,v,s,step])=>(
                <label key={l} style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"#8ea2bf"}}>
                  {l}
                  <input type="number" step={step} value={v}
                    onChange={e=>s(parseFloat(e.target.value)||0)}
                    style={{...inp,width:58,fontSize:11}}/>
                </label>
              ))}
              <label style={{display:"flex",alignItems:"center",gap:6,fontSize:11,color:"#8ea2bf",cursor:"pointer"}}>
                <input type="checkbox" checked={btTrailEn} onChange={e=>setBtTrailEn(e.target.checked)}/>
                Trail SL
              </label>
            </div>
            {btMode==="bulk" && (
              <textarea value={btSymbols} onChange={e=>setBtSymbols(e.target.value)}
                rows={3} placeholder="RELIANCE, INFY, TCS, HDFCBANK..."
                style={{...inp,width:"100%",fontSize:11,resize:"vertical"}}/>
            )}
            <div style={{fontSize:10,color:"#5a728f",marginBottom:8}}>
              Uses current signal params (TSI {signalParams.tsi_long||25}/{signalParams.tsi_short||13}, EMA {signalParams.ema_fast||9}/{signalParams.ema_slow||50})
            </div>
            <button onClick={runBacktest} disabled={btRunning}
              style={{background:btRunning?"#1e3a5f":"rgba(232,179,57,0.12)",
                border:"1px solid #e8b339",borderRadius:0,padding:"8px 20px",
                color:"#49c2e4",fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer"}}>
              {btRunning?"⟳ Running backtest...":"▶ Run Backtest"}
            </button>
          </div>

          {/* Results */}
          {btResult && (
            <div>
              {/* Summary */}
              <div style={{background:"#07101c",border:"1px solid #1a2c44",borderRadius:0,
                padding:"12px 16px",marginBottom:10}}>
                <div style={{fontSize:12,fontWeight:700,color:"#e7edf5",marginBottom:10}}>
                  📊 Summary — {btResult.summary.days}d · {btResult.summary.timeframe} · TP {btResult.summary.tp_pct}% SL {btResult.summary.sl_pct}%{btResult.summary.trail_pct?` Trail ${btResult.summary.trail_pct}%`:""}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:12}}>
                  {[
                    ["Total Trades", btResult.summary.total_trades, "#e7edf5"],
                    ["Win Rate",     `${btResult.summary.win_rate}%`, btResult.summary.win_rate>=50?"#2ed47a":"#ff5b6c"],
                    ["Wins / Losses",`${btResult.summary.wins} / ${btResult.summary.losses}`, "#8ea2bf"],
                    ["Total P&L",    `${btResult.summary.total_pnl_pct>0?"+":""}${btResult.summary.total_pnl_pct}%`,
                      btResult.summary.total_pnl_pct>0?"#2ed47a":"#ff5b6c"],
                  ].map(([k,v,c])=>(
                    <div key={k} style={{background:"#0b1624",borderRadius:0,padding:"8px 10px",textAlign:"center"}}>
                      <div style={{fontSize:10,color:"#5a728f",marginBottom:3}}>{k}</div>
                      <div style={{fontSize:15,fontWeight:700,color:c}}>{v}</div>
                    </div>
                  ))}
                </div>

                {/* Per-symbol table */}
                {btResult.symbols?.length>1 && (
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                      <thead>
                        <tr style={{background:"#0d1117"}}>
                          {["Symbol","Trades","Win%","Avg Win","Avg Loss","R:R","P&L%","MaxDD"].map(h=>(
                            <th key={h} style={{padding:"5px 8px",color:"#5a728f",fontWeight:600,textAlign:"right",
                              borderBottom:"1px solid #1a2c44",whiteSpace:"nowrap"}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {btResult.symbols.map(s=>(
                          <tr key={s.symbol} style={{borderBottom:"1px solid #0d1117"}}>
                            <td style={{padding:"5px 8px",color:"#e7edf5",fontWeight:700}}>{s.symbol}</td>
                            <td style={{padding:"5px 8px",color:"#8ea2bf",textAlign:"right"}}>{s.total}</td>
                            <td style={{padding:"5px 8px",textAlign:"right",
                              color:s.win_rate>=50?"#2ed47a":"#ff5b6c",fontWeight:700}}>{s.win_rate}%</td>
                            <td style={{padding:"5px 8px",color:"#2ed47a",textAlign:"right"}}>+{s.avg_win}%</td>
                            <td style={{padding:"5px 8px",color:"#ff5b6c",textAlign:"right"}}>-{s.avg_loss}%</td>
                            <td style={{padding:"5px 8px",color:"#49c2e4",textAlign:"right"}}>{s.rr}</td>
                            <td style={{padding:"5px 8px",textAlign:"right",
                              color:s.total_pnl_pct>0?"#2ed47a":"#ff5b6c",fontWeight:700}}>
                              {s.total_pnl_pct>0?"+":""}{s.total_pnl_pct}%</td>
                            <td style={{padding:"5px 8px",color:"#f59e0b",textAlign:"right"}}>{s.max_drawdown}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Trade list */}
              {btResult.trades?.length>0 && (
                <div style={{background:"#07101c",border:"1px solid #1a2c44",borderRadius:0,padding:"12px 16px"}}>
                  <div style={{fontSize:11,fontWeight:700,color:"#8ea2bf",marginBottom:8}}>
                    Trade Log ({btResult.trades.length} trades shown)
                  </div>
                  <div style={{maxHeight:300,overflowY:"auto"}}>
                    {btResult.trades.slice().reverse().map((t,i)=>(
                      <div key={i} style={{display:"flex",gap:10,padding:"4px 0",
                        borderBottom:"1px solid #0b1624",alignItems:"center",fontSize:11}}>
                        <span style={{color:t.hit==="TP"?"#2ed47a":"#ff5b6c",fontWeight:700,width:24}}>
                          {t.hit==="TP"?"✅":"🛑"}
                        </span>
                        <span style={{color:"#49c2e4",fontWeight:700,width:70}}>{t.symbol}</span>
                        <span style={{color:t.side==="BUY"?"#2ed47a":"#ff5b6c",width:36}}>{t.side}</span>
                        <span style={{color:"#8ea2bf",width:55}}>₹{t.entry}</span>
                        <span style={{color:"#5a728f"}}>→</span>
                        <span style={{color:"#8ea2bf",width:55}}>₹{t.exit}</span>
                        <span style={{fontWeight:700,width:60,
                          color:t.pnl_pct>0?"#2ed47a":"#ff5b6c"}}>
                          {t.pnl_pct>0?"+":""}{t.pnl_pct}%
                        </span>
                        <span style={{color:"#5a728f",fontSize:10}}>{t.candles}c</span>
                        <span style={{color:"#23405f",fontSize:9,marginLeft:"auto"}}>
                          {t.entry_time?.slice(5,16)||""}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ── useTrailingSLSuggestions ──────────────────────────────────────────────────
// LEGAL trailing SL for trader users:
// Monitors LTP and SUGGESTS when SL should trail — user taps to confirm.
// Computer suggests → human confirms → NOT algo trading (SEBI compliant).
function useTrailingSLSuggestions({ watchers, livePrices, userRole, onShowToast, tgChat }) {
  const [suggestions, setSuggestions] = useState([]); // [{watcherId, symbol, newSL, oldSL, ltp}]
  const lastNotifiedRef = useRef({});

  useEffect(()=>{
    if(userRole === "admin") return; // admin uses auto-trail via watcher, not suggestions

    const pending = [];
    for(const w of watchers) {
      if(w.status !== "WATCHING" || !w.trailEnabled || !w.trailPct) continue;
      const liveQ = livePrices[w.symbol] || livePrices[`${w.exchange}:${w.symbol}`];
      const ltp   = parseFloat(liveQ?.ltp || liveQ?.last_price || 0);
      if(!ltp || ltp <= 0) continue;

      const trailPct = parseFloat(w.trailPct || 1);
      const hwm      = parseFloat(w.highWaterMark || w.entryPrice || 0);
      const curSL    = parseFloat(w.slPrice || 0);

      if(w.side === "BUY" && ltp > hwm) {
        const suggestedSL = parseFloat((ltp * (1 - trailPct/100)).toFixed(2));
        if(suggestedSL > curSL + 0.01) { // meaningfully better SL
          pending.push({
            watcherId: w.id, gttId: w.gttId,
            symbol: w.symbol, exchange: w.exchange||"NSE",
            side: w.side, qty: w.qty,
            entryPrice: w.entryPrice,
            ltp, newSL: suggestedSL, oldSL: curSL,
            trailPct, tpPrice: w.tpPrice, tpEnabled: w.tpEnabled,
          });
        }
      } else if(w.side === "SELL" && ltp < hwm) {
        const suggestedSL = parseFloat((ltp * (1 + trailPct/100)).toFixed(2));
        if(!curSL || suggestedSL < curSL - 0.01) {
          pending.push({
            watcherId: w.id, gttId: w.gttId,
            symbol: w.symbol, exchange: w.exchange||"NSE",
            side: w.side, qty: w.qty,
            entryPrice: w.entryPrice,
            ltp, newSL: suggestedSL, oldSL: curSL,
            trailPct, tpPrice: w.tpPrice, tpEnabled: w.tpEnabled,
          });
        }
      }
    }
    setSuggestions(pending);

    // Telegram alert for new suggestions (once per symbol per 10 min)
    if(tgChat && pending.length > 0) {
      const now = Date.now();
      for(const s of pending) {
        const lastSent = lastNotifiedRef.current[s.symbol] || 0;
        if(now - lastSent > 600000) { // 10 min cooldown
          lastNotifiedRef.current[s.symbol] = now;
          apiFetch("/alert/telegram", {
            method:"POST", headers:{"Content-Type":"application/json"},
            body: JSON.stringify({
              message: `📈 <b>Trail SL — ${s.symbol}</b>\nLTP ₹${s.ltp} moved past peak\n<b>Suggested SL: ₹${s.newSL}</b> (was ₹${s.oldSL})\n→ Open RABTRADE to update with 1 tap`,
              chat_id: tgChat,
            })
          }).catch(()=>{});
        }
      }
    }
  },[watchers, livePrices, userRole, tgChat]);

  return suggestions;
}

function AIPanel({ results, loading, error, visible, onClose, onTrade, onShowToast,
                   tabMode="equity", autoTrade, setAutoTrade, minAutoConf, setMinAutoConf,
                   autoFired={}, marginPct, setMarginPct, maxDaily, setMaxDaily,
                   maxScans, setMaxScans, scanCount, balance, onAddVirtual=null,
                   isAdmin=false, canTrade=false, setStrategyPopup=null }) {
  if(!visible) return null;
  const buyCount  = results.filter(r=>r.signal==="BUY").length;
  const sellCount = results.filter(r=>r.signal==="SELL").length;
  const holdCount = results.filter(r=>r.signal==="HOLD").length;

  // Balance display helper — Delta uses $ balance, others use ₹ margin
  const isDeltaTab = tabMode==="delta";
  const deltaAvail = balance?._delta ? parseFloat(balance.available_balance||0) : null;
  const eqAvail    = !isDeltaTab && balance ? getMargin(balance, "nrml") : null;
  const eqMIS      = !isDeltaTab && balance ? getMargin(balance, "mis")  : null;
  const eqBreakdown = !isDeltaTab && balance ? getMarginBreakdown(balance) : null;
  const comAvail = balance?.commodity
    ? (parseFloat(balance.commodity.available||0) + parseFloat(balance.commodity.collateral||0))
    : null;
  const today      = new Date().toISOString().slice(0,10);
  const scansToday = scanCount?.date === today ? scanCount.count : 0;

  return (
    <div style={{borderTop:"2px solid rgba(163,113,247,0.5)",
      background:"rgba(10,14,23,0.98)",flexShrink:0,maxHeight:"70vh",
      display:"flex",flexDirection:"column"}}>

      {/* ── Header ── */}
      <div style={{padding:"8px 12px",borderBottom:"1px solid rgba(163,113,247,0.2)",
        flexShrink:0,background:"rgba(163,113,247,0.06)"}}>
        <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
          <span style={{fontSize:11,fontWeight:700,color:"#49c2e4"}}>🤖 AI Analysis</span>
          {results.length>0&&(
            <div style={{display:"flex",gap:4}}>
              <span style={{fontSize:12,padding:"1px 7px",borderRadius:0,
                background:"rgba(22,163,74,0.15)",color:"#2ed47a",fontWeight:700}}>▲ {buyCount}</span>
              <span style={{fontSize:12,padding:"1px 7px",borderRadius:0,
                background:"rgba(220,38,38,0.15)",color:"#ff5b6c",fontWeight:700}}>▼ {sellCount}</span>
              <span style={{fontSize:12,padding:"1px 7px",borderRadius:0,
                background:"rgba(139,148,158,0.1)",color:"#8ea2bf",fontWeight:700}}>◆ {holdCount}</span>
            </div>
          )}
          {loading&&<span style={{fontSize:12,color:"#49c2e4",animation:"pulse 1s infinite"}}>⟳ Analyzing...</span>}

          {/* ── Balance pill — $ for Delta, ₹ for equity/mcx ── */}
          {(deltaAvail!==null||eqAvail!==null)&&(
            <div style={{display:"flex",flexDirection:"column",gap:1}}>
              {isDeltaTab&&deltaAvail!==null&&(
                <span style={{fontSize:12,padding:"2px 8px",borderRadius:0,
                  background:deltaAvail<50?"rgba(220,38,38,0.15)":deltaAvail<200?"rgba(96,165,250,0.12)":"rgba(22,163,74,0.1)",
                  color:deltaAvail<50?"#ff5b6c":deltaAvail<200?"#e8b339":"#2ed47a",
                  fontWeight:700,border:"1px solid currentColor",opacity:0.9}}>
                  💰 ${deltaAvail.toFixed(2)} Delta balance
                  {deltaAvail < 50 && " ⚠ LOW"}
                </span>
              )}
              {!isDeltaTab&&eqAvail!==null&&(
                <span style={{fontSize:12,padding:"2px 8px",borderRadius:0,
                  background: eqAvail<5000?"rgba(220,38,38,0.15)":eqAvail<20000?"rgba(96,165,250,0.12)":"rgba(22,163,74,0.1)",
                  color:      eqAvail<5000?"#ff5b6c"           :eqAvail<20000?"#e8b339"             :"#2ed47a",
                  fontWeight:700,border:"1px solid currentColor",opacity:0.9}}>
                  💰 ₹{eqAvail.toLocaleString("en-IN",{maximumFractionDigits:0})} total
                  {eqAvail < 5000 && " ⚠ LOW"}
                </span>
              )}
              {!isDeltaTab&&eqBreakdown&&eqBreakdown.collateral>0&&(
                <span style={{fontSize:10,color:"#8ea2bf",paddingLeft:8}}>
                  Cash ₹{Math.round(eqBreakdown.cash).toLocaleString("en-IN")} + Pledge ₹{Math.round(eqBreakdown.collateral).toLocaleString("en-IN")}
                </span>
              )}
            </div>
          )}
          {/* Scan counter */}
          {maxScans>0&&(
            <span style={{fontSize:12,color:"#5a728f"}}>
              Scans today: {scansToday}/{maxScans}
            </span>
          )}
          {maxScans===0&&scansToday>0&&(
            <span style={{fontSize:12,color:"#5a728f"}}>
              Scans today: {scansToday}
            </span>
          )}

          <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
            {setAutoTrade&&isAdmin&&(
              <button onClick={()=>setAutoTrade(p=>!p)}
                style={{background:autoTrade?"rgba(220,38,38,0.15)":"rgba(22,163,74,0.08)",
                  border:`1px solid ${autoTrade?"rgba(248,81,73,0.5)":"rgba(63,185,80,0.4)"}`,
                  borderRadius:0,padding:"3px 10px",
                  color:autoTrade?"#ff5b6c":"#2ed47a",
                  fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                {autoTrade?"⏹ AUTO ON":"▶ AUTO"}
              </button>
            )}
            {!isAdmin&&(
              <span style={{fontSize:11,color:"#5a728f",padding:"2px 6px",
                background:"rgba(96,165,250,0.05)",border:"1px solid #1a2c44",borderRadius:0}}>
                📡 Signal only
              </span>
            )}
            <button onClick={onClose}
              style={{background:"none",border:"none",color:"#5a728f",
                fontSize:14,cursor:"pointer",padding:"2px 4px"}}>✕</button>
          </div>
        </div>

        {/* ── Low balance warning ── */}
        {!isDeltaTab&&eqAvail!==null && eqAvail < 5000 && (
          <div style={{marginTop:6,padding:"6px 10px",
            background:"rgba(220,38,38,0.08)",borderRadius:0,
            border:"1px solid rgba(248,81,73,0.3)",fontSize:12,color:"#ff5b6c"}}>
            ⚠ Total margin low — ₹{eqAvail.toLocaleString("en-IN",{maximumFractionDigits:0})} (cash + pledged stocks).
            Add funds or pledge more holdings in Zerodha Console to increase collateral.
          </div>
        )}
        {isDeltaTab&&deltaAvail!==null && deltaAvail < 50 && (
          <div style={{marginTop:6,padding:"6px 10px",
            background:"rgba(220,38,38,0.08)",borderRadius:0,
            border:"1px solid rgba(248,81,73,0.3)",fontSize:12,color:"#ff5b6c"}}>
            ⚠ Delta balance low — ${deltaAvail.toFixed(2)}. Add USDT/BTC to your Delta Exchange account.
          </div>
        )}

        {autoTrade&&isAdmin&&(
          <div style={{marginTop:6,padding:"8px 10px",
            background:"rgba(220,38,38,0.06)",borderRadius:0,
            border:"1px solid rgba(248,81,73,0.2)"}}>
            <div style={{fontSize:12,color:"#ff5b6c",fontWeight:700,marginBottom:6}}>
              ⚠ AUTO TRADE ACTIVE — LIMIT orders placed automatically (no confirmation)
            </div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
              {setMarginPct&&(
                <div style={{display:"flex",alignItems:"center",gap:4}}>
                  <span style={{fontSize:11,color:"#8ea2bf"}}>Margin/trade:</span>
                  <input type="range" min={2} max={30} step={0.5} value={marginPct||10}
                    onChange={e=>setMarginPct(parseInt(e.target.value))}
                    style={{width:55,accentColor:"#ff5b6c"}}/>
                  <span style={{fontSize:10,color:"#e7edf5",fontWeight:700,minWidth:28}}>{marginPct||10}%</span>
                  {eqAvail&&(
                    <span style={{fontSize:11,color:"#8ea2bf"}}>
                      = ₹{Math.round(eqAvail*(marginPct||10)/100).toLocaleString("en-IN")} cap
                    </span>
                  )}
                </div>
              )}
              {setMaxDaily&&(
                <div style={{display:"flex",alignItems:"center",gap:4}}>
                  <span style={{fontSize:11,color:"#8ea2bf"}}>Max trades/day:</span>
                  {[3,5,10,15].map(n=>(
                    <button key={n} onClick={()=>setMaxDaily(n)}
                      style={{background:(maxDaily||5)===n?"rgba(248,81,73,0.25)":"#0f1d30",
                        border:`1px solid ${(maxDaily||5)===n?"#ff5b6c":"#23405f"}`,
                        borderRadius:0,padding:"2px 8px",
                        color:(maxDaily||5)===n?"#ff5b6c":"#8ea2bf",
                        fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
                      {n}
                    </button>
                  ))}
                </div>
              )}
              {setMinAutoConf&&(
                <div style={{display:"flex",alignItems:"center",gap:4}}>
                  <span style={{fontSize:11,color:"#8ea2bf"}}>Min conf:</span>
                  <input type="range" min={50} max={90} step={5} value={minAutoConf||75}
                    onChange={e=>setMinAutoConf(parseInt(e.target.value))}
                    style={{width:50,accentColor:"#ff5b6c"}}/>
                  <span style={{fontSize:10,color:"#ff5b6c",fontWeight:700,minWidth:24}}>{minAutoConf||75}%</span>
                </div>
              )}
              {setMaxScans&&(
                <div style={{display:"flex",alignItems:"center",gap:4}}>
                  <span style={{fontSize:11,color:"#8ea2bf"}}>Scans/day:</span>
                  {[0,2,5,10].map(n=>(
                    <button key={n} onClick={()=>setMaxScans(n)}
                      style={{background:(maxScans||0)===n?"rgba(0,212,170,0.2)":"#0f1d30",
                        border:`1px solid ${(maxScans||0)===n?"#e8b339":"#23405f"}`,
                        borderRadius:0,padding:"2px 8px",
                        color:(maxScans||0)===n?"#e8b339":"#8ea2bf",
                        fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
                      {n===0?"∞":n}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Results grid ── */}
      <div style={{flex:1,overflowY:"auto",padding:"8px 10px",
        WebkitOverflowScrolling:"touch"}}>

        {loading&&(
          <div style={{textAlign:"center",padding:20,color:"#49c2e4",fontSize:11}}>
            ⟳ Claude analyzing Technical + Volume + News + F&O + Institutional...<br/>
            <span style={{fontSize:12,color:"#5a728f"}}>Takes ~10–20 seconds per symbol</span>
          </div>
        )}
        {error&&(
          <div style={{background:"rgba(220,38,38,0.08)",border:"1px solid rgba(248,81,73,0.3)",
            borderRadius:0,padding:"10px 12px",fontSize:11,color:"#ff5b6c",marginBottom:8}}>
            ⚠ {error}
            {(error.includes("ANTHROPIC")||error.includes("API"))&&(
              <div style={{marginTop:4,fontSize:12,color:"#8ea2bf"}}>
                Set GEMINI_API_KEY in /etc/systemd/system/rabtrade.service
              </div>
            )}
          </div>
        )}

        {/* Pending orders strip — show orders placed but not yet filled */}
        {Object.values(autoFired).filter(v=>v?.status==="PENDING").length>0&&(
          <div style={{marginBottom:8,padding:"6px 10px",
            background:"rgba(96,165,250,0.08)",borderRadius:0,
            border:"1px solid rgba(96,165,250,0.25)"}}>
            <div style={{fontSize:12,color:"#e8b339",fontWeight:700,marginBottom:6,letterSpacing:1}}>
              ⏳ PENDING ORDERS — waiting for fill (watcher activates on fill)
            </div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {Object.values(autoFired).filter(v=>v?.status==="PENDING").map((o,i)=>(
                <div key={i} style={{background:"rgba(96,165,250,0.1)",border:"1px solid rgba(96,165,250,0.3)",
                  borderRadius:0,padding:"4px 10px",fontSize:12}}>
                  <span style={{color:o.side==="BUY"?"#2ed47a":"#ff5b6c",fontWeight:700}}>{o.side}</span>
                  <span style={{color:"#e7edf5",marginLeft:4}}>{o.symbol}</span>
                  <span style={{color:"#8ea2bf",marginLeft:4}}>x{o.qty} @ ₹{o.price}</span>
                  <span style={{color:"#5a728f",marginLeft:4,fontSize:11}}>#{o.order_id?.slice(-6)}</span>
                </div>
              ))}
            </div>
            <div style={{fontSize:11,color:"#5a728f",marginTop:4}}>
              TP/SL watcher only activates after order is confirmed filled by broker
            </div>
          </div>
        )}

        {/* Box grid — 2 columns on wide, 1 on narrow */}
        {!loading&&results.length>0&&(
          <div style={{display:"grid",
            gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:8}}>
            {results.map((r,i)=>(
              <AISignalCard
                key={r.symbol||i}
                result={r}
                onTrade={onTrade}
                onShowToast={onShowToast}
                tabMode={tabMode}
                autoFired={!!(autoFired[r.symbol])}
                onAddVirtual={onAddVirtual}
                canTrade={canTrade}
                setStrategyPopup={setStrategyPopup}
              />
            ))}
          </div>
        )}

        {!loading&&!error&&results.length===0&&(
          <div style={{textAlign:"center",padding:30,color:"#5a728f",fontSize:11}}>
            Click 🤖 Scan to analyze current watchlist
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// UNIFIED EQUITY TAB — Intraday / Investment / F&O  (replaces ScannerTab + AutoFOTab)
// ══════════════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════════════
// SCAN CARD GRID — Navy theme, shows signals + buy/sell volume + trade button
// ════════════════════════════════════════════════════════════════════════════
// ── Pre-entry trade validation button ────────────────────────────────────────
// Re-checks a single symbol live before entry (pullback/entry-zone + 1H trend +
// AI) so the user doesn't chase a moved signal into a high-premium CE/PE that
// then retraces into their stoploss. Calls POST /scan/validate.
function ValidateEntryButton({ symbol, exchange="NSE", timeframe="15min", direction="BUY", signalPrice=null, preset=null, onResult=null }) {
  const [loading, setLoading] = useState(false);
  const [res, setRes]         = useState(preset);
  const [err, setErr]         = useState(null);

  // Adopt batch-validation results pushed down from "Validate All"
  useEffect(()=>{ if(preset) setRes(preset); }, [preset]);

  const vColor = (v)=> v==="GO"?"#2ed47a":v==="WAIT"?"#f59e0b":v==="SKIP"?"#ff5b6c":"#8ea2bf";
  const vIcon  = (v)=> v==="GO"?"✓":v==="WAIT"?"⏸":v==="SKIP"?"✕":"?";

  const run = async () => {
    setLoading(true); setErr(null); setRes(null);
    try {
      const out = await apiFetch("/scan/validate", {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ symbol, exchange, timeframe, direction,
                               signal_price: signalPrice||null }),
      });
      setRes(out);
      if(onResult) onResult(out);
    } catch(e) {
      setErr(e.message||"Validation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{marginTop:2}}>
      <button onClick={run} disabled={loading}
        title="Re-check this trade live before entry — pullback/entry zone, 1D trend, and AI verdict"
        style={{background:"rgba(56,189,248,0.08)",
          border:"1px solid rgba(56,189,248,0.35)",borderRadius:0,
          padding:"3px 0",width:"100%",color:"#38bdf8",
          fontFamily:"inherit",fontSize:10,fontWeight:700,
          cursor:loading?"wait":"pointer",textAlign:"center"}}>
        {loading?"⏳ Checking entry…":"🔎 Validate Entry"}
      </button>
      {err&&<div style={{fontSize:9,color:"#ff5b6c",marginTop:3}}>⚠ {err}</div>}
      {res&&(
        <div style={{marginTop:4,padding:"6px 7px",background:"#0b1624",
          border:`1px solid ${vColor(res.verdict)}55`,
          borderLeft:`3px solid ${vColor(res.verdict)}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontSize:12,fontWeight:800,color:vColor(res.verdict),letterSpacing:0.5}}>
              {vIcon(res.verdict)} {res.verdict}
            </span>
            {res.suggested_entry!=null&&(
              <span style={{fontSize:9,color:"#8ea2bf"}}>entry ₹{res.suggested_entry}</span>
            )}
          </div>
          <div style={{fontSize:9,color:"#8ea2bf",marginTop:3,display:"flex",gap:6,flexWrap:"wrap"}}>
            {res.extension_atr!=null&&<span>ext {res.extension_atr} ATR</span>}
            {res.rr!=null&&<span>RR {res.rr}</span>}
            {res.moved_pct!=null&&(
              <span style={{color:Math.abs(res.moved_pct)>1.5?"#f59e0b":"#8ea2bf"}}>
                moved {res.moved_pct>0?"+":""}{res.moved_pct}%
              </span>
            )}
            {res.htf_trend&&(
              <span style={{color:res.htf_aligned?"#2ed47a":"#ff5b6c"}}>{res.htf_tf||"HTF"} {res.htf_trend}{res.htf_aligned?" ✓":" ✗ opposes"}</span>
            )}
          </div>
          {res.rule_reasons&&res.rule_reasons.length>0&&(
            <div style={{fontSize:9,color:"#b9c6d8",marginTop:3,lineHeight:1.4}}>
              {res.rule_reasons[0]}
            </div>
          )}
          {res.ai&&res.ai.verdict&&(
            <div style={{fontSize:9,color:"#a78bfa",marginTop:3,lineHeight:1.4}}>
              🤖 {res.ai.verdict} ({res.ai.confidence}%): {res.ai.reason}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Live VPS TP/SL tracker — what the server is watching right now ────────────
function VpsTpSlTracker({ authed }) {
  const [rows, setRows] = useState([]);
  const [closed, setClosed] = useState([]);
  useEffect(()=>{
    if(!authed) return;
    const load=()=>{
      apiFetch("/vps-scanner/positions").then(d=>setRows(d.positions||[])).catch(()=>{});
      apiFetch("/vps-scanner/paper").then(d=>setClosed((d.closed||[]).slice(0,8))).catch(()=>{});
    };
    load(); const t=setInterval(load, 20000);
    return ()=>clearInterval(t);
  },[authed]);
  const dc = (d)=> d==="BUY"?"#2ed47a":"#ff5b6c";
  return (
    <div style={{display:"flex",flexDirection:"column",gap:4}}>
      <div style={{fontSize:10,fontWeight:700,color:"#49c2e4"}}>🎯 VPS TP/SL Tracker · {rows.length} active</div>
      {rows.length===0 && <div style={{fontSize:10,color:"#5a728f"}}>No active VPS positions.</div>}
      {rows.map((r,i)=>(
        <div key={i} style={{background:"#0b1624",border:"1px solid #1a2c44",
          borderLeft:`3px solid ${r.paper?"#60a5fa":"#2ed47a"}`,padding:"6px 8px",fontSize:10}}>
          <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
            {r.paper&&<span style={{color:"#60a5fa",fontWeight:700}}>📝</span>}
            <span style={{fontWeight:700,color:"#e7edf5"}}>{r.instrument}</span>
            <span style={{color:dc(r.dir),fontWeight:700}}>{r.dir}</span>
            <span style={{color:"#8ea2bf"}}>x{r.qty}</span>
            {r.gtt_id&&<span style={{color:"#a78bfa",fontSize:8,fontWeight:700}}>GTT</span>}
            <span style={{marginLeft:"auto",color:"#8ea2bf"}}>@ {r.entry}</span>
          </div>
          <div style={{display:"flex",gap:10,marginTop:2}}>
            <span style={{color:"#5a728f"}}>und {r.underlying}</span>
            <span style={{color:"#2ed47a"}}>TP {r.tp}</span>
            <span style={{color:"#ff5b6c"}}>SL {r.sl}</span>
          </div>
          {r.signal&&r.signal!=="—"&&<div style={{fontSize:9,color:"#a78bfa",marginTop:2}}>🏷 {r.signal}</div>}
        </div>
      ))}
      {closed.length>0 && (
        <div style={{fontSize:9,color:"#5a728f",marginTop:2,lineHeight:1.5}}>
          Recent paper closes: {closed.map(c=>`${c.option_sym} ${(c.status||"").replace("CLOSED_","").replace("EXITED_","")} ₹${c.pnl||0}`).join(" · ")}
        </div>
      )}
    </div>
  );
}

// ── VPS Trade Summary — P&L of auto-trade + paper, open (unreal) + closed ─────
function VpsSummary({ authed }) {
  const [s, setS] = useState(null);
  useEffect(()=>{
    if(!authed) return;
    const load=()=>apiFetch("/vps-scanner/summary").then(setS).catch(()=>{});
    load(); const t=setInterval(load, 20000);
    return ()=>clearInterval(t);
  },[authed]);
  if(!s) return null;
  const pc = (v)=> (parseFloat(v)||0)>0?"#2ed47a":(parseFloat(v)||0)<0?"#ff5b6c":"#8ea2bf";
  const Blk = ({title, b, color})=>(
    <div style={{flex:1,minWidth:150,background:"#0b1624",border:"1px solid #1a2c44",
      borderLeft:`3px solid ${color}`,padding:"7px 9px"}}>
      <div style={{fontSize:11,fontWeight:700,color,marginBottom:3}}>{title}</div>
      <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"#8ea2bf"}}>
        <span>Open {b.open}</span>
        <span style={{color:pc(b.unrealized),fontWeight:700}}>unreal ₹{b.unrealized}</span>
      </div>
      <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"#8ea2bf",marginTop:2}}>
        <span>Closed {b.closed}{(b.wins||b.losses)?` · ${b.wins}W/${b.losses}L`:""}</span>
        <span style={{color:pc(b.realized),fontWeight:700}}>real ₹{b.realized}</span>
      </div>
    </div>
  );
  return (
    <div style={{display:"flex",flexDirection:"column",gap:5,marginBottom:8}}>
      <div style={{fontSize:12,fontWeight:700,color:"#49c2e4"}}>📊 VPS Trade Summary</div>
      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
        <Blk title="🤖 Auto-trade" b={s.real}  color="#2ed47a" />
        <Blk title="📝 Paper"      b={s.paper} color="#60a5fa" />
      </div>
    </div>
  );
}

// ── SELL CE/PE index ideas — on-demand view of the sell-suggestion engine ─────
function VpsSellIdeas() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(null);   // "index" | "stocks"
  const run = async (m, withTg) => {
    setMode(m); setLoading(true);
    const path = m === "stocks" ? "/vps-scanner/sell-stocks" : "/vps-scanner/sell-suggestions";
    try { setData(await apiFetch(`${path}?ai=true${withTg ? "&send_telegram=true" : ""}`)); }
    catch (e) { setData({ error: e.message || "failed" }); }
    finally { setLoading(false); }
  };
  const sugs = (data && data.suggestions) || [];
  const sideC = s => s === "CE" ? "#ff5b6c" : "#2ed47a";  // sell CE=bearish, sell PE=bullish
  const roiC  = ok => ok ? "#2ed47a" : "#e8b339";
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <button onClick={() => run("index", false)} disabled={loading}
          style={{ background: "rgba(232,179,57,0.15)", border: "1px solid #e8b339", borderRadius: 0,
            padding: "4px 12px", color: "#e8b339", fontFamily: "inherit", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>
          {loading && mode === "index" ? "Scanning…" : "🔍 Index Ideas"}
        </button>
        <button onClick={() => run("stocks", false)} disabled={loading}
          style={{ background: "rgba(96,165,250,0.15)", border: "1px solid #60a5fa", borderRadius: 0,
            padding: "4px 12px", color: "#60a5fa", fontFamily: "inherit", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>
          {loading && mode === "stocks" ? "Scanning…" : "🏦 Stock Ideas"}
        </button>
        {mode && <button onClick={() => run(mode, true)} disabled={loading}
          style={{ background: "transparent", border: "1px solid #23405f", borderRadius: 0,
            padding: "4px 10px", color: "#8ea2bf", fontFamily: "inherit", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>
          + Telegram
        </button>}
        {data && data.generated_at &&
          <span style={{ fontSize: 9, color: "#5a728f" }}>
            {data.count} {mode === "stocks" ? "stock" : "index"} · {new Date(data.generated_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
          </span>}
      </div>
      {data && data.error && <div style={{ fontSize: 11, color: "#ff5b6c", marginTop: 4 }}>{data.error}</div>}
      {mode === "stocks" && data && data.stats &&
        <div style={{ fontSize: 9, color: "#5a728f", marginTop: 3 }}>
          scanned {data.stats.evaluated} · <b style={{ color: "#8ea2bf" }}>{data.stats.ideas} ideas</b> · skipped: no-signal {data.stats.no_signal} · event {data.stats.event_upcoming} · illiquid {data.stats.illiquid} · below-ROI {data.stats.below_roi}
        </div>}
      {mode === "stocks" && data && !data.error && sugs.length === 0 &&
        <div style={{ fontSize: 10, color: "#8ea2bf", marginTop: 4 }}>No liquid stock setup to show today. Best sell candidates are calm names + stocks whose event just passed (IV crush) — some days there's nothing worth flagging.</div>}
      {mode === "stocks" && sugs.length > 0 && sugs.some(s => s.below_target) &&
        <div style={{ fontSize: 10, color: "#e8b339", marginTop: 4 }}>⚠️ Nothing cleared the {data.min_roi || 4}% ROI bar today — showing the best calm/post-event setups <b>below target</b>. Premium is thin; verify the risk is worth it before selling.</div>}
      <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 6 }}>
        {sugs.map(s => (
          <div key={`${s.index}-${s.strike}`} style={{ background: "#0f1d30", border: "1px solid #1a2c44",
            borderLeft: `3px solid ${s.ok ? sideC(s.side) : "#5a728f"}`, padding: "7px 10px" }}>
            {s.ok ? (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#e7edf5" }}>
                    <span style={{ color: sideC(s.side) }}>SELL {s.side}</span> {s.index} {Math.round(s.strike)}
                    {s.post_event && <span style={{ marginLeft: 5, fontSize: 9, color: "#60a5fa", fontWeight: 700 }}>🎯 POST-EVENT{s.days_to_event != null ? ` ~${Math.abs(s.days_to_event)}d` : ""}</span>}
                    {s.calm && <span style={{ marginLeft: 5, fontSize: 9, color: "#2ed47a", fontWeight: 700 }}>🟢 CALM{s.realized_vol_pct != null ? ` ${s.realized_vol_pct}%` : ""}</span>}
                    {s.iv_spike && <span style={{ marginLeft: 5, fontSize: 9, color: "#e8b339", fontWeight: 700 }}>⚡IV SPIKE</span>}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: roiC(s.roi_meets_target) }}>
                    ROI ~{s.roi_pct}% {s.roi_meets_target ? "✅" : (s.below_target ? "⚠️ below bar" : "⚠️")}
                  </span>
                </div>
                {s.tech_reasons && s.tech_reasons.length > 0 &&
                  <div style={{ fontSize: 10, color: "#2ed47a", marginTop: 2 }}>📶 {s.tech_reasons.join(" + ")} <span style={{ color: "#5a728f" }}>(daily)</span></div>}
                <div style={{ fontSize: 10, color: "#8ea2bf", marginTop: 2 }}>
                  exp {s.expiry} ({s.dte}d) · spot {s.spot} · {s.otm_pct}% OTM · bias {s.bias} ({s.trend})
                </div>
                <div style={{ fontSize: 10, color: "#8ea2bf" }}>
                  Prem ₹{s.premium} × {s.lot} · margin ₹{Number(s.margin).toLocaleString("en-IN")} · IV {s.iv_annual_pct}%{s.oi ? ` · OI ${Number(s.oi).toLocaleString("en-IN")}` : ""}
                </div>
                <div style={{ fontSize: 10, color: "#5a728f" }}>
                  OI resistance {s.resistance_oi_strike} · support {s.support_oi_strike}
                </div>
                {s.ai && s.ai.note && <div style={{ fontSize: 10, color: "#a78bfa", marginTop: 2 }}>🤖 {s.ai.note}</div>}
                {s.ai && s.ai.iv_collapse_likely !== undefined && s.ai.iv_collapse_likely !== null &&
                  <div style={{ fontSize: 10, color: "#8ea2bf" }}>📉 IV collapse likely: {String(s.ai.iv_collapse_likely)}</div>}
                {s.ai && s.ai.news_risk && <div style={{ fontSize: 10, color: "#8ea2bf" }}>📰 {s.ai.news_risk}</div>}
              </>
            ) : (
              <div style={{ fontSize: 11, color: "#8ea2bf" }}>
                <b style={{ color: "#e7edf5" }}>{s.index}</b> — no idea <span style={{ color: "#5a728f" }}>({s.reason || "n/a"})</span>
              </div>
            )}
          </div>
        ))}
      </div>
      {!data && <div style={{ fontSize: 9, color: "#5a728f", marginTop: 4 }}>Index Ideas = OTM short strikes for NIFTY/BANKNIFTY/etc (weekly). Stock Ideas = needs ≥1 DAILY core signal (TSI / RSI / Volume) first — D/S zone & support/resistance only confirm; core sets the side (bull → sell PE, bear → sell CE); then news, OI, post-event IV crush. Calm + just-had-event names, no upcoming event, liquid/high-OI, ≥4% ROI (monthly). Idea only — verify before selling.</div>}
    </div>
  );
}

// ── VPS AI trade report — what signals / combos / stocks are working, over a
//    selectable 7 / 30 / 90-day window, plus the full per-trade history (with the
//    scan signal that fired each trade) from the durable ledger. ───────────────
function VpsAiReport({ authed }) {
  const [rep, setRep] = useState(null);
  const [hist, setHist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);
  const [showHist, setShowHist] = useState(false);
  const run = async(d)=>{
    const w = d || days;
    setLoading(true);
    try {
      const [r, h] = await Promise.all([
        apiFetch(`/vps-scanner/ai-analysis?days=${w}`),
        apiFetch(`/vps-scanner/history?days=${w}`).catch(()=>null),
      ]);
      setRep(r); setHist(h);
    }
    catch(e){ setRep({error: e.message||"failed"}); }
    finally { setLoading(false); }
  };
  const s = (rep&&rep.stats)||{};
  const ai = (rep&&rep.ai)||{};
  const pc = (v)=> (parseFloat(v)||0)>=0?"#2ed47a":"#ff5b6c";
  const dc = (d)=> d==="BUY"?"#2ed47a":"#ff5b6c";
  const Table = ({title, obj})=>{
    const rows = Object.entries(obj||{}).slice(0,5);
    if(!rows.length) return null;
    return (
      <div style={{marginTop:6}}>
        <div style={{fontSize:10,fontWeight:700,color:"#8ea2bf",marginBottom:2}}>{title}</div>
        {rows.map(([k,v])=>(
          <div key={k} style={{display:"flex",justifyContent:"space-between",fontSize:11,padding:"2px 0"}}>
            <span style={{color:"#e7edf5"}}>{k}</span>
            <span style={{color:"#8ea2bf"}}>{v.trades}t · {v.win_rate}%W · <span style={{color:pc(v.pnl),fontWeight:700}}>₹{v.pnl}</span></span>
          </div>
        ))}
      </div>
    );
  };
  const WinBtn = ({d})=>(
    <button onClick={()=>{setDays(d); run(d);}} disabled={loading}
      style={{background: days===d?"rgba(167,139,250,0.30)":"transparent",
        border:"1px solid #a78bfa",borderRadius:0,padding:"2px 8px",color:"#a78bfa",
        fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>{d}d</button>
  );
  const trades = (hist&&hist.trades)||[];
  return (
    <div style={{background:"#0b1624",border:"1px solid #1a2c44",borderLeft:"3px solid #a78bfa",padding:"8px 10px",marginBottom:8}}>
      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
        <span style={{fontSize:12,fontWeight:700,color:"#a78bfa"}}>🧠 AI Trade Report</span>
        <div style={{display:"flex",gap:4,marginLeft:"auto"}}>
          <WinBtn d={7} /><WinBtn d={30} /><WinBtn d={90} />
          <button onClick={()=>run()} disabled={loading} style={{background:"rgba(167,139,250,0.15)",
            border:"1px solid #a78bfa",borderRadius:0,padding:"2px 10px",color:"#a78bfa",fontFamily:"inherit",
            fontSize:10,fontWeight:700,cursor:"pointer"}}>{loading?"Analyzing…":`Analyze ${days}d`}</button>
        </div>
      </div>
      {rep && !rep.error && (
        <div style={{marginTop:6}}>
          <div style={{fontSize:11,color:"#8ea2bf"}}>
            last {s.days||days}d · {s.trades||0} trades · {s.win_rate||0}% win · P&L <span style={{color:pc(s.total_pnl),fontWeight:700}}>₹{s.total_pnl||0}</span>
          </div>
          {ai && !ai.error && ai.headline && <div style={{fontSize:12,color:"#e7edf5",fontWeight:700,marginTop:4}}>{ai.headline}</div>}
          {ai && ai.best_signals && <div style={{fontSize:11,color:"#2ed47a",marginTop:3}}>✅ {(ai.best_signals||[]).join("; ")}</div>}
          {ai && ai.weak_signals && <div style={{fontSize:11,color:"#f59e0b",marginTop:2}}>⚠️ {(ai.weak_signals||[]).join("; ")}</div>}
          {ai && ai.why_losses && <div style={{fontSize:11,color:"#ff5b6c",marginTop:2}}>🔻 Why losses: {(ai.why_losses||[]).join("; ")}</div>}
          {ai && ai.best_stocks && <div style={{fontSize:11,color:"#49c2e4",marginTop:2}}>⭐ {(ai.best_stocks||[]).join("; ")}</div>}
          {ai && ai.strength_insight && <div style={{fontSize:11,color:"#b9c6d8",marginTop:2}}>💪 {ai.strength_insight}</div>}
          {ai && ai.actions && <div style={{fontSize:11,color:"#e8b339",marginTop:2}}>🎯 {(ai.actions||[]).join("; ")}</div>}
          <Table title="By signal" obj={s.by_signal} />
          <Table title="By combination" obj={s.by_combination} />
          <Table title="By stock" obj={s.by_stock} />
          {trades.length>0 && (
            <div style={{marginTop:8}}>
              <button onClick={()=>setShowHist(v=>!v)} style={{background:"transparent",border:"none",
                color:"#8ea2bf",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer",padding:0}}>
                {showHist?"▾":"▸"} Trade history ({trades.length}) — every auto-trade with its scan signal
              </button>
              {showHist && (
                <div style={{marginTop:4,maxHeight:280,overflowY:"auto"}}>
                  {trades.map((t)=>(
                    <div key={t.id} style={{display:"flex",flexDirection:"column",gap:1,fontSize:10,
                        padding:"4px 0",borderBottom:"1px solid #12253c"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <span>
                          <span style={{color:dc(t.side),fontWeight:700}}>{t.side}</span>
                          <span style={{color:"#e7edf5",fontWeight:700,marginLeft:4}}>{t.symbol}</span>
                          <span style={{color:"#5a728f",marginLeft:4}}>{t.instrument}{t.paper?" · paper":""}</span>
                        </span>
                        <span style={{color: t.status==="OPEN"?"#e8b339":(t.status==="CANCELLED"?"#5a728f":pc(t.pnl)),fontWeight:700}}>
                          {t.status==="OPEN"?"OPEN":(t.status==="CANCELLED"?"—":`${(t.pnl||0)>=0?"+":""}₹${t.pnl}`)}
                        </span>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",color:"#8ea2bf"}}>
                        <span>
                          {t.src==="AI"
                            ? <span style={{color:"#a78bfa",fontWeight:700}}>🤖 AI{t.confidence?` ${t.confidence}%`:""}{t.clarity?` · ${t.clarity}`:""}</span>
                            : <>📡 {(t.scan_names&&t.scan_names.length)?t.scan_names.join(" + "):"—"}</>}
                          {t.exit_reason?<span style={{color:"#5a728f"}}> · exit {t.exit_reason}</span>:null}
                        </span>
                        <span style={{color:"#5a728f"}}>
                          {t.entry_price?`@₹${t.entry_price}`:""}{t.exit_price?` → ₹${t.exit_price}`:""}
                        </span>
                      </div>
                      {t.reason?(
                        <div style={{color:"#7d8ca3",fontStyle:"italic",lineHeight:1.35,paddingRight:4}}>
                          {t.src==="AI"?"why: ":""}{t.reason}
                        </div>
                      ):null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {rep && rep.error && <div style={{fontSize:11,color:"#ff5b6c",marginTop:4}}>{rep.error}</div>}
      {!rep && <div style={{fontSize:10,color:"#5a728f",marginTop:4}}>Pick 7 / 30 / 90 days — shows which signals, combinations & stocks are making money, plus every auto-trade with the scan that fired it. Weekly summary auto-sent to Telegram every Friday 4pm.</div>}
    </div>
  );
}

// ── VPS Paper Trades log — open + closed (with P&L) from /vps-scanner/paper ────
function VpsPaperLog({ authed }) {
  const [data, setData] = useState(null);
  useEffect(()=>{
    if(!authed) return;
    const load=()=>apiFetch("/vps-scanner/paper").then(setData).catch(()=>{});
    load(); const t=setInterval(load, 20000);
    return ()=>clearInterval(t);
  },[authed]);
  const open   = (data&&data.open)   || [];
  const closed = (data&&data.closed) || [];
  const dc = (d)=> d==="BUY"?"#2ed47a":"#ff5b6c";
  const pc = (v)=> (parseFloat(v)||0)>=0?"#2ed47a":"#ff5b6c";
  const tag = (s)=> (s||"").replace("CLOSED_","").replace("EXITED_","");
  return (
    <div style={{display:"flex",flexDirection:"column",gap:6}}>
      <VpsSummary authed={authed} />
      <VpsAiReport authed={authed} />
      <div style={{fontSize:13,fontWeight:700,color:"#60a5fa"}}>
        📝 VPS Paper Trades · open {open.length} · realized ₹{(data&&data.realized_pnl)||0}
      </div>
      {open.length===0 && closed.length===0 && (
        <div style={{fontSize:12,color:"#5a728f",lineHeight:1.6}}>
          No paper trades yet. A paper trade is logged only on a validated <b style={{color:"#2ed47a"}}>GO</b> signal — a fired scan that also passes the daily-trend/validation check. Plain scan alerts (GO/WAIT) still come to Telegram; a quiet stretch with no GO is normal. Check the Telegram <b>Δ Delta scanner live</b> heartbeat to confirm the scanner is running and in Paper mode.
        </div>
      )}
      {open.map((p,i)=>(
        <div key={"o"+i} style={{background:"#0b1624",borderLeft:"3px solid #60a5fa",padding:"8px 10px",fontSize:12}}>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <span style={{fontWeight:700,color:"#e7edf5",fontSize:13}}>{p.option_sym}</span>
            <span style={{color:dc(p.dir),fontWeight:700}}>{p.dir}</span>
            <span style={{color:"#8ea2bf"}}>@ {p.opt_entry}</span>
            {(p.cur_price!==undefined&&p.cur_price!==null)&&(
              <span style={{color:"#49c2e4",fontWeight:700}}
                title={p.is_spread?"Current net debit/credit (marked to live option prices)":"Current price (marked to live option price)"}>
                now {p.cur_price}
              </span>
            )}
            <span style={{color:"#2ed47a"}}>TP {p.und_tp}</span>
            <span style={{color:"#ff5b6c"}}>SL {p.und_sl}</span>
            {(p.pnl!==undefined&&p.pnl!==null)&&(
              <span style={{color:pc(p.pnl),fontWeight:700,marginLeft:"auto"}}>P&L ₹{p.pnl}</span>
            )}
            <span style={{color:"#e8b339",fontWeight:700,marginLeft:(p.pnl!==undefined&&p.pnl!==null)?0:"auto"}}>OPEN</span>
          </div>
          {p.signal&&p.signal!=="—"&&<div style={{fontSize:10,color:"#a78bfa",marginTop:3}}>🏷 {p.signal}</div>}
        </div>
      ))}
      {closed.map((p,i)=>(
        <div key={"c"+i} style={{background:"#0b1624",borderLeft:`3px solid ${pc(p.pnl)}`,padding:"8px 10px",fontSize:12,opacity:0.92}}>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <span style={{fontWeight:700,color:"#e7edf5",fontSize:13}}>{p.option_sym}</span>
            <span style={{color:dc(p.dir),fontWeight:700}}>{p.dir}</span>
            <span style={{color:"#8ea2bf"}}>{tag(p.status)}</span>
            <span style={{color:pc(p.pnl),fontWeight:700,marginLeft:"auto"}}>P&L ₹{p.pnl||0}</span>
          </div>
          {p.signal&&p.signal!=="—"&&<div style={{fontSize:10,color:"#a78bfa",marginTop:3}}>🏷 {p.signal}</div>}
        </div>
      ))}
    </div>
  );
}

// ── Reusable list of validation results (verdict per stock) ───────────────────
// Always visible (works in grid AND table view) so "Validate All" results are
// never hidden. Sorted GO → WAIT → SKIP.
function ValidationResultList({ results, currency="₹", title="Validation results" }) {
  const list = (results||[]).filter(r=>r && !r.error);
  if(list.length===0) return null;
  const vColor=(v)=>v==="GO"?"#2ed47a":v==="WAIT"?"#f59e0b":v==="SKIP"?"#ff5b6c":"#8ea2bf";
  const vIcon =(v)=>v==="GO"?"✓":v==="WAIT"?"⏸":v==="SKIP"?"✕":"?";
  const rank={GO:0,WAIT:1,SKIP:2};
  const sorted=[...list].sort((a,b)=>(rank[a.verdict]??3)-(rank[b.verdict]??3));
  const go=sorted.filter(r=>r.verdict==="GO").length;
  const wait=sorted.filter(r=>r.verdict==="WAIT").length;
  const skip=sorted.filter(r=>r.verdict==="SKIP").length;
  return (
    <div style={{background:"#0f1d30",border:"1px solid #1a2c44",borderRadius:0,margin:"0 14px 8px"}}>
      <div style={{display:"flex",gap:10,alignItems:"center",padding:"6px 10px",borderBottom:"1px solid #1a2c44",fontSize:10}}>
        <span style={{fontWeight:700,color:"#e7edf5"}}>{title}</span>
        <span style={{color:"#2ed47a",fontWeight:700}}>GO {go}</span>
        <span style={{color:"#f59e0b",fontWeight:700}}>WAIT {wait}</span>
        <span style={{color:"#ff5b6c",fontWeight:700}}>SKIP {skip}</span>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:3,padding:"6px 8px",maxHeight:200,overflowY:"auto"}}>
        {sorted.map((r,i)=>(
          <div key={r.symbol||i} style={{display:"flex",alignItems:"center",gap:8,fontSize:10,
            padding:"4px 6px",background:"#0b1624",borderLeft:`3px solid ${vColor(r.verdict)}`}}>
            <span style={{fontWeight:800,color:vColor(r.verdict),minWidth:52}}>{vIcon(r.verdict)} {r.verdict}</span>
            <span style={{fontWeight:700,color:"#e7edf5",minWidth:72}}>{r.symbol}</span>
            <span style={{color:r.direction==="BUY"?"#2ed47a":"#ff5b6c",minWidth:30}}>{r.direction}</span>
            {r.suggested_entry!=null&&<span style={{color:"#8ea2bf",whiteSpace:"nowrap"}}>entry {currency}{r.suggested_entry}</span>}
            {r.rr!=null&&<span style={{color:"#8ea2bf",whiteSpace:"nowrap"}}>RR {r.rr}</span>}
            {(r.ai&&r.ai.reason)
              ? <span style={{color:"#a78bfa",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>🤖 {r.ai.reason}</span>
              : (r.rule_reasons&&r.rule_reasons[0]
                  ? <span style={{color:"#b9c6d8",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.rule_reasons[0]}</span>
                  : null)}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Reusable "Validate All" bar (used by MCX + Delta tabs) ────────────────────
// Batch-validates every scan result that has a signal, shows a GO/WAIT/SKIP
// summary, and — like the equity flow — alerts (toast + Telegram) when a
// symbol's verdict changes to a good entry or an exit. Optional 5-min monitor.
function ValidateAllBar({ fetcher, endpoint, buildItems, sendTgAlert=null, onShowToast=null, authed=true, storeKey="val" }) {
  const [validating, setValidating]     = useState(false);
  const [autoValidate, setAutoValidate] = useLocalStorage(`${storeKey}_auto_validate`, false);
  const [summary, setSummary]           = useState(null);   // {go,wait,skip,results}
  const prevRef  = useRef({});
  const timerRef = useRef(null);
  const cycleRef = useRef(null);

  const run = useCallback(async (silent=false)=>{
    const items = (buildItems && buildItems()) || [];
    if(items.length===0){ if(!silent&&onShowToast) onShowToast("No signals to validate — run a scan first","error"); return; }
    if(!silent) setValidating(true);
    try{
      const out = await fetcher(endpoint,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({items,use_ai:true})});
      const results=(out&&out.results)||[]; const changes=[]; let go=0,wait=0,skip=0;
      results.forEach(res=>{
        if(res.error) return;
        if(res.verdict==="GO")go++; else if(res.verdict==="WAIT")wait++; else if(res.verdict==="SKIP")skip++;
        const prev=prevRef.current[res.symbol];
        if(prev&&prev!==res.verdict){
          const kind=res.verdict==="GO"?"entry":res.verdict==="SKIP"?"exit":"wait";
          changes.push({sym:res.symbol,kind,res});
        }
        prevRef.current[res.symbol]=res.verdict;
      });
      setSummary({go,wait,skip,results});
      changes.forEach(c=>{
        const emoji=c.kind==="entry"?"✅":c.kind==="exit"?"🚫":"⏸";
        const label=c.kind==="entry"?"GOOD ENTRY":c.kind==="exit"?"EXIT / AVOID":"WAIT";
        const px  = c.res.ltp!=null ? ` @ ${c.res.ltp}` : "";
        const ent = c.res.suggested_entry!=null ? ` | entry ${c.res.suggested_entry}` : "";
        const rsn = (c.res.ai&&c.res.ai.reason) ? ` — ${c.res.ai.reason}`
                  : (c.res.rule_reasons&&c.res.rule_reasons[0]) ? ` — ${c.res.rule_reasons[0]}` : "";
        const msg = `${emoji} ${c.sym} → ${label} (${c.res.direction})${px}${ent}${rsn}`;
        if(onShowToast) onShowToast(msg, c.kind==="exit"?"error":"success");
        if(sendTgAlert) sendTgAlert(msg, {validation:true});   // validation is allowed past the Telegram gate
      });
      if(!silent&&onShowToast){
        if(changes.length===0) onShowToast(`Validated ${results.length} — no status changes`,"info");
        else onShowToast(`Validated ${results.length} — ${changes.length} status change(s)`,"success");
      }
    }catch(e){ if(!silent&&onShowToast) onShowToast("Validate all failed: "+(e.message||""),"error"); }
    finally{ if(!silent) setValidating(false); }
  }, [fetcher, endpoint, buildItems, sendTgAlert, onShowToast]);

  cycleRef.current = run;
  useEffect(()=>{
    if(timerRef.current){ clearInterval(timerRef.current); timerRef.current=null; }
    if(autoValidate && authed){ timerRef.current=setInterval(()=>cycleRef.current&&cycleRef.current(true), 5*60*1000); }
    return ()=>{ if(timerRef.current){ clearInterval(timerRef.current); timerRef.current=null; } };
  }, [autoValidate, authed]);

  const goSyms = summary ? summary.results.filter(r=>r.verdict==="GO").map(r=>r.symbol) : [];

  return (
    <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
      <div style={{display:"flex",gap:0,border:"1px solid rgba(56,189,248,0.4)"}}>
        <button onClick={()=>run(false)} disabled={validating||!authed}
          title="Re-check every signal live before entry — extension / higher-TF trend, GO/WAIT/SKIP per symbol"
          style={{background:"rgba(56,189,248,0.08)",border:"none",padding:"6px 12px",color:"#38bdf8",
            fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:validating?"wait":"pointer",opacity:validating?0.6:1}}>
          {validating?"⟳ Validating…":"🔎 Validate All"}
        </button>
        <button onClick={()=>setAutoValidate(v=>!v)}
          title="Auto re-validate every 5 min and alert (in-app + Telegram) when a signal's status changes"
          style={{background:autoValidate?"rgba(46,212,122,0.2)":"rgba(56,189,248,0.05)",border:"none",
            borderLeft:"1px solid rgba(56,189,248,0.3)",padding:"6px 10px",
            color:autoValidate?"#2ed47a":"#5a728f",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
          {autoValidate?"🔔 Auto ON":"🔕 Auto"}
        </button>
      </div>
      {summary&&(
        <div style={{display:"flex",gap:8,alignItems:"center",fontSize:10,flexWrap:"wrap"}}>
          <span style={{color:"#2ed47a",fontWeight:700}}>GO {summary.go}</span>
          <span style={{color:"#f59e0b",fontWeight:700}}>WAIT {summary.wait}</span>
          <span style={{color:"#ff5b6c",fontWeight:700}}>SKIP {summary.skip}</span>
          {goSyms.length>0&&<span style={{color:"#8ea2bf"}}>· GO: {goSyms.slice(0,8).join(", ")}{goSyms.length>8?"…":""}</span>}
        </div>
      )}
      {summary&&summary.results&&(
        <div style={{width:"100%"}}>
          <ValidationResultList results={summary.results} title="Results" />
        </div>
      )}
    </div>
  );
}

function ScanCardGrid({ results, activeScan, scanMode, canTrade, onTrade, setStrategyPopup,
  tfViewMode, timeframe, mColor, indexQuotes, showIntraday, showInvestment, setChartModal=null, userBroker="zerodha", onShowToast=null, onAIScanOne=null, validations=null, onValidateResult=null, showValidatedOnly=false }) {

  if(!results||results.length===0) return null;

  // Auto Scan+Validate mode: show only cards validated as a GO (good entry)
  const _visibleResults = showValidatedOnly
    ? results.filter(r=>{ const v = validations && validations[r.base_symbol||r.symbol]; return v && v.verdict==="GO"; })
    : results;
  if(showValidatedOnly && _visibleResults.length===0){
    return (
      <div style={{padding:"18px 14px",fontSize:11,color:"#5a728f",textAlign:"center"}}>
        ⚡ Scan+Validate on — waiting for a validated <b style={{color:"#2ed47a"}}>GO</b> entry…
      </div>
    );
  }

  return (
    <div style={{
      display:"grid",
      gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",
      gap:10, padding:"12px 14px",
    }}>
      {_visibleResults.map((r,i)=>{
        const sigs   = (r.signals||[]).filter(s=>activeScan.includes(s.scan));
        const hasBuy = sigs.some(s=>s.type==="BUY");
        const hasSell= sigs.some(s=>s.type==="SELL");
        const isMixed= hasBuy&&hasSell;
        const noSig  = !hasBuy&&!hasSell;

        // Live price from index quotes or scan result
        const liveQ  = indexQuotes&&(indexQuotes[r.symbol]||indexQuotes[`NSE:${r.symbol}`]);
        const ltp    = parseFloat(liveQ?.ltp||liveQ?.last_price||r.ltp||r.price||0);
        const chg    = parseFloat(r.net_change||liveQ?.net_change||liveQ?.change||0);
        const chgPct = parseFloat(r.pct_change||liveQ?.pct_change||liveQ?.pct||0);
        const isLive = !!(liveQ?.ltp||liveQ?.last_price);

        // Signal color
        const sigColor = isMixed?"#f59e0b":hasBuy?"#2ed47a":hasSell?"#ff5b6c":"#5a728f";
        const cardBorderColor = isMixed?"rgba(245,158,11,0.4)":hasBuy?"rgba(34,197,94,0.3)":hasSell?"rgba(239,68,68,0.3)":"#1a2c44";

        // Volume bar
        const buyPct  = r.buyVolPct||50;
        const sellPct = r.sellVolPct||50;
        const volDom  = buyPct>=60?"BUY":sellPct>=60?"SELL":"NEUTRAL";
        const volColor= volDom==="BUY"?"#2ed47a":volDom==="SELL"?"#ff5b6c":"#5a728f";

        // RSI color
        const rsi = r.rsi||50;
        const rsiColor = rsi>=70?"#ff5b6c":rsi<=30?"#2ed47a":"#8ea2bf";

        return (
          <div key={r.symbol||i} style={{
            background:"#0f1d30",
            border:`1px solid ${cardBorderColor}`,
            borderLeft:`3px solid ${sigColor}`,
            borderRadius:0,
            padding:"10px 12px",
            display:"flex",flexDirection:"column",gap:6,
            fontFamily:"inherit",
            position:"relative",
            transition:"border-color 0.15s",
          }}>
            {/* Header: symbol + price */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:"#e7edf5",letterSpacing:0.5}}>
                  {r.symbol}
                </div>
                {r.error&&<div style={{fontSize:9,color:"#5a728f",marginTop:1}}>⚠ {
                  r.error.includes("list index")||r.error.includes("IndexError")
                    ? "Insufficient candle data"
                    : r.error.includes("Not enough data")
                    ? r.error  // already clear: "Not enough data (43 candles)"
                    : r.error.length > 60 ? r.error.slice(0,57)+"…" : r.error
                }</div>}
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:13,fontWeight:700,color:isLive?"#e7edf5":"#8ea2bf"}}>
                  {ltp>0?`₹${ltp.toLocaleString("en-IN",{maximumFractionDigits:ltp<100?2:0})}`:"—"}
                  {isLive&&<span style={{fontSize:9,color:"#2ed47a",marginLeft:2}}>●</span>}
                </div>
                {chgPct!==0&&(
                  <div style={{fontSize:10,color:chgPct>=0?"#2ed47a":"#ff5b6c",fontWeight:600}}>
                    {chgPct>=0?"+":""}{chgPct.toFixed(2)}%
                  </div>
                )}
              </div>
            </div>

            {/* Signals */}
            {sigs.length>0?(
              <div style={{display:"flex",gap:3,flexWrap:"wrap"}}>
                {sigs.map((s,j)=>(
                  <span key={j} className={s.type==="BUY"?"sig-buy":"sig-sell"}>
                    {s.type==="BUY"?"▲":"▼"} {s.label}
                  </span>
                ))}
              </div>
            ):(
              <div style={{fontSize:10,color:"#5a728f",fontStyle:"italic"}}>No signal</div>
            )}

            {/* Volume bar */}
            <div>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                <span style={{fontSize:9,color:"#2ed47a"}}>B {buyPct}%</span>
                <span style={{fontSize:9,color:volColor,fontWeight:700}}>{volDom}</span>
                <span style={{fontSize:9,color:"#ff5b6c"}}>S {sellPct}%</span>
              </div>
              <div style={{height:4,background:"#0b1624",borderRadius:2,overflow:"hidden",display:"flex"}}>
                <div style={{width:`${buyPct}%`,background:"#2ed47a",transition:"width 0.5s"}}/>
                <div style={{flex:1,background:"#ff5b6c"}}/>
              </div>
            </div>

            {/* Indicators row */}
            <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
              <span style={{fontSize:10,color:rsiColor,fontWeight:700}}>RSI {rsi.toFixed(0)}</span>
              {r.ema9&&r.ema50&&(
                <span style={{fontSize:9,color:r.ema9>r.ema50?"#2ed47a":"#ff5b6c"}}>
                  E9 {r.ema9>r.ema50?"▲":"▼"} E50
                </span>
              )}
              {r.macd!=null&&(
                <span style={{fontSize:9,fontWeight:700,
                  color:r.macd>0?"#06b6d4":"#8ea2bf",
                  padding:"1px 4px",borderRadius:0,
                  background:r.macd>0?"rgba(6,182,212,0.08)":"transparent"}}>
                  MACD {r.macd>0?"+":""}{r.macd?.toFixed(3)}
                </span>
              )}
              {r.stoch_k!=null&&(
                <span style={{fontSize:9,fontWeight:700,
                  color:r.stoch_k>=80?"#ff5b6c":r.stoch_k<=20?"#2ed47a":"#e879f9",
                  padding:"1px 4px",borderRadius:0}}>
                  K{r.stoch_k?.toFixed(0)}/D{r.stoch_d?.toFixed(0)}
                </span>
              )}
              {r.roc!=null&&(
                <span style={{fontSize:9,fontWeight:700,
                  color:r.roc>2?"#2ed47a":r.roc<-2?"#ff5b6c":"#8ea2bf",
                  background:r.roc>2?"rgba(34,197,94,0.08)":r.roc<-2?"rgba(239,68,68,0.08)":"transparent",
                  padding:"1px 4px",borderRadius:0}}>
                  ROC {r.roc>0?"+":""}{r.roc?.toFixed(1)}%
                </span>
              )}
              <span style={{fontSize:9,color:"#5a728f",marginLeft:"auto"}}>
                {r.volRatio?`${r.volRatio}x vol`:""}
              </span>
            </div>

            {/* Dynamic Demand/Supply zone range */}
            {(r.demandLow!=null || r.supplyLow!=null) && (
              <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:1}}>
                {r.demandLow!=null&&r.demandHigh!=null&&(
                  <span style={{fontSize:8,padding:"1px 4px",borderRadius:0,
                    background:"rgba(0,200,120,0.08)",color:"#00c878",fontWeight:600,
                    border:"1px solid rgba(0,200,120,0.2)"}}>
                    D ₹{parseFloat(r.demandLow).toLocaleString("en-IN",{maximumFractionDigits:0})}–{parseFloat(r.demandHigh).toLocaleString("en-IN",{maximumFractionDigits:0})}
                  </span>
                )}
                {r.supplyLow!=null&&r.supplyHigh!=null&&(
                  <span style={{fontSize:8,padding:"1px 4px",borderRadius:0,
                    background:"rgba(255,80,80,0.08)",color:"#ff5050",fontWeight:600,
                    border:"1px solid rgba(255,80,80,0.2)"}}>
                    S ₹{parseFloat(r.supplyLow).toLocaleString("en-IN",{maximumFractionDigits:0})}–{parseFloat(r.supplyHigh).toLocaleString("en-IN",{maximumFractionDigits:0})}
                  </span>
                )}
              </div>
            )}

            {/* Per-stock AI scan — deep AI analysis on this symbol only */}
            {onAIScanOne&&!r.error&&(
              <button onClick={()=>onAIScanOne(r.base_symbol||r.symbol)}
                title={`Run AI scan on ${r.base_symbol||r.symbol} only`}
                style={{background:"rgba(163,113,247,0.08)",
                  border:"1px solid rgba(163,113,247,0.35)",borderRadius:0,
                  padding:"3px 0",marginTop:2,width:"100%",
                  color:"#a78bfa",fontFamily:"inherit",fontSize:10,fontWeight:700,
                  cursor:"pointer",textAlign:"center"}}>
                🤖 AI Scan This
              </button>
            )}

            {/* Pre-entry validation — check freshness/entry zone before trading */}
            {!r.error&&(hasBuy||hasSell)&&(
              <ValidateEntryButton
                symbol={r.base_symbol||r.symbol}
                exchange={r.exchange||"NSE"}
                timeframe={timeframe||"15min"}
                direction={hasBuy?"BUY":"SELL"}
                signalPrice={r.price||r.ltp||ltp}
                preset={validations&&validations[r.base_symbol||r.symbol]}
                onResult={onValidateResult?(res)=>onValidateResult(r.base_symbol||r.symbol,res):null}
              />
            )}

            {/* Action buttons — based on timeframe */}
            {ltp>0&&!r.error&&canTrade&&(
              <div style={{display:"flex",flexDirection:"column",gap:3,marginTop:2}}>

                {/* Row 1: F&O Strategy — always shown */}
                {setStrategyPopup&&(
                  <button onClick={()=>setStrategyPopup({
                    symbol:r.symbol,exchange:"NSE",ltp,
                    hasBuy,hasSell,signals:sigs,
                    lotSize:r.lot_size||1,mode:"fo",
                  })}
                    style={{background:"rgba(232,179,57,0.12)",
                      border:`1px solid ${isMixed?"rgba(245,158,11,0.5)":"rgba(232,179,57,0.4)"}`,
                      borderRadius:0,padding:"4px 0",
                      color:isMixed?"#f59e0b":"#49c2e4",
                      fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer",
                      textAlign:"center"}}>
                    🎯 {isMixed?"SELL OTM Straddle":"F&O Strategy"}
                  </button>
                )}

                {/* Row 2: Intraday MIS — only for TF < 1hr */}
                {showIntraday&&!isMixed&&sigs.length>0&&(
                  <div style={{display:"flex",gap:3}}>
                    {hasBuy&&<button onClick={()=>onTrade({symbol:r.symbol,exchange:"NSE",side:"BUY",price:ltp,qty:1,product:"MIS",signals:sigs,signalLabel:sigs.map(s=>s.label).join(" · ")})}
                      style={{flex:1,background:"rgba(34,197,94,0.12)",border:"1px solid rgba(34,197,94,0.35)",
                        borderRadius:0,padding:"3px 0",color:"#2ed47a",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                      ▲ MIS
                    </button>}
                    {hasSell&&<button onClick={()=>onTrade({symbol:r.symbol,exchange:"NSE",side:"SELL",price:ltp,qty:1,product:"MIS",signals:sigs,signalLabel:sigs.map(s=>s.label).join(" · ")})}
                      style={{flex:1,background:"rgba(239,68,68,0.12)",border:"1px solid rgba(239,68,68,0.35)",
                        borderRadius:0,padding:"3px 0",color:"#ff5b6c",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                      ▼ MIS
                    </button>}
                  </div>
                )}

                {/* Row 3: Investment CNC — only for TF >= 1hr */}
                {showInvestment&&!isMixed&&sigs.length>0&&(
                  <div style={{display:"flex",gap:3}}>
                    {hasBuy&&<button onClick={()=>onTrade({symbol:r.symbol,exchange:"NSE",side:"BUY",price:ltp,qty:1,product:"CNC",signals:sigs,signalLabel:sigs.map(s=>s.label).join(" · ")})}
                      style={{flex:1,background:"rgba(34,197,94,0.08)",border:"1px solid rgba(34,197,94,0.25)",
                        borderRadius:0,padding:"3px 0",color:"#2ed47a",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                      ▲ CNC
                    </button>}
                    {hasSell&&<button onClick={()=>onTrade({symbol:r.symbol,exchange:"NSE",side:"SELL",price:ltp,qty:1,product:"CNC",signals:sigs,signalLabel:sigs.map(s=>s.label).join(" · ")})}
                      style={{flex:1,background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.25)",
                        borderRadius:0,padding:"3px 0",color:"#ff5b6c",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                      ▼ CNC
                    </button>}
                  </div>
                )}

                {/* GTT is now auto-created via OrderTicket after main order fills */}

                {/* Mixed signal note */}
                {isMixed&&(
                  <div style={{fontSize:9,color:"#f59e0b",textAlign:"center",lineHeight:1.4}}>
                    Mixed ↕ signals · Sell OTM CE+PE for premium
                  </div>
                )}
              </div>
            )}

            {/* Chart button — always visible */}
            {setChartModal&&(
              <button onClick={()=>setChartModal({symbol:r.resolved_symbol||r.symbol,exchange:r.trade_exchange||"NSE",zones:r.zones||null})}
                style={{background:"transparent",border:"1px solid #1a2c44",borderRadius:0,
                  padding:"2px 0",color:"#23405f",fontFamily:"inherit",fontSize:9,
                  cursor:"pointer",textAlign:"center",marginTop:r.error?0:2,
                  transition:"color 0.15s,border-color 0.15s"}}
                onMouseEnter={e=>{e.currentTarget.style.color="#49c2e4";e.currentTarget.style.borderColor="#e8b339"}}
                onMouseLeave={e=>{e.currentTarget.style.color="#23405f";e.currentTarget.style.borderColor="#1a2c44"}}>
                📈 Chart
              </button>
            )}
            {/* Open in Broker — opens OrderTicket for product/type selection, then broker */}
            {ltp>0&&!r.error&&(
              <button onClick={()=>{
                  if(onTrade) {
                    // Open OrderTicket so user can choose F&O/MIS/CNC + qty/price
                    onTrade({
                      symbol:    r.symbol,
                      exchange:  r.trade_exchange||"NSE",
                      side:      hasBuy&&!hasSell?"BUY":hasSell&&!hasBuy?"SELL":"BUY",
                      price:     ltp,
                      qty:       r.lot_size||1,
                      signals:   sigs,
                      instrumentType: r.instrument_type||"EQ",
                    });
                  } else {
                    openInBroker({symbol:r.symbol,exchange:r.trade_exchange||"NSE",
                      side:hasBuy&&!hasSell?"BUY":"SELL",qty:r.lot_size||1,price:ltp,orderType:"LIMIT",product:"MIS"});
                  }
                }}
                style={{background:"transparent",border:"1px solid rgba(255,107,53,0.3)",borderRadius:0,
                  padding:"2px 0",color:"rgba(255,107,53,0.7)",fontFamily:"inherit",fontSize:9,
                  cursor:"pointer",textAlign:"center"}}
                onMouseEnter={e=>{e.currentTarget.style.color="#ff6b35";e.currentTarget.style.borderColor="rgba(255,107,53,0.7)"}}
                onMouseLeave={e=>{e.currentTarget.style.color="rgba(255,107,53,0.7)";e.currentTarget.style.borderColor="rgba(255,107,53,0.3)"}}>
                {BROKER_CONFIGS[userBroker]?.logo||"🟠"} {BROKER_CONFIGS[userBroker]?.name?.split(" ")[0]||"Kite"}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function EquityTab({isAdmin=false, canTrade=false, scanLimit=0, scansUsed=0, checkScanLimit=()=>true, userBroker="zerodha", results, setResults, alerts, setAlerts, seenKeys, setSeenKeys,
                   activeScan, setActiveScan, indexQuotes, authed, onFireAlerts,
                   tgToken, tgChat, pushEnabled, onTrade, onAddAutoTP, onShowToast,
                   signalParams, setSignalParams, bgEngineEnabled, setBgEngineEnabled,
                   setBgEngineCfg, bgLastScan, margins, setOptModal, uid,
                   onAddNote, onAddVirtual, watchers, onJournalEntry=null,
                   tgMinSignals=1, setTgMinSignals=()=>{}, sendTgAlert=null, vpsAutoTradeOn=false}) {

  // ── Scan mode ────────────────────────────────────────────────────────────────
  // intraday: 5-30min, MIS, tight TP/SL
  // investment: 1day+, CNC, wide TP/SL
  // fo: 5min-1hour, NRML, options/futures
  const [scanMode, setScanMode] = useLocalStorage('eq_scan_mode', 'intraday');
  const [showWatchlist, setShowWatchlist] = useLocalStorage('eq_show_watchlist', true);
  const [showTimeframes,setShowTimeframes]= useLocalStorage('eq_show_tframes', true);
  const [showSignals,   setShowSignals]   = useLocalStorage('eq_show_signals', true);

  // ── Auto-Virtual: add all clear-signal stocks to Virtual Tab after scan ──────
  const [autoVirtual,   setAutoVirtual]   = useLocalStorage('eq_auto_virtual', false);
  const [autoVirtualMin,setAutoVirtualMin]= useLocalStorage('eq_auto_virtual_min', 2);

  // ── Mode-specific AT configs ─────────────────────────────────────────────────
  const [atCfgIntra, setAtCfgIntra] = useLocalStorage('eq_at_cfg_intra', {
    capital:10000, minSignals:2, conditions:[1,2,3,4,5,8],
    tpPct:1.5, slPct:0.8, trailPct:0.5, side:'SIGNAL',
    cooldown:3600,       // 1 hour — same stock won't trade again for 1H
    dailyLimit:5,        // max 5 total trades/day
    maxPerSymbol:1,      // max 1 trade per stock per day
    maxTradesPct:20, autoSize:true,
  });
  const [atCfgInvest, setAtCfgInvest] = useLocalStorage('eq_at_cfg_invest', {
    capital:25000, minSignals:3, conditions:[1,2,3,4,5,8],
    tpPct:8.0, slPct:4.0, trailPct:2.0, side:'SIGNAL',
    cooldown:86400,      // 24H for investment
    dailyLimit:3, maxPerSymbol:1, maxTradesPct:30, autoSize:true,
  });
  const [atCfgFO, setAtCfgFO] = useLocalStorage('eq_at_cfg_fo', {
    capital:50000, minSignals:2, conditions:[1,2,3,4,5,8],
    tpPct:2.0, slPct:1.0, cooldown:3600, minDays:7,
    optTpPct:20, optSlPct:15, trailPct:5,
    dailyLimit:3,        // max 3 F&O trades/day
    maxPerSymbol:1,      // max 1 trade per symbol per day
    minMargin:5000,      // min ₹5000 margin required to trade
    maxTradesPct:20, instrumentMode:'AUTO', side:'SIGNAL',
  });
  // Migration: ensure required fields exist
  useEffect(()=>{
    if(!atCfgFO.side)         setAtCfgFO(p=>({...p, side:'SIGNAL'}));
    if(!atCfgIntra.side)      setAtCfgIntra(p=>({...p, side:'SIGNAL'}));
    if(!atCfgFO.maxPerSymbol) setAtCfgFO(p=>({...p, maxPerSymbol:1, cooldown:3600, dailyLimit:3, minMargin:5000}));
    if(!atCfgIntra.maxPerSymbol) setAtCfgIntra(p=>({...p, maxPerSymbol:1, cooldown:3600, dailyLimit:5}));
  },[]);

  const atCfg    = scanMode==='intraday' ? atCfgIntra : scanMode==='investment' ? atCfgInvest : atCfgFO;
  const setAtCfg = scanMode==='intraday' ? setAtCfgIntra : scanMode==='investment' ? setAtCfgInvest : setAtCfgFO;

  // ── Mode-specific timeframes ─────────────────────────────────────────────────
  // All timeframes always available — single scan, multiple trade views
  const MODE_TF = {
    intraday:   ['5min','10min','15min','30min','1hour','1day','1week'],
    investment: ['5min','10min','15min','30min','1hour','1day','1week'],
    fo:         ['5min','10min','15min','30min','1hour','1day','1week'],
  };
  const MODE_DEFAULT_TF = { intraday:'15min', investment:'1day', fo:'15min' };

  // Legacy TF states removed — using unified timeframe below

  // ── Unified timeframe — single state drives everything ───────────────────────
  const [timeframe, setTfRaw] = useLocalStorage('eq_tf_unified', '15min');

  // Auto-derive trade view from timeframe:
  // < 1hr  → Intraday (MIS) + F&O buttons
  // 1hr+   → F&O + Investment (CNC) buttons
  const getViewMode = (tf) => {
    if(tf==='1week'||tf==='1day') return 'investment'; // F&O + CNC
    if(tf==='1hour') return 'both';                    // F&O + MIS + CNC
    return 'intraday';                                 // MIS + F&O
  };
  const tfViewMode  = getViewMode(timeframe);
  const showIntraday   = tfViewMode==='intraday'||tfViewMode==='both';
  const showInvestment = tfViewMode==='investment'||tfViewMode==='both';
  const showFO         = true; // F&O always available

  // setTimeframe updates unified TF and auto-sets scanMode
  const setTimeframe = (tf) => {
    setTfRaw(tf);
    const vm = getViewMode(tf);
    if(vm==='investment') { if(scanMode==='intraday') setScanMode('investment'); }
    else if(vm==='intraday') { if(scanMode==='investment') setScanMode('intraday'); }
    // 1hr (both): keep current scanMode
  };

  // Keep legacy refs working
  const timeframeIntra  = timeframe;
  const timeframeInvest = timeframe;
  const timeframeFO     = timeframe;
  const setTfIntra  = setTimeframe;
  const setTfInvest = setTimeframe;
  const setTfFO     = setTimeframe;

  // ── Watchlists — unified (equity + FO + indices) ─────────────────────────────
  const EQUITY_LISTS = {
    'NIFTY 50':    WATCHLISTS['NIFTY 50'],
    'NIFTY BANK':  WATCHLISTS['NIFTY BANK'],
    'NIFTY IT':    WATCHLISTS['NIFTY IT'],
    'NIFTY MIDCAP':WATCHLISTS['NIFTY MIDCAP'],
    'NIFTY PHARMA':WATCHLISTS['NIFTY PHARMA'],
    'NIFTY AUTO':  WATCHLISTS['NIFTY AUTO'],
    'NIFTY FMCG':  WATCHLISTS['NIFTY FMCG'],
    'NIFTY METAL': WATCHLISTS['NIFTY METAL'],
  };
  const FO_LISTS = {
    'NSE F&O ALL': WATCHLISTS['NSE F&O ALL'],   // all ~182 F&O eligible stocks
    'INDEX FUT':   ['NIFTY 50','BANKNIFTY','FINNIFTY','MIDCPNIFTY','SENSEX'],
    'NIFTY F&O':   FO_WATCHLISTS['NIFTY F&O'],
    'BANK F&O':    FO_WATCHLISTS['BANK F&O'],
    'FIN F&O':     FO_WATCHLISTS['FIN F&O'],
    'MIDCAP F&O':  FO_WATCHLISTS['MIDCAP F&O'],
    'STOCK F&O':   FO_WATCHLISTS['STOCK F&O'],
  };

  // All watchlists combined — user picks from any list regardless of scanMode
  const ALL_LISTS = {...FO_LISTS, ...EQUITY_LISTS};
  const DEFAULT_LIST = 'NIFTY F&O';

  const [customLists, setCustomLists]   = useLocalStorage('eq_custom_lists', {});
  const [selectedLists, setSelectedLists] = useLocalStorage('eq_selected_lists', ['NIFTY 50']);
  const [showWLMgr, setShowWLMgr]       = useState(false);

  // Reset selected list when mode changes to a list that may not exist in new mode
  const prevModeRef = useRef(scanMode);
  useEffect(()=>{
    if(prevModeRef.current !== scanMode){
      prevModeRef.current = scanMode;
      const allListNames = Object.keys(ALL_LISTS);
      const valid = selectedLists.filter(l => allListNames.includes(l) || customLists[l]);
      if(valid.length === 0) setSelectedLists([DEFAULT_LIST]);
      else setSelectedLists(valid);
    }
  }, [scanMode]);

  const scanSymbols = useMemo(()=>{
    const allNames = [...Object.keys(ALL_LISTS), ...Object.keys(customLists)];
    const seen = new Set();
    return selectedLists
      .flatMap(n => customLists[n] || ALL_LISTS[n] || [])
      .filter(s => { if(seen.has(s)) return false; seen.add(s); return true; });
  }, [selectedLists, customLists, scanMode]);

  // ── Scan state ───────────────────────────────────────────────────────────────
  const [scanning,      setScanning]    = useState(false);
  const [scanResults,   setScanResults] = useState([]);
  const [scanError,     setScanError]   = useState(null);
  const [lastScan,      setLastScan]    = useState(null);
  const [scanTimeframe, setScanTF]      = useState(null);

  // ── Pre-entry validation state (batch + auto-monitor for status changes) ──────
  const [validations,   setValidations] = useState({});   // symbol -> validate result
  const [validating,    setValidating]  = useState(false);
  const [autoValidate,  setAutoValidate]     = useLocalStorage('eq_auto_validate', false);       // re-validate existing (5 min)
  const [autoScanValidate, setAutoScanValidate] = useLocalStorage('eq_auto_scan_validate', false); // scan+validate, show GO only (3 min)
  const valMonitorRef      = useRef(null);
  const scanValRef         = useRef(null);
  const scanValCycleRef    = useRef(null);
  const prevVerdictsRef    = useRef({});                   // symbol -> last verdict (for change alerts)

  // Batch-validate every scan result that currently has a signal. When run on a
  // timer (silent=true), it diffs each symbol's verdict vs the previous run and
  // fires a toast + Telegram alert when the status flips (e.g. WAIT→GO "good
  // entry" or →SKIP "exit/avoid").
  const runValidateAll = useCallback(async (silent=false, opts={})=>{
    const src          = opts.results || scanResults || [];
    const goOnlyAlerts = opts.goOnlyAlerts === true;   // alert only on NEW good entries (auto scan+validate)
    const items = src
      .filter(r=>!r.error && (r.signals||[]).some(s=>activeScan.includes(s.scan)))
      .map(r=>{
        const sigs = (r.signals||[]).filter(s=>activeScan.includes(s.scan));
        const dir  = sigs.some(s=>s.type==="BUY") ? "BUY" : "SELL";
        return { symbol:r.base_symbol||r.symbol, exchange:r.exchange||"NSE",
                 timeframe, direction:dir, signal_price:r.price||r.ltp||null };
      });
    if(items.length===0){
      if(!silent&&onShowToast) onShowToast("No signals to validate — run a scan first","error");
      return;
    }
    if(!silent) setValidating(true);
    try{
      const out = await apiFetch("/scan/validate/batch",{
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ items, use_ai:true }),
      });
      const map = {}; const changes = [];
      (out.results||[]).forEach(res=>{
        if(res.error) return;
        map[res.symbol] = res;
        const prev = prevVerdictsRef.current[res.symbol];
        if(goOnlyAlerts){
          // Only alert when a signal BECOMES a good entry (avoids WAIT/SKIP noise)
          if(res.verdict==="GO" && prev!=="GO"){
            changes.push({ sym:res.symbol, from:prev||"new", kind:"entry", res });
          }
        } else if(prev && prev!==res.verdict){
          const kind = res.verdict==="GO" ? "entry" : res.verdict==="SKIP" ? "exit" : "wait";
          changes.push({ sym:res.symbol, from:prev, kind, res });
        }
        prevVerdictsRef.current[res.symbol] = res.verdict;
      });
      setValidations(prev=>({...prev, ...map}));

      // Fire alerts on status changes
      changes.forEach(c=>{
        const emoji = c.kind==="entry" ? "✅" : c.kind==="exit" ? "🚫" : "⏸";
        const label = c.kind==="entry" ? "GOOD ENTRY" : c.kind==="exit" ? "EXIT / AVOID" : "WAIT";
        const msg = `${emoji} ${c.sym} → ${label} (${c.res.direction}) @ ₹${c.res.ltp}`
          + (c.res.suggested_entry!=null ? ` | entry ₹${c.res.suggested_entry}` : "")
          + (c.res.ai&&c.res.ai.reason ? ` — ${c.res.ai.reason}` : "");
        if(onShowToast) onShowToast(msg, c.kind==="exit" ? "error" : "success");
        if(sendTgAlert) sendTgAlert(msg, {validation:true});  // only validation may reach Telegram
      });
      if(!silent&&onShowToast){
        if(changes.length===0) onShowToast(`Validated ${Object.keys(map).length} signals — no status changes`,"info");
        else onShowToast(`Validated ${Object.keys(map).length} — ${changes.length} status change(s)`,"success");
      }
    }catch(e){
      if(!silent&&onShowToast) onShowToast("Validate all failed: "+(e.message||""),"error");
    }finally{
      if(!silent) setValidating(false);
    }
  }, [scanResults, activeScan, timeframe, onShowToast, sendTgAlert]);

  // Auto-monitor — re-validate every 5 min while enabled; alerts fire on change
  useEffect(()=>{
    if(valMonitorRef.current){ clearInterval(valMonitorRef.current); valMonitorRef.current=null; }
    if(autoValidate && authed){
      valMonitorRef.current = setInterval(()=>runValidateAll(true), 5*60*1000);
    }
    return ()=>{ if(valMonitorRef.current){ clearInterval(valMonitorRef.current); valMonitorRef.current=null; } };
  }, [autoValidate, authed, runValidateAll]);

  // Auto Scan+Validate cycle — re-run the technical scan (silent, no raw-signal
  // alerts), validate the fresh signals, alert only NEW good entries, and show
  // only validated GO cards in the grid. Reassigned each render so the interval
  // always calls the latest closure (current state).
  scanValCycleRef.current = async ()=>{
    // Reuse the last technical scan if it's still fresh (< 10 min) to save scan
    // quota — validation itself always re-fetches live prices, so entry timing
    // stays current regardless of scan age. Only re-scan when stale.
    const FRESH_MS = 10*60*1000;
    let results;
    if(scanResults && scanResults.length>0 && (Date.now()-lastScanAtRef.current) < FRESH_MS){
      results = scanResults;
    } else {
      try { results = (await runScan({skipAlerts:true})) || []; } catch { results = []; }
    }
    await runValidateAll(true, { results, goOnlyAlerts:true });
  };
  useEffect(()=>{
    if(scanValRef.current){ clearInterval(scanValRef.current); scanValRef.current=null; }
    if(autoScanValidate && authed){
      if(scanValCycleRef.current) scanValCycleRef.current();                       // run immediately
      scanValRef.current = setInterval(()=>{ if(scanValCycleRef.current) scanValCycleRef.current(); }, 3*60*1000);
    }
    return ()=>{ if(scanValRef.current){ clearInterval(scanValRef.current); scanValRef.current=null; } };
  }, [autoScanValidate, authed]);
  const [filterType,    setFilterType]  = useState('ALL');
  const [sortBy,        setSortBy]      = useState('signal');
  const [tableSearch,   setTableSearch] = useState('');
  const [viewMode,      setViewMode]    = useState('grid');
  const [resultView,    setResultView]  = useState('scan'); // 'scan'|'ai'
  const [chartModal,    setChartModal]   = useState(null); // {symbol,exchange}
  const [aiScanMode,    setAiScanMode]  = useLocalStorage('eq_ai_scan_mode', 'equity'); // 'equity'|'fo'
  const [liveMode,      setLiveMode]    = useState(false);
  const [scanMinDays,   setScanMinDays] = useLocalStorage('eq_min_days', 7);
  const [liveQuotes,    setLiveQuotes]  = useState({});

  // ── MTF (Multi-Timeframe) strategy mode ───────────────────────────────────────
  const [mtfEnabled,  setMtfEnabled]  = useLocalStorage('eq_mtf_enabled', false);
  const [mtfTrendTF,  setMtfTrendTF]  = useLocalStorage('eq_mtf_trend', '1hour');
  const [mtfEntryTF,  setMtfEntryTF]  = useLocalStorage('eq_mtf_entry', '15min');

  // ── AT state — 'intra' | 'fo' | false ───────────────────────────────────────
  const [atEnabled,  setAtEnabled]  = useState(false);
  const [atLog,      setAtLog]      = useState([]);

  // atFired persists across refreshes — {symbol: timestamp, `${symbol}_${date}`: count}
  const [atFired,    setAtFired]    = useLocalStorage('eq_at_fired', {});
  // dailyTrades persists across refreshes
  const [dailyTrades,setDailyTrades]= useLocalStorage('eq_at_daily', {date:'',count:0});
  // Per-symbol daily trade count — 1 trade per symbol per day
  const [symDailyTrades, setSymDailyTrades] = useLocalStorage('eq_at_sym_daily', {}); // {`${sym}_${date}`: count}

  const [showATCfg,  setShowATCfg]  = useState(false);
  // Only auto-trade signals that pass the pre-entry validation (verdict = GO)
  const [atValidate, setAtValidate] = useLocalStorage('eq_at_validate', true);
  const [atPaper,    setAtPaper]    = useLocalStorage('eq_at_paper', false);  // record paper trades instead of real orders
  const atCfgRef      = useRef(atCfgIntra);
  const atEnabledRef  = useRef(false);
  const atValidateRef = useRef(true);
  const atPaperRef    = useRef(false);
  const vpsATRef      = useRef(false);      // VPS is auto-trading → don't double up on desktop
  useEffect(()=>{ vpsATRef.current = vpsAutoTradeOn; },[vpsAutoTradeOn]);
  const lastScanAtRef = useRef(0);          // epoch ms of last technical scan (for freshness reuse)
  const atFiredRef    = useRef({});
  const dailyTradesRef= useRef({date:'',count:0});
  const symDailyRef   = useRef({});
  useEffect(()=>{ atCfgRef.current      = atEnabled==='fo' ? atCfgFO : atCfgIntra; },[atEnabled,atCfgIntra,atCfgFO]);
  useEffect(()=>{ atEnabledRef.current  = atEnabled;   },[atEnabled]);
  useEffect(()=>{ atValidateRef.current = atValidate;  },[atValidate]);
  useEffect(()=>{ atPaperRef.current    = atPaper;     },[atPaper]);

  // Record validated-GO signals as PAPER trades instead of placing real orders.
  function _paperTradeResults(resList, tabMode, exchange){
    if(!onAddVirtual) return;
    let n=0;
    (resList||[]).forEach(r=>{
      const sigs=(r.signals||[]).filter(s=>activeScan.includes(s.scan));
      const side=sigs.some(s=>s.type==="BUY")?"BUY":"SELL";
      const price=parseFloat(r.ltp||r.price||0);
      if(!price) return;
      onAddVirtual({ symbol:r.base_symbol||r.symbol, exchange:exchange||r.exchange||"NSE",
        side, price, qty:1, product:(timeframe==="1day"||timeframe==="1week")?"CNC":"MIS",
        tab_mode:tabMode||"equity", source:"auto",
        signals:sigs.map(s=>s.label).join(" · "), signal:sigs });
      n++;
    });
    if(n>0&&onShowToast) onShowToast(`📝 Paper: recorded ${n} validated GO trade(s) — no real order`,"info");
  }
  useEffect(()=>{ atFiredRef.current    = atFired;     },[atFired]);
  useEffect(()=>{ dailyTradesRef.current= dailyTrades; },[dailyTrades]);
  useEffect(()=>{ symDailyRef.current   = symDailyTrades; },[symDailyTrades]);

  // FO-specific state
  const [foResultView, setFoResultView] = useState('scan');
  const [tradeModal,   setTradeModal]   = useState(null);
  const [strategyPopup, setStrategyPopup] = useState(null); // {symbol,ltp,hasBuy,hasSell,signals,lotSize}
  const [foStrat,      setFoStrat]      = useState('AUTO');
  const [foCapital,    setFoCapital]    = useState(50000);
  const [foMinDays,    setFoMinDays]    = useState(15);
  const [foPlacing,    setFoPlacing]    = useState(false);
  const [stratPreview, setStratPreview] = useState(null);
  const [stratLoading, setStratLoading] = useState(false);
  const [scanSel,      setScanSel]      = useState(new Set());

  // Signal settings
  const [showSignalModal, setShowSignalModal] = useState(false);

  // ── AI panel ─────────────────────────────────────────────────────────────────
  const aiExchange = scanMode==='fo' ? 'NFO' : 'NSE';
  const { aiResults, aiLoading, aiError, aiVisible, setAiVisible,
          runAIScan, autoTrade:aiAutoTrade, setAutoTrade:setAiAutoTrade,
          minAutoConf, setMinAutoConf, autoFired:aiAutoFired,
          marginPct, setMarginPct, maxDaily, setMaxDaily,
          maxScans, setMaxScans, scanCount, balance
        } = useAIPanel({authed, signalParams, exchange: aiExchange});

  // ── Live quotes — refreshed every 5s for scan results ───────────────────────
  const liveQRef = useRef(null);
  useEffect(()=>{
    if(!authed || scanResults.length===0){ clearInterval(liveQRef.current); return; }
    const fetchQ = async()=>{
      try{
        const INDICES_SET = new Set(Object.keys(INDEX_QUOTES_MAP));
        const syms = scanResults.slice(0,50).map(r=>r.symbol).filter(Boolean);
        // For stocks: NSE:SYMBOL, for indices: NSE:NIFTY MIDCAP SELECT etc
        const parts = syms.map(s=>
          INDICES_SET.has(s) ? `NSE:${INDEX_QUOTES_MAP[s]}` : `NSE:${s}`
        );
        if(!parts.length) return;
        const q = await apiFetch(`/quotes?symbols=${encodeURIComponent(parts.join(','))}`);
        if(q && typeof q === 'object') {
          // Also merge indexQuotes so indices always have a fallback
          setLiveQuotes(prev=>({...prev, ...q}));
        }
      }catch{}
    };
    fetchQ();
    liveQRef.current = setInterval(fetchQ, 5000);
    return ()=>clearInterval(liveQRef.current);
  }, [authed, scanResults.length, scanMode]);

  // ── Product helper ────────────────────────────────────────────────────────────
  const getProduct = () => {
    if(scanMode==='intraday')   return 'MIS';
    if(scanMode==='investment')  return 'CNC';
    return 'NRML';  // fo
  };

  // ── Run scan ──────────────────────────────────────────────────────────────────
  const liveRef = useRef(null);
  async function runScan(opts={}) {
    const skipAlerts = opts && opts.skipAlerts === true;   // auto scan+validate suppresses raw-signal alerts
    if(!authed || scanSymbols.length===0) return [];
    if(!checkScanLimit()) {
      setScanError(`Daily scan limit reached (${scansUsed}/${scanLimit}). Resets at midnight.`);
      return [];
    }
    setScanning(true); setScanError(null);
    try {
      let results;

      if(mtfEnabled) {
        // ── MTF mode: fetch 2 TFs, only fire when both align ────────────────
        const res = await apiFetch('/scan/mtf', {
          method:'POST', headers:{'Content-Type':'application/json'},
          body: JSON.stringify({
            symbols:       scanSymbols,
            exchange:      'NSE',
            trend_tf:      mtfTrendTF,
            entry_tf:      mtfEntryTF,
            signal_params: signalParams,
          })
        });
        results = res.results || [];
      } else {
        // ── Normal single-TF scan ────────────────────────────────────────────
        const res = await apiFetch('/scan', {
          method:'POST', headers:{'Content-Type':'application/json'},
          body: JSON.stringify({
            symbols:         scanSymbols,
            exchange:        'NSE',
            timeframe,
            signal_params:   signalParams,
            tech_weight:     0.7,
            news_enabled:    false,
            min_days_expiry: scanMinDays || 7,
          })
        });
        results = res.results || [];
      }

      const ts = `${new Date().toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})} · ${mtfEnabled?`${mtfTrendTF}+${mtfEntryTF}`:timeframe}`;
      setScanResults(results); setScanTF(timeframe); setLastScan(ts);
      lastScanAtRef.current = Date.now();   // mark scan freshness
      if(onAddNote) results.forEach(r=>{
        if((r.signals||[]).length>0) onAddNote(r.symbol,{
          source:'Scanner', tab:scanMode, timeframe,
          signals: r.signals.map(s=>s.label).join(','),
          label: r.signals[0]?.label||'',
          type:  r.signals[0]?.type||'NEUTRAL',
          score: r.score||0, ltp: r.ltp||0, ts: Date.now(),
        });
      });

      // Fire Telegram / push alerts for new signals (skipped in auto scan+validate)
      if(!skipAlerts && onFireAlerts) {
        const tf = mtfEnabled?`${mtfTrendTF}+${mtfEntryTF}`:timeframe;
        const newAlerts = results
          .filter(r=>(r.signals||[]).some(s=>activeScan.includes(s.scan)))
          .flatMap(r=>{
            const zone = (r.demandLow && r.demandHigh) || (r.supplyLow && r.supplyHigh)
              ? {
                  demand: r.demandLow && r.demandHigh ? {low:r.demandLow, high:r.demandHigh} : null,
                  supply: r.supplyLow && r.supplyHigh ? {low:r.supplyLow, high:r.supplyHigh} : null,
                } : null;
            return (r.signals||[])
              .filter(s=>activeScan.includes(s.scan))
              .map(s=>({symbol:r.symbol, price:r.ltp||r.price||0, signal:s, exchange:'NSE', timeframe:tf, zone}));
          });
        if(newAlerts.length>0) onFireAlerts(newAlerts);
      }

      if(atEnabledRef.current) runATEngineGated(results);

      // ── Auto-Virtual: add stocks with enough signals to Virtual Tab ──────
      if(autoVirtual && onAddVirtual) {
        autoVirtualFromScan(results, onAddVirtual, autoVirtualMin, scanMode, "NSE", onShowToast, activeScan);
      }
      return results;
    } catch(e) {
      setScanError(e.message||'Scan failed');
      return [];
    } finally { setScanning(false); }
  }

  useEffect(()=>{
    if(liveMode && authed){ runScan(); liveRef.current=setInterval(runScan,60000); }
    else clearInterval(liveRef.current);
    return()=>clearInterval(liveRef.current);
  },[liveMode,authed,timeframe,activeScan,scanMode]);

  // Auto-rescan on TF change
  const prevTfRef = useRef(null);
  useEffect(()=>{
    if(prevTfRef.current===null){prevTfRef.current=timeframe;return;}
    if(prevTfRef.current===timeframe) return;
    prevTfRef.current=timeframe;
    if(!authed) return;
    setScanResults([]);
    if(scanSymbols.length>0) runScan();
  },[timeframe,authed]);

  // ── AT gate: validate signals first, auto-trade only the GO ones ──────────────
  // When atValidate is on, every signal is run through the pre-entry validation
  // and only symbols that come back GO are handed to the trade engine. On a
  // validation failure we SKIP trading (safer than trading unvalidated).
  async function runATEngineGated(results) {
    if(vpsATRef.current){
      if(onShowToast) onShowToast("🖥️ VPS is auto-trading — desktop auto-trade skipped (avoids double orders)", "warn");
      return;
    }
    if(!atValidateRef.current){ runATEngine(results); return; }
    const signalled = (results||[]).filter(r=>!r.error && (r.signals||[]).some(s=>activeScan.includes(s.scan)));
    if(signalled.length===0) return;
    const items = signalled.map(r=>{
      const sigs = (r.signals||[]).filter(s=>activeScan.includes(s.scan));
      const dir  = sigs.some(s=>s.type==="BUY") ? "BUY" : "SELL";
      return { symbol:r.base_symbol||r.symbol, exchange:r.exchange||"NSE",
               timeframe, direction:dir, signal_price:r.price||r.ltp||null };
    });
    try{
      const out = await apiFetch("/scan/validate/batch",{
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ items, use_ai:true }),
      });
      const map = {}; const goSet = new Set();
      (out.results||[]).forEach(v=>{ if(v.error) return; map[v.symbol]=v; if(v.verdict==="GO") goSet.add(v.symbol); });
      if(Object.keys(map).length) setValidations(prev=>({...prev, ...map}));
      const goResults = signalled.filter(r=>goSet.has(r.base_symbol||r.symbol));
      if(goResults.length>0){
        if(atPaperRef.current) _paperTradeResults(goResults, "equity", "NSE");
        else runATEngine(goResults);
      }
      else if(onShowToast) onShowToast(`Auto-trade: ${signalled.length} signal(s), none validated GO — no trade`,"info");
    }catch(e){
      if(onShowToast) onShowToast("Auto-trade validation failed — trades skipped this cycle","warn");
    }
  }

  // ── AT engine ─────────────────────────────────────────────────────────────────
  function runATEngine(results) {
    if(!isAdmin) return;

    const atEnabledNow = atEnabledRef.current;
    if(!atEnabledNow) return;

    const isIntraAT  = atEnabledNow === 'intra';
    const isFOAT     = atEnabledNow === 'fo';
    const cfg        = isFOAT ? atCfgFO : atCfgIntra;
    const fired      = atFiredRef.current;
    const symDaily   = symDailyRef.current;
    const now        = Date.now();
    const todayKey   = new Date().toISOString().slice(0,10); // "2025-04-13"
    const product    = isIntraAT ? 'MIS' : 'NRML';

    // ── STRICT DAILY LIMIT CHECK ─────────────────────────────────────────────
    const dailyRec   = dailyTradesRef.current;
    const todayCount = dailyRec.date === todayKey ? dailyRec.count : 0;
    const dailyLimit = cfg.dailyLimit || 5;
    if(todayCount >= dailyLimit) {
      // Only toast once per hour
      const limitKey   = `daily_limit_toast`;
      const lastToast  = fired[limitKey]||0;
      if(now - lastToast > 3600000) {
        if(onShowToast) onShowToast(`🛑 AT daily limit reached: ${todayCount}/${dailyLimit} trades today`,'warn');
        atFiredRef.current[limitKey] = now;
        setAtFired(p=>({...p,[limitKey]:now}));
      }
      return;
    }

    // ── Running count for THIS scan batch (synchronous guard) ────────────────
    // Prevents multiple stocks in the same scan from all firing before refs update
    let batchCount = todayCount; // starts at current daily total
    const batchFiredSyms = new Set(); // symbols already fired this batch

    results.forEach(stock=>{
      if(!stock.signals||stock.signals.length===0||stock.error) return;

      const sym    = stock.symbol;
      const ts_str = new Date().toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'});
      const logSkip= (status, note) => setAtLog(p=>[{
        id:`${ts_str}-${sym}`,time:ts_str,symbol:sym,
        mode:isIntraAT?'INTRA':'F&O',status,note},...p].slice(0,100));

      // ── 1-TRADE-PER-SYMBOL-PER-DAY ──────────────────────────────────────
      const symKey      = `${sym}_${todayKey}`;
      const symTodayCount = (symDailyRef.current[symKey] || 0);
      const maxPerSym   = cfg.maxPerSymbol ?? 1;
      // Also block if already fired this batch (within same scan cycle)
      if(symTodayCount >= maxPerSym || batchFiredSyms.has(sym)) {
        return; // silent — already traded this symbol today
      }

      // ── BATCH DAILY LIMIT (synchronous — prevents same-scan over-fire) ────
      if(batchCount >= dailyLimit) return;

      // ── SIGNAL MATCHING ──────────────────────────────────────────────────
      const conditions  = cfg.conditions||[1,2,3,4,5,8];
      const minRequired = cfg.minSignals||2;
      const matching    = stock.signals.filter(s=>conditions.includes(s.scan));

      if(matching.length < minRequired) {
        const skipKey  = `skip_${sym}`;
        const lastSkip = fired[skipKey]||0;
        if(now-lastSkip > 120000) {
          logSkip('SKIP_SIG', `${matching.length}/${minRequired} signals`);
          atFiredRef.current[skipKey] = now;
        }
        return;
      }

      // ── MIXED SIGNALS ────────────────────────────────────────────────────
      const buySignals  = matching.filter(s=>s.type==='BUY');
      const sellSignals = matching.filter(s=>s.type==='SELL');
      if(buySignals.length > 0 && sellSignals.length > 0) {
        logSkip('SKIP_MIXED', `${buySignals.length}B vs ${sellSignals.length}S`);
        return;
      }

      const dominantSide = buySignals.length > sellSignals.length ? 'BUY'
        : sellSignals.length > buySignals.length ? 'SELL' : null;
      if(!dominantSide && cfg.side==='SIGNAL') {
        logSkip('SKIP_EQUAL', `${buySignals.length}B = ${sellSignals.length}S`);
        return;
      }

      // ── PERSISTENT COOLDOWN CHECK (survives refresh) ─────────────────────
      const lastFire    = fired[sym] || 0;
      const cooldownSec = cfg.cooldown || 3600; // default 1 HOUR for safety
      if(now - lastFire < cooldownSec * 1000) {
        return; // silent — cooldown not expired
      }

      const side = cfg.side==='SIGNAL' ? dominantSide : (cfg.side||'BUY');

      // ── RE-CHECK DAILY LIMIT (concurrent protection) ─────────────────────
      const latestDailyRec   = dailyTradesRef.current;
      const latestTodayCount = latestDailyRec.date === todayKey ? latestDailyRec.count : 0;
      if(latestTodayCount >= dailyLimit) return;

      // ── MARK fired IMMEDIATELY (before async order) ──────────────────────
      batchFiredSyms.add(sym);
      batchCount++;
      const newFired = {...atFiredRef.current, [sym]: now};
      atFiredRef.current = newFired;
      setAtFired(newFired);

      // Helper to increment both counters after confirmed order
      const confirmTrade = () => {
        const newSymDaily = {...symDailyRef.current, [symKey]: (symDailyRef.current[symKey]||0) + 1};
        symDailyRef.current = newSymDaily;
        setSymDailyTrades(newSymDaily);
        const latestRec = dailyTradesRef.current;
        const latestCnt = latestRec.date === todayKey ? latestRec.count : 0;
        const newDaily  = {date: todayKey, count: latestCnt + 1};
        dailyTradesRef.current = newDaily;
        setDailyTrades(newDaily);
      };

      // ── F&O AT: find ATM option for this stock/index ─────────────────────
      if(isFOAT) {
        // Min margin check
        const minMarginReq = cfg.minMargin || 0;
        if(minMarginReq > 0 && margins) {
          const availMargin = getMargin(margins, 'nrml');
          if(availMargin < minMarginReq) {
            logSkip('SKIP_MARGIN', `Margin ₹${Math.round(availMargin).toLocaleString('en-IN')} < min ₹${minMarginReq.toLocaleString('en-IN')}`);
            return;
          }
        }
        const optType = cfg.instrumentMode==='CE'?'CE'
          : cfg.instrumentMode==='PE'?'PE'
          : side==='BUY'?'CE':'PE'; // BUY signal → buy CE, SELL signal → buy PE

        if(onShowToast) onShowToast(`🎯 F&O AT: ${side} signal on ${stock.symbol} → finding ATM ${optType}`);
        runFOAutoTrade(stock, side, cfg, product, confirmTrade);
        return;
      }

      // ── Intraday AT: direct equity MIS order ────────────────────────────
      const isMIS = product==='MIS';
      const livePrice = parseFloat(stock.ltp||stock.price||0);
      if(!livePrice || livePrice <= 0) {
        if(onShowToast) onShowToast(`⚠ AT skip ${stock.symbol} — no live price`, 'warn');
        return;
      }
      const availMargin = getMargin(margins, isMIS ? "mis" : "nrml");
      const tradeCapital = cfg.autoSize && availMargin>0
        ? calcPositionSize(availMargin, cfg.capital, cfg.maxTradesPct||20, livePrice)
        : (cfg.capital||10000);
      const tick  = stock.tick_size||0.05;
      const qty   = Math.max(1, Math.floor(tradeCapital/livePrice));
      const entry = roundToTick(livePrice, tick);
      const tp    = roundToTick(entry*(side==='BUY'?1+cfg.tpPct/100:1-cfg.tpPct/100), tick);
      const sl    = roundToTick(entry*(side==='BUY'?1-cfg.slPct/100:1+cfg.slPct/100), tick);

      if(onShowToast) onShowToast(`⚡ Intraday AT: ${side} ${qty}×${stock.symbol} @ ₹${entry}`);

      apiFetch('/order/place',{method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({
          tradingsymbol:    stock.symbol,       // VPS expects tradingsymbol
          exchange:         'NSE',
          transaction_type: side,
          quantity:         qty,
          order_type:       'LIMIT',
          price:            entry,
          product,
          tag:              'RABT-AUTO-EQ-INTRA'
        })
      }).then(r=>{
        const ts = new Date().toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'});
        setAtLog(p=>[{id:`${ts}-${stock.symbol}`,time:ts,symbol:stock.symbol,
          side,qty,price:entry,tp,sl,product,status:r.ok?'PLACED':'FAILED',
          mode:'INTRA'},...p].slice(0,50));
        if(r.ok && onAddAutoTP) {
          const trailPct = cfg.trailPct || (isIntraAT ? 1.0 : 0.5);
          // FIX: initial SL was set at TRAIL distance from entry (1%) — normal
          // tick noise hit it within seconds of order execution. Start at the
          // configured fixed SL (slPct, wider); the trail then RATCHETS the SL
          // tighter only after price moves in our favour (high-water mark).
          const trailSL   = roundToTick(side==='BUY' ? entry*(1-trailPct/100) : entry*(1+trailPct/100), tick);
          const initialSL = (sl && cfg.slPct > 0) ? sl : trailSL;
          onAddAutoTP({
            id:`atp-eq-${Date.now()}`,
            symbol:      stock.symbol,
            exchange:    'NSE',
            side,
            entryPrice:  entry,
            tpPrice:     tp,
            slPrice:     initialSL,        // fixed SL first — trail tightens it later
            qty,
            product,
            tpEnabled:   !!(tp && cfg.tpPct > 0),
            slEnabled:   !!(initialSL && cfg.slPct > 0),
            trailEnabled: cfg.trailPct > 0 || true, // always trail for AT
            trailPct:    trailPct,
            highWaterMark: entry,          // starts at entry, VPS updates as price moves
            tickSize:    tick,
            instrumentType: 'EQ',
            source:      'AT-INTRA',
            orderId:     r.order_id,       // link watcher to order
            createdAt:   new Date().toISOString(),
            status:      'WATCHING',
            // Zone-aware TP/SL — attach nearest D/S zones from scan result
            ...extractNearestZones(stock.zones, entry),
            zoneTouched: 'none',
          });
        }
        if(r.ok && onJournalEntry) onJournalEntry({
          orderId:r.order_id,symbol:stock.symbol,exchange:'NSE',
          side,qty,price:entry,product,
          source:scanMode==='investment'?'scanner_auto_invest':'scanner_auto',
          tabMode:scanMode,tpPrice:tp,slPrice:sl,
          tpPct:cfg.tpPct,slPct:cfg.slPct,tag:'RABT-AUTO-EQ-INTRA',
          signals: matching.map(s=>s.label).join(' · '),
        });
        if(r.ok) confirmTrade();
        // Telegram alert for auto-trade
        if(r.ok && tgChat) {
          const tgMsg = `⚡ <b>AUTO-TRADE: ${side} ${stock.symbol}</b>\nQty: ${qty} @ ₹${entry} · ${product}\nTP: ₹${tp} · SL: ₹${sl}\nSignals: ${matching.map(s=>s.label).join(', ')}`;
          apiFetch('/alert/telegram',{method:'POST',headers:{'Content-Type':'application/json'},
            body:JSON.stringify({message:tgMsg,bot_token:tgToken||'',chat_id:tgChat})}).catch(()=>{});
        }
      }).catch(e=>{
        const ts = new Date().toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'});
        setAtLog(p=>[{id:`${ts}-${stock.symbol}`,time:ts,symbol:stock.symbol,
          side,qty,price:entry,status:'ERROR',error:e.message,mode:scanMode.toUpperCase()},...p].slice(0,50));
      });
    });
  }

  // FO auto trade via backend (same logic as old AutoFOTab)
  async function runFOAutoTrade(stock, side, cfg, product, confirmTrade=null) {
    const ts = ()=>new Date().toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'});
    try {
      // Step 1: find ATM option contract
      const optType = cfg.instrumentMode==='CE'?'CE'
        : cfg.instrumentMode==='PE'?'PE'
        : side==='BUY'?'CE':'PE'; // BUY signal → CE, SELL signal → PE

      const atmRes = await apiFetch('/fo/find_atm', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          symbol:      stock.symbol,
          exchange:    'NSE',
          signal_side: side,
          option_type: optType,
          min_days:    cfg.minDays||7,
        })
      });

      if(!atmRes.found) {
        setAtLog(p=>[{id:`${ts()}-${stock.symbol}`,time:ts(),symbol:stock.symbol,
          side,status:'NO_CONTRACT',mode:'FO',
          note:atmRes.reason||`No ${optType} found`},...p].slice(0,50));
        return;
      }

      const contract  = atmRes.contract;
      const optSymbol = contract.tradingsymbol;
      const optLtp    = parseFloat(contract.option_ltp||0);
      const lotSize   = parseInt(contract.lot_size||1);
      const tick      = parseFloat(contract.tick_size||0.05);
      // Product: MIS for intraday option (lower margin), NRML for overnight
      const optProduct = cfg.optProduct || 'NRML';

      // ── Liquidity filter — skip non-liquid / wide-spread strikes ─────────
      if(optLtp < 0.5) {
        setAtLog(p=>[{id:`${ts()}-${stock.symbol}`,time:ts(),symbol:stock.symbol,
          side,status:'SKIP_LIQUIDITY',mode:'FO',
          note:`Option LTP ₹${optLtp} too low — non-liquid, skipping`},...p].slice(0,50));
        return;
      }
      const _bid = parseFloat(contract.bid||0);
      const _ask = parseFloat(contract.ask||0);
      if(_bid>0 && _ask>0) {
        const _spreadPct = (_ask - _bid) / optLtp;
        if(_spreadPct > 0.20) { // spread > 20% of premium
          setAtLog(p=>[{id:`${ts()}-${stock.symbol}`,time:ts(),symbol:stock.symbol,
            side,status:'SKIP_SPREAD',mode:'FO',
            note:`Spread ${(_spreadPct*100).toFixed(0)}% too wide on ${optSymbol}`},...p].slice(0,50));
          return;
        }
      }

      // Step 2: place the option order
      const orderRes = await apiFetch('/order/place', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          tradingsymbol:    optSymbol,
          exchange:         contract.exchange||'NFO',
          transaction_type: 'BUY',
          quantity:         lotSize,
          order_type:       optLtp>0 ? 'LIMIT' : 'LIMIT',
          price:            optLtp>0 ? roundToTick(optLtp, tick) : tick,
          product:          optProduct,
          tag:              'RABT-FO-AT',
        })
      });

      // Log success
      setAtLog(p=>[{
        id:`${ts()}-${stock.symbol}`,time:ts(),
        symbol:stock.symbol,
        contract:optSymbol,
        side:'BUY', qty:lotSize, price:optLtp,
        strike:contract.strike, expiry:contract.expiry,
        status:'PLACED', mode:'FO',
        note:`${optType} @ ₹${optLtp} · ${optSymbol}`
      },...p].slice(0,50));

      // Add TP/SL watcher on the option
      if(onAddAutoTP && optLtp > 0) {
        const foTpPct    = cfg.optTpPct || 20;
        const foSlPct    = cfg.optSlPct || 15;
        const foTrailPct = cfg.trailPct  || 5;
        const tp  = roundToTick(optLtp*(1+foTpPct/100), tick);
        const sl  = roundToTick(optLtp*(1-foSlPct/100), tick);
        onAddAutoTP({
          id:             `atp-fo-${Date.now()}`,
          symbol:         optSymbol,
          exchange:       contract.exchange||'NFO',
          side:           'BUY',
          entryPrice:     optLtp,
          tpPrice:        tp,
          // FIX: initial SL was premium×(1−trail%) = only 5% below entry — option
          // premiums swing 5% in seconds, so SL fired immediately. Start at the
          // configured option SL (15%); trail ratchets it up as premium rises.
          slPrice:        sl,
          qty:            lotSize,
          product:        optProduct,
          tpEnabled:      true,
          slEnabled:      true,
          trailEnabled:   true,
          trailPct:       foTrailPct,
          highWaterMark:  optLtp,
          tickSize:       tick,
          instrumentType: optType,
          source:         'FO-AT',
          orderId:        orderRes.order_id,
          createdAt:      new Date().toISOString(),
          status:         'WATCHING',
          // FIX: no zones on option watchers — underlying-scale zones vs option
          // premium made the zone logic force-fire SL immediately after entry.
          demandZone:     null,
          supplyZone:     null,
          zoneTouched:    'none',
        });
      }

      if(onJournalEntry) onJournalEntry({
        orderId:   orderRes.order_id,
        symbol:    optSymbol,
        exchange:  contract.exchange||'NFO',
        side:      'BUY',
        qty:       lotSize,
        price:     optLtp,
        product:   'NRML',
        source:    'fo_auto',
        tabMode:   'fo',
        tpPrice:   optLtp>0 ? roundToTick(optLtp*1.3, tick) : null,
        slPrice:   optLtp>0 ? roundToTick(optLtp*0.8, tick) : null,
      });

      if(confirmTrade) confirmTrade();
      else incrementDailyCount(dailyTradesRef, setDailyTrades);
      if(onShowToast) onShowToast(`🎯 F&O AT: BUY ${lotSize}×${optSymbol} @ ₹${optLtp}`,'ok');
      // Telegram alert for F&O auto-trade
      if(tgChat) {
        const tgMsg = `🎯 <b>F&O AUTO-TRADE: BUY ${optSymbol}</b>\nQty: ${lotSize} @ ₹${optLtp}\nUnderlying: ${stock.symbol} @ ₹${stock.price||stock.ltp||0}`;
        apiFetch('/alert/telegram',{method:'POST',headers:{'Content-Type':'application/json'},
          body:JSON.stringify({message:tgMsg,bot_token:tgToken||'',chat_id:tgChat})}).catch(()=>{});
      }

    } catch(e) {
      setAtLog(p=>[{id:`${ts()}-${stock.symbol}`,time:ts(),symbol:stock.symbol,
        side,status:'ERROR',mode:'FO',note:e.message},...p].slice(0,50));
    }
  }

  // ── Filtered results ──────────────────────────────────────────────────────────
  const displayResults = useMemo(()=>{
    let r = scanResults;
    if(filterType!=='ALL') r = r.filter(x=>{
      const sigs = (x.signals||[]).filter(s=>activeScan.includes(s.scan));
      if(filterType==='BUY')  return sigs.some(s=>s.type==='BUY');
      if(filterType==='SELL') return sigs.some(s=>s.type==='SELL');
      return false;
    });
    if(tableSearch) r = r.filter(x=>(x.symbol||'').toUpperCase().includes(tableSearch.toUpperCase()));
    r = [...r].sort((a,b)=>{
      if(sortBy==='score') return (b.score||0)-(a.score||0);
      if(sortBy==='symbol') return (a.symbol||'').localeCompare(b.symbol||'');
      const sA=(a.signals||[]).filter(s=>activeScan.includes(s.scan));
      const sB=(b.signals||[]).filter(s=>activeScan.includes(s.scan));
      const rA=sA.some(s=>s.type==='BUY')?0:sA.some(s=>s.type==='SELL')?1:2;
      const rB=sB.some(s=>s.type==='BUY')?0:sB.some(s=>s.type==='SELL')?1:2;
      return rA!==rB?rA-rB:(b.score||0)-(a.score||0);
    });
    return r;
  },[scanResults,filterType,tableSearch,activeScan,sortBy]);

  const buyCount  = scanResults.filter(r=>(r.signals||[]).filter(s=>activeScan.includes(s.scan)).some(s=>s.type==='BUY')).length;
  const sellCount = scanResults.filter(r=>(r.signals||[]).filter(s=>activeScan.includes(s.scan)).some(s=>s.type==='SELL')).length;

  const inp = {background:'#0b1624',border:'1px solid #23405f',borderRadius:0,
    padding:'6px 8px',color:'#e7edf5',fontFamily:'inherit',fontSize:11};

  // Mode colors
  const MODE_COLOR = { intraday:'#6b7280', investment:'#2ed47a', fo:'#49c2e4' };
  const mColor = MODE_COLOR[scanMode]||'#e8b339';

  // ── FO strategy modal helpers (carried from AutoFOTab) ────────────────────────
  async function previewStrategy(sym) {
    if(!sym) return;
    setStratLoading(true); setStratPreview(null);
    try {
      const r = await apiFetch('/fo/strategy',{method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({symbol:sym,strategy:foStrat,capital:foCapital,min_days:foMinDays})});
      setStratPreview(r);
    } catch(e) { setStratPreview({error:e.message}); }
    finally { setStratLoading(false); }
  }

  async function placeStrategyLegs(legs, capital, product) {
    for(const leg of legs) {
      await apiFetch('/order/place',{method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({symbol:leg.tradingsymbol,exchange:'NFO',
          transaction_type:leg.action,quantity:leg.quantity,
          order_type:'LIMIT',price:leg.ltp,product,tag:'RABT-AUTO-FO'})});
    }
  }

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',fontFamily:'inherit'}}>

      {/* ── Top toolbar ── */}
      <div style={{background:'#0b1624',borderBottom:'1px solid #1e3a5f',padding:'8px 14px',
        display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',flexShrink:0}}>

        {/* Unified scan — TF drives trade buttons, no mode switching needed */}
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <div style={{background:'#07101c',border:'1px solid #1e3a5f',borderRadius:0,
            padding:'4px 12px',fontSize:11,fontWeight:700,color:'#49c2e4'}}>
            {tfViewMode==='intraday'?'⚡ INTRA + F&O':
             tfViewMode==='investment'?'📈 F&O + CNC':
             '⚡📈🎯 INTRA + F&O + CNC'}
          </div>
          <span style={{fontSize:10,color:'#5a728f'}}>
            {tfViewMode==='intraday'?'MIS + Options':
             tfViewMode==='investment'?'CNC + Options':
             'All trade types'}
          </span>
        </div>

        {/* Toolbar collapse controls */}
        <div style={{display:'flex',gap:3,alignItems:'center',marginLeft:'auto'}}>
          <span style={{fontSize:10,color:'#23405f'}}>Hide:</span>
          {[
            [showWatchlist,  setShowWatchlist,  '📋'],
            [showTimeframes, setShowTimeframes,  '⏱'],
            [showSignals,    setShowSignals,     '📡'],
          ].map(([on,set,icon],i)=>(
            <button key={i} onClick={()=>set(p=>!p)}
              style={{background:on?'transparent':'rgba(232,179,57,0.1)',
                border:`1px solid ${on?'#1a2c44':'#e8b339'}`,
                borderRadius:0,padding:'2px 6px',color:on?'#23405f':'#49c2e4',
                fontFamily:'inherit',fontSize:11,cursor:'pointer'}}>
              {icon}
            </button>
          ))}
        </div>

        {/* Watchlist selector */}
        {showWatchlist&&(
        <div style={{display:'flex',gap:3,flexWrap:'wrap'}}>
          {Object.keys(ALL_LISTS).map(name=>(
            <button key={name} onClick={()=>{
              if(selectedLists.includes(name)) setSelectedLists(selectedLists.filter(l=>l!==name));
              else setSelectedLists([...selectedLists, name]);
            }}
              style={{background:selectedLists.includes(name)?`${mColor}22`:'#0f1d30',
                border:`1px solid ${selectedLists.includes(name)?mColor:'#23405f'}`,
                borderRadius:0,padding:'3px 8px',color:selectedLists.includes(name)?mColor:'#8ea2bf',
                fontFamily:'inherit',fontSize:12,cursor:'pointer',fontWeight:700}}>
              {name}
            </button>
          ))}
        </div>
        )}

        {/* Timeframe — single row, all modes */}
        {showTimeframes&&(
        <div style={{display:'flex',gap:2,flexWrap:'wrap',alignItems:'center'}}>
          {[
            {tf:'5min',  label:'5m',  hint:'INTRA'},
            {tf:'10min', label:'10m', hint:'INTRA'},
            {tf:'15min', label:'15m', hint:'INTRA'},
            {tf:'30min', label:'30m', hint:'INTRA'},
            {tf:'1hour', label:'1H',  hint:'FO+INTRA'},
            {tf:'1day',  label:'1D',  hint:'FO+CNC'},
            {tf:'1week', label:'1W',  hint:'FO+CNC'},
          ].map(({tf,label,hint})=>{
            const isIntra = ['5min','10min','15min','30min'].includes(tf);
            const isLong  = ['1day','1week'].includes(tf);
            const is1H    = tf==='1hour';
            const tfColor = isIntra?MODE_COLOR.intraday:isLong?MODE_COLOR.investment:MODE_COLOR.fo;
            const isActive= timeframe===tf;
            return(
              <button key={tf} onClick={()=>setTimeframe(tf)}
                style={{background:isActive?`${tfColor}22`:'#0f1d30',
                  border:`1px solid ${isActive?tfColor:'#1a2c44'}`,
                  borderRadius:0,padding:'3px 8px',
                  color:isActive?tfColor:'#8ea2bf',
                  fontFamily:'inherit',fontSize:11,cursor:'pointer',fontWeight:isActive?700:400,
                  position:'relative'}}>
                {label}
                {isActive&&<span style={{fontSize:8,marginLeft:2,opacity:0.7}}>{hint}</span>}
              </button>
            );
          })}
        </div>
        )}

        {/* Signal filter — all users pick scans: TSI/RSI/Vol/EMA/Pat/Zone/MACD/BB/Stoch/PTDS */}
        {showSignals&&(
        <div style={{display:'flex',gap:2,flexWrap:'wrap',alignItems:'center'}}>
          {[
            {n:1,  label:"TSI",         color:"#49c2e4"},
            {n:2,  label:"RSI",         color:"#a78bfa"},
            {n:3,  label:"Volume",      color:"#34d399"},
            {n:4,  label:"EMA",         color:"#f59e0b"},
            {n:5,  label:"📐 Patterns", color:"#fb923c"},
            {n:8,  label:"D/S Zone",    color:"#f472b6"},
          ].map(({n,label,color})=>{
            const on = activeScan.includes(n);
            return(
              <button key={n}
                onClick={()=>setActiveScan(p=>p.includes(n)?p.filter(x=>x!==n):[...p,n])}
                style={{background:on?`${color}22`:'#0f1d30',
                  border:`1px solid ${on?color:'#1a2c44'}`,
                  borderRadius:0,padding:'3px 8px',
                  color:on?color:'#8ea2bf',
                  fontFamily:'inherit',fontSize:11,
                  fontWeight:on?700:400,cursor:'pointer',
                  whiteSpace:'nowrap'}}>
                {on?'✓ ':''}{label}
              </button>
            );
          })}
          {/* ALL / NONE quick toggle */}
          <button onClick={()=>setActiveScan([1,2,3,4,5,8])}
            style={{background:'transparent',border:'none',color:'#e8b339',fontSize:10,cursor:'pointer',padding:'0 3px'}}>all</button>
          <button onClick={()=>setActiveScan([])}
            style={{background:'transparent',border:'none',color:'#5a728f',fontSize:10,cursor:'pointer',padding:'0 3px'}}>none</button>
        </div>
        )} {/* end showSignals */}

        {/* FO expiry filter */}
        {scanMode==='fo'&&(
          <div style={{display:'flex',gap:3,alignItems:'center'}}>
            <span style={{fontSize:12,color:'#8ea2bf'}}>Expiry≥</span>
            {[3,7,14,21].map(d=>(
              <button key={d} onClick={()=>setScanMinDays(d)}
                style={{background:scanMinDays===d?`${mColor}22`:'#0f1d30',
                  border:`1px solid ${scanMinDays===d?mColor:'#1a2c44'}`,
                  borderRadius:0,padding:'2px 7px',color:scanMinDays===d?mColor:'#8ea2bf',
                  fontFamily:'inherit',fontSize:12,cursor:'pointer'}}>
                {d}d
              </button>
            ))}
          </div>
        )}

        {/* Signal settings */}
        <button onClick={()=>setShowSignalModal(true)}
          style={{...inp,padding:'4px 8px',cursor:'pointer',fontSize:12}}>
          ⚙ Signals
        </button>

        {/* Telegram min signals — visible to all users */}
        {tgChat && (
          <div style={{display:'flex',alignItems:'center',gap:3,
            padding:'3px 7px',background:'#0f1d30',border:'1px solid #1a2c44',
            borderRadius:0,fontSize:10,color:'#5a728f'}}>
            <span>🔔 Min</span>
            {[1,2,3,4].map(n=>(
              <button key={n} onClick={()=>setTgMinSignals(n)}
                style={{background:tgMinSignals===n?'rgba(96,165,250,0.2)':'transparent',
                  border:`1px solid ${tgMinSignals===n?'#e8b339':'#1a2c44'}`,
                  borderRadius:0,padding:'1px 5px',
                  color:tgMinSignals===n?'#49c2e4':'#5a728f',
                  fontFamily:'inherit',fontSize:9,cursor:'pointer',fontWeight:700}}>
                {n}
              </button>
            ))}
          </div>
        )}

        {/* Scan / Live */}
        <button onClick={runScan} disabled={scanning||!authed}
          style={{background:`linear-gradient(135deg,${mColor}44,${mColor}22)`,
            border:`1px solid ${mColor}`,borderRadius:0,padding:'5px 16px',
            color:mColor,fontFamily:'inherit',fontSize:11,fontWeight:700,cursor:'pointer',
            opacity:scanning?0.6:1}}>
          {scanning?'⟳ Scanning...':'▶ SCAN'}
        </button>
        <button onClick={()=>setLiveMode(!liveMode)} disabled={!authed}
          style={{background:liveMode?'rgba(22,163,74,0.15)':'rgba(220,38,38,0.08)',
            border:`1px solid ${liveMode?'#2ed47a':'#ff5b6c'}`,borderRadius:0,
            padding:'5px 10px',color:liveMode?'#2ed47a':'#ff5b6c',
            fontFamily:'inherit',fontSize:10,fontWeight:700,cursor:'pointer'}}>
          {liveMode?'● LIVE ON':'● LIVE OFF'}
        </button>

        {/* ── Auto-Virtual toggle ── */}
        <AutoVirtualToggle enabled={autoVirtual} setEnabled={setAutoVirtual}
          minSigs={autoVirtualMin} setMinSigs={setAutoVirtualMin}/>

        {/* ── Two separate AT buttons — Intraday and F&O ── */}
        {isAdmin ? (
          <div style={{display:'flex',gap:4,alignItems:'center'}}>
            {/* Intraday AT */}
            <button onClick={()=>setAtEnabled(atEnabled==='intra'?false:'intra')} disabled={!authed}
              style={{background:atEnabled==='intra'?'rgba(107,114,128,0.2)':'#0f1d30',
                border:`1.5px solid ${atEnabled==='intra'?'#6b7280':'#23405f'}`,borderRadius:0,
                padding:'5px 10px',color:atEnabled==='intra'?'#374151':'#8ea2bf',
                fontFamily:'inherit',fontSize:10,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>
              ⚡ {atEnabled==='intra'?'INTRA ON':'INTRA AT'}
            </button>
            {/* F&O AT */}
            <button onClick={()=>setAtEnabled(atEnabled==='fo'?false:'fo')} disabled={!authed}
              style={{background:atEnabled==='fo'?'rgba(161,98,7,0.2)':'#0f1d30',
                border:`1.5px solid ${atEnabled==='fo'?'#49c2e4':'#23405f'}`,borderRadius:0,
                padding:'5px 10px',color:atEnabled==='fo'?'#49c2e4':'#8ea2bf',
                fontFamily:'inherit',fontSize:10,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>
              🎯 {atEnabled==='fo'?'F&O ON':'F&O AT'}
            </button>
            <button onClick={()=>setShowATCfg(!showATCfg)}
              style={{...inp,padding:'4px 8px',cursor:'pointer',fontSize:12,
                border:`1px solid ${atEnabled?'#49c2e4':'#23405f'}`}}>
              ⚙
            </button>
          </div>
        ) : (
          <span style={{fontSize:12,color:'#5a728f',padding:'4px 8px',
            background:'rgba(96,165,250,0.06)',border:'1px solid #1a2c44',
            borderRadius:0}}>
            📡 Signal Mode
          </span>
        )}

        {/* AI scan — mode toggle + one-click scan */}
        <div style={{display:'flex',gap:0,borderRadius:0,overflow:'hidden',border:'1px solid rgba(163,113,247,0.4)'}}>
          {[['equity','📈 MIS'],['fo','🎯 F&O']].map(([mode,lbl])=>(
            <button key={mode} onClick={()=>setAiScanMode(mode)}
              style={{background:aiScanMode===mode?'rgba(163,113,247,0.25)':'rgba(163,113,247,0.05)',
                border:'none',borderRight:mode==='equity'?'1px solid rgba(163,113,247,0.3)':'none',
                padding:'4px 10px',color:aiScanMode===mode?'#a78bfa':'#5a728f',
                fontFamily:'inherit',fontSize:10,fontWeight:aiScanMode===mode?700:400,cursor:'pointer'}}>
              {lbl}
            </button>
          ))}
          <button onClick={()=>{ setResultView('ai'); runAIScan(scanSymbols, 'NSE', timeframe, null, aiScanMode, onShowToast, onAddNote, onJournalEntry, sendTgAlert, isAdmin); }}
            disabled={aiLoading||!authed}
            style={{background:resultView==='ai'?'rgba(163,113,247,0.2)':'rgba(163,113,247,0.05)',
              border:'none',borderLeft:'1px solid rgba(163,113,247,0.3)',
              padding:'4px 12px',color:'#a78bfa',fontFamily:'inherit',
              fontSize:11,fontWeight:700,cursor:'pointer',opacity:aiLoading?0.6:1}}>
            {aiLoading?'⟳':'🤖'} {aiLoading?'Scanning...':'Scan'}
          </button>
          {/* AI scan ONLY stocks that have signals in the current technical scan */}
          <button onClick={()=>{
              const sigSyms=[...new Set((scanResults||[])
                .filter(r=>!r.error&&(r.signals||[]).some(s=>activeScan.includes(s.scan)))
                .map(r=>r.base_symbol||r.symbol))];
              if(sigSyms.length===0){ if(onShowToast) onShowToast("No stocks with scan signals yet — run technical scan first","error"); return; }
              setResultView('ai');
              runAIScan(sigSyms, 'NSE', timeframe, null, aiScanMode, onShowToast, onAddNote, onJournalEntry, sendTgAlert, isAdmin);
            }}
            disabled={aiLoading||!authed}
            title="AI scan only the stocks that currently show technical scan signals"
            style={{background:'rgba(163,113,247,0.05)',
              border:'none',borderLeft:'1px solid rgba(163,113,247,0.3)',
              padding:'4px 12px',color:'#e8b339',fontFamily:'inherit',
              fontSize:11,fontWeight:700,cursor:'pointer',opacity:aiLoading?0.6:1}}>
            🎯 Signals Only
          </button>
        </div>

        {/* ── Pre-entry validation — batch check all signals + auto-monitor ── */}
        <div style={{display:'flex',gap:0,borderRadius:0,overflow:'hidden',border:'1px solid rgba(56,189,248,0.4)'}}>
          <button onClick={()=>runValidateAll(false)}
            disabled={validating||!authed}
            title="Re-check EVERY signal live — pullback/entry zone, 1D trend and AI GO/WAIT/SKIP for each"
            style={{background:'rgba(56,189,248,0.08)',border:'none',
              padding:'4px 12px',color:'#38bdf8',fontFamily:'inherit',
              fontSize:11,fontWeight:700,cursor:'pointer',opacity:validating?0.6:1}}>
            {validating?'⟳ Validating…':'🔎 Validate All'}
          </button>
          <button onClick={()=>setAutoValidate(v=>{ const nv=!v; if(nv) setAutoScanValidate(false); return nv; })}
            title="Auto re-validate every 5 min and alert (in-app + Telegram) when a signal's status changes to GOOD ENTRY or EXIT"
            style={{background:autoValidate?'rgba(46,212,122,0.2)':'rgba(56,189,248,0.05)',
              border:'none',borderLeft:'1px solid rgba(56,189,248,0.3)',
              padding:'4px 10px',color:autoValidate?'#2ed47a':'#5a728f',fontFamily:'inherit',
              fontSize:10,fontWeight:700,cursor:'pointer'}}>
            {autoValidate?'🔔 Auto ON':'🔕 Auto'}
          </button>
          <button onClick={()=>setAutoScanValidate(v=>{ const nv=!v; if(nv) setAutoValidate(false); return nv; })}
            title="Auto Scan + Validate every 3 min: re-scans, validates the fresh signals, alerts only NEW good entries, and shows only validated GO cards"
            style={{background:autoScanValidate?'rgba(46,212,122,0.25)':'rgba(56,189,248,0.05)',
              border:'none',borderLeft:'1px solid rgba(56,189,248,0.3)',
              padding:'4px 10px',color:autoScanValidate?'#2ed47a':'#5a728f',fontFamily:'inherit',
              fontSize:10,fontWeight:700,cursor:'pointer'}}>
            {autoScanValidate?'⚡ Scan+Val ON':'⚡ Scan+Val'}
          </button>
        </div>

        {/* Status */}
        <span style={{fontSize:12,color:'#5a728f',marginLeft:'auto'}}>
          {lastScan&&`${lastScan}`}
          {scanTimeframe&&timeframe!==scanTimeframe&&` ⚠ Stale`}
        </span>
      </div>

      {/* ── AT config panel — two sections ── */}
      {isAdmin&&showATCfg&&(
        <div style={{background:'#07101c',borderBottom:'1px solid #d4c4a8',padding:'10px 14px',fontSize:10}}>

          {/* ── Validation gate — auto-trade only signals that validate as GO ── */}
          <div onClick={()=>setAtValidate(v=>!v)}
            title="When ON, the auto-trade engine validates every signal (pullback/entry zone + 1H + AI) and only trades the ones that come back GO. On validation error it skips the trade."
            style={{display:'flex',alignItems:'center',gap:8,marginBottom:10,padding:'7px 10px',cursor:'pointer',
              background:atValidate?'rgba(46,212,122,0.08)':'rgba(255,91,108,0.06)',
              border:`1px solid ${atValidate?'rgba(46,212,122,0.4)':'rgba(255,91,108,0.35)'}`,borderRadius:0}}>
            <span style={{fontSize:13}}>{atValidate?'✅':'⚠️'}</span>
            <span style={{fontSize:11,fontWeight:700,color:atValidate?'#2ed47a':'#ff8a95'}}>
              {atValidate?'VALIDATE BEFORE AUTO-TRADE — ON':'VALIDATE BEFORE AUTO-TRADE — OFF'}
            </span>
            <span style={{fontSize:9,color:'#8ea2bf',marginLeft:'auto'}}>
              {atValidate?'Only GO-validated signals are traded':'Trades raw signals — no entry check'}
            </span>
          </div>

          {/* ── Paper mode — record validated trades instead of placing real orders ── */}
          <div onClick={()=>setAtPaper(v=>!v)}
            title="Paper mode: validated-GO auto-trade signals are recorded as paper trades in Virtual Trading instead of placing real orders."
            style={{display:'flex',alignItems:'center',gap:8,marginBottom:10,padding:'7px 10px',cursor:'pointer',
              background:atPaper?'rgba(96,165,250,0.1)':'#0b1624',
              border:`1px solid ${atPaper?'#60a5fa':'#1a2c44'}`,borderRadius:0}}>
            <span style={{fontSize:13}}>{atPaper?'📝':'💵'}</span>
            <span style={{fontSize:11,fontWeight:700,color:atPaper?'#60a5fa':'#e7edf5'}}>
              {atPaper?'PAPER MODE — ON (no real orders)':'PAPER MODE — OFF (live orders)'}
            </span>
            <span style={{fontSize:9,color:'#8ea2bf',marginLeft:'auto'}}>
              {atPaper?'GO signals logged to Virtual Trading':'Real orders placed on GO'}
            </span>
          </div>

          <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>

            {/* ── Section 1: Intraday AT ── */}
            <div style={{flex:1,minWidth:260,background:'rgba(107,114,128,0.06)',
              border:`1.5px solid ${atEnabled==='intra'?'#6b7280':'#1a2c44'}`,
              borderRadius:0,padding:'10px 12px'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                <span style={{fontSize:11,fontWeight:700,color:'#374151'}}>
                  ⚡ INTRADAY AUTO-TRADE (MIS)
                </span>
                <span style={{fontSize:10,color:atEnabled==='intra'?'#2ed47a':'#8ea2bf',fontWeight:700}}>
                  {atEnabled==='intra'?'● ACTIVE':'○ OFF'}
                </span>
              </div>
              <div style={{fontSize:10,color:'#8ea2bf',marginBottom:8}}>
                Buys/sells equity directly (MIS). Works on any watchlist at any short timeframe (5m–30m).
              </div>
              <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
                {/* Capital — wider input for 5-6 digit amounts */}
                <label style={{display:'flex',alignItems:'center',gap:4,color:'#8ea2bf',fontSize:11}}>
                  Capital ₹
                  <input type="number" step="1000" value={atCfgIntra.capital||0}
                    onChange={e=>setAtCfgIntra(p=>({...p,capital:parseInt(e.target.value)||0}))}
                    style={{...inp,width:90,padding:'2px 6px',fontSize:11}}/>
                </label>
                {[
                  ['Min Sigs','minSignals','number',1],
                  ['TP %','tpPct','number',0.1],
                  ['SL %','slPct','number',0.1],
                  ['Trail %','trailPct','number',0.1],
                  ['Daily Max','dailyLimit','number',1],
                  ['Per Stock','maxPerSymbol','number',1],
                  ['Cooldown(s)','cooldown','number',60],
                ].map(([label,key,type,step])=>(
                  <label key={key} style={{display:'flex',alignItems:'center',gap:4,color:'#8ea2bf',fontSize:11}}>
                    {label}
                    <input type={type} step={step} value={atCfgIntra[key]||0}
                      onChange={e=>setAtCfgIntra(p=>({...p,[key]:parseFloat(e.target.value)||0}))}
                      style={{...inp,width:52,padding:'2px 6px',fontSize:11}}/>
                  </label>
                ))}
                <label style={{display:'flex',alignItems:'center',gap:4,color:'#8ea2bf',fontSize:11}}>
                  Side
                  <select value={atCfgIntra.side||'SIGNAL'}
                    onChange={e=>setAtCfgIntra(p=>({...p,side:e.target.value}))}
                    style={{...inp,padding:'2px 6px',fontSize:11}}>
                    <option value="SIGNAL">Signal</option>
                    <option value="BUY">BUY only</option>
                    <option value="SELL">SELL only</option>
                  </select>
                </label>
              </div>
              {margins&&(()=>{
                const avail = getMargin(margins,'mis');
                return <div style={{fontSize:10,color:'#2ed47a',marginTop:6}}>
                  💰 MIS margin avail: ₹{Math.round(avail).toLocaleString('en-IN')}
                </div>;
              })()}
            </div>

            {/* ── Section 2: F&O AT ── */}
            <div style={{flex:1,minWidth:260,background:'rgba(161,98,7,0.05)',
              border:`1.5px solid ${atEnabled==='fo'?'#49c2e4':'#1a2c44'}`,
              borderRadius:0,padding:'10px 12px'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                <span style={{fontSize:11,fontWeight:700,color:'#49c2e4'}}>
                  🎯 F&O AUTO-TRADE (NFO Options)
                </span>
                <span style={{fontSize:10,color:atEnabled==='fo'?'#2ed47a':'#8ea2bf',fontWeight:700}}>
                  {atEnabled==='fo'?'● ACTIVE':'○ OFF'}
                </span>
              </div>
              <div style={{fontSize:10,color:'#8ea2bf',marginBottom:8}}>
                Scans ANY watchlist. On signal → finds ATM CE/PE for that stock/index → places NFO option order.
              </div>
              {/* Limits row — highlighted in red so admin pays attention */}
              <div style={{background:'rgba(239,68,68,0.06)',border:'1px solid rgba(239,68,68,0.2)',
                borderRadius:0,padding:'6px 8px',marginBottom:8}}>
                <div style={{fontSize:9,color:'#ff5b6c',fontWeight:700,marginBottom:4}}>
                  🛑 STRICT LIMITS — enforced before every order
                </div>
                <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
                  {[
                    ['Daily Max','dailyLimit','number',1],
                    ['Per Stock/day','maxPerSymbol','number',1],
                    ['Cooldown (sec)','cooldown','number',300],
                    ['Min Signals','minSignals','number',1],
                  ].map(([label,key,type,step])=>(
                    <label key={key} style={{display:'flex',alignItems:'center',gap:4,color:'#fca5a5',fontSize:11}}>
                      {label}
                      <input type={type} step={step} value={atCfgFO[key]??0}
                        onChange={e=>setAtCfgFO(p=>({...p,[key]:parseFloat(e.target.value)||0}))}
                        style={{...inp,width:58,padding:'2px 6px',fontSize:11,
                          border:'1px solid rgba(239,68,68,0.4)',color:'#fca5a5'}}/>
                    </label>
                  ))}
                  <label style={{display:'flex',alignItems:'center',gap:4,color:'#fca5a5',fontSize:11}}>
                    Min Margin ₹
                    <input type="number" step="1000" value={atCfgFO.minMargin||5000}
                      onChange={e=>setAtCfgFO(p=>({...p,minMargin:parseInt(e.target.value)||0}))}
                      style={{...inp,width:80,padding:'2px 6px',fontSize:11,
                        border:'1px solid rgba(239,68,68,0.4)',color:'#fca5a5'}}/>
                  </label>
                </div>
                <div style={{fontSize:9,color:'#5a728f',marginTop:4}}>
                  Cooldown: same stock blocked for N seconds after trade · Per Stock: max trades on 1 stock/day
                </div>
              </div>
              {/* Options config row */}
              <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
                {[
                  ['Opt TP %','optTpPct','number',1],
                  ['Opt SL %','optSlPct','number',1],
                  ['Trail %','trailPct','number',1],
                  ['Min Days','minDays','number',1],
                ].map(([label,key,type,step])=>(
                  <label key={key} style={{display:'flex',alignItems:'center',gap:4,color:'#8ea2bf',fontSize:11}}>
                    {label}
                    <input type={type} step={step} value={atCfgFO[key]||0}
                      onChange={e=>setAtCfgFO(p=>({...p,[key]:parseFloat(e.target.value)||0}))}
                      style={{...inp,width:50,padding:'2px 6px',fontSize:11}}/>
                  </label>
                ))}
                <label style={{display:'flex',alignItems:'center',gap:4,color:'#8ea2bf',fontSize:11}}>
                  Instrument
                  <select value={atCfgFO.instrumentMode||'AUTO'}
                    onChange={e=>setAtCfgFO(p=>({...p,instrumentMode:e.target.value}))}
                    style={{...inp,padding:'2px 6px',fontSize:11}}>
                    <option value="AUTO">Auto (CE/PE)</option>
                    <option value="CE">Force CE</option>
                    <option value="PE">Force PE</option>
                    <option value="FUT">Futures (FUT)</option>
                  </select>
                </label>
                <label style={{display:'flex',alignItems:'center',gap:4,color:'#8ea2bf',fontSize:11}}>
                  Product
                  <select value={atCfgFO.optProduct||'NRML'}
                    onChange={e=>setAtCfgFO(p=>({...p,optProduct:e.target.value}))}
                    style={{...inp,padding:'2px 6px',fontSize:11}}>
                    <option value="NRML">NRML (full margin)</option>
                    <option value="MIS">MIS (intraday — lower margin)</option>
                  </select>
                </label>
                <label style={{display:'flex',alignItems:'center',gap:4,color:'#8ea2bf',fontSize:11}}>
                  Direction
                  <select value={atCfgFO.side||'SIGNAL'}
                    onChange={e=>setAtCfgFO(p=>({...p,side:e.target.value}))}
                    style={{...inp,padding:'2px 6px',fontSize:11}}>
                    <option value="SIGNAL">Follow Signal ↕</option>
                    <option value="BUY">Always BUY CE</option>
                    <option value="SELL">Always BUY PE</option>
                  </select>
                </label>
              </div>
              <div style={{fontSize:10,color:'#e8b339',marginTop:6,lineHeight:1.5}}>
                💡 BUY signal → BUY CE · SELL signal → BUY PE · Works on any stock/index watchlist
              </div>
            </div>
          </div>

          {/* AT Log */}
          <div style={{marginTop:10,background:'#07101c',borderRadius:0,
            padding:'6px 10px',maxHeight:120,overflow:'auto',border:'1px solid #1a2c44'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
              <span style={{fontSize:10,color:'#8ea2bf',fontWeight:700}}>
                AT LOG — {atLog.filter(l=>l.status==='PLACED').length}/{(atEnabled==='fo'?atCfgFO:atCfgIntra).dailyLimit||5} today · {atLog.filter(l=>l.status?.startsWith('SKIP')).length} skipped
              </span>
              <div style={{display:'flex',gap:4}}>
                <button onClick={()=>{ setAtFired({}); atFiredRef.current={}; setSymDailyTrades({}); symDailyRef.current={}; if(onShowToast)onShowToast('✓ AT cooldown + daily counts cleared','ok'); }}
                  style={{background:'rgba(232,179,57,0.1)',border:'1px solid #23405f',borderRadius:0,
                    padding:'1px 7px',color:'#49c2e4',fontFamily:'inherit',fontSize:9,cursor:'pointer'}}>
                  Clear Cooldown
                </button>
                <button onClick={()=>setAtLog([])}
                  style={{background:'none',border:'none',color:'#23405f',fontFamily:'inherit',fontSize:9,cursor:'pointer'}}>
                  ✕ clear
                </button>
              </div>
            </div>
            {atLog.length===0&&(
              <div style={{fontSize:10,color:'#23405f',fontStyle:'italic'}}>
                No AT activity yet. Waiting for scan signals...
              </div>
            )}
            {atLog.slice(0,10).map(l=>(
              <div key={l.id} style={{fontSize:10,display:'flex',gap:6,marginBottom:2,alignItems:'center',
                color:l.status==='PLACED'?'#2ed47a':
                      l.status==='ERROR'?'#ff5b6c':
                      l.status==='SKIP_SIG'?'#5a728f':
                      l.status==='SKIP_MIXED'?'#f59e0b':'#8ea2bf'}}>
                <span style={{color:'#5a728f',flexShrink:0}}>{l.time}</span>
                <span style={{fontWeight:700,color:'#e7edf5',flexShrink:0}}>{l.symbol}</span>
                <span style={{fontSize:9,padding:'1px 4px',borderRadius:0,flexShrink:0,
                  background:l.status==='PLACED'?'rgba(34,197,94,0.15)':
                             l.status==='ERROR'?'rgba(239,68,68,0.15)':'rgba(100,116,139,0.1)'}}>
                  {l.status}
                </span>
                {l.note&&<span style={{color:'#5a728f',fontStyle:'italic',fontSize:9}}>{l.note}</span>}
                {l.status==='PLACED'&&l.price&&<span style={{color:'#8ea2bf',marginLeft:'auto'}}>₹{l.price}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Scan/AI result toggle bar ── */}
      <div style={{display:'flex',background:'#0b1624',borderBottom:'1px solid #1a2c44',
        padding:'4px 14px',gap:6,flexShrink:0,alignItems:'center'}}>
        <button onClick={()=>setResultView('scan')}
          style={{background:resultView==='scan'?`${mColor}22`:'transparent',
            border:`1px solid ${resultView==='scan'?mColor:'#1a2c44'}`,
            borderRadius:0,padding:'3px 12px',color:resultView==='scan'?mColor:'#8ea2bf',
            fontFamily:'inherit',fontSize:10,fontWeight:resultView==='scan'?700:400,cursor:'pointer'}}>
          📡 Scan {scanResults.length>0&&`(${scanResults.length})`}
          {buyCount>0&&<span style={{color:'#2ed47a',marginLeft:4}}>▲{buyCount}</span>}
          {sellCount>0&&<span style={{color:'#ff5b6c',marginLeft:4}}>▼{sellCount}</span>}
        </button>
        <button onClick={()=>setResultView('ai')}
          style={{background:resultView==='ai'?'rgba(163,113,247,0.15)':'transparent',
            border:`1px solid ${resultView==='ai'?'#49c2e4':'#1a2c44'}`,
            borderRadius:0,padding:'3px 12px',color:resultView==='ai'?'#49c2e4':'#8ea2bf',
            fontFamily:'inherit',fontSize:10,fontWeight:resultView==='ai'?700:400,cursor:'pointer'}}>
          🤖 AI {aiResults.length>0&&`(${aiResults.length})`}
          {aiLoading&&<span style={{marginLeft:4,fontSize:11,animation:'spin 1s linear infinite',display:'inline-block'}}>⟳</span>}
        </button>
        <div style={{display:'flex',gap:2,marginLeft:8}}>
          {['ALL','BUY','SELL'].map(f=>(
            <button key={f} onClick={()=>setFilterType(f)}
              style={{background:filterType===f?`${mColor}18`:'transparent',
                border:`1px solid ${filterType===f?mColor:'#1a2c44'}`,
                borderRadius:0,padding:'2px 8px',color:filterType===f?mColor:'#8ea2bf',
                fontFamily:'inherit',fontSize:12,cursor:'pointer',fontWeight:filterType===f?700:400}}>
              {f}
            </button>
          ))}
        </div>
        <div style={{display:'flex',gap:2,marginLeft:6,alignItems:'center'}}>
          <span style={{fontSize:11,color:'#5a728f'}}>Sort:</span>
          {[['signal','Signal'],['score','Score'],['symbol','A-Z']].map(([v,l])=>(
            <button key={v} onClick={()=>setSortBy(v)}
              style={{background:sortBy===v?`${mColor}18`:'transparent',
                border:`1px solid ${sortBy===v?mColor:'#1a2c44'}`,
                borderRadius:0,padding:'2px 7px',color:sortBy===v?mColor:'#8ea2bf',
                fontFamily:'inherit',fontSize:11,cursor:'pointer',fontWeight:sortBy===v?700:400}}>
              {l}
            </button>
          ))}
        </div>
        <input value={tableSearch} onChange={e=>setTableSearch(e.target.value)}
          placeholder="Search..." style={{...inp,padding:'2px 8px',fontSize:12,width:90,marginLeft:4}}/>
        <div style={{display:'flex',gap:2,marginLeft:4}}>
          <button onClick={()=>setViewMode('grid')} title="Grid view"
            style={{background:viewMode==='grid'?`${mColor}22`:'transparent',
              border:`1px solid ${viewMode==='grid'?mColor:'#1a2c44'}`,
              borderRadius:0,padding:'2px 7px',color:viewMode==='grid'?mColor:'#8ea2bf',
              fontFamily:'inherit',fontSize:12,cursor:'pointer'}}>⊞</button>
          <button onClick={()=>setViewMode('table')} title="Table view"
            style={{background:viewMode==='table'?`${mColor}22`:'transparent',
              border:`1px solid ${viewMode==='table'?mColor:'#1a2c44'}`,
              borderRadius:0,padding:'2px 7px',color:viewMode==='table'?mColor:'#8ea2bf',
              fontFamily:'inherit',fontSize:12,cursor:'pointer'}}>☰</button>
        </div>
        <span style={{marginLeft:'auto',fontSize:12,color:'#5a728f'}}>
          {scanSymbols.length} symbols · {displayResults.length} results
        </span>
      </div>

      {/* ── Content area ── */}
      <div style={{flex:1,overflow:'hidden',display:'flex',flexDirection:'column'}}>

        {/* AI view */}
        {resultView==='ai'&&(
          aiLoading&&aiResults.length===0 ? (
            <div style={{textAlign:'center',padding:'60px 20px',color:'#5a728f'}}>
              <div style={{fontSize:24,animation:'spin 1s linear infinite',display:'inline-block'}}>⟳</div>
              <div style={{fontSize:12,marginTop:8}}>Running AI Analysis on {scanSymbols.length} symbols...</div>
            </div>
          ) : aiError ? (
            <div style={{textAlign:'center',padding:'40px 20px',color:'#ff5b6c'}}>
              <div style={{fontSize:24,marginBottom:8}}>⚠</div>
              <div style={{fontSize:13,color:'#ff5b6c',marginBottom:4}}>AI Scan Failed</div>
              <div style={{fontSize:11,color:'#8ea2bf',marginBottom:12,maxWidth:400,margin:'0 auto 12px'}}>{aiError}</div>
              <button onClick={()=>{ setResultView('ai'); runAIScan(scanSymbols, 'NSE', timeframe, null, aiScanMode, onShowToast, onAddNote, onJournalEntry, sendTgAlert, isAdmin); }}
                disabled={aiLoading}
                style={{background:'rgba(163,113,247,0.15)',border:'1px solid rgba(163,113,247,0.4)',borderRadius:0,
                  padding:'8px 18px',color:'#a78bfa',fontFamily:'inherit',fontSize:11,fontWeight:700,cursor:'pointer',marginRight:8}}>
                🔄 Retry
              </button>
              <button onClick={()=>setResultView('scan')}
                style={{background:'transparent',border:'1px solid #1a2c44',borderRadius:0,
                  padding:'8px 18px',color:'#8ea2bf',fontFamily:'inherit',fontSize:11,cursor:'pointer'}}>
                ← Back to Scan
              </button>
            </div>
          ) : aiResults.length===0 ? (
            <div style={{textAlign:'center',padding:'60px 20px',color:'#5a728f'}}>
              <div style={{fontSize:24,marginBottom:8}}>🤖</div>
              <div style={{fontSize:13,color:'#e7edf5',marginBottom:4}}>AI Equity Scanner</div>
              <div style={{fontSize:11,marginBottom:16}}>Click 🤖 AI in the toolbar to run AI analysis</div>
              <button onClick={()=>setResultView('scan')}
                style={{background:'transparent',border:'1px solid #1a2c44',borderRadius:0,
                  padding:'8px 18px',color:'#8ea2bf',fontFamily:'inherit',fontSize:11,cursor:'pointer'}}>
                ← Back to Scan
              </button>
            </div>
          ) : (
            <AIPanel isAdmin={isAdmin} canTrade={canTrade} results={aiResults} loading={aiLoading} error={aiError} setStrategyPopup={setStrategyPopup}
              visible={true} onClose={()=>setResultView('scan')}
              onTrade={(params)=>{
                if(aiScanMode==='fo') {
                  // F&O AI scan — open strategy popup with ATM options
                  setStrategyPopup({
                    symbol:  params.symbol,
                    exchange:'NSE',
                    ltp:     params.price||params.entryPrice||0,
                    hasBuy:  params.side==='BUY',
                    hasSell: params.side==='SELL',
                    signals: [],
                    lotSize: params.qty||50,
                    mode:    'fo',
                  });
                } else {
                  onTrade(params);
                }
              }}
              onShowToast={onShowToast}
              tabMode={aiScanMode}
              autoTrade={aiAutoTrade} setAutoTrade={setAiAutoTrade}
              minAutoConf={minAutoConf} setMinAutoConf={setMinAutoConf}
              autoFired={aiAutoFired}
              marginPct={marginPct} setMarginPct={setMarginPct}
              maxDaily={maxDaily} setMaxDaily={setMaxDaily}
              maxScans={maxScans} setMaxScans={setMaxScans}
              scanCount={scanCount} balance={balance}/>
          )
        )}

        {/* Scan results */}
        {resultView==='scan'&&(
          <div style={{flex:1,overflowY:'auto'}}>
            {scanError&&(
              <div style={{padding:'10px 14px',color:'#ff5b6c',fontSize:11,
                background:'rgba(248,81,73,0.08)',borderBottom:'1px solid rgba(248,81,73,0.2)'}}>
                ⚠ {scanError}
              </div>
            )}
            {!scanning&&scanResults.length===0&&!scanError&&(
              <div style={{textAlign:'center',padding:'60px 20px',color:'#5a728f'}}>
                <div style={{fontSize:32,marginBottom:10}}>
                  {scanMode==='intraday'?'⚡':scanMode==='investment'?'📈':'🎯'}
                </div>
                <div style={{fontSize:13,color:'#e7edf5',marginBottom:6}}>
                  {scanMode==='intraday'?'Intraday Scanner (MIS)':
                   scanMode==='investment'?'Investment Scanner (CNC)':
                   'F&O Scanner (NRML)'}
                </div>
                <div style={{fontSize:11,color:'#5a728f',lineHeight:1.8,marginBottom:16}}>
                  {scanMode==='intraday'&&<>5-30 min timeframes · MIS orders with broker leverage<br/>Tight TP/SL for quick intraday trades</>}
                  {scanMode==='investment'&&<>Daily/weekly timeframes · CNC delivery orders<br/>Wide TP/SL for swing and positional trades</>}
                  {scanMode==='fo'&&<>Options and futures · NRML orders<br/>Indices (NIFTY, BANKNIFTY) + F&O stocks</>}
                </div>
                <button onClick={runScan} disabled={!authed}
                  style={{background:`linear-gradient(135deg,${mColor}44,${mColor}22)`,
                    border:`1px solid ${mColor}`,borderRadius:0,padding:'10px 28px',
                    color:mColor,fontFamily:'inherit',fontSize:12,fontWeight:700,cursor:'pointer'}}>
                  ▶ START SCAN
                </button>
              </div>
            )}
            {scanning&&scanResults.length===0&&(
              <div style={{textAlign:'center',padding:'40px',color:'#5a728f',fontSize:12}}>
                ⟳ Scanning {scanSymbols.length} symbols [{scanMode}]...
              </div>
            )}

            {/* Validation results — always visible (both grid & table view) */}
            {Object.keys(validations).length>0&&(
              <ValidationResultList results={Object.values(validations)} title="🔎 Validate All results" />
            )}

            {/* Results — grid (default) or table */}
            {displayResults.length>0&&viewMode==='grid'&&(
              <ScanCardGrid
                results={displayResults}
                activeScan={activeScan}
                scanMode={scanMode}
                canTrade={canTrade}
                onTrade={onTrade}
                setStrategyPopup={setStrategyPopup}
                tfViewMode={tfViewMode}
                timeframe={timeframe}
                mColor={mColor}
                indexQuotes={indexQuotes}
                showIntraday={showIntraday}
                showInvestment={showInvestment}
                setChartModal={setChartModal}
                userBroker={userBroker}
                onShowToast={onShowToast}
                onAIScanOne={(sym)=>{ setResultView('ai'); runAIScan([sym], 'NSE', timeframe, null, aiScanMode, onShowToast, onAddNote, onJournalEntry, sendTgAlert, isAdmin); }}
                validations={validations}
                onValidateResult={(sym,res)=>{ setValidations(prev=>({...prev,[sym]:res})); prevVerdictsRef.current[sym]=res.verdict; }}
                showValidatedOnly={autoScanValidate}
              />
            )}
            {displayResults.length>0&&viewMode==='table'&&(
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
                  <thead>
                    <tr style={{background:'#0b1624',borderBottom:'1px solid #1a2c44',position:'sticky',top:0,zIndex:1}}>
                      {['Symbol','LTP','Signals','Score',scanMode==='fo'?'Action':'Trade'].map(h=>(
                        <th key={h} style={{padding:'7px 10px',color:'#8ea2bf',fontWeight:600,
                          textAlign:'left',fontSize:12,letterSpacing:1,whiteSpace:'nowrap'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayResults.map((r,i)=>{
                      const sigs  = (r.signals||[]).filter(s=>activeScan.includes(s.scan));
                      const hasBuy= sigs.some(s=>s.type==='BUY');
                      const hasSell=sigs.some(s=>s.type==='SELL');
                      const dotColor = hasBuy&&hasSell?'#e8b339':hasBuy?'#2ed47a':hasSell?'#ff5b6c':'#8ea2bf';

                      // ── Live price: try all key variants ──────────────────
                      const idxKey = INDEX_QUOTES_MAP[r.symbol]||r.symbol;
                      const liveQ  = liveQuotes[`NSE:${r.symbol}`]
                                  || liveQuotes[r.symbol]
                                  || liveQuotes[`NSE:${idxKey}`]
                                  || liveQuotes[idxKey];
                      const idxQ   = indexQuotes[`NSE:${idxKey}`]
                                  || indexQuotes[idxKey]
                                  || indexQuotes[r.symbol];
                      const liveLtp = parseFloat(
                        liveQ?.last_price || liveQ?.ltp ||
                        idxQ?.last_price  || idxQ?.ltp  ||
                        0
                      );
                      const ltp    = liveLtp > 0 ? liveLtp : parseFloat(r.ltp||r.price||0);
                      const isLive = liveLtp > 0;
                      const dispP  = ltp>0?ltp.toLocaleString('en-IN',{maximumFractionDigits:2}):r.error?'ERR':'—';
                      return(
                        <tr key={r.symbol||i} style={{borderBottom:'1px solid #161b22',
                          background:i%2===0?'#0b1624':'#0a0f1a',
                          transition:'background 0.1s'}}
                          onMouseEnter={e=>e.currentTarget.style.background='rgba(0,212,170,0.04)'}
                          onMouseLeave={e=>e.currentTarget.style.background=i%2===0?'#0b1624':'#07101c'}>
                          <td style={{padding:'7px 10px',fontWeight:700,color:'#e7edf5',whiteSpace:'nowrap'}}>
                            <span style={{width:7,height:7,borderRadius:'50%',background:dotColor,
                              display:'inline-block',marginRight:7}}/>
                            {r.symbol}
                            {r.error&&<span style={{fontSize:12,color:'#ff5b6c',marginLeft:4}}>⚠</span>}
                          </td>
                          <td style={{padding:'7px 10px',color:'#e7edf5',whiteSpace:'nowrap'}}>
                            ₹{dispP}
                            {isLive
                              ? <span style={{fontSize:9,color:'#2ed47a',marginLeft:3}}>●</span>
                              : <span style={{fontSize:9,color:'#e8b339',marginLeft:3,fontStyle:'italic'}} title="Stale scan price — market may have moved">~</span>
                            }
                          </td>
                          <td style={{padding:'7px 10px',maxWidth:220}}>
                            <div style={{display:'flex',gap:3,flexWrap:'wrap'}}>
                              {sigs.map((s,j)=>(
                                <span key={j} style={{fontSize:11,padding:'1px 5px',borderRadius:0,
                                  background:s.type==='BUY'?'rgba(22,163,74,0.12)':'rgba(220,38,38,0.12)',
                                  color:s.type==='BUY'?'#2ed47a':'#ff5b6c',
                                  border:`1px solid ${s.type==='BUY'?'rgba(63,185,80,0.3)':'rgba(248,81,73,0.3)'}`}}>
                                  {s.type==='BUY'?'▲':'▼'} {s.label}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td style={{padding:'7px 10px',color:'#8ea2bf',fontSize:10}}>
                            {r.score!==undefined?(r.score*100).toFixed(0):'—'}
                          </td>
                          <td style={{padding:'7px 10px',whiteSpace:'nowrap'}}>
                            {ltp>0&&!r.error&&(
                              !canTrade ? (
                                <span style={{fontSize:11,padding:'2px 6px',
                                  background:'rgba(96,165,250,0.06)',
                                  border:'1px solid #1a2c44',borderRadius:0,
                                  color:'#5a728f'}}>
                                  {hasBuy?'▲ BUY':'▼ SELL'} signal
                                </span>
                              ) : (()=>{
                                const isMixed = hasBuy && hasSell;
                                // Unified trade buttons — based on timeframe, not scanMode
                                const isMixedSig = hasBuy && hasSell;
                                return (
                                  <div style={{display:'flex',gap:3,flexWrap:'wrap',alignItems:'center'}}>
                                    {/* F&O Strategy — always shown */}
                                    <button onClick={()=>setStrategyPopup({
                                      symbol:r.symbol, exchange:'NSE', ltp,
                                      hasBuy, hasSell, signals:sigs,
                                      lotSize:r.lot_size||1, mode:'fo',
                                    })}
                                      style={{background:isMixedSig?'rgba(245,158,11,0.12)':'rgba(232,179,57,0.12)',
                                        border:`1px solid ${isMixedSig?'rgba(245,158,11,0.5)':'rgba(232,179,57,0.4)'}`,
                                        borderRadius:0,padding:'3px 8px',
                                        color:isMixedSig?'#f59e0b':'#e8b339',
                                        fontFamily:'inherit',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                                      🎯 {isMixedSig?'Straddle':'F&O'}
                                    </button>
                                    {/* Intraday MIS — TF < 1hr */}
                                    {showIntraday&&!isMixedSig&&hasBuy&&(
                                      <button onClick={()=>onTrade({symbol:r.symbol,exchange:'NSE',side:'BUY',price:ltp,qty:1,product:'MIS',signals:sigs,signalLabel:sigs.map(s=>s.label).join(' · ')})}
                                        style={{background:'rgba(34,197,94,0.12)',border:'1px solid rgba(34,197,94,0.35)',borderRadius:0,padding:'3px 7px',color:'#2ed47a',fontFamily:'inherit',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                                        ▲ MIS
                                      </button>
                                    )}
                                    {showIntraday&&!isMixedSig&&hasSell&&(
                                      <button onClick={()=>onTrade({symbol:r.symbol,exchange:'NSE',side:'SELL',price:ltp,qty:1,product:'MIS',signals:sigs,signalLabel:sigs.map(s=>s.label).join(' · ')})}
                                        style={{background:'rgba(239,68,68,0.12)',border:'1px solid rgba(239,68,68,0.35)',borderRadius:0,padding:'3px 7px',color:'#ff5b6c',fontFamily:'inherit',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                                        ▼ MIS
                                      </button>
                                    )}
                                    {/* Investment CNC — TF >= 1hr */}
                                    {showInvestment&&!isMixedSig&&hasBuy&&(
                                      <button onClick={()=>onTrade({symbol:r.symbol,exchange:'NSE',side:'BUY',price:ltp,qty:1,product:'CNC',signals:sigs,signalLabel:sigs.map(s=>s.label).join(' · ')})}
                                        style={{background:'rgba(34,197,94,0.08)',border:'1px solid rgba(34,197,94,0.25)',borderRadius:0,padding:'3px 7px',color:'#2ed47a',fontFamily:'inherit',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                                        ▲ CNC
                                      </button>
                                    )}
                                    {showInvestment&&!isMixedSig&&hasSell&&(
                                      <button onClick={()=>onTrade({symbol:r.symbol,exchange:'NSE',side:'SELL',price:ltp,qty:1,product:'CNC',signals:sigs,signalLabel:sigs.map(s=>s.label).join(' · ')})}
                                        style={{background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.25)',borderRadius:0,padding:'3px 7px',color:'#ff5b6c',fontFamily:'inherit',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                                        ▼ CNC
                                      </button>
                                    )}
                                  </div>
                                );
                              })()
                            )}
                            {/* ── Add to Virtual ── */}
                            {ltp>0&&!r.error&&onAddVirtual&&(
                              <button onClick={()=>onAddVirtual({
                                symbol:  r.symbol,
                                exchange:'NSE',
                                side:    hasBuy?'BUY':'SELL',
                                entry:   ltp,
                                tp:      ltp*(hasBuy?1+atCfg.tpPct/100:1-atCfg.tpPct/100),
                                sl:      ltp*(hasBuy?1-atCfg.slPct/100:1+atCfg.slPct/100),
                                qty:     1,
                                product: getProduct(),
                                tab_mode: scanMode,
                                source:  'scan',
                                signals: sigs.map(s=>s.label).join(', '),
                                score:   r.score||0,
                              })}
                                title="Paper trade — add to Virtual"
                                style={{background:'rgba(96,165,250,0.08)',
                                  border:'1px solid rgba(96,165,250,0.25)',
                                  borderRadius:0,padding:'3px 7px',color:'#6b7280',
                                  fontFamily:'inherit',fontSize:12,cursor:'pointer',marginLeft:3}}>
                                🎮
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* AT Log */}
            {atLog.length>0&&(
              <div style={{borderTop:'1px solid #1a2c44',maxHeight:140,overflowY:'auto',background:'#0a0f1a'}}>
                <div style={{padding:'5px 14px',fontSize:12,color:'#8ea2bf',letterSpacing:2,
                  display:'flex',justifyContent:'space-between',alignItems:'center',
                  borderBottom:'1px solid #1a2c44'}}>
                  <span>AUTO TRADE LOG [{scanMode.toUpperCase()}]</span>
                  <button onClick={()=>setAtLog([])} style={{background:'none',border:'none',
                    color:'#23405f',fontSize:11,cursor:'pointer'}}>clear</button>
                </div>
                {atLog.map(t=>(
                  <div key={t.id} style={{display:'flex',gap:8,alignItems:'center',
                    padding:'4px 14px',borderBottom:'1px solid #161b22',fontSize:10,flexWrap:'wrap'}}>
                    <span style={{color:'#8ea2bf',fontSize:12}}>{t.time}</span>
                    <span style={{fontSize:11,padding:'1px 5px',borderRadius:0,fontWeight:700,
                      background:`${MODE_COLOR[t.mode?.toLowerCase()]||'#8ea2bf'}18`,
                      color:MODE_COLOR[t.mode?.toLowerCase()]||'#8ea2bf'}}>
                      {t.mode}
                    </span>
                    <span style={{color:t.side==='BUY'?'#2ed47a':'#ff5b6c',fontWeight:700}}>{t.side||'BUY'}</span>
                    <span style={{color:'#e7edf5',fontWeight:600}}>{t.symbol}</span>
                    {t.contract&&t.contract!==t.symbol&&<span style={{color:'#49c2e4',fontSize:12}}>→{t.contract}</span>}
                    <span style={{color:'#8ea2bf'}}>@ ₹{t.price}</span>
                    {t.qty&&<span style={{color:'#8ea2bf',fontSize:12}}>×{t.qty}</span>}
                    <span style={{marginLeft:'auto',fontSize:12,padding:'1px 6px',borderRadius:0,
                      background:t.status==='PLACED'?'rgba(22,163,74,0.12)':t.status==='ERROR'?'rgba(220,38,38,0.12)':'rgba(96,165,250,0.12)',
                      color:t.status==='PLACED'?'#2ed47a':t.status==='ERROR'?'#ff5b6c':'#6b7280'}}>
                      {t.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── FO Strategy Trade Modal ── */}
      {strategyPopup&&(
        <StrategyPopup
          {...strategyPopup}
          canTrade={canTrade}
          onTrade={(params)=>{
            // params.symbol is now the resolved tradingsymbol (e.g. NIFTY24DEC19800PE)
            // params.price is the option LTP from fo/find_atm
            onTrade({
              symbol:         params.symbol,
              exchange:       params.exchange || (params.instrumentType==="FUT"?"NFO":"NFO"),
              side:           params.side,
              price:          params.price || 0,
              qty:            params.qty||1,
              product:        "NRML",
              instrumentType: params.instrumentType,
              suggestedStrike:params.suggestedStrike,
              signals:        strategyPopup.signals||[],
              signalLabel:    (strategyPopup.signals||[]).map(s=>s.label).join(' · '),
              underlying:     strategyPopup.symbol,
            });
          }}
          onClose={()=>setStrategyPopup(null)}
        />
      )}

      {tradeModal&&scanMode==='fo'&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.82)',zIndex:1000,
          display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:'#0f1d30',border:'1px solid #23405f',borderRadius:12,
            width:'min(420px,95vw)',maxHeight:'88vh',overflow:'auto'}}>
            <div style={{padding:'14px 16px',borderBottom:'1px solid #1a2c44',
              display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:'#e7edf5'}}>
                  {tradeModal.side==='BUY'?'▲':'▼'} {tradeModal.symbol}
                  <span style={{fontSize:11,color:'#8ea2bf',fontWeight:400,marginLeft:8}}>F&O Strategy</span>
                </div>
                <div style={{fontSize:11,color:tradeModal.side==='BUY'?'#2ed47a':'#ff5b6c'}}>
                  {tradeModal.side==='BUY'?'Bullish → BUY CE / Long FUT':'Bearish → BUY PE / Short FUT'}
                </div>
              </div>
              <button onClick={()=>{setTradeModal(null);setStratPreview(null);}}
                style={{background:'none',border:'none',color:'#8ea2bf',fontSize:18,cursor:'pointer'}}>✕</button>
            </div>
            <div style={{padding:'14px 16px',display:'flex',flexDirection:'column',gap:10}}>
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                {['AUTO','CE','PE','FUT','STRADDLE','STRANGLE'].map(s=>(
                  <button key={s} onClick={()=>{setFoStrat(s);}}
                    style={{background:foStrat===s?'rgba(0,212,170,0.15)':'#0b1624',
                      border:`1px solid ${foStrat===s?'#e8b339':'#1a2c44'}`,
                      borderRadius:0,padding:'5px 10px',color:foStrat===s?'#e8b339':'#8ea2bf',
                      fontFamily:'inherit',fontSize:10,cursor:'pointer',fontWeight:foStrat===s?700:400}}>
                    {s}
                  </button>
                ))}
              </div>
              <div style={{display:'flex',gap:8}}>
                <label style={{fontSize:10,color:'#8ea2bf',display:'flex',alignItems:'center',gap:4}}>
                  Capital ₹<input type="number" value={foCapital} onChange={e=>setFoCapital(parseInt(e.target.value)||0)}
                    style={{...inp,width:80,padding:'3px 6px',fontSize:10}}/>
                </label>
                <label style={{fontSize:10,color:'#8ea2bf',display:'flex',alignItems:'center',gap:4}}>
                  Min Days<input type="number" value={foMinDays} onChange={e=>setFoMinDays(parseInt(e.target.value)||15)}
                    style={{...inp,width:50,padding:'3px 6px',fontSize:10}}/>
                </label>
                <button onClick={()=>previewStrategy(tradeModal.symbol)}
                  style={{background:'#0f1d30',border:'1px solid #23405f',borderRadius:0,
                    padding:'4px 12px',color:'#6b7280',fontFamily:'inherit',fontSize:10,cursor:'pointer'}}>
                  {stratLoading?'⟳':'↻'} Preview
                </button>
              </div>
              {stratPreview&&!stratPreview.error&&stratPreview.legs&&(
                <div>
                  <div style={{fontSize:10,color:'#8ea2bf',marginBottom:6}}>
                    {stratPreview.strategy} · Est cost ₹{(stratPreview.estimated_cost||0).toLocaleString('en-IN')}
                  </div>
                  {stratPreview.legs.map((leg,i)=>(
                    <div key={i} style={{fontSize:10,padding:'5px 8px',marginBottom:3,borderRadius:0,
                      background:'#0b1624',border:'1px solid #1a2c44',
                      borderLeft:`3px solid ${leg.action==='BUY'?'#2ed47a':'#ff5b6c'}`}}>
                      <span style={{color:leg.action==='BUY'?'#2ed47a':'#ff5b6c',fontWeight:700,marginRight:6}}>
                        {leg.action}
                      </span>
                      {leg.tradingsymbol}
                      <span style={{color:'#8ea2bf',fontSize:12,marginLeft:6}}>
                        ₹{leg.strike} · {leg.option_type} · {leg.expiry} · ₹{leg.ltp}
                      </span>
                    </div>
                  ))}
                  <button onClick={async()=>{
                    setFoPlacing(true);
                    try {
                      await placeStrategyLegs(stratPreview.legs,foCapital,'NRML');
                      onShowToast&&onShowToast('✓ '+stratPreview.strategy+' placed');
                      setTradeModal(null); setStratPreview(null);
                    } catch(e){ onShowToast&&onShowToast('Order failed: '+e.message,'error'); }
                    finally{ setFoPlacing(false); }
                  }} disabled={foPlacing}
                    style={{marginTop:8,width:'100%',
                      background:'linear-gradient(135deg,rgba(0,212,170,0.3),rgba(0,212,170,0.15))',
                      border:'1px solid rgba(0,212,170,0.5)',borderRadius:0,padding:'10px',
                      color:'#e8b339',fontFamily:'inherit',fontSize:12,fontWeight:700,cursor:'pointer'}}>
                    {foPlacing?'⟳ Placing...':'🚀 Place Order'}
                  </button>
                </div>
              )}
              {stratPreview?.error&&(
                <div style={{padding:'8px 12px',background:'rgba(248,81,73,0.08)',
                  border:'1px solid rgba(248,81,73,0.3)',borderRadius:0,fontSize:11,color:'#ff5b6c'}}>
                  ✗ {stratPreview.error}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Signal settings modal */}
      {showSignalModal&&(
        <SignalSettingsModal
          params={signalParams||DEFAULT_SIGNAL_PARAMS}
          onChange={setSignalParams}
          onClose={()=>setShowSignalModal(false)}
        />
      )}
      {chartModal&&(
        <ChartModal
          symbol={chartModal.symbol}
          exchange={chartModal.exchange||"NSE"}
          timeframe={timeframe}
          onClose={()=>setChartModal(null)}
          signalParams={signalParams}
          atCfgIntra={atCfgIntra}
          onShowToast={onShowToast}
          authed={authed}
          scanZones={chartModal.zones||null}
          isAdmin={isAdmin}
          parentActiveScan={activeScan}
        />
      )}

    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// TAB: POSITIONS (live from Zerodha + select & save as strategy)
// ══════════════════════════════════════════════════════════════════════════════
// ── TP/SL watcher card embedded in position ───────────────────────────────
const PosCard=memo(({p,idx,_selected,_toggleSel,_getSignals,_watchers,_setWatchers,_livePrices,_onTrade,_showToast,_positionNotes,_setPositionNotes,_tpslOpenRef,_userRole="admin",_journal=[],_gttTrails={},_onGttTrailChange=null})=>{
  const key = p.tradingsymbol+(p.exchange||"");
  // Ref-backed showTPSL so panel stays open through live price re-renders
  const [showTPSL, _setShowTPSL] = useState(()=>_tpslOpenRef?.current?.[key]||false);
  const setShowTPSL = (val) => {
    const v = typeof val==="function" ? val(showTPSL) : val;
    if(_tpslOpenRef) _tpslOpenRef.current[key] = v;
    _setShowTPSL(v);
  };
  const sel=_selected.has(key);
  // Guard: ensure sigs is always an array (journal signals may be stored as string)
  const _rawSigs = _getSignals ? _getSignals(p.tradingsymbol) : [];
  const sigs = Array.isArray(_rawSigs) ? _rawSigs : [];
  const sym=p.tradingsymbol.toUpperCase();

  // Find matching journal entry for this position (signal/reason display)
  const journalMatch = _journal?.find(j => j.symbol === p.tradingsymbol && j.status === "OPEN");
  const posSignal = journalMatch?.signals || journalMatch?.tradeReason || "";
  const [showAddReason, setShowAddReason] = useState(false);
  const [posReason, setPosReason] = useState("");

  // Find matching watcher for this position (match symbol + exchange + side)
  const posExch = (p.exchange||"NSE").toUpperCase();
  const watcher = (_watchers||[]).find(w=>{
    const wSym  = (w.symbol||"").toUpperCase();
    const wExch = (w.exchange||"NSE").toUpperCase();
    // Match by symbol (and optionally exchange), status must be WATCHING
    return wSym===sym && w.status==="WATCHING" && (wExch===posExch || !w.exchange);
  });

  // ── GTT / Trail SL status — MUST be after watcher declaration ────────────
  const gttTrailInfo   = _gttTrails?.[sym] || null;
  const linkedGttId    = watcher?.gtt_id || watcher?.gttId || gttTrailInfo?.gtt_id || null;
  const gttTrailActive = !!(gttTrailInfo);
  const gttTrailCount  = gttTrailInfo?.trail_count || 0;
  const gttTrailPct    = gttTrailInfo?.trail_pct || watcher?.trailPct || 0;
  const gttLastPrice   = parseFloat(gttTrailInfo?.last_trail_price || 0);

  // TP/SL inline state (local, syncs to watcher)
  const [tpEnabled, setTpEnabled] = useState(watcher?.tpEnabled||false);
  const [tpPrice,   setTpPrice]   = useState(String(watcher?.tpPrice||""));
  const [slEnabled, setSlEnabled] = useState(watcher?.slEnabled||false);
  const [slPrice,   setSlPrice]   = useState(String(watcher?.slPrice||""));
  const [trailEnabled,setTrailEnabled] = useState(watcher?.trailEnabled||false);
  const [trailPct,  setTrailPct]  = useState(()=>{
    if(watcher?.trailPct) return String(watcher.trailPct);
    const itype = (p.instrument_type||"").toUpperCase();
    return ["CE","PE"].includes(itype) ? "2" : "0.5";
  });
  const [saving,    setSaving]    = useState(false);
  const [dsZones,   setDsZones]   = useState(null);
  // Fetch D/S zones when TP/SL panel opens
  useEffect(()=>{
    if(!showTPSL) return;
    const exch = (p.exchange||"NSE").toUpperCase();
    apiFetch("/chart/candles",{method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({symbol:p.tradingsymbol,exchange:exch,timeframe:"1hour",days:30})
    }).then(d=>{ if(d?.zones) setDsZones(d.zones); }).catch(()=>{});
  },[showTPSL]);

  const avgP = parseFloat(p.average_price||0);
  // Use live price if available, fallback to Kite last_price
  const liveQ  = _livePrices[p.tradingsymbol] || _livePrices[`${p.exchange}:${p.tradingsymbol}`];
  const ltp    = parseFloat(liveQ?.ltp || liveQ?.last_price || p.last_price || 0);
  const isLive = !!(liveQ?.ltp || liveQ?.last_price);
  const side   = p.quantity>0 ? "BUY" : "SELL";

  // Quick pct → price helpers
  const pctToPrice=(pct,dir)=>{
    if(!avgP) return 0;
    return dir==="up" ? (avgP*(1+pct/100)).toFixed(2) : (avgP*(1-pct/100)).toFixed(2);
  };

  const saveWatcher=async()=>{
    if(!tpEnabled && !slEnabled && !trailEnabled) return;
    setSaving(true);
    const wid = watcher?.id || `pos-${p.tradingsymbol}-${Date.now()}`;
    const newW = {
      id:          wid,
      symbol:      p.tradingsymbol,
      exchange:    p.exchange||"NSE",
      side,
      qty:         Math.abs(p.quantity),
      entryPrice:  avgP,
      product:     p.product||"MIS",
      instrumentType: p.instrument_type||"EQ",
      tickSize:    p.tick_size||0.05,
      tpEnabled,   tpPrice:  parseFloat(tpPrice)||0,
      slEnabled,   slPrice:  parseFloat(slPrice)||0,
      trailEnabled,trailPct: parseFloat(trailPct)||1.5,
      highWaterMark: ltp,
      lowWaterMark:  ltp,
      status: "WATCHING",
      createdAt: new Date().toISOString(),
    };
    // Update local state
    if(watcher) {
      _setWatchers(prev=>prev.map(w=>w.id===watcher.id ? newW : w));
    } else {
      _setWatchers(prev=>[newW,...prev]);
    }

    if(_userRole === "admin") {
      // Admin: register on VPS — auto-fires exit orders
      try {
        await apiFetch("/watcher/add", {
          method:"POST",
          headers:{"Content-Type":"application/json"},
          body: JSON.stringify({
            watcher_id:      wid,
            symbol:          p.tradingsymbol,
            exchange:        p.exchange||"NSE",
            side,
            qty:             Math.abs(p.quantity),
            entry_price:     avgP,
            product:         p.product||"MIS",
            tp_enabled:      tpEnabled,
            tp_price:        parseFloat(tpPrice)||0,
            sl_enabled:      slEnabled,
            sl_price:        parseFloat(slPrice)||0,
            trail_enabled:   trailEnabled,
            trail_pct:       parseFloat(trailPct)||1.5,
            high_water_mark: ltp,
            low_water_mark:  ltp,
            tick_size:       parseFloat(p.tick_size||0.05),
          })
        });
        // Also register existing GTT for trailing if trail is enabled
        // This makes the Kite GTT SL auto-update as price moves (30s interval)
        if(trailEnabled && parseFloat(trailPct) > 0) {
          apiFetch("/gtt/trail/register-auto", {
            method:"POST", headers:{"Content-Type":"application/json"},
            body: JSON.stringify({
              symbol:      p.tradingsymbol,
              exchange:    p.exchange||"NSE",
              side,
              trail_pct:   parseFloat(trailPct)||1.5,
              entry_price: avgP,
            })
          }).then(r=>{
            if(r?.ok && _showToast)
              _showToast(`✓ TP/SL on VPS + GTT ${r.gtt_id} trailing ${trailPct}% auto-enabled`,"ok");
            else if(_showToast)
              _showToast(`✓ TP/SL active on VPS for ${p.tradingsymbol} (no existing GTT found — set SL via order ticket to enable GTT trail)`,"ok");
          }).catch(()=>{
            if(_showToast) _showToast(`✓ TP/SL active on VPS for ${p.tradingsymbol}`,"ok");
          });
        } else {
          if(_showToast) _showToast(`✓ TP/SL active on VPS for ${p.tradingsymbol}`,"ok");
        }
      } catch(e) {
        if(_showToast) _showToast(`⚠ TP/SL saved locally — VPS sync failed: ${e.message}`,"warn");
      }
    } else {
      // Trader/viewer: create GTT via Kite — broker-native, SEBI compliant
      // No automated order from our VPS
      try {
        const r = await createGTT({
          symbol: p.tradingsymbol, exchange: p.exchange||"NSE",
          side, qty: Math.abs(p.quantity), entryPrice: avgP,
          tpPrice: tpEnabled ? parseFloat(tpPrice) : null,
          slPrice: slEnabled ? parseFloat(slPrice) : null,
          tpEnabled, slEnabled,
        });
        const gttId = r?.gtt_id;
        if(gttId) {
          _setWatchers(prev=>prev.map(w=>w.id===wid ? {...w, gttId} : w));
          if(_showToast) _showToast(`✓ GTT created in Kite (ID: ${gttId}) — Zerodha will monitor TP/SL`,"ok");
        } else {
          if(_showToast) _showToast(`⚠ GTT creation failed — set TP/SL manually in Kite`,"warn");
        }
      } catch(e) {
        if(_showToast) _showToast(`⚠ GTT failed: ${e.message} — set TP/SL in Kite manually`,"warn");
      }
    }
    setSaving(false);
  };

  const cancelWatcher=()=>{
    if(!watcher) return;
    _setWatchers(prev=>prev.map(w=>w.id===watcher.id
      ? {...w,status:"CANCELLED"} : w));
    setTpEnabled(false); setSlEnabled(false); setTrailEnabled(false);
    if(_showToast) _showToast(`✕ TP/SL cancelled for ${p.tradingsymbol}`);
  };

  // Payoff chart for F&O positions
  const [showPayoffPos, setShowPayoffPos] = useState(false);
  // Robust F&O detection — instrument_type sometimes empty from Kite
  const isFOPos = (()=>{
    const sym   = (p.tradingsymbol||"").toUpperCase();
    const itype = (p.instrument_type||"").toUpperCase();
    const exch  = (p.exchange||"").toUpperCase();
    if(["CE","PE","FUT"].includes(itype)) return true;
    if(["NFO","MCX","BFO","CDS"].includes(exch)) return true;
    if(/CE$|PE$|FUT$/.test(sym)) return true;
    if(/\d{2}[A-Z]{3}\d{2}/.test(sym)) return true;
    return false;
  })();
  const posPayoffLegs = useMemo(()=>{
    if(!isFOPos||!avgP) return [];
    const isCE  = p.instrument_type==="CE";
    const isPE  = p.instrument_type==="PE";
    const isFut = p.instrument_type==="FUT";
    // strike_price from Kite is the actual strike
    const strike  = parseFloat(p.strike_price||p.strike||avgP)||avgP;
    // For options: premium = average_price (what was paid per unit)
    // For futures: premium = 0 (mark-to-market daily)
    const premium = isFut ? 0 : parseFloat(p.average_price||0);
    // qty = number of contracts (already includes lot size in Kite)
    // multiplier = lot size (e.g. 50 for NIFTY, 15 for BANKNIFTY)
    // For payoff chart: qty=signedQty (raw contracts), lotSize=multiplier
    const absQty  = Math.abs(parseInt(p.quantity)||1);
    const lotSz   = parseFloat(p.multiplier||1);
    const dir     = p.quantity>0?"BUY":"SELL";
    // Pass qty=1 lot and lotSize=absQty*lotSz for correct total exposure
    if(isCE)  return [{type:"CE",  side:dir, strike, premium, qty:1, lotSize:absQty*lotSz}];
    if(isPE)  return [{type:"PE",  side:dir, strike, premium, qty:1, lotSize:absQty*lotSz}];
    if(isFut) return [{type:"FUT", side:dir, strike:avgP, premium:0, qty:1, lotSize:absQty*lotSz}];
    return [];
  },[p,avgP,isFOPos]);

  // P&L calculation — F&O uses multiplier (lot size), use signed qty for direction
  // Kite: quantity is signed (+ve = long, -ve = short)
  const signedQty  = parseInt(p.quantity) || 0;
  const multiplier = parseFloat(p.multiplier || 1);
  const livePnl    = avgP > 0 && ltp > 0
    ? parseFloat(((ltp - avgP) * signedQty * multiplier).toFixed(2))
    : parseFloat(p.pnl || 0);
  const pnlPct = avgP > 0
    ? ((ltp - avgP) / avgP * 100 * (signedQty >= 0 ? 1 : -1)).toFixed(2)
    : "0.00";

  return(
    <div style={{background:sel?"rgba(0,212,170,0.07)":"#0f1d30",
      border:`1.5px solid ${sel?"#e8b339":livePnl>=0?"rgba(63,185,80,0.2)":"rgba(248,81,73,0.2)"}`,
      borderLeft:`3px solid ${sel?"#e8b339":livePnl>=0?"#2ed47a":"#ff5b6c"}`,
      borderRadius:0,marginBottom:8,overflow:"hidden"}}>

      {/* ── Main position row — clicking header toggles selection, but NOT when TP/SL open ── */}
      <div style={{padding:"10px 12px",cursor:"pointer"}}
        onClick={()=>{ if(!showTPSL && !showPayoffPos) _toggleSel(key); }}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
          <div>
            <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
              <span style={{fontSize:13,fontWeight:700,color:"#e7edf5"}}>{p.tradingsymbol}</span>
              <span style={{fontSize:12,padding:"1px 6px",borderRadius:0,fontWeight:700,
                background:side==="BUY"?"rgba(22,163,74,0.12)":"rgba(220,38,38,0.12)",
                color:side==="BUY"?"#2ed47a":"#ff5b6c"}}>{side}</span>
              <span style={{fontSize:12,color:"#8ea2bf"}}>{p.product} · {p.exchange}</span>
              {watcher&&(
                <span style={{fontSize:11,padding:"1px 5px",borderRadius:0,
                  background:"rgba(0,212,170,0.1)",color:"#e8b339",fontWeight:700,
                  animation:"pulse 2s infinite"}}>🎯 TP/SL ON</span>
              )}
              {/* GTT Active badge — shows if a Kite GTT is linked to this position */}
              {linkedGttId && (
                <span style={{fontSize:11,padding:"1px 6px",borderRadius:0,
                  background:"rgba(99,102,241,0.12)",border:"1px solid rgba(99,102,241,0.35)",
                  color:"#818cf8",fontWeight:700}}
                  title={`Kite GTT ID: ${linkedGttId} — Zerodha will auto-exit on trigger`}>
                  🏷 GTT #{linkedGttId}
                </span>
              )}
              {/* GTT Trail Active badge — shows if VPS is auto-trailing the GTT's SL */}
              {gttTrailActive && (
                <span style={{fontSize:11,padding:"1px 6px",borderRadius:0,
                  background:"rgba(16,185,129,0.12)",border:"1px solid rgba(16,185,129,0.4)",
                  color:"#34d399",fontWeight:700,animation:"pulse 2s infinite"}}
                  title={`GTT SL trailing at ${gttTrailPct}% — updated ${gttTrailCount} time${gttTrailCount!==1?"s":""} by VPS`}>
                  🔄 GTT Trail {gttTrailPct}%
                  {gttTrailCount>0&&<span style={{fontWeight:400,color:"#6ee7b7",marginLeft:3}}>↑{gttTrailCount}x</span>}
                </span>
              )}
              {posSignal && (
                <span style={{fontSize:10,padding:"1px 5px",borderRadius:3,
                  background:"rgba(232,179,57,0.1)",color:"#e8b339",
                  maxWidth:220,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}
                  title={posSignal}>
                  {journalMatch?.underlying && journalMatch.underlying !== p.tradingsymbol
                    ? `${journalMatch.underlying}: ${posSignal}`
                    : posSignal}
                </span>
              )}
              {!posSignal && !showAddReason && (
                <button onClick={(e)=>{e.stopPropagation(); setShowAddReason(true);}}
                  style={{fontSize:10,padding:"1px 5px",borderRadius:3,
                    background:"rgba(96,165,250,0.1)",border:"1px solid rgba(96,165,250,0.2)",
                    color:"#60a5fa",cursor:"pointer"}}>
                  + Add signal
                </button>
              )}
            </div>
            <div style={{fontSize:12,color:"#8ea2bf",marginTop:2}}>
              Qty: {p.quantity} · Avg: ₹{p.average_price}
            </div>
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:15,fontWeight:700,color:pnlColor(livePnl)}}>{fmt(livePnl)}</div>
            <div style={{fontSize:12,display:"flex",alignItems:"center",gap:4,justifyContent:"flex-end"}}>
              <span style={{color:isLive?"#e8b339":"#8ea2bf"}}>
                {isLive?"🟢":""}₹{ltp.toLocaleString("en-IN",{maximumFractionDigits:2})}
              </span>
              <span style={{color:pnlColor(pnlPct)}}>{pnlPct>0?"+":""}{pnlPct}%</span>
            </div>
          </div>
        </div>
        {/* Current TP/SL/Trail summary if watcher active */}
        {watcher&&(
          <div style={{display:"flex",gap:6,marginTop:5,flexWrap:"wrap",alignItems:"center",
            padding:"4px 8px",background:"rgba(232,179,57,0.06)",border:"1px solid rgba(232,179,57,0.15)",borderRadius:0}}>
            {watcher.tpEnabled && parseFloat(watcher.tpPrice)>0 &&(
              <span style={{fontSize:11,color:"#2ed47a",fontWeight:700,
                background:"rgba(34,197,94,0.1)",padding:"2px 6px",borderRadius:0,
                border:"1px solid rgba(34,197,94,0.25)"}}>
                🎯 TP ₹{parseFloat(watcher.tpPrice).toLocaleString("en-IN")}
                {watcher.tpPct>0&&<span style={{color:"#5a728f",fontWeight:400,marginLeft:3}}>({watcher.tpPct}%)</span>}
              </span>
            )}
            {watcher.slEnabled&&!watcher.trailEnabled && parseFloat(watcher.slPrice)>0 &&(
              <span style={{fontSize:11,color:"#ff5b6c",fontWeight:700,
                background:"rgba(239,68,68,0.1)",padding:"2px 6px",borderRadius:0,
                border:"1px solid rgba(239,68,68,0.25)"}}>
                🛡 SL ₹{parseFloat(watcher.slPrice).toLocaleString("en-IN")}
                {watcher.slPct>0&&<span style={{color:"#5a728f",fontWeight:400,marginLeft:3}}>({watcher.slPct}%)</span>}
              </span>
            )}
            {watcher.trailEnabled&&(()=>{
              const hwm      = parseFloat(watcher.highWaterMark||watcher.entryPrice||0);
              const tPct     = parseFloat(watcher.trailPct||0);
              const trailSL  = hwm>0&&tPct>0
                ? watcher.side==="BUY"
                  ? (hwm*(1-tPct/100)).toFixed(2)
                  : (hwm*(1+tPct/100)).toFixed(2)
                : watcher.slPrice;
              const hwmMoved = hwm>0 && Math.abs(hwm-parseFloat(watcher.entryPrice||0))>0.01;
              return (
                <span style={{fontSize:11,color:"#49c2e4",fontWeight:700,
                  background:"rgba(96,165,250,0.1)",padding:"2px 6px",
                  borderRadius:0,border:"1px solid rgba(96,165,250,0.3)"}}>
                  🔄 Trail {watcher.trailPct}% · SL ₹{trailSL}
                  {hwmMoved&&<span style={{color:"#5a728f",marginLeft:4,fontWeight:400}}>
                    (peak ₹{hwm.toFixed(0)})
                  </span>}
                </span>
              );
            })()}
          </div>
        )}
        {/* GTT Trail detail row — shown whenever GTT trail is registered (with or without VPS watcher) */}
        {gttTrailActive && (
          <div style={{display:"flex",gap:6,marginTop:5,flexWrap:"wrap",alignItems:"center",
            padding:"4px 8px",
            background:"rgba(16,185,129,0.06)",
            border:"1px solid rgba(16,185,129,0.25)",borderRadius:0}}>
            <span style={{fontSize:10,color:"#34d399",fontWeight:700}}>🔄 GTT SL Auto-Trail</span>
            <span style={{fontSize:10,color:"#6ee7b7"}}>
              {gttTrailPct}% trail
              {gttLastPrice>0&&<> · last moved @ ₹{gttLastPrice.toLocaleString("en-IN",{maximumFractionDigits:2})}</>}
              {gttTrailCount>0&&<> · updated {gttTrailCount}x</>}
            </span>
            {linkedGttId&&(
              <span style={{fontSize:10,color:"#5a728f",marginLeft:"auto"}}>
                GTT #{linkedGttId}
              </span>
            )}
            {/* Stop trailing button — removes from GTT_TRAIL_CONFIG, GTT stays active */}
            <button onClick={e=>{
              e.stopPropagation();
              if(!window.confirm(`Stop trailing GTT ${linkedGttId} for ${sym}?\nThe GTT will remain active at its current SL — only auto-update will stop.`)) return;
              apiFetch(`/gtt/trail/${linkedGttId}`,{method:"DELETE"})
                .then(()=>{ if(_onGttTrailChange) _onGttTrailChange(); })
                .catch(()=>{});
            }} style={{marginLeft:"auto",fontSize:10,padding:"1px 6px",
              background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",
              color:"#f87171",borderRadius:0,cursor:"pointer"}}>
              ✕ Stop Trail
            </button>
          </div>
        )}
        {/* Fallback: show TP/SL from journal if no watcher but journal has values */}
        {!watcher && journalMatch && (journalMatch.tpPrice || journalMatch.slPrice) && (
          <div style={{display:"flex",gap:6,marginTop:5,flexWrap:"wrap",alignItems:"center",
            padding:"4px 8px",background:"rgba(107,114,128,0.06)",border:"1px solid rgba(107,114,128,0.15)",borderRadius:0}}>
            {journalMatch.tpPrice && parseFloat(journalMatch.tpPrice)>0 && (
              <span style={{fontSize:11,color:"#6b7280",fontWeight:700}}>
                TP ₹{parseFloat(journalMatch.tpPrice).toLocaleString("en-IN")}
              </span>
            )}
            {journalMatch.slPrice && parseFloat(journalMatch.slPrice)>0 && (
              <span style={{fontSize:11,color:"#6b7280",fontWeight:700}}>
                SL ₹{parseFloat(journalMatch.slPrice).toLocaleString("en-IN")}
              </span>
            )}
            <span style={{fontSize:9,color:"#475569",fontStyle:"italic"}}>journal · no watcher</span>
          </div>
        )}
        {sigs.map((s,j)=><Badge key={j} type={s.type}>{s.type==="BUY"?"▲":"▼"} {s.label}</Badge>)}

        {/* ── Inline add-reason for external trades ── */}
        {showAddReason && (
          <div style={{display:"flex",gap:6,marginTop:6,alignItems:"center"}} onClick={e=>e.stopPropagation()}>
            <select value={posReason} onChange={e=>setPosReason(e.target.value)}
              style={{flex:1,padding:"4px 6px",fontSize:11,background:"#0a1628",
                border:"1px solid #23405f",borderRadius:4,color:"#e7edf5"}}>
              <option value="">Select reason...</option>
              {TRADE_REASONS.map(r=><option key={r} value={r}>{r}</option>)}
            </select>
            <button onClick={()=>{
              if(posReason) {
                apiFetch("/trade-log/signal/update",{method:"POST",headers:{"Content-Type":"application/json"},
                  body:JSON.stringify({order_id:p.tradingsymbol,trade_reason:posReason,signals:posReason,source:"external"})
                }).catch(()=>{});
                setShowAddReason(false);
                if(_showToast) _showToast(`Signal saved for ${p.tradingsymbol}`);
              }
            }}
              style={{padding:"4px 8px",fontSize:10,background:"rgba(232,179,57,0.2)",
                border:"1px solid #e8b339",borderRadius:4,color:"#e8b339",cursor:"pointer",fontWeight:700}}>
              Save
            </button>
            <button onClick={()=>setShowAddReason(false)}
              style={{padding:"4px 6px",fontSize:10,background:"transparent",
                border:"1px solid #23405f",borderRadius:4,color:"#8ea2bf",cursor:"pointer"}}>
              X
            </button>
          </div>
        )}

        {/* ── Signal logic from auto-trade ── */}
        {(()=>{
          const note = _positionNotes&&_positionNotes[sym];
          if(!note) return null;
          const srcColor = note.source==="AI Auto"?"#49c2e4":note.source==="Scanner"?"#e8b339":"#6b7280";
          const sigColor = note.signal==="BUY"?"#2ed47a":note.signal==="SELL"?"#ff5b6c":"#8ea2bf";
          return (
            <div style={{marginTop:6,padding:"6px 8px",borderRadius:0,
              background:"rgba(0,0,0,0.08)",border:"1px solid rgba(255,255,255,0.06)"}}>
              {/* Source + signal */}
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4,flexWrap:"wrap"}}>
                <span style={{fontSize:11,padding:"1px 6px",borderRadius:0,fontWeight:700,
                  background:`${srcColor}18`,color:srcColor,border:`1px solid ${srcColor}40`}}>
                  {note.source==="AI Auto"?"🤖 AI AUTO":
                   note.source==="Scanner"?"📡 SCANNER":"⚡ AUTO"}
                </span>
                {note.signal&&(
                  <span style={{fontSize:12,fontWeight:700,color:sigColor}}>
                    {note.signal==="BUY"?"▲":"▼"} {note.signal}
                  </span>
                )}
                {note.confidence>0&&(
                  <span style={{fontSize:11,color:"#8ea2bf"}}>{note.confidence}% conf</span>
                )}
                {note.clarity&&(
                  <span style={{fontSize:11,fontWeight:700,
                    color:note.clarity==="CLEAR"?"#2ed47a":note.clarity==="STRONG"?"#e8b339":
                          note.clarity==="MODERATE"?"#e8b339":"#ff5b6c"}}>
                    {note.clarity}
                  </span>
                )}
                {note.tab&&(
                  <span style={{fontSize:11,color:"#5a728f",textTransform:"uppercase"}}>{note.tab}</span>
                )}
              </div>
              {/* Summary */}
              {note.summary&&(
                <div style={{fontSize:12,color:"#5c3d2e",lineHeight:1.4,marginBottom:3,
                  fontStyle:"italic"}}>
                  {note.summary}
                </div>
              )}
              {/* Key reasons */}
              {(note.reasons||[]).slice(0,3).map((r,i)=>(
                <div key={i} style={{display:"flex",gap:5,marginBottom:2,alignItems:"flex-start"}}>
                  <span style={{color:sigColor,fontSize:11,flexShrink:0,marginTop:1}}>▸</span>
                  <span style={{fontSize:12,color:"#8ea2bf",lineHeight:1.3}}>{r}</span>
                </div>
              ))}
              {/* Entry / TP / SL from signal */}
              {(note.entry||note.tp||note.sl)&&(
                <div style={{display:"flex",gap:8,marginTop:4,flexWrap:"wrap"}}>
                  {note.entry&&<span style={{fontSize:11,color:"#8ea2bf"}}>Entry <span style={{color:"#5c3d2e"}}>₹{note.entry}</span></span>}
                  {note.tp&&<span style={{fontSize:11,color:"#8ea2bf"}}>TP <span style={{color:"#2ed47a"}}>₹{note.tp}</span></span>}
                  {note.sl&&<span style={{fontSize:11,color:"#8ea2bf"}}>SL <span style={{color:"#ff5b6c"}}>₹{note.sl}</span></span>}
                  {note.firedAt&&<span style={{fontSize:11,color:"#5a728f"}}>{new Date(note.firedAt).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}</span>}
                </div>
              )}
              {/* Parameter votes */}
              {note.votes&&(
                <div style={{display:"flex",gap:3,marginTop:4}}>
                  {Object.entries(note.votes).map(([k,v])=>{
                    const bull=v==="BULLISH"||v==="CONFIRMING"||v==="BUYING";
                    const bear=v==="BEARISH"||v==="WEAK"||v==="SELLING";
                    const lbl={technical:"T",volume:"V",news:"N",institutional:"I",fo_data:"F"};
                    return <div key={k} title={`${k}: ${v}`}
                      style={{width:14,height:14,borderRadius:"50%",fontSize:7,fontWeight:800,
                        color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",
                        background:bull?"#2ed47a":bear?"#ff5b6c":"#23405f"}}>
                      {lbl[k]||k[0].toUpperCase()}
                    </div>;
                  })}
                </div>
              )}
              {/* Clear note button */}
              <button onClick={e=>{e.stopPropagation();
                _setPositionNotes&&_setPositionNotes(prev=>{const n={...prev};delete n[sym];return n;});
              }} style={{marginTop:4,background:"none",border:"none",color:"#5a728f",
                fontSize:11,cursor:"pointer",padding:0}}>
                ✕ clear note
              </button>
            </div>
          );
        })()}
      </div>

      {/* ── Action buttons row ── */}
      <div style={{display:"flex",gap:6,padding:"0 10px 8px"}} onClick={e=>e.stopPropagation()}>
        <button onClick={()=>_onTrade({symbol:p.tradingsymbol,exchange:p.exchange||"NSE",
            side:p.quantity>0?"SELL":"BUY",price:p.last_price,qty:Math.abs(p.quantity),
            product:p.product,instrumentType:p.instrument_type})}
          style={{flex:1,background:"rgba(220,38,38,0.12)",border:"1px solid rgba(248,81,73,0.35)",
            borderRadius:0,padding:"6px 0",color:"#ff5b6c",fontFamily:"inherit",
            fontSize:10,fontWeight:700,cursor:"pointer",minHeight:36}}>
          ⚡ Exit
        </button>
        <button onClick={e=>{e.stopPropagation();setShowTPSL(p=>!p);}}
          style={{flex:1,
            background:watcher?"rgba(0,212,170,0.12)":"rgba(232,179,57,0.07)",
            border:`1px solid ${watcher?"rgba(0,212,170,0.4)":"#23405f"}`,
            borderRadius:0,padding:"6px 0",
            color:watcher?"#e8b339":"#8ea2bf",
            fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer",minHeight:36}}>
          {_userRole==="trader"?"🔒":"🎯"} {_userRole==="trader"?(watcher?"Edit GTT":"Set GTT (Kite)"):(watcher?"Edit TP/SL":"Set TP/SL")}
        </button>
        <button onClick={()=>_onTrade({symbol:p.tradingsymbol,exchange:p.exchange||"NSE",
            side:"BUY",price:p.last_price,qty:1,product:p.product})}
          style={{background:"rgba(63,185,80,0.1)",border:"1px solid rgba(63,185,80,0.3)",
            borderRadius:0,padding:"6px 10px",color:"#2ed47a",fontFamily:"inherit",
            fontSize:10,fontWeight:700,cursor:"pointer",minHeight:36}}>
          + Add
        </button>
        {/* Payoff chart button — only for F&O */}
        {isFOPos&&(
          <button onClick={()=>setShowPayoffPos(p=>!p)}
            style={{background:showPayoffPos?"rgba(232,179,57,0.12)":"rgba(232,179,57,0.07)",
              border:`1px solid ${showPayoffPos?"#e8b339":"#23405f"}`,
              borderRadius:0,padding:"6px 10px",color:showPayoffPos?"#e8b339":"#8ea2bf",
              fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer",minHeight:36}}>
            📊
          </button>
        )}
      </div>

      {/* ── Payoff chart panel with Greeks ── */}
      {showPayoffPos&&isFOPos&&posPayoffLegs.length>0&&ltp>0&&(
        <div style={{padding:"0 10px 14px",borderTop:"1px solid rgba(232,179,57,0.12)"}}
          onClick={e=>e.stopPropagation()}>

          {/* Greeks row */}
          {(p.instrument_type==="CE"||p.instrument_type==="PE")&&(()=>{
            const S  = ltp;                                         // underlying price (approx)
            const K  = parseFloat(p.strike_price||avgP)||avgP;     // strike
            const T  = Math.max(1, (new Date(p.expiry||Date.now()+86400000)-Date.now()) / (365*86400000));
            const r  = 0.065;                                       // risk-free rate ~6.5%
            const σ  = 0.20;                                        // assumed IV 20%
            const isCall = p.instrument_type==="CE";

            // Black-Scholes Greeks (simplified)
            const d1 = (Math.log(S/K) + (r + σ*σ/2)*T) / (σ*Math.sqrt(T));
            const d2 = d1 - σ*Math.sqrt(T);
            const N  = x=>{ const a=x<0?-x:x; const k=1/(1+0.2316419*a);
              const t=k*(0.319381530+k*(-0.356563782+k*(1.781477937+k*(-1.821255978+k*1.330274429))));
              const n=(1/Math.sqrt(2*Math.PI))*Math.exp(-a*a/2);
              const r2=1-n*t; return x>=0?r2:1-r2; };
            const Npdf= x=>(1/Math.sqrt(2*Math.PI))*Math.exp(-x*x/2);

            const delta  = isCall ? N(d1) : N(d1)-1;
            const gamma  = Npdf(d1)/(S*σ*Math.sqrt(T));
            const theta  = (-(S*Npdf(d1)*σ)/(2*Math.sqrt(T)) - r*K*Math.exp(-r*T)*(isCall?N(d2):N(-d2))) / 365;
            const vega   = S*Npdf(d1)*Math.sqrt(T)/100;

            // Intrinsic & time value
            const intrinsic = isCall ? Math.max(0,S-K) : Math.max(0,K-S);
            const timeVal   = Math.max(0, avgP - intrinsic);
            const moneyness = Math.abs(S-K)/K*100;
            const mLabel    = moneyness<0.5?"ATM":isCall?(S>K?"ITM":"OTM"):(S<K?"ITM":"OTM");

            return (
              <div style={{marginTop:10,marginBottom:8}}>
                {/* Moneyness badge */}
                <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:8,flexWrap:"wrap"}}>
                  <span style={{fontSize:10,fontWeight:700,color:"#e8b339",letterSpacing:1}}>
                    📐 OPTION GREEKS
                  </span>
                  <span style={{fontSize:10,padding:"1px 7px",borderRadius:0,fontWeight:700,
                    background:mLabel==="ATM"?"rgba(161,98,7,0.15)":mLabel==="ITM"?"rgba(22,163,74,0.12)":"rgba(220,38,38,0.1)",
                    color:mLabel==="ATM"?"#49c2e4":mLabel==="ITM"?"#2ed47a":"#ff5b6c"}}>
                    {mLabel}
                  </span>
                  <span style={{fontSize:10,color:"#8ea2bf"}}>
                    Strike ₹{K.toLocaleString("en-IN")} · LTP ₹{ltp.toFixed(2)}
                  </span>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:6,marginBottom:8}}>
                  {[
                    ["Δ Delta",   delta.toFixed(3),  delta>0?"#2ed47a":"#ff5b6c",
                     "Price change per ₹1 move in underlying"],
                    ["Γ Gamma",   gamma.toFixed(4),  "#e8b339",
                     "Rate of delta change"],
                    ["Θ Theta/d", `₹${Math.abs(theta*parseFloat(p.multiplier||50)).toFixed(0)}`,
                     "#ff5b6c","Daily time decay on full position"],
                    ["ν Vega",    vega.toFixed(3),   "#6366f1",
                     "P&L per 1% IV change"],
                  ].map(([label,value,color,tip])=>(
                    <div key={label} title={tip}
                      style={{background:"#0b1624",border:"1px solid #d4c4a8",
                        borderRadius:0,padding:"5px 8px",textAlign:"center",cursor:"help"}}>
                      <div style={{fontSize:9,color:"#8ea2bf",marginBottom:2}}>{label}</div>
                      <div style={{fontSize:12,fontWeight:700,color}}>{value}</div>
                    </div>
                  ))}
                </div>
                {/* Intrinsic vs Time value bar */}
                <div style={{marginBottom:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:9,color:"#8ea2bf",marginBottom:3}}>
                    <span>Intrinsic ₹{intrinsic.toFixed(2)}</span>
                    <span>Time Value ₹{timeVal.toFixed(2)}</span>
                    <span>Premium ₹{avgP.toFixed(2)}</span>
                  </div>
                  {avgP>0&&(
                    <div style={{height:6,background:"#e5e0d8",borderRadius:0,overflow:"hidden",display:"flex"}}>
                      <div style={{width:`${Math.min(100,intrinsic/avgP*100)}%`,
                        background:"#2ed47a",transition:"width 0.5s"}}/>
                      <div style={{flex:1,background:"#e8b339",opacity:0.4}}/>
                    </div>
                  )}
                  <div style={{display:"flex",gap:6,marginTop:2,fontSize:9,color:"#8ea2bf"}}>
                    <span style={{color:"#2ed47a"}}>■ Intrinsic</span>
                    <span style={{color:"#e8b339"}}>■ Time Value</span>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Payoff chart */}
          <div style={{fontSize:10,fontWeight:700,color:"#e8b339",letterSpacing:1,marginBottom:6}}>
            📊 PAYOFF AT EXPIRY
          </div>
          <PayoffChart legs={posPayoffLegs} spotPrice={ltp} currency="₹" compact={false}/>
          <div style={{fontSize:9,color:"#5a728f",marginTop:4,lineHeight:1.5}}>
            Chart shows theoretical P&L at expiry ·
            {p.instrument_type!=="FUT"&&` Strike ₹${parseFloat(p.strike_price||avgP||0).toFixed(0)} ·`}
            {` Avg ₹${avgP.toFixed(2)} ·`}
            {` ${p.quantity>0?"LONG":"SHORT"} ${p.instrument_type}`}
            {p.multiplier>1&&` · Lot ${p.multiplier} × ${Math.abs(parseInt(p.quantity)||1)} = ${Math.abs(parseInt(p.quantity)||1)*parseFloat(p.multiplier||1)} units`}
          </div>
        </div>
      )}

      {/* ── TP/SL inline panel ── */}
      {showTPSL&&(
        <div style={{padding:"10px 12px 12px",borderTop:"1px solid #1a2c44",
          background:"rgba(0,0,0,0.2)"}} onClick={e=>e.stopPropagation()}>

          {/* Quick pct buttons — two rows: equity and option */}
          <div style={{marginBottom:8}}>
            <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:4,alignItems:"center"}}>
              <span style={{fontSize:10,color:"#8ea2bf",minWidth:40}}>Equity:</span>
              {[0.5,1,1.5,2,3].map(pct=>(
                <button key={pct} onClick={()=>{
                  setTpPrice(pctToPrice(pct, side==="BUY"?"up":"down"));
                  setSlPrice(pctToPrice(pct/2, side==="BUY"?"down":"up"));
                  setTpEnabled(true); setSlEnabled(true);
                }} style={{background:"rgba(232,179,57,0.07)",border:"1px solid #23405f",
                  borderRadius:0,padding:"2px 8px",color:"#8ea2bf",fontFamily:"inherit",
                  fontSize:11,cursor:"pointer"}}>{pct}%</button>
              ))}
            </div>
            {/* Option-specific pct buttons */}
            {isFOPos&&(
              <div style={{display:"flex",gap:4,flexWrap:"wrap",alignItems:"center"}}>
                <span style={{fontSize:10,color:"#49c2e4",minWidth:40}}>Option:</span>
                {[10,20,30,50,80].map(pct=>(
                  <button key={pct} onClick={()=>{
                    setTpPrice(pctToPrice(pct, side==="BUY"?"up":"down"));
                    setSlPrice(pctToPrice(pct/2, side==="BUY"?"down":"up"));
                    setTpEnabled(true); setSlEnabled(true);
                  }} style={{background:"rgba(161,98,7,0.1)",border:"1px solid rgba(161,98,7,0.4)",
                    borderRadius:0,padding:"2px 8px",color:"#49c2e4",fontFamily:"inherit",
                    fontSize:11,cursor:"pointer",fontWeight:700}}>{pct}%</button>
                ))}
              </div>
            )}
            {/* Demand/Supply zone TP/SL */}
            {dsZones&&(dsZones.demand?.length>0||dsZones.supply?.length>0)&&(
              <div style={{marginTop:6,padding:"6px 8px",background:"rgba(139,92,246,0.08)",
                border:"1px solid rgba(139,92,246,0.25)",borderRadius:0}}>
                <div style={{fontSize:10,color:"#a78bfa",fontWeight:700,marginBottom:4}}>
                  🎯 Demand / Supply Zone TP/SL
                </div>
                <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                  {/* Nearest demand zone → use as SL for BUY or TP for SELL */}
                  {dsZones.demand?.slice(0,2).map((z,i)=>(
                    <button key={`d${i}`} onClick={()=>{
                      if(side==="BUY"){
                        setSlPrice(String(z[0].toFixed(1)));
                        setSlEnabled(true);
                      } else {
                        setTpPrice(String(z[1].toFixed(1)));
                        setTpEnabled(true);
                      }
                    }} style={{background:"rgba(34,197,94,0.1)",border:"1px solid rgba(34,197,94,0.3)",
                      borderRadius:0,padding:"2px 8px",color:"#2ed47a",fontFamily:"inherit",fontSize:10,cursor:"pointer"}}>
                      🟢 Demand {z[0].toFixed(0)}–{z[1].toFixed(0)}
                      <span style={{color:"#5a728f",marginLeft:3,fontSize:9}}>{side==="BUY"?"→ SL":"→ TP"}</span>
                    </button>
                  ))}
                  {/* Nearest supply zone → use as TP for BUY or SL for SELL */}
                  {dsZones.supply?.slice(0,2).map((z,i)=>(
                    <button key={`s${i}`} onClick={()=>{
                      if(side==="BUY"){
                        setTpPrice(String(z[0].toFixed(1)));
                        setTpEnabled(true);
                      } else {
                        setSlPrice(String(z[1].toFixed(1)));
                        setSlEnabled(true);
                      }
                    }} style={{background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",
                      borderRadius:0,padding:"2px 8px",color:"#ff5b6c",fontFamily:"inherit",fontSize:10,cursor:"pointer"}}>
                      🔴 Supply {z[0].toFixed(0)}–{z[1].toFixed(0)}
                      <span style={{color:"#5a728f",marginLeft:3,fontSize:9}}>{side==="BUY"?"→ TP":"→ SL"}</span>
                    </button>
                  ))}
                </div>
                <div style={{fontSize:9,color:"#5a728f",marginTop:3}}>
                  Tap to auto-fill TP/SL from nearest demand/supply zone
                </div>
              </div>
            )}
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
            {/* TP */}
            <div>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                <button onClick={()=>setTpEnabled(p=>!p)}
                  style={{width:14,height:14,borderRadius:0,border:`2px solid ${tpEnabled?"#2ed47a":"#23405f"}`,
                    background:tpEnabled?"#2ed47a":"transparent",cursor:"pointer",flexShrink:0}}/>
                <span style={{fontSize:12,color:"#2ed47a",fontWeight:700}}>TARGET PRICE</span>
              </div>
              <input value={tpPrice} onChange={e=>setTpPrice(e.target.value)}
                placeholder={`e.g. ${pctToPrice(1.5,side==="BUY"?"up":"down")}`}
                style={{width:"100%",background:"#0b1624",border:`1px solid ${tpEnabled?"rgba(63,185,80,0.5)":"#1a2c44"}`,
                  borderRadius:0,padding:"5px 8px",color:tpEnabled?"#2ed47a":"#8ea2bf",
                  fontFamily:"inherit",fontSize:11,outline:"none",boxSizing:"border-box"}}/>
            </div>
            {/* SL */}
            <div>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                <button onClick={()=>setSlEnabled(p=>!p)}
                  style={{width:14,height:14,borderRadius:0,border:`2px solid ${slEnabled?"#ff5b6c":"#23405f"}`,
                    background:slEnabled?"#ff5b6c":"transparent",cursor:"pointer",flexShrink:0}}/>
                <span style={{fontSize:12,color:"#ff5b6c",fontWeight:700}}>STOP LOSS</span>
              </div>
              <input value={slPrice} onChange={e=>setSlPrice(e.target.value)}
                placeholder={`e.g. ${pctToPrice(0.75,side==="BUY"?"down":"up")}`}
                style={{width:"100%",background:"#0b1624",border:`1px solid ${slEnabled?"rgba(248,81,73,0.5)":"#1a2c44"}`,
                  borderRadius:0,padding:"5px 8px",color:slEnabled?"#ff5b6c":"#8ea2bf",
                  fontFamily:"inherit",fontSize:11,outline:"none",boxSizing:"border-box"}}/>
            </div>
          </div>

          {/* Trail SL toggle */}
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
            <button onClick={()=>setTrailEnabled(p=>!p)}
              style={{width:14,height:14,borderRadius:0,
                border:`2px solid ${trailEnabled?"#e8b339":"#23405f"}`,
                background:trailEnabled?"#e8b339":"transparent",cursor:"pointer",flexShrink:0}}/>
            <span style={{fontSize:12,color:trailEnabled?"#e8b339":"#8ea2bf",fontWeight:700}}>
              TRAIL SL
            </span>
            {trailEnabled&&(
              <div style={{display:"flex",alignItems:"center",gap:4}}>
                <input value={trailPct} onChange={e=>setTrailPct(e.target.value)}
                  style={{width:48,background:"#0b1624",border:"1px solid rgba(96,165,250,0.4)",
                    borderRadius:0,padding:"3px 6px",color:"#e8b339",fontFamily:"inherit",
                    fontSize:11,outline:"none",textAlign:"center"}}/>
                <span style={{fontSize:12,color:"#8ea2bf"}}>% trail</span>
              </div>
            )}
          </div>
          {/* Admin trail explanation */}
          {trailEnabled&&_userRole==="admin"&&(
            <div style={{fontSize:10,color:"#49c2e4",marginBottom:10,
              padding:"4px 8px",background:"rgba(73,194,228,0.06)",
              border:"1px solid rgba(73,194,228,0.2)",borderRadius:0}}>
              🔄 VPS monitors every 5s and moves SL up as price rises.<br/>
              If a GTT exists for this position, it will also be auto-updated every 30s via Kite.
            </div>
          )}

          {/* Save / Cancel buttons */}
          <div style={{display:"flex",gap:6}}>
            <button onClick={saveWatcher} disabled={saving||(!tpEnabled&&!slEnabled&&!trailEnabled)}
              style={{flex:1,background:"linear-gradient(135deg,rgba(34,197,94,0.15),rgba(232,179,57,0.12))",
                border:"1px solid rgba(0,212,170,0.4)",borderRadius:0,padding:"7px 0",
                color:"#e8b339",fontFamily:"inherit",fontSize:11,fontWeight:700,
                cursor:saving||(!tpEnabled&&!slEnabled&&!trailEnabled)?"not-allowed":"pointer",
                opacity:saving||(!tpEnabled&&!slEnabled&&!trailEnabled)?0.5:1}}>
              {saving?"Saving...":"✓ Activate TP/SL"}
            </button>
            {watcher&&(
              <button onClick={cancelWatcher}
                style={{background:"rgba(248,81,73,0.1)",border:"1px solid rgba(248,81,73,0.3)",
                  borderRadius:0,padding:"7px 12px",color:"#ff5b6c",fontFamily:"inherit",
                  fontSize:11,cursor:"pointer"}}>
                ✕ Cancel
              </button>
            )}
          </div>

          <div style={{fontSize:11,color:"#5a728f",marginTop:6,textAlign:"center"}}>
            {_userRole==="admin"
              ? "VPS watcher: auto-exits via market order. GTT in Kite also trails if set."
              : "Syncs to VPS — watches 24/7 even if browser closes"}
          </div>
        </div>
      )}
    </div>
  );
});

function PositionsTab({positions,holdings,loading,onRefresh,scanResults,activeScan,
                       trades,onTrade,watchers,setWatchers,showToast,
                       positionNotes,setPositionNotes,journal=[],journalClose=null,uid="",
                       userRole="admin", tgChat="", onJournalEntry=null}) {
  const [tab,       setTab]       = useState("fo");
  const [editWatcher, setEditWatcher] = useState(null);
  const [selected,  setSelected]  = useState(new Set());
  const [updatingSL, setUpdatingSL] = useState({}); // watcherId → loading

  // ── GTT Trail status map: symbol → {gtt_id, trail_pct, trail_count, last_trail_price} ──
  const [gttTrails, setGttTrails] = useState({}); // keyed by symbol.toUpperCase()
  const fetchGttTrails = () => {
    apiFetch("/gtt/trail/status").then(r=>{
      if(r?.trails) {
        const map = {};
        Object.entries(r.trails).forEach(([gtt_id, cfg])=>{
          const sym = (cfg.symbol||"").toUpperCase();
          if(sym) map[sym] = { ...cfg, gtt_id };
        });
        setGttTrails(map);
      }
    }).catch(()=>{});
  };
  useEffect(()=>{ fetchGttTrails(); }, []); // fetch once on mount
  // Re-fetch every 30s to pick up trail_count / last_trail_price updates
  useEffect(()=>{
    const t = setInterval(fetchGttTrails, 30000);
    return ()=>clearInterval(t);
  }, []);

  // ── AUTO-CLOSE journal entries from broker tradebook ─────────────────────
  // Trades exited at the broker (GTT / SL / TP / manual on Kite) never told the
  // app — entries stayed OPEN and had to be closed by hand before a P&L
  // statement was possible. This reconciles OPEN journal entries against the
  // day's tradebook every 5 min and closes them at the actual exit average.
  const reconcileRef = useRef(0);
  const reconcileJournal = useCallback(async(force=false)=>{
    if(!journalClose) return 0;
    const openJ = (journal||[]).filter(j=>j.status==="OPEN");
    if(openJ.length===0) return 0;
    const now = Date.now();
    if(!force && now - reconcileRef.current < 300000) return 0; // max every 5 min
    reconcileRef.current = now;
    try {
      const tb = await apiFetch("/tradebook/fetch");
      if(!tb?.ok || !Array.isArray(tb.tradebook)) return 0;
      let closed = 0;
      const todayStr = new Date().toDateString();
      openJ.forEach(j=>{
        // SAFETY: tradebook only covers TODAY's fills. Only auto-close entries
        // that were also OPENED today — otherwise an intraday round-trip on the
        // same symbol would wrongly close an older multi-day position.
        if(!j.entryTime || new Date(j.entryTime).toDateString()!==todayStr) return;
        const m = tb.tradebook.find(x=>(x.symbol||"").toUpperCase()===(j.symbol||"").toUpperCase());
        if(!m) return;
        // Broker shows position fully closed → close journal at real exit average
        if(m.open_side==="FLAT" && m.closed_qty>0){
          const exitP = j.side==="BUY" ? m.sell_avg : m.buy_avg;
          if(exitP>0){ journalClose(j.id, exitP, "TRADEBOOK AUTO"); closed++; }
        }
      });
      if(closed>0 && showToast) showToast(`📒 ${closed} trade${closed>1?"s":""} auto-closed from tradebook`);
      return closed;
    } catch(e){ return 0; }
  },[journal, journalClose, showToast]);
  useEffect(()=>{
    reconcileJournal();
    const t = setInterval(()=>reconcileJournal(), 300000);
    return ()=>clearInterval(t);
  },[reconcileJournal]);

  // ── Trade Log: expandable TA snapshot rows + external trade form + self-journal ──
  const [expandedTA, setExpandedTA] = useState(null); // journal id of expanded row
  const [showExtTradeForm, setShowExtTradeForm] = useState(false);
  const [extTrade, setExtTrade] = useState({symbol:"",exchange:"NSE",side:"BUY",qty:1,entryPrice:0,date:"",reason:"",source:"external"});
  const [editingJournal, setEditingJournal] = useState(null); // journal id being edited (self-journal)
  const [journalNote, setJournalNote] = useState(""); // note text for self-journaling
  const [fetchingTA, setFetchingTA] = useState(false); // loading state for TA fetch

  // ── Live LTP map — polls every 5s for all open positions ──────────────────
  const [livePrices, setLivePrices] = useState({}); // symbol → {ltp, change, pct}
  const liveRef = useRef(null);
  // Keep TP/SL panel open state outside of PosCard so it survives live price re-renders
  const tpslOpenRef = useRef({}); // key → boolean

  useEffect(()=>{
    const net = (positions?.net||[]).filter(p=>p.quantity!==0);
    if(!net.length){ setLivePrices({}); return; }

    const fetchLive = async()=>{
      try {
        // Group by exchange to batch quote calls
        const byExch = {};
        net.forEach(p=>{
          const exch = p.exchange || "NSE";
          if(!byExch[exch]) byExch[exch] = [];
          byExch[exch].push(p.tradingsymbol);
        });
        const allQuotes = {};
        await Promise.all(Object.entries(byExch).map(async([exch, syms])=>{
          try {
            const symStr = syms.map(s=>`${exch}:${s}`).join(",");
            const res = await apiFetch(`/quotes?symbols=${symStr}`);
            Object.entries(res).forEach(([sym,q])=>{
              allQuotes[sym] = q;
            });
          } catch {}
        }));
        setLivePrices(allQuotes);
      } catch {}
    };

    fetchLive();
    liveRef.current = setInterval(fetchLive, 5000);
    return ()=>clearInterval(liveRef.current);
  },[positions]);

  // Trail SL suggestions — computed from live prices (trader users only)
  const trailSuggestions = useTrailingSLSuggestions({
    watchers, livePrices, userRole, onShowToast: showToast, tgChat,
  });

  // ── Auto-cleanup stale watchers: remove watchers with no matching open position ──
  useEffect(()=>{
    const isActiveW = (w) => w.status==="ACTIVE"||w.status==="active"||w.status==="WATCHING";
    const openNet = (positions?.net||[]).filter(p=>p.quantity!==0);
    if(!openNet.length && (watchers||[]).some(isActiveW)) {
      // No open positions but active watchers → mark all stale
      setWatchers(prev=>prev.map(w=>isActiveW(w) ? {...w, status:"STALE_NO_POSITION"} : w));
      return;
    }
    if(!openNet.length) return;
    const openSymbols = new Set(openNet.map(p=>(p.tradingsymbol||"").toUpperCase()));
    setWatchers(prev=>{
      let changed = false;
      const updated = prev.map(w=>{
        if(isActiveW(w) && !openSymbols.has((w.symbol||"").toUpperCase())) {
          changed = true;
          return {...w, status:"STALE_NO_POSITION"};
        }
        return w;
      });
      return changed ? updated : prev;
    });
  },[positions]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fix equity filter: MIS equity should appear in Equity tab ────────────
  const net   = (positions?.net||[]).filter(p=>p.quantity!==0);

  // ── Robust F&O detection ───────────────────────────────────────────────────
  // Kite sometimes returns empty instrument_type — use tradingsymbol pattern as fallback
  const isFO = (p) => {
    const sym   = (p.tradingsymbol||"").toUpperCase();
    const itype = (p.instrument_type||"").toUpperCase();
    const exch  = (p.exchange||"").toUpperCase();
    const prod  = (p.product||"").toUpperCase();
    // Explicit instrument_type check (most reliable)
    if(["CE","PE","FUT"].includes(itype)) return true;
    // Exchange checks (NFO/MCX/BFO/CDS are always F&O)
    if(["MCX","NFO","BFO","CDS"].includes(exch)) return true;
    // Symbol pattern: ends in CE/PE/FUT, or has expiry date pattern
    if(/CE$|PE$/.test(sym)) return true;
    if(/FUT$/.test(sym)) return true;
    if(/\d{2}[A-Z]{3}\d{2}/.test(sym)) return true; // e.g. NIFTY24DEC23600CE
    // NSE with NRML product — check if it looks like a futures symbol
    // Avoid classifying plain equity with NRML (e.g. NSE:RELIANCE with NRML) as F&O
    if(exch === "NSE" && prod === "NRML") {
      // Only FUT/options symbols have digit-heavy names or specific suffixes
      if(/\d{4,}/.test(sym)) return true;  // has 4+ digits = likely expiry/strike
      return false;  // plain NSE equity with NRML → equity tab
    }
    return false;
  };

  const foPos  = net.filter(p => isFO(p));
  const eqPos  = net.filter(p => !isFO(p));
  const allPos = [...foPos,...eqPos];

  const totalPnl = net.reduce((a,p)=>a+(p.pnl||0),0);
  const holdPnl  = (holdings||[]).reduce((a,h)=>a+(h.pnl||0),0);
  const openStratNames = [...new Set(trades.filter(t=>t.status==="OPEN").map(t=>t.strategy))];

  // ── Live total P&L from live prices ──────────────────────────────────────
  const liveTotalPnl = net.reduce((acc, p)=>{
    const liveQ = livePrices[p.tradingsymbol] || livePrices[`${p.exchange}:${p.tradingsymbol}`];
    const ltp   = parseFloat(liveQ?.ltp || liveQ?.last_price || p.last_price || 0);
    const avgP  = parseFloat(p.average_price || 0);
    const qty   = parseInt(p.quantity) || 0;           // signed qty (+long, -short)
    const mult  = parseFloat(p.multiplier || 1);       // lot size for F&O
    if(ltp > 0 && avgP > 0 && qty !== 0) {
      return acc + (ltp - avgP) * qty * mult;
    }
    return acc + (p.pnl || 0);
  }, 0);
  const hasLive = Object.keys(livePrices).length > 0;

  const getSignals=(sym)=>{
    const base=sym.replace(/\d+.*$/,"").replace(/(CE|PE|FUT)$/,"");
    // First check live scan results
    const r=scanResults.find(x=>x.symbol===base||x.symbol===sym);
    if(r && Array.isArray(r.signals) && r.signals.length>0)
      return r.signals.filter(s=>activeScan.includes(s.scan));
    // Fall back to open journal entry signals (e.g. from custom scan trades)
    const je=(journal||[]).find(e=>(e.symbol===sym||e.symbol===base)&&e.status==="OPEN");
    if(je && Array.isArray(je.signals) && je.signals.length>0) return je.signals;
    return [];
  };
  const toggleSel=(key)=>setSelected(prev=>{const n=new Set(prev);n.has(key)?n.delete(key):n.add(key);return n;});



  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%"}}>
      {/* Header */}
      <div style={{background:"#0b1624",borderBottom:"1px solid #1a2c44",
        padding:"10px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{display:"flex",gap:16}}>
          <div style={{textAlign:"center"}}>
            <div style={{fontSize:12,color:hasLive?"#e8b339":"#8ea2bf"}}>
              {hasLive?"🟢 LIVE P&L":"TODAY P&L"}
            </div>
            <div style={{fontSize:16,fontWeight:700,color:pnlColor(liveTotalPnl)}}>{fmt(liveTotalPnl)}</div>
          </div>
          <div style={{textAlign:"center"}}>
            <div style={{fontSize:12,color:"#8ea2bf"}}>HOLDINGS P&L</div>
            <div style={{fontSize:16,fontWeight:700,color:pnlColor(holdPnl)}}>{fmt(holdPnl)}</div>
          </div>
          <div style={{textAlign:"center"}}>
            <div style={{fontSize:12,color:"#e8b339"}}>TP/SL ACTIVE</div>
            <div style={{fontSize:16,fontWeight:700,color:"#e8b339"}}>
              {(watchers||[]).filter(w=>w.status==="WATCHING").length}
            </div>
          </div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={onRefresh} disabled={loading}
            style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,
              padding:"6px 14px",color:"#e8b339",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>
            {loading?"⟳":"↻"} Refresh
          </button>
        </div>
      </div>

      <div style={{display:"flex",borderBottom:"1px solid #1a2c44",background:"#0b1624",overflowX:"auto"}}>
        {[
          ["fo",       `F&O (${foPos.length})`],
          ["eq",       `Equity (${eqPos.length})`],
          ["holdings", "Holdings"],
          ["history",  "📊 History"],
        ].map(([id,label])=>(
          <button key={id} onClick={()=>{setTab(id);setSelected(new Set());}}
            style={{background:tab===id?"#0b1624":"transparent",border:"none",
              borderBottom:`2px solid ${tab===id?"#e8b339":"transparent"}`,
              padding:"9px 14px",color:tab===id?"#e7edf5":"#8ea2bf",
              fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>
            {label}
          </button>
        ))}
      </div>

      <div style={{flex:1,overflow:"auto",padding:12,WebkitOverflowScrolling:"touch"}}>

        {/* ── Trail SL suggestions banner (trader users only) ── */}
        {userRole==="trader" && trailSuggestions.length>0 && (
          <div style={{marginBottom:10,background:"rgba(245,158,11,0.08)",
            border:"1px solid rgba(245,158,11,0.35)",borderRadius:0,padding:"10px 12px"}}>
            <div style={{fontSize:11,fontWeight:700,color:"#f59e0b",marginBottom:8}}>
              📈 Trail SL Suggestions — tap to update (1 tap = manual action, SEBI compliant)
            </div>
            {trailSuggestions.map(s=>(
              <div key={s.watcherId} style={{display:"flex",alignItems:"center",gap:8,
                marginBottom:6,padding:"6px 8px",background:"rgba(0,0,0,0.2)",borderRadius:0}}>
                <div style={{flex:1}}>
                  <span style={{fontWeight:700,color:"#e7edf5",fontSize:11}}>{s.symbol}</span>
                  <span style={{color:"#8ea2bf",fontSize:10,marginLeft:6}}>
                    LTP ₹{s.ltp} · Current SL ₹{s.oldSL}
                  </span>
                  <div style={{fontSize:10,color:"#f59e0b",marginTop:1}}>
                    Suggested SL → <strong>₹{s.newSL}</strong> ({s.trailPct}% below ₹{s.ltp})
                  </div>
                </div>
                <button
                  disabled={updatingSL[s.watcherId]}
                  onClick={async()=>{
                    setUpdatingSL(p=>({...p,[s.watcherId]:true}));
                    try {
                      // Step 1: Cancel old GTT if exists
                      if(s.gttId) await apiFetch(`/gtt/${s.gttId}`,{method:"DELETE"}).catch(()=>{});
                      // Step 2: Create new GTT with updated SL (and TP if set)
                      const r = await createGTT({
                        symbol: s.symbol, exchange: s.exchange, side: s.side,
                        qty: s.qty, entryPrice: s.ltp,
                        tpPrice: s.tpEnabled ? s.tpPrice : null,
                        slPrice: s.newSL,
                        tpEnabled: s.tpEnabled, slEnabled: true,
                      });
                      // Step 3: Update watcher local state
                      setWatchers(prev=>prev.map(w=>w.id===s.watcherId
                        ? {...w, slPrice:s.newSL, highWaterMark:s.ltp,
                           gttId: r?.gtt_id||w.gttId}
                        : w
                      ));
                      showToast(`✓ ${s.symbol} SL updated to ₹${s.newSL}`,"ok");
                    } catch(e) {
                      showToast(`Failed to update SL: ${e.message}`,"error");
                    } finally {
                      setUpdatingSL(p=>({...p,[s.watcherId]:false}));
                    }
                  }}
                  style={{background:"rgba(245,158,11,0.2)",border:"1px solid rgba(245,158,11,0.5)",
                    borderRadius:0,padding:"5px 12px",color:"#f59e0b",fontFamily:"inherit",
                    fontSize:10,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",
                    opacity:updatingSL[s.watcherId]?0.5:1}}>
                  {updatingSL[s.watcherId]?"Updating...":"🔄 Update SL"}
                </button>
              </div>
            ))}
            <div style={{fontSize:9,color:"#5a728f",marginTop:4}}>
              Each update is a single user-initiated action. No automatic orders placed. Compliant with SEBI April 2026 framework.
            </div>
          </div>
        )}

        {tab==="fo"&&(foPos.length===0
          ?<div style={{textAlign:"center",padding:60,color:"#8ea2bf",fontSize:13}}>No open F&O positions</div>
          :foPos.map((p,i)=><PosCard key={p.tradingsymbol+i} p={p} idx={i} _selected={selected} _toggleSel={toggleSel} _getSignals={getSignals} _watchers={watchers} _setWatchers={setWatchers} _livePrices={livePrices} _onTrade={onTrade} _showToast={showToast} _positionNotes={positionNotes} _setPositionNotes={setPositionNotes} _tpslOpenRef={tpslOpenRef} _userRole={userRole} _journal={journal} _gttTrails={gttTrails} _onGttTrailChange={fetchGttTrails}/>)
        )}
        {tab==="eq"&&(eqPos.length===0
          ?<div style={{textAlign:"center",padding:60,color:"#8ea2bf",fontSize:13}}>
              No equity / MIS positions<br/>
              <span style={{fontSize:11,color:"#5a728f"}}>MIS intraday positions appear here</span>
            </div>
          :eqPos.map((p,i)=><PosCard key={p.tradingsymbol+i} p={p} idx={foPos.length+i} _selected={selected} _toggleSel={toggleSel} _getSignals={getSignals} _watchers={watchers} _setWatchers={setWatchers} _livePrices={livePrices} _onTrade={onTrade} _showToast={showToast} _positionNotes={positionNotes} _setPositionNotes={setPositionNotes} _tpslOpenRef={tpslOpenRef} _userRole={userRole} _journal={journal} _gttTrails={gttTrails} _onGttTrailChange={fetchGttTrails}/>)
        )}
        {tab==="watchers"&&(
          <div>
            <AutoTPPanel
              watchers={watchers||[]}
              setWatchers={setWatchers}
              onEdit={(w)=>{
                // Open edit in a simple inline form — reuse existing EditWatcherModal via a local state
                setEditWatcher(w);
              }}
            />
          </div>
        )}
        {tab==="holdings"&&(
          (holdings||[]).length===0
            ?<div style={{textAlign:"center",padding:60,color:"#8ea2bf",fontSize:13}}>No holdings</div>
            :(holdings||[]).map((h,i)=>{
              const sigs=getSignals(h.tradingsymbol);
              return(
                <div key={i} style={{background:"#0f1d30",border:"1px solid #1a2c44",
                  borderLeft:`3px solid ${h.pnl>=0?"#2ed47a":"#ff5b6c"}`,borderRadius:0,
                  padding:"12px 14px",marginBottom:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:700,color:"#e7edf5"}}>{h.tradingsymbol}</div>
                      <div style={{fontSize:10,color:"#8ea2bf"}}>Qty: {h.quantity} · Avg: ₹{h.average_price}</div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:15,fontWeight:700,color:pnlColor(h.pnl)}}>{fmt(h.pnl)}</div>
                      <div style={{fontSize:10,color:"#8ea2bf"}}>LTP: ₹{h.last_price}</div>
                    </div>
                  </div>
                  {sigs.map((s,j)=><Badge key={j} type={s.type}>{s.type==="BUY"?"▲":"▼"} {s.label}</Badge>)}
                </div>
              );
            })
        )}
      </div>

      {tab==="history"&&(()=>{
          const srcColor = s=>({
            "ai_auto":"#49c2e4","scanner_auto":"#e8b339","fo_auto":"#6b7280",
            "mcx_auto":"#c87820","manual":"#2ed47a","delta":"#6366f1","external":"#e8b339",
          }[s]||"#8ea2bf");
          const srcLabel = s=>({
            "ai_auto":"🤖 AI","scanner_auto":"📡 Scanner","fo_auto":"🎯 F&O",
            "mcx_auto":"🏭 MCX","manual":"✏️ Manual","delta":"Δ Delta","external":"📋 External",
          }[s]||s||"—");
          const pnlC = p=>p>0?"#2ed47a":p<0?"#ff5b6c":"#8ea2bf";
          const fmtP = p=>(p>=0?"+":"")+`₹${Math.abs(p).toLocaleString("en-IN",{maximumFractionDigits:0})}`;

          const closedJ = (journal||[]).filter(j=>j.status==="CLOSED" && j.exitPrice>0);
          const openJ   = (journal||[]).filter(j=>j.status==="OPEN");
          const totalPnlJ = closedJ.reduce((a,j)=>a+(j.pnl||0),0);
          const wins      = closedJ.filter(j=>(j.pnl||0)>0).length;
          const winRate   = closedJ.length>0 ? Math.round(wins/closedJ.length*100) : 0;

          return (
            <div style={{flex:1,overflow:"auto"}}>
              <div style={{display:"flex",gap:16,padding:"10px 14px",
                background:"#0b1624",borderBottom:"1px solid #d4c4a8",flexWrap:"wrap"}}>
                {[
                  ["TOTAL P&L", fmtP(totalPnlJ), pnlC(totalPnlJ)],
                  ["CLOSED", closedJ.length, "#e7edf5"],
                  ["WIN RATE", winRate+"%", winRate>=50?"#2ed47a":"#ff5b6c"],
                  ["OPEN", openJ.length, "#e8b339"],
                ].map(([l,v,c])=>(
                  <div key={l} style={{textAlign:"center"}}>
                    <div style={{fontSize:10,color:"#8ea2bf"}}>{l}</div>
                    <div style={{fontSize:14,fontWeight:700,color:c}}>{v}</div>
                  </div>
                ))}
                {journal.length===0&&(
                  <div style={{fontSize:11,color:"#5a728f",alignSelf:"center",fontStyle:"italic"}}>
                    No journal data yet — trades placed through the app appear here.
                  </div>
                )}
              </div>

              {openJ.length>0&&(
                <div style={{padding:"8px 12px",background:"rgba(232,179,57,0.04)",borderBottom:"1px solid #ede5d8"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                    <span style={{fontSize:11,color:"#e8b339",fontWeight:700}}>
                      ⏳ OPEN — closed outside app? Enter exit price:
                    </span>
                    <button onClick={()=>{ reconcileJournal(true); }}
                      title="Match open entries against today's Kite tradebook and auto-close exited trades"
                      style={{background:"rgba(73,194,228,0.08)",border:"1px solid rgba(73,194,228,0.35)",
                        borderRadius:0,padding:"2px 8px",color:"#49c2e4",
                        fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                      ⟳ Sync Tradebook
                    </button>
                  </div>
                  {openJ.map((j,i)=>(
                    <div key={j.id||i} style={{display:"flex",alignItems:"center",gap:8,
                      padding:"6px 0",borderBottom:"1px solid #f5f0e8",flexWrap:"wrap"}}>
                      <span style={{fontWeight:700,color:"#e7edf5",minWidth:80}}>{j.symbol}</span>
                      <span style={{color:j.side==="BUY"?"#2ed47a":"#ff5b6c",fontWeight:700}}>{j.side}</span>
                      <span style={{color:"#8ea2bf"}}>×{j.qty} @ ₹{parseFloat(j.entryPrice||0).toFixed(2)}</span>
                      {/* Show signal at entry — with underlying stock name for F/O */}
                      {(j.signals||j.tradeReason)&&(
                        <span style={{fontSize:9,color:j.side==="BUY"?"#2ed47a":"#ff5b6c",maxWidth:200,
                          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}
                          title={j.tradeReason||j.signals}>
                          {j.underlying && j.underlying !== j.symbol
                            ? `${j.underlying}: ${j.tradeReason||j.signals}`
                            : (j.tradeReason||j.signals)}
                        </span>
                      )}
                      {j.taSnapshot?.rsi&&(
                        <span style={{fontSize:8,color:"#49c2e4"}}>RSI {j.taSnapshot.rsi.toFixed(0)}</span>
                      )}
                      <span style={{fontSize:10,padding:"1px 5px",borderRadius:0,
                        background:`${srcColor(j.source==="external"?"manual":j.source)}18`,color:srcColor(j.source==="external"?"manual":j.source)}}>
                        {j.source==="external"?"📋 External":srcLabel(j.source)}
                      </span>
                      <button onClick={()=>{
                        const ep = prompt(`Close ${j.symbol}\nEntry: ₹${j.entryPrice}\nEnter exit price:`);
                        if(!ep||isNaN(parseFloat(ep))) return;
                        if(journalClose) journalClose(j.id, parseFloat(ep), "MANUAL");
                      }}
                        style={{background:"rgba(220,38,38,0.1)",border:"1px solid rgba(220,38,38,0.3)",
                          borderRadius:0,padding:"2px 7px",color:"#ff5b6c",
                          fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                        ✕ Close
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Log External Trade button (admin + trader) ─────────────────── */}
              {userRole!=="viewer"&&(
                <div style={{padding:"6px 12px",display:"flex",gap:8,alignItems:"center",borderBottom:"1px solid #ede5d8"}}>
                  <button onClick={()=>setShowExtTradeForm(p=>!p)}
                    style={{background:showExtTradeForm?"rgba(73,194,228,0.15)":"rgba(73,194,228,0.08)",
                      border:"1px solid rgba(73,194,228,0.3)",borderRadius:0,padding:"4px 10px",
                      color:"#49c2e4",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                    {showExtTradeForm?"✕ Cancel":"+ Log External Trade"}
                  </button>
                  <span style={{fontSize:9,color:"#5a728f"}}>
                    {userRole==="admin"?"Trade taken outside app? Log it with signal/reason here."
                      :"Add personal notes to your trades"}
                  </span>
                </div>
              )}

              {/* ── External Trade Form ────────────────────────────────────────── */}
              {showExtTradeForm&&(
                <div style={{padding:"10px 12px",background:"rgba(73,194,228,0.04)",borderBottom:"1px solid #ede5d8"}}>
                  <div style={{fontSize:11,color:"#49c2e4",fontWeight:700,marginBottom:8}}>
                    Log External Trade
                  </div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"flex-end"}}>
                    <div>
                      <div style={{fontSize:9,color:"#8ea2bf",marginBottom:2}}>SYMBOL</div>
                      <input value={extTrade.symbol} onChange={e=>setExtTrade(p=>({...p,symbol:e.target.value.toUpperCase()}))}
                        placeholder="RELIANCE" style={{width:90,background:"#0b1624",border:"1px solid #d4c4a8",
                        borderRadius:0,padding:"4px 6px",color:"#e7edf5",fontSize:11,fontFamily:"inherit"}}/>
                    </div>
                    <div>
                      <div style={{fontSize:9,color:"#8ea2bf",marginBottom:2}}>EXCHANGE</div>
                      <select value={extTrade.exchange} onChange={e=>setExtTrade(p=>({...p,exchange:e.target.value}))}
                        style={{background:"#0b1624",border:"1px solid #d4c4a8",borderRadius:0,padding:"4px 6px",
                        color:"#e7edf5",fontSize:11,fontFamily:"inherit"}}>
                        <option>NSE</option><option>BSE</option><option>NFO</option><option>MCX</option>
                      </select>
                    </div>
                    <div>
                      <div style={{fontSize:9,color:"#8ea2bf",marginBottom:2}}>SIDE</div>
                      <select value={extTrade.side} onChange={e=>setExtTrade(p=>({...p,side:e.target.value}))}
                        style={{background:"#0b1624",border:"1px solid #d4c4a8",borderRadius:0,padding:"4px 6px",
                        color:extTrade.side==="BUY"?"#2ed47a":"#ff5b6c",fontSize:11,fontFamily:"inherit",fontWeight:700}}>
                        <option>BUY</option><option>SELL</option>
                      </select>
                    </div>
                    <div>
                      <div style={{fontSize:9,color:"#8ea2bf",marginBottom:2}}>QTY</div>
                      <input type="number" value={extTrade.qty} onChange={e=>setExtTrade(p=>({...p,qty:parseInt(e.target.value)||1}))}
                        style={{width:50,background:"#0b1624",border:"1px solid #d4c4a8",borderRadius:0,
                        padding:"4px 6px",color:"#e7edf5",fontSize:11,fontFamily:"inherit"}}/>
                    </div>
                    <div>
                      <div style={{fontSize:9,color:"#8ea2bf",marginBottom:2}}>ENTRY ₹</div>
                      <input type="number" value={extTrade.entryPrice||""} onChange={e=>setExtTrade(p=>({...p,entryPrice:parseFloat(e.target.value)||0}))}
                        placeholder="0.00" style={{width:70,background:"#0b1624",border:"1px solid #d4c4a8",
                        borderRadius:0,padding:"4px 6px",color:"#e7edf5",fontSize:11,fontFamily:"inherit"}}/>
                    </div>
                  </div>
                  <div style={{marginTop:8}}>
                    <div style={{fontSize:9,color:"#8ea2bf",marginBottom:2}}>SIGNAL / REASON (why did you take this trade?)</div>
                    <textarea value={extTrade.reason} onChange={e=>setExtTrade(p=>({...p,reason:e.target.value}))}
                      placeholder="e.g. RSI oversold bounce at demand zone, MACD bullish cross on 15min..."
                      rows={2} style={{width:"100%",background:"#0b1624",border:"1px solid #d4c4a8",
                      borderRadius:0,padding:"6px",color:"#e7edf5",fontSize:11,fontFamily:"inherit",resize:"vertical"}}/>
                  </div>
                  <div style={{display:"flex",gap:8,marginTop:8,alignItems:"center"}}>
                    <button disabled={!extTrade.symbol||!extTrade.entryPrice||fetchingTA}
                      onClick={async()=>{
                        if(!extTrade.symbol||!extTrade.entryPrice) return;
                        setFetchingTA(true);
                        // Fetch TA snapshot for this symbol
                        let taSnap = null;
                        try {
                          const snapRes = await apiFetch("/trade-log/ta-snapshot",{method:"POST",
                            headers:{"Content-Type":"application/json"},
                            body:JSON.stringify({symbol:extTrade.symbol, exchange:extTrade.exchange, timeframe:"15minute"})});
                          if(snapRes?.ok) taSnap = snapRes;
                        } catch(e) { console.warn("TA snapshot fetch failed:", e); }

                        // Build journal entry
                        if(onJournalEntry) {
                          onJournalEntry({
                            orderId:    null,
                            symbol:     extTrade.symbol,
                            exchange:   extTrade.exchange,
                            side:       extTrade.side,
                            qty:        extTrade.qty,
                            price:      extTrade.entryPrice,
                            product:    "MIS",
                            source:     "external",
                            tabMode:    extTrade.exchange==="MCX"?"mcx":extTrade.exchange==="NFO"?"fno":"equity",
                            signals:    extTrade.reason||"External trade",
                            signalDetails: extTrade.reason ? [{label:extTrade.reason, scan:null, type:extTrade.side}] : [],
                            taSnapshot: taSnap,
                            tradeReason: extTrade.reason,
                            notes:      `External trade · ${extTrade.reason||"no reason"}`,
                            allowNoId:  true,
                          });
                        }
                        setFetchingTA(false);
                        setShowExtTradeForm(false);
                        setExtTrade({symbol:"",exchange:"NSE",side:"BUY",qty:1,entryPrice:0,date:"",reason:"",source:"external"});
                        showToast?.("Trade logged with TA snapshot");
                      }}
                      style={{background:fetchingTA?"#1a2a3d":"#49c2e4",border:"none",borderRadius:0,padding:"5px 14px",
                        color:fetchingTA?"#8ea2bf":"#0b1624",fontSize:11,fontWeight:700,cursor:fetchingTA?"wait":"pointer",fontFamily:"inherit"}}>
                      {fetchingTA?"Fetching TA...":"Save Trade + Fetch TA"}
                    </button>
                    <span style={{fontSize:9,color:"#5a728f"}}>Auto-captures RSI, TSI, MACD, EMA, BB, Stoch at current time</span>
                  </div>
                </div>
              )}

              {closedJ.length===0 ? (
                <div style={{textAlign:"center",padding:"50px 20px",color:"#5a728f",fontSize:12}}>
                  No closed trades yet. Trades placed & closed through the app appear here.
                </div>
              ) : (
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:11,minWidth:600}}>
                    <thead>
                      <tr style={{background:"#0b1624",position:"sticky",top:0,borderBottom:"1px solid #d4c4a8"}}>
                        {["DATE","SYMBOL","SIGNAL","SIDE","QTY","ENTRY","EXIT","P&L","ROI","SOURCE","HOW","✎"].map(h=>(
                          <th key={h} style={{padding:"5px 6px",color:"#8ea2bf",fontSize:10,fontWeight:700,textAlign:"left",whiteSpace:"nowrap"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[...closedJ].sort((a,b)=>new Date(b.exitTime||b.entryTime||0)-new Date(a.exitTime||a.entryTime||0))
                        .map((j,i)=>{
                          const pnl=j.pnl||0; const roi=j.roi||0;
                          const d=new Date(j.exitTime||j.entryTime||0);
                          const ds=isNaN(d)?"":`${d.toLocaleDateString("en-IN",{day:"2-digit",month:"short"})} ${d.toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}`;
                          const ta = j.taSnapshot;
                          const isExpanded = expandedTA===j.id;
                          const hasTA = ta && (ta.rsi||ta.tsi||ta.macd);
                          const sigText = j.tradeReason || j.signals || "—";
                          return(
                            <Fragment key={j.id||i}>
                            <tr style={{borderBottom:isExpanded?"none":"1px solid #ede5d8",cursor:hasTA?"pointer":"default",
                              background:pnl>0?"rgba(22,163,74,0.04)":pnl<0?"rgba(220,38,38,0.04)":"transparent"}}
                              onClick={()=>hasTA&&setExpandedTA(isExpanded?null:j.id)}>
                              <td style={{padding:"5px 6px",color:"#5a728f",fontSize:10,whiteSpace:"nowrap"}}>{ds}</td>
                              <td style={{padding:"5px 6px",fontWeight:700,color:"#e7edf5"}}>
                                {j.symbol}
                                {j.tabMode&&<div style={{fontSize:9,color:"#5a728f"}}>{j.tabMode}</div>}
                              </td>
                              <td style={{padding:"5px 6px",maxWidth:140}}>
                                <div style={{fontSize:9,color:j.side==="BUY"?"#2ed47a":"#ff5b6c",
                                  overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                  {sigText}
                                </div>
                                {hasTA&&<div style={{fontSize:8,color:"#49c2e4",marginTop:1}}>
                                  RSI {ta.rsi?.toFixed(0)} · TSI {ta.tsi?.toFixed(1)}
                                  {isExpanded?" ▲":" ▼"}
                                </div>}
                                {j.source==="external"&&!hasTA&&<div style={{fontSize:8,color:"#e8b339"}}>external</div>}
                              </td>
                              <td style={{padding:"5px 6px",color:j.side==="BUY"?"#2ed47a":"#ff5b6c",fontWeight:700}}>{j.side}</td>
                              <td style={{padding:"5px 6px",color:"#8ea2bf"}}>
                                {j.qty}
                                {j.lotSize>1&&<span style={{fontSize:9,color:"#49c2e4",marginLeft:2}}>×{j.lotSize}</span>}
                              </td>
                              <td style={{padding:"5px 6px",color:"#8ea2bf"}}>₹{parseFloat(j.entryPrice||0).toFixed(2)}</td>
                              <td style={{padding:"5px 6px",color:"#e7edf5"}}>₹{parseFloat(j.exitPrice||0).toFixed(2)}</td>
                              <td style={{padding:"5px 6px",fontWeight:700,color:pnlC(pnl)}}>
                                {(()=>{
                                  if(j.exitPrice&&j.entryPrice) {
                                    const recalc = calcJournalPnL(j, j.exitPrice);
                                    return recalc.pnl!==null ? fmtP(recalc.pnl) : (pnl!==0?fmtP(pnl):"—");
                                  }
                                  return pnl!==0?fmtP(pnl):"—";
                                })()}
                              </td>
                              <td style={{padding:"5px 6px",color:pnlC(roi)}}>{roi!==0?(roi>=0?"+":"")+parseFloat(roi).toFixed(2)+"%":"—"}</td>
                              <td style={{padding:"5px 6px"}}>
                                <span style={{fontSize:10,padding:"1px 5px",borderRadius:0,
                                  background:`${srcColor(j.source==="external"?"manual":j.source)}18`,color:srcColor(j.source==="external"?"manual":j.source)}}>
                                  {j.source==="external"?"📋 External":srcLabel(j.source)}
                                </span>
                              </td>
                              <td style={{padding:"5px 6px",fontSize:10,color:"#8ea2bf"}}>
                                {j.exitReason==="TP"?"✅ TP":j.exitReason==="SL"?"🔴 SL":j.exitReason==="MANUAL"?"✏️":"—"}
                              </td>
                              <td style={{padding:"5px 6px"}} onClick={e=>e.stopPropagation()}>
                                {/* Self-journal: edit note/reason */}
                                <button onClick={()=>{setEditingJournal(editingJournal===j.id?null:j.id);setJournalNote(j.tradeReason||j.notes||"");}}
                                  title="Edit note" style={{background:"none",border:"none",cursor:"pointer",
                                  color:editingJournal===j.id?"#49c2e4":"#5a728f",fontSize:12,padding:0}}>
                                  ✎
                                </button>
                              </td>
                            </tr>

                            {/* ── Expanded TA Snapshot Row ──────────────────────────── */}
                            {isExpanded&&hasTA&&(
                              <tr style={{background:"rgba(73,194,228,0.04)",borderBottom:"1px solid #ede5d8"}}>
                                <td colSpan={12} style={{padding:"8px 12px"}}>
                                  <div style={{fontSize:10,color:"#49c2e4",fontWeight:700,marginBottom:4}}>
                                    TA Snapshot at Entry ({ta.timeframe||"15min"})
                                  </div>
                                  <div style={{display:"flex",gap:12,flexWrap:"wrap",fontSize:10}}>
                                    {[
                                      ["RSI",ta.rsi?.toFixed(1),ta.rsi<30?"#2ed47a":ta.rsi>70?"#ff5b6c":"#8ea2bf"],
                                      ["TSI",ta.tsi?.toFixed(2),"#e7edf5"],
                                      ["TSI Sig",ta.tsi_signal?.toFixed(2),"#8ea2bf"],
                                      ["MACD",ta.macd?.toFixed(3),"#e7edf5"],
                                      ["MACD Sig",ta.macd_signal?.toFixed(3),"#8ea2bf"],
                                      ["EMA 9",ta.ema9?.toFixed(2),"#49c2e4"],
                                      ["EMA 50",ta.ema50?.toFixed(2),"#e8b339"],
                                      ["Stoch K",ta.stoch_k?.toFixed(0),ta.stoch_k<20?"#2ed47a":ta.stoch_k>80?"#ff5b6c":"#8ea2bf"],
                                      ["Stoch D",ta.stoch_d?.toFixed(0),"#8ea2bf"],
                                      ["BB Up",ta.bb_upper?.toFixed(1),"#8ea2bf"],
                                      ["BB Mid",ta.bb_mid?.toFixed(1),"#5a728f"],
                                      ["BB Low",ta.bb_lower?.toFixed(1),"#8ea2bf"],
                                      ["Vol Ratio",ta.volRatio?.toFixed(1)+"x",ta.volRatio>1.5?"#2ed47a":"#8ea2bf"],
                                      ["Buy Vol",ta.buyVolPct?ta.buyVolPct+"%":"—","#2ed47a"],
                                      ["Sell Vol",ta.sellVolPct?ta.sellVolPct+"%":"—","#ff5b6c"],
                                      ["Trend",ta.trend||"—",ta.trend==="bullish"?"#2ed47a":"#ff5b6c"],
                                    ].filter(([,v])=>v&&v!=="undefined"&&v!=="null"&&v!=="NaN").map(([label,val,clr])=>(
                                      <div key={label} style={{textAlign:"center",minWidth:50}}>
                                        <div style={{color:"#5a728f",fontSize:8}}>{label}</div>
                                        <div style={{color:clr,fontWeight:600}}>{val}</div>
                                      </div>
                                    ))}
                                  </div>
                                  {/* Show signal details if present */}
                                  {ta.signals?.length>0&&(
                                    <div style={{marginTop:6,display:"flex",gap:4,flexWrap:"wrap"}}>
                                      {ta.signals.map((s,si)=>(
                                        <span key={si} style={{fontSize:9,padding:"1px 6px",borderRadius:0,
                                          background:s.type==="BUY"?"rgba(46,212,122,0.1)":"rgba(255,91,108,0.1)",
                                          color:s.type==="BUY"?"#2ed47a":"#ff5b6c",border:`1px solid ${s.type==="BUY"?"rgba(46,212,122,0.2)":"rgba(255,91,108,0.2)"}`}}>
                                          {s.label}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {j.tradeReason&&(
                                    <div style={{marginTop:4,fontSize:10,color:"#e8b339",fontStyle:"italic"}}>
                                      Reason: {j.tradeReason}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )}

                            {/* ── Self-journal: inline note editor ─────────────────── */}
                            {editingJournal===j.id&&(
                              <tr style={{background:"rgba(232,179,57,0.04)",borderBottom:"1px solid #ede5d8"}}>
                                <td colSpan={12} style={{padding:"6px 12px"}} onClick={e=>e.stopPropagation()}>
                                  <div style={{display:"flex",gap:6,alignItems:"center"}}>
                                    <input value={journalNote} onChange={e=>setJournalNote(e.target.value)}
                                      placeholder="Add your signal/reason/notes for this trade..."
                                      style={{flex:1,background:"#0b1624",border:"1px solid #d4c4a8",borderRadius:0,
                                        padding:"4px 8px",color:"#e7edf5",fontSize:11,fontFamily:"inherit"}}/>
                                    <button onClick={async()=>{
                                      // Save note to Firebase using top-level _db + imported doc/setDoc
                                      try {
                                        const journalRef = doc(_db, "users", uid, "journal", j.id);
                                        await setDoc(journalRef, {tradeReason:journalNote, updatedAt:new Date().toISOString()}, {merge:true});
                                        // Update local state
                                        j.tradeReason = journalNote;
                                        setEditingJournal(null);
                                        showToast?.("Note saved");
                                      } catch(e) { console.warn("Save note failed:", e); showToast?.("Save failed: "+e.message); }
                                    }}
                                      style={{background:"#e8b339",border:"none",borderRadius:0,padding:"4px 10px",
                                        color:"#0b1624",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                                      Save
                                    </button>
                                    <button onClick={()=>setEditingJournal(null)}
                                      style={{background:"none",border:"1px solid #d4c4a8",borderRadius:0,padding:"4px 8px",
                                        color:"#8ea2bf",fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>
                                      ✕
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                            </Fragment>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })()}

    {/* EditWatcher modal */}
    {editWatcher&&(
      <EditWatcherModal
        watcher={editWatcher}
        onClose={()=>setEditWatcher(null)}
        onSave={(updated)=>{
          setWatchers(p=>p.map(w=>w.id===updated.id?updated:w));
          setEditWatcher(null);
        }}
      />
    )}
    </div>
  );
}



// ══════════════════════════════════════════════════════════════════════════════
// TAB: ANALYTICS — trade performance analytics
// ══════════════════════════════════════════════════════════════════════════════
function AnalyticsTab({positions, uid, canTrade=false, userRole="viewer", journal=[], journalClose=null}) {
  const [kiteTrades,  setKiteTrades]  = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");
  const [view,        setView]        = useState("overview"); // overview|history|chart|ai
  const [dateFrom,    setDateFrom]    = useState("");
  const [dateTo,      setDateTo]      = useState("");
  const [symSearch,   setSymSearch]   = useState("");
  const [srcFilter,   setSrcFilter]   = useState("ALL"); // ALL|manual|auto
  const [aiQ,        setAiQ]         = useState("");
  const [aiMsgs,     setAiMsgs]     = useState([]);
  const [aiLoading,  setAiLoading]   = useState(false);
  const aiEndRef = useRef(null);
  useEffect(()=>aiEndRef.current?.scrollIntoView({behavior:"smooth"}),[aiMsgs]);

  const isViewer = userRole === "viewer";

  const srcColor = s=>({
    "ai_auto":"#49c2e4","scanner_auto":"#e8b339","fo_auto":"#6b7280",
    "mcx_auto":"#c87820","app_manual":"#2ed47a","manual":"#8ea2bf",
    "tp_sl_exit":"#ff5b6c","AI Auto":"#49c2e4","Manual":"#2ed47a",
  }[s]||"#8ea2bf");

  const srcLabel = s=>({
    "ai_auto":"🤖 AI Auto","scanner_auto":"📡 Scanner AT","fo_auto":"🎯 F&O Auto",
    "mcx_auto":"🏭 MCX Auto","app_manual":"✏️ Manual","manual":"🖐 Manual",
    "tp_sl_exit":"⚡ TP/SL Exit","AI Auto":"🤖 AI Auto","Manual":"✏️ Manual",
  }[s]||s||"Unknown");

  const isAutoSrc = s=>["ai_auto","scanner_auto","fo_auto","mcx_auto","MCX_AT","MCX_BG","AI Auto"].includes(s);

  const [dateRange, setDateRange] = useState("month"); // today|week|month|all

  const [dataSourceInfo, setDataSourceInfo] = useState(null); // debug info for user

  async function loadKiteTrades() {
    setLoading(true); setError(""); setDataSourceInfo(null);
    const info = { kite:0, orders:0, aiLog:0, errors:[] };

    // ── Source 1: Kite /trades/history — TODAY only (Kite API limitation) ───
    // Kite resets this at midnight. Returns fills for current session only.
    let kiteRaw = [];
    try {
      const d = await apiFetch("/trades/history");
      kiteRaw = d.trades || [];
      info.kite = kiteRaw.length;
    } catch(e) { info.errors.push(`Kite trades: ${e.message}`); }

    // ── Source 2: Kite /portfolio/orders — TODAY filled orders ───────────────
    let ordersRaw = [];
    try {
      const od = await apiFetch("/portfolio/orders");
      ordersRaw = (od.orders||od||[]).filter(o=>
        o.status==="COMPLETE" && parseInt(o.filled_quantity||o.quantity||0)>0
      );
      info.orders = ordersRaw.length;
    } catch(e) { info.errors.push(`Kite orders: ${e.message}`); }

    // ── Source 3: Backend AI trade log — HISTORICAL, persisted on VPS ────────
    // This is the only source with multi-day history.
    // Backend stores every AI auto-trade in a local DB/file on your VPS.
    let aiLogRaw = [];
    try {
      const al = await apiFetch("/ai/trade-log");
      aiLogRaw = al.trades || [];
      info.aiLog = aiLogRaw.length;
    } catch(e) { info.errors.push(`AI log: ${e.message}`); }

    // ── Dedup and merge Kite today's data ────────────────────────────────────
    const allKiteIds = new Set(kiteRaw.map(t=>t.order_id));
    const ordersNorm = ordersRaw
      .filter(o=>!allKiteIds.has(o.order_id))
      .map(o=>({
        order_id:   o.order_id,
        tradingsymbol: o.tradingsymbol||o.symbol,
        exchange:   o.exchange||"NSE",
        transaction_type: o.transaction_type||o.side,
        quantity:   o.filled_quantity||o.quantity,
        average_price: o.average_price||o.price,
        pnl:0, roi:0,
        product:    o.product||"MIS",
        tag:        o.tag||"",
        order_timestamp: o.exchange_update_timestamp||o.order_timestamp||"",
      }));

    // ── Date filter (only applies to AI log — Kite is always today) ──────────
    const now = new Date();
    const cutoffs = {
      today: new Date(now.toDateString()),
      week:  new Date(now - 7*864e5),
      month: new Date(now - 30*864e5),
      all:   new Date(0),
    };
    const cutoff = cutoffs[dateRange] || new Date(0);

    // Kite data: always today, no date filter needed
    const kiteMerged = [...kiteRaw, ...ordersNorm];

    // AI log: filter by selected date range
    const aiNorm = aiLogRaw
      .filter(t=>{
        if(!t.timestamp) return true;
        const d = new Date(t.timestamp);
        return !isNaN(d) && d >= cutoff;
      })
      .map(t=>({
        order_id:   t.order_id||t.id||`ai_${t.symbol}_${t.timestamp}`,
        tradingsymbol: t.symbol,
        exchange:   t.exchange||"NSE",
        transaction_type: t.side||"BUY",
        quantity:   t.qty||1,
        average_price: t.entry_price||t.price||0,
        pnl:        t.pnl||0,
        roi:        t.roi||0,
        product:    t.product||"NRML",
        tag:        t.tag||"RABT-AI-AUTO",
        order_timestamp: t.timestamp||"",
        status:     t.status||"OPEN",
        confidence: t.confidence,
        tab_mode:   t.tab_mode,
        isAiLog:    true,
      }));

    // Final merge — Kite today first, then AI log (dedup by order_id)
    const kiteIds = new Set(kiteMerged.map(t=>t.order_id));
    const aiExtra = aiNorm.filter(t=>!kiteIds.has(t.order_id));
    const combined = [...kiteMerged, ...aiExtra];

    setKiteTrades(combined);
    setDataSourceInfo(info);

    // Show error only if ALL sources failed — journal may still have data
    if(combined.length === 0) {
      const isMarketDay = now.getDay()!==0 && now.getDay()!==6;
      const isMarketHours = now.getHours()>=9 && now.getHours()<16;
      const hasJournal = (journal||[]).length > 0;
      if(info.errors.length > 0 && !hasJournal) {
        setError(`Could not reach backend: ${info.errors[0]}. Check VPS is running. Historical trades are in Firebase Journal below.`);
      } else if(!hasJournal) {
        if(!isMarketDay || !isMarketHours) {
          setError("Market closed — Kite resets daily. Check Firebase Journal for historical trades.");
        } else {
          setError("No trades today. Firebase Journal shows all app-placed trades across all days.");
        }
      }
    } else if(info.errors.length > 0) {
      // Partial error — some sources worked
      setError(`Partial data: ${info.errors.join(", ")}. Firebase Journal is the primary historical source.`);
    }
    setLoading(false);
  }

  useEffect(()=>{ if(!isViewer) loadKiteTrades(); },[dateRange]);

  // ── ALL trades: Kite API (today) + Firebase Journal (all time) ─────────────
  const allTrades = useMemo(()=>{
    // Normalize Kite trades (includes Kite orders + AI log trades)
    const kiteNorm = kiteTrades.map(t=>({
      order_id:  t.order_id,
      symbol:    t.tradingsymbol||t.symbol,
      exchange:  t.exchange||"NSE",
      side:      t.transaction_type||t.side||"BUY",
      qty:       parseInt(t.quantity||t.filled_quantity||1),
      entry:     parseFloat(t.average_price||t.entry_price||t.price||0),
      price:     parseFloat(t.average_price||t.price||0),
      pnl:       parseFloat(t.pnl||0),
      roi:       parseFloat(t.roi||0),
      status:    t.status||"CLOSED",
      product:   t.product||"MIS",
      source:    t.isAiLog?"ai_auto"
               : t.tag?.includes("RABT-AI")||t.tag?.includes("AI-AUTO")?"ai_auto"
               : t.tag?.includes("RABT-AUTO-FO")?"fo_auto"
               : t.tag?.includes("RABT-AUTO-MCX")?"mcx_auto"
               : t.tag?.includes("RABT-AUTO")?"scanner_auto"
               : "app_manual",
      timestamp: t.exchange_timestamp||t.order_timestamp||t.timestamp||"",
      tag:       t.tag||"",
      confidence:t.confidence||null,
      tab_mode:  t.tab_mode||null,
      isLocal:   false,
    }));

    // Merge journal (Firebase app-recorded trades) — deduplicate by orderId
    // Only show CLOSED journal entries in analytics stats (open trades have no P&L)
    const kiteOrderIds = new Set(kiteNorm.map(t=>t.order_id).filter(Boolean));
    const journalNorm = (journal||[])
      .filter(j=>!kiteOrderIds.has(j.orderId))
      .map(j=>({
        order_id:  j.id,
        symbol:    j.symbol,
        exchange:  j.exchange||"NSE",
        side:      j.side||"BUY",
        qty:       parseInt(j.qty||1),
        entry:     parseFloat(j.entryPrice||0),
        price:     parseFloat(j.exitPrice||j.entryPrice||0),
        pnl:       j.pnl!==null ? parseFloat(j.pnl) : 0,
        roi:       j.roi!==null ? parseFloat(j.roi) : 0,
        status:    j.status||"OPEN",
        product:   j.product||"MIS",
        source:    j.source||"manual",
        timestamp: j.exitTime||j.entryTime||"",
        tag:       j.tag||"",
        confidence:j.confidence||null,
        tab_mode:  j.tabMode||null,
        isJournal:  true,
        exitReason: j.exitReason||null,
        isOpen:     j.status==="OPEN",   // flag open trades
      }));

    // Put closed first, open trades at end (they have no P&L yet)
    const closedJournal = journalNorm.filter(j=>!j.isOpen);
    const openJournal   = journalNorm.filter(j=>j.isOpen);
    return [...kiteNorm, ...closedJournal, ...openJournal];
  },[kiteTrades, journal]);

  // ── Filters ──────────────────────────────────────────────────────────────
  const filtered = useMemo(()=>{
    return allTrades.filter(t=>{
      if(symSearch&&!(t.symbol||"").toUpperCase().includes(symSearch.toUpperCase())) return false;
      if(srcFilter==="manual"&&isAutoSrc(t.source)) return false;
      if(srcFilter==="auto"&&!isAutoSrc(t.source)) return false;
      if(dateFrom){ const d=new Date(t.timestamp||0); if(isNaN(d)||d<new Date(dateFrom)) return false; }
      if(dateTo){   const d=new Date(t.timestamp||0); if(isNaN(d)||d>new Date(dateTo+" 23:59:59")) return false; }
      return true;
    }).sort((a,b)=>new Date(b.timestamp||0)-new Date(a.timestamp||0));
  },[allTrades, symSearch, srcFilter, dateFrom, dateTo]);

  // Closed = trades with actual P&L, or explicitly CLOSED status
  // Open   = journal entries with status=OPEN (entry recorded, no exit yet)
  const closedTrades = filtered.filter(t=>
    t.status==="CLOSED" ||
    t.pnl!==0 ||
    // Kite SELL orders with both entry and exit price = completed round trip
    (t.side==="SELL" && t.entry>0 && t.price>0 && t.entry!==t.price)
  );
  // Open = journal entries explicitly marked OPEN (exit not captured yet)
  const openTrades = filtered.filter(t=>
    t.isOpen === true ||
    (t.status==="OPEN" && t.pnl===0)
  );

  // ── Stats (on closed trades) ─────────────────────────────────────────────
  const stats = useMemo(()=>{
    if(!closedTrades.length) return null;
    const wins   = closedTrades.filter(t=>t.pnl>0);
    const losses = closedTrades.filter(t=>t.pnl<0);
    const totalPnl = closedTrades.reduce((s,t)=>s+(t.pnl||0),0);
    const avgPnl   = totalPnl/closedTrades.length;
    const winRate  = (wins.length/closedTrades.length*100).toFixed(1);
    const avgWin   = wins.length   ? wins.reduce((s,t)=>s+t.pnl,0)/wins.length    : 0;
    const avgLoss  = losses.length ? losses.reduce((s,t)=>s+t.pnl,0)/losses.length: 0;
    const rr       = avgLoss!==0 ? Math.abs(avgWin/avgLoss).toFixed(2) : "∞";
    const best     = closedTrades.reduce((a,b)=>b.pnl>a.pnl?b:a, closedTrades[0]);
    const worst    = closedTrades.reduce((a,b)=>b.pnl<a.pnl?b:a, closedTrades[0]);
    // Expectancy = (winRate*avgWin) + ((1-winRate)*avgLoss)
    const wr = wins.length/closedTrades.length;
    const expectancy = (wr*avgWin + (1-wr)*avgLoss).toFixed(0);
    // By source
    const bySrc={};
    closedTrades.forEach(t=>{
      const s=t.source||"manual";
      if(!bySrc[s])bySrc[s]={count:0,pnl:0,wins:0};
      bySrc[s].count++; bySrc[s].pnl+=t.pnl||0; if(t.pnl>0)bySrc[s].wins++;
    });
    // Manual vs Auto split
    const manualTrades = closedTrades.filter(t=>!isAutoSrc(t.source));
    const autoTrades   = closedTrades.filter(t=>isAutoSrc(t.source));
    const manualPnl = manualTrades.reduce((s,t)=>s+t.pnl,0);
    const autoPnl   = autoTrades.reduce((s,t)=>s+t.pnl,0);
    // ROC curve data (cumulative PnL over time)
    const sorted = [...closedTrades].sort((a,b)=>new Date(a.timestamp)-new Date(b.timestamp));
    let cum=0;
    const rocData = sorted.map(t=>{ cum+=t.pnl; return {ts:t.timestamp,pnl:t.pnl,cum,sym:t.symbol}; });
    // By day
    const byDay={};
    closedTrades.forEach(t=>{
      const d=(t.timestamp||"").slice(0,10); if(!d) return;
      if(!byDay[d])byDay[d]={pnl:0,count:0,wins:0};
      byDay[d].pnl+=t.pnl||0; byDay[d].count++;
      if(t.pnl>0)byDay[d].wins++;
    });
    // By hour
    const byHour={};
    closedTrades.forEach(t=>{
      try{ const h=new Date(t.timestamp).getHours(); if(!byHour[h])byHour[h]={pnl:0,count:0}; byHour[h].pnl+=t.pnl; byHour[h].count++; }catch{}
    });
    // Max drawdown
    let peak=0, maxDD=0, runDD=0;
    rocData.forEach(d=>{ if(d.cum>peak)peak=d.cum; runDD=peak-d.cum; if(runDD>maxDD)maxDD=runDD; });
    // Win streak
    let maxStreak=0,curStreak=0,lastDir=null;
    [...closedTrades].reverse().forEach(t=>{
      const dir=t.pnl>0?"W":"L";
      if(dir===lastDir){curStreak++;}else{curStreak=1;lastDir=dir;}
      if(dir==="W")maxStreak=Math.max(maxStreak,curStreak);
    });
    return {total:closedTrades.length,wins:wins.length,losses:losses.length,
      totalPnl,avgPnl,winRate,avgWin,avgLoss,rr,best,worst,expectancy,
      bySrc,byDay,byHour,rocData,maxStreak,maxDD,
      manualTrades,autoTrades,manualPnl,autoPnl};
  },[closedTrades]);

  const pnlColor = v=>v>=0?"#2ed47a":"#ff5b6c";
  const fmt = v=>(v>=0?"+":"")+parseFloat(v||0).toLocaleString("en-IN",{maximumFractionDigits:0});
  const fmtDate = ts=>{ try{return new Date(ts).toLocaleString("en-IN",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"})}catch{return "—"} };

  // ── ROC Curve SVG renderer ───────────────────────────────────────────────
  const RocCurve = ({data})=>{
    if(!data||data.length<2) return null;
    const W=320,H=100,PAD=10;
    const minC=Math.min(...data.map(d=>d.cum),0);
    const maxC=Math.max(...data.map(d=>d.cum),0);
    const range=maxC-minC||1;
    const toX=i=>(PAD+(i/(data.length-1))*(W-PAD*2));
    const toY=v=>(H-PAD-(v-minC)/range*(H-PAD*2));
    const pts=data.map((d,i)=>`${toX(i)},${toY(d.cum)}`).join(" ");
    const zero=toY(0);
    const col=data[data.length-1]?.cum>=0?"#2ed47a":"#ff5b6c";
    return (
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:H,display:"block"}}>
        <line x1={PAD} y1={zero} x2={W-PAD} y2={zero} stroke="#1a2c44" strokeWidth={0.5} strokeDasharray="3"/>
        <polyline points={pts} fill="none" stroke={col} strokeWidth={1.5}/>
        {data.map((d,i)=>d.pnl!==0&&(
          <circle key={i} cx={toX(i)} cy={toY(d.cum)} r={2.5}
            fill={d.pnl>0?"#2ed47a":"#ff5b6c"} opacity={0.7}/>
        ))}
      </svg>
    );
  };

  // ── AI Chat wired to analytics data ─────────────────────────────────────
  const sendAiMsg = async()=>{
    if(!aiQ.trim()||aiLoading) return;
    const q=aiQ.trim(); setAiQ("");
    const newMsgs=[...aiMsgs,{role:"user",content:q}];
    setAiMsgs(newMsgs); setAiLoading(true);
    try {
      const summaryForAi = stats ? {
        totalTrades: stats.total, wins: stats.wins, losses: stats.losses,
        winRate: stats.winRate, totalPnl: stats.totalPnl.toFixed(0),
        avgPnl: stats.avgPnl.toFixed(0), rr: stats.rr,
        expectancy: stats.expectancy, maxDrawdown: stats.maxDD.toFixed(0),
        maxWinStreak: stats.maxStreak, bestTrade: stats.best?.symbol,
        bestPnl: stats.best?.pnl?.toFixed(0), worstTrade: stats.worst?.symbol,
        worstPnl: stats.worst?.pnl?.toFixed(0),
        manualPnl: stats.manualPnl.toFixed(0), autoPnl: stats.autoPnl.toFixed(0),
        manualCount: stats.manualTrades.length, autoCount: stats.autoTrades.length,
        bySource: Object.entries(stats.bySrc).map(([s,d])=>`${srcLabel(s)}: ${d.count} trades, P&L ₹${d.pnl.toFixed(0)}, Win ${d.count>0?(d.wins/d.count*100).toFixed(0):0}%`).join("; "),
        recentTrades: closedTrades.slice(0,10).map(t=>`${t.symbol} ${t.side} P&L ₹${t.pnl?.toFixed(0)}`).join(", "),
        topSymbols: Object.entries(closedTrades.reduce((a,t)=>{const s=t.symbol;if(!a[s])a[s]=0;a[s]+=t.pnl;return a;},{})).sort((a,b)=>b[1]-a[1]).slice(0,5).map(([s,p])=>`${s}:₹${p.toFixed(0)}`).join(", "),
        openPositions: openTrades.length,
      } : "No closed trades available yet.";
      const sysPrompt = `You are RABTrade's personal trading analytics advisor.
The user is looking at their LIVE trading performance data.

PORTFOLIO SUMMARY (live):
${typeof summaryForAi==="string" ? summaryForAi : JSON.stringify(summaryForAi, null, 2)}

OPEN POSITIONS: ${openTrades.length} active

INSTRUCTIONS:
- Be concise and direct — mobile app
- Give specific, actionable advice based on THEIR actual numbers
- If win rate is low, identify patterns
- If auto-trade is outperforming manual (or vice versa), call it out
- Compare manual vs auto trade performance
- Suggest timeframe, symbol, or source optimizations
- Keep responses under 150 words unless user asks for detail`;
      // Call VPS /ai/chat which proxies to Gemini
      let txt = "";
      const res = await fetch(`${API_BASE}/ai/chat`,{
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          model:"gemini-2.5-flash", max_tokens:400,
          system:sysPrompt,
          messages:newMsgs.map(m=>({role:m.role,content:m.content}))
        })
      });
      if(!res.ok) {
        const err = await res.json().catch(()=>({}));
        throw new Error(err.detail||`Server error ${res.status}`);
      }
      const data=await res.json();
      txt = data.content?.[0]?.text||data.text||"No response.";
      setAiMsgs(p=>[...p,{role:"assistant",content:txt}]);
    } catch(e){
      setAiMsgs(p=>[...p,{role:"assistant",content:`⚠ ${e.message}`}]);
    } finally { setAiLoading(false); }
  };

  if(isViewer) return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
      height:"100%",fontFamily:"inherit",gap:12,padding:20,textAlign:"center"}}>
      <div style={{fontSize:32}}>📊</div>
      <div style={{fontSize:14,fontWeight:700,color:"#e7edf5"}}>Analytics — Viewer Mode</div>
      <div style={{fontSize:12,color:"#8ea2bf",maxWidth:300,lineHeight:1.7}}>
        Viewers don't place trades. Upgrade to Trader role for full analytics.
      </div>
    </div>
  );

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",fontFamily:"inherit",background:"#07101c"}}>

      {/* ── Toolbar ── */}
      <div style={{background:"#0b1624",borderBottom:"1px solid #d4c4a8",padding:"8px 12px",
        display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",flexShrink:0}}>

        {/* View tabs */}
        <div style={{display:"flex",background:"#0f1d30",borderRadius:0,overflow:"hidden",border:"1px solid #d4c4a8"}}>
          {[["overview","📊"],["history","📋"],["chart","📈"],["ai","🤖 AI"]].map(([v,l])=>(
            <button key={v} onClick={()=>setView(v)}
              style={{background:view===v?"rgba(232,179,57,0.12)":"transparent",border:"none",
                padding:"5px 11px",color:view===v?"#e8b339":"#8ea2bf",
                fontFamily:"inherit",fontSize:11,fontWeight:view===v?700:400,cursor:"pointer",whiteSpace:"nowrap"}}>
              {l}
            </button>
          ))}
        </div>

        {/* Date range */}
        <div style={{display:"flex",background:"#0f1d30",borderRadius:0,overflow:"hidden",border:"1px solid #d4c4a8"}}>
          {[["today","Today"],["week","7d"],["month","30d"],["all","All"]].map(([v,l])=>(
            <button key={v} onClick={()=>setDateRange(v)}
              style={{background:dateRange===v?"rgba(232,179,57,0.2)":"transparent",border:"none",
                borderRight:"1px solid #d4c4a8",padding:"5px 9px",
                color:dateRange===v?"#e8b339":"#8ea2bf",
                fontFamily:"inherit",fontSize:11,fontWeight:dateRange===v?700:400,cursor:"pointer"}}>
              {l}
            </button>
          ))}
        </div>

        {/* Source filter */}
        {["ALL","manual","auto"].map(f=>(
          <button key={f} onClick={()=>setSrcFilter(f)}
            style={{background:srcFilter===f?"rgba(232,179,57,0.12)":"transparent",
              border:`1px solid ${srcFilter===f?"#e8b339":"#1a2c44"}`,borderRadius:0,
              padding:"3px 8px",color:srcFilter===f?"#e8b339":"#8ea2bf",
              fontFamily:"inherit",fontSize:11,cursor:"pointer",fontWeight:srcFilter===f?700:400}}>
            {f==="ALL"?"All":f==="manual"?"✏️ Manual":"🤖 Auto"}
          </button>
        ))}

        <input placeholder="Symbol..." value={symSearch} onChange={e=>setSymSearch(e.target.value)}
          style={{background:"#0f1d30",border:"1px solid #c8b494",borderRadius:0,padding:"4px 8px",
            color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none",width:80}}/>
        <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}
          style={{background:"#0f1d30",border:"1px solid #c8b494",borderRadius:0,padding:"4px 6px",
            color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none"}}/>
        <span style={{color:"#5a728f",fontSize:11}}>→</span>
        <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)}
          style={{background:"#0f1d30",border:"1px solid #c8b494",borderRadius:0,padding:"4px 6px",
            color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none"}}/>
        <button onClick={loadKiteTrades} disabled={loading}
          style={{background:"#0f1d30",border:"1px solid #c8b494",borderRadius:0,
            padding:"4px 10px",color:"#e8b339",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>
          {loading?"⟳":"↻"} Refresh
        </button>
        <span style={{marginLeft:"auto",fontSize:11,color:"#8ea2bf"}}>
          {closedTrades.length} closed · {openTrades.length} open
          {stats&&<b style={{color:pnlColor(stats.totalPnl),marginLeft:6}}>{fmt(stats.totalPnl)}</b>}
        </span>
      </div>

      {/* Data source info strip — always visible, shows what was fetched */}
      {dataSourceInfo&&(
        <div style={{padding:"5px 12px",background:"rgba(232,179,57,0.05)",borderBottom:"1px solid #d4c4a8",
          display:"flex",gap:12,flexWrap:"wrap",alignItems:"center",flexShrink:0}}>
          <span style={{fontSize:10,color:"#8ea2bf",fontWeight:700}}>DATA SOURCES:</span>
          <span style={{fontSize:10,color:dataSourceInfo.kite>0?"#2ed47a":"#5a728f"}}>
            📡 Kite Today: <b>{dataSourceInfo.kite}</b> trades
          </span>
          <span style={{fontSize:10,color:dataSourceInfo.orders>0?"#2ed47a":"#5a728f"}}>
            📋 Orders: <b>{dataSourceInfo.orders}</b> filled
          </span>
          <span style={{fontSize:10,color:dataSourceInfo.aiLog>0?"#2ed47a":"#5a728f"}}>
            🤖 AI Log: <b>{dataSourceInfo.aiLog}</b> trades
          </span>
          <span style={{fontSize:10,color:(journal||[]).length>0?"#2ed47a":"#5a728f"}}>
            🔥 App Journal: <b>{(journal||[]).length}</b> trades (all time)
          </span>
          {dataSourceInfo.errors.length>0&&(
            <span style={{fontSize:10,color:"#ff5b6c"}}>⚠ {dataSourceInfo.errors[0]}</span>
          )}
          <span style={{fontSize:10,color:"#5a728f",marginLeft:"auto",fontStyle:"italic"}}>
            Kite = today only · Firebase Journal = all app trades (permanent)
          </span>
        </div>
      )}

      {error&&(
        <div style={{padding:"8px 12px",background:"rgba(220,38,38,0.04)",borderBottom:"1px solid rgba(220,38,38,0.15)",flexShrink:0}}>
          <div style={{fontSize:11,color:"#ff5b6c",marginBottom:4}}>⚠ {error}</div>
          <div style={{fontSize:10,color:"#8ea2bf",lineHeight:1.7}}>
            <b>Kite API limitation:</b> Zerodha only provides <b>today's trades</b> — no multi-day history endpoint exists.<br/>
            <b style={{color:"#2ed47a"}}>✅ Firebase App Journal</b> records ALL trades placed through RABTRADE (manual, scanner AT, AI auto, MCX, Delta) — this builds up over time automatically.<br/>
            The journal count above shows how many trades have been recorded so far. Once you place trades through the app, they appear here permanently.
          </div>
        </div>
      )}

      <div style={{flex:1,overflowY:"auto",WebkitOverflowScrolling:"touch"}}>

        {/* ══ OVERVIEW ══ */}
        {view==="overview"&&(
          <div style={{padding:"12px"}}>
            {!stats ? (
              <div style={{padding:"8px"}}>
                {loading
                  ? <div style={{textAlign:"center",padding:"40px",color:"#5a728f",fontSize:12}}>⟳ Fetching from Kite + Firebase Journal...</div>
                  : <div style={{background:"#0f1d30",borderRadius:0,border:"1px solid #d4c4a8",padding:"16px"}}>
                      <div style={{fontSize:13,fontWeight:700,color:"#e7edf5",marginBottom:12}}>📊 No trade data yet — here's what's happening:</div>
                      <div style={{display:"flex",flexDirection:"column",gap:8,fontSize:11,color:"#8ea2bf",lineHeight:1.7}}>
                        <div style={{padding:"8px 10px",background:"rgba(22,163,74,0.06)",borderRadius:0,border:"1px solid rgba(22,163,74,0.2)"}}>
                          <b style={{color:"#2ed47a"}}>✅ Firebase App Journal is active</b> — records ALL trades placed through RABTRADE permanently.<br/>
                          Captures: <b>Manual orders · Scanner AT · AI Auto · MCX AT · Delta orders</b><br/>
                          Journal count: <b style={{color:"#e8b339"}}>{(journal||[]).length} trades recorded</b> so far.
                        </div>
                        <div style={{padding:"8px 10px",background:"rgba(220,38,38,0.06)",borderRadius:0,border:"1px solid rgba(220,38,38,0.15)"}}>
                          <b style={{color:"#ff5b6c"}}>🔴 Kite API:</b> Only provides today's trades — resets at midnight. No multi-day history available from Zerodha's API.
                        </div>
                        <div style={{padding:"8px 10px",background:"rgba(232,179,57,0.06)",borderRadius:0,border:"1px solid rgba(232,179,57,0.12)"}}>
                          <b style={{color:"#e8b339"}}>📋 VPS AI Log:</b> {dataSourceInfo?.aiLog||0} trades from backend.
                          {(dataSourceInfo?.aiLog||0)===0
                            ? " No AI auto-trades logged yet, or VPS is not running."
                            : " AI auto-trades from VPS shown above."}
                        </div>
                        <div style={{padding:"8px 10px",background:"rgba(107,114,128,0.06)",borderRadius:0,border:"1px solid rgba(107,114,128,0.15)",fontSize:10}}>
                          💡 <b>Start trading through the app</b> — every order placed here gets recorded to your Firebase journal permanently, across all days and devices.
                        </div>
                      </div>
                      <button onClick={loadKiteTrades}
                        style={{marginTop:12,background:"#e8b339",border:"none",borderRadius:7,
                          padding:"8px 18px",color:"#fff",fontFamily:"inherit",fontSize:12,
                          fontWeight:700,cursor:"pointer"}}>↻ Retry</button>
                    </div>
                }
              </div>
            ) : (<>

              {/* KPI grid */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))",gap:8,marginBottom:12}}>
                {[
                  ["Total P&L",     fmt(stats.totalPnl),          pnlColor(stats.totalPnl)],
                  ["Win Rate",      `${stats.winRate}%`,           stats.winRate>=50?"#2ed47a":"#ff5b6c"],
                  ["Trades",        `${stats.total}`,              "#e7edf5"],
                  ["W / L",         `${stats.wins} / ${stats.losses}`, "#8ea2bf"],
                  ["Avg P&L",       fmt(stats.avgPnl),             pnlColor(stats.avgPnl)],
                  ["Risk:Reward",   `1 : ${stats.rr}`,             "#e8b339"],
                  ["Expectancy",    fmt(stats.expectancy),         pnlColor(parseFloat(stats.expectancy))],
                  ["Max Drawdown",  fmt(-stats.maxDD),             "#ff5b6c"],
                  ["Best Trade",    fmt(stats.best?.pnl||0),       "#2ed47a"],
                  ["Worst Trade",   fmt(stats.worst?.pnl||0),      "#ff5b6c"],
                  ["Win Streak",    `${stats.maxStreak}`,          "#e8b339"],
                  ["Open",          `${openTrades.length}`,        "#8ea2bf"],
                ].map(([label,val,color])=>(
                  <div key={label} style={{background:"#0f1d30",border:"1px solid #d4c4a8",borderRadius:0,padding:"8px 10px"}}>
                    <div style={{fontSize:10,color:"#8ea2bf",letterSpacing:0.5,marginBottom:3}}>{label.toUpperCase()}</div>
                    <div style={{fontSize:13,fontWeight:700,color}}>{val}</div>
                  </div>
                ))}
              </div>

              {/* Manual vs Auto comparison */}
              <div style={{background:"#0f1d30",border:"1px solid #d4c4a8",borderRadius:0,padding:"12px",marginBottom:10}}>
                <div style={{fontSize:11,color:"#8ea2bf",letterSpacing:1,fontWeight:700,marginBottom:10}}>MANUAL vs AUTO TRADE</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                  {[
                    {label:"✏️ Manual",trades:stats.manualTrades,pnl:stats.manualPnl},
                    {label:"🤖 Auto",  trades:stats.autoTrades,  pnl:stats.autoPnl},
                  ].map(({label,trades:tl,pnl})=>{
                    const w=tl.filter(t=>t.pnl>0).length;
                    const wr=tl.length?((w/tl.length)*100).toFixed(0):0;
                    return (
                      <div key={label} style={{background:"#0b1624",borderRadius:0,padding:"10px"}}>
                        <div style={{fontSize:12,fontWeight:700,color:"#e7edf5",marginBottom:6}}>{label}</div>
                        <div style={{fontSize:18,fontWeight:800,color:pnlColor(pnl),marginBottom:4}}>{fmt(pnl)}</div>
                        <div style={{fontSize:11,color:"#8ea2bf"}}>{tl.length} trades · {wr}% win</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* By source */}
              {Object.keys(stats.bySrc).length>0&&(
                <div style={{background:"#0f1d30",border:"1px solid #d4c4a8",borderRadius:0,padding:"12px",marginBottom:10}}>
                  <div style={{fontSize:11,color:"#8ea2bf",letterSpacing:1,fontWeight:700,marginBottom:10}}>BY SOURCE</div>
                  {Object.entries(stats.bySrc).sort((a,b)=>b[1].pnl-a[1].pnl).map(([src,d])=>{
                    const maxAbs=Math.max(...Object.values(stats.bySrc).map(x=>Math.abs(x.pnl)),1);
                    const wr=d.count>0?((d.wins/d.count)*100).toFixed(0):0;
                    return (
                      <div key={src} style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                        <span style={{fontSize:11,color:srcColor(src),minWidth:110,flexShrink:0}}>{srcLabel(src)}</span>
                        <div style={{flex:1,height:6,background:"#1a2c44",borderRadius:0,overflow:"hidden"}}>
                          <div style={{height:"100%",width:`${Math.min(Math.abs(d.pnl)/maxAbs*100,100)}%`,
                            background:d.pnl>=0?"#2ed47a":"#ff5b6c",borderRadius:0}}/>
                        </div>
                        <span style={{fontSize:11,fontWeight:700,color:pnlColor(d.pnl),minWidth:60,textAlign:"right"}}>{fmt(d.pnl)}</span>
                        <span style={{fontSize:10,color:"#5a728f",minWidth:55,textAlign:"right"}}>{d.count}t · {wr}%W</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Daily P&L bars */}
              {Object.keys(stats.byDay).length>0&&(
                <div style={{background:"#0f1d30",border:"1px solid #d4c4a8",borderRadius:0,padding:"12px",marginBottom:10}}>
                  <div style={{fontSize:11,color:"#8ea2bf",letterSpacing:1,fontWeight:700,marginBottom:10}}>DAILY P&L</div>
                  <div style={{display:"flex",gap:3,alignItems:"flex-end",height:70,overflowX:"auto",paddingBottom:4}}>
                    {Object.entries(stats.byDay).sort().map(([day,d])=>{
                      const maxP=Math.max(...Object.values(stats.byDay).map(x=>Math.abs(x.pnl)),1);
                      const h=Math.max(4,Math.abs(d.pnl)/maxP*56);
                      return(
                        <div key={day} title={`${day}: ₹${d.pnl.toFixed(0)} (${d.count} trades)`}
                          style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2,flexShrink:0,minWidth:28}}>
                          <span style={{fontSize:9,color:pnlColor(d.pnl),fontWeight:700}}>{d.pnl>=0?"+":""}{Math.abs(d.pnl)>999?(d.pnl/1000).toFixed(1)+"k":d.pnl.toFixed(0)}</span>
                          <div style={{width:20,height:`${h}px`,background:d.pnl>=0?"#2ed47a":"#ff5b6c",borderRadius:0,opacity:0.85}}/>
                          <span style={{fontSize:9,color:"#5a728f"}}>{day.slice(5)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Hour of day performance */}
              {Object.keys(stats.byHour).length>1&&(
                <div style={{background:"#0f1d30",border:"1px solid #d4c4a8",borderRadius:0,padding:"12px",marginBottom:10}}>
                  <div style={{fontSize:11,color:"#8ea2bf",letterSpacing:1,fontWeight:700,marginBottom:10}}>PERFORMANCE BY HOUR</div>
                  <div style={{display:"flex",gap:3,alignItems:"flex-end",height:55,overflowX:"auto"}}>
                    {Array.from({length:24},(_,h)=>h).filter(h=>stats.byHour[h]).map(h=>{
                      const d=stats.byHour[h];
                      const maxP=Math.max(...Object.values(stats.byHour).map(x=>Math.abs(x.pnl)),1);
                      const ht=Math.max(3,Math.abs(d.pnl)/maxP*44);
                      return(
                        <div key={h} title={`${h}:00 — ₹${d.pnl.toFixed(0)} (${d.count} trades)`}
                          style={{display:"flex",flexDirection:"column",alignItems:"center",gap:1,flexShrink:0,minWidth:22}}>
                          <div style={{width:16,height:`${ht}px`,background:d.pnl>=0?"#2ed47a":"#ff5b6c",borderRadius:2,opacity:0.8}}/>
                          <span style={{fontSize:8,color:"#5a728f"}}>{h}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{fontSize:10,color:"#5a728f",marginTop:4}}>Best hour: {Object.entries(stats.byHour).sort((a,b)=>b[1].pnl-a[1].pnl)[0]?.[0]}:00</div>
                </div>
              )}
            </>)}
          </div>
        )}

        {/* ══ HISTORY TABLE ══ */}
        {view==="history"&&(
          <div>
            {/* Open trades section — with manual close button */}
            {openTrades.length>0&&(
              <div style={{padding:"10px 12px",background:"rgba(232,179,57,0.04)",borderBottom:"1px solid #d4c4a8"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                  <div style={{fontSize:11,color:"#e8b339",fontWeight:700}}>⏳ OPEN POSITIONS ({openTrades.length})</div>
                  <div style={{fontSize:10,color:"#5a728f",fontStyle:"italic"}}>Closed outside app? Use ✕ to mark closed with exit price</div>
                </div>
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                    <thead><tr style={{background:"#0b1624"}}>
                      {["SYMBOL","SIDE","QTY","ENTRY","PRODUCT","SOURCE","CLOSE"].map(h=>(
                        <th key={h} style={{padding:"5px 8px",color:"#8ea2bf",fontSize:10,fontWeight:700,textAlign:"left"}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {openTrades.map((t,i)=>(
                        <tr key={i} style={{borderBottom:"1px solid #ede5d8"}}>
                          <td style={{padding:"5px 8px",fontWeight:700,color:"#e7edf5"}}>{t.symbol}</td>
                          <td style={{padding:"5px 8px",color:t.side==="BUY"?"#2ed47a":"#ff5b6c",fontWeight:700}}>{t.side}</td>
                          <td style={{padding:"5px 8px",color:"#8ea2bf"}}>{t.qty}</td>
                          <td style={{padding:"5px 8px",color:"#8ea2bf"}}>₹{t.entry||"—"}</td>
                          <td style={{padding:"5px 8px",color:"#5a728f"}}>{t.product}</td>
                          <td style={{padding:"5px 8px"}}>
                            <span style={{fontSize:10,padding:"1px 5px",borderRadius:0,
                              background:`${srcColor(t.source)}18`,color:srcColor(t.source)}}>
                              {srcLabel(t.source)}
                            </span>
                          </td>
                          <td style={{padding:"5px 8px"}}>
                            <button onClick={()=>{
                              const exitPrice = prompt(`${t.symbol} ${t.side}\nEntry: ₹${t.entry}\n\nEnter exit price:`);
                              if(!exitPrice || isNaN(parseFloat(exitPrice))) return;
                              const ep = parseFloat(exitPrice);
                              const en = parseFloat(t.entry||0);
                              const q  = parseInt(t.qty||1);
                              const pnl = t.side==="BUY" ? (ep-en)*q : (en-ep)*q;
                              const roi = en>0 ? (pnl/(en*q)*100).toFixed(2) : 0;
                              // Close in Firebase journal
                              if(journalClose && t.order_id) {
                                journalClose(t.order_id, ep, "MANUAL");
                              }
                              alert(`✓ Closed ${t.symbol}\nP&L: ₹${pnl.toFixed(2)} (${roi}%)\nRefresh to update analytics.`);
                              loadKiteTrades();
                            }}
                              style={{background:"rgba(220,38,38,0.1)",border:"1px solid rgba(220,38,38,0.3)",
                                borderRadius:0,padding:"3px 8px",color:"#ff5b6c",
                                fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                              ✕ Close
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Closed trades */}
            {closedTrades.length===0 ? (
              <div style={{textAlign:"center",padding:"40px",color:"#5a728f",fontSize:11}}>
                {loading?"Loading...":"No closed trades match filters."}
              </div>
            ) : (
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:11,minWidth:560}}>
                  <thead>
                    <tr style={{background:"#0b1624",position:"sticky",top:0,borderBottom:"1px solid #d4c4a8"}}>
                      {["DATE","SYMBOL","SIDE","QTY","ENTRY","EXIT","P&L","ROI%","SOURCE","SIGNAL"].map(h=>(
                        <th key={h} style={{padding:"6px 8px",color:"#8ea2bf",fontSize:10,letterSpacing:0.5,fontWeight:700,textAlign:"left",whiteSpace:"nowrap"}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {closedTrades.map((t,i)=>{
                      const pnl=t.pnl||0;
                      const roi=t.roi||0;
                      const isAI = t.source==="ai_auto";
                      return(
                        <tr key={t.order_id||i} style={{borderBottom:"1px solid #ede5d8",
                          background:pnl>0?"rgba(22,163,74,0.04)":pnl<0?"rgba(220,38,38,0.04)":"transparent"}}>
                          <td style={{padding:"6px 8px",color:"#5a728f",whiteSpace:"nowrap",fontSize:10}}>{fmtDate(t.timestamp)}</td>
                          <td style={{padding:"6px 8px",fontWeight:700,color:"#e7edf5"}}>
                            {t.symbol}
                            {t.tab_mode&&<div style={{fontSize:9,color:"#5a728f"}}>{t.tab_mode}</div>}
                          </td>
                          <td style={{padding:"6px 8px",color:t.side==="BUY"?"#2ed47a":"#ff5b6c",fontWeight:700}}>{t.side}</td>
                          <td style={{padding:"6px 8px",color:"#8ea2bf"}}>{t.qty}</td>
                          <td style={{padding:"6px 8px",color:"#8ea2bf"}}>{t.entry>0?`₹${parseFloat(t.entry).toFixed(2)}`:"—"}</td>
                          <td style={{padding:"6px 8px",color:"#e7edf5"}}>{t.price>0?`₹${parseFloat(t.price).toFixed(2)}`:"—"}</td>
                          <td style={{padding:"6px 8px",fontWeight:700,color:pnlColor(pnl)}}>
                            {pnl!==0 ? fmt(pnl) : (
                              <div style={{display:"flex",alignItems:"center",gap:4}}>
                                <span style={{color:"#5a728f"}}>open</span>
                                {journalClose&&t.order_id&&(
                                  <button onClick={()=>{
                                    const ep = prompt(`Mark ${t.symbol} as closed\nEntry: ₹${t.entry}\n\nEnter exit price:`);
                                    if(!ep||isNaN(parseFloat(ep))) return;
                                    const exitP = parseFloat(ep);
                                    const enP   = parseFloat(t.entry||0);
                                    const q     = parseInt(t.qty||1);
                                    const pnlCalc = t.side==="BUY"?(exitP-enP)*q:(enP-exitP)*q;
                                    journalClose(t.order_id, exitP, "MANUAL");
                                    alert(`✓ Closed\nP&L: ₹${pnlCalc.toFixed(2)}\nRefresh to update.`);
                                    loadKiteTrades();
                                  }}
                                    style={{background:"none",border:"1px solid #d4c4a8",borderRadius:0,
                                      padding:"1px 5px",fontSize:9,color:"#8ea2bf",cursor:"pointer",
                                      fontFamily:"inherit"}}>
                                    ✕
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                          <td style={{padding:"6px 8px",color:pnlColor(roi)}}>{roi!==0?(roi>=0?"+":"")+parseFloat(roi).toFixed(2)+"%":"—"}</td>
                          <td style={{padding:"6px 8px"}}>
                            <div style={{display:"flex",flexDirection:"column",gap:2}}>
                              <span style={{fontSize:10,padding:"1px 5px",borderRadius:0,
                                background:`${srcColor(t.source)}18`,color:srcColor(t.source)}}>
                                {srcLabel(t.source)}
                              </span>
                              {isAI&&t.confidence&&(
                                <span style={{fontSize:9,color:"#49c2e4"}}>{t.confidence}% conf</span>
                              )}
                            </div>
                          </td>
                          {/* ── SIGNAL column ── */}
                          <td style={{padding:"6px 8px",maxWidth:200}}>
                            {t.underlying && t.underlying !== t.symbol && (
                              <div style={{fontSize:8,color:"#e8b339",fontWeight:700,marginBottom:2}}>
                                📊 {t.underlying}
                              </div>
                            )}
                            {t.signals && t.signals !== "no signal" ? (
                              <div style={{display:"flex",flexWrap:"wrap",gap:2}}>
                                {(t.signalDetails||[{label:t.signals}]).map((s,si)=>{
                                  const scanColors = {1:"#49c2e4",2:"#a78bfa",3:"#34d399",4:"#f59e0b",5:"#fb923c",8:"#f472b6"};
                                  const clr = scanColors[s.scan] || (s.type==="BUY"?"#2ed47a":s.type==="SELL"?"#ff5b6c":"#5a728f");
                                  return(
                                    <span key={si} style={{fontSize:9,padding:"1px 4px",borderRadius:0,
                                      background:`${clr}18`,color:clr,whiteSpace:"nowrap",
                                      border:`1px solid ${clr}30`}}>
                                      {s.label}
                                    </span>
                                  );
                                })}
                              </div>
                            ) : (
                              <span style={{fontSize:9,color:"#475569",fontStyle:"italic"}}>no signal</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ══ CHART — ROC curve + cumulative ══ */}
        {view==="chart"&&(
          <div style={{padding:"12px"}}>
            {!stats||stats.rocData.length<2 ? (
              <div style={{textAlign:"center",padding:"40px",color:"#5a728f",fontSize:11}}>Not enough closed trades to draw chart.</div>
            ) : (<>
              {/* Cumulative P&L curve */}
              <div style={{background:"#0f1d30",border:"1px solid #d4c4a8",borderRadius:0,padding:"12px",marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <div style={{fontSize:11,color:"#8ea2bf",letterSpacing:1,fontWeight:700}}>CUMULATIVE P&L (ROC CURVE)</div>
                  <div style={{fontSize:13,fontWeight:800,color:pnlColor(stats.totalPnl)}}>{fmt(stats.totalPnl)}</div>
                </div>
                <RocCurve data={stats.rocData}/>
                <div style={{display:"flex",justifyContent:"space-between",marginTop:6,fontSize:10,color:"#5a728f"}}>
                  <span>Trade #1</span>
                  <span style={{color:"#ff5b6c"}}>Max DD: {fmt(-stats.maxDD)}</span>
                  <span>Trade #{stats.total}</span>
                </div>
              </div>

              {/* Trade-by-trade P&L bars */}
              <div style={{background:"#0f1d30",border:"1px solid #d4c4a8",borderRadius:0,padding:"12px",marginBottom:10}}>
                <div style={{fontSize:11,color:"#8ea2bf",letterSpacing:1,fontWeight:700,marginBottom:10}}>TRADE-BY-TRADE P&L</div>
                <div style={{display:"flex",gap:2,alignItems:"center",height:80,overflowX:"auto"}}>
                  {stats.rocData.map((d,i)=>{
                    const maxP=Math.max(...stats.rocData.map(x=>Math.abs(x.pnl)),1);
                    const h=Math.max(2,Math.abs(d.pnl)/maxP*70);
                    return(
                      <div key={i} title={`#${i+1} ${d.sym}: ₹${d.pnl?.toFixed(0)}`}
                        style={{width:8,height:`${h}px`,background:d.pnl>=0?"#2ed47a":"#ff5b6c",
                          borderRadius:1,flexShrink:0,opacity:0.85}}/>
                    );
                  })}
                </div>
              </div>

              {/* Win/Loss distribution */}
              <div style={{background:"#0f1d30",border:"1px solid #d4c4a8",borderRadius:0,padding:"12px",marginBottom:10}}>
                <div style={{fontSize:11,color:"#8ea2bf",letterSpacing:1,fontWeight:700,marginBottom:10}}>WIN / LOSS DISTRIBUTION</div>
                <div style={{display:"flex",gap:3,height:60,alignItems:"flex-end"}}>
                  {(()=>{
                    const buckets={};
                    closedTrades.forEach(t=>{
                      const k=t.pnl>=0?Math.floor(t.pnl/500)*500:Math.ceil(t.pnl/500)*500;
                      if(!buckets[k])buckets[k]=0;
                      buckets[k]++;
                    });
                    const sorted=Object.entries(buckets).sort((a,b)=>parseFloat(a[0])-parseFloat(b[0]));
                    const maxC=Math.max(...sorted.map(([,c])=>c),1);
                    return sorted.map(([k,c])=>(
                      <div key={k} title={`₹${k} range: ${c} trades`}
                        style={{display:"flex",flexDirection:"column",alignItems:"center",gap:1,flex:1,minWidth:16}}>
                        <div style={{width:"100%",maxWidth:24,height:`${Math.max(2,c/maxC*52)}px`,
                          background:parseFloat(k)>=0?"#2ed47a":"#ff5b6c",borderRadius:2,opacity:0.8}}/>
                        <span style={{fontSize:8,color:"#5a728f"}}>{k>=0?"+":""}{k>=1000?(k/1000)+"k":k}</span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </>)}
          </div>
        )}

        {/* ══ AI CHAT wired to analytics ══ */}
        {view==="ai"&&(
          <div style={{display:"flex",flexDirection:"column",height:"100%"}}>
            {/* Context banner */}
            {stats&&(
              <div style={{padding:"8px 12px",background:"rgba(232,179,57,0.06)",borderBottom:"1px solid #d4c4a8",flexShrink:0}}>
                <div style={{fontSize:10,color:"#e8b339",fontWeight:700,marginBottom:3}}>📊 Live portfolio context loaded</div>
                <div style={{fontSize:10,color:"#8ea2bf"}}>
                  {stats.total} trades · {fmt(stats.totalPnl)} P&L · {stats.winRate}% win · Manual {fmt(stats.manualPnl)} vs Auto {fmt(stats.autoPnl)}
                </div>
              </div>
            )}
            {!stats&&(
              <div style={{padding:"8px 12px",background:"rgba(139,148,158,0.06)",borderBottom:"1px solid #d4c4a8",fontSize:10,color:"#8ea2bf",flexShrink:0}}>
                No trade data yet — AI will give general advice.
              </div>
            )}

            {/* Messages */}
            <div style={{flex:1,overflowY:"auto",padding:"10px 12px",display:"flex",flexDirection:"column",gap:8}}>
              {aiMsgs.length===0&&(
                <div style={{textAlign:"center",padding:"30px 0",color:"#5a728f",fontSize:11}}>
                  <div style={{fontSize:24,marginBottom:6}}>🤖</div>
                  Ask me about your trade performance — I have live access to your data.
                  <div style={{marginTop:12,display:"flex",flexWrap:"wrap",gap:6,justifyContent:"center"}}>
                    {["Why is my win rate low?","Compare manual vs auto","Which symbol is best?","When should I trade?","How to improve ROC?"].map(q=>(
                      <button key={q} onClick={()=>{setAiQ(q);}}
                        style={{background:"#0f1d30",border:"1px solid #d4c4a8",borderRadius:12,
                          padding:"4px 10px",color:"#8ea2bf",fontFamily:"inherit",fontSize:10,cursor:"pointer"}}>
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {aiMsgs.map((m,i)=>(
                <div key={i} style={{alignSelf:m.role==="user"?"flex-end":"flex-start",maxWidth:"88%"}}>
                  {m.role==="user"
                    ? <div style={{background:"rgba(232,179,57,0.12)",border:"1px solid rgba(232,179,57,0.3)",
                        borderRadius:"12px 12px 3px 12px",padding:"8px 12px",fontSize:12,color:"#e7edf5"}}>{m.content}</div>
                    : <div style={{background:"#0f1d30",border:"1px solid #d4c4a8",borderLeft:"3px solid #92400e",
                        borderRadius:"3px 12px 12px 12px",padding:"10px 12px",fontSize:12,color:"#e7edf5",
                        lineHeight:1.6,whiteSpace:"pre-wrap"}}>{m.content}</div>
                  }
                </div>
              ))}
              {aiLoading&&(
                <div style={{alignSelf:"flex-start",background:"#0f1d30",border:"1px solid #d4c4a8",
                  borderLeft:"3px solid #92400e",borderRadius:"3px 12px 12px 12px",
                  padding:"10px 14px",fontSize:12,color:"#e8b339"}}>⟳ Analyzing your portfolio...</div>
              )}
              <div ref={aiEndRef}/>
            </div>

            {/* Input */}
            <div style={{padding:"8px 12px",borderTop:"1px solid #d4c4a8",display:"flex",gap:8,flexShrink:0,background:"#0b1624"}}>
              <input value={aiQ} onChange={e=>setAiQ(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&sendAiMsg()}
                placeholder="Ask about your trades, win rate, best symbol..."
                style={{flex:1,background:"#0f1d30",border:"1px solid #c8b494",borderRadius:0,
                  padding:"9px 12px",color:"#e7edf5",fontFamily:"inherit",fontSize:12,outline:"none"}}/>
              <button onClick={sendAiMsg} disabled={aiLoading||!aiQ.trim()}
                style={{background:"linear-gradient(135deg,#92400e,#c8a97e)",border:"none",
                  borderRadius:0,padding:"9px 14px",color:"#fff",fontFamily:"inherit",
                  fontSize:12,fontWeight:700,cursor:aiLoading||!aiQ.trim()?"default":"pointer",
                  opacity:aiLoading||!aiQ.trim()?0.5:1}}>→</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ── Direct Kite API order placement (non-admin traders, uses browser IP) ──────
async function placeKiteOrderDirect(params, apiKey, accessToken) {
  if(!apiKey || !accessToken) throw new Error("Kite API key / access token not available. Go to Settings → Zerodha API Setup and login.");
  const body = new URLSearchParams({
    tradingsymbol:    params.tradingsymbol,
    exchange:         params.exchange,
    transaction_type: params.transaction_type,
    quantity:         String(params.quantity),
    order_type:       params.order_type,
    product:          params.product,
    price:            String(params.price || 0),
    trigger_price:    String(params.trigger_price || 0),
    tag:              params.tag || "RABT-TRADER",
    variety:          "regular",
  });
  const res = await fetch("https://api.kite.trade/orders/regular", {
    method: "POST",
    headers: {
      "Authorization": `token ${apiKey}:${accessToken}`,
      "X-Kite-Version": "3",
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: body.toString(),
  });
  const data = await res.json();
  if(data.status === "success") return { ok: true, order_id: data.data?.order_id || data.data };
  throw new Error(data.message || data.error_type || "Order failed");
}

function OrderTicket({ticket, onClose, onSuccess, onAddAutoTP, onJournalEntry, userRole="admin", userBroker="zerodha", onAddVirtual=null, authed=false, kiteClientKey="", kiteClientToken=""}) {
  const [side,setSide]           = useState(ticket.side||"BUY");
  const [orderType,setOrderType] = useState("LIMIT");
  const [price,setPrice]         = useState(String(ticket.price||""));
  const [triggerPrice,setTriggerPrice] = useState(String(ticket.triggerPrice||""));
  const [qty,setQty]             = useState(String(ticket.qty||1));
  const [exchange,setExchange]   = useState(ticket.exchange||"NSE");
  // ── Product type — auto from instrument, but user can override ──────────
  const _autoProduct = ()=>{
    const ex = (ticket.exchange||"NSE").toUpperCase();
    const it = (ticket.instrumentType||"").toUpperCase();
    if(ex==="NFO"||ex==="MCX"||ex==="CDS"||ex==="BFO") return "NRML";
    if(["FUT","CE","PE","OPT"].includes(it)) return "NRML";
    return ticket.product||"MIS";
  };
  const [product,setProduct]     = useState(_autoProduct());
  const [submitting,setSubmitting] = useState(false);
  const [error,setError]         = useState(null);
  const [zones,setZones]         = useState(null); // demand/supply zones
  const [alsoVirtual,setAlsoVirtual] = useState(false);
  const [tradeReason, setTradeReason] = useState(ticket.tradeReason || "");
  const [showReasonPopup, setShowReasonPopup] = useState(false);
  const hasSignal = (ticket.signals && ticket.signals.length > 0) || ticket.signalLabel;
  const submitRef = useRef(null);

  // Fetch zones for this symbol on mount
  useEffect(()=>{
    const exch = (ticket.exchange||"NSE").toUpperCase();
    const sym  = ticket.symbol;
    if(!sym) return;
    apiFetch("/chart/candles",{method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({symbol:sym,exchange:exch,timeframe:"1hour",days:30})
    }).then(d=>{ if(d?.zones) setZones(d.zones); }).catch(()=>{});
  },[]);

  // ── Smart defaults based on instrument type ──────────────────────────────
  const itype = (ticket.instrumentType||"").toUpperCase();
  const isOption  = ["CE","PE"].includes(itype);
  const isFutures = itype==="FUT";
  const isEquity  = !isOption && !isFutures;

  // Default TP/SL/Trail pct based on instrument
  const defaultTpPct    = isOption ? 20  : isFutures ? 1.5 : 2;
  const defaultSlPct    = isOption ? 15  : isFutures ? 1.0 : 1.5;
  const defaultTrailPct = isOption ? "5" : isFutures ? "0.5" : "1";

  // Pre-fill TP/SL from ticket or auto-calculate from defaults
  const ep = parseFloat(ticket.price||0);
  const isBuyDefault = (ticket.side||"BUY")==="BUY";
  const autoTpPrice = ep>0 ? parseFloat((ep*(isBuyDefault?1+defaultTpPct/100:1-defaultTpPct/100)).toFixed(2)) : null;
  const autoSlPrice = ep>0 ? parseFloat((ep*(isBuyDefault?1-defaultSlPct/100:1+defaultSlPct/100)).toFixed(2)) : null;

  // ── TP / SL state ────────────────────────────────────────────────────────
  const [tpEnabled,setTpEnabled] = useState(true);   // always ON by default
  const [slEnabled,setSlEnabled] = useState(true);   // always ON by default
  const [trailEnabled,setTrailEnabled] = useState(true); // trail ON by default
  const [tpInput,setTpInput]     = useState(
    ticket.tpPrice ? String(parseFloat(ticket.tpPrice).toFixed(2))
    : autoTpPrice  ? String(autoTpPrice)
    : ""
  );
  const [slInput,setSlInput]     = useState(
    ticket.slPrice ? String(parseFloat(ticket.slPrice).toFixed(2))
    : autoSlPrice  ? String(autoSlPrice)
    : ""
  );
  const [trailPct,setTrailPct]   = useState(defaultTrailPct);
  const [tpMode,setTpMode]       = useState("price");
  const [slMode,setSlMode]       = useState("price");

  const isBuy       = side==="BUY";
  const isSL        = orderType==="SL";
  const entryPrice  = parseFloat(price)||0;

  // Derive the other value from user input
  const tpPrice = useMemo(()=>{
    if(!entryPrice) return null;
    if(tpMode==="price") return parseFloat(tpInput)||null;
    const pct = parseFloat(tpInput);
    if(!pct) return null;
    return parseFloat((entryPrice*(isBuy?1+pct/100:1-pct/100)).toFixed(2));
  },[tpMode,tpInput,entryPrice,isBuy]);

  const tpPct = useMemo(()=>{
    if(!entryPrice||!tpPrice) return null;
    return Math.abs((tpPrice-entryPrice)/entryPrice*100).toFixed(2);
  },[tpPrice,entryPrice]);

  const slPrice = useMemo(()=>{
    if(!entryPrice) return null;
    if(slMode==="price") return parseFloat(slInput)||null;
    const pct = parseFloat(slInput);
    if(!pct) return null;
    return parseFloat((entryPrice*(isBuy?1-pct/100:1+pct/100)).toFixed(2));
  },[slMode,slInput,entryPrice,isBuy]);

  const slPct = useMemo(()=>{
    if(!entryPrice||!slPrice) return null;
    return Math.abs((slPrice-entryPrice)/entryPrice*100).toFixed(2);
  },[slPrice,entryPrice]);

  // ── Validate TP/SL logic ─────────────────────────────────────────────────
  const tpValid = !tpEnabled || (tpPrice&&(isBuy?tpPrice>entryPrice:tpPrice<entryPrice));
  const slValid = !slEnabled || (slPrice&&(isBuy?slPrice<entryPrice:slPrice>entryPrice));

  // ── Quick % fill helper ───────────────────────────────────────────────────
  const quickFill = (pct)=>{
    if(!entryPrice) return;
    const tp = (entryPrice*(isBuy?1+pct/100:1-pct/100)).toFixed(2);
    const sl = (entryPrice*(isBuy?1-pct/2/100:1+pct/2/100)).toFixed(2);
    setTpInput(tp); setTpMode("price"); setTpEnabled(true);
    setSlInput(sl); setSlMode("price"); setSlEnabled(true);
  };

  const submit = async()=>{
    if(!price||!qty){ setError("Price and quantity required"); return; }
    if(isSL&&!triggerPrice){ setError("Trigger price required for SL order"); return; }
    // If no signal attached and no reason given, show popup first
    if(!hasSignal && !tradeReason) {
      setShowReasonPopup(true);
      setSubmitting(false);
      return;
    }
    if(tpEnabled&&!tpValid){ setError(`TP must be ${isBuy?"above":"below"} entry price`); return; }
    if(slEnabled&&!slValid){ setError(`SL must be ${isBuy?"below":"above"} entry price`); return; }
    setSubmitting(true); setError(null);

    // ── User NOT authenticated with Kite: show login prompt or deeplink ─────
    // If user HAS a Kite session (authed=true), skip this and place via API below.
    if(!authed) {
      // If Zerodha user — prompt to login for direct order placement
      if(userBroker==="zerodha") {
        // Show connect prompt overlay
        const ov = document.createElement('div');
        ov.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.85);z-index:999999;display:flex;align-items:center;justify-content:center;font-family:"IBM Plex Sans",sans-serif';
        ov.innerHTML = `
          <div style="background:#0b1624;border:1px solid #1a2c44;width:420px;max-width:95vw;padding:28px;text-align:center;position:relative">
            <div style="font-size:32px;margin-bottom:12px">🔗</div>
            <div style="font-size:15px;font-weight:700;color:#e7edf5;margin-bottom:8px">Connect Kite for Direct Orders</div>
            <div style="font-size:12px;color:#8ea2bf;line-height:1.7;margin-bottom:20px">
              Login to your Zerodha Kite account to place orders directly from Rab Trade.<br/>
              Without Kite login, orders open as a generic Kite dashboard page.
            </div>
            <button id="_kl_login" style="width:100%;padding:14px;background:#ff6b35;border:none;color:#fff;font-size:14px;font-weight:700;cursor:pointer;font-family:inherit;margin-bottom:10px">
              🟠 LOGIN TO KITE — PLACE ORDER DIRECTLY
            </button>
            <button id="_kl_skip" style="width:100%;padding:10px;background:transparent;border:1px solid #1a2c44;color:#5a728f;font-size:12px;cursor:pointer;font-family:inherit">
              Skip — Open Kite Dashboard Instead
            </button>
            <button id="_kl_close" style="position:absolute;top:12px;right:16px;background:none;border:none;color:#5a728f;font-size:18px;cursor:pointer">✕</button>
          </div>`;
        document.body.appendChild(ov);
        ov.querySelector('#_kl_close').onclick = () => { ov.remove(); setSubmitting(false); };
        ov.onclick = (e) => { if(e.target===ov) { ov.remove(); setSubmitting(false); } };
        ov.querySelector('#_kl_login').onclick = () => {
          ov.remove();
          window.dispatchEvent(new CustomEvent("rabt-show-settings"));
          setSubmitting(false); onClose();
        };
        ov.querySelector('#_kl_skip').onclick = () => {
          ov.remove();
          openInBroker({
            symbol: ticket.symbol, exchange, side,
            qty: parseInt(qty), price: parseFloat(price),
            orderType, product,
          });
          setSubmitting(false); onClose();
        };
        return;
      }
      const prod = product; // use the user-selected product type
      openInBroker({
        symbol: ticket.symbol, exchange, side,
        qty: parseInt(qty), price: parseFloat(price),
        orderType, product: prod,
      });
      const _brokerName = BROKER_CONFIGS[userBroker]?.name||"Broker";
      const _brokerLogo = BROKER_CONFIGS[userBroker]?.logo||"🟠";

      // ── Record in journal (no orderId — broker-placed, we don't have the API ID) ──
      if(onJournalEntry) {
        const _sigs = ticket.signals || [];
        // Auto-fetch TA snapshot in background
        let _taSnap = null;
        try {
          const _snapRes = await apiFetch("/trade-log/ta-snapshot",{method:"POST",
            headers:{"Content-Type":"application/json"},
            body:JSON.stringify({symbol:ticket.symbol, exchange, timeframe:"15minute"})});
          if(_snapRes?.ok) _taSnap = _snapRes;
        } catch(e) { console.warn("TA snapshot fetch failed:", e.message); }

        onJournalEntry({
          orderId:    null,
          symbol:     ticket.symbol,
          exchange,
          side,
          qty:        parseInt(qty),
          price:      parseFloat(price),
          product:    prod,
          source:     "manual",
          tabMode:    ticket.tabMode||"equity",
          tpPrice:    tpEnabled ? tpPrice : null,
          slPrice:    slEnabled ? slPrice : null,
          tpPct:      tpEnabled ? parseFloat(tpPct||0) : null,
          slPct:      slEnabled ? parseFloat(slPct||0) : null,
          instrumentType: ticket.instrumentType||"EQ",
          lotSize:    ticket.lotSize||ticket.lot_size||1,
          strike:     ticket.strikePrice||ticket.strike||null,
          expiry:     ticket.expiry||null,
          signals:    _sigs.length ? _sigs.map(s=>s.label).join(" · ") : "",
          signalDetails: _sigs.length ? _sigs : [],
          taSnapshot: _taSnap,
          tradeReason: tradeReason || "",
          notes:      `${_brokerName} · ${orderType} @ ₹${price}`,
          allowNoId:  true,
        });
      }
      // Create GTT OCO + trail for TP/SL — Zerodha only
      if(userBroker==="zerodha" && (tpEnabled||slEnabled) && (tpPrice||slPrice)) {
        const entryP = parseFloat(price)||0;
        const _trailPct = trailEnabled ? parseFloat(trailPct||1.5) : 0;
        placeGTTWithTrail({
          symbol:ticket.symbol, exchange, side, qty:parseInt(qty),
          entryPrice:entryP,
          tpPrice:tpEnabled?tpPrice:null,
          slPrice:slEnabled?slPrice:null,
          trailPct:_trailPct,
        }).then(r=>{
          if(r?.ok) onSuccess?.(`${_brokerLogo} ${_brokerName} opened · GTT OCO created (${r.gtt_id})${_trailPct>0?" + Trail":""}`);
          else onSuccess?.(`${_brokerLogo} ${_brokerName} opened · Set TP/SL manually`);
        }).catch(()=>{ onSuccess?.(`${_brokerLogo} Order opened in ${_brokerName}`); });
      } else {
        if((tpEnabled||slEnabled) && userBroker!=="zerodha") {
          onSuccess?.(`${_brokerLogo} Order details copied · Set TP/SL manually in ${_brokerName}`);
          if(alsoVirtual && onAddVirtual) {
            onAddVirtual({symbol:ticket.symbol,exchange,side,qty:parseInt(qty),
              price:parseFloat(price)||0,product:prod,
              orderType,source:"virtual",tpPrice:tpEnabled?tpPrice:null,slPrice:slEnabled?slPrice:null,
              trailEnabled:!!trailEnabled,trailPct:trailEnabled?parseFloat(trailPct||1.5):0,
              signals:ticket.signals?ticket.signals.map(s=>s.label).join(" · "):"",createdAt:new Date().toISOString()});
          }
          setSubmitting(false);
          onClose();
          return;
        }
        const bmsg = `${_brokerLogo} Order details copied · Place in ${_brokerName}`;
        if(alsoVirtual && onAddVirtual) {
          onAddVirtual({symbol:ticket.symbol,exchange,side,qty:parseInt(qty),
            price:parseFloat(price)||0,product:getProduct(exchange,ticket.instrumentType||""),
            orderType,source:"virtual",tpPrice:tpEnabled?tpPrice:null,slPrice:slEnabled?slPrice:null,
            trailEnabled:!!trailEnabled,trailPct:trailEnabled?parseFloat(trailPct||1.5):0,
            signals:ticket.signals?ticket.signals.map(s=>s.label).join(" · "):"",createdAt:new Date().toISOString()});
          onSuccess?.(bmsg+" · 📋 Added to Virtual Tab");
        } else {
          onSuccess?.(bmsg);
        }
      }
      setSubmitting(false);
      onClose();
      return;
    }

    // ── User IS authenticated — place order (admin via VPS, trader via proxy or deeplink) ──

    const isAdminUser = userRole === "admin";
    const hasProxy = !!(sessionStorage.getItem("kite_proxy_host")||"").trim();

    // Non-admin Zerodha WITHOUT proxy → use Kite deeplink (can't route via VPS)
    if(!isAdminUser && userBroker === "zerodha" && !hasProxy) {
      openInKite({
        symbol: ticket.symbol, exchange, side,
        qty: parseInt(qty)||1,
        price: parseFloat(price)||0,
        orderType, product,
        triggerPrice: isSL ? parseFloat(triggerPrice)||0 : 0,
      });
      setSubmitting(false);
      onSuccess?.("🟠 Opened in Zerodha Kite — confirm the order there");
      onClose();
      return;
    }

    try{
      const body = {
        tradingsymbol:    ticket.symbol,
        exchange,
        transaction_type: side,
        quantity:         parseInt(qty),
        order_type:       orderType,
        price:            parseFloat(price),
        trigger_price:    isSL ? parseFloat(triggerPrice) : 0,
        product:          product, // user-selected product (MIS / NRML / CNC)
      };

      // Admin: VPS places order directly.
      // Non-admin Zerodha with proxy: VPS routes through their SOCKS5 proxy → Kite sees their IP.
      let res;
      res = await apiFetch("/order/place",{method:"POST",
        headers:{"Content-Type":"application/json"},body:JSON.stringify(body)});

      // ── Record in Firebase trade journal WITH TA snapshot ──────────────
      if(onJournalEntry && res.order_id) {
        // Auto-fetch TA snapshot in background (non-blocking)
        let taSnap = null;
        try {
          const snapRes = await apiFetch("/trade-log/ta-snapshot",{method:"POST",
            headers:{"Content-Type":"application/json"},
            body:JSON.stringify({symbol:ticket.symbol, exchange, timeframe:"15minute"})});
          if(snapRes?.ok) taSnap = snapRes;
        } catch(e) { console.warn("TA snapshot fetch failed:", e.message); }

        onJournalEntry({
          orderId:   res.order_id,
          symbol:    ticket.symbol,
          exchange,
          side,
          qty:       parseInt(qty),
          price:     parseFloat(price),
          product:   body.product,
          source:    "manual",
          tabMode:   ticket.tabMode||"equity",
          tpPrice:   tpEnabled ? tpPrice : null,
          slPrice:   slEnabled ? slPrice : null,
          tpPct:     tpEnabled ? parseFloat(tpPct||0) : null,
          slPct:     slEnabled ? parseFloat(slPct||0) : null,
          instrumentType: ticket.instrumentType||"EQ",
          lotSize:   ticket.lotSize||ticket.lot_size||1,
          strike:    ticket.strikePrice||ticket.strike||null,
          expiry:    ticket.expiry||null,
          signals:       ticket.signals?.length ? ticket.signals.map(s=>s.label).join(' · ') : (ticket.signalLabel||""),
          signalDetails: ticket.signals?.length ? ticket.signals : (ticket.signalLabel ? [{label:ticket.signalLabel,scan:null,type:side}] : []),
          underlying:    ticket.underlying||"",
          taSnapshot:    taSnap,
          tradeReason:   tradeReason || "",
          notes:         `${orderType} order @ ₹${price}${trailEnabled?` trail ${trailPct}%`:""}`,
          allowNoId:     false,
        });
      }

      // Save signal/reason to backend for trade history enrichment
      if(res.order_id) {
        apiFetch("/trade-log/signal",{method:"POST",headers:{"Content-Type":"application/json"},
          body:JSON.stringify({
            order_id:res.order_id, symbol:ticket.symbol, side,
            signals: ticket.signals?.length ? ticket.signals.map(s=>s.label).join(' · ') : (ticket.signalLabel||""),
            signal_details: ticket.signals||[],
            trade_reason: tradeReason||"",
            source: ticket.signals?.length ? "scan" : "manual",
            underlying: ticket.underlying||"",
          })}).catch(()=>{});
      }

      // TP/SL: GTT for ALL users (admin + trader) — broker-native, SEBI compliant
      if((tpEnabled||slEnabled||trailEnabled) && entryPrice>0) {
        if(userBroker !== "zerodha") {
          // Non-Zerodha brokers: no GTT support — remind user
          onSuccess?.(`✓ Order placed via ${BROKER_CONFIGS[userBroker]?.name||"Broker"}. Set TP/SL manually in your broker app.`);
          onClose();
          setSubmitting(false);
          return;
        }
        // Zerodha: place GTT OCO (TP+SL) + optional trailing SL
        const _trailPct = trailEnabled ? parseFloat(trailPct||1.5) : 0;
        placeGTTWithTrail({
          symbol:ticket.symbol, exchange, side, qty:parseInt(qty),
          entryPrice,
          tpPrice: tpEnabled ? tpPrice : null,
          slPrice: slEnabled ? slPrice : (trailEnabled
            ? parseFloat((entryPrice*(side==="BUY"?1-_trailPct/100:1+_trailPct/100)).toFixed(2))
            : null),
          trailPct: _trailPct,
        }).then(r=>{
          if(r?.ok) {
            const trailNote = _trailPct>0 ? ` + Trail ${_trailPct}%` : "";
            onSuccess?.(`✓ Entry + GTT OCO created (ID: ${r.gtt_id})${trailNote} — Kite will auto-exit`);
          } else {
            onSuccess?.("✓ Entry placed. GTT creation failed — set TP/SL manually in Kite.");
          }
        }).catch(()=>{
          onSuccess?.("✓ Entry placed. Set TP/SL as GTT in your Kite app.");
        });
        onClose();
        setSubmitting(false);
        return;
      }

      const exitNote = (tpEnabled||slEnabled||trailEnabled)
        ? ` · Watcher: ${tpEnabled?`TP ₹${tpPrice}`:""}${tpEnabled&&slEnabled?" / ":""}${slEnabled?`SL ₹${slPrice}`:""}${trailEnabled?` (Trail ${trailPct}%)`:""}` : "";
      onSuccess(`✓ Order placed! ID: ${res.order_id}${exitNote}`);
      onClose();
    }catch(e){
      const msg = e.message||"";
      const isAdminUserErr = userRole === "admin";
      if(msg.includes("API key / access token not available") || msg.includes("access token not available")){
        setError("⚠️ Kite access token missing. Go to Settings → Zerodha API Setup → Login via Kite to refresh your session.");
      } else if(msg.includes("not allowed")||msg.includes("whitelist")||msg.includes("NetworkError")||msg.includes("Failed to fetch")||msg.toLowerCase().includes("ip")){
        if(isAdminUserErr){
          setError(
            "⚠️ IP 165.22.209.25 not whitelisted on your Kite Connect app.\n" +
            "Go to Settings → Zerodha API Setup for instructions, or visit developers.kite.trade → your app → Allowed IPs."
          );
        } else {
          setError(
            "⚠️ IP not whitelisted — Kite rejected the order.\n" +
            "To place orders via VPS, go to Settings → Zerodha API Setup → set up your Static IP Proxy.\n" +
            "Or use 'Open in Zerodha Kite' to place orders manually (remove proxy config from settings)."
          );
        }
      } else if(msg.includes("Kite not authenticated")||msg.includes("Please login")||msg.includes("session expired")||msg.includes("TokenException")||msg.includes("Invalid token")){
        setError("Kite session expired. Go to Settings → Zerodha API Setup → Login via Kite.");
      } else if(msg.includes("API credentials")||msg.includes("register-keys")){
        setError("Kite API not configured. Go to Settings → Zerodha API Setup to add your API key and secret.");
      } else {
        setError(msg);
      }
    }
    finally{ setSubmitting(false); }
  };
  submitRef.current = submit;

  const inp = {background:"#0b1624",border:"1px solid #23405f",borderRadius:0,
    padding:"9px 12px",color:"#e7edf5",fontFamily:"inherit",fontSize:13,
    outline:"none",width:"100%",boxSizing:"border-box"};

  // Shared styles for TP/SL rows
  const exitRow = (enabled,color)=>({
    background: enabled?`${color}0d`:"#0b1624",
    border:`1px solid ${enabled?color:"#1a2c44"}`,
    borderRadius:0,padding:"10px 12px",marginBottom:8,
    transition:"all 0.15s",
  });

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.82)",zIndex:500,
      display:"flex",alignItems:"center",justifyContent:"center",padding:16}}
      onClick={onClose}>
      <div style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:14,
        padding:22,width:390,maxWidth:"95vw",maxHeight:"92vh",overflowY:"auto"}}
        onClick={e=>e.stopPropagation()}>

        {/* Header */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:hasSignal?8:16}}>
          <div>
            <div style={{fontSize:16,fontWeight:700,color:"#e7edf5"}}>{ticket.symbol}</div>
            <div style={{fontSize:10,color:"#8ea2bf"}}>Regular · {exchange}</div>
          </div>
          <button onClick={onClose}
            style={{background:"none",border:"none",color:"#8ea2bf",fontSize:20,cursor:"pointer"}}>✕</button>
        </div>

        {/* Signal badges — show underlying stock name for F/O */}
        {hasSignal&&(
          <div style={{background:"rgba(232,179,57,0.08)",border:"1px solid rgba(232,179,57,0.25)",
            borderRadius:0,padding:"8px 10px",marginBottom:14}}>
            {ticket.underlying && ticket.underlying !== ticket.symbol && (
              <div style={{fontSize:10,color:"#e8b339",fontWeight:700,marginBottom:4}}>
                📊 {ticket.underlying} signals:
              </div>
            )}
            <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
              {ticket.signals?.length > 0 ? ticket.signals.map((s,i)=>(
                <span key={i} style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:0,
                  background:s.type==="BUY"?"rgba(34,197,94,0.12)":"rgba(239,68,68,0.10)",
                  color:s.type==="BUY"?"#2ed47a":"#ff5b6c",
                  border:`1px solid ${s.type==="BUY"?"rgba(34,197,94,0.3)":"rgba(239,68,68,0.25)"}`}}>
                  {s.type==="BUY"?"▲":"▼"} {s.label}
                </span>
              )) : ticket.signalLabel ? (
                <span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:0,
                  background:"rgba(232,179,57,0.12)",color:"#e8b339",
                  border:"1px solid rgba(232,179,57,0.3)"}}>
                  {ticket.signalLabel}
                </span>
              ) : null}
            </div>
          </div>
        )}

        {/* BUY / SELL */}
        <div style={{display:"flex",border:"1px solid #23405f",borderRadius:0,overflow:"hidden",marginBottom:14}}>
          {["BUY","SELL"].map(s=>(
            <button key={s} onClick={()=>setSide(s)}
              style={{flex:1,background:side===s?(s==="BUY"?"#2ed47a":"#ff5b6c"):"transparent",
                border:"none",padding:"10px",color:side===s?"#fff":"#8ea2bf",
                fontFamily:"inherit",fontSize:13,fontWeight:700,cursor:"pointer",letterSpacing:1}}>
              {s==="BUY"?"▲ BUY":"▼ SELL"}
            </button>
          ))}
        </div>

        {/* Order type */}
        <div style={{display:"flex",gap:8,marginBottom:12}}>
          {["LIMIT","SL"].map(t=>(
            <button key={t} onClick={()=>setOrderType(t)}
              style={{flex:1,background:orderType===t?"rgba(0,212,170,0.15)":"transparent",
                border:`1px solid ${orderType===t?"#e8b339":"#23405f"}`,
                borderRadius:0,padding:"7px",color:orderType===t?"#e8b339":"#8ea2bf",
                fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer"}}>
              {t}
            </button>
          ))}
        </div>

        {/* Product type — MIS / NRML / CNC */}
        <div style={{marginBottom:12}}>
          <div style={{fontSize:10,color:"#5a728f",letterSpacing:1,marginBottom:5}}>PRODUCT</div>
          <div style={{display:"flex",gap:6}}>
            {[
              {v:"MIS",  label:"MIS",  tip:"Intraday — auto-square off at 3:15"},
              {v:"NRML", label:"NRML", tip:"F&O / Positional — carry overnight"},
              {v:"CNC",  label:"CNC",  tip:"Delivery — equity long-term holding"},
            ].map(({v,label,tip})=>(
              <button key={v} onClick={()=>setProduct(v)} title={tip}
                style={{flex:1,background:product===v?"rgba(251,191,36,0.15)":"transparent",
                  border:`1px solid ${product===v?"#f59e0b":"#23405f"}`,
                  borderRadius:0,padding:"7px 4px",color:product===v?"#f59e0b":"#8ea2bf",
                  fontFamily:"inherit",fontSize:11,fontWeight:product===v?700:400,cursor:"pointer"}}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Exchange */}
        <div style={{display:"flex",gap:8,marginBottom:12}}>
          {["NSE","BSE","NFO","MCX"].map(ex=>(
            <button key={ex} onClick={()=>setExchange(ex)}
              style={{flex:1,background:exchange===ex?"rgba(96,165,250,0.15)":"transparent",
                border:`1px solid ${exchange===ex?"#6b7280":"#23405f"}`,borderRadius:0,
                padding:"6px",color:exchange===ex?"#6b7280":"#8ea2bf",
                fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer"}}>
              {ex}
            </button>
          ))}
        </div>

        {/* Qty + Price */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
          <div>
            <div style={{fontSize:12,color:"#8ea2bf",letterSpacing:1,marginBottom:4}}>QTY</div>
            <input type="text" inputMode="numeric" value={qty}
              onChange={e=>setQty(e.target.value)} style={inp} placeholder="1"/>
          </div>
          <div>
            <div style={{fontSize:12,color:"#8ea2bf",letterSpacing:1,marginBottom:4}}>
              {isSL?"LIMIT PRICE":"PRICE"}
            </div>
            <input type="text" inputMode="decimal" value={price}
              onChange={e=>setPrice(e.target.value)} style={inp} placeholder="0.00"/>
          </div>
        </div>

        {/* Trigger — SL only */}
        {isSL&&(
          <div style={{marginBottom:12}}>
            <div style={{fontSize:12,color:"#8ea2bf",letterSpacing:1,marginBottom:4}}>TRIGGER PRICE</div>
            <input type="text" inputMode="decimal" value={triggerPrice}
              onChange={e=>setTriggerPrice(e.target.value)} style={inp} placeholder="0.00"/>
          </div>
        )}

        {/* Order value */}
        {price&&qty&&(
          <div style={{background:"#0b1624",borderRadius:0,padding:"7px 12px",marginBottom:14,
            display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontSize:10,color:"#8ea2bf"}}>Order Value</span>
            <span style={{fontSize:12,fontWeight:700,color:"#e7edf5"}}>
              ₹{(parseFloat(price)*parseInt(qty)||0).toLocaleString("en-IN")}
            </span>
          </div>
        )}

        {/* ── TP / SL — ZERODHA STYLE ── */}
        <div style={{marginBottom:14}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <span style={{fontSize:10,color:"#8ea2bf",fontWeight:700,letterSpacing:1}}>EXIT ORDERS</span>
            {/* Quick % buttons — equity + option */}
            {entryPrice>0&&(
              <div style={{display:"flex",gap:3,flexWrap:"wrap",justifyContent:"flex-end"}}>
                {/* Equity pcts */}
                {[0.5,1,2,3].map(pct=>(
                  <button key={pct} onClick={()=>quickFill(pct)}
                    style={{background:"#0b1624",border:"1px solid #1a2c44",borderRadius:0,
                      padding:"2px 6px",color:"#8ea2bf",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>
                    {pct}%
                  </button>
                ))}
                {/* Option pcts — shown when NFO/MCX instrument */}
                {(["NFO","MCX","BFO"].includes(ticket.exchange)||["CE","PE","FUT"].includes(ticket.instrumentType))&&
                  [10,20,30,50].map(pct=>(
                    <button key={`o${pct}`} onClick={()=>quickFill(pct)}
                      style={{background:"rgba(161,98,7,0.1)",border:"1px solid rgba(161,98,7,0.4)",
                        borderRadius:0,padding:"2px 6px",color:"#49c2e4",fontFamily:"inherit",
                        fontSize:11,cursor:"pointer",fontWeight:700}}>
                      {pct}%
                    </button>
                  ))
                }
              </div>
            )}
          </div>

          {/* ── Demand/Supply Zone suggestions ──────────────────────────────── */}
          {zones && (zones.demand?.length>0 || zones.supply?.length>0) && (() => {
            const ep2 = parseFloat(price)||0;
            const isBuy = side==="BUY";
            // For BUY: supply zones = potential TP, demand zones = potential SL
            // For SELL: demand zones = potential TP, supply zones = potential SL
            const tpZones = isBuy ? (zones.supply||[]) : (zones.demand||[]);
            const slZones = isBuy ? (zones.demand||[]) : (zones.supply||[]);
            const validTp = tpZones.filter(z=> isBuy ? z[0]>ep2 : z[1]<ep2 ).slice(0,2);
            const validSl = slZones.filter(z=> isBuy ? z[1]<ep2 : z[0]>ep2 ).slice(0,2);
            if(!validTp.length && !validSl.length) return null;
            return (
              <div style={{background:"rgba(232,179,57,0.05)",border:"1px solid rgba(232,179,57,0.2)",
                borderRadius:0,padding:"8px 10px",marginBottom:8}}>
                <div style={{fontSize:9,color:"#5a728f",letterSpacing:1,marginBottom:6,fontWeight:700}}>
                  📊 ZONE-BASED TP/SL — tap to use
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {validTp.map((z,i)=>{
                    const mid = parseFloat(((z[0]+z[1])/2).toFixed(2));
                    const pct = ep2>0 ? ((mid-ep2)/ep2*100*(isBuy?1:-1)).toFixed(1) : "";
                    return (
                      <button key={`tp${i}`} onClick={()=>{
                        setTpInput(String(mid)); setTpEnabled(true); setTpMode("price");
                      }}
                        style={{background:"rgba(34,197,94,0.1)",border:"1px solid rgba(34,197,94,0.3)",
                          borderRadius:0,padding:"3px 8px",fontFamily:"inherit",fontSize:10,
                          color:"#2ed47a",cursor:"pointer",textAlign:"left"}}>
                        <div style={{fontWeight:700}}>TP ₹{mid}</div>
                        <div style={{fontSize:8,color:"#5a728f"}}>{isBuy?"Supply":"Demand"} zone {pct>0?"+":""}{pct}%</div>
                      </button>
                    );
                  })}
                  {validSl.map((z,i)=>{
                    const mid = parseFloat(((z[0]+z[1])/2).toFixed(2));
                    const pct = ep2>0 ? Math.abs((mid-ep2)/ep2*100).toFixed(1) : "";
                    return (
                      <button key={`sl${i}`} onClick={()=>{
                        setSlInput(String(mid)); setSlEnabled(true); setSlMode("price");
                      }}
                        style={{background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.25)",
                          borderRadius:0,padding:"3px 8px",fontFamily:"inherit",fontSize:10,
                          color:"#ff5b6c",cursor:"pointer",textAlign:"left"}}>
                        <div style={{fontWeight:700}}>SL ₹{mid}</div>
                        <div style={{fontSize:8,color:"#5a728f"}}>{isBuy?"Demand":"Supply"} zone -{pct}%</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Take Profit */}
          <div style={exitRow(tpEnabled,"#2ed47a")}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:tpEnabled?8:0}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div onClick={()=>setTpEnabled(p=>!p)}
                  style={{width:34,height:18,borderRadius:9,background:tpEnabled?"#2ed47a":"#23405f",
                    cursor:"pointer",position:"relative",transition:"background 0.2s",flexShrink:0}}>
                  <div style={{position:"absolute",top:2,left:tpEnabled?16:2,width:14,height:14,
                    borderRadius:"50%",background:"#fff",transition:"left 0.2s"}}/>
                </div>
                <span style={{fontSize:12,color:tpEnabled?"#2ed47a":"#8ea2bf",fontWeight:700}}>
                  Take Profit
                </span>
              </div>
              {tpEnabled&&tpPrice&&(
                <span style={{fontSize:11,color:"#2ed47a",fontWeight:700}}>
                  ₹{tpPrice} {tpPct&&`(+${tpPct}%)`}
                </span>
              )}
            </div>
            {tpEnabled&&(
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <button onClick={()=>setTpMode(m=>m==="price"?"pct":"price")}
                  style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,
                    padding:"4px 8px",color:"#8ea2bf",fontFamily:"inherit",fontSize:12,cursor:"pointer",flexShrink:0}}>
                  {tpMode==="price"?"₹ Price":"% Pct"}
                </button>
                <input type="text" inputMode="decimal"
                  value={tpInput} onChange={e=>setTpInput(e.target.value)}
                  placeholder={tpMode==="price"?"Target price":"% gain"}
                  style={{flex:1,background:"#0b1624",border:`1px solid ${tpValid?"#2ed47a":"#ff5b6c"}`,
                    borderRadius:0,padding:"7px 10px",color:"#2ed47a",
                    fontFamily:"inherit",fontSize:12,outline:"none"}}/>
                {tpMode==="pct"&&tpPrice&&(
                  <span style={{fontSize:10,color:"#2ed47a",flexShrink:0}}>→ ₹{tpPrice}</span>
                )}
                {tpMode==="price"&&tpPct&&(
                  <span style={{fontSize:10,color:"#2ed47a",flexShrink:0}}>+{tpPct}%</span>
                )}
              </div>
            )}
          </div>

          {/* Stop Loss */}
          <div style={exitRow(slEnabled,"#ff5b6c")}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:slEnabled?8:0}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div onClick={()=>setSlEnabled(p=>!p)}
                  style={{width:34,height:18,borderRadius:9,background:slEnabled?"#ff5b6c":"#23405f",
                    cursor:"pointer",position:"relative",transition:"background 0.2s",flexShrink:0}}>
                  <div style={{position:"absolute",top:2,left:slEnabled?16:2,width:14,height:14,
                    borderRadius:"50%",background:"#fff",transition:"left 0.2s"}}/>
                </div>
                <span style={{fontSize:12,color:slEnabled?"#ff5b6c":"#8ea2bf",fontWeight:700}}>
                  Stop Loss
                </span>
              </div>
              {slEnabled&&slPrice&&(
                <span style={{fontSize:11,color:"#ff5b6c",fontWeight:700}}>
                  ₹{slPrice} {slPct&&`(-${slPct}%)`}
                </span>
              )}
            </div>
            {slEnabled&&(
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <button onClick={()=>setSlMode(m=>m==="price"?"pct":"price")}
                  style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,
                    padding:"4px 8px",color:"#8ea2bf",fontFamily:"inherit",fontSize:12,cursor:"pointer",flexShrink:0}}>
                  {slMode==="price"?"₹ Price":"% Pct"}
                </button>
                <input type="text" inputMode="decimal"
                  value={slInput} onChange={e=>setSlInput(e.target.value)}
                  placeholder={slMode==="price"?"Stop price":"% loss"}
                  style={{flex:1,background:"#0b1624",border:`1px solid ${slValid?"#ff5b6c":"#e8b339"}`,
                    borderRadius:0,padding:"7px 10px",color:"#ff5b6c",
                    fontFamily:"inherit",fontSize:12,outline:"none"}}/>
                {slMode==="pct"&&slPrice&&(
                  <span style={{fontSize:10,color:"#ff5b6c",flexShrink:0}}>→ ₹{slPrice}</span>
                )}
                {slMode==="price"&&slPct&&(
                  <span style={{fontSize:10,color:"#ff5b6c",flexShrink:0}}>-{slPct}%</span>
                )}
              </div>
            )}
          </div>

          {/* Trailing SL — available independently */}
          <div style={{display:"flex",alignItems:"center",gap:10,padding:"6px 12px",
            background:"#0b1624",border:`1px solid ${trailEnabled?"rgba(96,165,250,0.4)":"#1a2c44"}`,
            borderRadius:0,marginTop:4}}>
            <div onClick={()=>setTrailEnabled(p=>!p)}
              style={{width:30,height:16,borderRadius:0,background:trailEnabled?"#49c2e4":"#23405f",
                cursor:"pointer",position:"relative",flexShrink:0}}>
              <div style={{position:"absolute",top:1,left:trailEnabled?14:1,width:14,height:14,
                borderRadius:"50%",background:"#fff",transition:"left 0.15s"}}/>
            </div>
            <span style={{fontSize:11,color:trailEnabled?"#49c2e4":"#8ea2bf",fontWeight:600}}>
              🔄 Trailing SL
            </span>
            {trailEnabled&&(
              <>
                <input type="text" inputMode="decimal" value={trailPct}
                  onChange={e=>setTrailPct(e.target.value)}
                  style={{width:50,background:"#0f1d30",border:"1px solid #49c2e4",borderRadius:0,
                    padding:"3px 6px",color:"#49c2e4",fontFamily:"inherit",fontSize:11,outline:"none",textAlign:"center"}}/>
                <span style={{fontSize:10,color:"#8ea2bf"}}>% from peak</span>
              </>
            )}
          </div>

          {(tpEnabled||slEnabled||trailEnabled)&&(
            <div style={{marginTop:8,fontSize:12,color:"#5a728f",lineHeight:1.6,
              padding:"5px 8px",borderLeft:"2px solid #1a2c44"}}>
              {userRole==="admin"
                ? <>{"Watcher monitors LTP every 5s · Exit via LIMIT order when target hit"}{trailEnabled&&" · SL trails price by "+trailPct+"%"}</>
                : <>{"GTT active in Kite — Zerodha monitors and exits automatically · SEBI compliant"}{trailEnabled&&<span style={{color:"#f59e0b"}}> · Note: GTT does not support trailing SL — use a fixed SL</span>}</>
              }
            </div>
          )}
        </div>

        {error&&(
          <div style={{background:"rgba(248,81,73,0.1)",border:"1px solid rgba(248,81,73,0.3)",
            borderRadius:4,padding:"10px 12px",color:"#ff5b6c",fontSize:11,marginBottom:12,lineHeight:1.6}}>
            {error.split('\n').map((line,i)=>(
              <div key={i} style={{marginBottom:i<error.split('\n').length-1?4:0}}>{line}</div>
            ))}
            {(error.includes("whitelist")||error.includes("165.22.209.25")||error.includes("Kite session")||error.includes("API not configured"))&&(
              <div style={{marginTop:6,borderTop:"1px solid rgba(248,81,73,0.2)",paddingTop:6,color:"#e8b339"}}>
                {error.includes("Proxy")||error.includes("proxy")
                  ? "→ Open Settings → Zerodha API Setup → Static IP Proxy section."
                  : <>→ Open{" "}<a href="https://developers.kite.trade" target="_blank" rel="noreferrer"
                      style={{color:"#4a9eff"}}>developers.kite.trade</a>{" "}to update your Kite app settings.</>
                }
              </div>
            )}
          </div>
        )}

        {/* Virtual order option */}
        {onAddVirtual && (
          <label style={{display:"flex",alignItems:"center",gap:6,padding:"6px 2px",
            cursor:"pointer",marginBottom:4}}>
            <input type="checkbox" checked={alsoVirtual}
              onChange={e=>setAlsoVirtual(e.target.checked)}
              style={{accentColor:"#49c2e4",width:13,height:13}}/>
            <span style={{fontSize:10,color:"#8ea2bf"}}>
              📋 Also add to <strong style={{color:"#49c2e4"}}>Virtual Tab</strong> for tracking
            </span>
          </label>
        )}

        <div style={{display:"flex",gap:6}}>
          {(()=>{
            const _hasProxy = !!(sessionStorage.getItem("kite_proxy_host")||"").trim();
            const _isNonAdminZerodha = userRole!=="admin" && userBroker==="zerodha";
            const _useProxy  = _isNonAdminZerodha && _hasProxy;
            const _deeplink  = _isNonAdminZerodha && !_hasProxy;
            const _btnBg = submitting?"#1a2c44"
              : _deeplink ? "#ff6b35"
              : side==="BUY" ? "#2ed47a" : "#ff5b6c";
            const _btnLabel = submitting
              ? (_deeplink?"Opening...":"Placing...")
              : _deeplink
                ? `${BROKER_CONFIGS[userBroker]?.logo||"🟠"} Open in ${BROKER_CONFIGS[userBroker]?.name||"Kite"}`
                : (side==="BUY"?`▲ BUY ${ticket.symbol}`:`▼ SELL ${ticket.symbol}`);
            return (
              <button onClick={submit} disabled={submitting}
                style={{flex:1,background:_btnBg,border:"none",borderRadius:0,padding:"13px",
                  color:"#fff",fontFamily:"inherit",fontSize:13,fontWeight:700,
                  cursor:submitting?"default":"pointer",letterSpacing:1}}>
                {_btnLabel}
              </button>
            );
          })()}
          {onAddVirtual && (
            <button onClick={()=>{
              const _sigs = ticket.signals || [];
              const ep = parseFloat(price)||0;
              if(ep<=0){ setError("Price required for virtual trade"); return; }
              onAddVirtual({
                symbol:    ticket.symbol,
                exchange:  exchange,
                side,
                entry:     ep,
                price:     ep,
                qty:       parseInt(qty)||1,
                product,
                tp:        tpEnabled&&tpPrice ? tpPrice : null,
                sl:        slEnabled&&slPrice ? slPrice : null,
                trailEnabled: !!trailEnabled,
                trailPct:  trailEnabled ? parseFloat(trailPct||1.5) : 0,
                tab_mode:  ticket.tabMode||"equity",
                source:    _sigs.length>0 ? "scan" : "manual",
                signals:   _sigs.length ? _sigs.map(s=>s.label).join(" · ") : (tradeReason||"manual"),
                note:      _sigs.length ? _sigs.map(s=>s.label).join(" · ") : (tradeReason||""),
                instrumentType: ticket.instrumentType||"EQ",
              });
              onSuccess?.(`🎮 Added ${ticket.symbol} to Virtual Tab (paper trade)`);
              onClose();
            }}
              style={{padding:"13px 14px",
                background:"rgba(96,165,250,0.12)",
                border:"1px solid rgba(96,165,250,0.35)",
                borderRadius:0,color:"#60a5fa",
                fontFamily:"inherit",fontSize:11,fontWeight:700,
                cursor:"pointer",whiteSpace:"nowrap"}}>
              🎮 Virtual Only
            </button>
          )}
        </div>
        {userRole!=="admin"&&userBroker==="zerodha"&&(
          <div style={{fontSize:9,color:"#5a728f",textAlign:"center",marginTop:4}}>
            {(sessionStorage.getItem("kite_proxy_host")||"").trim()
              ? "🔒 Proxy configured — order placed via VPS through your static IP"
              : "Order details copied to clipboard · Confirm manually in Zerodha Kite"
            }
          </div>
        )}

        {/* ── Trade reason popup — shown when no signal attached ── */}
        {showReasonPopup && (
          <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.7)",zIndex:50,
            display:"flex",alignItems:"center",justifyContent:"center",padding:12,borderRadius:12}}>
            <div style={{background:"#0f1d30",border:"1px solid #e8b339",borderRadius:10,
              padding:16,width:"min(340px,90%)",maxHeight:"70vh",overflow:"auto"}}
              onClick={e=>e.stopPropagation()}>
              <div style={{fontSize:14,fontWeight:700,color:"#e8b339",marginBottom:10}}>
                Why are you taking this trade?
              </div>
              <div style={{fontSize:11,color:"#8ea2bf",marginBottom:12}}>
                No scan signal attached. Please select a reason or type your own.
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:12}}>
                {TRADE_REASONS.map(r=>(
                  <button key={r} onClick={()=>setTradeReason(r)}
                    style={{textAlign:"left",padding:"8px 10px",fontSize:12,
                      background:tradeReason===r?"rgba(232,179,57,0.15)":"rgba(20,35,55,0.6)",
                      border:tradeReason===r?"1px solid #e8b339":"1px solid #23405f",
                      borderRadius:6,color:tradeReason===r?"#e8b339":"#e7edf5",cursor:"pointer"}}>
                    {r}
                  </button>
                ))}
              </div>
              <input type="text" placeholder="Or type custom reason..."
                value={TRADE_REASONS.includes(tradeReason)?"":tradeReason}
                onChange={e=>setTradeReason(e.target.value)}
                style={{width:"100%",padding:"8px 10px",fontSize:12,background:"#0a1628",
                  border:"1px solid #23405f",borderRadius:6,color:"#e7edf5",marginBottom:12,
                  boxSizing:"border-box"}}/>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>{setShowReasonPopup(false);setSubmitting(false);}}
                  style={{flex:1,padding:"8px 0",fontSize:12,background:"transparent",
                    border:"1px solid #23405f",borderRadius:6,color:"#8ea2bf",cursor:"pointer"}}>
                  Cancel
                </button>
                <button onClick={()=>{
                  if(tradeReason.trim()) { setShowReasonPopup(false); setTimeout(()=>submitRef.current?.(),50); }
                }}
                  disabled={!tradeReason.trim()}
                  style={{flex:1,padding:"8px 0",fontSize:12,fontWeight:700,
                    background:tradeReason.trim()?"rgba(232,179,57,0.2)":"rgba(30,40,55,0.5)",
                    border:"1px solid "+(tradeReason.trim()?"#e8b339":"#23405f"),
                    borderRadius:6,color:tradeReason.trim()?"#e8b339":"#5a728f",cursor:"pointer"}}>
                  Continue with trade
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: ORDERS (live order book + modify/cancel)
// ══════════════════════════════════════════════════════════════════════════════
function OrdersTab({onTrade, watchers, setWatchers, tgToken, tgChat, showToast}) {
  const [orders,setOrders]       = useState([]);
  const [loading,setLoading]     = useState(false);
  const [error,setError]         = useState(null);
  const [modifying,setModifying] = useState(null);
  const [modForm,setModForm]     = useState({price:"",triggerPrice:"",qty:""});
  const [subTab,setSubTab]       = useState("orders");
  const [editWatcher,setEditWatcher] = useState(null);

  // ── GTT state ──
  const [gtts, setGtts]           = useState([]);
  const [gttLoading, setGttLoading] = useState(false);
  const [gttTrails, setGttTrails] = useState({});

  const fetchGtts = useCallback(async()=>{
    setGttLoading(true);
    try {
      const list = await apiFetch("/gtt/list");
      setGtts(Array.isArray(list) ? list : list?.gtts || list?.data || []);
    } catch(e) { console.warn("GTT list error:", e); }
    finally { setGttLoading(false); }
  },[]);

  const fetchGttTrails = useCallback(async()=>{
    try {
      const data = await apiFetch("/gtt/trail/status");
      setGttTrails(data?.trails || data || {});
    } catch(e) { console.warn("GTT trail status error:", e); }
  },[]);

  const cancelGtt = async(gttId)=>{
    try {
      await apiFetch(`/gtt/${gttId}`,{method:"DELETE"});
      showToast?.(`✓ GTT #${gttId} cancelled`);
      fetchGtts();
    } catch(e) { showToast?.(`✗ Cancel failed: ${e.message}`); }
  };

  const stopTrail = async(gttId)=>{
    try {
      await apiFetch(`/gtt/trail/${gttId}`,{method:"DELETE"});
      showToast?.(`✓ Trailing stopped for GTT #${gttId}`);
      fetchGttTrails();
    } catch(e) { showToast?.(`✗ Stop trail failed: ${e.message}`); }
  };

  // Auto-fetch GTTs when tab is active
  useEffect(()=>{
    if(subTab==="gtt") { fetchGtts(); fetchGttTrails(); }
  },[subTab, fetchGtts, fetchGttTrails]);

  const STATUS_COLOR = {COMPLETE:"#2ed47a",REJECTED:"#ff5b6c",CANCELLED:"#8ea2bf",OPEN:"#6b7280","TRIGGER PENDING":"#e8b339"};

  const fetchOrders = useCallback(async()=>{
    setLoading(true); setError(null);
    try{ setOrders(await apiFetch("/orders")); }
    catch(e){ setError(e.message); }
    finally{ setLoading(false); }
  },[]);

  useEffect(()=>{ fetchOrders(); const t=setInterval(fetchOrders,15000); return()=>clearInterval(t); },[fetchOrders]);

  const cancelOrder = async(id)=>{
    try{ await apiFetch(`/order/cancel/${id}`,{method:"DELETE"}); fetchOrders(); }
    catch(e){ alert("Cancel failed: "+e.message); }
  };

  const submitModify = async()=>{
    if(!modifying) return;
    try{
      await apiFetch("/order/modify",{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({order_id:modifying.order_id,order_type:modifying.order_type,
          price:parseFloat(modForm.price)||0,trigger_price:parseFloat(modForm.triggerPrice)||0,
          quantity:parseInt(modForm.qty)||modifying.quantity,validity:modifying.validity||"DAY"})
      });
      setModifying(null); fetchOrders();
    }catch(e){ alert("Modify failed: "+e.message); }
  };

  const todaySummary = useMemo(()=>{
    const completed=orders.filter(o=>o.status==="COMPLETE");
    return{total:completed.length,buys:completed.filter(o=>o.transaction_type==="BUY").length,
      sells:completed.filter(o=>o.transaction_type==="SELL").length,
      pending:orders.filter(o=>["OPEN","TRIGGER PENDING"].includes(o.status)).length};
  },[orders]);

  const watchingCount = (watchers||[]).filter(w=>w.status==="WATCHING").length;

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%"}}>
      <div style={{background:"#0b1624",borderBottom:"1px solid #1a2c44",padding:"10px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{display:"flex",gap:16}}>
          <div style={{textAlign:"center"}}>
            <div style={{fontSize:12,color:"#8ea2bf"}}>EXECUTED</div>
            <div style={{fontSize:16,fontWeight:700,color:"#2ed47a"}}>{todaySummary.total}</div>
          </div>
          <div style={{textAlign:"center"}}>
            <div style={{fontSize:12,color:"#8ea2bf"}}>PENDING</div>
            <div style={{fontSize:16,fontWeight:700,color:"#6b7280"}}>{todaySummary.pending}</div>
          </div>
          <div style={{textAlign:"center"}}>
            <div style={{fontSize:12,color:"#8ea2bf"}}>B / S</div>
            <div style={{fontSize:13,fontWeight:700,color:"#e7edf5"}}>
              <span style={{color:"#2ed47a"}}>{todaySummary.buys}</span>
              <span style={{color:"#8ea2bf"}}> / </span>
              <span style={{color:"#ff5b6c"}}>{todaySummary.sells}</span>
            </div>
          </div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={()=>onTrade({symbol:"",exchange:"NSE",side:"BUY",price:"",qty:1})} style={{background:"linear-gradient(135deg,#3fb950,#92400e)",border:"none",borderRadius:0,padding:"6px 14px",color:"#fff",fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer"}}>+ NEW ORDER</button>
          <button onClick={fetchOrders} disabled={loading} style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,padding:"6px 12px",color:"#e8b339",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>{loading?"⟳":"↻"}</button>
        </div>
      </div>

      {error && <div style={{padding:"6px 14px",background:"rgba(248,81,73,0.1)",color:"#ff5b6c",fontSize:11}}>⚠ {error}</div>}

      <div style={{display:"flex",borderBottom:"1px solid #1a2c44",background:"#0b1624"}}>
        <button onClick={()=>setSubTab("orders")} style={{background:subTab==="orders"?"#0f1d30":"transparent",border:"none",borderBottom:"2px solid "+(subTab==="orders"?"#e8b339":"transparent"),padding:"8px 18px",color:subTab==="orders"?"#e7edf5":"#8ea2bf",fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer"}}>Order Book</button>
        <button onClick={()=>setSubTab("gtt")} style={{background:subTab==="gtt"?"#0f1d30":"transparent",border:"none",borderBottom:"2px solid "+(subTab==="gtt"?"#60a5fa":"transparent"),padding:"8px 18px",color:subTab==="gtt"?"#e7edf5":"#8ea2bf",fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer"}}>🛡 GTT</button>
        <button onClick={()=>setSubTab("autotp")} style={{background:subTab==="autotp"?"#0f1d30":"transparent",border:"none",borderBottom:"2px solid "+(subTab==="autotp"?"#e8b339":"transparent"),padding:"8px 18px",color:subTab==="autotp"?"#e7edf5":"#8ea2bf",fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer"}}>{"Auto Exit ("+watchingCount+")"}</button>
      </div>

      {editWatcher && <EditWatcherModal watcher={editWatcher} onClose={()=>setEditWatcher(null)} onSave={(updated)=>setWatchers(p=>p.map(w=>w.id===updated.id?updated:w))}/>}

      <div style={{flex:1,overflow:"auto",padding:12}}>
        {/* ── GTT Management Panel ── */}
        {subTab==="gtt" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <div style={{fontSize:12,fontWeight:700,color:"#60a5fa"}}>🛡 Active GTT Orders</div>
              <button onClick={()=>{fetchGtts();fetchGttTrails();}}
                style={{background:"rgba(96,165,250,0.1)",border:"1px solid rgba(96,165,250,0.3)",
                  borderRadius:0,padding:"4px 10px",color:"#60a5fa",fontFamily:"inherit",
                  fontSize:10,cursor:"pointer"}}>
                ↻ Refresh
              </button>
            </div>
            {gttLoading && <div style={{textAlign:"center",padding:30,color:"#8ea2bf",fontSize:11}}>Loading GTTs...</div>}
            {!gttLoading && gtts.length===0 && (
              <div style={{textAlign:"center",padding:60,color:"#5a728f",fontSize:11}}>
                No active GTT orders. Place GTT from scan signals or strategy popup.
              </div>
            )}
            {gtts.filter(g=>g.status==="active").map(g=>{
              const trail = gttTrails[String(g.id)];
              const conds = g.condition || {};
              const trigVals = conds.trigger_values || g.trigger_values || [];
              const isBuy = (g.orders||[])[0]?.transaction_type==="BUY" || g.type==="single";
              return (
                <div key={g.id} style={{background:"#0b1624",border:"1px solid #1a2c44",borderRadius:0,
                  padding:"10px 12px",marginBottom:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <span style={{fontSize:12,fontWeight:700,color:"#e7edf5"}}>{conds.tradingsymbol||g.tradingsymbol||"—"}</span>
                      <span style={{fontSize:10,color:"#8ea2bf",marginLeft:8}}>#{g.id}</span>
                      <span style={{fontSize:9,padding:"1px 6px",marginLeft:6,borderRadius:0,
                        background:g.type==="two-leg"?"rgba(232,179,57,0.1)":"rgba(96,165,250,0.1)",
                        color:g.type==="two-leg"?"#e8b339":"#60a5fa",
                        border:`1px solid ${g.type==="two-leg"?"rgba(232,179,57,0.3)":"rgba(96,165,250,0.3)"}`}}>
                        {g.type==="two-leg"?"OCO":"Single"}
                      </span>
                    </div>
                    <div style={{display:"flex",gap:4}}>
                      {trail && (
                        <button onClick={()=>stopTrail(g.id)}
                          style={{background:"rgba(232,179,57,0.1)",border:"1px solid rgba(232,179,57,0.3)",
                            borderRadius:0,padding:"3px 8px",color:"#e8b339",fontFamily:"inherit",
                            fontSize:9,cursor:"pointer"}}>
                          ⏹ Stop Trail
                        </button>
                      )}
                      <button onClick={()=>cancelGtt(g.id)}
                        style={{background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",
                          borderRadius:0,padding:"3px 8px",color:"#ff5b6c",fontFamily:"inherit",
                          fontSize:9,cursor:"pointer"}}>
                        ✗ Cancel
                      </button>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:12,marginTop:6,fontSize:10,color:"#8ea2bf"}}>
                    {trigVals.length>=2 && (
                      <>
                        <span>SL: <b style={{color:"#ff5b6c"}}>₹{parseFloat(trigVals[0]).toLocaleString("en-IN")}</b></span>
                        <span>TP: <b style={{color:"#2ed47a"}}>₹{parseFloat(trigVals[1]).toLocaleString("en-IN")}</b></span>
                      </>
                    )}
                    {trigVals.length===1 && (
                      <span>Trigger: <b style={{color:"#e8b339"}}>₹{parseFloat(trigVals[0]).toLocaleString("en-IN")}</b></span>
                    )}
                    <span>Qty: {(g.orders||[])[0]?.quantity||"—"}</span>
                    <span style={{color:"#5a728f"}}>Created: {new Date(g.created_at||g.updated_at||Date.now()).toLocaleDateString("en-IN")}</span>
                  </div>
                  {trail && (
                    <div style={{marginTop:6,padding:"4px 8px",background:"rgba(232,179,57,0.06)",
                      border:"1px solid rgba(232,179,57,0.15)",borderRadius:0,fontSize:10}}>
                      <span style={{color:"#e8b339",fontWeight:600}}>⟳ Trailing</span>
                      <span style={{color:"#8ea2bf",marginLeft:8}}>
                        Entry ₹{trail.entry_price?.toLocaleString("en-IN")||"—"} ·
                        Trail {trail.trail_pct}% ·
                        Last trail ₹{trail.last_trail_price?.toLocaleString("en-IN")||"—"}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
            {/* Show triggered/cancelled GTTs */}
            {gtts.filter(g=>g.status!=="active").length>0 && (
              <div style={{marginTop:12}}>
                <div style={{fontSize:10,color:"#5a728f",fontWeight:600,marginBottom:6}}>History</div>
                {gtts.filter(g=>g.status!=="active").slice(0,10).map(g=>(
                  <div key={g.id} style={{display:"flex",justifyContent:"space-between",
                    padding:"4px 8px",borderBottom:"1px solid #0f1d30",fontSize:10,color:"#5a728f"}}>
                    <span>{g.condition?.tradingsymbol||g.tradingsymbol||"—"} #{g.id}</span>
                    <span style={{color:g.status==="triggered"?"#2ed47a":g.status==="cancelled"?"#ff5b6c":"#8ea2bf"}}>
                      {g.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {subTab==="autotp" && <AutoTPPanel watchers={watchers||[]} setWatchers={setWatchers} onEdit={(w)=>setEditWatcher(w)}/>}
        {subTab==="orders" && <div>
          {orders.length===0 && !loading && (
            <div style={{textAlign:"center",padding:60,color:"#8ea2bf",fontSize:13}}>No orders today</div>
          )}
          {orders.map((o,i)=>{
            const sc = STATUS_COLOR[o.status]||"#8ea2bf";
            const canAct = ["OPEN","TRIGGER PENDING"].includes(o.status);
            const isBuy = o.transaction_type==="BUY";
            return (
              <div key={o.order_id||i} style={{background:"#0f1d30",border:"1px solid "+(isBuy?"rgba(63,185,80,0.2)":"rgba(248,81,73,0.2)"),borderLeft:"3px solid "+(isBuy?"#2ed47a":"#ff5b6c"),borderRadius:0,padding:"11px 14px",marginBottom:8}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                  <div>
                    <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:2}}>
                      <span style={{fontSize:12,fontWeight:700,padding:"2px 6px",borderRadius:0,background:isBuy?"rgba(22,163,74,0.15)":"rgba(220,38,38,0.15)",color:isBuy?"#2ed47a":"#ff5b6c"}}>{o.transaction_type}</span>
                      <span style={{fontSize:13,fontWeight:700,color:"#e7edf5"}}>{o.tradingsymbol}</span>
                      <span style={{fontSize:12,color:"#8ea2bf"}}>{o.exchange}</span>
                    </div>
                    <div style={{fontSize:10,color:"#8ea2bf"}}>Qty: {o.quantity} · {o.order_type} · {o.product}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:10,fontWeight:700,color:sc,marginBottom:2}}>{o.status}</div>
                    <div style={{fontSize:11,color:"#e6edf3",fontWeight:600}}>
                      {"₹"+(o.average_price>0?o.average_price:o.price)}
                      {o.trigger_price>0 && <span style={{color:"#e8b339",fontSize:10}}>{" TRG ₹"+o.trigger_price}</span>}
                    </div>
                  </div>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{fontSize:12,color:"#8ea2bf"}}>{(o.order_timestamp||"").toString().slice(11,16)}</span>
                  <div style={{display:"flex",gap:6}}>
                    {canAct && <button onClick={()=>{setModifying(o);setModForm({price:String(o.price||""),triggerPrice:String(o.trigger_price||""),qty:String(o.quantity)});}} style={{background:"rgba(96,165,250,0.1)",border:"1px solid rgba(96,165,250,0.3)",borderRadius:0,padding:"3px 10px",color:"#6b7280",fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>Modify</button>}
                    {canAct && <button onClick={()=>cancelOrder(o.order_id)} style={{background:"rgba(248,81,73,0.1)",border:"1px solid rgba(248,81,73,0.3)",borderRadius:0,padding:"3px 10px",color:"#ff5b6c",fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>Cancel</button>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>}
      </div>

      {modifying && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:400,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:12,padding:26,width:340}}>
            <div style={{fontSize:14,fontWeight:700,color:"#e7edf5",marginBottom:4}}>Modify Order</div>
            <div style={{fontSize:10,color:"#8ea2bf",marginBottom:16}}>{modifying.tradingsymbol} · {modifying.transaction_type} · {modifying.order_type}</div>
            {[["QUANTITY","qty"],["PRICE","price"],["TRIGGER PRICE","triggerPrice"]].map(([label,key])=>(
              (modifying.order_type==="LIMIT" && key==="triggerPrice") ? null :
              <div key={key} style={{marginBottom:12}}>
                <div style={{fontSize:12,color:"#8ea2bf",letterSpacing:1,marginBottom:5}}>{label}</div>
                <input type="number" step="0.05" value={modForm[key]} onChange={e=>setModForm(p=>({...p,[key]:e.target.value}))} style={{background:"#0b1624",border:"1px solid #23405f",borderRadius:0,padding:"9px 12px",color:"#e7edf5",fontFamily:"inherit",fontSize:13,outline:"none",width:"100%",boxSizing:"border-box"}}/>
              </div>
            ))}
            <div style={{display:"flex",gap:10,marginTop:6}}>
              <button onClick={()=>setModifying(null)} style={{flex:1,background:"#1a2c44",border:"none",borderRadius:0,padding:"10px",color:"#8ea2bf",fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>Cancel</button>
              <button onClick={submitModify} style={{flex:2,background:"#6b7280",border:"none",borderRadius:0,padding:"10px",color:"#fff",fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer"}}>Update Order</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// AUTO TAKE-PROFIT / STOP-LOSS WATCHER
// Polls LTP every 5 sec. When target hit → places LIMIT exit order.
// ══════════════════════════════════════════════════════════════════════════════

function useAutoTPWatcher({watchers, setWatchers, showToast, tgToken, tgChat, onJournalClose=null, sendTgAlert=null, isAdmin=false}) {
  const watchersRef   = useRef(watchers);
  // ── Ref-wrap ALL callback props so the interval closure never goes stale ─────
  const isAdminRef    = useRef(isAdmin);
  const showToastRef  = useRef(showToast);
  const sendTgRef     = useRef(sendTgAlert);
  const journalRef    = useRef(onJournalClose);
  const tgTokenRef    = useRef(tgToken);
  const tgChatRef     = useRef(tgChat);
  useEffect(()=>{ watchersRef.current  = watchers;     },[watchers]);
  useEffect(()=>{ isAdminRef.current   = isAdmin;      },[isAdmin]);
  useEffect(()=>{ showToastRef.current = showToast;    },[showToast]);
  useEffect(()=>{ sendTgRef.current    = sendTgAlert;  },[sendTgAlert]);
  useEffect(()=>{ journalRef.current   = onJournalClose;},[onJournalClose]);
  useEffect(()=>{ tgTokenRef.current   = tgToken;      },[tgToken]);
  useEffect(()=>{ tgChatRef.current    = tgChat;       },[tgChat]);

  useEffect(()=>{
    const interval = setInterval(async()=>{
      // Auto-exit orders: admin only. Trader/viewer use GTT (broker-native).
      // IMPORTANT: read isAdminRef.current — NOT isAdmin (stale closure bug fix)
      if(!isAdminRef.current) return;

      const active = watchersRef.current.filter(w=>w.status==="WATCHING");
      if(!active.length) return;

      // ── Step 1: Fetch real open positions — MANDATORY before any action ──────
      // If this fails we return immediately — never fire orders without position confirmation
      let openPos = {};   // symbol.upper() → {qty, ltp, product, exchange}
      try {
        const posData = await apiFetch("/portfolio/positions");
        const net = posData?.net || [];
        net.forEach(p=>{
          const qty = parseInt(p.quantity||0);
          if(qty !== 0) {
            openPos[p.tradingsymbol.toUpperCase()] = {
              qty:       Math.abs(qty),
              ltp:       parseFloat(p.last_price||0),
              product:   p.product||"MIS",
              exchange:  p.exchange||"NSE",
            };
          }
        });
      } catch(e) {
        // Cannot verify positions — skip this cycle entirely, no orders fired
        return;
      }

      const now = new Date().toISOString();
      const toClose   = [];  // manually closed positions
      const triggered = [];  // TP/SL hit
      const trailed   = [];  // trail SL updated

      for(const w of active){
        const sym = (w.symbol||"").toUpperCase();
        const pos = openPos[sym];

        // ── Position no longer open — manually closed or expired ─────────────
        if(!pos){
          toClose.push({...w, status:"CANCELLED_POSITION_CLOSED",
            triggeredType:"MANUAL_EXIT", triggeredAt:now});
          continue;
        }

        // ── Use LTP from positions (already fetched, no extra API call) ───────
        // Fall back to quote fetch if position ltp is stale (0 or missing)
        let ltp = pos.ltp;
        if(!ltp || ltp <= 0) {
          try {
            const q = await apiFetch(`/quotes?symbols=${encodeURIComponent(`${w.exchange}:${w.symbol}`)}`);
            ltp = parseFloat(q[sym]?.ltp || q[w.symbol]?.ltp || 0);
          } catch { continue; }
        }
        if(!ltp || ltp <= 0) continue;

        let wU = {...w};

        // ── Trailing SL ───────────────────────────────────────────────────────
        // ACTIVATION: trail only once price is at least trail% in profit vs
        // entry — otherwise the first uptick pulls SL to ~entry and entry
        // noise fires it within seconds (same fix as server watcher).
        if(w.trailEnabled && parseFloat(w.trailPct||0) > 0){
          const pct = parseFloat(w.trailPct);
          const entryP = parseFloat(w.entryPrice||0);
          if(w.side==="BUY"){
            const hwm = parseFloat(w.highWaterMark||w.entryPrice||0);
            if(ltp > hwm && (entryP<=0 || ltp >= entryP*(1+pct/100))){
              const newHWM = ltp;
              const newSL  = parseFloat((ltp*(1-pct/100)).toFixed(2));
              wU = {...wU, highWaterMark:newHWM};
              if(newSL > parseFloat(w.slPrice||0)){
                wU = {...wU, slPrice:newSL, slEnabled:true};
              }
              trailed.push(wU);
            }
          } else {
            const lwm = parseFloat(w.lowWaterMark||w.entryPrice||0);
            if(ltp < lwm && (entryP<=0 || ltp <= entryP*(1-pct/100))){
              const newLWM = ltp;
              const newSL  = parseFloat((ltp*(1+pct/100)).toFixed(2));
              wU = {...wU, lowWaterMark:newLWM};
              if(!w.slPrice || newSL < parseFloat(w.slPrice)){
                wU = {...wU, slPrice:newSL, slEnabled:true};
              }
              trailed.push(wU);
            }
          }
        }

        // ── TP / SL check ─────────────────────────────────────────────────────
        let hit = null;
        if(wU.tpEnabled && wU.tpPrice){
          const tp = parseFloat(wU.tpPrice);
          if((wU.side==="BUY"&&ltp>=tp)||(wU.side==="SELL"&&ltp<=tp)) hit="TP";
        }
        if(!hit && wU.slEnabled && wU.slPrice){
          const sl = parseFloat(wU.slPrice);
          if((wU.side==="BUY"&&ltp<=sl)||(wU.side==="SELL"&&ltp>=sl)) hit="SL";
        }

        // ── Zone-aware exit override ──────────────────────────────────────────
        // If watcher has D/S zone data, apply zone-based exit logic:
        //   • Price INSIDE zone → suspend TP/SL, wait for breakout/rejection
        //   • Price breaks BELOW demand zone → force SL (support failed)
        //   • Price breaks ABOVE supply zone → move SL to supply.low (now support), hold
        //   • Price drops BELOW supply after touching it → force TP (supply rejection)
        //   • Price bounces ABOVE demand after touching it → clear zone, continue watching
        // FIX: zone logic must only run when zones are on the SAME price scale
        // as the traded instrument. Option watchers (F&O/MCX AT) carried the
        // UNDERLYING's zones — e.g. demand ₹2850–2880 vs option premium ₹150 —
        // so rule ② "price below demand → SL" fired instantly on every option
        // auto-trade (SL configured -15% but exited within a minute).
        const _isOption = ["CE","PE"].includes((wU.instrumentType||"").toUpperCase()) ||
                          /(CE|PE)$/.test((wU.symbol||"").toUpperCase());
        const _entryRef = parseFloat(wU.entryPrice||ltp||0);
        const _zoneSane = z => z && _entryRef>0 && z.low < _entryRef*3 && z.high > _entryRef*0.33;
        const _dzOK = !_isOption && _zoneSane(wU.demandZone) ? wU.demandZone : null;
        const _szOK = !_isOption && _zoneSane(wU.supplyZone) ? wU.supplyZone : null;
        if(_dzOK || _szOK) {
          const dz  = _dzOK;         // {low, high}
          const sz  = _szOK;         // {low, high}
          const buf = 0.0015;        // 0.15% boundary buffer

          const inDemand = dz && ltp >= dz.low*(1-buf) && ltp <= dz.high*(1+buf);
          const inSupply = sz && ltp >= sz.low*(1-buf) && ltp <= sz.high*(1+buf);

          if(inDemand || inSupply) {
            // ① Price is INSIDE a zone — suspend any TP/SL trigger, track zone touch
            hit = null;
            const zt = inSupply ? "supply" : "demand";
            if(wU.zoneTouched !== zt) {
              wU = {...wU, zoneTouched: zt};
              if(!trailed.some(x=>x.id===wU.id)) trailed.push(wU);
            }
          } else if(dz && ltp < dz.low*(1-buf)) {
            // ② Price broke BELOW demand zone → SL (support broken)
            hit = "SL";
          } else if(sz && ltp > sz.high*(1+buf)) {
            // ③ Price broke ABOVE supply zone → move SL up to supply.low, keep holding
            const newSL = parseFloat(sz.low.toFixed(4));
            if(newSL > parseFloat(wU.slPrice||0)) {
              wU = {...wU, slPrice: newSL, slEnabled: true, zoneTouched: "above_supply"};
              if(!trailed.some(x=>x.id===wU.id)) trailed.push(wU);
              showToastRef.current?.(`⬆ ${wU.symbol} SL moved to ₹${newSL} (supply breakout)`,"ok");
            }
            hit = null; // don't exit — breakout continuation
          } else if(sz && ltp < sz.low*(1-buf) &&
              (wU.zoneTouched==="supply" || wU.zoneTouched==="above_supply")) {
            // ④ Price was at/above supply, now fell below it → TP (supply rejection)
            hit = "TP";
          } else if(dz && ltp > dz.high*(1+buf) && wU.zoneTouched==="demand") {
            // ⑤ Price bounced UP from demand zone → clear zone touch, keep watching
            wU = {...wU, zoneTouched: "none"};
            if(!trailed.some(x=>x.id===wU.id)) trailed.push(wU);
            hit = null;
          }
        }

        if(!hit) continue;

        // ── Fire exit order ───────────────────────────────────────────────────
        const exitSide  = wU.side==="BUY" ? "SELL" : "BUY";
        const exitRaw   = parseFloat(hit==="TP" ? wU.tpPrice : wU.slPrice);
        const exitPrice = roundToTick(exitRaw, wU.tickSize||0.05);
        // Use actual open qty from Kite positions (not stale watcher qty)
        const exitQty   = pos.qty || wU.qty || 1;
        // Product MUST match what was used when position was opened
        // Priority: live position product > watcher saved product > inferred
        const isOptOrFut = ["CE","PE","FUT"].includes((wU.instrumentType||"").toUpperCase()) ||
                           ["NFO","MCX"].includes((wU.exchange||"").toUpperCase());
        const product   = pos.product    // from live Kite position — most reliable
                       || wU.product     // saved when order was placed
                       || (isOptOrFut ? "NRML" : "MIS");

        try{
          // ── Cancel VPS watcher FIRST before placing order ─────────────────
          // This prevents VPS from also firing the same exit (double trade)
          apiFetch(`/watcher/${wU.id||wU.watcher_id}`,{method:"DELETE"}).catch(()=>{});

          // Always use LIMIT order — Kite requires market_protection for MARKET orders
          // Add slippage buffer so LIMIT fills immediately: BUY exit = price+0.5%, SELL exit = price-0.5%
          const tick       = parseFloat(wU.tickSize||0.05);

          // Exit order strategy:
          // TP → LIMIT at TP price (clean fill at target)
          // SL → LIMIT with slippage buffer (works for all products/exchanges)
          //      SL-M can fail for NSE equity if Kite rejects it
          // Slippage: SELL exit → price slightly below (still fills), BUY exit → slightly above
          let finalOrderType, finalPrice, finalTrigger;
          const isNFO = (wU.exchange||"NSE").match(/NFO|MCX/);
          const slipPct = isNFO ? 0.015 : 0.005; // 1.5% options, 0.5% equity

          if(hit === "TP") {
            finalOrderType = "LIMIT";
            finalPrice     = roundToTick(exitPrice, tick);
            finalTrigger   = 0;
          } else {
            // SL — LIMIT with slippage so it fills immediately at market
            finalOrderType = "LIMIT";
            finalPrice     = exitSide==="SELL"
              ? roundToTick(Math.max(exitPrice*(1-slipPct), tick), tick)
              : roundToTick(exitPrice*(1+slipPct), tick);
            finalTrigger   = 0;
          }

          // Validate — never send price=0 or NaN
          if(!finalPrice || isNaN(finalPrice) || finalPrice<=0) {
            showToastRef.current?.(`⚠ ${wU.symbol} exit skipped — invalid price ₹${exitPrice}`,"warn");
            continue;
          }

          const res = await apiFetch("/order/place",{
            method:"POST", headers:{"Content-Type":"application/json"},
            body: JSON.stringify({
              tradingsymbol:    wU.symbol,
              exchange:         wU.exchange||"NSE",
              transaction_type: exitSide,
              quantity:         exitQty,
              order_type:       finalOrderType,
              price:            finalPrice,
              trigger_price:    finalTrigger,
              product,
              tag: `AutoExit_${hit}`,
            })
          });

          if(!res.order_id) {
            const errMsg = res.message||res.error||res.detail||JSON.stringify(res);
            showToastRef.current?.(`⚠ ${wU.symbol} exit failed: ${errMsg}`, "error");
            continue;
          }
          const pnlEst = ((exitRaw - parseFloat(wU.entryPrice||0)) *
            (wU.side==="BUY"?1:-1) * exitQty).toFixed(0);
          const trailNote = wU.trailEnabled ? ` [Trail ₹${wU.slPrice}]` : "";
          const pnlSign   = parseInt(pnlEst)>=0 ? "+" : "";
          const msg = `${hit==="TP"?"🟢 TP":"🔴 SL"} ${wU.symbol} @ ₹${exitPrice} | P&L ₹${pnlEst}${trailNote}`;
          showToastRef.current?.(msg, hit==="TP"?"ok":"error");
          // Rich Telegram alert for TP/SL exit
          const tgMsg = hit==="TP"
            ? `✅ <b>TARGET HIT — ${wU.symbol}</b>\nExit: ₹${exitPrice} · Qty: ${exitQty}\n<b>P&L: ${pnlSign}₹${pnlEst}</b>\nEntry ₹${wU.entryPrice} → TP ₹${wU.tpPrice}`
            : `🛑 <b>STOP LOSS HIT — ${wU.symbol}</b>\nExit: ₹${exitPrice} · Qty: ${exitQty}\n<b>P&L: ${pnlSign}₹${pnlEst}</b>\nEntry ₹${wU.entryPrice} → SL ₹${exitPrice}${trailNote}`;
          if(sendTgRef.current) sendTgRef.current(tgMsg, {allow:true});  // TP/SL exits allowed to Telegram
          else if(tgChatRef.current) apiFetch("/alert/telegram",{
            method:"POST",headers:{"Content-Type":"application/json"}, _tgAllow:true,
            body:JSON.stringify({message:tgMsg,bot_token:tgTokenRef.current||"",chat_id:tgChatRef.current})
          }).catch(()=>{});
          triggered.push({...wU, status:"TRIGGERED", triggeredAt:now, triggeredType:hit,
            exitOrderId:res.order_id, exitPrice, ltpAtTrigger:ltp,
            pnlEst, qtyUsed:exitQty});
          // ── Close Firebase journal entry ─────────────────────────────────
          if(journalRef.current && wU.journalId) {
            journalRef.current(wU.journalId, exitPrice, hit);
          }
        }catch(e){
          showToastRef.current?.(`Auto-exit failed ${wU.symbol}: ${e.message}`, "error");
          triggered.push({...wU, status:"ERROR", triggeredAt:now, triggeredType:hit});
        }
      }

      // ── Apply all updates atomically ──────────────────────────────────────
      if(toClose.length||triggered.length||trailed.length){
        setWatchers(prev=>prev.map(w=>{
          const c=toClose.find(x=>x.id===w.id);   if(c) return c;
          const t=triggered.find(x=>x.id===w.id); if(t) return t;
          const r=trailed.find(x=>x.id===w.id);   if(r) return r;
          return w;
        }));
      }
    }, 5000);
    return ()=>clearInterval(interval);
  },[]);
}

// Auto-TP panel — shown inside Orders tab as a section
function AutoTPPanel({watchers, setWatchers, onEdit}) {
  // Only show WATCHING (confirmed open position)
  const watching  = watchers.filter(w=>w.status==="WATCHING");
  // Auto-trades placed but not yet filled — SL arms the moment they fill
  const pending   = watchers.filter(w=>w.status==="PENDING_FILL");
  // History: TP hit, SL hit, manual exit, cancelled (exclude pending)
  const triggered = watchers.filter(w=>w.status!=="WATCHING"&&w.status!=="PENDING_FILL").slice(0,30);

  const remove = (id)=>setWatchers(p=>p.filter(w=>w.id!==id));
  const cancel = (id)=>setWatchers(p=>p.map(w=>w.id===id?{...w,status:"CANCELLED"}:w));

  const statusLabel = (w)=>{
    if(w.status==="TRIGGERED"&&w.triggeredType==="TP") return {l:"✓ TP HIT",   c:"#2ed47a"};
    if(w.status==="TRIGGERED"&&w.triggeredType==="SL") return {l:"✗ SL HIT",   c:"#ff5b6c"};
    if(w.status==="TRIGGERED")                         return {l:"⚡ EXITED",   c:"#e8b339"};
    if(w.status==="CANCELLED_POSITION_CLOSED")         return {l:"👤 MANUAL EXIT",c:"#8ea2bf"};
    if(w.status==="EXITED_TP")                         return {l:"✓ TP HIT",   c:"#2ed47a"};
    if(w.status==="EXITED_SL")                         return {l:"✗ SL HIT",   c:"#ff5b6c"};
    if(w.status==="CANCELLED")                         return {l:"✕ CANCELLED",c:"#5a728f"};
    if(w.status==="ERROR")                             return {l:"⚠ ERROR",    c:"#ff5b6c"};
    return {l:w.status, c:"#8ea2bf"};
  };

  if(watchers.length===0) return(
    <div style={{textAlign:"center",padding:"24px 0",color:"#8ea2bf",fontSize:12}}>
      No auto-exit watchers. Enable ⚡ Auto Exit when placing an order.
    </div>
  );

  return(
    <div>
      {pending.length>0&&(
        <>
          <div style={{fontSize:12,color:"#e8b339",letterSpacing:2,fontWeight:700,padding:"8px 0 6px"}}>
            ⏳ PENDING FILL ({pending.length}) — SL arms the moment the order fills
          </div>
          {pending.map(w=>(
            <div key={w.id} style={{background:"#0f1d30",border:"1px solid rgba(232,179,57,0.35)",
              borderLeft:"3px solid #e8b339",borderRadius:0,padding:"8px 14px",marginBottom:8}}>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <span style={{fontSize:12,fontWeight:700,padding:"2px 6px",
                  background:w.side==="BUY"?"rgba(22,163,74,0.15)":"rgba(220,38,38,0.15)",
                  color:w.side==="BUY"?"#2ed47a":"#ff5b6c"}}>{w.side}</span>
                <span style={{fontSize:13,fontWeight:700,color:"#e7edf5"}}>{w.symbol}</span>
                <span style={{fontSize:10,padding:"2px 6px",background:"rgba(232,179,57,0.15)",
                  color:"#e8b339",fontWeight:700}}>● AWAITING FILL</span>
              </div>
              <div style={{fontSize:10,color:"#8ea2bf",marginTop:3}}>
                Qty: {w.qty} · Limit: ₹{w.entryPrice} · SL ₹{w.slPrice} · TP ₹{w.tpPrice||"trail"}
              </div>
            </div>
          ))}
        </>
      )}
      {watching.length>0&&(
        <>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0 6px"}}>
            <div style={{fontSize:12,color:"#e8b339",letterSpacing:2,fontWeight:700}}>
              WATCHING ({watching.length})
            </div>
            {watching.length>0&&(
              <div style={{display:"flex",alignItems:"center",gap:4,
                background:"rgba(22,163,74,0.08)",border:"1px solid rgba(63,185,80,0.2)",
                borderRadius:0,padding:"2px 8px"}}>
                <div style={{width:5,height:5,borderRadius:"50%",background:"#2ed47a",
                  animation:"pulse 2s ease-in-out infinite"}}/>
                <span style={{fontSize:11,color:"#2ed47a",fontWeight:700}}>VPS ACTIVE 24/7</span>
              </div>
            )}
          </div>
          {watching.map(w=>{
            // Calculate current trailing SL price from HWM
            const hwm      = parseFloat(w.highWaterMark||w.entryPrice||0);
            const trailPct = parseFloat(w.trailPct||0);
            const trailSL  = w.trailEnabled && hwm>0 && trailPct>0
              ? w.side==="BUY"
                ? (hwm*(1-trailPct/100)).toFixed(2)
                : (hwm*(1+trailPct/100)).toFixed(2)
              : null;
            const hwmMoved = hwm > 0 && hwm !== parseFloat(w.entryPrice||0);

            // P&L estimate
            const currentPrice = parseFloat(w.highWaterMark||w.entryPrice||0);
            const pnlPct = w.entryPrice>0
              ? ((currentPrice-w.entryPrice)/w.entryPrice*100*(w.side==="BUY"?1:-1)).toFixed(1)
              : 0;

            return(
              <div key={w.id} style={{background:"#0f1d30",
                border:`1px solid ${w.trailEnabled?"rgba(96,165,250,0.35)":"rgba(0,212,170,0.3)"}`,
                borderLeft:`3px solid ${w.trailEnabled?"#49c2e4":"#e8b339"}`,
                borderRadius:0,padding:"10px 14px",marginBottom:8}}>

                {/* Header */}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                  <div>
                    <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:3}}>
                      <span style={{fontSize:12,fontWeight:700,padding:"2px 6px",borderRadius:0,
                        background:w.side==="BUY"?"rgba(22,163,74,0.15)":"rgba(220,38,38,0.15)",
                        color:w.side==="BUY"?"#2ed47a":"#ff5b6c"}}>{w.side}</span>
                      <span style={{fontSize:13,fontWeight:700,color:"#e7edf5"}}>{w.symbol}</span>
                      <span style={{fontSize:10,padding:"2px 6px",borderRadius:0,
                        background:"rgba(0,212,170,0.12)",color:"#2ed47a",fontWeight:700}}>
                        ● WATCHING
                      </span>
                    </div>
                    <div style={{fontSize:10,color:"#8ea2bf"}}>
                      Qty: {w.qty} · Entry: ₹{w.entryPrice}
                      {hwmMoved&&<span style={{color:pnlPct>0?"#2ed47a":"#ff5b6c",marginLeft:6,fontWeight:700}}>
                        {pnlPct>0?"+":""}{pnlPct}% from entry
                      </span>}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={()=>onEdit(w)}
                      style={{background:"rgba(96,165,250,0.1)",border:"1px solid rgba(96,165,250,0.3)",
                        borderRadius:0,padding:"3px 8px",color:"#49c2e4",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>
                      ✏ Edit
                    </button>
                    <button onClick={()=>cancel(w.id)}
                      style={{background:"rgba(220,38,38,0.08)",border:"1px solid rgba(248,81,73,0.25)",
                        borderRadius:0,padding:"3px 8px",color:"#ff5b6c",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>
                      ✕
                    </button>
                  </div>
                </div>

                {/* TP / SL / Trail row */}
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {w.tpEnabled&&(
                    <div style={{background:"rgba(34,197,94,0.08)",border:"1px solid rgba(34,197,94,0.25)",
                      borderRadius:0,padding:"5px 10px",flex:1,minWidth:80,textAlign:"center"}}>
                      <div style={{fontSize:9,color:"#2ed47a",fontWeight:700,letterSpacing:1,marginBottom:2}}>TARGET</div>
                      <div style={{fontSize:13,fontWeight:700,color:"#2ed47a"}}>₹{w.tpPrice}</div>
                      <div style={{fontSize:10,color:"#2ed47a",opacity:0.7}}>+{w.tpPct||((w.tpPrice-w.entryPrice)/w.entryPrice*100).toFixed(1)}%</div>
                    </div>
                  )}
                  {w.slEnabled&&!w.trailEnabled&&(
                    <div style={{background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.25)",
                      borderRadius:0,padding:"5px 10px",flex:1,minWidth:80,textAlign:"center"}}>
                      <div style={{fontSize:9,color:"#ff5b6c",fontWeight:700,letterSpacing:1,marginBottom:2}}>STOP LOSS</div>
                      <div style={{fontSize:13,fontWeight:700,color:"#ff5b6c"}}>₹{w.slPrice}</div>
                      <div style={{fontSize:10,color:"#ff5b6c",opacity:0.7}}>-{w.slPct||((w.entryPrice-w.slPrice)/w.entryPrice*100).toFixed(1)}%</div>
                    </div>
                  )}
                  {w.trailEnabled&&(
                    <div style={{background:"rgba(96,165,250,0.08)",border:"1px solid rgba(96,165,250,0.35)",
                      borderRadius:0,padding:"5px 10px",flex:1,minWidth:100,textAlign:"center"}}>
                      <div style={{fontSize:9,color:"#49c2e4",fontWeight:700,letterSpacing:1,marginBottom:2}}>
                        🔄 TRAIL SL {w.trailPct}%
                      </div>
                      <div style={{fontSize:13,fontWeight:700,color:"#49c2e4"}}>
                        ₹{trailSL||w.slPrice||"—"}
                      </div>
                      {hwmMoved&&(
                        <div style={{fontSize:10,color:"#5a728f"}}>
                          HWM ₹{hwm.toFixed(2)}
                        </div>
                      )}
                      {!hwmMoved&&(
                        <div style={{fontSize:10,color:"#5a728f"}}>Moves with price ↑</div>
                      )}
                    </div>
                  )}
                </div>

                {/* Trail SL explanation when price has moved */}
                {w.trailEnabled&&trailSL&&hwmMoved&&(
                  <div style={{marginTop:6,fontSize:10,color:"#5a728f",lineHeight:1.6,
                    borderTop:"1px solid #1a2c44",paddingTop:6}}>
                    Entry ₹{w.entryPrice} → Peak ₹{hwm.toFixed(2)} → Trail SL now at{" "}
                    <strong style={{color:"#49c2e4"}}>₹{trailSL}</strong>
                    {" "}(locks in {((hwm-parseFloat(trailSL))/w.entryPrice*100*(w.side==="BUY"?1:-1)).toFixed(1)}% profit buffer)
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {triggered.length>0&&(
        <>
          <div style={{fontSize:12,color:"#8ea2bf",letterSpacing:2,fontWeight:700,padding:"10px 0 6px"}}>
            HISTORY ({triggered.length})
          </div>
          {triggered.map(w=>{
            const sl = statusLabel(w);
            const isWin = sl.l.includes("TP");
            const isMn  = sl.l.includes("MANUAL");
            return (
            <div key={w.id} style={{background:"#0f1d30",
              border:`1px solid ${isWin?"rgba(63,185,80,0.2)":isMn?"rgba(139,148,158,0.15)":"rgba(248,81,73,0.2)"}`,
              borderLeft:`3px solid ${sl.c}`,
              borderRadius:0,padding:"10px 14px",marginBottom:6}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:2}}>
                    <span style={{fontSize:12,fontWeight:700,color:sl.c,
                      background:`${sl.c}18`,padding:"1px 6px",borderRadius:0}}>
                      {sl.l}
                    </span>
                    <span style={{fontSize:12,fontWeight:700,color:"#e7edf5"}}>{w.symbol}</span>
                    <span style={{fontSize:12,color:w.side==="BUY"?"#2ed47a":"#ff5b6c"}}>{w.side}</span>
                  </div>
                  <div style={{fontSize:10,color:"#8ea2bf"}}>
                    Entry ₹{w.entryPrice} · Qty {w.qty}
                    {w.exitPrice&&` · Exit ₹${w.exitPrice}`}
                    {w.triggeredAt&&` · ${new Date(w.triggeredAt).toLocaleString("en-IN",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}`}
                  </div>
                </div>
                <div style={{textAlign:"right"}}>
                  {w.pnlEst!=null&&(
                    <div style={{fontSize:13,fontWeight:700,
                      color:parseFloat(w.pnlEst)>=0?"#2ed47a":"#ff5b6c"}}>
                      {parseFloat(w.pnlEst)>=0?"+":""}₹{w.pnlEst}
                    </div>
                  )}
                  <button onClick={()=>remove(w.id)}
                    style={{background:"none",border:"none",color:"#23405f",
                      cursor:"pointer",fontSize:11,marginTop:2}}>✕</button>
                </div>
              </div>
            </div>
          );})}
        </>
      )}
    </div>
  );
}

// Edit watcher modal
function EditWatcherModal({watcher, onSave, onClose}) {
  const [tpEnabled,setTpEnabled] = useState(watcher.tpEnabled);
  const [tpPct,setTpPct]         = useState(String(watcher.tpPct));
  const [slEnabled,setSlEnabled] = useState(watcher.slEnabled);
  const [slPct,setSlPct]         = useState(String(watcher.slPct));

  const ep = watcher.entryPrice;
  const newTpPrice = ep>0?(watcher.side==="BUY"?ep*(1+parseFloat(tpPct||0)/100):ep*(1-parseFloat(tpPct||0)/100)).toFixed(2):"—";
  const newSlPrice = ep>0?(watcher.side==="BUY"?ep*(1-parseFloat(slPct||0)/100):ep*(1+parseFloat(slPct||0)/100)).toFixed(2):"—";

  const save=()=>{
    onSave({...watcher,
      tpEnabled, tpPct:parseFloat(tpPct), tpPrice:parseFloat(newTpPrice)||watcher.tpPrice,
      slEnabled, slPct:parseFloat(slPct), slPrice:parseFloat(newSlPrice)||watcher.slPrice,
    });
    onClose();
  };

  const inp={background:"#0b1624",border:"1px solid #23405f",borderRadius:0,padding:"7px 10px",color:"#e7edf5",fontFamily:"inherit",fontSize:12,outline:"none",width:"80px",textAlign:"center"};
  const tog=(on,color)=>({width:34,height:18,borderRadius:9,background:on?color:"#23405f",cursor:"pointer",position:"relative",transition:"background 0.2s",flexShrink:0});

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.78)",zIndex:600,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
      onClick={(e)=>{ if(e.target===e.currentTarget) return; /* only close via cancel button */ }}>
      <div style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:12,padding:24,width:320}}
        onClick={e=>e.stopPropagation()}>
        <div style={{fontSize:14,fontWeight:700,color:"#e7edf5",marginBottom:4}}>Edit Auto Exit</div>
        <div style={{fontSize:11,color:"#8ea2bf",marginBottom:18}}>{watcher.symbol} · {watcher.side} · Entry ₹{watcher.entryPrice}</div>

        {/* TP row */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,background:"#0b1624",padding:"10px 12px",borderRadius:0}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <div onClick={()=>setTpEnabled(p=>!p)} style={tog(tpEnabled,"#2ed47a")}>
              <div style={{position:"absolute",top:2,left:tpEnabled?16:2,width:14,height:14,borderRadius:"50%",background:"#fff",transition:"left 0.2s"}}/>
            </div>
            <span style={{fontSize:11,color:tpEnabled?"#2ed47a":"#8ea2bf",fontWeight:600}}>Take Profit</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <input type="number" step="0.1" min="0.1" value={tpPct} onChange={e=>setTpPct(e.target.value)} disabled={!tpEnabled} style={{...inp,opacity:tpEnabled?1:0.4}}/>
            <span style={{fontSize:10,color:"#8ea2bf"}}>%</span>
            <span style={{fontSize:10,color:"#2ed47a",fontWeight:700,minWidth:60}}>→ ₹{newTpPrice}</span>
          </div>
        </div>

        {/* SL row */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20,background:"#0b1624",padding:"10px 12px",borderRadius:0}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <div onClick={()=>setSlEnabled(p=>!p)} style={tog(slEnabled,"#ff5b6c")}>
              <div style={{position:"absolute",top:2,left:slEnabled?16:2,width:14,height:14,borderRadius:"50%",background:"#fff",transition:"left 0.2s"}}/>
            </div>
            <span style={{fontSize:11,color:slEnabled?"#ff5b6c":"#8ea2bf",fontWeight:600}}>Stop Loss</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <input type="number" step="0.1" min="0.1" value={slPct} onChange={e=>setSlPct(e.target.value)} disabled={!slEnabled} style={{...inp,opacity:slEnabled?1:0.4}}/>
            <span style={{fontSize:10,color:"#8ea2bf"}}>%</span>
            <span style={{fontSize:10,color:"#ff5b6c",fontWeight:700,minWidth:60}}>→ ₹{newSlPrice}</span>
          </div>
        </div>

        <div style={{display:"flex",gap:10}}>
          <button onClick={onClose} style={{flex:1,background:"#1a2c44",border:"none",borderRadius:0,padding:"10px",color:"#8ea2bf",fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>Cancel</button>
          <button onClick={save} style={{flex:2,background:"linear-gradient(135deg,#92400e,#92400e)",border:"none",borderRadius:0,padding:"10px",color:"#fff",fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer"}}>Save Changes</button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// AUTO F&O TRADE TAB
// Signal → find ATM CE/PE (min 15d expiry) → place order → TP/SL watcher
// ══════════════════════════════════════════════════════════════════════════════



// ══════════════════════════════════════════════════════════════════════════════
// MCX SCAN TAB — Commodity Scanner with Price Action + Demand/Supply Zones
// ══════════════════════════════════════════════════════════════════════════════

function McxTab({isAdmin=false, canTrade=false, scanLimit=0, scansUsed=0, checkScanLimit=()=>true, onTrade, onAddAutoTP, onShowToast, authed, signalParams, setSignalParams, bgEngineEnabled, setBgEngineEnabled, setBgEngineCfg, setBgAtMode, bgLog, setBgLog, onAddNote, onAddVirtual, onJournalEntry=null, onFireAlerts=null, sendTgAlert=null, vpsAutoTradeOn=false}) {
  const SCAN_LABELS = {1:"TSI Cross",2:"RSI Extreme",3:"Vol+RSI",4:"EMA Cross",5:"📐 Patterns",8:"D/S Zone"};

  // ── AI Panel ──────────────────────────────────────────────────────────────
  const { aiResults:mAiRes, aiLoading:mAiLoad, aiError:mAiErr,
          aiVisible:mAiVis, setAiVisible:setMAiVis, runAIScan:runMAI,
          autoTrade:mAutoTrade, setAutoTrade:setMAutoTrade,
          minAutoConf:mMinConf, setMinAutoConf:setMMinConf, autoFired:mAutoFired,
          marginPct:mMargPct, setMarginPct:setMMargPct,
          maxDaily:mMaxDay, setMaxDaily:setMMaxDay,
          maxScans:mMaxScans, setMaxScans:setMMaxScans,
          scanCount:mScanCount, balance:mBalance
        } = useAIPanel({authed, signalParams, exchange:"MCX"});

  // ── MCX Watchlists (multi-select) ─────────────────────────────────────────
  const [customSymbols, setCustomSymbols] = useLocalStorage("mcx_custom_symbols", MCX_SYMBOLS);
  const [showWatchlistEditor, setShowWatchlistEditor] = useState(false);
  const [watchlistInput, setWatchlistInput] = useState("");
  const [mcxSelLists, setMcxSelLists] = useLocalStorage("mcx_sel_lists", ["DEFAULT"]);
  const MCX_NAMED = {"DEFAULT": MCX_SYMBOLS, "ENERGY":["CRUDEOIL","NATURALGAS"], "METALS":["GOLD","SILVER","COPPER","ZINC","LEAD","ALUMINIUM"], "AGRI":["COTTON","KAPAS","CARDAMOM"]};
  const scanSymbols = useMemo(()=>{
    if(mcxSelLists.includes("CUSTOM")) return customSymbols.length>0?customSymbols:MCX_SYMBOLS;
    const seen = new Set();
    return mcxSelLists.flatMap(n=>MCX_NAMED[n]||[]).filter(s=>{ if(seen.has(s))return false;seen.add(s);return true; });
  },[mcxSelLists, customSymbols]);

  // ── view: "scan" | "chain" ────────────────────────────────────────────────
  const [view, setView] = useState("scan");

  // ── Scan state ────────────────────────────────────────────────────────────
  const [scanning,    setScanning]    = useState(false);
  const [scanResults, setScanResults] = useState([]);
  const [scanError,   setScanError]   = useState(null);
  const [lastScan,    setLastScan]    = useState(null);
  const [scanTimeframeMcx, setScanTimeframeMcx] = useState(null);
  const [activeScan,  setActiveScan]  = useState([1,2,3,4,5,8]); // all scans incl. Price Action + Zone
  const [timeframe,   setTimeframe]   = useState("1day");
  const [liveMode,    setLiveMode]    = useState(false);
  const [showSRPanel, setShowSRPanel] = useState(false);
  const liveQMcxRef = useRef(null);
  const [liveQuotesMcx, setLiveQuotesMcx] = useState({});

  // ── Auto-Virtual for MCX ──────────────────────────────────────────────────
  const [autoVirtual,    setAutoVirtual]    = useLocalStorage('mcx_auto_virtual', false);
  const [autoVirtualMin, setAutoVirtualMin] = useLocalStorage('mcx_auto_virtual_min', 2);

  // ── Live price poll every 5s for MCX scan results ─────────────────────────
  useEffect(()=>{
    if(liveQMcxRef.current) clearInterval(liveQMcxRef.current);
    const syms = scanResults.map(r=>r.resolved_symbol||r.symbol).filter(Boolean);
    if(!syms.length || !authed) return;
    const fetchQ = async()=>{
      try {
        const keys = syms.map(s=>`MCX:${s}`).join(",");
        const q = await apiFetch(`/quotes?symbols=${encodeURIComponent(keys)}`);
        setLiveQuotesMcx(q);
      } catch {}
    };
    fetchQ();
    liveQMcxRef.current = setInterval(fetchQ, 5000);
    return ()=>clearInterval(liveQMcxRef.current);
  },[scanResults, authed]);

  // ── Auto-trade state ─────────────────────────────────────────────────────
  const [atEnabled,   setAtEnabled]   = useState(false);
  const [atMode,      setAtMode]      = useLocalStorage('nse_pro_mcx_at_mode', "futures");
  const [showSignalModal, setShowSignalModal] = useState(false);
  const [atCfg,       setAtCfg]       = useLocalStorage('nse_pro_mcx_at_cfg', {
    capital:    50000, minSignals: 3, conditions: [1,3],
    tpPct: 2.0, slPct: 1.0, cooldown: 300, minDays: 15,
    optTpPct: 30, optSlPct: 20,
    dailyLimit:   5,
    maxTradesPct: 20,
    autoSize:  true,
  });
  // Migrate old conditions — upgrade [1,3] to full set
  useEffect(()=>{
    if(!atCfg.conditions || atCfg.conditions.length <= 2){
      setAtCfg(p=>({...p, conditions:[1,2,3,4,5,8], minSignals:2}));
    }
  },[]);
  const [scanMinDays, setScanMinDays] = useLocalStorage('nse_pro_mcx_min_days', 7); // expiry filter for scan
  const [mcxResultView, setMcxResultView] = useState("scan"); // "scan" | "ai"
  const [mcxViewMode,   setMcxViewMode]   = useState("grid"); // "grid" | "table"
  const [mcxStratPopup, setMcxStratPopup] = useState(null);
  const [chartModal,    setChartModal]    = useState(null); // {symbol,exchange}
  const [atLog,   setAtLog]   = useState([]);
  const [atFired, setAtFired] = useState({});
  const [dailyTrades,setDailyTrades] = useState({date:"",count:0});
  const dailyTradesRef = useRef({date:"",count:0});
  useEffect(()=>{ dailyTradesRef.current = dailyTrades; },[dailyTrades]);
  const atCfgRef    = useRef(atCfg);
  const atFiredRef  = useRef(atFired);
  const atEnabledRef= useRef(false);
  const atModeRef   = useRef(atMode);
  const [atValidate, setAtValidate] = useLocalStorage('mcx_at_validate', true);  // auto-trade only GO-validated signals
  const [atPaper,    setAtPaper]    = useLocalStorage('mcx_at_paper', false);    // paper trades instead of real orders
  const atValidateRef = useRef(true);
  const atPaperRef    = useRef(false);
  const vpsATRef      = useRef(false);
  useEffect(()=>{ vpsATRef.current = vpsAutoTradeOn; },[vpsAutoTradeOn]);
  useEffect(()=>{ atCfgRef.current   = atCfg;    },[atCfg]);
  useEffect(()=>{ atFiredRef.current = atFired;   },[atFired]);
  useEffect(()=>{ atEnabledRef.current = atEnabled;},[atEnabled]);
  useEffect(()=>{ atModeRef.current  = atMode;    },[atMode]);
  useEffect(()=>{ atValidateRef.current = atValidate; },[atValidate]);
  useEffect(()=>{ atPaperRef.current = atPaper; },[atPaper]);

  // Record validated-GO MCX signals as paper trades instead of real orders.
  function mcxPaperTrade(resList){
    if(!onAddVirtual) return;
    let n=0;
    (resList||[]).forEach(r=>{
      const sigs=(r.signals||[]).filter(s=>activeScan.includes(s.scan));
      const side=sigs.some(s=>s.type==="BUY")?"BUY":"SELL";
      const price=parseFloat(r.price||r.ltp||0);
      if(!price) return;
      onAddVirtual({ symbol:r.base_symbol||r.symbol, exchange:"MCX", side, price, qty:1,
        product:"NRML", tab_mode:"mcx", source:"auto",
        signals:sigs.map(s=>s.label).join(" · "), signal:sigs });
      n++;
    });
    if(n>0&&onShowToast) onShowToast(`📝 Paper: recorded ${n} validated GO MCX trade(s)`,"info");
  }

  // Validate signalled MCX results and return only the ones that come back GO.
  // On validation failure returns [] (safer to skip than trade unvalidated).
  async function mcxValidateForAT(results){
    const signalled = (results||[]).filter(r=>!r.error && (r.signals||[]).some(s=>activeScan.includes(s.scan)));
    if(signalled.length===0) return [];
    const items = signalled.map(r=>{
      const sigs=(r.signals||[]).filter(s=>activeScan.includes(s.scan));
      const dir=sigs.some(s=>s.type==="BUY")?"BUY":"SELL";
      return {symbol:r.base_symbol||r.symbol, exchange:"MCX", timeframe, direction:dir, signal_price:r.price||r.ltp||null};
    });
    try{
      const out = await apiFetch("/scan/validate/batch",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({items,use_ai:true})});
      const goSet = new Set((out.results||[]).filter(v=>!v.error&&v.verdict==="GO").map(v=>v.symbol));
      return signalled.filter(r=>goSet.has(r.base_symbol||r.symbol));
    }catch(e){
      if(onShowToast) onShowToast("MCX auto-trade validation failed — trades skipped","warn");
      return [];
    }
  }

  // ── Chain state (ATM viewer) ──────────────────────────────────────────────
  const [instruments,   setInstruments]   = useState(null);
  const [instrLoading,  setInstrLoading]  = useState(false);
  const [instrError,    setInstrError]    = useState(null);
  const [selected,      setSelected]      = useState(null);
  const [typeFilter,    setTypeFilter]    = useState("FUT");
  const [expiryFilter,  setExpFilter]     = useState("ALL");
  const [chainQuotes,   setChainQuotes]   = useState({});
  const [chainQL,       setChainQL]       = useState(false);
  const [chainSearch,   setChainSearch]   = useState("");

  // ── Load MCX chain ────────────────────────────────────────────────────────
  const loadChain = useCallback(async()=>{
    setInstrLoading(true); setInstrError(null);
    try {
      const data = await apiFetch("/mcx/instruments");
      setInstruments(data);
      const keys = Object.keys(data.commodities||{});
      if(keys.length>0 && !selected) setSelected(keys.sort()[0]);
    } catch(e){ setInstrError(e.message); }
    finally{ setInstrLoading(false); }
  },[]);

  const fetchChainQuotes = useCallback(async(contracts)=>{
    if(!contracts||contracts.length===0) return;
    setChainQL(true);
    try {
      const syms = contracts.slice(0,20).map(c=>`MCX:${c.tradingsymbol}`).join(",");
      const data = await apiFetch(`/quotes?symbols=${encodeURIComponent(syms)}`);
      setChainQuotes(prev=>({...prev,...data}));
    } catch{}
    finally{ setChainQL(false); }
  },[]);

  useEffect(()=>{ if(view==="chain") loadChain(); },[view]);

  const commodities = instruments?.commodities||{};
  const allNames    = Object.keys(commodities).sort();
  const displayedContracts = selected
    ? (commodities[selected]||[])
        .filter(c=>typeFilter==="ALL"||c.instrument_type===typeFilter)
        .filter(c=>expiryFilter==="ALL"||c.expiry===expiryFilter)
        .filter(c=>!chainSearch||c.tradingsymbol.includes(chainSearch.toUpperCase()))
    : [];
  const chainExpiries = selected ? [...new Set((commodities[selected]||[]).map(c=>c.expiry))].sort():[];
  useEffect(()=>{ if(displayedContracts.length>0) fetchChainQuotes(displayedContracts); },[selected,typeFilter,expiryFilter]);

  // ── MCX Scan ──────────────────────────────────────────────────────────────
  const runScan = useCallback(async()=>{
    if(!authed) return;
    if(!checkScanLimit()) {
      setScanError(`Daily scan limit reached (${scansUsed}/${scanLimit}). Resets at midnight.`);
      return;
    }
    setScanning(true); setScanError(null);
    try {
      // Single-TF scan with full signal set (TSI + RSI + Vol + EMA + Patterns + D/S Zone)
      const res = await apiFetch("/scan",{
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          symbols:          scanSymbols,
          exchange:         "MCX",
          timeframe,
          tech_weight:      0.7,
          news_enabled:     false,
          signal_params:    signalParams,
          min_days_expiry:  scanMinDays || 7,
        })
      });
      const results = res.results || [];

      const ts = `${new Date().toLocaleTimeString("en-IN")} · ${timeframe}`;
      setLastScan(ts); setScanTimeframeMcx(timeframe);
      setScanResults(results);

      // ── Fire Telegram alerts for MCX signals ───────────────────────────
      if(onFireAlerts) {
        const mcxAlerts = (results||[])
          .filter(r=>(r.signals||[]).some(s=>activeScan.includes(s.scan)))
          .flatMap(r=>{
            const zone = (r.demandLow && r.demandHigh) || (r.supplyLow && r.supplyHigh)
              ? {
                  demand: r.demandLow && r.demandHigh ? {low:r.demandLow, high:r.demandHigh} : null,
                  supply: r.supplyLow && r.supplyHigh ? {low:r.supplyLow, high:r.supplyHigh} : null,
                } : null;
            return (r.signals||[])
              .filter(s=>activeScan.includes(s.scan))
              .map(s=>({symbol:r.symbol, price:r.price||r.ltp||0, signal:s, exchange:'MCX', timeframe, zone}));
          });
        if(mcxAlerts.length>0) onFireAlerts(mcxAlerts);
      }

      // ── Auto-Virtual: add MCX stocks with enough signals to Virtual Tab ──
      if(autoVirtual && onAddVirtual) {
        autoVirtualFromScan(results, onAddVirtual, autoVirtualMin, "mcx", "MCX", onShowToast, activeScan);
      }

      // ── Auto-trade engine (validate first when enabled) ────────────────
      if(!atEnabledRef.current) return;
      if(vpsATRef.current){
        if(onShowToast) onShowToast("🖥️ VPS is auto-trading MCX — desktop AT skipped (avoids double orders)", "warn");
        return;
      }
      let atResults = results;
      if(atValidateRef.current){
        atResults = await mcxValidateForAT(results);
        if(!atResults || atResults.length===0){
          if(onShowToast) onShowToast("MCX auto-trade: no signals validated GO — no trade","info");
          return;
        }
      }
      // Paper mode — record validated trades, place no real orders
      if(atPaperRef.current){ mcxPaperTrade(atResults); return; }
      const cfg   = atCfgRef.current;
      const fired = atFiredRef.current;
      const mode  = atModeRef.current;
      const now   = Date.now();

      // ── Daily limit check ──────────────────────────────────────────────
      const mcxDaily = checkDailyLimit(dailyTradesRef, cfg.dailyLimit||0);
      if(!mcxDaily.ok) {
        if(onShowToast) onShowToast(`MCX daily limit ${cfg.dailyLimit} reached — AT paused`,"error");
        return;
      }

      for(const stock of (atResults||[])) {
        if(!stock.price||stock.error) continue;
        if((now-(fired[stock.symbol]||0))/1000 < cfg.cooldown) continue;
        if(!checkDailyLimit(dailyTradesRef, cfg.dailyLimit||0).ok) break;

        const conditions  = cfg.conditions || [1,2,3,4,5,8];
        const minRequired = cfg.minSignals||1;
        const matchingSigs = (stock.signals||[]).filter(s=>
          conditions.includes(s.scan) && activeScan.includes(s.scan)
        );
        if(matchingSigs.length < minRequired) continue;

        const buySigs  = matchingSigs.filter(s=>s.type==="BUY").length;
        const sellSigs = matchingSigs.filter(s=>s.type==="SELL").length;

        // ── Skip mixed signals — must all agree on direction ──────────────
        if(buySigs > 0 && sellSigs > 0) {
          const ts = new Date().toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"});
          setAtLog(p=>[{id:`${ts}-${stock.symbol}`,time:ts,symbol:stock.symbol,
            side:"MIXED",mode:"MCX",status:"SKIP_MIXED",
            note:`Conflicting: ${buySigs} BUY vs ${sellSigs} SELL signals`},...p].slice(0,200));
          continue;
        }

        // Skip if equal — no clear direction
        if(buySigs === sellSigs) {
          const ts = new Date().toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"});
          setAtLog(p=>[{id:`${ts}-${stock.symbol}`,time:ts,symbol:stock.symbol,
            side:"?",mode:"MCX",status:"SKIP_EQUAL",
            note:`Equal signals: ${buySigs}B = ${sellSigs}S — no clear direction`},...p].slice(0,200));
          continue;
        }

        // Clear direction — more buys than sells or vice versa
        const side = buySigs > sellSigs ? "BUY" : "SELL";

        // ── Smart position sizing — commodity cash + collateral ─────────────
        const mcxMar = window._mcxMargins;
        const mcxAvailMargin = (parseFloat(mcxMar?.commodity?.available||0) + parseFloat(mcxMar?.commodity?.collateral||0)) || 0;
        const tradeCapital = cfg.autoSize && mcxAvailMargin > 0
          ? calcPositionSize(mcxAvailMargin, cfg.capital, cfg.maxTradesPct||20, stock.price)
          : cfg.capital;

        setAtFired(p=>({...p,[stock.symbol]:now}));

        try {
          if(mode==="futures") {
            const underlyingSymbol = stock.resolved_symbol || stock.symbol;
            const qty   = Math.max(1, Math.floor(tradeCapital / stock.price));
            const price = parseFloat(stock.price.toFixed(2));
            const tp    = parseFloat((price*(side==="BUY"?1+cfg.tpPct/100:1-cfg.tpPct/100)).toFixed(2));
            const sl    = parseFloat((price*(side==="BUY"?1-cfg.slPct/100:1+cfg.slPct/100)).toFixed(2));

            const res = await apiFetch("/order/place",{
              method:"POST",headers:{"Content-Type":"application/json"},
              body:JSON.stringify({tradingsymbol:underlyingSymbol,exchange:"MCX",
                transaction_type:side,quantity:qty,order_type:"LIMIT",
                price,trigger_price:0,product:"NRML",tag:"RABT-AUTO-MCX"})
            });
            if(onAddAutoTP) onAddAutoTP({id:`atp-mcx-${Date.now()}`,symbol:underlyingSymbol,
              exchange:"MCX",side,entryPrice:price,qty,
              tpEnabled:true,tpPct:cfg.tpPct,tpPrice:tp,
              slEnabled:true,slPct:cfg.slPct,slPrice:sl,
              status:"WATCHING",orderId:res.order_id,
              createdAt:new Date().toISOString(),triggeredAt:null,triggeredType:null,
              // Zone-aware TP/SL
              ...extractNearestZones(stock.zones, price),
              zoneTouched:'none',
            });
            if(onJournalEntry) onJournalEntry({
              orderId:res.order_id,symbol:underlyingSymbol,exchange:"MCX",
              side,qty,price,product:"NRML",source:"mcx_auto",tabMode:"mcx",
              tpPrice:tp,slPrice:sl,tpPct:cfg.tpPct,slPct:cfg.slPct,
              tag:"RABT-AUTO-MCX"});
            setAtLog(p=>[{id:`at-${Date.now()}`,time:new Date().toLocaleTimeString("en-IN"),
              symbol:stock.symbol,contract:underlyingSymbol,mode:"FUT",
              side,qty,price,tp,sl,signals:matchingSigs.map(s=>s.label).join(", "),
              status:"PLACED",orderId:res.order_id},...p].slice(0,200));
            if(onShowToast) onShowToast("🏭 MCX Auto: "+side+" "+underlyingSymbol+" @ ₹"+price);
            incrementDailyTrades(setDailyTrades, dailyTradesRef);

          } else {
            // Options mode — find ATM CE/PE on MCX
            const optSide = side==="BUY"?"BUY":"SELL";
            const foRes = await apiFetch("/fo/find_atm",{
              method:"POST",headers:{"Content-Type":"application/json"},
              body:JSON.stringify({symbol:stock.symbol,exchange:"MCX",
                signal_side:optSide,min_days:cfg.minDays})
            });
            if(!foRes.found){
              setAtLog(p=>[{id:`at-nf-${Date.now()}`,time:new Date().toLocaleTimeString("en-IN"),
                symbol:stock.symbol,mode:"OPT",status:"NO_CONTRACT",
                note:foRes.reason,signals:matchingSigs.map(s=>s.label).join(", ")},...p].slice(0,200));
              continue;
            }
            const contract    = foRes.contract;
            const optionPrice = contract.option_ltp||0;
            if(optionPrice<=0){
              setAtLog(p=>[{id:`at-nq-${Date.now()}`,time:new Date().toLocaleTimeString("en-IN"),
                symbol:stock.symbol,mode:"OPT",status:"NO_QUOTE",
                note:"Option LTP=0",signals:matchingSigs.map(s=>s.label).join(", ")},...p].slice(0,200));
              continue;
            }
            const lotSize  = contract.lot_size || 1;
            // Capital / (option premium × lot size) = number of lots, capped at 10
            const maxLots  = Math.max(1, Math.floor(cfg.capital / (optionPrice * lotSize)));
            const qty      = Math.min(maxLots, 10) * lotSize; // max 10 lots
            const tp       = parseFloat((optionPrice*(1+cfg.optTpPct/100)).toFixed(2));
            const sl       = parseFloat((optionPrice*(1-cfg.optSlPct/100)).toFixed(2));
            const optType  = side==="BUY"?"CE":"PE";

            const res = await apiFetch("/order/place",{
              method:"POST",headers:{"Content-Type":"application/json"},
              body:JSON.stringify({tradingsymbol:contract.tradingsymbol,exchange:"MCX",
                transaction_type:"BUY",quantity:qty,order_type:"LIMIT",
                price:optionPrice,trigger_price:0,product:"NRML",tag:"RABT-AUTO-MCX-OPT"})
            });
            if(onAddAutoTP) onAddAutoTP({id:`atp-mcxopt-${Date.now()}`,
              symbol:contract.tradingsymbol,exchange:"MCX",side:"BUY",
              entryPrice:optionPrice,qty,tpEnabled:true,tpPct:cfg.optTpPct,tpPrice:tp,
              slEnabled:true,slPct:cfg.optSlPct,slPrice:sl,status:"WATCHING",
              orderId:res.order_id,createdAt:new Date().toISOString(),
              triggeredAt:null,triggeredType:null,
              // Zone-aware TP/SL — use underlying stock price for zone ref
              ...extractNearestZones(stock.zones, stock.price||optionPrice),
              zoneTouched:'none',
            });
            setAtLog(p=>[{id:`at-${Date.now()}`,time:new Date().toLocaleTimeString("en-IN"),
              symbol:stock.symbol,contract:contract.tradingsymbol,mode:"OPT",
              optType,strike:contract.strike,expiry:contract.expiry,
              daysLeft:contract.days_to_expiry,side:"BUY",qty,
              price:optionPrice,tp,sl,
              signals:matchingSigs.map(s=>s.label).join(", "),
              status:"PLACED",orderId:res.order_id},...p].slice(0,200));
            if(onShowToast) onShowToast("🏭 MCX Opt: BUY "+contract.tradingsymbol+" @ ₹"+optionPrice);
            incrementDailyTrades(setDailyTrades, dailyTradesRef);
          }
        } catch(e){
          setAtLog(p=>[{id:`at-err-${Date.now()}`,time:new Date().toLocaleTimeString("en-IN"),
            symbol:stock.symbol,mode,status:"FAILED",error:e.message,
            signals:matchingSigs.map(s=>s.label).join(", ")},...p].slice(0,200));
        }
      }
    } catch(e){ setScanError(e.message); }
    finally{ setScanning(false); }
  },[authed, timeframe, activeScan]);

  // Live scan interval — restarts when timeframe changes
  const liveRef = useRef(null);
  useEffect(()=>{
    if(liveMode&&authed){ runScan(); liveRef.current=setInterval(runScan,60000); }
    else clearInterval(liveRef.current);
    return()=>clearInterval(liveRef.current);
  },[liveMode,authed,timeframe,runScan]);

  // ── Auto-rescan when timeframe changes (clear stale results) ──────────────
  const prevTfMcxRef = useRef(null);
  useEffect(()=>{
    if(prevTfMcxRef.current === null){ prevTfMcxRef.current = timeframe; return; }
    if(prevTfMcxRef.current === timeframe) return;
    prevTfMcxRef.current = timeframe;
    if(!authed) return;
    setScanResults([]); // clear stale results immediately
    runScan();          // rescan with new timeframe
  },[timeframe, authed]);

  const toggleScan = n=>setActiveScan(p=>p.includes(n)?p.filter(x=>x!==n):[...p,n]);
  const meta = MCX_BASE[selected]||{name:selected,unit:"",color:"#8ea2bf"};
  const withSignals = scanResults.filter(r=>(r.signals||[]).some(s=>activeScan.includes(s.scan)));

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",fontFamily:"inherit"}}>

      {/* ── Top bar ── */}
      <div style={{background:"#0b1624",borderBottom:"1px solid #1a2c44",padding:"8px 14px",display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
        {/* View toggle */}
        <div style={{display:"flex",background:"#07101c",border:"1px solid #1a2c44",borderRadius:0,overflow:"hidden",flexShrink:0}}>
          {[["scan","📡 Scan"],["chain","📋 F&O Chain"]].map(([v,l])=>(
            <button key={v} onClick={()=>setView(v)}
              style={{background:view===v?"rgba(0,212,170,0.15)":"transparent",border:"none",
                borderRight:"1px solid #1a2c44",padding:"5px 14px",
                color:view===v?"#e8b339":"#8ea2bf",fontFamily:"inherit",fontSize:10,fontWeight:600,cursor:"pointer"}}>
              {l}
            </button>
          ))}
        </div>

        {view==="scan"&&<>
          {/* Timeframe */}
          <div style={{display:"flex",background:"#07101c",border:"1px solid #1a2c44",borderRadius:0,overflow:"hidden"}}>
            {[["1day","1D"],["1hour","1H"],["30min","30m"],["15min","15m"]].map(([v,l])=>(
              <button key={v} onClick={()=>setTimeframe(v)}
                style={{background:timeframe===v?"rgba(0,212,170,0.2)":"transparent",border:"none",
                  borderRight:"1px solid #1a2c44",padding:"4px 10px",
                  color:timeframe===v?"#e8b339":"#8ea2bf",fontFamily:"inherit",fontSize:10,fontWeight:timeframe===v?700:400,cursor:"pointer"}}>
                {l}
              </button>
            ))}
          </div>
          {/* Stale warning */}
          {scanTimeframeMcx && scanTimeframeMcx!==timeframe && scanResults.length>0 && (
            <span style={{fontSize:12,color:"#ff5b6c",fontWeight:700,
              background:"rgba(248,81,73,0.1)",padding:"2px 7px",borderRadius:0,
              border:"1px solid rgba(248,81,73,0.3)",flexShrink:0}}>
              ⚠ Results are {scanTimeframeMcx} — click SCAN to update
            </span>
          )}

          {/* Signal filters — all scan types for MCX too */}
          <div style={{display:"flex",gap:2,flexWrap:"wrap",alignItems:"center"}}>
          {[
            {n:1,  color:"#49c2e4"},
            {n:2,  color:"#a78bfa"},
            {n:3,  color:"#34d399"},
            {n:4,  color:"#f59e0b"},
            {n:5,  color:"#fb923c"},
            {n:8,  color:"#f472b6"},
            {n:13, color:"#22d3ee"},
          ].map(({n,color})=>{
            const on=activeScan.includes(n);
            return(
              <button key={n} onClick={()=>toggleScan(n)}
                style={{background:on?`${color}1a`:"transparent",
                  border:"1px solid "+(on?color:"#1a2c44"),
                  borderRadius:0,padding:"3px 8px",color:on?color:"#8ea2bf",
                  fontFamily:"inherit",fontSize:12,cursor:"pointer",fontWeight:on?700:400}}>
                {on?"✓ ":""}{SCAN_LABELS[n]}
              </button>
            );
          })}
          </div>
          <div style={{display:"flex",alignItems:"center",gap:4,marginLeft:4,flexShrink:0}}>
            <span style={{fontSize:12,color:"#8ea2bf"}}>Expiry≥</span>
            {[3,7,14,21].map(d=>(
              <button key={d} onClick={()=>setScanMinDays(d)}
                style={{background:scanMinDays===d?"rgba(96,165,250,0.15)":"transparent",
                  border:`1px solid ${scanMinDays===d?"#6b7280":"#1a2c44"}`,
                  borderRadius:0,padding:"2px 6px",
                  color:scanMinDays===d?"#6b7280":"#8ea2bf",
                  fontFamily:"inherit",fontSize:12,cursor:"pointer",fontWeight:scanMinDays===d?700:400}}>
                {d}d
              </button>
            ))}
          </div>
          {lastScan&&<span style={{fontSize:12,color:"#8ea2bf"}}>🕐 {lastScan}</span>}
          {withSignals.length>0&&<span style={{fontSize:12,fontWeight:700,color:"#e8b339"}}>{withSignals.length} signals</span>}

          {/* Live toggle */}
          <button onClick={()=>setLiveMode(p=>!p)}
            style={{background:liveMode?"rgba(22,163,74,0.15)":"rgba(220,38,38,0.08)",border:"1px solid "+(liveMode?"#2ed47a":"#ff5b6c"),borderRadius:0,padding:"5px 10px",color:liveMode?"#2ed47a":"#ff5b6c",fontFamily:"inherit",fontSize:10,fontWeight:600,cursor:"pointer"}}>
            {liveMode?"● LIVE ON":"● LIVE OFF"}
          </button>

          {/* Auto-Virtual toggle */}
          <AutoVirtualToggle enabled={autoVirtual} setEnabled={setAutoVirtual}
            minSigs={autoVirtualMin} setMinSigs={setAutoVirtualMin}/>

          <button onClick={runScan} disabled={scanning}
            style={{background:scanning?"#1a2c44":"linear-gradient(135deg,#92400e,#92400e)",border:"none",borderRadius:0,padding:"6px 14px",color:"#fff",fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:scanning?"default":"pointer"}}>
            {scanning?"⟳":"▶"} SCAN
          </button>
          <ValidateAllBar
            fetcher={apiFetch} endpoint="/scan/validate/batch" storeKey="mcx"
            authed={authed} onShowToast={onShowToast} sendTgAlert={sendTgAlert}
            buildItems={()=>scanResults
              .filter(r=>!r.error&&(r.signals||[]).some(s=>activeScan.includes(s.scan)))
              .map(r=>{ const sigs=(r.signals||[]).filter(s=>activeScan.includes(s.scan));
                        const dir=sigs.some(s=>s.type==="BUY")?"BUY":"SELL";
                        return {symbol:r.base_symbol||r.symbol,exchange:"MCX",timeframe,direction:dir,signal_price:r.price||r.ltp||null}; })}
          />
          {isAdmin&&(
            <button onClick={()=>runMAI(scanSymbols,"MCX","1day",onAddAutoTP,"mcx",onShowToast,onAddNote,onJournalEntry,sendTgAlert,isAdmin)}
              disabled={mAiLoad||!authed}
              style={{background:mAiVis?"rgba(163,113,247,0.2)":"rgba(163,113,247,0.08)",
                border:"1px solid rgba(163,113,247,0.4)",borderRadius:0,
                padding:"6px 10px",color:"#49c2e4",fontFamily:"inherit",
                fontSize:11,fontWeight:700,cursor:"pointer",opacity:mAiLoad?0.6:1}}>
              {mAiLoad?"⟳":"🤖"} AI
            </button>
          )}
          <button onClick={()=>{setWatchlistInput(scanSymbols.join(",")); setShowWatchlistEditor(true);}}
            style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,padding:"5px 10px",
              color:"#8ea2bf",fontFamily:"inherit",fontSize:10,cursor:"pointer"}} title="Edit watchlist">
            📋 {scanSymbols.length}
          </button>
          <button onClick={()=>setShowSRPanel(true)}
            style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,
              padding:"5px 8px",color:"#6b7280",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}
            title="S/R & Demand/Supply Zones">
            📊 S/R
          </button>
        </>}

        {/* S/R Panel */}
        {showSRPanel&&(
          <SRZonePanel
            scanResults={scanResults}
            onClose={()=>setShowSRPanel(false)}
          />
        )}

        {/* Watchlist editor modal */}
        {showWatchlistEditor&&(
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
            <div style={{background:"#0b1624",border:"1px solid #23405f",borderRadius:12,padding:20,width:"100%",maxWidth:480}}>
              <div style={{fontSize:12,fontWeight:700,color:"#e7edf5",marginBottom:12}}>📋 MCX Watchlist</div>
              <div style={{fontSize:10,color:"#8ea2bf",marginBottom:8}}>Enter symbols separated by commas</div>
              <textarea value={watchlistInput} onChange={e=>setWatchlistInput(e.target.value)}
                rows={6} style={{width:"100%",background:"#0f1d30",border:"1px solid #23405f",
                  borderRadius:0,padding:10,color:"#e7edf5",fontFamily:"inherit",fontSize:11,
                  resize:"vertical",boxSizing:"border-box"}}
                placeholder="CRUDEOIL,GOLD,SILVER,NATURALGAS..."/>
              <div style={{fontSize:12,color:"#5a728f",marginTop:4}}>Available: {MCX_SYMBOLS.join(", ")}</div>
              <div style={{display:"flex",gap:8,marginTop:12}}>
                <button onClick={()=>{
                  const syms = watchlistInput.split(",").map(s=>s.trim().toUpperCase()).filter(Boolean);
                  setCustomSymbols(syms.length > 0 ? syms : MCX_SYMBOLS);
                  setShowWatchlistEditor(false);
                }} style={{flex:1,background:"linear-gradient(135deg,#92400e,#92400e)",border:"none",
                  borderRadius:0,padding:"8px",color:"#fff",fontFamily:"inherit",fontSize:11,
                  fontWeight:600,cursor:"pointer"}}>
                  ✓ Save
                </button>
                <button onClick={()=>{setCustomSymbols(MCX_SYMBOLS); setShowWatchlistEditor(false);}}
                  style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,padding:"8px 12px",
                    color:"#8ea2bf",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>
                  Reset
                </button>
                <button onClick={()=>setShowWatchlistEditor(false)}
                  style={{background:"none",border:"1px solid #23405f",borderRadius:0,padding:"8px 12px",
                    color:"#8ea2bf",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {view==="chain"&&<>
          <div style={{flex:1}}/>
          <button onClick={loadChain} style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,padding:"5px 12px",color:"#8ea2bf",fontFamily:"inherit",fontSize:10,cursor:"pointer"}}>
            ↻ Reload
          </button>
        </>}
      </div>

      {/* ── Auto-trade banner (scan view only, admin only) ── */}
      {view==="scan"&&(
        <div style={{background:atEnabled?"rgba(248,81,73,0.1)":"#07101c",borderBottom:"1px solid "+(atEnabled?"rgba(248,81,73,0.3)":"#1a2c44"),padding:"6px 14px",display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
          {isAdmin ? (
            <>
              {/* ON/OFF */}
              <button onClick={()=>{
                  const next = !atEnabled;
                  setAtEnabled(next);
                  if(setBgEngineEnabled) setBgEngineEnabled(next);
                  if(setBgAtMode) setBgAtMode(atMode);
                  if(next && setBgEngineCfg) setBgEngineCfg({
                    ...atCfg,
                    atEnabled: true,
                    scanList: scanSymbols,
                    timeframe: timeframe,
                  });
                }}
                style={{background:atEnabled?"rgba(248,81,73,0.2)":"rgba(0,212,170,0.08)",border:"1px solid "+(atEnabled?"#ff5b6c":"#e8b339"),borderRadius:0,padding:"4px 14px",color:atEnabled?"#ff5b6c":"#e8b339",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer",letterSpacing:0.5}}>
                {atEnabled?"🤖 AUTO ON — CLICK TO STOP":"🤖 AUTO OFF — CLICK TO START"}
              </button>
              <button onClick={()=>setAtValidate(v=>!v)}
                title="When ON, MCX auto-trade validates each signal (entry zone + 1H + AI) and only trades the GO ones. On validation error it skips."
                style={{background:atValidate?"rgba(46,212,122,0.15)":"rgba(255,91,108,0.08)",
                  border:"1px solid "+(atValidate?"#2ed47a":"#ff5b6c"),borderRadius:0,padding:"4px 10px",
                  color:atValidate?"#2ed47a":"#ff8a95",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                {atValidate?"✅ Validate ✓":"⚠️ Validate ✗"}
              </button>
              <button onClick={()=>setAtPaper(v=>!v)}
                title="Paper mode: record validated-GO signals as paper trades in Virtual Trading instead of placing real orders."
                style={{background:atPaper?"rgba(96,165,250,0.15)":"rgba(232,179,57,0.06)",
                  border:"1px solid "+(atPaper?"#60a5fa":"#23405f"),borderRadius:0,padding:"3px 10px",
                  color:atPaper?"#60a5fa":"#8ea2bf",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                {atPaper?"📝 Paper ON":"📝 Paper"}
              </button>
            </>
          ) : (
            <span style={{fontSize:12,color:'#5a728f',padding:'3px 10px',
              background:'rgba(96,165,250,0.05)',border:'1px solid #1a2c44',borderRadius:0}}>
              📡 Signal Mode · Use Buy/Sell buttons to trade manually
            </span>
          )}

          {/* AT config controls — admin only */}
          {isAdmin&&(<>
          {/* Mode: Futures / Options */}
          <div style={{display:"flex",background:"#07101c",border:"1px solid #1a2c44",borderRadius:0,overflow:"hidden"}}>
            {[["futures","📈 Futures"],["options","🎯 Options (ATM)"]].map(([v,l])=>(
              <button key={v} onClick={()=>setAtMode(v)}
                style={{background:atMode===v?"rgba(96,165,250,0.15)":"transparent",border:"none",
                  borderRight:"1px solid #1a2c44",padding:"4px 12px",
                  color:atMode===v?"#e8b339":"#8ea2bf",fontFamily:"inherit",fontSize:12,fontWeight:atMode===v?700:400,cursor:"pointer"}}>
                {l}
              </button>
            ))}
          </div>

          {/* Capital + Auto-size */}
          <div style={{display:"flex",alignItems:"center",gap:5}}>
            <span style={{fontSize:12,color:"#8ea2bf"}}>Min ₹</span>
            <input type="number" step="10000" min="10000" value={atCfg.capital}
              onChange={e=>setAtCfg(p=>({...p,capital:parseInt(e.target.value)||50000}))}
              style={{width:80,background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,padding:"3px 6px",color:"#e7edf5",fontFamily:"inherit",fontSize:10,outline:"none"}}/>
            <button onClick={()=>setAtCfg(p=>({...p,autoSize:!p.autoSize}))}
              style={{padding:"2px 7px",borderRadius:0,border:"1px solid "+(atCfg.autoSize?"#6b7280":"#1a2c44"),
                background:atCfg.autoSize?"rgba(96,165,250,0.15)":"transparent",
                color:atCfg.autoSize?"#6b7280":"#8ea2bf",fontFamily:"inherit",fontSize:12,cursor:"pointer",fontWeight:700}}>
              AUTO {atCfg.autoSize?"ON":"OFF"}
            </button>
            {atCfg.autoSize&&<>
              <span style={{fontSize:12,color:"#8ea2bf"}}>max</span>
              <input type="number" min="5" max="100" step="5" value={atCfg.maxTradesPct||20}
                onChange={e=>setAtCfg(p=>({...p,maxTradesPct:parseFloat(e.target.value)}))}
                style={{width:38,background:"#0f1d30",border:"1px solid #6b7280",borderRadius:0,padding:"2px 4px",color:"#6b7280",fontFamily:"inherit",fontSize:10,textAlign:"center"}}/>
              <span style={{fontSize:12,color:"#8ea2bf"}}>%</span>
            </>}
          </div>

          {/* Daily limit */}
          <div style={{display:"flex",alignItems:"center",gap:5}}>
            <span style={{fontSize:12,color:"#e8b339",fontWeight:700}}>📅</span>
            <input type="number" min="0" max="50" step="1" value={atCfg.dailyLimit??5}
              onChange={e=>setAtCfg(p=>({...p,dailyLimit:parseInt(e.target.value)||0}))}
              style={{width:36,background:"#0f1d30",border:"1px solid #92400e",borderRadius:0,padding:"2px 4px",color:"#e8b339",fontFamily:"inherit",fontSize:10,textAlign:"center"}}/>
            <span style={{fontSize:12,color:"#8ea2bf"}}>trades/day</span>
            <span style={{fontSize:12,color:checkDailyLimit(dailyTradesRef,atCfg.dailyLimit||0).ok?"#2ed47a":"#ff5b6c",fontWeight:700}}>
              ({checkDailyLimit(dailyTradesRef,atCfg.dailyLimit||0).count}/{atCfg.dailyLimit||"∞"})
            </span>
          </div>

          {/* Min signals */}
          <div style={{display:"flex",alignItems:"center",gap:4}}>
            <span style={{fontSize:12,color:"#8ea2bf"}}>Min signals:</span>
            {[1,2,3].map(n=>(
              <button key={n} onClick={()=>setAtCfg(p=>({...p,minSignals:n}))}
                style={{background:atCfg.minSignals===n?"rgba(0,212,170,0.2)":"transparent",border:"1px solid "+(atCfg.minSignals===n?"#e8b339":"#1a2c44"),borderRadius:0,padding:"2px 7px",color:atCfg.minSignals===n?"#e8b339":"#8ea2bf",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                {n}
              </button>
            ))}
          </div>

          {/* TP/SL quick */}
          <div style={{display:"flex",alignItems:"center",gap:4}}>
            <span style={{fontSize:12,color:"#2ed47a"}}>TP</span>
            <input type="number" step="0.5" min="0.5" value={atMode==="futures"?atCfg.tpPct:atCfg.optTpPct}
              onChange={e=>{const v=parseFloat(e.target.value)||2;setAtCfg(p=>atMode==="futures"?({...p,tpPct:v}):({...p,optTpPct:v}));}}
              style={{width:44,background:"#0f1d30",border:"1px solid #3fb950",borderRadius:0,padding:"2px 4px",color:"#2ed47a",fontFamily:"inherit",fontSize:10,outline:"none"}}/>
            <span style={{fontSize:12,color:"#8ea2bf"}}>%</span>
            <span style={{fontSize:12,color:"#ff5b6c",marginLeft:4}}>SL</span>
            <input type="number" step="0.5" min="0.5" value={atMode==="futures"?atCfg.slPct:atCfg.optSlPct}
              onChange={e=>{const v=parseFloat(e.target.value)||1;setAtCfg(p=>atMode==="futures"?({...p,slPct:v}):({...p,optSlPct:v}));}}
              style={{width:44,background:"#0f1d30",border:"1px solid #f85149",borderRadius:0,padding:"2px 4px",color:"#ff5b6c",fontFamily:"inherit",fontSize:10,outline:"none"}}/>
            <span style={{fontSize:12,color:"#8ea2bf"}}>%</span>
          </div>

          {/* Min days (both modes) */}
          <div style={{display:"flex",alignItems:"center",gap:4}}>
            <span style={{fontSize:12,color:"#6b7280"}}>Min expiry days:</span>
            {[3,7,15,21,30].map(d=>(
              <button key={d} onClick={()=>setAtCfg(p=>({...p,minDays:d}))}
                style={{background:atCfg.minDays===d?"rgba(96,165,250,0.15)":"transparent",
                  border:"1px solid "+(atCfg.minDays===d?"#6b7280":"#1a2c44"),
                  borderRadius:0,padding:"2px 6px",
                  color:atCfg.minDays===d?"#6b7280":"#8ea2bf",
                  fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
                {d}d
              </button>
            ))}
            <span style={{fontSize:11,color:"#5a728f",marginLeft:2}}>
              (skip contracts expiring &lt; {atCfg.minDays||7}d)
            </span>
          </div>

          {atEnabled&&<span style={{fontSize:12,color:"#ff5b6c",fontWeight:700,marginLeft:"auto",display:"flex",gap:8,alignItems:"center"}}>
            {(atCfg.dailyLimit||0)>0&&(
              <span style={{color:checkDailyLimit(dailyTradesRef,atCfg.dailyLimit).ok?"#e8b339":"#ff5b6c",
                padding:"1px 5px",background:"rgba(96,165,250,0.1)",borderRadius:0}}>
                📅 {checkDailyLimit(dailyTradesRef,atCfg.dailyLimit).count}/{atCfg.dailyLimit}
              </span>
            )}
            <span>⚠ LIVE — {atLog.filter(l=>l.status==="PLACED").length} trades fired</span>
          </span>}
          </>)}
        </div>
      )}

      {/* ── SCAN VIEW ── */}
      {view==="scan"&&(
        <div style={{flex:1,overflow:"hidden",display:"flex",flexDirection:"column"}}>
          {scanError&&<div style={{padding:"10px 14px",color:"#ff5b6c",fontSize:11,background:"rgba(220,38,38,0.08)",borderBottom:"1px solid rgba(248,81,73,0.2)"}}>⚠ {scanError}</div>}

          {/* ── Scan / AI result toggle — AI only for admin ── */}
          <div style={{display:"flex",background:"#0b1624",borderBottom:"1px solid #1a2c44",
            padding:"4px 14px",gap:6,flexShrink:0,alignItems:"center"}}>
            <button onClick={()=>setMcxResultView("scan")}
              style={{background:mcxResultView==="scan"?"rgba(96,165,250,0.15)":"transparent",
                border:"1px solid "+(mcxResultView==="scan"?"#e8b339":"#1a2c44"),
                borderRadius:0,padding:"3px 12px",color:mcxResultView==="scan"?"#e8b339":"#8ea2bf",
                fontFamily:"inherit",fontSize:10,fontWeight:mcxResultView==="scan"?700:400,cursor:"pointer"}}>
              📡 Scan {scanResults.length>0&&`(${scanResults.length})`}
            </button>
            {isAdmin&&(
              <button onClick={()=>setMcxResultView("ai")}
                style={{background:mcxResultView==="ai"?"rgba(163,113,247,0.15)":"transparent",
                  border:"1px solid "+(mcxResultView==="ai"?"#49c2e4":"#1a2c44"),
                  borderRadius:0,padding:"3px 12px",color:mcxResultView==="ai"?"#49c2e4":"#8ea2bf",
                  fontFamily:"inherit",fontSize:10,fontWeight:mcxResultView==="ai"?700:400,cursor:"pointer"}}>
                🤖 AI {mAiRes.length>0&&`(${mAiRes.length})`}
                {mAiLoad&&<span style={{marginLeft:4,fontSize:11,animation:"spin 1s linear infinite",display:"inline-block"}}>⟳</span>}
              </button>
            )}
            <div style={{display:"flex",gap:2,marginLeft:"auto"}}>
              <button onClick={()=>setMcxViewMode("grid")} title="Grid view"
                style={{background:mcxViewMode==="grid"?"rgba(232,179,57,0.2)":"transparent",
                  border:"1px solid "+(mcxViewMode==="grid"?"#e8b339":"#1a2c44"),
                  borderRadius:0,padding:"2px 7px",color:mcxViewMode==="grid"?"#e8b339":"#8ea2bf",
                  fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>⊞</button>
              <button onClick={()=>setMcxViewMode("table")} title="Table view"
                style={{background:mcxViewMode==="table"?"rgba(232,179,57,0.2)":"transparent",
                  border:"1px solid "+(mcxViewMode==="table"?"#e8b339":"#1a2c44"),
                  borderRadius:0,padding:"2px 7px",color:mcxViewMode==="table"?"#e8b339":"#8ea2bf",
                  fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>☰</button>
            </div>
          </div>

          {/* ── AI results view — admin only ── */}
          {mcxResultView==="ai"&&!isAdmin&&setMcxResultView("scan")}
          {mcxResultView==="ai"&&isAdmin&&(
            mAiLoad&&mAiRes.length===0 ? (
              <div style={{textAlign:"center",padding:"60px 20px",color:"#8ea2bf"}}>
                <div style={{fontSize:24,marginBottom:8,animation:"spin 1s linear infinite",display:"inline-block"}}>⟳</div>
                <div style={{fontSize:12,marginTop:8}}>Running AI Analysis...</div>
              </div>
            ) : mAiRes.length===0 ? (
              <div style={{textAlign:"center",padding:"60px 20px",color:"#8ea2bf"}}>
                <div style={{fontSize:24,marginBottom:8}}>🤖</div>
                <div style={{fontSize:13,color:"#e7edf5",marginBottom:4}}>AI MCX Scanner</div>
                <div style={{fontSize:11,marginBottom:16}}>Click the 🤖 AI button in the toolbar to run AI analysis</div>
                <button onClick={()=>setMcxResultView("scan")}
                  style={{background:"transparent",border:"1px solid #1a2c44",borderRadius:0,
                    padding:"8px 18px",color:"#8ea2bf",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>
                  ← Back to Scan
                </button>
              </div>
            ) : (
              <AIPanel isAdmin={isAdmin} canTrade={canTrade} results={mAiRes} loading={mAiLoad} error={mAiErr}
                visible={true} onClose={()=>{setMAiVis(false);setMcxResultView("scan");}}
                onTrade={onTrade} onShowToast={onShowToast}
                tabMode="mcx"
                autoTrade={mAutoTrade} setAutoTrade={setMAutoTrade}
                minAutoConf={mMinConf} setMinAutoConf={setMMinConf}
                autoFired={mAutoFired}
                marginPct={mMargPct} setMarginPct={setMMargPct}
                maxDaily={mMaxDay} setMaxDaily={setMMaxDay}
                maxScans={mMaxScans} setMaxScans={setMMaxScans}
                scanCount={mScanCount} balance={mBalance}/>
            )
          )}

          {/* Scan results — only when scan tab active */}
          {mcxResultView==="scan"&&(
          <div style={{flex:1,overflowY:"auto"}}>
            {!scanning&&scanResults.length===0&&(
              <div style={{textAlign:"center",padding:"60px 20px",color:"#8ea2bf"}}>
                <div style={{fontSize:28,marginBottom:10}}>🏭</div>
                <div style={{fontSize:13,color:"#e7edf5",marginBottom:6}}>MCX Commodity Scanner</div>
                <div style={{fontSize:11,lineHeight:1.8}}>
                  Scans all MCX commodities using technical signals<br/>
                  Auto-resolves to near-month futures contracts<br/>
                  <span style={{color:"#e8b339"}}>Futures mode</span> — trades futures directly<br/>
                  <span style={{color:"#6b7280"}}>Options mode</span> — finds ATM CE/PE with {atCfg.minDays}+ days expiry
                </div>
                <button onClick={runScan} style={{marginTop:16,background:"linear-gradient(135deg,#92400e,#f0c040)",border:"none",borderRadius:0,padding:"10px 24px",color:"#000",fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                  ▶ START SCAN
                </button>
              </div>
            )}
            {scanning&&scanResults.length===0&&(
              <div style={{textAlign:"center",padding:"40px",color:"#8ea2bf",fontSize:12}}>
                ⟳ Scanning {scanSymbols.length} MCX symbols...
              </div>
            )}

            {scanResults.length>0&&mcxViewMode==="grid"&&(
              <div style={{
                display:"grid",
                gridTemplateColumns:"repeat(auto-fill,minmax(190px,1fr))",
                gap:8,padding:"10px 12px",
              }}>
                {scanResults.map((r,i)=>{
                  const sigs    = (r.signals||[]).filter(s=>activeScan.includes(s.scan));
                  const hasBuy  = sigs.some(s=>s.type==="BUY");
                  const hasSell = sigs.some(s=>s.type==="SELL");
                  const isMixed = hasBuy&&hasSell;
                  const meta    = MCX_BASE[r.symbol]||{name:r.symbol,color:"#f0c040"};
                  const lqMcx   = liveQuotesMcx[r.resolved_symbol||r.symbol]||liveQuotesMcx[r.symbol];
                  const ltp     = lqMcx?parseFloat(lqMcx.ltp||0):r.price||0;
                  const pct     = lqMcx?.pct??lqMcx?.change??r.pct_change??null;
                  const isLive  = !!lqMcx?.ltp;
                  const sigColor= isMixed?"#f59e0b":hasBuy?"#2ed47a":hasSell?"#ff5b6c":"#5a728f";
                  const buyPct  = r.buyVolPct||50;
                  const sellPct = r.sellVolPct||50;
                  const volDom  = buyPct>=60?"BUY":sellPct>=60?"SELL":"NEUTRAL";
                  const rsi     = r.rsi||50;
                  return (
                    <div key={r.symbol} style={{
                      background:"#0f1d30",
                      border:`1px solid ${isMixed?"rgba(245,158,11,0.4)":hasBuy?"rgba(34,197,94,0.3)":hasSell?"rgba(239,68,68,0.3)":"#1a2c44"}`,
                      borderLeft:`3px solid ${sigColor}`,
                      borderRadius:0,padding:"10px 12px",
                      display:"flex",flexDirection:"column",gap:5,
                    }}>
                      {/* Header */}
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                        <div>
                          <div style={{fontSize:12,fontWeight:700,color:meta.color}}>{meta.name||r.symbol}</div>
                          <div style={{fontSize:10,color:"#5a728f"}}>{r.resolved_symbol||r.symbol}</div>
                          {r.days_to_expiry!=null&&(
                            <div style={{fontSize:9,color:r.near_expiry?"#ff5b6c":"#5a728f"}}>
                              {r.near_expiry?"⚠ ":""}{r.days_to_expiry}d expiry
                            </div>
                          )}
                        </div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontSize:12,fontWeight:700,color:isLive?"#e7edf5":"#8ea2bf"}}>
                            ₹{ltp>0?ltp.toLocaleString("en-IN",{maximumFractionDigits:0}):"—"}
                            {isLive&&<span style={{fontSize:8,color:"#2ed47a",marginLeft:2}}>●</span>}
                          </div>
                          {pct!=null&&<div style={{fontSize:10,color:pct>=0?"#2ed47a":"#ff5b6c",fontWeight:600}}>
                            {pct>=0?"+":""}{parseFloat(pct).toFixed(2)}%
                          </div>}
                        </div>
                      </div>
                      {/* Signals */}
                      {sigs.length>0?(
                        <div style={{display:"flex",gap:3,flexWrap:"wrap"}}>
                          {sigs.map((s,j)=>(
                            <span key={j} className={s.type==="BUY"?"sig-buy":"sig-sell"}>
                              {s.type==="BUY"?"▲":"▼"} {s.label}
                            </span>
                          ))}
                        </div>
                      ):(
                        <div style={{fontSize:9,color:"#5a728f",fontStyle:"italic"}}>No signal</div>
                      )}
                      {/* D/S Zone badges — shown when zone signal present */}
                      {(r.zones?.demand?.length>0 || r.zones?.supply?.length>0) && sigs.some(s=>s.scan===8) && (
                        <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:1}}>
                          {(r.zones.demand||[]).slice(0,2).map((z,i)=>(
                            <span key={`d${i}`} style={{fontSize:8,fontWeight:700,padding:"1px 5px",borderRadius:0,
                              background:"rgba(34,197,94,0.12)",color:"#2ed47a",border:"1px solid rgba(34,197,94,0.3)"}}>
                              🟢 D ₹{z[0]?.toFixed(0)}–{z[1]?.toFixed(0)} {z[2]}×
                            </span>
                          ))}
                          {(r.zones.supply||[]).slice(0,2).map((z,i)=>(
                            <span key={`s${i}`} style={{fontSize:8,fontWeight:700,padding:"1px 5px",borderRadius:0,
                              background:"rgba(239,68,68,0.10)",color:"#ff5b6c",border:"1px solid rgba(239,68,68,0.25)"}}>
                              🔴 S ₹{z[0]?.toFixed(0)}–{z[1]?.toFixed(0)} {z[2]}×
                            </span>
                          ))}
                        </div>
                      )}
                      {/* Vol bar */}
                      <div>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                          <span style={{fontSize:9,color:"#2ed47a"}}>B {buyPct}%</span>
                          <span style={{fontSize:9,color:volDom==="BUY"?"#2ed47a":volDom==="SELL"?"#ff5b6c":"#5a728f",fontWeight:700}}>{volDom}</span>
                          <span style={{fontSize:9,color:"#ff5b6c"}}>S {sellPct}%</span>
                        </div>
                        <div style={{height:4,background:"#0b1624",borderRadius:2,overflow:"hidden",display:"flex"}}>
                          <div style={{width:`${buyPct}%`,background:"#2ed47a"}}/>
                          <div style={{flex:1,background:"#ff5b6c"}}/>
                        </div>
                      </div>
                      {/* Indicators */}
                      <div style={{display:"flex",gap:6,alignItems:"center"}}>
                        <span style={{fontSize:10,color:rsi>=70?"#ff5b6c":rsi<=30?"#2ed47a":"#8ea2bf",fontWeight:700}}>
                          RSI {rsi.toFixed(0)}
                        </span>
                        {r.ema9&&r.ema50&&(
                          <span style={{fontSize:9,color:r.ema9>r.ema50?"#2ed47a":"#ff5b6c"}}>
                            E9 {r.ema9>r.ema50?"▲":"▼"} E50
                          </span>
                        )}
                      </div>
                      {/* Trade button */}
                      {ltp>0&&!r.error&&(
                        <button onClick={()=>setMcxStratPopup({
                          symbol:r.resolved_symbol||r.symbol,
                          exchange:"MCX",ltp,hasBuy,hasSell,signals:sigs,
                          lotSize:r.lot_size||1,mode:"mcx",
                        })}
                          style={{background:isMixed?"rgba(245,158,11,0.12)":"rgba(232,179,57,0.12)",
                            border:`1px solid ${isMixed?"rgba(245,158,11,0.4)":"rgba(232,179,57,0.4)"}`,
                            borderRadius:0,padding:"4px 0",
                            color:isMixed?"#f59e0b":"#e8b339",
                            fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer",
                            textAlign:"center",marginTop:2}}>
                          🎯 {isMixed?"Straddle":"MCX Strategy"}
                        </button>
                      )}
                      {/* Chart button */}
                      {!r.error&&(
                        <button onClick={()=>setChartModal({symbol:r.resolved_symbol||r.symbol, exchange:"MCX", zones:r.zones||null})}
                          style={{background:"transparent",border:"1px solid #1a2c44",
                            borderRadius:0,padding:"2px 0",color:"#23405f",
                            fontFamily:"inherit",fontSize:9,cursor:"pointer",textAlign:"center",marginTop:2,
                            transition:"color 0.15s,border-color 0.15s"}}
                          onMouseEnter={e=>{e.currentTarget.style.color="#49c2e4";e.currentTarget.style.borderColor="#e8b339"}}
                          onMouseLeave={e=>{e.currentTarget.style.color="#23405f";e.currentTarget.style.borderColor="#1a2c44"}}>
                          📈 Chart
                        </button>
                      )}
                      {/* Open in Broker — for users without API */}
                      {ltp>0&&!r.error&&(()=>{
                        const _bk = getUserBroker();
                        const _bc = BROKER_CONFIGS[_bk]||BROKER_CONFIGS.zerodha;
                        return (
                          <button onClick={()=>openInBroker({
                              symbol:   r.resolved_symbol||r.symbol,
                              exchange: "MCX",
                              side:     hasBuy&&!hasSell?"BUY":hasSell&&!hasBuy?"SELL":"BUY",
                              qty:      r.lot_size||1,
                              price:    ltp,
                              orderType:"LIMIT",
                              product:  "NRML",
                            })}
                            style={{background:"transparent",border:`1px solid ${_bc.color||"rgba(255,107,53,0.3)"}44`,
                              borderRadius:0,padding:"2px 0",color:(_bc.color||"#ff6b35")+"bb",
                              fontFamily:"inherit",fontSize:9,cursor:"pointer",textAlign:"center",marginTop:2}}
                            onMouseEnter={e=>{e.currentTarget.style.color=_bc.color||"#ff6b35";e.currentTarget.style.borderOpacity="1"}}
                            onMouseLeave={e=>{e.currentTarget.style.color=(_bc.color||"#ff6b35")+"bb"}}>
                            {_bc.logo||"🟠"} {(_bc.name||"Broker").split(" ")[0]}
                          </button>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            )}

            {scanResults.length>0&&mcxViewMode==="table"&&(
              <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}><table style={{width:"100%",borderCollapse:"collapse",fontSize:11,minWidth:480}}>
                <thead>
                  <tr style={{background:"#0b1624",borderBottom:"1px solid #1a2c44",position:"sticky",top:0,zIndex:1}}>
                    {["COMMODITY","CONTRACT","PRICE","RSI","TSI","EMA9","EMA50","VOL×","SIGNALS","TRADE"].map(h=>(
                      <th key={h} style={{padding:"7px 10px",color:"#8ea2bf",fontSize:12,letterSpacing:1,fontWeight:700,textAlign:"left",whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {scanResults.map((r,i)=>{
                    const sigs    = (r.signals||[]).filter(s=>activeScan.includes(s.scan));
                    const hasBuy  = sigs.some(s=>s.type==="BUY");
                    const hasSell = sigs.some(s=>s.type==="SELL");
                    const rowBg   = hasBuy?"rgba(63,185,80,0.04)":hasSell?"rgba(248,81,73,0.04)":"transparent";
                    const meta    = MCX_BASE[r.symbol]||{name:r.symbol,color:"#8ea2bf"};
                    const lqMcx   = liveQuotesMcx[r.resolved_symbol||r.symbol] || liveQuotesMcx[r.symbol];
                    const livePMcx= lqMcx ? parseFloat(lqMcx.ltp||0) : null;
                    const dispPMcx= livePMcx || r.price || 0;
                    const pctMcx  = lqMcx?.pct ?? lqMcx?.change ?? null;
                    return(
                      <tr key={r.symbol} style={{borderBottom:"1px solid #161b22",background:rowBg}}>
                        <td style={{padding:"8px 10px"}}>
                          <div style={{fontWeight:700,color:meta.color,fontSize:11}}>{meta.name||r.symbol}</div>
                          <div style={{fontSize:12,color:"#8ea2bf"}}>{r.symbol}</div>
                        </td>
                        <td style={{padding:"8px 10px",fontFamily:"inherit",fontSize:12,color:"#6b7280"}}>
                          <div>{r.resolved_symbol||r.symbol}</div>
                          {r.days_to_expiry!=null&&(
                            <div style={{fontSize:11,marginTop:2,
                              color:r.near_expiry?"#ff5b6c":r.days_to_expiry<=14?"#e8b339":"#2ed47a",
                              fontWeight:700}}>
                              {r.near_expiry&&"⚠ "}
                              {r.days_to_expiry}d to expiry
                            </div>
                          )}
                          {r.error&&<div style={{color:"#ff5b6c",fontSize:12}}>{r.error}</div>}
                        </td>
                        <td style={{padding:"8px 10px"}}>
                          <div style={{display:"flex",alignItems:"center",gap:4}}>
                            <span style={{fontWeight:700,color:livePMcx?"#e7edf5":"#8ea2bf"}}>
                              {dispPMcx?`₹${dispPMcx.toLocaleString("en-IN",{maximumFractionDigits:2})}`:"—"}
                            </span>
                            {livePMcx&&<span style={{fontSize:11,color:"#e8b339"}}>🟢</span>}
                            {pctMcx!=null&&<span style={{fontSize:11,color:pctMcx>=0?"#2ed47a":"#ff5b6c",fontWeight:700}}>{pctMcx>=0?"+":""}{parseFloat(pctMcx).toFixed(2)}%</span>}
                          </div>
                        </td>
                        <td style={{padding:"8px 10px",color:r.rsi>70?"#ff5b6c":r.rsi<30?"#2ed47a":"#8ea2bf"}}>{r.rsi!=null?r.rsi.toFixed(1):"—"}</td>
                        <td style={{padding:"8px 10px",color:r.tsi>0?"#2ed47a":"#ff5b6c"}}>{r.tsi!=null?r.tsi.toFixed(2):"—"}</td>
                        <td style={{padding:"8px 10px",color:"#8ea2bf",fontSize:10}}>{r.ema9!=null?r.ema9.toLocaleString("en-IN"):"—"}</td>
                        <td style={{padding:"8px 10px",color:"#8ea2bf",fontSize:10}}>{r.ema50!=null?r.ema50.toLocaleString("en-IN"):"—"}</td>
                        <td style={{padding:"5px 10px",minWidth:90}}>
                          {r.buyVolPct!=null?(
                            <div>
                              <div style={{display:"flex",height:7,borderRadius:0,overflow:"hidden",background:"#1a2c44"}}>
                                <div style={{width:`${r.buyVolPct}%`,background:"#2ed47a"}}/>
                                <div style={{width:`${r.sellVolPct}%`,background:"#ff5b6c"}}/>
                              </div>
                              <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginTop:2}}>
                                <span style={{color:"#2ed47a",fontWeight:700}}>B {r.buyVolPct}%</span>
                                <span style={{color:"#ff5b6c",fontWeight:700}}>S {r.sellVolPct}%</span>
                              </div>
                            </div>
                          ):(
                            <span style={{color:r.volRatio>1.5?"#e8b339":"#8ea2bf"}}>{r.volRatio!=null?r.volRatio+"x":"—"}</span>
                          )}
                        </td>
                        <td style={{padding:"8px 10px",maxWidth:220}}>
                          {sigs.length===0&&<span style={{color:"#23405f",fontSize:12}}>No signal</span>}
                          <div style={{display:"flex",flexWrap:"wrap",gap:3}}>
                            {sigs.map((s,j)=>(
                              <span key={j} className={s.type==="BUY"?"sig-buy":"sig-sell"}>
                                {s.type==="BUY"?"▲":"▼"} {s.label}
                              </span>
                            ))}
                          </div>
                          {/* D/S Zone badges in table view */}
                          {(r.zones?.demand?.length>0||r.zones?.supply?.length>0)&&sigs.some(s=>s.scan===8)&&(
                            <div style={{display:"flex",gap:3,flexWrap:"wrap",marginTop:3}}>
                              {(r.zones.demand||[]).slice(0,1).map((z,i)=>(
                                <span key={`d${i}`} style={{fontSize:8,padding:"1px 4px",borderRadius:0,
                                  background:"rgba(34,197,94,0.1)",color:"#2ed47a",border:"1px solid rgba(34,197,94,0.25)"}}>
                                  🟢D {z[2]}×
                                </span>
                              ))}
                              {(r.zones.supply||[]).slice(0,1).map((z,i)=>(
                                <span key={`s${i}`} style={{fontSize:8,padding:"1px 4px",borderRadius:0,
                                  background:"rgba(239,68,68,0.08)",color:"#ff5b6c",border:"1px solid rgba(239,68,68,0.2)"}}>
                                  🔴S {z[2]}×
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td style={{padding:"8px 10px",whiteSpace:"nowrap"}}>
                          <div style={{display:"flex",gap:4,flexDirection:"column"}}>
                          {r.price&&!r.error&&canTrade&&(
                            <button onClick={()=>setMcxStratPopup({
                              symbol:r.resolved_symbol||r.symbol,
                              exchange:"MCX",ltp:dispPMcx||r.price,
                              hasBuy,hasSell,signals:sigs,
                              lotSize:r.lot_size||1,mode:"mcx",
                            })}
                              style={{background:"rgba(232,179,57,0.12)",border:"1px solid rgba(232,179,57,0.4)",
                                borderRadius:0,padding:"3px 8px",color:"#e8b339",fontFamily:"inherit",
                                fontSize:11,fontWeight:700,cursor:"pointer"}}>
                              🎯 Strategy
                            </button>
                          )}
                          {r.price&&!r.error&&(()=>{
                            const _bk2 = getUserBroker();
                            const _bc2 = BROKER_CONFIGS[_bk2]||BROKER_CONFIGS.zerodha;
                            return (
                              <button onClick={()=>openInBroker({
                                  symbol:r.resolved_symbol||r.symbol,exchange:"MCX",
                                  side:hasBuy&&!hasSell?"BUY":"SELL",
                                  qty:r.lot_size||1,price:dispPMcx||r.price,
                                  orderType:"LIMIT",product:"NRML"})}
                                style={{background:"transparent",border:`1px solid ${_bc2.color||"rgba(255,107,53,0.3)"}44`,
                                  borderRadius:0,padding:"3px 8px",color:(_bc2.color||"#ff6b35")+"cc",
                                  fontFamily:"inherit",fontSize:10,cursor:"pointer"}}>
                                {_bc2.logo||"🟠"} {(_bc2.name||"Broker").split(" ")[0]}
                              </button>
                            );
                          })()}
                          {!r.error&&(
                            <button onClick={()=>setChartModal({symbol:r.resolved_symbol||r.symbol,exchange:"MCX",zones:r.zones||null})}
                              style={{background:"transparent",border:"1px solid #1a2c44",
                                borderRadius:0,padding:"3px 8px",color:"#5a728f",
                                fontFamily:"inherit",fontSize:10,cursor:"pointer"}}
                              onMouseEnter={e=>{e.currentTarget.style.color="#49c2e4";e.currentTarget.style.borderColor="#e8b339"}}
                              onMouseLeave={e=>{e.currentTarget.style.color="#5a728f";e.currentTarget.style.borderColor="#1a2c44"}}>
                              📈 Chart
                            </button>
                          )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table></div>
            )}
          </div>
          )}

          {/* Auto-trade log */}
          {atLog.length>0&&(
            <div style={{borderTop:"1px solid #1a2c44",maxHeight:160,overflowY:"auto",background:"#07101c"}}>
              <div style={{padding:"5px 14px",fontSize:12,color:"#8ea2bf",letterSpacing:2,display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:"1px solid #1a2c44"}}>
                <span>AUTO TRADE LOG</span>
                <button onClick={()=>setAtLog([])} style={{background:"none",border:"none",color:"#23405f",fontSize:11,cursor:"pointer"}}>clear</button>
              </div>
              {atLog.map(t=>(
                <div key={t.id} style={{display:"flex",gap:8,alignItems:"center",padding:"4px 14px",borderBottom:"1px solid #161b22",fontSize:10,flexWrap:"wrap"}}>
                  <span style={{color:"#8ea2bf",flexShrink:0,fontSize:12}}>{t.time}</span>
                  <span style={{fontSize:11,padding:"1px 5px",borderRadius:0,background:t.mode==="FUT"?"rgba(96,165,250,0.15)":"rgba(96,165,250,0.15)",color:t.mode==="FUT"?"#e8b339":"#6b7280",fontWeight:700}}>{t.mode}</span>
                  <span style={{color:t.side==="BUY"?"#2ed47a":"#ff5b6c",fontWeight:700,flexShrink:0}}>{t.side||"BUY"}</span>
                  <span style={{color:"#e7edf5",fontWeight:600}}>{t.symbol}</span>
                  {t.contract&&t.contract!==t.symbol&&<span style={{color:"#6b7280",fontSize:12}}>→ {t.contract}</span>}
                  {t.strike&&<span style={{color:"#8ea2bf",fontSize:12}}>K{t.strike} {t.expiry} ({t.daysLeft}d)</span>}
                  <span style={{color:"#8ea2bf"}}>@ ₹{t.price}</span>
                  {t.qty&&<span style={{color:"#8ea2bf",fontSize:12}}>×{t.qty}</span>}
                  <span style={{color:"#2ed47a",fontSize:12}}>TP ₹{t.tp}</span>
                  <span style={{color:"#ff5b6c",fontSize:12}}>SL ₹{t.sl}</span>
                  <span style={{flex:1,color:"#8ea2bf",fontSize:12,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.signals||t.note||t.error}</span>
                  <span style={{color:t.status==="PLACED"?"#2ed47a":t.status==="FAILED"?"#ff5b6c":"#8ea2bf",fontWeight:700,fontSize:12,flexShrink:0}}>{t.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}


      {/* ── CHAIN VIEW ── */}
      {view==="chain"&&(
        <div style={{display:"flex",flex:1,overflow:"hidden"}}>
          {/* Left: commodity list */}
          <div style={{width:130,background:"#0b1624",borderRight:"1px solid #1a2c44",overflowY:"auto",flexShrink:0}}>
            <div style={{padding:"10px 12px 6px",fontSize:12,color:"#8ea2bf",letterSpacing:2,fontWeight:700}}>COMMODITIES</div>
            {instrLoading&&<div style={{padding:"20px 12px",color:"#8ea2bf",fontSize:11,textAlign:"center"}}>Loading...</div>}
            {instrError&&<div style={{padding:"10px 12px",color:"#ff5b6c",fontSize:10}}>{instrError}</div>}
            {allNames.map(name=>{
              const info=MCX_BASE[name]||{name,color:"#8ea2bf"};
              return(
                <div key={name} onClick={()=>setSelected(name)}
                  style={{padding:"8px 12px",cursor:"pointer",borderLeft:"3px solid "+(selected===name?info.color:"transparent"),background:selected===name?"rgba(232,179,57,0.06)":"transparent"}}>
                  <div style={{fontSize:11,fontWeight:600,color:selected===name?info.color:"#5c3d2e"}}>{info.name||name}</div>
                  <div style={{fontSize:12,color:"#8ea2bf"}}>{(commodities[name]||[]).length} contracts</div>
                </div>
              );
            })}
          </div>

          {/* Right: contracts table */}
          <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
            <div style={{background:"#0b1624",borderBottom:"1px solid #1a2c44",padding:"8px 14px",display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
              <span style={{fontSize:13,fontWeight:700,color:meta.color}}>{meta.name||selected}</span>
              <div style={{display:"flex",background:"#07101c",border:"1px solid #1a2c44",borderRadius:0,overflow:"hidden"}}>
                {["ALL","FUT","CE","PE"].map(t=>(
                  <button key={t} onClick={()=>setTypeFilter(t)}
                    style={{background:typeFilter===t?"rgba(0,212,170,0.15)":"transparent",border:"none",borderRight:"1px solid #1a2c44",padding:"4px 9px",color:typeFilter===t?"#e8b339":t==="CE"?"#2ed47a":t==="PE"?"#ff5b6c":"#8ea2bf",fontFamily:"inherit",fontSize:12,fontWeight:typeFilter===t?700:400,cursor:"pointer"}}>
                    {t==="FUT"?"Futures":t==="CE"?"CE":t==="PE"?"PE":"All"}
                  </button>
                ))}
              </div>
              <select value={expiryFilter} onChange={e=>setExpFilter(e.target.value)}
                style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,padding:"4px 8px",color:"#e7edf5",fontFamily:"inherit",fontSize:10,outline:"none"}}>
                <option value="ALL">All Expiries</option>
                {chainExpiries.map(e=><option key={e} value={e}>{e}</option>)}
              </select>
              <input value={chainSearch} onChange={e=>setChainSearch(e.target.value)} placeholder="Search..."
                style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,padding:"4px 8px",color:"#e7edf5",fontFamily:"inherit",fontSize:10,outline:"none",width:110}}/>
              <button onClick={()=>fetchChainQuotes(displayedContracts)} style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,padding:"4px 10px",color:"#e8b339",fontFamily:"inherit",fontSize:10,cursor:"pointer"}}>
                {chainQL?"⟳":"↻"} Quotes
              </button>
              <span style={{fontSize:12,color:"#8ea2bf",marginLeft:"auto"}}>{displayedContracts.length} contracts</span>
            </div>
            <div style={{flex:1,overflowY:"auto"}}>
              {displayedContracts.length===0&&<div style={{textAlign:"center",padding:"40px",color:"#8ea2bf",fontSize:12}}>{selected?"No contracts match filters":"Select a commodity"}</div>}
              {displayedContracts.length>0&&(
                <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}><table style={{width:"100%",borderCollapse:"collapse",fontSize:11,minWidth:480}}>
                  <thead>
                    <tr style={{background:"#0b1624",borderBottom:"1px solid #1a2c44"}}>
                      {["SYMBOL","TYPE","EXPIRY","STRIKE","LOT","LTP","OI","CHG%","TRADE"].map(h=>(
                        <th key={h} style={{padding:"7px 10px",color:"#8ea2bf",fontSize:12,letterSpacing:1,fontWeight:700,textAlign:"left",whiteSpace:"nowrap"}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayedContracts.map((c,i)=>{
                      const q=chainQuotes[c.tradingsymbol]||{};
                      const ltp=q.last_price||0;
                      const prev=q.ohlc?.close||0;
                      const chg=prev>0?((ltp-prev)/prev*100):0;
                      const tColor=c.instrument_type==="FUT"?"#e8b339":c.instrument_type==="CE"?"#2ed47a":"#ff5b6c";
                      return(
                        <tr key={c.tradingsymbol} style={{borderBottom:"1px solid #161b22",background:i%2===0?"transparent":"rgba(255,255,255,0.01)"}}>
                          <td style={{padding:"7px 10px",fontWeight:700,color:"#e7edf5",fontFamily:"inherit",fontSize:10}}>{c.tradingsymbol}</td>
                          <td style={{padding:"7px 10px"}}>
                            <span style={{fontSize:12,fontWeight:700,padding:"2px 6px",borderRadius:0,background:tColor+"22",color:tColor}}>{c.instrument_type}</span>
                          </td>
                          <td style={{padding:"7px 10px",color:"#8ea2bf",fontSize:10}}>{c.expiry}</td>
                          <td style={{padding:"7px 10px",color:"#8ea2bf",fontSize:10}}>{c.strike>0?c.strike:"—"}</td>
                          <td style={{padding:"7px 10px",color:"#8ea2bf",fontSize:10}}>{c.lot_size}</td>
                          <td style={{padding:"7px 10px",fontWeight:700,color:ltp>0?"#e7edf5":"#23405f"}}>{ltp>0?"₹"+ltp.toLocaleString("en-IN"):"—"}</td>
                          <td style={{padding:"7px 10px",color:"#8ea2bf",fontSize:10}}>{q.oi?q.oi.toLocaleString("en-IN"):"—"}</td>
                          <td style={{padding:"7px 10px",fontWeight:700,color:chg>0?"#2ed47a":chg<0?"#ff5b6c":"#8ea2bf"}}>{ltp>0?(chg>=0?"+":"")+chg.toFixed(2)+"%":"—"}</td>
                          <td style={{padding:"7px 10px",whiteSpace:"nowrap"}}>
                            {ltp>0&&<>
                              <button onClick={()=>onTrade({symbol:c.tradingsymbol,exchange:"MCX",side:"BUY",price:ltp,qty:c.lot_size})}
                                style={{background:"rgba(22,163,74,0.15)",border:"1px solid rgba(63,185,80,0.4)",borderRadius:0,padding:"3px 6px",color:"#2ed47a",fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer",marginRight:3}}>▲B</button>
                              <button onClick={()=>onTrade({symbol:c.tradingsymbol,exchange:"MCX",side:"SELL",price:ltp,qty:c.lot_size})}
                                style={{background:"rgba(220,38,38,0.15)",border:"1px solid rgba(248,81,73,0.4)",borderRadius:0,padding:"3px 6px",color:"#ff5b6c",fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer"}}>▼S</button>
                            </>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table></div>
              )}
            </div>
          </div>
        </div>
      )}
      {mcxStratPopup&&(
        <StrategyPopup
          {...mcxStratPopup}
          canTrade={canTrade}
          onTrade={(params)=>{
            onTrade({
              symbol:         params.symbol,
              exchange:       "MCX",
              side:           params.side,
              price:          params.price,
              qty:            params.qty||1,
              product:        "NRML",
              instrumentType: params.instrumentType,
              suggestedStrike:params.suggestedStrike,
              signals:        mcxStratPopup.signals||[],
              signalLabel:    (mcxStratPopup.signals||[]).map(s=>s.label).join(' · '),
              underlying:     mcxStratPopup.symbol,
            });
          }}
          onClose={()=>setMcxStratPopup(null)}
        />
      )}
      {showSignalModal&&(
        <SignalSettingsModal
          params={signalParams||DEFAULT_SIGNAL_PARAMS}
          onChange={setSignalParams}
          onClose={()=>setShowSignalModal(false)}
        />
      )}
      {chartModal&&(
        <ChartModal
          symbol={chartModal.symbol}
          exchange={chartModal.exchange||"MCX"}
          timeframe={timeframe||"1hour"}
          onClose={()=>setChartModal(null)}
          signalParams={signalParams}
          onShowToast={onShowToast}
          authed={authed}
          scanZones={chartModal.zones||null}
          isAdmin={isAdmin}
          parentActiveScan={activeScan}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// DELTA EXCHANGE TAB
// ══════════════════════════════════════════════════════════════════════════════
function DeltaTab({isAdmin=false, canTrade=false, signalParams, setSignalParams, bgEngineEnabled, setBgEngineEnabled, setBgEngineCfg, bgLog, setBgLog, onSetDeltaAuthed, onShowToast, onAddNote, onJournalEntry=null, onFireAlerts=null, sendTgAlert=null, onAddVirtual=null, vpsAutoTradeOn=false}) {
  const [showSignalModal, setShowSignalModal] = useState(false);
  const [deltaAuthed, setDeltaAuthed] = useState(null);
  const [deltaStratPopup, setDeltaStratPopup] = useState(null);
  const [chartModal,      setChartModal]      = useState(null); // {symbol,exchange}
  // ── AI Panel ──────────────────────────────────────────────────────────────
  const { aiResults:dAiRes, aiLoading:dAiLoad, aiError:dAiErr,
          aiVisible:dAiVis, setAiVisible:setDAiVis, runAIScan:runDAI,
          autoTrade:dAutoTrade, setAutoTrade:setDAutoTrade,
          minAutoConf:dMinConf, setMinAutoConf:setDMinConf, autoFired:dAutoFired,
          marginPct:dMargPct, setMarginPct:setDMargPct,
          maxDaily:dMaxDay, setMaxDaily:setDMaxDay,
          maxScans:dMaxScans, setMaxScans:setDMaxScans,
          scanCount:dScanCount, balance:dBalance
        } = useAIPanel({authed:deltaAuthed, signalParams, exchange:"DELTA"});  // null=loading, true, false
  const [view,        setView]        = useState("scan"); // scan | wallet | positions | orders | chain

  // ── Scan state ──────────────────────────────────────────────────────────
  const [watchlist,     setWatchlist]     = useState("TOP 5");
  const [customLists,   setCustomLists]   = useLocalStorage("delta_custom_lists", {});
  const [showWLMgr,     setShowWLMgr]     = useState(false);
  const [newListName,   setNewListName]   = useState("");
  const [newSymInput,   setNewSymInput]   = useState("");
  const [timeframe,   setTimeframe]   = useState("1h");
  const [activeScan,  setActiveScan]  = useState([1,3]);
  const [mtfEnabled,  setMtfEnabled]  = useLocalStorage("delta_mtf_enabled", false);
  const [mtfTrendTF,  setMtfTrendTF]  = useLocalStorage("delta_mtf_trend", "1hour");
  const [mtfEntryTF,  setMtfEntryTF]  = useLocalStorage("delta_mtf_entry", "15min");
  const [scanning,    setScanning]    = useState(false);
  const [scanResults, setScanResults] = useState([]);
  const [scanError,   setScanError]   = useState(null);
  const [lastScan,    setLastScan]    = useState(null);
  const [liveMode,    setLiveMode]    = useState(false);
  const liveRef = useRef(null);

  // ── Auto-trade state ────────────────────────────────────────────────────
  const [atEnabled,  setAtEnabled]  = useState(false);
  const [atMode,     setAtMode]     = useLocalStorage('nse_pro_delta_at_mode', "perp");
  const [atCfg,      setAtCfg]      = useLocalStorage('nse_pro_delta_at_cfg', {
    capital: 10, minSignals: 2, conditions: [1,2,3,4,5,8],
    tpPct: 3.0, slPct: 1.5, cooldown: 300, minDays: 1,
    optTpPct: 50, optSlPct: 30, orderType: "limit_order",
    maxMarginUsagePct: 90, liqWarningPct: 10,
    trailEnabled: false, trailPct: 1.5,
    dailyLimit: 5, maxTradesPct: 50,
    autoSize: true, otmSteps: 2,
    expiryType: "weekly", allowMixed: true,
    leverage: 10,       // default leverage for AT orders
    scalpLeverage: 50,  // leverage for 3m scalp trades
  });
  // Migrate old conditions
  useEffect(()=>{
    if(!atCfg.conditions || atCfg.conditions.length <= 2){
      setAtCfg(p=>({...p, conditions:[1,2,3,4,5,8]}));
    }
  },[]);
  const [atLog,   setAtLog]   = useState([]);
  const [atFired, setAtFired] = useState({});
  const [dailyTrades,setDailyTrades] = useState({date:"",count:0});
  const dailyTradesRef = useRef({date:"",count:0});
  useEffect(()=>{ dailyTradesRef.current = dailyTrades; },[dailyTrades]);

  // ── State that refs depend on — must be declared before refs ──────────────
  const [riskData,      setRiskData]      = useState(null);
  const [riskRefAt,     setRiskRefAt]     = useState(0);
  const [deltaWatchers, setDeltaWatchers] = useState([]);

  const atCfgRef         = useRef(atCfg);
  const atEnabledRef     = useRef(false);
  const atModeRef        = useRef(atMode);
  const atFiredRef       = useRef(atFired);
  const riskDataRef      = useRef(null);
  const deltaWatchersRef = useRef([]);
  const atCfgDeltaRef    = useRef(atCfg);
  useEffect(()=>{ atCfgRef.current       = atCfg;         },[atCfg]);
  useEffect(()=>{ atEnabledRef.current   = atEnabled;     },[atEnabled]);
  useEffect(()=>{ atModeRef.current      = atMode;        },[atMode]);
  useEffect(()=>{ atFiredRef.current     = atFired;       },[atFired]);
  useEffect(()=>{ riskDataRef.current    = riskData;      },[riskData]);
  useEffect(()=>{ deltaWatchersRef.current = deltaWatchers; },[deltaWatchers]);
  useEffect(()=>{ atCfgDeltaRef.current  = atCfg;         },[atCfg]);
  const [atValidate, setAtValidate] = useLocalStorage('delta_at_validate', true);  // auto-trade only GO-validated
  const [atPaper,    setAtPaper]    = useLocalStorage('delta_at_paper', false);   // paper trades instead of real orders
  const atValidateRef = useRef(true);
  const atPaperRef    = useRef(false);
  const vpsATRef      = useRef(false);
  useEffect(()=>{ atValidateRef.current  = atValidate;    },[atValidate]);
  useEffect(()=>{ atPaperRef.current     = atPaper;       },[atPaper]);
  useEffect(()=>{ vpsATRef.current       = vpsAutoTradeOn;},[vpsAutoTradeOn]);

  // Record validated-GO Delta signals as paper trades instead of real orders.
  function deltaPaperTrade(resList){
    if(!onAddVirtual) return;
    let n=0;
    (resList||[]).forEach(r=>{
      const sigs=(r.signals||[]).filter(s=>activeScan.includes(s.scan));
      const side=sigs.some(s=>s.type==="BUY")?"BUY":"SELL";
      const price=parseFloat(r.price||r.ltp||0);
      if(!price) return;
      onAddVirtual({ symbol:r.symbol, exchange:"DELTA", side, price, qty:1,
        product:"NRML", tab_mode:"delta", source:"auto",
        signals:sigs.map(s=>s.label).join(" · "), signal:sigs });
      n++;
    });
    if(n>0&&onShowToast) onShowToast(`📝 Paper: recorded ${n} validated GO Delta trade(s)`,"info");
  }

  // Validate signalled Delta results and return only the GO ones (else []).
  async function deltaValidateForAT(results){
    const signalled = (results||[]).filter(r=>!r.error && (r.signals||[]).some(s=>activeScan.includes(s.scan)));
    if(signalled.length===0) return [];
    const items = signalled.map(r=>{
      const sigs=(r.signals||[]).filter(s=>activeScan.includes(s.scan));
      const dir=sigs.some(s=>s.type==="BUY")?"BUY":"SELL";
      return {symbol:r.symbol, resolution:timeframe, direction:dir, signal_price:r.price||r.ltp||null};
    });
    try{
      const out = await deltaFetch("/delta/scan/validate/batch",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({items})});
      const goSet = new Set((out.results||[]).filter(v=>!v.error&&v.verdict==="GO").map(v=>v.symbol));
      return signalled.filter(r=>goSet.has(r.symbol));
    }catch(e){
      if(onShowToast) onShowToast("Delta auto-trade validation failed — trades skipped","warn");
      return [];
    }
  }

  // Candle duration in seconds per timeframe
  const TF_SECONDS = {"1m":60,"3m":180,"5m":300,"15m":900,"30m":1800,
    "1h":3600,"2h":7200,"4h":14400,"6h":21600,"12h":43200,"1d":86400};

  // ══════════════════════════════════════════════════════════════════════════
  // DELTA TP / SL / TRAILING SL WATCHER — polls positions every 5s
  // ══════════════════════════════════════════════════════════════════════════
  const deltaWatcherRef = useRef(null);

  useEffect(()=>{
    // Start watcher when authed, stop when not
    if(!deltaAuthed){ clearInterval(deltaWatcherRef.current); return; }

    deltaWatcherRef.current = setInterval(async()=>{
      const active = deltaWatchersRef.current.filter(w=>w.status==="WATCHING");
      if(active.length===0) return;

      // Fetch live prices for all open positions in one call
      let posMap = {};
      try {
        const d = await deltaFetch("/delta/positions/ltp");
        (d.positions||[]).forEach(p=>{ posMap[p.symbol]=p; });
      } catch{ return; }

      const triggered = [];
      const trailed   = [];

      for(const w of active){
        const pos = posMap[w.symbol];
        // If position no longer exists on Delta — manually closed or liquidated
        if(!pos || parseFloat(pos.size||0)===0){
          triggered.push({...w,
            status: "CANCELLED_POSITION_CLOSED",
            triggeredType: "MANUAL_EXIT",
            triggeredAt: new Date().toISOString(),
            note: "Position closed on Delta exchange"});
          continue;
        }

        const ltp = parseFloat(pos.ltp || pos.mark_price || 0);
        if(!ltp) continue;

        let updated = {...w};

        // ── Trailing SL logic ──────────────────────────────────────────────
        if(w.trailEnabled && w.trailPct > 0){
          if(w.side==="BUY"){
            const hwm = parseFloat(w.hwm || w.entryPrice);
            if(ltp > hwm){
              // Price moved up — raise SL to lock profits
              const newHWM = ltp;
              const newSL  = parseFloat((ltp*(1-w.trailPct/100)).toFixed(6));
              if(newSL > (updated.slPrice||0)){
                updated = {...updated, hwm:newHWM, slPrice:newSL, slEnabled:true};
                trailed.push(updated);
              } else {
                updated = {...updated, hwm:newHWM};
                trailed.push(updated);
              }
            }
          } else {
            // SELL position — trail upward (price drops, SL drops with it)
            const lwm = parseFloat(w.lwm || w.entryPrice);
            if(ltp < lwm){
              const newLWM = ltp;
              const newSL  = parseFloat((ltp*(1+w.trailPct/100)).toFixed(6));
              if(!updated.slPrice || newSL < updated.slPrice){
                updated = {...updated, lwm:newLWM, slPrice:newSL, slEnabled:true};
                trailed.push(updated);
              } else {
                updated = {...updated, lwm:newLWM};
                trailed.push(updated);
              }
            }
          }
        }

        // ── TP check ─────────────────────────────────────────────────────
        let hit = null;
        if(updated.tpEnabled && updated.tpPrice){
          const reached = updated.side==="BUY" ? ltp>=updated.tpPrice : ltp<=updated.tpPrice;
          if(reached) hit="TP";
        }
        // ── SL check (uses trailing-updated slPrice) ──────────────────────
        if(!hit && updated.slEnabled && updated.slPrice){
          const reached = updated.side==="BUY" ? ltp<=updated.slPrice : ltp>=updated.slPrice;
          if(reached) hit="SL";
        }

        if(hit){
          // Close position via market order (reduce_only)
          try{
            const closeRes = await deltaFetch("/delta/position/close",{
              method:"POST",
              headers:{"Content-Type":"application/json"},
              body: JSON.stringify({
                product_id: w.productId,
                size:       Math.abs(parseInt(pos.size)||w.size),
                side:       w.side,
              })
            });
            const entryP  = parseFloat(w.entryPrice||0);
            const exitP   = ltp;
            const pnl     = w.side==="BUY" ? (exitP-entryP)*Math.abs(w.size) : (entryP-exitP)*Math.abs(w.size);
            const trailNote= w.trailEnabled ? ` [Trail HWM $${parseFloat(w.hwm||w.entryPrice).toFixed(2)}]` : "";
            const msg     = `${hit==="TP"?"🟢 TP HIT":"🔴 SL HIT"} ${w.symbol} @ $${exitP.toFixed(4)} | PnL $${pnl.toFixed(2)}${trailNote}`;
            // Toast and telegram
            if(onShowToast) onShowToast(msg, hit==="TP"?"ok":"error");
            try{
              const tgToken = localStorage.getItem("nse_pro_tg_token")||"";
              const tgChat  = localStorage.getItem("nse_pro_tg_chat")||"";
              if(tgChat){  // VPS uses shared token if tgToken is empty
                await deltaFetch("/delta/alert/telegram",{method:"POST",
                  headers:{"Content-Type":"application/json"},
                  body:JSON.stringify({message:`<b>${msg}</b>`,bot_token:tgToken,chat_id:tgChat})
                }).catch(()=>{});
              }
            }catch{}
            triggered.push({...updated, status:"TRIGGERED",
              triggeredType:hit, ltpAtTrigger:ltp,
              exitOrderId:closeRes.order_id, pnlEst:pnl.toFixed(4)});
          } catch(e){
            triggered.push({...updated, status:"ERROR", triggeredType:hit, error:e.message});
          }
        }
      }

      // Apply updates
      if(triggered.length>0 || trailed.length>0){
        setDeltaWatchers(prev=>prev.map(w=>{
          const t=triggered.find(x=>x.id===w.id); if(t) return t;
          const r=trailed.find(x=>x.id===w.id);   if(r) return r;
          return w;
        }));
      }
    }, 5000);  // poll every 5s

    return ()=>clearInterval(deltaWatcherRef.current);
  },[deltaAuthed]);

  // ── Scalper Offer auto-close watcher ────────────────────────────────────
  // Delta Scalper Offer: closing fee = ZERO if closed within window
  //   BTC futures  → 30 min window (close at 28 min to be safe)
  //   Other futures → 15 min window (close at 13 min to be safe)
  const scalp30Ref = useRef(null);
  useEffect(()=>{
    if(!deltaAuthed) return;
    scalp30Ref.current = setInterval(async()=>{
      const now     = Date.now();
      // Read from ref — not stale closure
      const active  = deltaWatchersRef.current.filter(w=>
        w.status==="WATCHING" && w.scalpMode===true && w.openedAt
      );
      if(active.length===0) return;

      for(const w of active) {
        const isBTC    = (w.symbol||"").toUpperCase().includes("BTC");
        const windowMs = isBTC ? 28*60*1000 : 13*60*1000;
        const elapsed  = now - w.openedAt;
        if(elapsed < windowMs) continue;

        // Normalize product id — AT watcher uses productId, ticket uses product_id
        const prodId = w.product_id || w.productId;
        const qty    = w.qty || w.size || 1;
        if(!prodId) {
          console.warn("Scalp auto-close: no product_id for", w.symbol);
          continue;
        }

        try {
          const label = isBTC ? "28min (BTC)" : "13min";
          if(onShowToast) onShowToast(
            `⏰ Auto-closing ${w.symbol} — scalper window ${label}`, "warn"
          );

          // /delta/position/close requires: product_id, size, side (current position side)
          // side is the CURRENT position side — endpoint flips it to close
          const positionSide = w.side || "BUY";

          await deltaFetch("/delta/position/close", {
            method:"POST", headers:{"Content-Type":"application/json"},
            body:JSON.stringify({
              product_id: parseInt(prodId),
              size:        parseInt(qty),
              side:        positionSide,  // required — endpoint flips to close
            })
          });
          setDeltaWatchers(p=>p.map(x=>x.id===w.id?{...x,status:"CLOSED_SCALP"}:x));
        } catch(e) {
          // Extract readable error message from object or string
          const msg = typeof e === "string" ? e
            : e?.message || e?.detail || JSON.stringify(e) || "unknown error";
          if(onShowToast) onShowToast(`⚠ Auto-close failed ${w.symbol}: ${msg}`, "error");
          console.warn("Scalp auto-close failed:", w.symbol, e);
        }
      }
    }, 15000);  // check every 15s
    return ()=>clearInterval(scalp30Ref.current);
  },[deltaAuthed]);

  // ── Wallet / Positions / Orders state ─────────────────────────────────────
  const [wallet,    setWallet]    = useState(null);
  const [positions, setPositions] = useState(null);
  const [orders,    setOrders]    = useState(null);
  const [products,  setProducts]  = useState(null);  // for chain
  const [acctLoading, setAcctLoading] = useState(false);
  const [acctError,   setAcctError]   = useState(null);

  // ── Order ticket ────────────────────────────────────────────────────────
  const [ticket,    setTicket]    = useState(null);  // {symbol, product_id, side, price}
  const [placing,   setPlacing]   = useState(false);
  const [placeMsg,  setPlaceMsg]  = useState(null);
  const [pidCache,  setPidCache]  = useState({});   // symbol → product_id cache
  const pidCacheRef = useRef({});
  useEffect(()=>{ pidCacheRef.current = pidCache; },[pidCache]);

  // ── Calculate smart qty from available balance and product margin ────────
  const calcDeltaSmartQty = (availBal, price, initialMarginPct=0.05, marginUsePct=0.9, leverage=1) => {
    // Effective margin per contract accounts for leverage
    const leverageMarginPct  = leverage > 1 ? (1/leverage) : initialMarginPct;
    const effectiveMarginPct = Math.max(initialMarginPct, leverageMarginPct);
    const marginPerContract  = price * effectiveMarginPct;
    if(marginPerContract <= 0) return 1;
    // Use 90% of available balance (matches Delta's available margin usage)
    return Math.max(1, Math.floor((availBal * marginUsePct) / marginPerContract));
  };

  // Lookup product_id + margin info, then open ticket with smart qty
  // instrumentType: "PERP" | "PE" | "CE" | null (defaults to PERP)
  // suggestedStrike: for options, preferred strike price
  const openDeltaTicket = async (symbol, side, price, instrumentType="PERP", suggestedStrike=null) => {
    const availBal = riskData?.available_balance || 0;
    const isOption = instrumentType==="PE" || instrumentType==="CE";
    const optType  = instrumentType; // "CE" | "PE"

    setPlaceMsg(null);
    setTicket({symbol, product_id:null, side, price:price||0, qty:1,
      orderType:"limit_order", tp:null, sl:null, availBal,
      contractType: isOption?(instrumentType==="CE"?"call_options":"put_options"):"perpetual_futures",
      _loading:true, instrumentType});

    try {
      let match = null;

      if(isOption) {
        // ── Delta option contract naming: BTC-310325-70000-C / BTC-310325-70000-P
        // The underlying_asset_symbol for BTCUSD perp options is "BTC"
        // We must query by contract_type, not by symbol
        const contractType = optType==="CE" ? "call_options" : "put_options";

        // Derive underlying from symbol: "BTCUSD" → "BTC", "ETHUSD" → "ETH"
        const underlying = symbol.replace(/USD.*$/i,"").replace(/USDT.*$/i,"");

        // Fetch options with underlying filter
        // Delta API: GET /v2/products?contract_type=call_options&state=live
        // Then filter by underlying_asset.symbol
        let allOptions = [];
        try {
          const d1 = await deltaFetch(`/delta/products?contract_type=${contractType}&state=live`);
          allOptions = (d1.products||[]).filter(p=>{
            const ua = (p.underlying_asset?.symbol||p.underlying_asset_symbol||"").toUpperCase();
            return ua===underlying.toUpperCase() || ua===symbol.toUpperCase();
          });
        } catch(e) {
          console.warn("Options fetch failed:", e.message);
        }

        // If VPS endpoint supports underlying_asset_symbol param directly:
        if(allOptions.length===0) {
          try {
            const d2 = await deltaFetch(`/delta/products?underlying_asset_symbol=${underlying}&contract_type=${contractType}&state=live`);
            allOptions = d2.products||[];
          } catch(e) {}
        }

        if(allOptions.length > 0) {
          const now = Date.now();
          const strike = suggestedStrike || price;
          const sorted = allOptions
            .filter(p=>new Date(p.expiry_at||p.expiry||0).getTime() > now)
            .sort((a,b)=>{
              // Nearest expiry first
              const exA = new Date(a.expiry_at||a.expiry||0).getTime();
              const exB = new Date(b.expiry_at||b.expiry||0).getTime();
              if(Math.abs(exA-exB) > 7*86400*1000) return exA-exB; // >7 days diff → prefer nearer
              // Same expiry week → closest strike
              return Math.abs(parseFloat(a.strike_price||0)-strike)
                   - Math.abs(parseFloat(b.strike_price||0)-strike);
            });
          match = sorted[0] || allOptions[0];
        }

        if(!match) {
          // Fallback: fetch all live products and scan for options
          try {
            const fallback = await deltaFetch(`/delta/products?state=live`);
            const all = fallback.products||[];
            const opts = all.filter(p=>{
              const ua = (p.underlying_asset?.symbol||"").toUpperCase();
              return p.contract_type===contractType &&
                (ua===underlying.toUpperCase() || ua.includes(underlying.toUpperCase()));
            });
            if(opts.length>0) {
              const now2 = Date.now();
              const strike2 = suggestedStrike || price;
              match = opts
                .filter(p=>new Date(p.expiry_at||p.expiry||0).getTime()>now2)
                .sort((a,b)=>{
                  const exA=new Date(a.expiry_at||a.expiry||0).getTime();
                  const exB=new Date(b.expiry_at||b.expiry||0).getTime();
                  if(Math.abs(exA-exB)>7*86400*1000) return exA-exB;
                  return Math.abs(parseFloat(a.strike_price||0)-strike2)
                       - Math.abs(parseFloat(b.strike_price||0)-strike2);
                })[0] || opts[0];
            }
          } catch(e) {}
        }

        if(!match) {
          // Final fallback: show error but still open perp ticket so user can trade
          setPlaceMsg({ok:false,
            msg:`⚠ No ${optType==="CE"?"CALL":"PUT"} options found for ${symbol} (underlying: ${symbol.replace(/USD.*/i,"")}). Check Delta Exchange has options listed. Opening perpetual instead.`});
          const perpData = await deltaFetch(`/delta/products?symbol=${symbol}&state=live`);
          match = (perpData.products||[]).find(p=>p.contract_type==="perpetual_futures") || (perpData.products||[])[0];
        }

      } else {
        // PERP: query directly by symbol
        const data = await deltaFetch(`/delta/products?symbol=${symbol}&state=live`);
        const products = data.products || [];
        match = products.find(p=>p.contract_type==="perpetual_futures")
             || products.find(p=>p.symbol===symbol)
             || products[0];
      }

      if(match) {
        const initMarginPct = parseFloat(match.initial_margin||match.initial_margin_per_unit||0.05);
        const optionMarginPct = isOption ? Math.max(initMarginPct, 0.10) : initMarginPct;
        const strikePrice = parseFloat(match.strike_price||0);

        // Fetch live option mark price (premium) from Delta ticker
        // Delta ticker endpoint: GET /v2/tickers/{symbol} (not by product_id)
        // options have symbols like "P-BTC-82000-290526" or "BTC-310325-70000-C"
        let optionMarkPrice = 0;
        if(isOption && match) {
          const optSymbol = match.symbol;
          // Try multiple Delta ticker endpoints
          const tickerAttempts = [
            ()=>deltaFetch(`/delta/ticker/${optSymbol}`),
            ()=>deltaFetch(`/delta/products/${match.id}/ticker`),
            ()=>deltaFetch(`/delta/tickers?symbol=${optSymbol}`),
          ];
          for(const attempt of tickerAttempts) {
            try {
              const t = await attempt();
              const mp = parseFloat(
                t?.result?.mark_price || t?.mark_price ||
                t?.result?.close || t?.close ||
                t?.result?.last_price || t?.last_price ||
                match.mark_price || match.close_price || 0
              );
              if(mp > 0) { optionMarkPrice = mp; break; }
            } catch(e) { continue; }
          }
          // Last resort: use product's last mark_price field if available
          if(!optionMarkPrice) {
            optionMarkPrice = parseFloat(match.mark_price||match.last_price||match.close_price||0);
          }
        }

        setPidCache(p=>({...p,[symbol]:match.id,[symbol+"_im"]:initMarginPct}));
        pidCacheRef.current[symbol] = match.id;
        pidCacheRef.current[symbol+"_im"] = initMarginPct;

        const ticketPrice = isOption
          ? (optionMarkPrice > 0 ? parseFloat(optionMarkPrice.toFixed(4)) : 0)
          : (price||0);

        // For smart qty: use underlying price * margin% for option sell
        // (Delta margin is based on underlying, not premium)
        const marginBase  = isOption ? price : ticketPrice;
        const userLev     = parseFloat(atCfg?.leverage||1)||1;
        const smartQty    = marginBase>0 && availBal>0
          ? calcDeltaSmartQty(availBal, marginBase, optionMarginPct, 0.9, userLev)
          : 1;

        setTicket({
          symbol:          match.symbol || symbol,
          product_id:      match.id,
          side,
          price:           ticketPrice,         // option premium (mark price)
          underlyingPrice: price||0,            // BTCUSD spot price for margin calc
          qty:             smartQty,
          orderType:       "limit_order",
          tp:null, sl:null,
          availBal,
          initMarginPct:   optionMarginPct,
          contractType:    match.contract_type,
          instrumentType:  optType,
          lotSize:         match.contract_value||1,
          strikePrice:     strikePrice||suggestedStrike,
          expiry:          match.expiry_at||match.expiry||"",
          underlying:      symbol,
          _loading:        false,
        });
      } else {
        setPlaceMsg({ok:false, msg:`✗ No products found for ${symbol}`});
        setTicket(null);
      }
    } catch(e) {
      setPlaceMsg({ok:false, msg:`✗ ${e.message}`});
      setTicket(null);
    }
  };

  // ── Check Delta auth on mount ───────────────────────────────────────────
  useEffect(()=>{
    let tries = 0;
    const MAX = 10;  // 10 × 2s = 20s before giving up
    const check = ()=>{
      deltaFetch("/delta/auth/status")
        .then(d=>{
          setDeltaAuthed(d.authenticated===true);
          if(!d.authenticated && d.reason) console.warn("Delta auth failed:", d.reason, d.tip||"");
        })
        .catch(err=>{ 
          tries++;
          if(tries < MAX) setTimeout(check, 2000);
          else { console.error("Delta :8001 unreachable after", MAX, "tries"); setDeltaAuthed(false); }
        });
    };
    check();
  },[]);

  // ── Scan ────────────────────────────────────────────────────────────────
  const runScan = useCallback(async()=>{
    if(!deltaAuthed) return;
    setScanning(true); setScanError(null);
    try {
      const symbols = customLists[watchlist] || DELTA_WATCHLISTS[watchlist] || DELTA_WATCHLISTS["TOP 5"];
      const data = await deltaFetch("/delta/scan",{
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ symbols, resolution: timeframe, tech_weight: 0.7,
          signal_params: signalParams||DEFAULT_SIGNAL_PARAMS })
      });
      setScanResults(data.results||[]);
      setLastScan(new Date().toLocaleTimeString("en-IN"));

      // ── Fire Telegram alerts for Delta signals ─────────────────────────
      if(onFireAlerts) {
        const deltaAlerts = (data.results||[])
          .filter(r=>(r.signals||[]).some(s=>activeScan.includes(s.scan)))
          .flatMap(r=>{
            const zone = (r.demandLow && r.demandHigh) || (r.supplyLow && r.supplyHigh)
              ? {
                  demand: r.demandLow && r.demandHigh ? {low:r.demandLow, high:r.demandHigh} : null,
                  supply: r.supplyLow && r.supplyHigh ? {low:r.supplyLow, high:r.supplyHigh} : null,
                } : null;
            return (r.signals||[])
              .filter(s=>activeScan.includes(s.scan))
              .map(s=>({symbol:r.symbol, price:r.price||r.ltp||0, signal:s, exchange:'DELTA', timeframe:timeframe||'15min', zone}));
          });
        if(deltaAlerts.length>0) onFireAlerts(deltaAlerts);
      }

      // ── Auto-trade engine (validate first when enabled) ────────────────
      if(!atEnabledRef.current) return;
      if(!isAdmin) return;  // AT blocked for non-admin (signal mode only)
      if(vpsATRef.current){
        if(onShowToast) onShowToast("🖥️ VPS is auto-trading — desktop Delta AT skipped (avoids double orders)", "warn");
        return;
      }
      let atResults = data.results||[];
      if(atValidateRef.current){
        atResults = await deltaValidateForAT(data.results||[]);
        if(!atResults || atResults.length===0){
          if(onShowToast) onShowToast("Delta auto-trade: no signals validated GO — no trade","info");
          return;
        }
      }
      // Paper mode — record validated trades, place no real orders
      if(atPaperRef.current){ deltaPaperTrade(atResults); return; }
      const cfg   = atCfgRef.current;
      const fired = atFiredRef.current;
      const mode  = atModeRef.current;
      const now   = Date.now();

      // ── Fetch fresh risk data before processing any AT orders ──────────────
      let risk = null;
      try {
        const sinceLastRisk = (Date.now() - riskRefAt) / 1000;
        if(sinceLastRisk > 30) {  // refresh risk every 30s max
          risk = await deltaFetch("/delta/risk");
          setRiskData(risk);
          riskDataRef.current = risk;
          setRiskRefAt(Date.now());
        } else {
          risk = riskDataRef.current;
        }
      } catch{ risk = null; }

      // ── Check liquidation warnings — block ALL new trades if danger ────────
      const liqWarnings = risk?.liq_warnings || [];
      const liqDangerSymbols = new Set(liqWarnings.map(w=>w.symbol));
      if(liqWarnings.length > 0) {
        const worst = liqWarnings.reduce((a,b)=>a.liq_dist_pct<b.liq_dist_pct?a:b);
        setAtLog(p=>[{id:`risk-liq-${Date.now()}`,
          time:new Date().toLocaleTimeString("en-IN"),
          status:"LIQ_DANGER",
          note:`⚠ ${liqWarnings.length} position(s) near liquidation! Closest: ${worst.symbol} ${worst.liq_dist_pct}% from liq @ ${worst.liq_price}. No new trades.`,
          symbol:"RISK_CHECK"},...p].slice(0,200));
        return; // abort entire scan cycle — protect existing positions first
      }

      // ── Margin check ──────────────────────────────────────────────────────
      const availBal    = risk?.available_balance ?? 0;
      const marginUsage = risk?.margin_usage_pct  ?? 0;
      const maxUsage    = cfg.maxMarginUsagePct ?? 90;
      if(availBal > 0 && marginUsage >= maxUsage) {
        setAtLog(p=>[{id:`risk-margin-${Date.now()}`,
          time:new Date().toLocaleTimeString("en-IN"),
          status:"MARGIN_FULL",
          note:`Margin ${marginUsage}% ≥ limit ${maxUsage}%. Free: $${availBal.toFixed(2)}`,
          symbol:"RISK_CHECK"},...p].slice(0,200));
        return;
      }

      // ── Daily trade limit ─────────────────────────────────────────────────
      const deltaDaily = checkDailyLimit(dailyTradesRef, cfg.dailyLimit||0);
      if(!deltaDaily.ok) {
        setAtLog(p=>[{id:`daily-limit-${Date.now()}`,
          time:new Date().toLocaleTimeString("en-IN"),
          status:"DAILY_LIMIT",
          note:`Daily limit ${cfg.dailyLimit} trades reached — no new trades today`,
          symbol:"RISK_CHECK"},...p].slice(0,200));
        return;
      }

      // ── Candle-close cooldown: timeframe seconds per symbol ────────────────
      const tfSeconds = TF_SECONDS[timeframe] || 3600;

      for(const stock of (atResults||[])) {
        if(!stock.price || stock.error) continue;

        // Candle-duration cooldown (prevents multiple fires per candle)
        const timeSinceFired = (now - (fired[stock.symbol]||0)) / 1000;
        if(timeSinceFired < Math.max(cfg.cooldown, tfSeconds)) continue;

        // Daily limit re-check inside loop
        if(!checkDailyLimit(dailyTradesRef, cfg.dailyLimit||0).ok) break;

        const sigs = (stock.signals||[]).filter(s=>cfg.conditions.includes(s.scan)&&activeScan.includes(s.scan));
        if(sigs.length < cfg.minSignals) continue;

        const buys  = sigs.filter(s=>s.type==="BUY").length;
        const sells = sigs.filter(s=>s.type==="SELL").length;
        const isMixed = buys > 0 && sells > 0;
        // Allow mixed signals if user enabled it
        if(isMixed && !cfg.allowMixed) continue;
        // Determine direction
        const side  = buys>=sells ? "buy" : "sell";

        // Per-symbol: skip if this symbol already has a position near liquidation
        if(liqDangerSymbols.has(stock.symbol)) {
          setAtLog(p=>[{id:`skip-liq-${Date.now()}`,time:new Date().toLocaleTimeString("en-IN"),
            symbol:stock.symbol, status:"SKIP_LIQ",
            note:`Skipped — position near liquidation`},...p].slice(0,200));
          continue;
        }

        // ── Smart position sizing — scale between min and max% of available ──
        const tradeCapital = cfg.autoSize && availBal > 0
          ? calcPositionSize(availBal, cfg.capital, cfg.maxTradesPct||50, stock.price)
          : cfg.capital;

        // Final per-order check: tradeCapital must fit in remaining free margin
        const safeAvail = availBal * (maxUsage / 100);
        if(availBal > 0 && tradeCapital > safeAvail) {
          setAtLog(p=>[{id:`skip-margin-${Date.now()}`,time:new Date().toLocaleTimeString("en-IN"),
            symbol:stock.symbol, status:"SKIP_MARGIN",
            note:`Order $${tradeCapital.toFixed(2)} > safe $${safeAvail.toFixed(2)} (free $${availBal.toFixed(2)})`},...p].slice(0,200));
          continue;
        }
        // Skip if available balance can't cover even 1 contract margin
        if(availBal > 0 && availBal < 5) {
          setAtLog(p=>[{id:`skip-bal-${Date.now()}`,time:new Date().toLocaleTimeString("en-IN"),
            symbol:stock.symbol, status:"SKIP_LOW_BALANCE",
            note:`Balance too low $${availBal.toFixed(2)} — need at least $5 for margin`},...p].slice(0,200));
          continue;
        }

        setAtFired(p=>({...p,[stock.symbol]:now}));

        try {
          if(mode==="perp") {
            // Find product_id for this symbol
            // Check pid cache first
            let pid = pidCacheRef.current[stock.symbol] || null;
            if(!pid) {
              try {
                const pd = await deltaFetch(`/delta/products?symbol=${stock.symbol}&state=live`);
                const perps = (pd.products||[]).filter(p=>p.symbol===stock.symbol);
                if(perps.length>0) {
                  pid = perps[0].id;
                  setPidCache(p=>({...p,[stock.symbol]:pid}));
                  pidCacheRef.current[stock.symbol] = pid;
                }
              } catch{}
            }

            if(!pid){
              setAtLog(p=>[{id:`at-nf-${Date.now()}`,time:new Date().toLocaleTimeString("en-IN"),
                symbol:stock.symbol,status:"NO_PRODUCT",note:"Product ID not found"},...p].slice(0,200));
              continue;
            }

            // ── Proper sizing using initial_margin from Delta product ──────
            // initial_margin is the % of notional needed as margin (e.g. 0.05 = 5%)
            // contract_value is how many USD per unit (usually 1 for perps)
            const productDetail = (await deltaFetch(`/delta/products?symbol=${stock.symbol}&state=live`).catch(()=>({products:[]})));
            const prodInfo      = (productDetail.products||[]).find(p=>p.symbol===stock.symbol) || {};
            const initMarginPct = parseFloat(prodInfo.initial_margin || "0.05") || 0.05;
            const contractVal   = parseFloat(prodInfo.contract_value || "1") || 1;
            // Margin per contract = price × contract_value × initial_margin%
            const marginPerContract = stock.price * contractVal * (initMarginPct > 1 ? initMarginPct/100 : initMarginPct);
            // qty = how many contracts we can open with tradeCapital
            const qty = marginPerContract > 0
              ? Math.max(1, Math.floor(tradeCapital / marginPerContract))
              : Math.max(1, Math.floor(tradeCapital / (stock.price * 0.05)));
            // Safety: never exceed 30% of available balance in one trade
            const maxQtyByCap = marginPerContract > 0
              ? Math.floor((availBal * 0.30) / marginPerContract)
              : qty;
            const finalQty = Math.max(1, Math.min(qty, maxQtyByCap));
            const price = parseFloat(stock.price.toFixed(6));
            // Fee-aware TP: Delta taker fee 0.05% × 2 sides = 0.10% round trip
            // Add 0.10% buffer → minimum TP = 0.20% to be profitable
            const TAKER_FEE    = 0.0005;
            const minTpPct     = (TAKER_FEE*2 + 0.001) * 100; // 0.20%
            const effectiveTpPct = Math.max(cfg.tpPct||3.0, minTpPct);
            const tp    = parseFloat((price*(side==="buy"?1+effectiveTpPct/100:1-effectiveTpPct/100)).toFixed(6));
            const sl    = parseFloat((price*(side==="buy"?1-cfg.slPct/100:1+cfg.slPct/100)).toFixed(6));
            // Margin adequacy: need 1.5× initial margin as buffer against liquidation
            const marginNeeded = marginPerContract * finalQty * 1.5;
            if(availBal > 0 && marginNeeded > availBal){
              setAtLog(p=>[{id:`at-skip-${Date.now()}`,time:new Date().toLocaleTimeString("en-IN"),
                symbol:stock.symbol,status:"SKIPPED",
                note:`Insufficient margin: need $${marginNeeded.toFixed(2)}, have $${availBal.toFixed(2)}`},...p].slice(0,200));
              continue;
            }

            // Bracket orders only work with limit_order on Delta Exchange
            const isScalp       = timeframe==="3m" || timeframe==="5m";
            const isBTC         = (stock.symbol||"").toUpperCase().includes("BTC");
            const leverage      = isScalp ? (cfg.scalpLeverage||50) : (cfg.leverage||10);
            const useAppWatcher = cfg.orderType==="market_order" || cfg.trailEnabled;
            const orderBody = {
              product_id: pid, side, size: finalQty,
              order_type: cfg.orderType,
              time_in_force: "gtc",
              ...(leverage > 1 ? {leverage} : {}),
              ...(cfg.orderType==="limit_order" ? {limit_price: String(price)} : {}),
              // Only attach exchange-side brackets for limit orders without trailing
              ...(!useAppWatcher && effectiveTpPct>0 ? {bracket_take_profit_limit_price: String(tp)} : {}),
              ...(!useAppWatcher && cfg.slPct>0 ? {bracket_stop_loss_limit_price:   String(sl)} : {}),
            };
            const res = await deltaFetch("/delta/order/place",{
              method:"POST", headers:{"Content-Type":"application/json"},
              body: JSON.stringify(orderBody)
            });

            // Add to app-side watcher — always for scalp mode, optionally for others
            if(useAppWatcher || isScalp){
              const watchId = `dw-${Date.now()}-${stock.symbol}`;
              setDeltaWatchers(prev=>[{
                id:watchId, symbol:stock.symbol, product_id:pid,
                side:side.toUpperCase(), size:finalQty,
                entryPrice:price,
                tpPrice:tp, tpEnabled:cfg.tpPct>0,
                slPrice:sl, slEnabled:cfg.slPct>0,
                trailEnabled:cfg.trailEnabled||false,
                trailPct:cfg.trailPct||1.5,
                hwm:price, lwm:price,
                status:   "WATCHING",
                openedAt: Date.now(),
                scalpMode:isScalp,
                windowMin:isBTC ? 28 : 13,
                orderId:  res.order_id,
              },...prev]);
            }

            setAtLog(p=>[{id:`at-${Date.now()}`,time:new Date().toLocaleTimeString("en-IN"),
              symbol:stock.symbol, mode:"PERP", side:side.toUpperCase(),
              qty, price, tp, sl, status:"PLACED", orderId:res.order_id,
              tpSlMode:useAppWatcher?"APP WATCHER":"EXCHANGE BRACKET",
              trail:cfg.trailEnabled?`Trail ${cfg.trailPct}%`:"",
              signals:sigs.map(s=>s.label).join(", ")},...p].slice(0,200));

          } else {
            // ── OPTIONS MODES: sell_otm / sell_far_otm / buy_atm ──────────────
            const underlying = stock.symbol.replace("USD","").replace("USDT_PERP","").replace("_PERP","");

            // Determine expiry min_days based on expiryType
            const expiryDays = {
              "daily":   0,   // 0-2 days (daily expiry)
              "weekly":  3,   // 3-9 days (weekly expiry)
              "monthly": 14,  // 14+ days (monthly expiry)
            }[cfg.expiryType||"weekly"] ?? 3;

            const expiryMaxDays = {
              "daily":   2,
              "weekly":  9,
              "monthly": 60,
            }[cfg.expiryType||"weekly"] ?? 9;

            // For sell_otm: sell OTM against the signal direction
            // BUY signal → sell OTM PUT (downside protected by directional bias)
            // SELL signal → sell OTM CALL
            // MIXED → sell both OTM CALL + OTM PUT (strangle)
            const isSellMode = mode==="sell_otm" || mode==="sell_far_otm";
            const otmSteps   = mode==="sell_far_otm" ? Math.max(cfg.otmSteps||2, 5) : (cfg.otmSteps||2);

            const foRes = await apiFetch("/crypto/fo/find_otm",{
              method:"POST", headers:{"Content-Type":"application/json"},
              body: JSON.stringify({
                underlying,
                signal_side:  isMixed ? "MIXED" : (side==="buy"?"BUY":"SELL"),
                mode:         mode,           // sell_otm | sell_far_otm | buy_atm
                otm_steps:    otmSteps,
                min_days:     expiryDays,
                max_days:     expiryMaxDays,
                expiry_type:  cfg.expiryType||"weekly",
              })
            });

            if(!foRes.found){
              setAtLog(p=>[{id:`at-nf-${Date.now()}`,time:new Date().toLocaleTimeString("en-IN"),
                symbol:stock.symbol, mode:mode.toUpperCase(), status:"NO_CONTRACT",
                note:foRes.reason, signals:sigs.map(s=>s.label).join(", ")},...p].slice(0,200));
              continue;
            }

            // Place each leg
            for(const contract of (foRes.legs||[])) {
              const optPrice = contract.option_ltp || 0;
              if(optPrice<=0) continue;

              const orderSide = contract.action || (isSellMode ? "sell" : "buy");
              const qty = Math.max(1, Math.floor(tradeCapital / Math.max(optPrice, 1)));

              // For SELL positions: TP is when premium decays (lower price), SL is higher price
              const tp = parseFloat((optPrice*(orderSide==="sell"
                ? 1 - cfg.optTpPct/100   // TP: premium decays to 50% for seller
                : 1 + cfg.optTpPct/100   // TP: premium increases for buyer
              )).toFixed(6));
              const sl = parseFloat((optPrice*(orderSide==="sell"
                ? 1 + cfg.optSlPct/100   // SL: premium increases 30% (seller exits)
                : 1 - cfg.optSlPct/100   // SL: premium drops 20% (buyer exits)
              )).toFixed(6));

              const useAppWatcher = cfg.orderType==="market_order" || cfg.trailEnabled;
              const res = await deltaFetch("/delta/order/place",{
                method:"POST", headers:{"Content-Type":"application/json"},
                body: JSON.stringify({
                  product_id: contract.product_id,
                  side: orderSide,
                  size: qty,
                  order_type: cfg.orderType,
                  time_in_force: "gtc",
                  ...(cfg.orderType==="limit_order" ? {limit_price:String(optPrice)} : {}),
                  ...(!useAppWatcher&&cfg.optTpPct>0 ? {bracket_take_profit_limit_price:String(tp)} : {}),
                  ...(!useAppWatcher&&cfg.optSlPct>0 ? {bracket_stop_loss_limit_price:String(sl)}   : {}),
                })
              });

              if(useAppWatcher){
                setDeltaWatchers(prev=>[{
                  id:`dw-opt-${Date.now()}-${contract.symbol}`,
                  symbol:contract.symbol, product_id:contract.product_id,
                  side:orderSide.toUpperCase(), size:qty,
                  entryPrice:optPrice,
                  tpPrice:tp, tpEnabled:cfg.optTpPct>0,
                  slPrice:sl, slEnabled:cfg.optSlPct>0,
                  trailEnabled:false, // no trailing for short options
                  hwm:optPrice, lwm:optPrice,
                  status:"WATCHING",
                  openedAt:new Date().toLocaleTimeString("en-IN"),
                  orderId:res.order_id,
                },...prev]);
              }

              setAtLog(p=>[{id:`at-${Date.now()}`,time:new Date().toLocaleTimeString("en-IN"),
                symbol:stock.symbol, contract:contract.symbol, mode:mode.toUpperCase(),
                optType:contract.instrument_type, strike:contract.strike,
                expiry:contract.expiry, daysLeft:contract.days_left,
                side:orderSide.toUpperCase(), qty, price:optPrice, tp, sl,
                status:"PLACED", orderId:res.order_id,
                expiryType:cfg.expiryType||"weekly",
                tpSlMode:useAppWatcher?"APP WATCHER":"EXCHANGE BRACKET",
                signals:sigs.map(s=>s.label).join(", ")},...p].slice(0,200));
            }
          }
        } catch(e){
          setAtLog(p=>[{id:`at-err-${Date.now()}`,time:new Date().toLocaleTimeString("en-IN"),
            symbol:stock.symbol, mode, status:"FAILED", error:e.message,
            signals:sigs.map(s=>s.label).join(", ")},...p].slice(0,200));
        }
      }
    } catch(e){ setScanError(e.message); }
    finally{ setScanning(false); }
  },[deltaAuthed, watchlist, timeframe, activeScan]);

  // Live scan interval + periodic risk refresh
  useEffect(()=>{
    if(liveMode&&deltaAuthed){
      runScan();
      liveRef.current=setInterval(runScan,60000);
      // Refresh risk every 60s so margin data stays fresh for AT engine
      const riskInterval = setInterval(()=>{
        deltaFetch("/delta/risk").then(d=>{setRiskData(d);riskDataRef.current=d;}).catch(()=>{});
      },60000);
      return()=>{ clearInterval(liveRef.current); clearInterval(riskInterval); };
    } else {
      clearInterval(liveRef.current);
    }
    return()=>clearInterval(liveRef.current);
  },[liveMode,deltaAuthed,runScan]);

  // ── Load account data ───────────────────────────────────────────────────
  const loadAcct = useCallback(async(type)=>{
    setAcctLoading(true); setAcctError(null);
    try {
      if(type==="wallet")    { const d=await deltaFetch("/delta/wallet");    setWallet(d.balances||[]); }
      if(type==="positions") { const d=await deltaFetch("/delta/positions"); setPositions(d.positions||[]); }
      if(type==="orders")    { const d=await deltaFetch("/delta/orders");    setOrders(d.orders||[]); }
      if(type==="chain")     {
        const d=await deltaFetch("/delta/products?state=live");
        const grouped={};
        (d.products||[]).forEach(p=>{
          const u=p.underlying||"OTHER";
          if(!grouped[u]) grouped[u]=[];
          grouped[u].push(p);
        });
        setProducts(grouped);
      }
    } catch(e){ setAcctError(e.message); }
    finally{ setAcctLoading(false); }
  },[]);

  useEffect(()=>{
    if(!deltaAuthed) return;
    if(view==="wallet")    loadAcct("wallet");
    if(view==="positions") loadAcct("positions");
    if(view==="orders")    loadAcct("orders");
    if(view==="chain")     loadAcct("chain");
    // Always refresh risk when switching tabs — AT engine needs it
    deltaFetch("/delta/risk").then(d=>{setRiskData(d);riskDataRef.current=d;}).catch(()=>{});
  },[view, deltaAuthed]);

  // ── Place order from ticket ─────────────────────────────────────────────
  const placeOrder = async()=>{
    if(!ticket) return;
    if(!ticket.product_id) {
      setPlaceMsg({ok:false, msg:"✗ Product ID missing — try opening ticket again"});
      return;
    }
    setPlacing(true); setPlaceMsg(null);
    try {
      const price = parseFloat(ticket.price||0);
      const leverage = parseInt(ticket.leverage||1);
      const body = {
        product_id:    parseInt(ticket.product_id),
        side:          ticket.side.toLowerCase(),
        size:          parseInt(ticket.qty||1),
        order_type:    ticket.orderType||"limit_order",
        time_in_force: "gtc",
        ...(leverage > 1 ? {leverage} : {}),
        ...(ticket.orderType!=="market_order" && price>0 ? {limit_price:String(price)} : {}),
        // Bracket TP — limit price = target, trigger = same (Delta uses limit for TP)
        ...(ticket.tp ? {
          bracket_take_profit_limit_price: String(ticket.tp),
          bracket_take_profit_price:       String(ticket.tp),
        } : {}),
        // Bracket SL — limit price slightly worse than trigger for guaranteed fill
        ...(ticket.sl ? {
          bracket_stop_loss_price:       String(ticket.sl),
          bracket_stop_loss_limit_price: String(
            ticket.side==="buy"
              ? (parseFloat(ticket.sl)*0.998).toFixed(2)   // SL limit 0.2% below trigger for buy
              : (parseFloat(ticket.sl)*1.002).toFixed(2)   // SL limit 0.2% above trigger for sell
          ),
          close_on_trigger: true,
        } : {}),
      };
      const res = await deltaFetch("/delta/order/place",{
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify(body)
      });
      // Record in Firebase journal
      if(onJournalEntry) {
        onJournalEntry({
          orderId:  res.order_id,
          symbol:   ticket.symbol,
          exchange: "DELTA",
          side:     ticket.side,
          qty:      parseInt(ticket.qty||1),
          price:    parseFloat(ticket.price||0),
          product:  "DELTA",
          source:   "manual",
          tabMode:  "delta",
          tpPrice:  ticket.tp ? parseFloat(ticket.tp) : null,
          slPrice:  ticket.sl ? parseFloat(ticket.sl) : null,
          instrumentType: ticket.contractType||"PERP",
          notes:    `Delta ${ticket.orderType} order`,
        });
      }
      setPlaceMsg({ok:true, msg:`✓ Order placed — ID: ${res.order_id}${(timeframe==="3m"||timeframe==="5m")?` ⏰ Scalp — auto-close in ${ticket.symbol?.includes("BTC")?"28":"13"}min`:""}`});
      // Add to watcher with scalpMode flag
      if(ticket.tp || ticket.sl || timeframe==="3m" || timeframe==="5m") {
        const isBTC = (ticket.symbol||"").toUpperCase().includes("BTC");
        setDeltaWatchers(prev=>[{
          id:         `dw-${Date.now()}-${ticket.symbol}`,
          symbol:     ticket.symbol,
          product_id: ticket.product_id,
          side:       ticket.side,
          qty:        parseInt(ticket.qty||1),
          entryPrice: parseFloat(ticket.price||0),
          tpEnabled:  !!ticket.tp,  tpPrice:  parseFloat(ticket.tp||0),
          slEnabled:  !!ticket.sl,  slPrice:  parseFloat(ticket.sl||0),
          status:     "WATCHING",
          openedAt:   Date.now(),
          scalpMode:  timeframe==="3m" || timeframe==="5m",
          windowMin:  isBTC ? 28 : 13,  // reminder of the window
          orderId:    res.order_id,
        },...prev]);
      }
      setTimeout(()=>setTicket(null), 2000);
    } catch(e){ setPlaceMsg({ok:false, msg:`✗ ${e.message}`}); }
    finally{ setPlacing(false); }
  };

  const toggleScan = n => setActiveScan(p=>p.includes(n)?p.filter(x=>x!==n):[...p,n]);
  const withSignals = scanResults.filter(r=>(r.signals||[]).some(s=>activeScan.includes(s.scan)));

  // ── Not connected banner ────────────────────────────────────────────────
  if(deltaAuthed===false) return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100%",gap:14,fontFamily:"inherit"}}>
      <div style={{fontSize:36}}>Δ</div>
      <div style={{fontSize:16,fontWeight:700,color:"#e7edf5"}}>Delta Exchange — Not Connected</div>
      <div style={{fontSize:12,color:"#8ea2bf",textAlign:"center",lineHeight:1.8,maxWidth:420}}>
        1. Open <code style={{color:"#e8b339"}}>delta_main.py</code> and set your API keys<br/>
        2. Set <code style={{color:"#e8b339"}}>PLATFORM = "india"</code> (or "global")<br/>
        3. Run <code style={{color:"#e8b339"}}>start.bat</code> — Delta starts on port 8001<br/>
        4. Make sure your IP is whitelisted in Delta → API Keys settings
      </div>
      <div style={{fontSize:11,color:"#23405f",textAlign:"center"}}>
        Diagnose: <a href={`https://api.rabtrade.in/delta/auth/status`} target="_blank" style={{color:"#6b7280"}}>/delta/auth/status</a>
        &nbsp;·&nbsp;<a href={`https://api.rabtrade.in/delta/health`} target="_blank" style={{color:"#6b7280"}}>/delta/health</a>
      </div>
      <button onClick={()=>{
          setDeltaAuthed(null);
          deltaFetch("/delta/auth/status")
            .then(d=>setDeltaAuthed(d.authenticated===true))
            .catch(()=>setDeltaAuthed(false));
        }}
        style={{background:"linear-gradient(135deg,#92400e,#92400e)",border:"none",borderRadius:0,padding:"10px 24px",color:"#fff",fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer"}}>
        ↻ Retry Connection
      </button>
    </div>
  );

  if(deltaAuthed===null) return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100%",color:"#8ea2bf",fontFamily:"inherit",gap:14}}>
      <div style={{fontSize:24}}>Δ</div>
      <div style={{fontSize:13,color:"#e7edf5"}}>Connecting to Delta Exchange...</div>
      <div style={{fontSize:11,color:"#8ea2bf"}}>Checking port 8001 — make sure start.bat is running</div>
      <div style={{display:"flex",gap:10}}>
        <button onClick={()=>{
          deltaFetch("/delta/auth/status")
            .then(d=>setDeltaAuthed(d.authenticated===true))
            .catch(()=>setDeltaAuthed(false));
        }} style={{background:"linear-gradient(135deg,#92400e,#92400e)",border:"none",borderRadius:0,padding:"8px 20px",color:"#fff",fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer"}}>
          ↻ Retry
        </button>
        <button onClick={()=>setDeltaAuthed(false)}
          style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,padding:"8px 16px",color:"#8ea2bf",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>
          Setup Guide
        </button>
      </div>
      <div style={{fontSize:10,color:"#23405f",marginTop:4}}>
        Test: <a href={`https://api.rabtrade.in/delta/health`} target="_blank" style={{color:"#e8b339"}}>{`https://api.rabtrade.in/delta/health`}</a>
      </div>
    </div>
  );

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",fontFamily:"inherit"}}>

      {/* ── Top nav ── */}
      <div style={{background:"#0b1624",borderBottom:"1px solid #1a2c44",padding:"6px 12px",display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
        <span style={{fontSize:14,fontWeight:700,color:"#e8b339",marginRight:6}}>Δ</span>
        {[["scan","📡 Scan"],["wallet","💰 Wallet"],["positions","📋 Positions"],["orders","📦 Orders"],["chain","🔗 Chain"],["watchers","🎯 Watchers"]].map(([v,l])=>(
          <button key={v} onClick={()=>setView(v)}
            style={{background:view===v?"rgba(0,212,170,0.15)":"transparent",
              border:"1px solid "+(view===v?"#e8b339":"#1a2c44"),borderRadius:0,
              padding:"4px 12px",color:view===v?"#e8b339":"#8ea2bf",
              fontFamily:"inherit",fontSize:10,fontWeight:view===v?700:400,cursor:"pointer"}}>
            {l}
          </button>
        ))}
        <div style={{flex:1}}/>
        <span style={{fontSize:12,color:"#2ed47a"}}>● LIVE</span>
        <span style={{fontSize:12,color:"#8ea2bf"}}>india.delta.exchange</span>
      </div>

      {/* ── SCAN VIEW ── */}
      {view==="scan"&&(
        <div style={{display:"flex",flexDirection:"column",flex:1,overflow:"hidden"}}>

          {/* Scan controls */}
          <div style={{background:"#07101c",borderBottom:"1px solid #1a2c44",padding:"6px 12px",display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
            {/* Watchlist tabs */}
            <div style={{display:"flex",gap:3,flexWrap:"wrap",alignItems:"center"}}>
              {[...Object.keys(DELTA_WATCHLISTS),...Object.keys(customLists)].map(k=>(
                <button key={k} onClick={()=>setWatchlist(k)}
                  style={{background:watchlist===k?"rgba(0,212,170,0.15)":"transparent",
                    border:"1px solid "+(watchlist===k?"#e8b339":customLists[k]?"#6b728033":"#1a2c44"),
                    borderRadius:0,padding:"3px 9px",
                    color:watchlist===k?"#e8b339":customLists[k]?"#6b7280":"#8ea2bf",
                    fontFamily:"inherit",fontSize:12,fontWeight:watchlist===k?700:400,cursor:"pointer",whiteSpace:"nowrap"}}>
                  {customLists[k]?"⭐ ":""}{k}
                  <span style={{color:"#8ea2bf",fontSize:11,marginLeft:3}}>
                    {(customLists[k]||DELTA_WATCHLISTS[k]||[]).length}
                  </span>
                </button>
              ))}
              <button onClick={()=>setShowWLMgr(true)}
                style={{background:"transparent",border:"1px solid #1a2c44",borderRadius:0,padding:"3px 8px",color:"#8ea2bf",fontFamily:"inherit",fontSize:10,cursor:"pointer"}}>＋</button>
            </div>
            {/* Timeframe */}
            <div style={{display:"flex",background:"#07101c",border:"1px solid #1a2c44",borderRadius:0,overflow:"hidden"}}>
              {[["3m","3m"],["5m","5m"],["15m","15m"],["30m","30m"],["1h","1H"],["4h","4H"],["1d","1D"]].map(([v,l])=>(
                <button key={v} onClick={()=>setTimeframe(v)}
                  style={{background:timeframe===v?"rgba(0,212,170,0.2)":"transparent",border:"none",
                    borderRight:"1px solid #1a2c44",padding:"4px 9px",
                    color:timeframe===v?"#e8b339":"#8ea2bf",fontFamily:"inherit",fontSize:10,fontWeight:timeframe===v?700:400,cursor:"pointer"}}>
                  {l}
                </button>
              ))}
            </div>
            {/* Signal filters + MTF */}
            {[1,2,3,4].map(n=>{const on=activeScan.includes(n);return(
              <button key={n} onClick={()=>toggleScan(n)}
                style={{background:on?"rgba(0,212,170,0.1)":"transparent",border:"1px solid "+(on?"#e8b339":"#1a2c44"),borderRadius:0,padding:"3px 8px",color:on?"#e8b339":"#8ea2bf",fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
                {on?"✓ ":""}{DELTA_SCAN_LABELS[n]}
              </button>
            );})}
            <button onClick={()=>{setMtfEnabled(p=>!p);setActiveScan(p=>!mtfEnabled?[...p.filter(x=>x!==6),6]:p.filter(x=>x!==6));}}
              style={{background:mtfEnabled?"rgba(96,165,250,0.2)":"transparent",border:"1.5px solid "+(mtfEnabled?"#49c2e4":"#1a2c44"),borderRadius:0,padding:"3px 8px",color:mtfEnabled?"#49c2e4":"#8ea2bf",fontFamily:"inherit",fontSize:12,fontWeight:mtfEnabled?700:400,cursor:"pointer"}}>
              {mtfEnabled?"✓ ":""}🔀 MTF
            </button>
            {mtfEnabled&&<>
              <span style={{fontSize:10,color:"#5a728f"}}>T:</span>
              {["1hour","4hour"].map(tf=>(<button key={tf} onClick={()=>setMtfTrendTF(tf)} style={{background:mtfTrendTF===tf?"rgba(96,165,250,0.15)":"transparent",border:"1px solid "+(mtfTrendTF===tf?"#49c2e4":"#1a2c44"),borderRadius:0,padding:"2px 5px",color:mtfTrendTF===tf?"#49c2e4":"#5a728f",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>{tf==="1hour"?"1H":"4H"}</button>))}
              <span style={{fontSize:10,color:"#5a728f"}}>E:</span>
              {["5min","15min"].map(tf=>(<button key={tf} onClick={()=>setMtfEntryTF(tf)} style={{background:mtfEntryTF===tf?"rgba(34,197,94,0.15)":"transparent",border:"1px solid "+(mtfEntryTF===tf?"#2ed47a":"#1a2c44"),borderRadius:0,padding:"2px 5px",color:mtfEntryTF===tf?"#2ed47a":"#5a728f",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>{tf==="5min"?"5m":"15m"}</button>))}
            </>}
            <div style={{flex:1}}/>
            {lastScan&&<span style={{fontSize:12,color:"#8ea2bf"}}>🕐 {lastScan}</span>}
            {withSignals.length>0&&<span style={{fontSize:12,fontWeight:700,color:"#e8b339"}}>{withSignals.length} signals</span>}
            <button onClick={()=>setLiveMode(p=>!p)}
              style={{background:liveMode?"rgba(22,163,74,0.15)":"rgba(220,38,38,0.08)",border:"1px solid "+(liveMode?"#2ed47a":"#ff5b6c"),borderRadius:0,padding:"4px 10px",color:liveMode?"#2ed47a":"#ff5b6c",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
              {liveMode?"● LIVE ON":"● LIVE OFF"}
            </button>
            <button onClick={()=>setShowSignalModal(true)}
              style={{background:"#0f1d30",border:"1px solid #a16207",borderRadius:0,padding:"5px 9px",color:"#49c2e4",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}
              title="Signal Thresholds — RSI/TSI/EMA/Vol">
              ⚙ Signals
            </button>
            <button onClick={runScan} disabled={scanning}
              style={{background:scanning?"#1a2c44":"linear-gradient(135deg,#92400e,#92400e)",border:"none",borderRadius:0,padding:"5px 14px",color:"#fff",fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:scanning?"default":"pointer"}}>
              {scanning?"⟳":"▶"} SCAN
            </button>
            <ValidateAllBar
              fetcher={deltaFetch} endpoint="/delta/scan/validate/batch" storeKey="delta"
              authed={deltaAuthed} onShowToast={onShowToast} sendTgAlert={sendTgAlert}
              buildItems={()=>scanResults
                .filter(r=>!r.error&&(r.signals||[]).some(s=>activeScan.includes(s.scan)))
                .map(r=>{ const sigs=(r.signals||[]).filter(s=>activeScan.includes(s.scan));
                          const dir=sigs.some(s=>s.type==="BUY")?"BUY":"SELL";
                          return {symbol:r.symbol,resolution:timeframe,direction:dir,signal_price:r.price||r.ltp||null}; })}
            />
            <button onClick={()=>runDAI(customLists[watchlist]||DELTA_WATCHLISTS[watchlist]||[], "DELTA", timeframe||"15m", null, "delta", onShowToast, onAddNote, onJournalEntry, sendTgAlert, isAdmin)}
              disabled={dAiLoad||!deltaAuthed}
              style={{background:dAiVis?"rgba(163,113,247,0.2)":"rgba(163,113,247,0.08)",
                border:"1px solid rgba(163,113,247,0.4)",borderRadius:0,
                padding:"5px 10px",color:"#49c2e4",fontFamily:"inherit",
                fontSize:10,fontWeight:700,cursor:"pointer",opacity:dAiLoad?0.6:1}}>
              {dAiLoad?"⟳":"🤖"} AI
            </button>
          </div>

          {/* Auto-trade bar — admin only */}
          <div style={{background:atEnabled&&isAdmin?"rgba(220,38,38,0.08)":"#07101c",borderBottom:"1px solid "+((atEnabled&&isAdmin)?"rgba(248,81,73,0.25)":"#0f1d30"),padding:"5px 12px",display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
            {isAdmin ? (
              <>
                <button onClick={()=>{
                    const next = !atEnabled;
                    setAtEnabled(next);
                    if(setBgEngineEnabled) setBgEngineEnabled(next);
                    if(next && setBgEngineCfg) setBgEngineCfg({
                      ...atCfg,
                      atEnabled: true,
                      symbols: customLists[watchlist]||DELTA_WATCHLISTS[watchlist]||[],
                      timeframe: timeframe,
                      pidCache: pidCacheRef.current||{},
                    });
                  }}
                  style={{background:atEnabled?"rgba(248,81,73,0.2)":"rgba(0,212,170,0.08)",border:"1px solid "+(atEnabled?"#ff5b6c":"#e8b339"),borderRadius:0,padding:"3px 12px",color:atEnabled?"#ff5b6c":"#e8b339",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                  {atEnabled?"🤖 AUTO ON — STOP":"🤖 AUTO OFF — START"}
                </button>
                <button onClick={()=>setAtValidate(v=>!v)}
                  title="When ON, Delta auto-trade validates each signal (EMA extension + higher-TF trend) and only trades the GO ones. On validation error it skips."
                  style={{background:atValidate?"rgba(46,212,122,0.15)":"rgba(255,91,108,0.08)",
                    border:"1px solid "+(atValidate?"#2ed47a":"#ff5b6c"),borderRadius:0,padding:"3px 10px",
                    color:atValidate?"#2ed47a":"#ff8a95",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                  {atValidate?"✅ Validate ✓":"⚠️ Validate ✗"}
                </button>
                <button onClick={()=>setAtPaper(v=>!v)}
                  title="Paper mode: record validated-GO signals as paper trades instead of placing real Delta orders."
                  style={{background:atPaper?"rgba(96,165,250,0.15)":"rgba(232,179,57,0.06)",
                    border:"1px solid "+(atPaper?"#60a5fa":"#23405f"),borderRadius:0,padding:"3px 10px",
                    color:atPaper?"#60a5fa":"#8ea2bf",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                  {atPaper?"📝 Paper ON":"📝 Paper"}
                </button>
              </>
            ) : (
              <span style={{fontSize:12,color:"#5a728f",padding:"3px 10px",
                background:"rgba(96,165,250,0.05)",border:"1px solid #1a2c44",borderRadius:0}}>
                📡 Signal Mode · Manual trading only
              </span>
            )}
            {isAdmin&&(
            <div style={{display:"flex",background:"#07101c",border:"1px solid #1a2c44",borderRadius:0,overflow:"hidden"}}>
              {[
                ["perp",         "📈 Perp"],
                ["sell_otm",     "🎯 Sell OTM"],
                ["sell_far_otm", "🛡 Sell Far OTM"],
                ["buy_atm",      "⚡ Buy ATM"],
              ].map(([v,l])=>(
                <button key={v} onClick={()=>setAtMode(v)}
                  style={{background:atMode===v?"rgba(96,165,250,0.15)":"transparent",border:"none",
                    borderRight:"1px solid #1a2c44",padding:"3px 8px",
                    color:atMode===v?"#e8b339":"#8ea2bf",fontFamily:"inherit",fontSize:12,
                    fontWeight:atMode===v?700:400,cursor:"pointer",whiteSpace:"nowrap"}}>
                  {l}
                </button>
              ))}
            </div>
            )}
            {/* Expiry type + OTM steps — options modes, admin only */}
            {isAdmin&&atMode!=="perp"&&(
              <div style={{display:"flex",background:"#07101c",border:"1px solid #1a2c44",borderRadius:0,overflow:"hidden"}}>
                {[["daily","Daily"],["weekly","Weekly"],["monthly","Monthly"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setAtCfg(p=>({...p,expiryType:v}))}
                    style={{background:atCfg.expiryType===v?"rgba(96,165,250,0.15)":"transparent",
                      border:"none",borderRight:"1px solid #1a2c44",padding:"3px 7px",
                      color:atCfg.expiryType===v?"#6b7280":"#8ea2bf",fontFamily:"inherit",
                      fontSize:12,fontWeight:atCfg.expiryType===v?700:400,cursor:"pointer"}}>
                    {l}
                  </button>
                ))}
              </div>
            )}
            {/* OTM steps for sell modes */}
            {(atMode==="sell_otm"||atMode==="sell_far_otm")&&(
              <div style={{display:"flex",alignItems:"center",gap:3}}>
                <span style={{fontSize:12,color:"#8ea2bf"}}>OTM:</span>
                {[1,2,3,5,8].map(n=>(
                  <button key={n} onClick={()=>setAtCfg(p=>({...p,otmSteps:n}))}
                    style={{background:atCfg.otmSteps===n?"rgba(163,113,247,0.2)":"transparent",
                      border:"1px solid "+(atCfg.otmSteps===n?"#49c2e4":"#1a2c44"),
                      borderRadius:0,padding:"2px 5px",color:atCfg.otmSteps===n?"#49c2e4":"#8ea2bf",
                      fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
                    {n}Δ
                  </button>
                ))}
              </div>
            )}
            {/* Allow mixed signals toggle */}
            <div style={{display:"flex",alignItems:"center",gap:4}}>
              <button onClick={()=>setAtCfg(p=>({...p,allowMixed:!p.allowMixed}))}
                style={{background:atCfg.allowMixed?"rgba(0,212,170,0.15)":"transparent",
                  border:"1px solid "+(atCfg.allowMixed?"#e8b339":"#23405f"),
                  borderRadius:0,padding:"2px 7px",color:atCfg.allowMixed?"#e8b339":"#8ea2bf",
                  fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                MIXED {atCfg.allowMixed?"✓":"✗"}
              </button>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:4}}>
              <span style={{fontSize:12,color:"#8ea2bf"}}>Capital $</span>
              <input type="number" value={atCfg.capital} min="1" step="10"
                onChange={e=>setAtCfg(p=>({...p,capital:parseFloat(e.target.value)||100}))}
                style={{width:64,background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,padding:"2px 5px",color:"#e7edf5",fontFamily:"inherit",fontSize:10,outline:"none"}}/>
            </div>
            {/* Leverage settings — always visible row */}
            <div style={{display:"flex",alignItems:"center",gap:6,
              borderLeft:"2px solid #92400e",paddingLeft:8}}>
              <span style={{fontSize:11,color:"#e8b339",fontWeight:700}}>LEV:</span>
              {[10,20,50,100,200].map(lev=>(
                <button key={lev}
                  onClick={()=>setAtCfg(p=>({...p,leverage:lev}))}
                  style={{background:(atCfg.leverage||10)===lev?"rgba(232,179,57,0.2)":"#0f1d30",
                    border:`1px solid ${(atCfg.leverage||10)===lev?"#e8b339":"#1a2c44"}`,
                    borderRadius:0,padding:"3px 7px",
                    color:(atCfg.leverage||10)===lev?"#e8b339":"#8ea2bf",
                    fontFamily:"inherit",fontSize:11,
                    fontWeight:(atCfg.leverage||10)===lev?700:400,cursor:"pointer"}}>
                  {lev}x
                </button>
              ))}
            </div>
            <div style={{display:"flex",alignItems:"center",gap:6,
              borderLeft:"2px solid #a16207",paddingLeft:8}}>
              <span style={{fontSize:11,color:"#49c2e4",fontWeight:700}}>⏰3m:</span>
              {[20,50,100,200].map(lev=>(
                <button key={lev}
                  onClick={()=>setAtCfg(p=>({...p,scalpLeverage:lev}))}
                  style={{background:(atCfg.scalpLeverage||50)===lev?"rgba(161,98,7,0.2)":"#0f1d30",
                    border:`1px solid ${(atCfg.scalpLeverage||50)===lev?"#49c2e4":"#1a2c44"}`,
                    borderRadius:0,padding:"3px 7px",
                    color:(atCfg.scalpLeverage||50)===lev?"#49c2e4":"#8ea2bf",
                    fontFamily:"inherit",fontSize:11,
                    fontWeight:(atCfg.scalpLeverage||50)===lev?700:400,cursor:"pointer"}}>
                  {lev}x
                </button>
              ))}
            </div>
            <div style={{display:"flex",alignItems:"center",gap:3}}>
              <span style={{fontSize:12,color:"#8ea2bf"}}>Min sigs:</span>
              {[1,2,3].map(n=>(
                <button key={n} onClick={()=>setAtCfg(p=>({...p,minSignals:n}))}
                  style={{background:atCfg.minSignals===n?"rgba(0,212,170,0.2)":"transparent",border:"1px solid "+(atCfg.minSignals===n?"#e8b339":"#1a2c44"),borderRadius:0,padding:"2px 6px",color:atCfg.minSignals===n?"#e8b339":"#8ea2bf",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                  {n}
                </button>
              ))}
            </div>
            <div style={{display:"flex",alignItems:"center",gap:3}}>
              <span style={{fontSize:12,color:"#2ed47a"}}>TP</span>
              <input type="text" inputMode="decimal" value={atMode==="perp"?atCfg.tpPct:atCfg.optTpPct}
                onChange={e=>{const v=parseFloat(e.target.value)||2; setAtCfg(p=>atMode==="perp"?{...p,tpPct:v}:{...p,optTpPct:v});}}
                style={{width:40,background:"#0f1d30",border:`1px solid ${(atMode==="perp"?atCfg.tpPct:atCfg.optTpPct)<0.2?"#e8b339":"#2ed47a"}`,borderRadius:0,padding:"2px 4px",color:"#2ed47a",fontFamily:"inherit",fontSize:10,outline:"none"}}/>
              <span style={{fontSize:12,color:"#8ea2bf"}}>%</span>
              {(atMode==="perp"?atCfg.tpPct:atCfg.optTpPct)<0.2&&<span style={{fontSize:11,color:"#e8b339"}}>⚠ min 0.2%</span>}
              <span style={{fontSize:12,color:"#ff5b6c",marginLeft:4}}>SL</span>
              <input type="text" inputMode="decimal" value={atMode==="perp"?atCfg.slPct:atCfg.optSlPct}
                onChange={e=>{const v=parseFloat(e.target.value)||1; setAtCfg(p=>atMode==="perp"?{...p,slPct:v}:{...p,optSlPct:v});}}
                style={{width:40,background:"#0f1d30",border:"1px solid #f85149",borderRadius:0,padding:"2px 4px",color:"#ff5b6c",fontFamily:"inherit",fontSize:10,outline:"none"}}/>
              <span style={{fontSize:12,color:"#8ea2bf"}}>%</span>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:3}}>
              <span style={{fontSize:12,color:"#8ea2bf"}}>Order:</span>
              {[["limit_order","Limit"],["market_order","Market"]].map(([v,l])=>(
                <button key={v} onClick={()=>setAtCfg(p=>({...p,orderType:v}))}
                  style={{background:atCfg.orderType===v?"rgba(96,165,250,0.15)":"transparent",border:"1px solid "+(atCfg.orderType===v?"#6b7280":"#1a2c44"),borderRadius:0,padding:"2px 6px",color:atCfg.orderType===v?"#6b7280":"#8ea2bf",fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
                  {l}
                </button>
              ))}
            </div>
            {/* Trailing SL toggle */}
            <div style={{display:"flex",alignItems:"center",gap:4,borderLeft:"1px solid #1a2c44",paddingLeft:8}}>
              <button onClick={()=>setAtCfg(p=>({...p,trailEnabled:!p.trailEnabled}))}
                style={{background:atCfg.trailEnabled?"rgba(163,113,247,0.15)":"transparent",
                  border:"1px solid "+(atCfg.trailEnabled?"#49c2e4":"#23405f"),
                  borderRadius:0,padding:"2px 7px",
                  color:atCfg.trailEnabled?"#49c2e4":"#8ea2bf",
                  fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                🎯 TRAIL {atCfg.trailEnabled?"ON":"OFF"}
              </button>
              {atCfg.trailEnabled&&(
                <>
                  <input type="number" step="0.1" min="0.1" max="20"
                    value={atCfg.trailPct||1.5}
                    onChange={e=>setAtCfg(p=>({...p,trailPct:parseFloat(e.target.value)||1.5}))}
                    style={{width:36,background:"#0f1d30",border:"1px solid #a16207",borderRadius:0,
                      padding:"2px 4px",color:"#49c2e4",fontFamily:"inherit",fontSize:10,outline:"none"}}/>
                  <span style={{fontSize:12,color:"#8ea2bf"}}>%</span>
                </>
              )}
            </div>
            {atEnabled&&<span style={{fontSize:12,color:"#ff5b6c",fontWeight:700,marginLeft:"auto"}}>⚠ LIVE — {atLog.filter(l=>l.status==="PLACED").length} trades</span>}
            {/* Test Options button — visible when in options mode */}
            {atMode!=="perp"&&(
              <button onClick={async()=>{
                // Test with the first symbol in the watchlist
                const testSym = (DELTA_WATCHLISTS[watchlist]||[])[0];
                if(!testSym){ onShowToast&&onShowToast("No symbols in watchlist","error"); return; }
                const underlying = testSym.replace("USD","").replace("USDT_PERP","").replace("_PERP","");
                const expiryDays = {daily:0,weekly:3,monthly:14}[atCfg.expiryType||"weekly"]??3;
                const expiryMax  = {daily:2,weekly:9,monthly:60}[atCfg.expiryType||"weekly"]??9;
                try {
                  onShowToast&&onShowToast(`🔍 Testing ${atMode} for ${underlying}...`);
                  const res = await apiFetch("/crypto/fo/find_otm",{
                    method:"POST",headers:{"Content-Type":"application/json"},
                    body:JSON.stringify({
                      underlying, signal_side:"BUY", mode:atMode,
                      otm_steps:atCfg.otmSteps||2,
                      min_days:expiryDays, max_days:expiryMax,
                      expiry_type:atCfg.expiryType||"weekly",
                    })
                  });
                  if(res.found){
                    const legs = res.legs||[];
                    const msg = legs.map(l=>`${l.action?.toUpperCase()} ${l.instrument_type} K${l.strike} exp:${res.expiry} @ $${l.option_ltp}`).join(" | ");
                    setAtLog(p=>[{id:`test-${Date.now()}`,time:new Date().toLocaleTimeString("en-IN"),
                      symbol:underlying,mode:"TEST",status:"FOUND",
                      note:`${atMode} | spot:$${res.spot} atm:$${res.atm_strike} | ${msg}`,
                      signals:"Manual test"},...p].slice(0,200));
                    onShowToast&&onShowToast(`✓ Found ${legs.length} contracts for ${underlying}`);
                  } else {
                    setAtLog(p=>[{id:`test-${Date.now()}`,time:new Date().toLocaleTimeString("en-IN"),
                      symbol:underlying,mode:"TEST",status:"NOT_FOUND",
                      note:res.reason||"No contracts found",signals:"Manual test"},...p].slice(0,200));
                    onShowToast&&onShowToast(`✗ ${res.reason}`,"error");
                  }
                } catch(e){ onShowToast&&onShowToast(`Test failed: ${e.message}`,"error"); }
              }}
                style={{background:"rgba(163,113,247,0.1)",border:"1px solid rgba(163,113,247,0.4)",
                  borderRadius:0,padding:"3px 10px",color:"#49c2e4",
                  fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                🧪 Test Options
              </button>
            )}
          </div>

          {/* ── Risk + Daily Limit controls — admin only ── */}
          {isAdmin&&<div style={{padding:"6px 12px",borderBottom:"1px solid #161b22",display:"flex",gap:12,alignItems:"center",flexWrap:"wrap",background:"rgba(248,81,73,0.04)"}}>
            {/* Daily limit */}
            <div style={{display:"flex",alignItems:"center",gap:5}}>
              <span style={{fontSize:12,color:"#e8b339",fontWeight:700}}>📅 MAX TRADES/DAY</span>
              <input type="number" min="0" max="50" step="1"
                value={atCfg.dailyLimit??5}
                onChange={e=>setAtCfg(p=>({...p,dailyLimit:parseInt(e.target.value)||0}))}
                style={{width:38,background:"#0b1624",border:"1px solid #23405f",borderRadius:0,
                  padding:"2px 5px",color:"#e7edf5",fontFamily:"inherit",fontSize:10,textAlign:"center"}}/>
              <span style={{fontSize:12,color:"#8ea2bf"}}>
                ({checkDailyLimit(dailyTradesRef,atCfg.dailyLimit||0).remaining} left today)
              </span>
            </div>

            <div style={{width:1,height:16,background:"#1a2c44"}}/>

            {/* Min capital + auto-size */}
            <div style={{display:"flex",alignItems:"center",gap:5}}>
              <span style={{fontSize:12,color:"#6b7280",fontWeight:700}}>💰 MIN SIZE $</span>
              <input type="number" min="1" step="1"
                value={atCfg.capital}
                onChange={e=>setAtCfg(p=>({...p,capital:parseFloat(e.target.value)||10}))}
                style={{width:52,background:"#0b1624",border:"1px solid #23405f",borderRadius:0,
                  padding:"2px 5px",color:"#e7edf5",fontFamily:"inherit",fontSize:10,textAlign:"center"}}/>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:5}}>
              <span style={{fontSize:12,color:"#6b7280"}}>MAX %/TRADE</span>
              <input type="number" min="5" max="100" step="5"
                value={atCfg.maxTradesPct??50}
                onChange={e=>setAtCfg(p=>({...p,maxTradesPct:parseFloat(e.target.value)||50}))}
                style={{width:38,background:"#0b1624",border:"1px solid #23405f",borderRadius:0,
                  padding:"2px 5px",color:"#e7edf5",fontFamily:"inherit",fontSize:10,textAlign:"center"}}/>
              <span style={{fontSize:12,color:"#8ea2bf"}}>%</span>
              <button onClick={()=>setAtCfg(p=>({...p,autoSize:!p.autoSize}))}
                style={{padding:"2px 7px",borderRadius:0,border:"1px solid "+(atCfg.autoSize?"#6b7280":"#1a2c44"),
                  background:atCfg.autoSize?"rgba(96,165,250,0.15)":"transparent",
                  color:atCfg.autoSize?"#6b7280":"#8ea2bf",fontFamily:"inherit",fontSize:12,cursor:"pointer",fontWeight:700}}>
                AUTO {atCfg.autoSize?"ON":"OFF"}
              </button>
            </div>

            <div style={{width:1,height:16,background:"#1a2c44"}}/>

            {/* Live margin status */}
            {riskData ? (
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <span style={{fontSize:12,color:riskData.available_balance>0?"#2ed47a":"#ff5b6c",fontWeight:700}}>
                  Free: ${(riskData.available_balance||0).toFixed(2)}
                </span>
                {riskData.available_balance>0&&atCfg.autoSize&&(
                  <span style={{fontSize:12,color:"#6b7280"}}>
                    → size ~${calcPositionSize(riskData.available_balance,atCfg.capital,atCfg.maxTradesPct||50,1).toFixed(2)}
                  </span>
                )}
                {(riskData.liq_warnings||[]).length>0&&(
                  <span style={{fontSize:12,color:"#ff5b6c",fontWeight:700,
                    padding:"1px 6px",background:"rgba(220,38,38,0.15)",borderRadius:0}}>
                    🚨 {riskData.liq_warnings.length} LIQ WARNING
                  </span>
                )}
                <button onClick={()=>deltaFetch("/delta/risk").then(d=>{setRiskData(d);riskDataRef.current=d;}).catch(()=>{})}
                  style={{background:"none",border:"none",color:"#23405f",cursor:"pointer",fontSize:12,padding:"1px 4px"}}>↻</button>
              </div>
            ) : (
              <span style={{fontSize:12,color:"#23405f"}}>margin loading...</span>
            )}
          </div>}

          {/* ── Liquidation danger banner in scan view ── */}
          {riskData&&(riskData.liq_warnings||[]).length>0&&(
            <div style={{padding:"8px 14px",background:"rgba(220,38,38,0.12)",
              borderBottom:"1px solid rgba(248,81,73,0.4)",display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
              <span style={{fontSize:14}}>🚨</span>
              <div>
                <span style={{fontSize:10,fontWeight:700,color:"#ff5b6c"}}>AUTO-TRADE BLOCKED — LIQUIDATION RISK: </span>
                {(riskData.liq_warnings||[]).map((w,i)=>(
                  <span key={i} style={{fontSize:10,color:"#e7edf5"}}>
                    {w.symbol} {w.side} <b style={{color:"#ff5b6c"}}>{w.liq_dist_pct}%</b> from liq{i<riskData.liq_warnings.length-1?", ":""}
                  </span>
                ))}
              </div>
              <button onClick={()=>deltaFetch("/delta/risk").then(d=>{setRiskData(d);riskDataRef.current=d;}).catch(()=>{})}
                style={{marginLeft:"auto",background:"rgba(248,81,73,0.2)",border:"1px solid #f85149",
                  borderRadius:0,padding:"2px 8px",color:"#ff5b6c",fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
                ↻ Re-check
              </button>
            </div>
          )}

          {scanError&&<div style={{padding:"8px 12px",color:"#ff5b6c",fontSize:11,background:"rgba(220,38,38,0.08)",borderBottom:"1px solid rgba(248,81,73,0.2)"}}> ⚠ {scanError}</div>}

          {/* Scan results */}
          <div style={{flex:1,overflowY:"auto"}}>
            {!scanning&&scanResults.length===0&&(
              <div style={{textAlign:"center",padding:"60px 20px",color:"#8ea2bf"}}>
                <div style={{fontSize:36,marginBottom:12}}>Δ</div>
                <div style={{fontSize:13,color:"#e7edf5",marginBottom:8}}>Delta Exchange Scanner</div>
                <div style={{fontSize:11,lineHeight:1.8,marginBottom:16}}>
                  TSI/RSI/EMA/Vol signals on crypto perpetuals & options<br/>
                  <span style={{color:"#e8b339"}}>Perpetual mode</span> — trades perps directly<br/>
                  <span style={{color:"#6b7280"}}>Options mode</span> — finds ATM calls/puts
                </div>
                <button onClick={runScan} style={{background:"linear-gradient(135deg,#92400e,#92400e)",border:"none",borderRadius:0,padding:"10px 24px",color:"#fff",fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                  ▶ START SCAN
                </button>
              </div>
            )}
            {scanning&&scanResults.length===0&&(
              <div style={{textAlign:"center",padding:"40px",color:"#8ea2bf",fontSize:12}}>
                ⟳ Scanning {(customLists[watchlist]||DELTA_WATCHLISTS[watchlist]||[]).length} contracts on Delta...
              </div>
            )}
            {scanResults.length>0&&(
              <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}><table style={{width:"100%",borderCollapse:"collapse",fontSize:11,minWidth:480}}>
                <thead>
                  <tr style={{background:"#0b1624",borderBottom:"1px solid #1a2c44",position:"sticky",top:0,zIndex:1}}>
                    {["SYMBOL","PRICE (USD)","RSI","TSI","EMA9","EMA50","VOL×","TREND","SIGNALS","TRADE"].map(h=>(
                      <th key={h} style={{padding:"7px 10px",color:"#8ea2bf",fontSize:12,letterSpacing:1,fontWeight:700,textAlign:"left",whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {scanResults.map((r,i)=>{
                    const sigs    = (r.signals||[]).filter(s=>activeScan.includes(s.scan));
                    const hasBuy  = sigs.some(s=>s.type==="BUY");
                    const hasSell = sigs.some(s=>s.type==="SELL");
                    const rowBg   = hasBuy?"rgba(63,185,80,0.04)":hasSell?"rgba(248,81,73,0.04)":"transparent";
                    return(
                      <tr key={r.symbol} style={{borderBottom:"1px solid #161b22",background:rowBg}}>
                        <td style={{padding:"8px 10px",fontWeight:700,color:"#e8b339",fontFamily:"inherit",fontSize:11}}>{r.symbol}</td>
                        <td style={{padding:"8px 10px",fontWeight:700,color:"#e7edf5"}}>
                          ${r.price!=null?parseFloat(r.price).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:6}):"—"}
                        </td>
                        <td style={{padding:"8px 10px",color:r.rsi>70?"#ff5b6c":r.rsi<30?"#2ed47a":"#5c3d2e"}}>
                          {r.rsi!=null?r.rsi.toFixed(1):"—"}
                        </td>
                        <td style={{padding:"8px 10px",color:r.tsi>0?"#2ed47a":"#ff5b6c"}}>
                          {r.tsi!=null?r.tsi.toFixed(2):"—"}
                        </td>
                        <td style={{padding:"8px 10px",color:"#8ea2bf",fontSize:10}}>{r.ema9!=null?parseFloat(r.ema9).toLocaleString("en-US",{maximumFractionDigits:2}):"—"}</td>
                        <td style={{padding:"8px 10px",color:"#8ea2bf",fontSize:10}}>{r.ema50!=null?parseFloat(r.ema50).toLocaleString("en-US",{maximumFractionDigits:2}):"—"}</td>
<td style={{padding:"5px 10px",minWidth:90}}>
                          {r.buyVolPct!=null ? (
                            <div>
                              <div style={{display:"flex",height:7,borderRadius:0,overflow:"hidden",background:"#1a2c44"}}>
                                <div style={{width:`${r.buyVolPct}%`,background:"#2ed47a"}}/>
                                <div style={{width:`${r.sellVolPct}%`,background:"#ff5b6c"}}/>
                              </div>
                              <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginTop:2}}>
                                <span style={{color:"#2ed47a",fontWeight:700}}>B {r.buyVolPct}%</span>
                                <span style={{color:"#ff5b6c",fontWeight:700}}>S {r.sellVolPct}%</span>
                              </div>
                            </div>
                          ) : (
                            <span style={{color:r.volRatio>1.5?"#e8b339":"#8ea2bf"}}>{r.volRatio!=null?r.volRatio+"x":"—"}</span>
                          )}
                        </td>
                        <td style={{padding:"8px 10px"}}>
                          <span style={{fontSize:12,fontWeight:700,padding:"2px 6px",borderRadius:0,
                            background:r.trend==="bullish"?"rgba(22,163,74,0.15)":"rgba(220,38,38,0.15)",
                            color:r.trend==="bullish"?"#2ed47a":"#ff5b6c"}}>
                            {r.trend==="bullish"?"▲ BULL":"▼ BEAR"}
                          </span>
                        </td>
                        <td style={{padding:"8px 10px",maxWidth:200}}>
                          {sigs.length===0&&<span style={{color:"#23405f",fontSize:12}}>—</span>}
                          <div style={{display:"flex",flexWrap:"wrap",gap:3}}>
                            {sigs.map((s,j)=>(
                              <span key={j} style={{fontSize:12,fontWeight:700,padding:"1px 6px",borderRadius:0,
                                background:s.type==="BUY"?"rgba(22,163,74,0.15)":"rgba(220,38,38,0.15)",
                                color:s.type==="BUY"?"#2ed47a":"#ff5b6c",
                                border:"1px solid "+(s.type==="BUY"?"rgba(63,185,80,0.3)":"rgba(248,81,73,0.3)")}}>
                                {s.type==="BUY"?"▲":"▼"} {s.label}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td style={{padding:"6px 8px",whiteSpace:"nowrap"}}>
                          {r.price&&!r.error&&(()=>{
                            const ltp = parseFloat(r.price||0);
                            const isBull = r.trend==="bullish" || sigs.some(s=>s.type==="BUY");
                            const isBear = r.trend==="bearish" || sigs.some(s=>s.type==="SELL");
                            return canTrade ? (
                              <button onClick={()=>setDeltaStratPopup({
                                symbol:  r.symbol,
                                exchange:"DELTA",
                                ltp,
                                hasBuy:  isBull,
                                hasSell: isBear,
                                signals: sigs,
                                lotSize: 1,
                                mode:    "delta",
                              })}
                                style={{background:"rgba(232,179,57,0.12)",
                                  border:"1px solid rgba(232,179,57,0.4)",
                                  borderRadius:0,padding:"4px 9px",
                                  color:"#e8b339",fontFamily:"inherit",
                                  fontSize:11,fontWeight:700,cursor:"pointer"}}>
                                🎯 Strategy
                              </button>
                            ) : (
                              <span style={{fontSize:11,color:"#5a728f",padding:"2px 6px",
                                background:"rgba(96,165,250,0.06)",border:"1px solid #1a2c44",borderRadius:0}}>
                                signal only
                              </span>
                            );
                          })()}
                          <button onClick={()=>setChartModal({symbol:r.symbol,exchange:"NSE"})}
                            style={{background:"transparent",border:"1px solid #1a2c44",
                              borderRadius:0,padding:"2px 6px",color:"#5a728f",
                              fontFamily:"inherit",fontSize:10,cursor:"pointer",display:"block",marginTop:3}}
                            onMouseEnter={e=>{e.currentTarget.style.color="#49c2e4";e.currentTarget.style.borderColor="#e8b339"}}
                            onMouseLeave={e=>{e.currentTarget.style.color="#5a728f";e.currentTarget.style.borderColor="#1a2c44"}}>
                            📈 Chart
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table></div>
            )}
          </div>

          {/* Auto-trade log */}
          {atLog.length>0&&(
            <div style={{borderTop:"1px solid #1a2c44",maxHeight:180,overflowY:"auto",background:"#07101c"}}>
              <div style={{padding:"4px 12px",fontSize:12,color:"#8ea2bf",letterSpacing:2,display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:"1px solid #1a2c44",position:"sticky",top:0,background:"#07101c"}}>
                <span>AUTO TRADE LOG ({atLog.length})</span>
                <button onClick={()=>setAtLog([])} style={{background:"none",border:"none",color:"#23405f",fontSize:10,cursor:"pointer"}}>clear</button>
              </div>
              {atLog.map(t=>{
                const modeColor = t.mode==="PERP"?"#e8b339":t.mode==="TEST"?"#49c2e4":t.mode?.includes("OTM")?"#e8b339":"#6b7280";
                const statusColor = t.status==="PLACED"?"#2ed47a":t.status==="FOUND"?"#2ed47a":t.status?.includes("SKIP")||t.status?.includes("NO_")?"#e8b339":"#ff5b6c";
                const isBuy = (t.side||"").toUpperCase()==="BUY";
                return(
                <div key={t.id} style={{padding:"5px 12px",borderBottom:"1px solid #0d1117",fontSize:10}}>
                  <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                    <span style={{color:"#5a728f",fontSize:11,flexShrink:0}}>{t.time}</span>
                    <span style={{fontSize:11,padding:"1px 5px",borderRadius:0,fontWeight:700,
                      background:`${modeColor}18`,color:modeColor}}>
                      {t.mode||"?"}
                    </span>
                    {t.side&&(
                      <span style={{fontSize:12,fontWeight:700,color:isBuy?"#2ed47a":"#ff5b6c"}}>
                        {isBuy?"▲":"▼"} {t.side}
                      </span>
                    )}
                    <span style={{color:"#e7edf5",fontWeight:700}}>{t.symbol}</span>
                    {/* Options specific */}
                    {t.contract&&t.contract!==t.symbol&&(
                      <span style={{color:"#6b7280",fontSize:12,fontWeight:700}}>→ {t.contract}</span>
                    )}
                    {t.optType&&<span style={{fontSize:11,padding:"1px 4px",borderRadius:0,
                      background:t.optType==="CALL"?"rgba(22,163,74,0.12)":"rgba(220,38,38,0.12)",
                      color:t.optType==="CALL"?"#2ed47a":"#ff5b6c",fontWeight:700}}>{t.optType}</span>}
                    {t.strike&&<span style={{color:"#8ea2bf",fontSize:12}}>K{t.strike}</span>}
                    {t.expiry&&<span style={{color:"#8ea2bf",fontSize:12}}>{t.expiry}{t.daysLeft?` (${t.daysLeft}d)`:""}</span>}
                    {t.price>0&&<span style={{color:"#e7edf5"}}>@ ${t.price}</span>}
                    {t.qty&&<span style={{color:"#8ea2bf",fontSize:12}}>×{t.qty}</span>}
                    {t.tp&&<span style={{color:"#2ed47a",fontSize:12}}>TP ${t.tp}</span>}
                    {t.sl&&<span style={{color:"#ff5b6c",fontSize:12}}>SL ${t.sl}</span>}
                    {t.marginUsed&&<span style={{color:"#e8b339",fontSize:11}}>M ${t.marginUsed}</span>}
                    <span style={{marginLeft:"auto",fontSize:12,fontWeight:700,color:statusColor}}>{t.status}</span>
                  </div>
                  {/* Note / signals / error on second line */}
                  {(t.note||t.signals||t.error)&&(
                    <div style={{fontSize:11,color:"#5a728f",marginTop:2,paddingLeft:4,
                      borderLeft:"2px solid #1a2c44",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {t.error?`⚠ ${t.error}`:(t.note||t.signals)}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── WALLET VIEW ── */}
      {view==="wallet"&&(
        <div style={{flex:1,overflowY:"auto",padding:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <span style={{fontSize:12,fontWeight:700,color:"#e7edf5"}}>💰 Wallet Balances</span>
            <button onClick={()=>loadAcct("wallet")} style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,padding:"4px 12px",color:"#e8b339",fontFamily:"inherit",fontSize:10,cursor:"pointer"}}>↻ Refresh</button>
          </div>
          {acctLoading&&<div style={{color:"#8ea2bf",fontSize:12}}>Loading...</div>}
          {acctError&&<div style={{color:"#ff5b6c",fontSize:11}}>⚠ {acctError}</div>}
          {wallet&&wallet.length===0&&<div style={{color:"#8ea2bf",fontSize:12}}>No balances found.</div>}
          {wallet&&wallet.map((b,i)=>(
            <div key={i} style={{background:"#0f1d30",border:"1px solid #1a2c44",borderRadius:0,padding:"14px 16px",marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <span style={{fontSize:14,fontWeight:700,color:"#e7edf5"}}>{b.asset}</span>
                <span style={{fontSize:13,fontWeight:700,color:"#e8b339"}}>{parseFloat(b.balance||0).toFixed(6)}</span>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
                {[["Available",b.available_balance,"#2ed47a"],["Order Margin",b.order_margin,"#e8b339"],["Position Margin",b.position_margin,"#6b7280"],["Unrealized PnL",b.unrealized_pnl,parseFloat(b.unrealized_pnl||0)>=0?"#2ed47a":"#ff5b6c"]].map(([l,v,c])=>(
                  <div key={l} style={{background:"#0b1624",borderRadius:0,padding:"8px 10px"}}>
                    <div style={{fontSize:12,color:"#8ea2bf",marginBottom:3}}>{l}</div>
                    <div style={{fontSize:11,fontWeight:700,color:c}}>{parseFloat(v||0).toFixed(6)}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── POSITIONS VIEW ── */}
      {view==="positions"&&(
        <div style={{flex:1,overflowY:"auto"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",borderBottom:"1px solid #1a2c44"}}>
            <span style={{fontSize:12,fontWeight:700,color:"#e7edf5"}}>📋 Open Positions</span>
            <button onClick={()=>{
              loadAcct("positions");
              deltaFetch("/delta/risk").then(d=>{setRiskData(d);riskDataRef.current=d;}).catch(()=>{});
            }} style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,padding:"4px 12px",color:"#e8b339",fontFamily:"inherit",fontSize:10,cursor:"pointer"}}>↻ Refresh</button>
          </div>

          {/* ── Risk Dashboard ── */}
          {riskData&&!riskData.error&&(
            <div style={{padding:"10px 14px",borderBottom:"1px solid #1a2c44"}}>
              {/* Liquidation danger banner */}
              {(riskData.liq_warnings||[]).length>0&&(
                <div style={{background:"rgba(220,38,38,0.15)",border:"1px solid #f85149",borderRadius:0,padding:"10px 14px",marginBottom:10,display:"flex",alignItems:"flex-start",gap:10}}>
                  <span style={{fontSize:18}}>🚨</span>
                  <div>
                    <div style={{fontSize:11,fontWeight:700,color:"#ff5b6c",marginBottom:4}}>
                      LIQUIDATION DANGER — {(riskData.liq_warnings||[]).length} position(s) at risk!
                    </div>
                    {(riskData.liq_warnings||[]).map((w,i)=>(
                      <div key={i} style={{fontSize:10,color:"#e7edf5",marginTop:2}}>
                        ⚠ <b>{w.symbol}</b> {w.side} — only <b style={{color:"#ff5b6c"}}>{w.liq_dist_pct}%</b> from liquidation
                        {" "}(Mark: ${parseFloat(w.mark_price||0).toLocaleString()} → Liq: ${parseFloat(w.liq_price||0).toLocaleString()})
                      </div>
                    ))}
                    <div style={{fontSize:12,color:"#ff5b6c",marginTop:6,fontWeight:700}}>
                      ⛔ Auto-trade is BLOCKED until positions are safe. Reduce position size or add margin.
                    </div>
                  </div>
                </div>
              )}

              {/* Margin bar */}
              <div style={{display:"flex",gap:16,alignItems:"center",flexWrap:"wrap"}}>
                <div style={{flex:1,minWidth:200}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                    <span style={{fontSize:10,color:"#8ea2bf"}}>Margin Used</span>
                    <span style={{fontSize:10,fontWeight:700,
                      color:riskData.margin_usage_pct>=(atCfg.maxMarginUsagePct||70)?"#ff5b6c":
                            riskData.margin_usage_pct>=50?"#e8b339":"#2ed47a"}}>
                      {riskData.margin_usage_pct}%
                    </span>
                  </div>
                  <div style={{height:6,background:"#1a2c44",borderRadius:0,overflow:"hidden",position:"relative"}}>
                    <div style={{height:"100%",width:`${Math.min(riskData.margin_usage_pct,100)}%`,
                      background:riskData.margin_usage_pct>=(atCfg.maxMarginUsagePct||70)?"#ff5b6c":
                                 riskData.margin_usage_pct>=50?"#e8b339":"#2ed47a",
                      borderRadius:0,transition:"width 0.3s"}}/>
                    {/* Max usage limit marker */}
                    <div style={{position:"absolute",top:0,left:`${atCfg.maxMarginUsagePct||70}%`,
                      width:2,height:"100%",background:"#fff",opacity:0.4}}/>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#8ea2bf",marginTop:2}}>
                    <span>Used ${(riskData.used_margin||0).toFixed(2)}</span>
                    <span>Limit {atCfg.maxMarginUsagePct||70}%</span>
                    <span>Total ${(riskData.total_balance||0).toFixed(2)}</span>
                  </div>
                </div>
                <div style={{display:"flex",gap:12,fontSize:10}}>
                  <div style={{textAlign:"center"}}>
                    <div style={{color:"#2ed47a",fontWeight:700,fontSize:12}}>${(riskData.available_balance||0).toFixed(2)}</div>
                    <div style={{color:"#8ea2bf",fontSize:12}}>Available</div>
                  </div>
                  <div style={{textAlign:"center"}}>
                    <div style={{color:"#e8b339",fontWeight:700,fontSize:12}}>${(riskData.position_margin||0).toFixed(2)}</div>
                    <div style={{color:"#8ea2bf",fontSize:12}}>Pos Margin</div>
                  </div>
                  <div style={{textAlign:"center"}}>
                    <div style={{color:riskData.safe_to_trade?"#2ed47a":"#ff5b6c",fontWeight:700,fontSize:12}}>
                      {riskData.safe_to_trade?"✓ SAFE":"✗ BLOCKED"}
                    </div>
                    <div style={{color:"#8ea2bf",fontSize:12}}>AT Status</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {acctLoading&&<div style={{padding:"20px",color:"#8ea2bf",fontSize:12}}>Loading...</div>}
          {acctError&&<div style={{padding:"10px 14px",color:"#ff5b6c",fontSize:11}}>⚠ {acctError}</div>}
          {positions&&positions.length===0&&<div style={{padding:"40px",textAlign:"center",color:"#8ea2bf",fontSize:12}}>No open positions.</div>}
          {positions&&positions.length>0&&(
            <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}><table style={{width:"100%",borderCollapse:"collapse",fontSize:11,minWidth:480}}>
              <thead>
                <tr style={{background:"#0b1624",borderBottom:"1px solid #1a2c44",position:"sticky",top:0}}>
                  {["SYMBOL","SIDE","SIZE","ENTRY","MARK","LIQ","LIQ DIST","P&L","MARGIN","TP / SL","ACTION"].map(h=>(
                    <th key={h} style={{padding:"7px 10px",color:"#8ea2bf",fontSize:12,letterSpacing:1,fontWeight:700,textAlign:"left",whiteSpace:"nowrap"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {positions.map((p,i)=>{
                  const pnl      = parseFloat(p.unrealized_pnl||0);
                  const liqPrice = p.liquidation_price ? parseFloat(p.liquidation_price) : null;
                  const markPrice= parseFloat(p.mark_price||0);
                  const entryPrice = parseFloat(p.entry_price||0);
                  const liqDist  = liqPrice && markPrice ? Math.abs(markPrice-liqPrice)/markPrice*100 : null;
                  const isDanger = liqDist !== null && liqDist < (atCfg.liqWarningPct||15);
                  const isWarn   = liqDist !== null && liqDist < 25 && !isDanger;
                  const isBuy    = p.side==="BUY";

                  // Find matching watcher for this position
                  const watcher = deltaWatchers.find(w=>
                    w.symbol===p.symbol && w.status==="WATCHING"
                  );

                  return(
                    <tr key={i} style={{borderBottom:"1px solid #161b22",
                      background:isDanger?"rgba(248,81,73,0.1)":isWarn?"rgba(96,165,250,0.06)":
                        pnl>0?"rgba(63,185,80,0.04)":pnl<0?"rgba(248,81,73,0.04)":"transparent"}}>

                      {/* Symbol */}
                      <td style={{padding:"8px 10px",fontWeight:700,color:isDanger?"#ff5b6c":"#e8b339",fontSize:11,whiteSpace:"nowrap"}}>
                        {isDanger&&"🚨 "}{p.symbol}
                      </td>

                      {/* Side */}
                      <td style={{padding:"8px 10px"}}>
                        <span style={{fontSize:12,fontWeight:700,padding:"2px 6px",borderRadius:0,
                          background:isBuy?"rgba(22,163,74,0.15)":"rgba(220,38,38,0.15)",
                          color:isBuy?"#2ed47a":"#ff5b6c"}}>{p.side}</span>
                      </td>

                      {/* Size */}
                      <td style={{padding:"8px 10px",fontWeight:700,color:"#e7edf5"}}>{p.size}</td>

                      {/* Entry */}
                      <td style={{padding:"8px 10px",color:"#8ea2bf",whiteSpace:"nowrap"}}>${entryPrice.toLocaleString(undefined,{maximumFractionDigits:4})}</td>

                      {/* Mark */}
                      <td style={{padding:"8px 10px",color:"#e7edf5",whiteSpace:"nowrap"}}>${markPrice.toLocaleString(undefined,{maximumFractionDigits:4})}</td>

                      {/* Liq price */}
                      <td style={{padding:"8px 10px",color:isDanger?"#ff5b6c":isWarn?"#e8b339":"#8ea2bf",fontWeight:isDanger?700:400,whiteSpace:"nowrap"}}>
                        {liqPrice?`$${liqPrice.toLocaleString(undefined,{maximumFractionDigits:4})}`:"—"}
                      </td>

                      {/* Liq distance bar */}
                      <td style={{padding:"8px 10px"}}>
                        {liqDist!=null?(
                          <div style={{display:"flex",alignItems:"center",gap:4}}>
                            <div style={{width:36,height:4,background:"#1a2c44",borderRadius:2,overflow:"hidden"}}>
                              <div style={{height:"100%",width:`${Math.min(100-liqDist,100)}%`,
                                background:isDanger?"#ff5b6c":isWarn?"#e8b339":"#2ed47a",borderRadius:2}}/>
                            </div>
                            <span style={{fontSize:12,fontWeight:700,color:isDanger?"#ff5b6c":isWarn?"#e8b339":"#2ed47a"}}>
                              {liqDist.toFixed(1)}%
                            </span>
                          </div>
                        ):"—"}
                      </td>

                      {/* P&L */}
                      <td style={{padding:"8px 10px",fontWeight:700,color:pnl>=0?"#2ed47a":"#ff5b6c",whiteSpace:"nowrap"}}>
                        {pnl>=0?"+":""}{pnl.toFixed(4)}
                        {entryPrice>0&&markPrice>0&&(
                          <div style={{fontSize:11,color:"#5a728f",fontWeight:400}}>
                            {(((markPrice-entryPrice)/entryPrice)*(isBuy?1:-1)*100).toFixed(2)}%
                          </div>
                        )}
                      </td>

                      {/* Margin */}
                      <td style={{padding:"8px 10px",color:"#8ea2bf",fontSize:10}}>
                        <div style={{fontWeight:700,color:"#e7edf5",fontSize:11}}>
                          ${parseFloat(p.margin||0).toFixed(2)}
                        </div>
                        {p.leverage&&parseFloat(p.leverage)>1&&(
                          <div style={{color:"#e8b339",fontSize:10,fontWeight:700}}>
                            {parseFloat(p.leverage).toFixed(0)}x lev
                          </div>
                        )}
                        {p.notional&&parseFloat(p.notional)>0&&(
                          <div style={{color:"#5a728f",fontSize:9}}>
                            notional ${parseFloat(p.notional).toLocaleString(undefined,{maximumFractionDigits:0})}
                          </div>
                        )}
                      </td>

                      {/* ── TP / SL — shows watcher values, editable inline ── */}
                      <td style={{padding:"6px 8px",minWidth:160}}>
                        {watcher ? (
                          <div style={{display:"flex",flexDirection:"column",gap:3}}>
                            {/* TP row */}
                            <div style={{display:"flex",alignItems:"center",gap:4}}>
                              <span style={{fontSize:11,color:"#2ed47a",fontWeight:700,minWidth:16}}>TP</span>
                              <input
                                type="text" inputMode="decimal"
                                defaultValue={watcher.tpEnabled&&watcher.tpPrice ? parseFloat(watcher.tpPrice).toFixed(4) : ""}
                                placeholder="—"
                                onBlur={e=>{
                                  const v = parseFloat(e.target.value);
                                  if(!isNaN(v)&&v>0) setDeltaWatchers(prev=>prev.map(w=>w.id===watcher.id?{...w,tpPrice:v,tpEnabled:true}:w));
                                }}
                                style={{width:70,background:"#0b1624",border:"1px solid rgba(63,185,80,0.35)",
                                  borderRadius:0,padding:"2px 5px",color:"#2ed47a",
                                  fontFamily:"inherit",fontSize:10,outline:"none"}}/>
                              {watcher.tpEnabled&&watcher.tpPrice&&entryPrice>0&&(
                                <span style={{fontSize:11,color:"#2ed47a",opacity:0.7}}>
                                  +{(Math.abs(parseFloat(watcher.tpPrice)-entryPrice)/entryPrice*100).toFixed(1)}%
                                </span>
                              )}
                            </div>
                            {/* SL row */}
                            <div style={{display:"flex",alignItems:"center",gap:4}}>
                              <span style={{fontSize:11,color:"#ff5b6c",fontWeight:700,minWidth:16}}>SL</span>
                              <input
                                type="text" inputMode="decimal"
                                defaultValue={watcher.slEnabled&&watcher.slPrice ? parseFloat(watcher.slPrice).toFixed(4) : ""}
                                placeholder="—"
                                onBlur={e=>{
                                  const v = parseFloat(e.target.value);
                                  if(!isNaN(v)&&v>0) setDeltaWatchers(prev=>prev.map(w=>w.id===watcher.id?{...w,slPrice:v,slEnabled:true}:w));
                                }}
                                style={{width:70,background:"#0b1624",border:"1px solid rgba(248,81,73,0.35)",
                                  borderRadius:0,padding:"2px 5px",color:"#ff5b6c",
                                  fontFamily:"inherit",fontSize:10,outline:"none"}}/>
                              {watcher.slEnabled&&watcher.slPrice&&entryPrice>0&&(
                                <span style={{fontSize:11,color:"#ff5b6c",opacity:0.7}}>
                                  -{(Math.abs(parseFloat(watcher.slPrice)-entryPrice)/entryPrice*100).toFixed(1)}%
                                </span>
                              )}
                            </div>
                            {/* Trail indicator */}
                            {watcher.trailEnabled&&(
                              <span style={{fontSize:11,color:"#49c2e4",fontWeight:700}}>🎯 Trail {watcher.trailPct}%</span>
                            )}
                          </div>
                        ) : (
                          /* No watcher — quick add TP/SL button */
                          <button onClick={()=>{
                            const tp = prompt(`TP price for ${p.symbol} (current $${markPrice.toFixed(4)}):`);
                            const sl = prompt(`SL price for ${p.symbol}:`);
                            if(!tp&&!sl) return;
                            const watchId = `dw-pos-${Date.now()}-${p.symbol}`;
                            setDeltaWatchers(prev=>[{
                              id:watchId, symbol:p.symbol, productId:p.product_id||null,
                              side:p.side, size:Math.abs(parseFloat(p.size)||1),
                              entryPrice:entryPrice,
                              tpPrice:tp?parseFloat(tp):null, tpEnabled:!!tp,
                              slPrice:sl?parseFloat(sl):null, slEnabled:!!sl,
                              trailEnabled:false, trailPct:1.5,
                              hwm:markPrice, lwm:markPrice,
                              status:"WATCHING",
                              openedAt:new Date().toLocaleTimeString("en-IN"),
                              source:"positions",
                            },...prev]);
                            onShowToast&&onShowToast(`✓ Watcher added for ${p.symbol}`);
                          }}
                            style={{background:"rgba(0,212,170,0.08)",border:"1px solid rgba(0,212,170,0.3)",
                              borderRadius:0,padding:"3px 8px",color:"#e8b339",
                              fontFamily:"inherit",fontSize:12,cursor:"pointer",whiteSpace:"nowrap"}}>
                            + Set TP/SL
                          </button>
                        )}
                      </td>

                      {/* Action */}
                      <td style={{padding:"8px 10px",whiteSpace:"nowrap"}}>
                        <div style={{display:"flex",gap:4,flexDirection:"column"}}>
                          <button onClick={async()=>{
                            const amt = prompt(`Add margin to ${p.symbol} ($):`);
                            if(!amt||isNaN(parseFloat(amt))) return;
                            try {
                              await deltaFetch("/delta/position/add_margin",{method:"POST",
                                headers:{"Content-Type":"application/json"},
                                body:JSON.stringify({product_id:p.product_id,delta_margin:parseFloat(amt).toFixed(4)})});
                              onShowToast&&onShowToast(`✓ +$${amt} margin to ${p.symbol}`);
                              setTimeout(()=>loadAcct("positions"),1000);
                            } catch(e){ onShowToast&&onShowToast(`✗ ${e.message}`,"error"); }
                          }}
                            style={{background:isDanger?"rgba(220,38,38,0.15)":"rgba(96,165,250,0.08)",
                              border:`1px solid ${isDanger?"#ff5b6c":"rgba(96,165,250,0.3)"}`,
                              borderRadius:0,padding:"3px 8px",
                              color:isDanger?"#ff5b6c":"#6b7280",
                              fontFamily:"inherit",fontSize:12,cursor:"pointer",fontWeight:700}}>
                            {isDanger?"🚨 +Margin":"+ Margin"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          )}
        </div>
      )}

      {/* ── ORDERS VIEW ── */}
      {view==="orders"&&(
        <div style={{flex:1,overflowY:"auto"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",borderBottom:"1px solid #1a2c44"}}>
            <span style={{fontSize:12,fontWeight:700,color:"#e7edf5"}}>📦 Open Orders</span>
            <button onClick={()=>loadAcct("orders")} style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,padding:"4px 12px",color:"#e8b339",fontFamily:"inherit",fontSize:10,cursor:"pointer"}}>↻ Refresh</button>
          </div>
          {acctLoading&&<div style={{padding:"20px",color:"#8ea2bf",fontSize:12}}>Loading...</div>}
          {acctError&&<div style={{padding:"10px 14px",color:"#ff5b6c",fontSize:11}}>⚠ {acctError}</div>}
          {orders&&orders.length===0&&<div style={{padding:"40px",textAlign:"center",color:"#8ea2bf",fontSize:12}}>No open orders.</div>}
          {orders&&orders.length>0&&(
            <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}><table style={{width:"100%",borderCollapse:"collapse",fontSize:11,minWidth:480}}>
              <thead>
                <tr style={{background:"#0b1624",borderBottom:"1px solid #1a2c44"}}>
                  {["ORDER ID","SYMBOL","SIDE","SIZE","UNFILLED","TYPE","PRICE","STATE","TIME"].map(h=>(
                    <th key={h} style={{padding:"7px 10px",color:"#8ea2bf",fontSize:12,letterSpacing:1,fontWeight:700,textAlign:"left"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.map((o,i)=>(
                  <tr key={i} style={{borderBottom:"1px solid #161b22"}}>
                    <td style={{padding:"8px 10px",color:"#8ea2bf",fontSize:12}}>{o.id}</td>
                    <td style={{padding:"8px 10px",fontWeight:700,color:"#e8b339"}}>{o.symbol}</td>
                    <td style={{padding:"8px 10px"}}>
                      <span style={{fontSize:12,fontWeight:700,padding:"2px 6px",borderRadius:0,background:o.side==="BUY"?"rgba(22,163,74,0.15)":"rgba(220,38,38,0.15)",color:o.side==="BUY"?"#2ed47a":"#ff5b6c"}}>{o.side}</span>
                    </td>
                    <td style={{padding:"8px 10px",color:"#e7edf5"}}>{o.size}</td>
                    <td style={{padding:"8px 10px",color:"#8ea2bf"}}>{o.unfilled_size}</td>
                    <td style={{padding:"8px 10px",color:"#8ea2bf",fontSize:12}}>{o.order_type}</td>
                    <td style={{padding:"8px 10px",color:"#e7edf5"}}>{o.limit_price?`$${o.limit_price}`:"MARKET"}</td>
                    <td style={{padding:"8px 10px"}}>
                      <span style={{fontSize:12,padding:"2px 6px",borderRadius:0,background:"rgba(96,165,250,0.1)",color:"#6b7280"}}>{o.state}</span>
                    </td>
                    <td style={{padding:"8px 10px",color:"#8ea2bf",fontSize:12}}>{o.created_at?new Date(o.created_at).toLocaleTimeString("en-IN"):""}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </div>
      )}

      {/* ── CHAIN VIEW ── */}
      {view==="chain"&&(
        <div style={{flex:1,overflowY:"auto",padding:12}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <span style={{fontSize:12,fontWeight:700,color:"#e7edf5"}}>🔗 All Products</span>
            <button onClick={()=>loadAcct("chain")} style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,padding:"4px 12px",color:"#e8b339",fontFamily:"inherit",fontSize:10,cursor:"pointer"}}>↻ Reload</button>
          </div>
          {acctLoading&&<div style={{color:"#8ea2bf",fontSize:12}}>Loading products...</div>}
          {acctError&&<div style={{color:"#ff5b6c",fontSize:11}}>⚠ {acctError}</div>}
          {products&&Object.entries(products).sort().map(([underlying, prods])=>(
            <div key={underlying} style={{marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:700,color:"#e8b339",padding:"4px 0",borderBottom:"1px solid #1a2c44",marginBottom:6}}>{underlying} <span style={{color:"#8ea2bf",fontWeight:400,fontSize:12}}>({prods.length})</span></div>
              <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                {prods.map((p,i)=>{
                  const tColor = p.product_type.includes("call")?"#2ed47a":p.product_type.includes("put")?"#ff5b6c":p.product_type.includes("perp")?"#e8b339":"#e8b339";
                  return(
                    <button key={i} onClick={()=>setTicket({symbol:p.symbol,product_id:p.id,side:"BUY",price:0,qty:1,orderType:"limit_order",tp:null,sl:null})}
                      style={{background:"#0b1624",border:`1px solid ${tColor}44`,borderRadius:0,padding:"4px 8px",cursor:"pointer",textAlign:"left"}}>
                      <div style={{fontSize:10,fontWeight:700,color:tColor}}>{p.symbol}</div>
                      <div style={{fontSize:11,color:"#8ea2bf"}}>{p.product_type}{p.strike_price>0?` K${p.strike_price}`:""}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Watchlist Manager Modal ── */}
      {showWLMgr&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1001}}>
          <div style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:12,padding:20,width:380,fontFamily:"inherit",maxHeight:"80vh",overflowY:"auto"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <span style={{fontWeight:700,fontSize:13,color:"#e8b339"}}>Δ Watchlist Manager</span>
              <button onClick={()=>setShowWLMgr(false)} style={{background:"none",border:"none",color:"#8ea2bf",fontSize:16,cursor:"pointer"}}>✕</button>
            </div>

            {/* Create new list */}
            <div style={{marginBottom:14,padding:10,background:"#0b1624",borderRadius:0,border:"1px solid #1a2c44"}}>
              <div style={{fontSize:12,color:"#8ea2bf",letterSpacing:1,marginBottom:6}}>CREATE NEW LIST</div>
              <div style={{display:"flex",gap:6}}>
                <input value={newListName} onChange={e=>setNewListName(e.target.value)}
                  placeholder="List name..." onKeyDown={e=>{
                    if(e.key==="Enter"&&newListName.trim()){
                      setCustomLists(p=>({...p,[newListName.trim()]:[]}));
                      setWatchlist(newListName.trim()); setNewListName(""); setShowWLMgr(false);
                    }
                  }}
                  style={{flex:1,background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,padding:"5px 8px",color:"#e7edf5",fontFamily:"inherit",fontSize:10,outline:"none"}}/>
                <button onClick={()=>{
                  if(!newListName.trim()) return;
                  setCustomLists(p=>({...p,[newListName.trim()]:[]}));
                  setWatchlist(newListName.trim()); setNewListName(""); setShowWLMgr(false);
                }} style={{background:"rgba(0,212,170,0.15)",border:"1px solid #92400e",borderRadius:0,padding:"5px 10px",color:"#e8b339",fontFamily:"inherit",fontSize:10,cursor:"pointer",fontWeight:700}}>
                  CREATE
                </button>
              </div>
            </div>

            {/* Built-in lists */}
            <div style={{fontSize:12,color:"#8ea2bf",letterSpacing:1,marginBottom:6}}>BUILT-IN LISTS</div>
            {Object.entries(DELTA_WATCHLISTS).map(([name,syms])=>(
              <div key={name} style={{padding:"6px 10px",background:"#0b1624",borderRadius:0,marginBottom:4,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:10,color:"#5c3d2e"}}>{name}</span>
                <span style={{fontSize:12,color:"#8ea2bf"}}>{syms.join(", ")}</span>
              </div>
            ))}

            {/* Custom lists */}
            {Object.keys(customLists).length>0&&<>
              <div style={{fontSize:12,color:"#8ea2bf",letterSpacing:1,margin:"10px 0 6px"}}>MY LISTS</div>
              {Object.entries(customLists).map(([name,syms])=>(
                <div key={name} style={{padding:10,background:"#0b1624",borderRadius:0,marginBottom:8,border:"1px solid #1a2c44"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                    <span style={{fontSize:11,fontWeight:700,color:"#6b7280"}}>⭐ {name}</span>
                    <button onClick={()=>{
                      setCustomLists(p=>{const n={...p};delete n[name];return n;});
                      if(watchlist===name) setWatchlist("TOP 5");
                    }} style={{background:"none",border:"none",color:"#ff5b6c",fontSize:10,cursor:"pointer"}}>🗑 Delete</button>
                  </div>
                  {/* Add symbol */}
                  <div style={{display:"flex",gap:5,marginBottom:6}}>
                    <input value={newSymInput} onChange={e=>setNewSymInput(e.target.value.toUpperCase())}
                      placeholder="e.g. BTCUSD" onKeyDown={e=>{
                        if(e.key==="Enter"&&newSymInput.trim()&&!syms.includes(newSymInput.trim())){
                          setCustomLists(p=>({...p,[name]:[...syms,newSymInput.trim()]}));
                          setNewSymInput("");
                        }
                      }}
                      style={{flex:1,background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,padding:"4px 7px",color:"#e7edf5",fontFamily:"inherit",fontSize:10,outline:"none"}}/>
                    <button onClick={()=>{
                      if(!newSymInput.trim()||syms.includes(newSymInput.trim())) return;
                      setCustomLists(p=>({...p,[name]:[...syms,newSymInput.trim()]}));
                      setNewSymInput("");
                    }} style={{background:"rgba(0,212,170,0.1)",border:"1px solid #92400e",borderRadius:0,padding:"4px 8px",color:"#e8b339",fontFamily:"inherit",fontSize:10,cursor:"pointer"}}>+ Add</button>
                  </div>
                  {/* Symbol chips */}
                  <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                    {syms.map(s=>(
                      <span key={s} style={{fontSize:12,padding:"2px 7px",borderRadius:0,background:"rgba(96,165,250,0.1)",border:"1px solid #6b728044",color:"#6b7280",display:"flex",alignItems:"center",gap:4}}>
                        {s}
                        <button onClick={()=>setCustomLists(p=>({...p,[name]:syms.filter(x=>x!==s)}))}
                          style={{background:"none",border:"none",color:"#ff5b6c",cursor:"pointer",fontSize:10,padding:0,lineHeight:1}}>×</button>
                      </span>
                    ))}
                    {syms.length===0&&<span style={{fontSize:12,color:"#8ea2bf"}}>No symbols yet — type above and press Enter</span>}
                  </div>
                </div>
              ))}
            </>}

            {/* Symbol lookup tip */}
            <div style={{marginTop:10,padding:8,background:"#07101c",borderRadius:0,fontSize:12,color:"#8ea2bf",lineHeight:1.7}}>
              💡 Find correct symbol names:<br/>
              <a href={`https://api.rabtrade.in/delta/products`} target="_blank" style={{color:"#e8b339"}}>
                {`https://api.rabtrade.in/delta/products`}
              </a><br/>
              Common: BTCUSD, ETHUSD, SOLUSD, BNBUSD, XRPUSD
            </div>
          </div>
        </div>
      )}

      {/* ── WATCHERS VIEW ── */}
      {view==="watchers"&&(
        <div style={{flex:1,overflowY:"auto"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",borderBottom:"1px solid #1a2c44"}}>
            <span style={{fontSize:12,fontWeight:700,color:"#e7edf5"}}>🎯 Delta TP/SL/Trail Watchers</span>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <span style={{fontSize:10,color:"#8ea2bf"}}>{deltaWatchers.filter(w=>w.status==="WATCHING").length} active · polls every 5s</span>
              <button onClick={()=>setDeltaWatchers(prev=>prev.filter(w=>w.status==="WATCHING"))}
                style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,padding:"3px 10px",color:"#8ea2bf",fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
                Clear Done
              </button>
            </div>
          </div>
          {deltaWatchers.length===0&&(
            <div style={{padding:"40px",textAlign:"center",color:"#8ea2bf",fontSize:12}}>
              <div style={{fontSize:28,marginBottom:10}}>🎯</div>
              <div>No active watchers.</div>
              <div style={{fontSize:10,marginTop:6,color:"#5a728f",lineHeight:1.7}}>
                Watchers start automatically when Auto-Trade places an order.<br/>
                They monitor TP, SL, and Trailing SL every 5 seconds.<br/>
                Also supports manual adds from the ticket.
              </div>
            </div>
          )}
          {deltaWatchers.length>0&&(
            <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}><table style={{width:"100%",borderCollapse:"collapse",fontSize:11,minWidth:480}}>
              <thead>
                <tr style={{background:"#0b1624",borderBottom:"1px solid #1a2c44",position:"sticky",top:0}}>
                  {["SYMBOL","SIDE","SIZE","ENTRY","TP","SL","TRAIL","STATUS","HWM","PNL EST","OPENED"].map(h=>(
                    <th key={h} style={{padding:"6px 10px",color:"#8ea2bf",fontSize:12,letterSpacing:1,fontWeight:700,textAlign:"left",whiteSpace:"nowrap"}}>{h}</th>
                  ))}
                  <th style={{padding:"6px 10px"}}/>
                </tr>
              </thead>
              <tbody>
                {deltaWatchers.map((w,i)=>{
                  const isActive=w.status==="WATCHING";
                  const isTP=w.status==="TRIGGERED"&&w.triggeredType==="TP";
                  const isSL=w.status==="TRIGGERED"&&w.triggeredType==="SL";
                  const isManual=w.status==="CANCELLED_POSITION_CLOSED"||w.status==="CLOSED";
                  const isScalpClosed=w.status==="CLOSED_SCALP";
                  const rowBg=isTP?"rgba(22,163,74,0.08)":isSL?"rgba(220,38,38,0.08)":
                              isManual||isScalpClosed?"rgba(139,148,158,0.04)":
                              (isActive&&w.scalpMode)?"rgba(161,98,7,0.06)":"transparent";
                  const hwmPct=w.hwm&&w.entryPrice&&parseFloat(w.hwm)!==parseFloat(w.entryPrice)?
                    ((parseFloat(w.hwm)-parseFloat(w.entryPrice))/parseFloat(w.entryPrice)*100).toFixed(2):null;

                  // ── Scalp countdown timer ──────────────────────────────────
                  const ScalpTimer = ()=>{
                    const [remaining, setRemaining] = useState(null);
                    useEffect(()=>{
                      if(!w.scalpMode || !w.openedAt) return;
                      const isBTC   = (w.symbol||"").toUpperCase().includes("BTC");
                      const windowMs = isBTC ? 28*60*1000 : 13*60*1000;
                      const update = ()=>{
                        const elapsed = Date.now() - w.openedAt;
                        const left    = Math.max(0, windowMs - elapsed);
                        setRemaining(left);
                      };
                      update();
                      const t = setInterval(update, 1000);
                      return ()=>clearInterval(t);
                    },[]);
                    if(remaining===null) return null;
                    const mins = Math.floor(remaining/60000);
                    const secs = Math.floor((remaining%60000)/1000);
                    const urgent = remaining < 2*60*1000; // under 2 min
                    const color  = remaining===0?"#6b7280":urgent?"#ff5b6c":"#49c2e4";
                    return(
                      <div style={{display:"flex",flexDirection:"column",gap:2}}>
                        <span style={{fontSize:11,color:"#49c2e4",fontWeight:700}}>⏰ SCALP</span>
                        {remaining > 0 ? (
                          <span style={{fontSize:13,fontWeight:800,color,
                            fontVariantNumeric:"tabular-nums",
                            animation:urgent?"pulse 1s infinite":"none"}}>
                            {mins}:{secs.toString().padStart(2,"0")}
                          </span>
                        ) : (
                          <span style={{fontSize:11,color:"#6b7280"}}>closing…</span>
                        )}
                        <div style={{height:3,borderRadius:2,background:"#e5e7eb",width:50,overflow:"hidden"}}>
                          <div style={{height:"100%",borderRadius:2,background:color,
                            width:`${Math.min(100,((w.scalpMode&&w.openedAt)?
                              (((w.symbol||"").toUpperCase().includes("BTC")?28*60*1000:13*60*1000)-remaining)/
                              ((w.symbol||"").toUpperCase().includes("BTC")?28*60*1000:13*60*1000)*100:0))}%`,
                            transition:"width 1s linear"}}/>
                        </div>
                      </div>
                    );
                  };

                  return(
                    <tr key={w.id||i} style={{borderBottom:"1px solid #161b22",background:rowBg}}>
                      <td style={{padding:"7px 10px",fontWeight:700,color:"#e8b339"}}>{w.symbol}</td>
                      <td style={{padding:"7px 10px"}}>
                        <span style={{fontSize:12,fontWeight:700,padding:"2px 5px",borderRadius:0,
                          background:w.side==="BUY"?"rgba(22,163,74,0.15)":"rgba(220,38,38,0.15)",
                          color:w.side==="BUY"?"#2ed47a":"#ff5b6c"}}>{w.side}</span>
                      </td>
                      <td style={{padding:"7px 10px",color:"#e7edf5"}}>{w.size}</td>
                      <td style={{padding:"7px 10px",color:"#8ea2bf"}}>${parseFloat(w.entryPrice||0).toFixed(4)}</td>
                      <td style={{padding:"7px 10px",color:w.tpEnabled?"#2ed47a":"#5a728f"}}>
                        {isActive?(
                          <input type="text" inputMode="decimal"
                            defaultValue={w.tpEnabled?parseFloat(w.tpPrice||0).toFixed(4):""}
                            placeholder="TP $"
                            onBlur={e=>{
                              const v = parseFloat(e.target.value);
                              if(!isNaN(v) && v>0) setDeltaWatchers(prev=>prev.map(x=>x.id===w.id?{...x,tpPrice:v,tpEnabled:true}:x));
                            }}
                            style={{width:72,background:"#0b1624",border:"1px solid rgba(63,185,80,0.4)",
                              borderRadius:0,padding:"2px 5px",color:"#2ed47a",
                              fontFamily:"inherit",fontSize:10,outline:"none"}}/>
                        ):(
                          w.tpEnabled?`$${parseFloat(w.tpPrice||0).toFixed(4)}`:"—"
                        )}
                      </td>
                      <td style={{padding:"7px 10px",color:w.slEnabled?"#ff5b6c":"#5a728f"}}>
                        {isActive?(
                          <input type="text" inputMode="decimal"
                            defaultValue={w.slEnabled?parseFloat(w.slPrice||0).toFixed(4):""}
                            placeholder="SL $"
                            onBlur={e=>{
                              const v = parseFloat(e.target.value);
                              if(!isNaN(v) && v>0) setDeltaWatchers(prev=>prev.map(x=>x.id===w.id?{...x,slPrice:v,slEnabled:true}:x));
                            }}
                            style={{width:72,background:"#0b1624",border:"1px solid rgba(248,81,73,0.4)",
                              borderRadius:0,padding:"2px 5px",color:"#ff5b6c",
                              fontFamily:"inherit",fontSize:10,outline:"none"}}/>
                        ):(
                          w.slEnabled?`$${parseFloat(w.slPrice||0).toFixed(4)}`:"—"
                        )}
                      </td>
                      <td style={{padding:"7px 10px"}}>
                        {w.trailEnabled?(
                          <span style={{color:"#49c2e4",fontSize:12,fontWeight:700}}>
                            🎯 {w.trailPct}%
                            {hwmPct&&parseFloat(hwmPct)>0&&(
                              <span style={{color:"#2ed47a"}}> +{hwmPct}%</span>
                            )}
                          </span>
                        ):<span style={{color:"#5a728f",fontSize:12}}>—</span>}
                      </td>
                      <td style={{padding:"7px 10px"}}>
                        {isActive&&w.scalpMode&&<ScalpTimer/>}
                        {isActive&&!w.scalpMode&&(
                          <span style={{fontSize:12,color:"#e8b339",fontWeight:700,
                            background:"rgba(0,212,170,0.08)",padding:"2px 6px",borderRadius:0}}>
                            👁 WATCHING
                          </span>
                        )}
                        {isTP&&(
                          <span style={{fontSize:12,color:"#2ed47a",fontWeight:700,
                            background:"rgba(63,185,80,0.1)",padding:"2px 6px",borderRadius:0}}>
                            ✓ TP HIT
                          </span>
                        )}
                        {isSL&&(
                          <span style={{fontSize:12,color:"#ff5b6c",fontWeight:700,
                            background:"rgba(248,81,73,0.1)",padding:"2px 6px",borderRadius:0}}>
                            ✗ SL HIT
                          </span>
                        )}
                        {(w.status==="CLOSED"||w.status==="CANCELLED_POSITION_CLOSED")&&(
                          <span style={{fontSize:12,color:"#8ea2bf",fontWeight:700,
                            background:"rgba(139,148,158,0.08)",padding:"2px 6px",borderRadius:0}}>
                            {w.triggeredType==="MANUAL_EXIT"?"👤 MANUAL EXIT":"📪 CLOSED"}
                          </span>
                        )}
                        {w.status==="CLOSED_SCALP"&&(
                          <span style={{fontSize:12,color:"#49c2e4",fontWeight:700,
                            background:"rgba(161,98,7,0.1)",padding:"2px 6px",borderRadius:0}}>
                            ⏰ SCALP CLOSED
                          </span>
                        )}
                        {w.status==="ERROR"&&(
                          <span style={{fontSize:12,color:"#ff5b6c"}}>⚠ ERR</span>
                        )}
                      </td>
                      <td style={{padding:"7px 10px",color:"#e8b339",fontSize:10}}>
                        {w.trailEnabled&&w.hwm?`$${parseFloat(w.hwm).toFixed(4)}`:"—"}
                      </td>
                      <td style={{padding:"7px 10px",fontWeight:700,
                        color:w.pnlEst&&parseFloat(w.pnlEst)>0?"#2ed47a":"#ff5b6c"}}>
                        {w.pnlEst?`$${w.pnlEst}`:"—"}
                      </td>
                      <td style={{padding:"7px 10px",color:"#5a728f",fontSize:12}}>{w.openedAt}</td>
                      <td style={{padding:"7px 10px"}}>
                        {isActive&&(
                          <button onClick={async()=>{
                            try{
                              await deltaFetch("/delta/position/close",{
                                method:"POST",headers:{"Content-Type":"application/json"},
                                body:JSON.stringify({product_id:w.productId,size:Math.abs(w.size),side:w.side})
                              });
                              setDeltaWatchers(prev=>prev.map(x=>x.id===w.id?{...x,
                                status:"CANCELLED_POSITION_CLOSED",
                                triggeredType:"MANUAL_EXIT",
                                triggeredAt:new Date().toISOString(),
                                note:"Manual close from app"}:x));
                              if(onShowToast) onShowToast(`Closed ${w.symbol}`,"ok");
                            }catch(e){
                              if(onShowToast) onShowToast("Close failed: "+e.message,"error");
                            }
                          }} style={{background:"rgba(220,38,38,0.15)",border:"1px solid #f85149",
                            borderRadius:0,padding:"2px 7px",color:"#ff5b6c",
                            fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
                            ✕ Close
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          )}
        </div>
      )}

      {/* ── Order Ticket Modal ── */}
      {ticket&&(()=>{
        const tPrice  = parseFloat(ticket.price||0);
        const tQty    = parseInt(ticket.qty||1);
        const isBuy   = ticket.side==="BUY";
        const isSell  = ticket.side==="SELL";
        const isOption = ticket.contractType==="call_options"||ticket.contractType==="put_options";
        const isOptionSell = isOption && isSell;
        const TAKER_FEE = 0.0005;
        const feeEst  = tPrice>0 ? (tPrice * tQty * TAKER_FEE * 2).toFixed(4) : null;
        const minTpPct = 0.20;
        const minTpPrice = tPrice>0 ? (isBuy ? tPrice*(1+minTpPct/100) : tPrice*(1-minTpPct/100)) : null;
        const availBal = riskData?.available_balance || ticket.availBal || 0;
        const tpVal  = parseFloat(ticket.tp||0);
        const slVal  = parseFloat(ticket.sl||0);
        const tpPct  = tPrice>0 && tpVal>0 ? ((tpVal-tPrice)/tPrice*(isBuy?1:-1)*100).toFixed(2) : null;
        const slPct  = tPrice>0 && slVal>0 ? ((slVal-tPrice)/tPrice*(isBuy?-1:1)*100).toFixed(2) : null;
        const tpOk   = tpVal>0 && minTpPrice && (isBuy ? tpVal>minTpPrice : tpVal<minTpPrice);
        const slOk   = slVal>0;

        // Margin calculation — account for user's selected leverage
        const initMarginPct     = ticket.initMarginPct || 0.05;
        const userLeverage      = parseFloat(ticket.leverage||1) || 1;
        // At 100x leverage: margin per contract = price/100 = 1% of notional
        const leverageMarginPct = userLeverage > 1 ? (1/userLeverage) : initMarginPct;
        // Delta enforces a minimum margin (initMarginPct), can't go below it
        const effectiveMarginPct = Math.max(initMarginPct, leverageMarginPct);

        const underlyingPrice  = ticket.underlyingPrice || 0;
        const marginPctForCalc = isOptionSell
          ? Math.max(effectiveMarginPct, 0.10)
          : effectiveMarginPct;
        const marginBase = isOption && underlyingPrice > 0 ? underlyingPrice : tPrice;
        const marginEst  = marginBase>0 ? (marginBase * tQty * marginPctForCalc).toFixed(2) : null;
        const marginPct  = Math.round(marginPctForCalc * 100);
        const marginOk   = !marginEst || availBal >= parseFloat(marginEst)*1.1;
        // Max qty uses 90% of balance (not 30%) so user can trade full position
        const maxQty     = availBal>0 && marginBase>0
          ? Math.max(1, Math.floor((availBal * 0.9) / (marginBase * marginPctForCalc)))
          : null;
        const balUsedPct = marginEst && availBal>0
          ? ((parseFloat(marginEst)/availBal)*100).toFixed(1) : null;

        return (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{background:"#0f1d30",border:"1px solid #c8b494",borderRadius:12,padding:20,width:360,fontFamily:"inherit",maxHeight:"92vh",overflowY:"auto",boxShadow:"0 8px 40px rgba(0,0,0,0.3)"}}>

            {/* Header */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div>
                {ticket._loading
                  ? <span style={{fontSize:13,color:"#8ea2bf"}}>⟳ Finding {ticket.instrumentType} contract...</span>
                  : <>
                    <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                      <span style={{fontWeight:700,fontSize:14,color:"#e8b339"}}>Δ {ticket.symbol}</span>
                      {isOption&&(
                        <span style={{fontSize:11,padding:"2px 7px",borderRadius:0,fontWeight:700,
                          background:ticket.contractType==="call_options"?"rgba(22,163,74,0.15)":"rgba(220,38,38,0.15)",
                          color:ticket.contractType==="call_options"?"#2ed47a":"#ff5b6c",
                          border:`1px solid ${ticket.contractType==="call_options"?"rgba(22,163,74,0.4)":"rgba(220,38,38,0.4)"}`}}>
                          {ticket.contractType==="call_options"?"CALL":"PUT"}
                        </span>
                      )}
                      {ticket.strikePrice>0&&(
                        <span style={{fontSize:11,color:"#e7edf5",fontWeight:700}}>
                          Strike: ${parseFloat(ticket.strikePrice).toFixed(2)}
                        </span>
                      )}
                      {ticket.expiry&&(
                        <span style={{fontSize:10,color:"#8ea2bf"}}>
                          Exp: {new Date(ticket.expiry).toLocaleDateString("en-IN",{day:"2-digit",month:"short"})}
                        </span>
                      )}
                    </div>
                    {ticket.underlying&&ticket.underlying!==ticket.symbol&&(
                      <div style={{fontSize:10,color:"#8ea2bf",marginTop:2}}>Underlying: {ticket.underlying} @ ${tPrice>0?tPrice.toFixed(2):"—"}</div>
                    )}
                  </>
                }
                {availBal>0&&<span style={{fontSize:12,color:"#8ea2bf",marginTop:2,display:"block"}}>
                  Bal: <b style={{color:"#2ed47a"}}>${availBal.toFixed(2)}</b>
                </span>}
              </div>
              <button onClick={()=>setTicket(null)} style={{background:"none",border:"none",color:"#8ea2bf",fontSize:16,cursor:"pointer"}}>✕</button>
            </div>

            {/* Option SELL info banner */}
            {isOptionSell&&(
              <div style={{background:"rgba(232,179,57,0.1)",border:"1px solid rgba(232,179,57,0.3)",
                borderRadius:7,padding:"8px 10px",marginBottom:10,fontSize:11,color:"#e8b339"}}>
                <div style={{fontWeight:700,marginBottom:3}}>⚠ Option SELL (Write) — Higher Margin Required</div>
                <div style={{color:"#8ea2bf",lineHeight:1.5}}>
                  Delta takes <b>~{marginPct}% of contract price</b> as initial margin for option writing.
                  You keep the premium if option expires worthless.{" "}
                  Max loss is theoretically unlimited — always set a Stop Loss.
                </div>
              </div>
            )}

            {/* Side */}
            <div style={{display:"flex",gap:6,marginBottom:10}}>
              {["BUY","SELL"].map(s=>(
                <button key={s} onClick={()=>setTicket(p=>({...p,side:s}))}
                  style={{flex:1,background:ticket.side===s?(s==="BUY"?"rgba(22,163,74,0.2)":"rgba(220,38,38,0.2)"):"#0b1624",
                    border:"1px solid "+(ticket.side===s?(s==="BUY"?"#2ed47a":"#ff5b6c"):"#23405f"),
                    borderRadius:0,padding:"7px",color:ticket.side===s?(s==="BUY"?"#2ed47a":"#ff5b6c"):"#8ea2bf",
                    fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                  {s==="BUY"?"▲ BUY / LONG":"▼ SELL / SHORT"}
                </button>
              ))}
            </div>

            {/* Leverage selector */}
            <div style={{marginBottom:10}}>
              <div style={{fontSize:11,color:"#8ea2bf",marginBottom:4}}>
                Leverage
                <span style={{fontSize:9,color:"#5a728f",marginLeft:6}}>
                  Higher leverage = more exposure, less margin needed per contract
                </span>
              </div>
              <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                {[1,2,5,10,20,50,100,200].map(lev=>{
                  const cur = ticket.leverage||1;
                  return(
                    <button key={lev} onClick={()=>setTicket(p=>({...p,leverage:lev}))}
                      style={{background:cur===lev?"rgba(232,179,57,0.2)":"#0b1624",
                        border:`1px solid ${cur===lev?"#e8b339":"#1a2c44"}`,
                        borderRadius:0,padding:"4px 8px",color:cur===lev?"#e8b339":"#8ea2bf",
                        fontFamily:"inherit",fontSize:11,fontWeight:cur===lev?700:400,cursor:"pointer"}}>
                      {lev}x
                    </button>
                  );
                })}
              </div>
              {(ticket.leverage||1) > 50 && (
                <div style={{fontSize:10,color:"#ff5b6c",marginTop:4}}>
                  ⚠ {ticket.leverage}x leverage — extreme risk. Only use for short-term scalps.
                </div>
              )}
            </div>

            {/* Order type */}
            <div style={{display:"flex",gap:6,marginBottom:10}}>
              {[["limit_order","Limit"],["market_order","Market"]].map(([v,l])=>(
                <button key={v} onClick={()=>setTicket(p=>({...p,orderType:v}))}
                  style={{flex:1,background:ticket.orderType===v?"rgba(107,114,128,0.15)":"#0b1624",
                    border:"1px solid "+(ticket.orderType===v?"#6b7280":"#23405f"),borderRadius:0,padding:"5px",
                    color:ticket.orderType===v?"#6b7280":"#8ea2bf",fontFamily:"inherit",fontSize:10,cursor:"pointer"}}>
                  {l}
                </button>
              ))}
            </div>

            {/* Qty + Price row */}
            <div style={{display:"flex",gap:8,marginBottom:6}}>
              <div style={{flex:1}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                  <span style={{fontSize:11,color:"#8ea2bf"}}>
                    Contracts
                    {userLeverage>1&&<span style={{color:"#e8b339",marginLeft:4,fontWeight:700}}>{userLeverage}x</span>}
                  </span>
                  {maxQty&&<span style={{fontSize:10,color:"#e8b339",cursor:"pointer",fontWeight:700}}
                    onClick={()=>setTicket(p=>({...p,qty:maxQty}))}>
                    Max: {maxQty} ({userLeverage>1?`${userLeverage}x lev`:"90% bal"})
                  </span>}
                </div>
                <input type="text" inputMode="numeric" value={ticket.qty||""}
                  placeholder="1"
                  onChange={e=>setTicket(p=>({...p,qty:e.target.value}))}
                  style={{width:"100%",background:"#0b1624",border:"2px solid #c8b494",borderRadius:0,
                    padding:"7px 8px",color:"#e7edf5",fontFamily:"inherit",fontSize:14,fontWeight:700,outline:"none",boxSizing:"border-box"}}/>
              </div>
              {ticket.orderType==="limit_order"&&(
                <div style={{flex:1.5}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                    <span style={{fontSize:11,color:"#8ea2bf"}}>
                      {isOption?"Option Premium $":"Limit Price $"}
                    </span>
                    {isOption&&(
                      <button onClick={async()=>{
                        try {
                          // Try symbol-based ticker first (more reliable than product_id)
                          const sym = ticket.symbol;
                          let mp = 0;
                          const attempts = [
                            ()=>deltaFetch(`/delta/ticker/${sym}`),
                            ()=>deltaFetch(`/delta/products/${ticket.product_id}/ticker`),
                          ];
                          for(const a of attempts) {
                            try {
                              const t = await a();
                              mp = parseFloat(t?.result?.mark_price||t?.mark_price||t?.result?.close||t?.close||0);
                              if(mp>0) break;
                            } catch(e) {}
                          }
                          if(mp>0) setTicket(p=>({...p, price:parseFloat(mp.toFixed(4))}));
                          else if(onShowToast) onShowToast("⚠ Could not fetch premium — enter manually","error");
                        } catch(e) {}
                      }} style={{background:"rgba(232,179,57,0.1)",border:"1px solid rgba(232,179,57,0.3)",
                        borderRadius:0,padding:"2px 6px",color:"#e8b339",fontFamily:"inherit",
                        fontSize:9,cursor:"pointer",fontWeight:700}}>
                        ↻ Fetch
                      </button>
                    )}
                  </div>
                  <input type="text" inputMode="decimal" value={ticket.price||""}
                    placeholder={isOption?"Enter premium (e.g. 120.50)":"0.00"}
                    onChange={e=>setTicket(p=>({...p,price:e.target.value}))}
                    style={{width:"100%",background:"#0b1624",border:"1px solid #c8b494",borderRadius:0,
                      padding:"7px 8px",color:"#e7edf5",fontFamily:"inherit",fontSize:13,outline:"none",boxSizing:"border-box"}}/>
                  {isOption&&ticket.underlyingPrice>0&&(
                    <div style={{fontSize:9,color:"#5a728f",marginTop:2}}>
                      Underlying: ${parseFloat(ticket.underlyingPrice).toLocaleString("en-US",{maximumFractionDigits:0})} · Margin based on underlying
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Quick qty buttons based on balance % */}
            {availBal>0&&marginBase>0&&(
              <div style={{display:"flex",gap:4,marginBottom:8,alignItems:"center"}}>
                <span style={{fontSize:11,color:"#5a728f",flexShrink:0}}>Use bal:</span>
                {[10,20,30,50].map(pct=>{
                  const q = Math.max(1,Math.floor((availBal*pct/100)/(marginBase*marginPctForCalc)));
                  return (
                    <button key={pct} onClick={()=>setTicket(p=>({...p,qty:q}))}
                      style={{flex:1,background:"#0b1624",border:"1px solid #d4c4a8",borderRadius:0,
                        padding:"4px 2px",color:"#8ea2bf",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>
                      {pct}%<br/><b style={{color:"#e7edf5"}}>{q}</b>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Margin estimate box */}
            {(marginBase>0||tPrice>0)&&tQty>0&&(
              <div style={{background:marginOk?"rgba(22,163,74,0.06)":"rgba(220,38,38,0.08)",
                border:`1px solid ${marginOk?"rgba(22,163,74,0.25)":"rgba(220,38,38,0.3)"}`,
                borderRadius:7,padding:"8px 10px",marginBottom:8,fontSize:11}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4}}>
                  <span style={{color:"#8ea2bf"}}>
                    Margin req ({marginPct}%{isOption?" of underlying":""})
                  </span>
                  <span style={{color:marginOk?"#2ed47a":"#ff5b6c",fontWeight:700,textAlign:"right"}}>
                    ${marginEst||"—"}
                  </span>
                  {isOption&&tPrice>0&&<>
                    <span style={{color:"#8ea2bf"}}>Premium (cost/contract)</span>
                    <span style={{color:"#e8b339",fontWeight:700,textAlign:"right"}}>${parseFloat(tPrice).toFixed(4)}</span>
                  </>}
                  <span style={{color:"#8ea2bf"}}>Available balance</span>
                  <span style={{color:"#2ed47a",fontWeight:700,textAlign:"right"}}>${availBal.toFixed(2)}</span>
                  {balUsedPct&&<>
                    <span style={{color:"#8ea2bf"}}>Balance used</span>
                    <span style={{color:parseFloat(balUsedPct)>80?"#ff5b6c":"#e8b339",fontWeight:700,textAlign:"right"}}>{balUsedPct}%</span>
                  </>}
                  {feeEst&&tPrice>0&&<>
                    <span style={{color:"#8ea2bf"}}>Fees (0.1% r/t)</span>
                    <span style={{color:"#e8b339",textAlign:"right"}}>${feeEst}</span>
                  </>}
                </div>
                {!marginOk&&(
                  <div style={{color:"#ff5b6c",fontSize:11,marginTop:5,fontWeight:700,borderTop:"1px solid rgba(220,38,38,0.2)",paddingTop:4}}>
                    ⚠ Insufficient margin — reduce contracts or add balance to Delta.
                  </div>
                )}
              </div>
            )}

            {/* TP / SL inputs */}
            <div style={{display:"flex",gap:8,marginBottom:4}}>
              <div style={{flex:1}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                  <span style={{fontSize:11,color:"#2ed47a"}}>TP Price $</span>
                  {tpPct&&<span style={{fontSize:11,color:"#2ed47a",fontWeight:700}}>{isBuy?"+":"-"}{Math.abs(tpPct)}%</span>}
                </div>
                <input type="text" inputMode="decimal" value={ticket.tp||""}
                  placeholder={minTpPrice?`≥ $${minTpPrice.toFixed(4)}`:"optional"}
                  onChange={e=>setTicket(p=>({...p,tp:e.target.value||null}))}
                  style={{width:"100%",background:"#0b1624",
                    border:`1px solid ${ticket.tp?(tpOk?"#2ed47a":"#e8b339"):"#23405f"}`,
                    borderRadius:0,padding:"6px 8px",color:"#2ed47a",
                    fontFamily:"inherit",fontSize:12,outline:"none",boxSizing:"border-box"}}/>
              </div>
              <div style={{flex:1}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                  <span style={{fontSize:11,color:"#ff5b6c"}}>SL Price $</span>
                  {slPct&&<span style={{fontSize:11,color:"#ff5b6c",fontWeight:700}}>-{Math.abs(slPct)}%</span>}
                </div>
                <input type="text" inputMode="decimal" value={ticket.sl||""}
                  placeholder={isOptionSell?"required for sell":"optional"}
                  onChange={e=>setTicket(p=>({...p,sl:e.target.value||null}))}
                  style={{width:"100%",background:"#0b1624",
                    border:`1px solid ${ticket.sl?"#ff5b6c":isOptionSell?"rgba(220,38,38,0.5)":"#23405f"}`,
                    borderRadius:0,padding:"6px 8px",color:"#ff5b6c",
                    fontFamily:"inherit",fontSize:12,outline:"none",boxSizing:"border-box"}}/>
              </div>
            </div>

            {/* % quick-set buttons */}
            {tPrice>0&&(
              <div style={{display:"flex",gap:4,marginBottom:10}}>
                <span style={{fontSize:11,color:"#5a728f",alignSelf:"center",flexShrink:0}}>Quick TP/SL:</span>
                {[0.5,1,2,3,5].map(pct=>(
                  <button key={pct} onClick={()=>setTicket(p=>({...p,
                    tp: (isBuy ? tPrice*(1+pct/100) : tPrice*(1-pct/100)).toFixed(4),
                    sl: (isBuy ? tPrice*(1-pct/2/100) : tPrice*(1+pct/2/100)).toFixed(4),
                  }))}
                    style={{flex:1,background:"#0b1624",border:"1px solid #d4c4a8",borderRadius:0,
                      padding:"3px 2px",color:"#8ea2bf",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>
                    {pct}%
                  </button>
                ))}
              </div>
            )}

            {/* Fee warning */}
            {ticket.tp&&tpPct&&parseFloat(tpPct)<minTpPct&&(
              <div style={{background:"rgba(232,179,57,0.08)",border:"1px solid rgba(232,179,57,0.3)",
                borderRadius:0,padding:"5px 8px",marginBottom:8,fontSize:11,color:"#e8b339"}}>
                ⚠ TP {tpPct}% may not cover Delta fees (~0.10% round-trip). Recommend ≥ 0.2%.
              </div>
            )}

            {placeMsg&&(
              <div style={{padding:"8px 10px",borderRadius:0,marginBottom:8,fontSize:11,
                background:placeMsg.ok?"rgba(22,163,74,0.08)":"rgba(220,38,38,0.06)",
                border:`1px solid ${placeMsg.ok?"rgba(22,163,74,0.3)":"rgba(220,38,38,0.2)"}`,
                color:placeMsg.ok?"#2ed47a":"#ff5b6c"}}>
                {placeMsg.msg}
                {!placeMsg.ok&&isOption&&(
                  <div style={{marginTop:6,fontSize:10,color:"#8ea2bf",lineHeight:1.6}}>
                    <b style={{color:"#e8b339"}}>Why:</b> Delta options use a different symbol format (e.g. <code>BTC-310325-70000-C</code>).<br/>
                    Your VPS backend's <code>/delta/products</code> endpoint needs to support<br/>
                    <code>?contract_type=call_options</code> or <code>?underlying_asset_symbol=BTC</code> query params.<br/>
                    <b>Workaround:</b> Use the Chain tab → find the option → click BUY/SELL there.
                  </div>
                )}
              </div>
            )}

            <button onClick={placeOrder} disabled={placing}
              style={{width:"100%",
                background:isBuy?"linear-gradient(135deg,#1a7f37,#3fb950)":"linear-gradient(135deg,#8b1a1a,#f85149)",
                border:"none",borderRadius:0,padding:"11px",color:"#fff",
                fontFamily:"inherit",fontSize:12,fontWeight:700,
                cursor:placing?"default":"pointer",opacity:placing?0.7:1}}>
              {placing?"Placing...":(isBuy?"▲ PLACE LONG":"▼ PLACE SHORT")}
            </button>

            {!marginOk&&(
              <div style={{marginTop:8,fontSize:12,color:"#ff5b6c",textAlign:"center"}}>
                ⚠ Insufficient margin buffer — reduce size or add funds to wallet
              </div>
            )}
          </div>
        </div>
        );
      })()}
      {showSignalModal&&(
        <SignalSettingsModal
          params={signalParams||DEFAULT_SIGNAL_PARAMS}
          onChange={setSignalParams}
          onClose={()=>setShowSignalModal(false)}
        />
      )}

      {/* Delta Strategy Popup */}
      {deltaStratPopup&&(
        <StrategyPopup
          {...deltaStratPopup}
          canTrade={canTrade}
          onTrade={(params)=>{
            const instrType = params.instrumentType;
            const isFut = instrType==="FUT";
            openDeltaTicket(
              deltaStratPopup.symbol,
              params.side,
              deltaStratPopup.ltp,
              isFut ? "PERP" : instrType,   // FUT → PERP on Delta
              params.suggestedStrike || null,
            );
          }}
          onClose={()=>setDeltaStratPopup(null)}
        />
      )}

      {/* AI Panel */}
      <AIPanel isAdmin={isAdmin} canTrade={canTrade} results={dAiRes} loading={dAiLoad} error={dAiErr}
        visible={dAiVis} onClose={()=>setDAiVis(false)}
        onTrade={(params)=>{
          // AI scan trade → open Delta strategy popup
          setDeltaStratPopup({
            symbol:  params.symbol||"",
            exchange:"DELTA",
            ltp:     params.price||params.entryPrice||0,
            hasBuy:  params.side==="BUY",
            hasSell: params.side==="SELL",
            signals: [],
            lotSize: 1,
            mode:    "delta",
          });
          setDAiVis(false);
        }}
        onShowToast={onShowToast}
        tabMode="delta"
        autoTrade={dAutoTrade} setAutoTrade={setDAutoTrade}
        minAutoConf={dMinConf} setMinAutoConf={setDMinConf}
        autoFired={dAutoFired}
        marginPct={dMargPct} setMarginPct={setDMargPct}
        maxDaily={dMaxDay} setMaxDaily={setDMaxDay}
        maxScans={dMaxScans} setMaxScans={setDMaxScans}
        scanCount={dScanCount} balance={dBalance}/>
      {chartModal&&(
        <ChartModal
          symbol={chartModal.symbol}
          exchange={chartModal.exchange||"NSE"}
          timeframe="1hour"
          onClose={()=>setChartModal(null)}
          signalParams={signalParams}
          onShowToast={onShowToast}
          authed={true}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// SETTINGS MODAL
// ══════════════════════════════════════════════════════════════════════════════
// ── Zerodha API Setup component (inside Settings) ────────────────────────────
function ZerodhaApiSetup({ uid = "", userRole = "admin" }) {
  const [apiKey,     setApiKey]     = useState("");
  const [apiSecret,  setApiSecret]  = useState("");
  const [accessTok,  setAccessTok]  = useState("");
  const [proxyHost,  setProxyHost]  = useState("");
  const [proxyPort,  setProxyPort]  = useState("1080");
  const [proxyUser,  setProxyUser]  = useState("");
  const [proxyPass,  setProxyPass]  = useState("");
  const [saved,      setSaved]      = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [testing,    setTesting]    = useState(false);
  const [testRes,    setTestRes]    = useState(null);
  const [err,        setErr]        = useState("");
  const [expanded,   setExpanded]   = useState(false);
  const isAdminRole = userRole === "admin";

  // Load saved state on mount; for non-admin also fetch access_token for client-side orders
  useEffect(()=>{
    if(!uid) return;
    apiFetch("/user/kite-creds").then(d=>{
      setSaved(d);
      if(d.api_key)    setApiKey(d.api_key);
      if(d.proxy_host) setProxyHost(d.proxy_host);
      if(d.proxy_port) setProxyPort(d.proxy_port);
      if(d.proxy_user) setProxyUser(d.proxy_user);
      setExpanded(!d.api_key); // auto-expand if no creds yet
    }).catch(()=>{});
    // Non-admin: fetch access_token and cache in sessionStorage for direct order placement
    if(!isAdminRole){
      apiFetch("/user/kite-access-token").then(d=>{
        if(d.ok && d.access_token){
          sessionStorage.setItem("kite_client_token", d.access_token);
          sessionStorage.setItem("kite_client_key", d.api_key);
        }
      }).catch(()=>{});
    }
  },[uid, isAdminRole]);

  const handleSave = async()=>{
    if(!apiKey||!apiSecret){ setErr("API key and secret are required."); return; }
    setSaving(true); setErr(""); setTestRes(null);
    try {
      const r = await apiFetch("/user/kite-creds","PUT",{
        api_key:      apiKey.trim(),
        api_secret:   apiSecret.trim(),
        access_token: accessTok.trim(),
        proxy_host:   proxyHost.trim(),
        proxy_port:   proxyPort.trim(),
        proxy_user:   proxyUser.trim(),
        proxy_pass:   proxyPass.trim(),
      });
      setSaved({api_key:apiKey.trim(), api_secret_saved:true, authenticated:r.status==="authenticated"});
      setApiSecret(""); // clear secret from UI after save
      setTestRes({ok:r.ok, msg: r.message});
      // Cache for non-admin client-side order placement
      if(!isAdminRole && accessTok.trim()) {
        sessionStorage.setItem("kite_client_token", accessTok.trim());
        sessionStorage.setItem("kite_client_key",   apiKey.trim());
      }
    } catch(e){
      const msg = e.message||"Save failed";
      if(msg.includes("165.22.209.25")||msg.includes("whitelist")||msg.includes("IP")){
        setErr(isAdminRole
          ? "⚠️ IP 165.22.209.25 not whitelisted — see instructions below."
          : "⚠️ Your public IP is not whitelisted — see instructions below. Your current IP is shown above.");
      } else {
        setErr(msg);
      }
    } finally { setSaving(false); }
  };

  const handleTest = async()=>{
    setTesting(true); setTestRes(null);
    try {
      const r = await apiFetch("/user/kite-test","POST",{});
      setTestRes({ok:r.ok, msg: r.message});
      if(r.ok) setSaved(p=>({...p, authenticated:true}));
    } catch(e){ setTestRes({ok:false, msg:e.message}); }
    finally { setTesting(false); }
  };

  const handleDelete = async()=>{
    if(!window.confirm("Remove saved Kite credentials?")) return;
    await apiFetch("/user/kite-creds","DELETE",{}).catch(()=>{});
    setSaved(null); setApiKey(""); setApiSecret(""); setAccessTok("");
    setTestRes(null); setExpanded(true);
  };

  const ikey = {background:"#07101c",border:"1px solid #1a2c44",borderRadius:4,
    padding:"7px 10px",color:"#e7edf5",fontFamily:"inherit",fontSize:11,width:"100%",boxSizing:"border-box"};
  const lbl  = {fontSize:10,color:"#5a728f",fontWeight:700,marginBottom:4,display:"block"};
  const btn  = (bg,col)=>({background:bg,border:"1px solid "+col,borderRadius:4,padding:"7px 14px",
    color:col,fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"});

  return (
    <div style={{marginBottom:16}}>
      {/* Header row */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
        fontSize:10,color:"#5a728f",fontWeight:700,letterSpacing:2,marginBottom:10}}>
        <span>🔑 ZERODHA API — ORDER PLACEMENT</span>
        <button onClick={()=>setExpanded(p=>!p)}
          style={{background:"none",border:"none",color:"#5a728f",cursor:"pointer",fontSize:12}}>
          {expanded?"▲ hide":"▼ setup"}
        </button>
      </div>

      {/* Status badge */}
      {saved?.api_key && (
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginBottom:expanded?10:0,
          background:"#0b1624",border:"1px solid #1a2c44",borderRadius:4,padding:"8px 12px"}}>
          <span style={{fontSize:11,color: saved.authenticated?"#2ed47a":"#e8b339",fontWeight:700}}>
            {saved.authenticated ? "✅ Session Active" : "⚠️ Not Logged In"}
          </span>
          <span style={{fontSize:10,color:"#5a728f"}}>API key: {saved.api_key.slice(0,8)}…</span>
          {!saved.authenticated && (
            <button onClick={()=>{ window.open(`${window.API_BASE||''}/login?uid=${uid}`,"_blank"); }}
              style={btn("rgba(232,179,57,0.1)","#e8b339")}>
              Login via Kite
            </button>
          )}
          <button onClick={handleTest} disabled={testing}
            style={btn("rgba(96,165,250,0.08)","#4a9eff")}>
            {testing?"Testing…":"Test"}
          </button>
          <button onClick={()=>setExpanded(p=>!p)}
            style={btn("transparent","#23405f")}>Edit</button>
        </div>
      )}

      {/* Expanded form */}
      {expanded && (
        <div style={{background:"#0b1624",border:"1px solid #1a2c44",borderRadius:4,padding:14}}>

          {/* IP whitelist notice — role-specific */}
          {isAdminRole ? (
            <div style={{background:"rgba(232,179,57,0.08)",border:"1px solid rgba(232,179,57,0.25)",
              borderRadius:4,padding:"10px 12px",marginBottom:14,fontSize:11,lineHeight:1.7}}>
              <b style={{color:"#e8b339"}}>⚠️ Required: Whitelist server IP on your Kite app</b><br/>
              <span style={{color:"#8ea2bf"}}>
                Go to{" "}
                <a href="https://developers.kite.trade" target="_blank" rel="noreferrer"
                  style={{color:"#4a9eff"}}>developers.kite.trade</a>
                {" "}→ Apps → your app → Edit → <b style={{color:"#e7edf5"}}>Allowed IPs</b> → add{" "}
                <code style={{color:"#e8b339",background:"rgba(232,179,57,0.1)",padding:"1px 5px",borderRadius:3}}>
                  165.22.209.25
                </code>
                {" "}→ Save.
              </span>
            </div>
          ) : (
            <div style={{background:"rgba(74,158,255,0.06)",border:"1px solid rgba(74,158,255,0.25)",
              borderRadius:4,padding:"10px 12px",marginBottom:14,fontSize:11,lineHeight:1.7}}>
              <b style={{color:"#4a9eff"}}>🌐 Orders go directly from your browser → Kite</b><br/>
              <span style={{color:"#8ea2bf"}}>
                Your orders use <b style={{color:"#e7edf5"}}>your own IP</b> — not our server.
                Make sure your static/virtual IP is whitelisted in your Kite Connect app at{" "}
                <a href="https://developers.kite.trade" target="_blank" rel="noreferrer"
                  style={{color:"#4a9eff"}}>developers.kite.trade</a>.
              </span>
            </div>
          )}

          <div style={{display:"grid",gap:10,marginBottom:12}}>
            <div>
              <label style={lbl}>API Key</label>
              <input value={apiKey} onChange={e=>setApiKey(e.target.value)}
                placeholder="e.g. abcde12345" style={ikey} autoComplete="off"/>
            </div>
            <div>
              <label style={lbl}>API Secret <span style={{color:"#3a5270"}}>(stored encrypted — never returned)</span></label>
              <input type="password" value={apiSecret} onChange={e=>setApiSecret(e.target.value)}
                placeholder={saved?.api_secret_saved?"(saved — leave blank to keep)":"Paste your API secret"}
                style={ikey} autoComplete="new-password"/>
            </div>
            <div>
              <label style={lbl}>
                Access Token{" "}
                <span style={{color:"#3a5270",fontWeight:400}}>
                  (optional — auto-generated after Kite login)
                </span>
              </label>
              <input value={accessTok} onChange={e=>setAccessTok(e.target.value)}
                placeholder="Paste if you have one, else leave blank"
                style={ikey} autoComplete="off"/>
            </div>
            {!isAdminRole && (
              <div style={{borderTop:"1px solid #1a2c44",paddingTop:12,marginTop:4}}>
                <label style={{...lbl,color:"#4a9eff",fontSize:11}}>
                  🌐 Static IP Proxy{" "}
                  <span style={{color:"#3a5270",fontWeight:400,fontSize:9}}>
                    — VPS routes your orders through your own IP
                  </span>
                </label>
                <div style={{display:"grid",gridTemplateColumns:"1fr 90px",gap:8,marginBottom:8}}>
                  <div>
                    <div style={{...lbl,marginBottom:3}}>Your Static IP</div>
                    <input value={proxyHost} onChange={e=>setProxyHost(e.target.value)}
                      placeholder="e.g. 103.45.67.89"
                      style={{...ikey, border:`1px solid ${proxyHost?"#4a9eff":"#1a2c44"}`}}
                      autoComplete="off"/>
                  </div>
                  <div>
                    <div style={{...lbl,marginBottom:3}}>Port</div>
                    <input value={proxyPort} onChange={e=>setProxyPort(e.target.value)}
                      placeholder="1080"
                      style={{...ikey, border:`1px solid ${proxyPort?"#4a9eff":"#1a2c44"}`}}/>
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:6}}>
                  <div>
                    <div style={{...lbl,marginBottom:3}}>Username <span style={{color:"#3a5270",fontWeight:400}}>(if any)</span></div>
                    <input value={proxyUser} onChange={e=>setProxyUser(e.target.value)}
                      placeholder="optional" style={ikey} autoComplete="off"/>
                  </div>
                  <div>
                    <div style={{...lbl,marginBottom:3}}>Password <span style={{color:"#3a5270",fontWeight:400}}>(if any)</span></div>
                    <input type="password" value={proxyPass} onChange={e=>setProxyPass(e.target.value)}
                      placeholder="optional" style={ikey} autoComplete="new-password"/>
                  </div>
                </div>
                <div style={{fontSize:9,color:"#3a5270",lineHeight:1.7}}>
                  Requires a <b style={{color:"#5a728f"}}>SOCKS5 proxy</b> running at your static IP.
                  Orders go: VPS → your proxy IP → Kite, so Kite sees your whitelisted IP.{" "}
                  Whitelist your static IP at{" "}
                  <a href="https://developers.kite.trade" target="_blank" rel="noreferrer"
                    style={{color:"#4a9eff"}}>developers.kite.trade</a>
                  {" "}→ your app → Allowed IPs.
                </div>
                <div style={{marginTop:6,background:"rgba(0,136,204,0.07)",border:"1px solid rgba(0,136,204,0.2)",
                  borderRadius:5,padding:"7px 10px",fontSize:9,color:"#4a9eff",lineHeight:1.7}}>
                  <b>💡 DigitalOcean Droplet users:</b> Create a $5/mo Ubuntu droplet → install Dante SOCKS5:
                  {" "}<code style={{background:"rgba(0,0,0,0.3)",borderRadius:3,padding:"0 4px",fontSize:9,color:"#7dd3fc"}}>
                    apt install dante-server
                  </code>{" "}
                  → enter your Droplet IP above with port 1080. Then whitelist the Droplet IP in your Kite API settings.
                </div>
                {saved?.proxy_configured && (
                  <div style={{marginTop:6,fontSize:10,color:"#2ed47a"}}>
                    ✓ Proxy active — orders routing through {proxyHost||saved.proxy_host}
                  </div>
                )}
              </div>
            )}
          </div>

          {err && (
            <div style={{fontSize:11,color:"#ff5b6c",marginBottom:10,
              background:"rgba(255,91,108,0.08)",border:"1px solid rgba(255,91,108,0.2)",
              borderRadius:4,padding:"8px 10px"}}>
              {err}
              {(err.includes("whitelist")||err.includes("IP")) && (
                <div style={{marginTop:6,color:"#8ea2bf"}}>
                  {isAdminRole
                    ? "Go to developers.kite.trade → your app → Edit → Allowed IPs → add 165.22.209.25"
                    : "Go to developers.kite.trade → your app → Edit → Allowed IPs → add your static/virtual IP"}
                </div>
              )}
            </div>
          )}

          {testRes && (
            <div style={{fontSize:11,marginBottom:10,
              color:testRes.ok?"#2ed47a":"#e8b339",
              background:testRes.ok?"rgba(46,212,122,0.06)":"rgba(232,179,57,0.06)",
              border:`1px solid ${testRes.ok?"rgba(46,212,122,0.2)":"rgba(232,179,57,0.2)"}`,
              borderRadius:4,padding:"8px 10px"}}>
              {testRes.msg}
            </div>
          )}

          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <button onClick={handleSave} disabled={saving||(!apiSecret&&!saved?.api_secret_saved)}
              style={btn("rgba(96,165,250,0.12)","#4a9eff")}>
              {saving?"Saving…":"💾 Save Credentials"}
            </button>
            {saved?.api_key && (
              <>
                <button onClick={handleTest} disabled={testing}
                  style={btn("rgba(46,212,122,0.08)","#2ed47a")}>
                  {testing?"Testing…":"🔌 Test Connection"}
                </button>
                <button onClick={handleDelete}
                  style={btn("rgba(255,91,108,0.08)","#ff5b6c")}>
                  🗑 Remove
                </button>
              </>
            )}
          </div>

          <div style={{marginTop:12,fontSize:10,color:"#3a5270",lineHeight:1.7}}>
            After saving, click <b style={{color:"#e7edf5"}}>Login via Kite</b> above to authenticate.
            Your access token refreshes daily — you'll need to re-login each day.{" "}
            <a href="https://kite.trade/docs/connect/v3/" target="_blank" rel="noreferrer"
              style={{color:"#4a9eff"}}>Kite Connect docs →</a>
          </div>
        </div>
      )}
    </div>
  );
}

function SettingsModal({onClose,tgToken,setTgToken,tgChat,setTgChat,pushEnabled,setPushEnabled,uid="",tgMinSignals=1,setTgMinSignals=()=>{},tgCooldownMins=60,setTgCooldownMins=()=>{},onBrokerChange=null,userRole="admin"}) {
  const [broker, setBroker] = useState(getUserBroker());
  const [testing,   setTesting]   = useState(false);
  const [testResult,setTestResult]= useState(null);
  const [polling,   setPolling]   = useState(false);
  const [showManual,setShowManual]= useState(false); // always start hidden
  const pollRef = useRef(null);

  const connectBot = () => {
    if(!uid) return;
    const botName = (window._RABT_BOT_NAME||"rabscanbot");
    window.open(`https://t.me/${botName}?start=${uid}`, "_blank");
    setPolling(true); setTestResult(null);
    let attempts = 0;
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      attempts++;
      if(attempts > 40) {
        clearInterval(pollRef.current); setPolling(false);
        setTestResult({ok:false, msg:"⏱ Timed out — try manual setup below"});
        setShowManual(true); return;
      }
      try {
        const res = await apiFetch(`/telegram/chatid?uid=${encodeURIComponent(uid)}`);
        if(res.chat_id) {
          clearInterval(pollRef.current); setPolling(false);
          const chatId   = String(res.chat_id);
          const botToken = res.bot_token || "";
          // Update React state (this also persists via useLocalStorage)
          setTgChat(chatId);
          if(botToken) setTgToken(botToken);
          // Also write raw localStorage so it persists even if React state lags
          localStorage.setItem("nse_pro_tg_chat",  JSON.stringify(chatId));
          localStorage.setItem("nse_pro_tg_token",  JSON.stringify(botToken));
          setTestResult({ok:true, msg:`✅ Connected! Alerts active for Chat ID: ${chatId}`});
        }
      } catch {}
    }, 3000);
  };

  useEffect(()=>()=>clearInterval(pollRef.current), []);

  const testTg = async () => {
    setTesting(true); setTestResult(null);
    try {
      // If no local token, use server-side token via uid-based test
      const url = tgToken
        ? `/alert/telegram/test?bot_token=${encodeURIComponent(tgToken)}&chat_id=${encodeURIComponent(tgChat)}`
        : `/alert/telegram/test?chat_id=${encodeURIComponent(tgChat)}&use_server_token=1`;
      const res = await apiFetch(url);
      if(res.ok) setTestResult({ok:true,  msg:"✅ Test message sent to Telegram!"});
      else       setTestResult({ok:false, msg:`❌ ${res.response?.description||"Telegram error"}`});
    } catch(e) { setTestResult({ok:false, msg:`❌ ${e.message}`}); }
    finally { setTesting(false); }
  };

  const enablePush = async () => {
    if(!("Notification" in window)) return;
    const p = await Notification.requestPermission();
    setPushEnabled(p==="granted");
    if(p==="granted") new Notification("RABTRADE",{body:"Notifications enabled!"});
  };

  const isConnected = !!tgChat; // chat ID alone is enough — VPS holds the bot token

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:300,
      display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={onClose}>
      <div style={{background:"#0f1d30",border:"1px solid #1a2c44",borderRadius:12,padding:24,
        width:420,maxWidth:"92vw",maxHeight:"90vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div style={{fontSize:15,fontWeight:700,color:"#e7edf5"}}>🔔 Alert Settings</div>
          <button onClick={onClose} style={{background:"none",border:"none",color:"#8ea2bf",cursor:"pointer",fontSize:18}}>✕</button>
        </div>

        {/* ── TELEGRAM ── */}
        <div style={{marginBottom:20}}>
          <div style={{fontSize:10,color:"#e8b339",fontWeight:700,letterSpacing:2,marginBottom:12}}>TELEGRAM ALERTS</div>

          {/* Connected badge */}
          {isConnected&&(
            <div style={{display:"flex",alignItems:"center",gap:10,background:"rgba(34,197,94,0.08)",
              border:"1px solid rgba(34,197,94,0.3)",borderRadius:0,padding:"10px 14px",marginBottom:12}}>
              <span style={{fontSize:20}}>✅</span>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:700,color:"#2ed47a"}}>Telegram Connected</div>
                <div style={{fontSize:10,color:"#5a728f"}}>Chat ID: {tgChat}</div>
              </div>
              <button onClick={()=>{ setTgToken(""); setTgChat(""); setTestResult(null); setShowManual(false); const _ck=uid?`nse_pro_tg_chat_${uid}`:"nse_pro_tg_chat"; const _tk=uid?`nse_pro_tg_token_${uid}`:"nse_pro_tg_token"; localStorage.removeItem(_ck); localStorage.removeItem(_tk); }}
                style={{background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",
                  borderRadius:0,padding:"3px 10px",color:"#ff5b6c",fontFamily:"inherit",fontSize:10,cursor:"pointer"}}>
                Disconnect
              </button>
            </div>
          )}

          {/* One-click connect card */}
          {!isConnected&&(
            <div style={{background:"#0b1624",borderRadius:0,padding:18,marginBottom:12,textAlign:"center"}}>
              <div style={{fontSize:28,marginBottom:8}}>✈️</div>
              <div style={{fontSize:13,fontWeight:700,color:"#e7edf5",marginBottom:6}}>Connect Telegram</div>
              <div style={{fontSize:11,color:"#8ea2bf",marginBottom:16,lineHeight:1.7}}>
                Just 2 taps — no tokens, no IDs needed.<br/>
                Click below, then tap <strong style={{color:"#e7edf5"}}>START</strong> in Telegram.
              </div>
              <button onClick={connectBot} disabled={polling||!uid}
                style={{width:"100%",background:"linear-gradient(135deg,#1d4ed8,#2563eb)",
                  border:"none",borderRadius:0,padding:"13px 0",color:"#fff",
                  fontFamily:"inherit",fontSize:13,fontWeight:700,cursor:polling?"default":"pointer",
                  display:"flex",alignItems:"center",justifyContent:"center",gap:8,boxSizing:"border-box"}}>
                {polling
                  ? <><span style={{animation:"spin 1s linear infinite",display:"inline-block"}}>⟳</span> Waiting for Telegram...</>
                  : <>Open Telegram &amp; Connect</>}
              </button>
              {polling&&<div style={{fontSize:10,color:"#5a728f",marginTop:8}}>Tap START in the Telegram bot that opened</div>}
              {testResult&&<div style={{fontSize:11,color:testResult.ok?"#2ed47a":"#ff5b6c",marginTop:8}}>{testResult.msg}</div>}
            </div>
          )}

          {/* Test when connected */}
          {isConnected&&(
            <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:10}}>
              <button onClick={testTg} disabled={testing}
                style={{background:"rgba(232,179,57,0.12)",border:"1px solid rgba(232,179,57,0.35)",
                  borderRadius:0,padding:"7px 16px",color:"#49c2e4",fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer"}}>
                {testing?"Sending...":"📨 Send Test"}
              </button>
              {testResult&&<span style={{fontSize:11,color:testResult.ok?"#2ed47a":"#ff5b6c"}}>{testResult.msg}</span>}
            </div>
          )}

          {/* Min signals setting */}
          {isConnected&&(
            <div style={{background:"#0b1624",border:"1px solid #1a2c44",borderRadius:0,
              padding:"10px 14px",marginBottom:10}}>
              <div style={{fontSize:11,color:"#e7edf5",fontWeight:600,marginBottom:8}}>
                🔔 Alert Filters
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                <span style={{fontSize:11,color:"#8ea2bf",flex:1}}>Min signals to alert</span>
                <div style={{display:"flex",gap:3}}>
                  {[1,2,3,4].map(n=>(
                    <button key={n} onClick={()=>setTgMinSignals(n)}
                      style={{background:tgMinSignals===n?"rgba(232,179,57,0.2)":"transparent",
                        border:`1px solid ${tgMinSignals===n?"#e8b339":"#23405f"}`,
                        borderRadius:0,padding:"4px 10px",color:tgMinSignals===n?"#49c2e4":"#5a728f",
                        fontFamily:"inherit",fontSize:11,fontWeight:tgMinSignals===n?700:400,cursor:"pointer"}}>
                      {n}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                <span style={{fontSize:11,color:"#8ea2bf",flex:1}}>Cooldown (same signal)</span>
                <div style={{display:"flex",gap:3}}>
                  {[[15,"15m"],[30,"30m"],[60,"1H"],[120,"2H"],[240,"4H"]].map(([mins,lbl])=>(
                    <button key={mins} onClick={()=>setTgCooldownMins(mins)}
                      style={{background:tgCooldownMins===mins?"rgba(34,197,94,0.2)":"transparent",
                        border:`1px solid ${tgCooldownMins===mins?"#2ed47a":"#23405f"}`,
                        borderRadius:0,padding:"4px 8px",color:tgCooldownMins===mins?"#2ed47a":"#5a728f",
                        fontFamily:"inherit",fontSize:11,fontWeight:tgCooldownMins===mins?700:400,cursor:"pointer"}}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{fontSize:10,color:"#5a728f",lineHeight:1.6}}>
                {tgMinSignals===1?"Any 1 indicator fires alert (noisy — use 2+)":tgMinSignals===2?"2+ indicators must agree (e.g. TSI + MACD bullish)":tgMinSignals===3?"3+ indicators confluent — high-quality setups":tgMinSignals===4?"4+ indicators aligned — very strong only":""}{" · "}
                Same signal won't repeat for {tgCooldownMins>=60?`${tgCooldownMins/60}hr`:`${tgCooldownMins}min`}
              </div>
              <div style={{marginTop:8,fontSize:10,color:"#5a728f",lineHeight:1.8,
                borderTop:"1px solid #1a2c44",paddingTop:8}}>
                Also alerts on:<br/>
                ✅ <span style={{color:"#2ed47a"}}>TP Hit</span> — target reached, order placed<br/>
                🛑 <span style={{color:"#ff5b6c"}}>SL Hit</span> — stop loss triggered<br/>
                ⚡ <span style={{color:"#49c2e4"}}>Auto-trade</span> — AT engine placed an order
              </div>
            </div>
          )}

          {/* Manual toggle */}
          <button onClick={()=>setShowManual(p=>!p)}
            style={{background:"none",border:"none",color:"#5a728f",fontSize:10,
              cursor:"pointer",textDecoration:"underline",padding:0}}>
            {showManual?"▲ Hide":"▼"} Manual setup (own bot token)
          </button>
          {showManual&&(
            <div style={{marginTop:8,display:"flex",flexDirection:"column",gap:6}}>
              <input value={tgToken} onChange={e=>setTgToken(e.target.value)}
                placeholder="Bot Token (from @BotFather)"
                style={{width:"100%",background:"#0b1624",border:"1px solid #1a2c44",borderRadius:0,
                  padding:"7px 10px",color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none",boxSizing:"border-box"}}/>
              <input value={tgChat} onChange={e=>setTgChat(e.target.value)}
                placeholder="Chat ID (from @userinfobot)"
                style={{width:"100%",background:"#0b1624",border:"1px solid #1a2c44",borderRadius:0,
                  padding:"7px 10px",color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none",boxSizing:"border-box"}}/>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <button onClick={testTg} disabled={testing||!tgToken||!tgChat}
                  style={{background:"rgba(232,179,57,0.12)",border:"1px solid rgba(232,179,57,0.35)",
                    borderRadius:0,padding:"6px 14px",color:"#49c2e4",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>
                  {testing?"Sending...":"📨 Test"}
                </button>
                {testResult&&<span style={{fontSize:11,color:testResult.ok?"#2ed47a":"#ff5b6c"}}>{testResult.msg}</span>}
              </div>
            </div>
          )}
        </div>

        {/* ── BROKER SELECTOR ── */}
        <div style={{marginBottom:16}}>
          <div style={{fontSize:10,color:"#5a728f",fontWeight:700,letterSpacing:2,marginBottom:10}}>
            🏦 YOUR BROKER — for Open in Broker button
          </div>
          <BrokerSelector value={broker} onChange={b=>{setBroker(b);if(onBrokerChange)onBrokerChange(b);}} compact={false}/>
          <div style={{fontSize:9,color:"#5a728f",marginTop:6,lineHeight:1.6}}>
            Tap <span style={{color:"#ff6b35"}}>🟠 Open in Broker</span> on any signal card — opens your broker's web with order pre-filled. No API needed.
            Zerodha users get the best deeplink experience. Other brokers open their homepage.
          </div>
        </div>

        {/* ── ZERODHA API SETUP ── */}
        {broker === "zerodha" && <ZerodhaApiSetup uid={uid} userRole={userRole} />}

        {/* ── BROWSER PUSH ── */}
        <div>
          <div style={{fontSize:10,color:"#5a728f",fontWeight:700,letterSpacing:2,marginBottom:10}}>BROWSER PUSH</div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
            background:"#0b1624",padding:14,borderRadius:0}}>
            <div>
              <div style={{fontSize:12,color:"#e7edf5",marginBottom:2}}>Desktop Notifications</div>
              <div style={{fontSize:10,color:"#8ea2bf"}}>Popup alert when signals fire</div>
            </div>
            {pushEnabled
              ? <span style={{color:"#2ed47a",fontSize:12,fontWeight:700}}>✓ ON</span>
              : <button onClick={enablePush}
                  style={{background:"rgba(96,165,250,0.1)",border:"1px solid #23405f",
                    borderRadius:0,padding:"7px 14px",color:"#8ea2bf",fontFamily:"inherit",
                    fontSize:11,fontWeight:600,cursor:"pointer"}}>ENABLE</button>
            }
          </div>
        </div>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// ROOT APP
// ══════════════════════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════════════════════
// LOGIN SCREEN
// ══════════════════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════════════════
// TERMS & CONDITIONS PAGE
// Shown once on first login for all non-admin users (viewer / trader)
// Stored in Firestore: fbRecord.termsAccepted = true
// ══════════════════════════════════════════════════════════════════════════════
function TermsPage({ fbUser, fbRecord, onAccept, onLogout }) {
  const [scrolled,  setScrolled]  = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [checked,   setChecked]   = useState(false);
  const scrollRef = useRef(null);

  const role = fbRecord?.role || "viewer";

  const handleScroll = (e) => {
    const el = e.target;
    const atBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 40;
    if(atBottom) setScrolled(true);
  };

  const handleAccept = async () => {
    if(!checked || !scrolled) return;
    setAccepting(true);
    try { await onAccept(); }
    finally { setAccepting(false); }
  };

  return (
    <div style={{minHeight:"100vh",background:"#07101c",display:"flex",
      alignItems:"center",justifyContent:"center",
      fontFamily:"'JetBrains Mono',monospace",padding:16}}>
      <div style={{width:"min(560px,100%)",background:"#0b1624",
        border:"1px solid #1a2c44",borderRadius:12,
        boxShadow:"0 8px 32px rgba(0,0,0,0.6)",
        display:"flex",flexDirection:"column",maxHeight:"94vh"}}>

        {/* Header */}
        <div style={{padding:"24px 28px 16px",borderBottom:"1px solid #1a2c44",flexShrink:0}}>
          <div style={{fontSize:18,fontWeight:700,color:"#e7edf5",marginBottom:4}}>
            📋 Terms &amp; Conditions
          </div>
          <div style={{fontSize:10,color:"#8ea2bf"}}>
            Please read and accept before continuing · Version 1.0
          </div>
          <div style={{marginTop:10,display:"flex",gap:6}}>
            <span style={{fontSize:12,padding:"2px 8px",borderRadius:0,fontWeight:700,
              background:role==="trader"?"rgba(96,165,250,0.15)":"rgba(139,148,158,0.1)",
              color:role==="trader"?"#6b7280":"#8ea2bf",
              border:`1px solid ${role==="trader"?"rgba(96,165,250,0.3)":"#23405f"}`}}>
              {role==="trader"?"📈 Trader Account":"👁 Viewer Account"}
            </span>
            <span style={{fontSize:12,padding:"2px 8px",borderRadius:0,
              background:"rgba(232,179,57,0.06)",color:"#5a728f",border:"1px solid #1a2c44"}}>
              {fbUser.email}
            </span>
          </div>
        </div>

        {/* Scrollable T&C content */}
        <div ref={scrollRef} onScroll={handleScroll}
          style={{flex:1,overflowY:"auto",padding:"20px 28px",
            fontSize:11,color:"#5c3d2e",lineHeight:1.9,WebkitOverflowScrolling:"touch"}}>

          <Section title="1. About This Platform">
            RAB Trade ("Platform") is a signal analysis and order management tool for Indian financial markets.
            It is operated by the platform owner ("We", "Us") for use by registered users.
            By using this Platform you agree to these terms in full.
          </Section>

          <Section title="2. Nature of Service">
            <b style={{color:"#e7edf5"}}>Signals are not financial advice.</b> All RSI, EMA, TSI,
            volume and AI-generated signals are technical analysis tools only.
            They do not constitute investment advice, recommendations, or guarantees of profit.
            You must make your own independent trading decisions.
          </Section>

          <Section title="3. Your Role &amp; Access">
            {role==="trader"?(
              <>
                <b style={{color:"#6b7280"}}>Trader Account:</b> You may view signals and place
                manual orders using your own Zerodha Kite Connect API credentials.
                Auto-trade (algorithmic execution without your manual confirmation) is not available
                on your account. Each order you place requires your deliberate action.
              </>
            ):(
              <>
                <b style={{color:"#8ea2bf"}}>Viewer Account:</b> You receive TA scan signals and alerts via Telegram.
                Signals are generated from our shared market data feed — you do not need a broker API.
                You cannot place trades through this Platform. This is a signal analytics subscription.
                Use the 🟠 "Open in Broker" button to manually execute signals in your own broker app.
              </>
            )}
          </Section>

          <Section title="4. Your Broker API &amp; IP Whitelisting">
            If you connect your Kite Connect API, you must:
            <ul style={{paddingLeft:16,marginTop:6,marginBottom:0}}>
              <li>Have a valid Kite Connect developer subscription (₹2,000/month from Zerodha)</li>
              <li>Whitelist our server IP <code style={{color:"#e8b339",background:"rgba(0,212,170,0.08)",padding:"1px 5px",borderRadius:0}}>165.22.209.25</code> on your Kite Connect dashboard</li>
              <li>Keep your API keys confidential and not share them with anyone</li>
              <li>Ensure compliance with Zerodha's own Terms of Service</li>
            </ul>
            Orders placed through this Platform are executed using your own API credentials on your own trading account.
            We never have access to your funds.
          </Section>

          <Section title="5. Risk Disclosure">
            <b style={{color:"#ff5b6c"}}>Trading in equity, F&amp;O, MCX and crypto derivatives
            involves substantial risk of loss.</b> You may lose more than your initial investment.
            Past performance of any signal, strategy or AI recommendation does not guarantee future results.
            Only trade with capital you can afford to lose entirely.
          </Section>

          <Section title="6. SEBI Algo Trading Compliance (April 2026)">
            Under SEBI's April 2026 algo trading framework:
            <ul style={{paddingLeft:16,marginTop:6,marginBottom:0}}>
              <li><b style={{color:"#e7edf5"}}>Manual trades</b> (you click the button) are fully compliant — no registration required</li>
              <li><b style={{color:"#e7edf5"}}>Auto-trade</b> is available to Admin only and operates below SEBI's 10 orders/second threshold</li>
              <li>Each user's orders are placed via their own Zerodha API — we never trade on your behalf</li>
              <li>This Platform operates as a <b style={{color:"#e7edf5"}}>White Box</b> tool — all signal logic (TSI, EMA, RSI) is fully visible and transparent to the user</li>
              <li>Static IP whitelisting is required: whitelist <code style={{color:"#e8b339",background:"rgba(232,179,57,0.08)",padding:"1px 5px",borderRadius:0}}>165.22.209.25</code> on your Kite Connect developer dashboard</li>
            </ul>
            You are solely responsible for ensuring your own API usage complies with your broker's and SEBI's current regulations.
          </Section>

          <Section title="7. No Liability">
            We are not liable for any trading losses, missed opportunities, technical failures,
            API outages, data inaccuracies, or any other damages arising from your use of the Platform.
            The Platform is provided "as is" without warranty of any kind.
            Your use of this Platform is entirely at your own risk.
          </Section>

          <Section title="8. Data &amp; Privacy">
            We store your email, role, and subscription status in Firebase.
            We do not store your trading history, order data, or broker credentials on our servers
            beyond the active session. Your API keys are held in memory only and cleared on logout.
          </Section>

          <Section title="9. Subscription &amp; Cancellation">
            Access is granted for the subscription period paid. No refunds are provided for unused periods.
            We reserve the right to suspend or terminate your access for violation of these terms,
            misuse of the Platform, or any breach of applicable law.
          </Section>

          <Section title="10. Governing Law">
            These terms are governed by the laws of India. Any disputes shall be subject to
            the exclusive jurisdiction of courts in India.
          </Section>

          <div style={{marginTop:20,padding:"12px 14px",background:"rgba(0,212,170,0.06)",
            border:"1px solid rgba(0,212,170,0.2)",borderRadius:0,fontSize:10,color:"#8ea2bf"}}>
            By clicking "I Accept", you confirm that you have read, understood, and agree to
            all the above terms. This acceptance is recorded with a timestamp and cannot be undone.
          </div>

          {/* Scroll indicator */}
          {!scrolled&&(
            <div style={{textAlign:"center",marginTop:16,color:"#5a728f",fontSize:10,animation:"pulse 2s infinite"}}>
              ↓ Scroll to bottom to continue
              <style>{"@keyframes pulse{0%,100%{opacity:0.5}50%{opacity:1}}"}</style>
            </div>
          )}
        </div>

        {/* Accept footer */}
        <div style={{padding:"16px 28px 24px",borderTop:"1px solid #1a2c44",flexShrink:0}}>
          {/* Checkbox */}
          <div onClick={()=>setChecked(p=>!p)}
            style={{display:"flex",alignItems:"flex-start",gap:10,
              marginBottom:14,cursor:"pointer",userSelect:"none"}}>
            <div style={{width:18,height:18,borderRadius:0,flexShrink:0,marginTop:1,
              background:checked?"#e8b339":"transparent",
              border:`2px solid ${checked?"#e8b339":"#23405f"}`,
              display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.15s"}}>
              {checked&&<span style={{color:"#07101c",fontSize:12,fontWeight:700,lineHeight:1}}>✓</span>}
            </div>
            <span style={{fontSize:11,color:"#5c3d2e",lineHeight:1.6}}>
              I have read and agree to the Terms &amp; Conditions. I understand that signals are
              not financial advice and trading involves risk of loss.
            </span>
          </div>

          {/* Buttons */}
          <div style={{display:"flex",gap:10}}>
            <button onClick={onLogout}
              style={{flex:1,background:"transparent",border:"1px solid #23405f",
                borderRadius:0,padding:"11px",color:"#8ea2bf",
                fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
              Decline &amp; Logout
            </button>
            <button onClick={handleAccept}
              disabled={!checked||!scrolled||accepting}
              style={{flex:2,
                background:checked&&scrolled
                  ?"linear-gradient(135deg,#92400e,#92400e)"
                  :"#1a2c44",
                border:"none",borderRadius:0,padding:"11px",
                color:checked&&scrolled?"#07101c":"#5a728f",
                fontFamily:"inherit",fontSize:12,fontWeight:700,
                cursor:checked&&scrolled&&!accepting?"pointer":"not-allowed",
                transition:"all 0.2s"}}>
              {accepting?"Saving..."
               :!scrolled?"Scroll to read all"
               :!checked?"Check the box above"
               :"✓ I Accept — Continue"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper — T&C section
function Section({title, children}) {
  return (
    <div style={{marginBottom:18}}>
      <div style={{fontSize:11,fontWeight:700,color:"#e7edf5",marginBottom:6,
        borderBottom:"1px solid #1a2c44",paddingBottom:4}}>
        {title}
      </div>
      <div style={{fontSize:11,color:"#8ea2bf",lineHeight:1.9}}>{children}</div>
    </div>
  );
}

function LoginScreen({onLogin}) {
  const [mode,    setMode]    = useState("login"); // login | reset
  const [email,   setEmail]   = useState("");
  const [pass,    setPass]    = useState("");
  const [err,     setErr]     = useState("");
  const [ok,      setOk]      = useState("");
  const [loading, setLoading] = useState(false);

  const inp = {
    width:"100%",background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,
    padding:"11px 12px",color:"#e7edf5",fontFamily:"inherit",fontSize:13,
    outline:"none",boxSizing:"border-box",
  };

  async function handleLogin(e) {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(_auth, email.trim(), pass);
      let rec = await fbGetUser(cred.user.uid);
      if(!rec) {
        // Firebase Auth user exists but no Firestore record yet
        // Admin added them via Firebase Console — auto-create their record
        const expires = new Date(Date.now() + 30*86400000).toISOString().split('T')[0]; // 30 days default
        await setDoc(doc(_db,"users",cred.user.uid),{
          email:       cred.user.email,
          displayName: cred.user.displayName || cred.user.email.split('@')[0],
          role:        "viewer",
          expiresAt:   expires,
          plan:        "monthly",
          createdAt:   serverTimestamp(),
          active:      true,
          autoCreated: true, // flag so admin knows to review
        });
        rec = await fbGetUser(cred.user.uid);
      }
      if(rec?.active===false) {
        await signOut(_auth);
        setErr("Account deactivated. Contact your administrator.");
        return;
      }
      onLogin(cred.user, rec);
    } catch(e) {
      const msg =
        e.code==="auth/invalid-credential"||e.code==="auth/wrong-password"
          ? "Invalid email or password"
        : e.code==="auth/too-many-requests"
          ? "Too many attempts. Try again later."
        : e.code==="auth/user-not-found"
          ? "No account found. Contact your administrator."
        : e.message;
      setErr(msg);
    } finally { setLoading(false); }
  }

  async function handleReset(e) {
    e.preventDefault();
    if(!email) { setErr("Enter your email first"); return; }
    setLoading(true);
    try {
      await sendPasswordResetEmail(_auth, email.trim());
      setOk("✓ Password reset email sent. Check your inbox.");
      setErr("");
    } catch(e) {
      setErr(e.code==="auth/user-not-found" ? "No account with this email" : e.message);
    } finally { setLoading(false); }
  }

  return (
    <div style={{minHeight:"100vh",background:"#07101c",display:"flex",alignItems:"center",
      justifyContent:"center",fontFamily:"'JetBrains Mono',monospace",padding:16}}>
      <div style={{width:"min(380px,100%)",background:"#0b1624",border:"1px solid #1a2c44",
        borderRadius:12,padding:"32px 28px",boxShadow:"0 8px 32px rgba(0,0,0,0.6)"}}>

        <div style={{textAlign:"center",marginBottom:28}}>
          <div style={{fontSize:32,marginBottom:8}}>📈</div>
          <div style={{fontSize:16,fontWeight:700,color:"#e7edf5",letterSpacing:2}}>RABTRADE</div>
          <div style={{fontSize:10,color:"#8ea2bf",letterSpacing:1,marginTop:4}}>
            {mode==="login"?"INSTITUTIONAL TRADING PLATFORM":"PASSWORD RECOVERY"}
          </div>
        </div>

        {err&&<div style={{fontSize:11,color:"#ff5b6c",marginBottom:16,padding:"9px 12px",
          background:"rgba(248,81,73,0.1)",borderRadius:0,borderLeft:"3px solid #f85149"}}>{err}</div>}
        {ok&&<div style={{fontSize:11,color:"#2ed47a",marginBottom:16,padding:"9px 12px",
          background:"rgba(63,185,80,0.1)",borderRadius:0,borderLeft:"3px solid #3fb950"}}>{ok}</div>}

        {mode==="login"&&(
          <form onSubmit={handleLogin}>
            <div style={{marginBottom:16}}>
              <div style={{fontSize:12,color:"#8ea2bf",marginBottom:6,letterSpacing:1}}>EMAIL</div>
              <input type="email" value={email} onChange={e=>setEmail(e.target.value)}
                required placeholder="you@example.com" autoComplete="email" style={inp}/>
            </div>
            <div style={{marginBottom:24}}>
              <div style={{fontSize:12,color:"#8ea2bf",marginBottom:6,letterSpacing:1}}>PASSWORD</div>
              <input type="password" value={pass} onChange={e=>setPass(e.target.value)}
                required placeholder="••••••••" autoComplete="current-password" style={inp}/>
            </div>
            <button type="submit" disabled={loading}
              style={{width:"100%",background:loading?"#1a2c44":"linear-gradient(135deg,#92400e,#92400e)",
                border:"none",borderRadius:0,padding:"13px",color:loading?"#8ea2bf":"#07101c",
                fontFamily:"inherit",fontSize:13,fontWeight:700,
                cursor:loading?"not-allowed":"pointer",letterSpacing:1}}>
              {loading?"⟳ Signing in...":"LOGIN"}
            </button>
            <div style={{textAlign:"center",marginTop:16}}>
              <button type="button" onClick={()=>{setMode("reset");setErr("");setOk("");}}
                style={{background:"none",border:"none",color:"#8ea2bf",fontSize:11,
                  cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>
                Forgot password?
              </button>
            </div>
          </form>
        )}

        {mode==="reset"&&(
          <form onSubmit={handleReset}>
            <div style={{fontSize:12,color:"#8ea2bf",marginBottom:20,lineHeight:1.7}}>
              Enter your email and we'll send a reset link.
            </div>
            <div style={{marginBottom:24}}>
              <div style={{fontSize:12,color:"#8ea2bf",marginBottom:6,letterSpacing:1}}>EMAIL</div>
              <input type="email" value={email} onChange={e=>setEmail(e.target.value)}
                required placeholder="you@example.com" autoComplete="email" style={inp}/>
            </div>
            <button type="submit" disabled={loading}
              style={{width:"100%",background:loading?"#1a2c44":"rgba(96,165,250,0.15)",
                border:"1px solid rgba(96,165,250,0.4)",borderRadius:0,padding:"13px",
                color:loading?"#8ea2bf":"#6b7280",fontFamily:"inherit",fontSize:13,
                fontWeight:700,cursor:loading?"not-allowed":"pointer"}}>
              {loading?"⟳ Sending...":"SEND RESET EMAIL"}
            </button>
            <div style={{textAlign:"center",marginTop:16}}>
              <button type="button" onClick={()=>{setMode("login");setErr("");setOk("");}}
                style={{background:"none",border:"none",color:"#8ea2bf",fontSize:11,
                  cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>
                ← Back to login
              </button>
            </div>
          </form>
        )}

        <div style={{textAlign:"center",marginTop:24,paddingTop:16,
          borderTop:"1px solid #1a2c44",fontSize:10,color:"#5a728f",lineHeight:1.7}}>
          Access is by invitation only.<br/>
          Contact your administrator to get an account.
        </div>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// API KEY SETUP SCREEN
// ══════════════════════════════════════════════════════════════════════════════

// ── Broker Selector Component ─────────────────────────────────────────────────
function BrokerSelector({ value, onChange, compact=false }) {
  const brokers = Object.entries(BROKER_CONFIGS);
  const brokerDesc = {
    zerodha:  "1-click API · ₹2K/mo",
    groww:    "Manual — opens Groww order page",
    upstox:   "Manual — opens order page",
    angelone: "Manual — opens stock page",
    fyers:    "Manual — opens trade page",
    dhan:     "Manual — opens homepage",
    kotak:    "1-click API trading · FREE",
    hdfcsky:  "Manual — opens HDFC SKY",
    hdfcsec:  "Manual — opens InvestRight",
  };
  if(compact) return (
    <div style={{display:"flex",gap:3,flexWrap:"wrap"}}>
      {brokers.map(([key,b])=>(
        <button key={key} onClick={()=>{ setUserBroker(key); onChange(key); }}
          style={{background:value===key?`${b.color}22`:"transparent",
            border:`1px solid ${value===key?b.color:"#23405f"}`,
            borderRadius:0,padding:"3px 8px",
            color:value===key?b.color:"#5a728f",
            fontFamily:"inherit",fontSize:10,cursor:"pointer",
            fontWeight:value===key?700:400}}>
          {b.logo} {b.name}
        </button>
      ))}
    </div>
  );
  return (
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
      {brokers.map(([key,b])=>(
        <button key={key} onClick={()=>{ setUserBroker(key); onChange(key); }}
          style={{background:value===key?`${b.color}18`:"#0b1624",
            border:`1.5px solid ${value===key?b.color:"#1a2c44"}`,
            borderRadius:0,padding:"10px 12px",
            display:"flex",alignItems:"center",gap:8,
            cursor:"pointer",textAlign:"left",
            transition:"all 0.15s"}}>
          <span style={{fontSize:20}}>{b.logo}</span>
          <div>
            <div style={{fontSize:11,fontWeight:700,color:value===key?b.color:"#e7edf5"}}>{b.name}</div>
            <div style={{fontSize:9,color:"#5a728f"}}>{brokerDesc[key]||"Opens broker web"}</div>
          </div>
          {value===key&&<span style={{marginLeft:"auto",fontSize:14,color:b.color}}>✓</span>}
        </button>
      ))}
    </div>
  );
}

function ApiKeySetup({firebaseUser, userRecord, onComplete, onLogout, isAdmin=false, onBrokerChange=null, onSkipAsViewer=null}) {
  const [kiteKey,      setKiteKey]      = useState(()=>sessionStorage.getItem("kite_key")||"");
  const [kiteSec,      setKiteSec]      = useState(()=>sessionStorage.getItem("kite_secret")||"");
  const [deltaKey,     setDeltaKey]     = useState(()=>sessionStorage.getItem("delta_key")||"");
  const [deltaSec,     setDeltaSec]     = useState(()=>sessionStorage.getItem("delta_secret")||"");
  const [platform,     setPlatform]     = useState(()=>sessionStorage.getItem("delta_platform")||"india");
  const [loading,      setLoading]      = useState(false);
  const [err,          setErr]          = useState("");
  const [step,     setStep]     = useState("form"); // form | kite-auth | done
  const [broker,   setBroker]   = useState(()=>getUserBroker()); // selected broker
  const days = daysRemaining(userRecord);

  // Broker API info — what each broker supports
  const BROKER_API_INFO = {
    zerodha:  { api: true,  apiUrl:"https://developers.kite.trade", apiName:"Kite Connect", cost:"₹2,000/mo", note:"Full API — VPS places orders automatically" },
    groww:    { api: false, apiUrl:"https://groww.in/trade",        apiName:"Groww Web",    cost:"Free",       note:"No public API — deeplink opens Groww order page" },
    hdfcsky:  { api: false, apiUrl:"https://developer.hdfcsky.com",  apiName:"HDFC SKY",     cost:"Free",       note:"No API login yet — manual/deeplink order mode. Portfolio & auto-trade need Zerodha or Kotak." },
    hdfcsec:  { api: false, apiUrl:"https://developer.hdfcsec.com",  apiName:"HDFC Securities",  cost:"Contact",    note:"No API login yet — manual/deeplink order mode. Portfolio & auto-trade need Zerodha or Kotak." },
    upstox:   { api: true,  apiUrl:"https://upstox.com/developer/api-documentation", apiName:"Upstox API", cost:"Free", note:"API available — VPS integration coming soon" },
    angelone: { api: true,  apiUrl:"https://smartapi.angelbroking.com", apiName:"SmartAPI", cost:"Free",       note:"API available — VPS integration coming soon" },
    fyers:    { api: true,  apiUrl:"https://myapi.fyers.in",          apiName:"Fyers API",        cost:"Free",       note:"API available — VPS integration coming soon" },
    dhan:     { api: true,  apiUrl:"https://dhanhq.co/docs",          apiName:"DhanHQ API",       cost:"Free",       note:"API available — VPS integration coming soon" },
    kotak:    { api: true,  apiUrl:"https://kotaksecurities.com/api", apiName:"Kotak Neo API",    cost:"Contact",    note:"API available — VPS integration coming soon" },
  };
  const brokerInfo = BROKER_API_INFO[broker] || BROKER_API_INFO.zerodha;
  const isZerodha = broker === "zerodha";
  const isKotak   = broker === "kotak";
  // Zerodha: any user with their own Kite Connect API key can use it
  //   (they whitelist server IP 165.22.209.25 in their own Kite app)
  // Kotak Neo: free API, no IP restriction
  // Users without API keys → viewer/overlay mode (skip option available)
  const hasApiIntegration = isZerodha || isKotak;

  // Kotak Neo state
  const [kotakKey,    setKotakKey]    = useState(()=>sessionStorage.getItem("kotak_consumer_key")||"");
  const [kotakSec,    setKotakSec]    = useState(()=>sessionStorage.getItem("kotak_consumer_secret")||"");
  const [kotakMobile, setKotakMobile] = useState(()=>sessionStorage.getItem("kotak_mobile")||"");
  const [kotakPass,   setKotakPass]   = useState("");
  const [kotakOtp,    setKotakOtp]    = useState("");
  const [kotakStep,   setKotakStep]   = useState("form"); // form | otp | done

  // Proxy state for non-admin Zerodha traders (SOCKS5 proxy through their static IP)
  const [kiteProxyHost, setKiteProxyHost] = useState(()=>sessionStorage.getItem("kite_proxy_host")||"");
  const [kiteProxyPort, setKiteProxyPort] = useState(()=>sessionStorage.getItem("kite_proxy_port")||"1080");
  const [kiteProxyUser, setKiteProxyUser] = useState(()=>sessionStorage.getItem("kite_proxy_user")||"");
  const [kiteProxyPass, setKiteProxyPass] = useState("");

  // ── Check if we just returned from Kite OAuth ─────────────────────────────
  useEffect(()=>{
    const returnUid = sessionStorage.getItem("kite_login_uid");
    if(!returnUid || returnUid !== firebaseUser.uid) return;
    // We returned from Kite — check auth status
    sessionStorage.removeItem("kite_login_uid");
    sessionStorage.removeItem("kite_login_return");
    setStep("kite-auth");
    setLoading(true);
    let count = 0;
    const poll = setInterval(async()=>{
      count++;
      try {
        const res = await fetch(`${API_BASE}/auth/status`, {
          headers: await _authHeadersFor(firebaseUser.uid)
        });
        const data = await res.json();
        if(data.authenticated) {
          clearInterval(poll);
          setLoading(false);
          onComplete();
          return;
        }
      } catch {}
      if(count > 10) { clearInterval(poll); setLoading(false); }
    }, 1000);
    return ()=>clearInterval(poll);
  },[]);

  // ── File upload handler ───────────────────────────────────────────────────
  function handleFileUpload(e) {
    const file = e.target.files[0];
    if(!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if(data.kite_api_key)    setKiteKey(data.kite_api_key);
        if(data.kite_api_secret) setKiteSec(data.kite_api_secret);
        if(data.delta_api_key)   setDeltaKey(data.delta_api_key);
        if(data.delta_api_secret) setDeltaSec(data.delta_api_secret);
        if(data.delta_platform)  setPlatform(data.delta_platform);
        setErr("");
      } catch {
        setErr("Invalid file format. Please use a valid JSON file.");
      }
    };
    reader.readAsText(file);
  }

  async function handleSave(e) {
    e.preventDefault();
    // Brokers without API integration → deeplink/overlay mode
    if(!hasApiIntegration) {
      setUserBroker(broker);
      sessionStorage.setItem("broker_mode", broker);
      onComplete();
      return;
    }

    // ── Zerodha Kite Connect ──
    if(isZerodha) {
      if(!kiteKey||!kiteSec) { setErr("Kite API key and secret are required."); return; }
      setErr(""); setLoading(true);
      try {
        sessionStorage.setItem("kite_key", kiteKey);
        sessionStorage.setItem("kite_secret", kiteSec);
        sessionStorage.setItem("delta_key", deltaKey);
        sessionStorage.setItem("delta_secret", deltaSec);
        sessionStorage.setItem("delta_platform", platform);

        const uid = firebaseUser.uid;
        const kRes = await fetch(`${API_BASE}/auth/register-keys`, {
          method:"POST", headers:{"Content-Type":"application/json"},
          body: JSON.stringify({uid, api_key:kiteKey, api_secret:kiteSec})
        });

        const kData = await kRes.json();

        // Save proxy creds for non-admin traders
        if(!isAdmin && kiteProxyHost) {
          sessionStorage.setItem("kite_proxy_host", kiteProxyHost);
          sessionStorage.setItem("kite_proxy_port", kiteProxyPort||"1080");
          sessionStorage.setItem("kite_proxy_user", kiteProxyUser||"");
          try {
            await fetch(`${API_BASE}/user/kite-creds`, {
              method:"PUT", headers: await _authHeadersFor(uid, {"Content-Type":"application/json"}),
              body: JSON.stringify({
                proxy_host: kiteProxyHost,
                proxy_port: kiteProxyPort||"1080",
                proxy_user: kiteProxyUser||"",
                proxy_pass: kiteProxyPass||"",
              })
            });
          } catch(e) { console.warn("Proxy creds save failed:", e.message); }
        }

        if(deltaKey && deltaSec) {
          await fetch(`${DELTA_API_BASE}/delta/auth/register-keys`, {
            method:"POST", headers:{"Content-Type":"application/json"},
            body: JSON.stringify({uid, api_key:deltaKey, api_secret:deltaSec, platform})
          });
        }

        if(kData.status === "restored") { onComplete(); }
        else { setStep("kite-auth"); }
      } catch(e) {
        setErr("Backend not reachable. Check server connection.");
      } finally { setLoading(false); }
      return;
    }

    // ── Kotak Neo ──
    if(isKotak) {
      if(!kotakKey||!kotakSec) { setErr("Kotak Consumer Key and Secret are required."); return; }
      if(!kotakMobile) { setErr("Mobile number is required for Kotak login."); return; }
      if(!kotakPass) { setErr("Password/MPIN is required."); return; }
      setErr(""); setLoading(true);
      try {
        sessionStorage.setItem("kotak_consumer_key", kotakKey);
        sessionStorage.setItem("kotak_consumer_secret", kotakSec);
        sessionStorage.setItem("kotak_mobile", kotakMobile);

        const uid = firebaseUser.uid;
        // Register keys + initiate login (sends OTP)
        const kRes = await fetch(`${API_BASE}/kotak/auth/login`, {
          method:"POST", headers:{"Content-Type":"application/json"},
          body: JSON.stringify({uid, consumer_key:kotakKey, consumer_secret:kotakSec, mobile:kotakMobile, password:kotakPass})
        });
        const kData = await kRes.json();

        if(kData.status === "authenticated") {
          // MPIN login succeeded without OTP
          onComplete();
        } else if(kData.status === "otp_required") {
          setKotakStep("otp");
        } else {
          setErr(kData.detail || kData.message || "Kotak login failed");
        }
      } catch(e) {
        setErr("Backend not reachable. Check server connection.");
      } finally { setLoading(false); }
      return;
    }
  }

  // ── Kotak OTP verification ──
  async function handleKotakOtp() {
    if(!kotakOtp) { setErr("Enter the OTP sent to your mobile."); return; }
    setErr(""); setLoading(true);
    try {
      const uid = firebaseUser.uid;
      const res = await fetch(`${API_BASE}/kotak/auth/2fa`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({uid, otp:kotakOtp})
      });
      const data = await res.json();
      if(data.status === "authenticated") {
        onComplete();
      } else {
        setErr(data.detail || data.message || "OTP verification failed");
      }
    } catch(e) {
      setErr("Backend not reachable.");
    } finally { setLoading(false); }
  }

  function handleKiteLogin() {
    const uid = firebaseUser.uid;
    const loginUrl = `${API_BASE}/login?uid=${uid}`;
    // Try popup first — if blocked by browser, fall back to direct navigation
    const w = window.open(loginUrl, "_blank", "width=800,height=700,scrollbars=yes");
    if(!w || w.closed || typeof w.closed === 'undefined') {
      // Popup was blocked — use direct navigation
      sessionStorage.setItem("kite_login_return", window.location.href);
      sessionStorage.setItem("kite_login_uid", uid);
      window.location.href = loginUrl;
      return;
    }
    // Popup opened — poll for auth completion
    let pollCount = 0;
    const poll = setInterval(async()=>{
      pollCount++;
      try {
        const res = await fetch(`${API_BASE}/auth/status`, {
          headers: await _authHeadersFor(uid)
        });
        const data = await res.json();
        if(data.authenticated) {
          clearInterval(poll);
          onComplete();
          return;
        }
      } catch {}
      if(pollCount > 90) clearInterval(poll);
    }, 2000);
  }

  return (
    <div style={{minHeight:"100vh",background:"#07101c",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'JetBrains Mono',monospace"}}>
      <div style={{width:480,background:"#0b1624",border:"1px solid #1a2c44",borderRadius:12,padding:"28px",boxShadow:"0 8px 32px rgba(0,0,0,0.6)"}}>
        {/* Header */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:"#e7edf5"}}>⚙️ API Key Setup</div>
            <div style={{fontSize:10,color:"#8ea2bf",marginTop:3}}>
              Welcome, {userRecord.displayName||firebaseUser.email}
              {days!==null&&<span style={{color:days<7?"#ff5b6c":days<30?"#e8b339":"#2ed47a",marginLeft:8}}>
                · {days}d remaining
              </span>}
            </div>
          </div>
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            {onSkipAsViewer&&(
              <button onClick={onSkipAsViewer} style={{background:"rgba(73,194,228,0.1)",border:"1px solid #49c2e4",borderRadius:0,
                padding:"4px 10px",color:"#49c2e4",cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:700}}>
                SKIP → VIEW ONLY
              </button>
            )}
            <button onClick={onLogout} style={{background:"none",border:"1px solid #23405f",borderRadius:0,
              padding:"4px 10px",color:"#8ea2bf",cursor:"pointer",fontFamily:"inherit",fontSize:12}}>
              LOGOUT
            </button>
          </div>
        </div>

        {/* ── Broker Selector ── */}
        <div style={{marginBottom:16}}>
          <div style={{fontSize:10,color:"#5a728f",letterSpacing:1,fontWeight:700,marginBottom:8}}>
            SELECT YOUR BROKER
          </div>
          <BrokerSelector value={broker} onChange={b=>{setBroker(b); setUserBroker(b); if(onBrokerChange)onBrokerChange(b);}} compact={false}/>
        </div>

        {/* ── Brokers WITHOUT API integration → deeplink/overlay mode ── */}
        {!hasApiIntegration && (
          <div style={{marginBottom:16}}>
            <div style={{background:"rgba(96,165,250,0.06)",border:"1px solid rgba(96,165,250,0.2)",
              borderRadius:0,padding:"12px 14px"}}>
              <div style={{fontSize:11,fontWeight:700,color:"#49c2e4",marginBottom:6}}>
                {BROKER_CONFIGS[broker]?.logo} {BROKER_CONFIGS[broker]?.name} — Manual Trade Mode
              </div>
              <div style={{fontSize:10,color:"#8ea2bf",lineHeight:1.8,marginBottom:8}}>
                Signals, charts and scan results load automatically from our shared market feed.<br/>
                When you tap <strong style={{color:"#ff6b35"}}>Place Order</strong>, order details copy to clipboard
                and {BROKER_CONFIGS[broker]?.name} opens — confirm manually in your broker.
              </div>
              <div style={{fontSize:9,color:"#5a728f",borderTop:"1px solid #1a2c44",paddingTop:8,marginBottom:10}}>
                ℹ️ {BROKER_CONFIGS[broker]?.name} does not have direct API integration yet.
                For 1-click order placement, switch to <strong style={{color:"#ff6b35"}}>Zerodha</strong> (paid API) or <strong style={{color:"#dc2626"}}>Kotak Neo</strong> (free API).
              </div>
              <button onClick={()=>{ setUserBroker(broker); onComplete(); }}
                style={{width:"100%",background:"rgba(96,165,250,0.15)",
                  border:"1px solid #e8b339",borderRadius:0,padding:"11px",
                  color:"#49c2e4",fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                Continue with {BROKER_CONFIGS[broker]?.name} →
              </button>
            </div>
          </div>
        )}

        {/* ── Kite OAuth step (Zerodha) ── */}
        {step === "kite-auth" && isZerodha ? (
          <div style={{textAlign:"center",padding:"20px 0"}}>
            <div style={{fontSize:28,marginBottom:12}}>{loading?"⟳":"🔑"}</div>
            <div style={{color:"#e7edf5",fontSize:13,fontWeight:700,marginBottom:8}}>
              {loading ? "Checking Kite login..." : "Connect Your Kite Account"}
            </div>
            <div style={{color:"#8ea2bf",fontSize:11,marginBottom:24,lineHeight:1.8}}>
              {loading
                ? "Please wait while we verify your Kite session..."
                : <>Your API keys are saved. Click below to login to Kite.<br/>
                   You'll be redirected to Kite and back automatically.</>
              }
            </div>
            {!loading && (
              <button onClick={handleKiteLogin}
                style={{background:"linear-gradient(135deg,#ff6b35,#f0c040)",border:"none",borderRadius:7,
                  padding:"12px 32px",color:"#000",fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                🔗 LOGIN WITH KITE
              </button>
            )}
            <div style={{marginTop:20}}>
              <button onClick={()=>setStep("form")} style={{background:"none",border:"none",color:"#6b7280",
                fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>
                ← Edit API Keys
              </button>
            </div>
          </div>
        ) : kotakStep === "otp" && isKotak ? (
          /* ── Kotak OTP step ── */
          <div style={{textAlign:"center",padding:"20px 0"}}>
            <div style={{fontSize:28,marginBottom:12}}>{loading?"⟳":"📱"}</div>
            <div style={{color:"#e7edf5",fontSize:13,fontWeight:700,marginBottom:8}}>
              Enter OTP
            </div>
            <div style={{color:"#8ea2bf",fontSize:11,marginBottom:16,lineHeight:1.8}}>
              OTP sent to your registered mobile number.<br/>
              Enter it below to complete Kotak Neo login.
            </div>
            <input value={kotakOtp} onChange={e=>setKotakOtp(e.target.value)} placeholder="Enter OTP"
              maxLength={6} autoFocus
              style={{width:200,background:"#0b1624",border:"1px solid #dc2626",borderRadius:0,padding:"12px",
                color:"#e7edf5",fontFamily:"inherit",fontSize:18,textAlign:"center",letterSpacing:8,outline:"none",
                marginBottom:16}}/>
            <br/>
            {err&&<div style={{fontSize:11,color:"#ff5b6c",marginBottom:12,padding:"8px 10px",background:"rgba(248,81,73,0.1)",borderRadius:0}}>{err}</div>}
            <button onClick={handleKotakOtp} disabled={loading}
              style={{background:"linear-gradient(135deg,#dc2626,#ff4444)",border:"none",borderRadius:7,
                padding:"12px 32px",color:"#fff",fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:loading?"not-allowed":"pointer"}}>
              {loading?"VERIFYING...":"VERIFY OTP"}
            </button>
            <div style={{marginTop:20}}>
              <button onClick={()=>setKotakStep("form")} style={{background:"none",border:"none",color:"#6b7280",
                fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>
                ← Back
              </button>
            </div>
          </div>
        ) : hasApiIntegration ? (
          <form onSubmit={handleSave}>

            {/* ══ ZERODHA KITE FORM ══ */}
            {isZerodha && <>
            <div style={{fontSize:10,color:"#5a728f",letterSpacing:1,fontWeight:700,marginBottom:10}}>
              ZERODHA KITE CONNECT — 1-CLICK TRADING
            </div>

            {/* Cost notice */}
            <div style={{marginBottom:14,padding:"10px 14px",
              background:"rgba(232,179,57,0.08)",border:"1px solid rgba(232,179,57,0.3)",borderRadius:0}}>
              <div style={{fontSize:10,color:"#e8b339",fontWeight:700,marginBottom:4}}>
                💰 Kite Connect API — ₹2,000/month
              </div>
              <div style={{fontSize:10,color:"#8ea2bf",lineHeight:1.8}}>
                This is Zerodha's official trading API. You need your own Kite Connect subscription
                from <a href="https://developers.kite.trade" target="_blank" rel="noreferrer" style={{color:"#49c2e4"}}>developers.kite.trade</a>.
                Once connected, you get <strong style={{color:"#2ed47a"}}>1-click order placement</strong> directly from RAB Signals.
              </div>
            </div>

            {/* IP Whitelist notice — different for admin vs non-admin */}
            {isAdmin ? (
            <div style={{marginBottom:14,padding:"10px 14px",
              background:"rgba(220,38,38,0.08)",border:"1px solid rgba(248,81,73,0.4)",borderRadius:0}}>
              <div style={{fontSize:10,color:"#ff5b6c",fontWeight:700,marginBottom:6}}>
                ⚠️ REQUIRED: Whitelist Server IP
              </div>
              <div style={{fontSize:10,color:"#8ea2bf",lineHeight:1.8}}>
                Add the RAB Trade server IP to your Kite Connect app's whitelist:
              </div>
              <div style={{margin:"8px 0",padding:"6px 10px",background:"rgba(0,0,0,0.3)",borderRadius:0,
                fontFamily:"inherit",fontSize:13,color:"#e8b339",textAlign:"center",letterSpacing:2,userSelect:"all"}}>
                165.22.209.25
              </div>
              <div style={{fontSize:10,color:"#8ea2bf",lineHeight:1.8}}>
                <a href="https://developers.kite.trade" target="_blank" rel="noreferrer"
                  style={{color:"#49c2e4"}}>developers.kite.trade</a> → Login → Your App → IP Whitelisting → Add IP → Save
              </div>
            </div>
            ) : (
            <div style={{marginBottom:14,padding:"10px 14px",
              background:"rgba(232,179,57,0.08)",border:"1px solid rgba(232,179,57,0.3)",borderRadius:0}}>
              <div style={{fontSize:10,color:"#e8b339",fontWeight:700,marginBottom:6}}>
                ⚠️ REQUIRED: Whitelist Your Own Static IP
              </div>
              <div style={{fontSize:10,color:"#8ea2bf",lineHeight:1.8}}>
                Your orders go <strong style={{color:"#e7edf5"}}>directly from your browser → Kite</strong> using your own IP.
                Whitelist your static/virtual IP in your Kite Connect app:<br/>
                <a href="https://developers.kite.trade" target="_blank" rel="noreferrer"
                  style={{color:"#49c2e4"}}>developers.kite.trade</a>
                {" "}→ Login → Your App → IP Whitelisting → add IP → Save.
              </div>
              <div style={{fontSize:9,color:"#5a728f",marginTop:6,lineHeight:1.6}}>
                Use a static/virtual IP (VPN or static IP service) — dynamic IPs change daily and will break your connection.
              </div>
            </div>
            )}

            {/* File upload */}
            <div style={{marginBottom:14,padding:"10px 14px",background:"rgba(96,165,250,0.06)",border:"1px dashed rgba(96,165,250,0.3)",borderRadius:0,textAlign:"center"}}>
              <div style={{fontSize:10,color:"#8ea2bf",marginBottom:8}}>📂 Upload API keys file (JSON)</div>
              <input type="file" accept=".json" onChange={handleFileUpload}
                style={{display:"none"}} id="apiKeyFile"/>
              <label htmlFor="apiKeyFile" style={{cursor:"pointer",background:"rgba(96,165,250,0.15)",
                border:"1px solid rgba(96,165,250,0.4)",borderRadius:0,padding:"6px 16px",
                fontSize:10,color:"#6b7280",fontFamily:"inherit"}}>
                Choose File
              </label>
            </div>

            {/* Kite key/secret + static IP inputs */}
            <div style={{background:"rgba(255,107,53,0.06)",border:"1px solid rgba(255,107,53,0.2)",borderRadius:0,padding:"14px",marginBottom:14}}>
              <div style={{fontSize:10,color:"#e8b339",fontWeight:700,letterSpacing:1,marginBottom:4}}>🟠 KITE API CREDENTIALS</div>
              <div style={{fontSize:9,color:"#5a728f",marginBottom:10,lineHeight:1.6}}>
                Your API key is only used for order placement. Scans and charts use our shared market data feed.
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div>
                  <div style={{fontSize:10,color:"#8ea2bf",marginBottom:4,letterSpacing:1}}>API KEY</div>
                  <input value={kiteKey} onChange={e=>setKiteKey(e.target.value)} placeholder="xxxxxxxx"
                    style={{width:"100%",background:"#0b1624",border:"1px solid #23405f",borderRadius:0,padding:"8px 10px",
                      color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none",boxSizing:"border-box"}}/>
                </div>
                <div>
                  <div style={{fontSize:10,color:"#8ea2bf",marginBottom:4,letterSpacing:1}}>API SECRET</div>
                  <input type="password" value={kiteSec} onChange={e=>setKiteSec(e.target.value)} placeholder="••••••••••••••••"
                    style={{width:"100%",background:"#0b1624",border:"1px solid #23405f",borderRadius:0,padding:"8px 10px",
                      color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none",boxSizing:"border-box"}}/>
                </div>
              </div>
            </div>

            {/* Proxy / Static IP — non-admin traders only */}
            {!isAdmin && (
            <div style={{background:"rgba(74,158,255,0.05)",border:"1px solid rgba(74,158,255,0.2)",borderRadius:0,padding:"14px",marginBottom:14}}>
              <div style={{fontSize:10,color:"#4a9eff",fontWeight:700,letterSpacing:1,marginBottom:4}}>🌐 STATIC IP PROXY</div>
              <div style={{fontSize:9,color:"#5a728f",marginBottom:10,lineHeight:1.6}}>
                Orders route from VPS through your static IP → Kite sees your own IP. Requires SOCKS5 proxy at your static IP.
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 80px",gap:10,marginBottom:10}}>
                <div>
                  <div style={{fontSize:10,color:"#8ea2bf",marginBottom:4,letterSpacing:1}}>YOUR STATIC IP</div>
                  <input value={kiteProxyHost||""} onChange={e=>setKiteProxyHost(e.target.value)} placeholder="103.45.67.89"
                    style={{width:"100%",background:"#0b1624",border:`1px solid ${kiteProxyHost?"#4a9eff":"#23405f"}`,borderRadius:0,padding:"8px 10px",
                      color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none",boxSizing:"border-box"}}/>
                </div>
                <div>
                  <div style={{fontSize:10,color:"#8ea2bf",marginBottom:4,letterSpacing:1}}>PORT</div>
                  <input value={kiteProxyPort||"1080"} onChange={e=>setKiteProxyPort(e.target.value)} placeholder="1080"
                    style={{width:"100%",background:"#0b1624",border:"1px solid #23405f",borderRadius:0,padding:"8px 10px",
                      color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none",boxSizing:"border-box"}}/>
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div>
                  <div style={{fontSize:10,color:"#8ea2bf",marginBottom:4,letterSpacing:1}}>PROXY USERNAME <span style={{color:"#3a5270"}}>(optional)</span></div>
                  <input value={kiteProxyUser||""} onChange={e=>setKiteProxyUser(e.target.value)} placeholder="optional"
                    style={{width:"100%",background:"#0b1624",border:"1px solid #23405f",borderRadius:0,padding:"8px 10px",
                      color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none",boxSizing:"border-box"}} autoComplete="off"/>
                </div>
                <div>
                  <div style={{fontSize:10,color:"#8ea2bf",marginBottom:4,letterSpacing:1}}>PROXY PASSWORD <span style={{color:"#3a5270"}}>(optional)</span></div>
                  <input type="password" value={kiteProxyPass||""} onChange={e=>setKiteProxyPass(e.target.value)} placeholder="optional"
                    style={{width:"100%",background:"#0b1624",border:"1px solid #23405f",borderRadius:0,padding:"8px 10px",
                      color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none",boxSizing:"border-box"}} autoComplete="new-password"/>
                </div>
              </div>
            </div>
            )}

            {/* Delta section (admin only — optional) */}
            {isAdmin && (
            <div style={{background:"rgba(96,165,250,0.04)",border:"1px solid rgba(96,165,250,0.15)",borderRadius:0,padding:"14px",marginBottom:14}}>
              <div style={{fontSize:10,color:"#6b7280",fontWeight:700,letterSpacing:1,marginBottom:12}}>Δ DELTA EXCHANGE (optional)</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                <div>
                  <div style={{fontSize:10,color:"#8ea2bf",marginBottom:4,letterSpacing:1}}>API KEY</div>
                  <input value={deltaKey} onChange={e=>setDeltaKey(e.target.value)} placeholder="optional"
                    style={{width:"100%",background:"#0b1624",border:"1px solid #23405f",borderRadius:0,padding:"8px 10px",
                      color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none",boxSizing:"border-box"}}/>
                </div>
                <div>
                  <div style={{fontSize:10,color:"#8ea2bf",marginBottom:4,letterSpacing:1}}>API SECRET</div>
                  <input type="password" value={deltaSec} onChange={e=>setDeltaSec(e.target.value)} placeholder="optional"
                    style={{width:"100%",background:"#0b1624",border:"1px solid #23405f",borderRadius:0,padding:"8px 10px",
                      color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none",boxSizing:"border-box"}}/>
                </div>
              </div>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <span style={{fontSize:10,color:"#8ea2bf"}}>Platform:</span>
                {["india","global","testnet"].map(p=>(
                  <button key={p} type="button" onClick={()=>setPlatform(p)}
                    style={{padding:"2px 8px",borderRadius:0,border:"1px solid "+(platform===p?"#6b7280":"#1a2c44"),
                      background:platform===p?"rgba(96,165,250,0.15)":"transparent",
                      color:platform===p?"#6b7280":"#8ea2bf",fontFamily:"inherit",fontSize:10,cursor:"pointer"}}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
            )}
            </>}

            {/* ══ KOTAK NEO FORM ══ */}
            {isKotak && <>
            <div style={{fontSize:10,color:"#5a728f",letterSpacing:1,fontWeight:700,marginBottom:10}}>
              KOTAK NEO — FREE 1-CLICK TRADING
            </div>

            {/* Free API notice */}
            <div style={{marginBottom:14,padding:"10px 14px",
              background:"rgba(46,212,122,0.08)",border:"1px solid rgba(46,212,122,0.3)",borderRadius:0}}>
              <div style={{fontSize:10,color:"#2ed47a",fontWeight:700,marginBottom:4}}>
                ✓ FREE API — No Monthly Charges
              </div>
              <div style={{fontSize:10,color:"#8ea2bf",lineHeight:1.8}}>
                Kotak Neo API is completely free. Get your Consumer Key and Secret from{" "}
                <a href="https://napi.kotaksecurities.com/devportal" target="_blank" rel="noreferrer" style={{color:"#49c2e4"}}>
                  napi.kotaksecurities.com/devportal
                </a>. Once connected, you get <strong style={{color:"#2ed47a"}}>1-click order placement</strong> directly from RAB Signals.
              </div>
            </div>

            {/* Kotak credentials */}
            <div style={{background:"rgba(220,38,38,0.06)",border:"1px solid rgba(220,38,38,0.2)",borderRadius:0,padding:"14px",marginBottom:14}}>
              <div style={{fontSize:10,color:"#dc2626",fontWeight:700,letterSpacing:1,marginBottom:10}}>🔴 KOTAK NEO API CREDENTIALS</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                <div>
                  <div style={{fontSize:10,color:"#8ea2bf",marginBottom:4,letterSpacing:1}}>CONSUMER KEY</div>
                  <input value={kotakKey} onChange={e=>setKotakKey(e.target.value)} placeholder="Your consumer key"
                    style={{width:"100%",background:"#0b1624",border:"1px solid #23405f",borderRadius:0,padding:"8px 10px",
                      color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none",boxSizing:"border-box"}}/>
                </div>
                <div>
                  <div style={{fontSize:10,color:"#8ea2bf",marginBottom:4,letterSpacing:1}}>CONSUMER SECRET</div>
                  <input type="password" value={kotakSec} onChange={e=>setKotakSec(e.target.value)} placeholder="••••••••••••••••"
                    style={{width:"100%",background:"#0b1624",border:"1px solid #23405f",borderRadius:0,padding:"8px 10px",
                      color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none",boxSizing:"border-box"}}/>
                </div>
              </div>
              <div style={{marginBottom:10}}>
                <div style={{fontSize:10,color:"#8ea2bf",marginBottom:4,letterSpacing:1}}>MOBILE NUMBER (registered with Kotak)</div>
                <input value={kotakMobile} onChange={e=>setKotakMobile(e.target.value)} placeholder="+91XXXXXXXXXX"
                  style={{width:"100%",background:"#0b1624",border:"1px solid #23405f",borderRadius:0,padding:"8px 10px",
                    color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none",boxSizing:"border-box"}}/>
              </div>
              <div>
                <div style={{fontSize:10,color:"#8ea2bf",marginBottom:4,letterSpacing:1}}>PASSWORD / MPIN</div>
                <input type="password" value={kotakPass} onChange={e=>setKotakPass(e.target.value)} placeholder="Your Kotak Neo password"
                  style={{width:"100%",background:"#0b1624",border:"1px solid #23405f",borderRadius:0,padding:"8px 10px",
                    color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none",boxSizing:"border-box"}}/>
              </div>
            </div>
            </>}

            <div style={{fontSize:10,color:"#8ea2bf",marginBottom:14,padding:"8px 10px",background:"rgba(0,0,0,0.3)",borderRadius:0,lineHeight:1.7}}>
              🔒 Keys stored in browser session only — cleared when you close the tab.
              Never stored on our servers permanently.
            </div>

            {err&&<div style={{fontSize:11,color:"#ff5b6c",marginBottom:12,padding:"8px 10px",background:"rgba(248,81,73,0.1)",borderRadius:0}}>{err}</div>}
            <button type="submit" disabled={loading}
              style={{width:"100%",background:loading?"#1a2c44":isKotak?"linear-gradient(135deg,#dc2626,#ff4444)":"linear-gradient(135deg,#92400e,#e8b339)",
                border:"none",borderRadius:0,padding:"12px",color:loading?"#8ea2bf":"#fff",
                fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:loading?"not-allowed":"pointer"}}>
              {loading?"CONNECTING...":isKotak?"CONNECT KOTAK NEO →":"CONNECT KITE →"}
            </button>

            {/* Skip option — use deeplink mode instead */}
            <div style={{textAlign:"center",marginTop:12}}>
              <button type="button" onClick={()=>{ setUserBroker(broker); onComplete(); }}
                style={{background:"none",border:"none",color:"#5a728f",fontSize:10,
                  cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>
                Skip — use manual trade mode instead
              </button>
            </div>
          </form>
        ) : null}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ADMIN PANEL
// ══════════════════════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════════════════════
// CREDIT ADMIN PANEL — view and manage trade credits per user
// ══════════════════════════════════════════════════════════════════════════════
function CreditAdminPanel() {
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding,  setAdding]  = useState(null); // uid being edited
  const [amount,  setAmount]  = useState(10);
  const [msg,     setMsg]     = useState("");

  useEffect(()=>{ loadCredits(); },[]);

  async function loadCredits() {
    setLoading(true);
    try {
      const d = await apiFetch('/credits/all');
      setUsers(d.users||[]);
    } catch(e){ setMsg("Error: "+e.message); }
    finally{ setLoading(false); }
  }

  async function grantCredits(uid) {
    try {
      await apiFetch('/credits/add',{method:'POST',
        headers:{'Content-Type':'application/json'},
        body: JSON.stringify({uid, amount, note:'admin_grant'})});
      setMsg("✓ Added "+amount+" credits to "+uid.slice(0,8)+"...");
      setAdding(null);
      await loadCredits();
    } catch(e){ setMsg("Error: "+e.message); }
  }

  if(loading) return <div style={{padding:40,textAlign:"center",color:"#8ea2bf"}}>⟳ Loading...</div>;

  return (
    <div>
      <div style={{fontSize:11,color:"#8ea2bf",marginBottom:16}}>
        Trade credits for per-trade plan users. Each order placed costs 1 credit.
      </div>
      {msg&&<div style={{padding:"8px 12px",background:"rgba(63,185,80,0.1)",border:"1px solid #3fb950",
        borderRadius:0,marginBottom:12,fontSize:11,color:"#2ed47a"}}>{msg}</div>}
      {users.length===0
        ? <div style={{textAlign:"center",padding:40,color:"#5a728f"}}>No per-trade users yet</div>
        : <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
              <thead>
                <tr style={{background:"#0b1624",borderBottom:"1px solid #1a2c44"}}>
                  {["User ID","Credits Left","Total Bought","Total Used","Last Used","Actions"].map(h=>(
                    <th key={h} style={{padding:"8px 12px",color:"#8ea2bf",
                      fontSize:12,letterSpacing:1,fontWeight:700,textAlign:"left"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u=>(
                  <tr key={u.uid} style={{borderBottom:"1px solid #161b22"}}>
                    <td style={{padding:"8px 12px",color:"#8ea2bf",fontSize:10,fontFamily:"inherit"}}>
                      {u.uid?.slice(0,12)}...
                    </td>
                    <td style={{padding:"8px 12px"}}>
                      <span style={{fontWeight:700,fontSize:13,
                        color:u.credits<=5?"#ff5b6c":u.credits<=20?"#e8b339":"#2ed47a"}}>
                        {u.credits||0}
                      </span>
                    </td>
                    <td style={{padding:"8px 12px",color:"#8ea2bf"}}>{u.total_bought||0}</td>
                    <td style={{padding:"8px 12px",color:"#8ea2bf"}}>{u.total_used||0}</td>
                    <td style={{padding:"8px 12px",color:"#8ea2bf",fontSize:12}}>
                      {u.last_used?.slice(0,10)||"—"}
                    </td>
                    <td style={{padding:"8px 12px"}}>
                      {adding===u.uid
                        ? <div style={{display:"flex",gap:6,alignItems:"center"}}>
                            <input type="number" value={amount} min={1} max={1000}
                              onChange={e=>setAmount(parseInt(e.target.value)||10)}
                              style={{width:60,background:"#0f1d30",border:"1px solid #23405f",
                                borderRadius:0,padding:"4px 6px",color:"#e7edf5",
                                fontFamily:"inherit",fontSize:10,outline:"none"}}/>
                            <button onClick={()=>grantCredits(u.uid)}
                              style={{background:"rgba(0,212,170,0.15)",border:"1px solid #92400e",
                                borderRadius:0,padding:"4px 10px",color:"#e8b339",
                                fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
                              ✓ Add
                            </button>
                            <button onClick={()=>setAdding(null)}
                              style={{background:"none",border:"1px solid #23405f",
                                borderRadius:0,padding:"4px 8px",color:"#8ea2bf",
                                fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
                              ✕
                            </button>
                          </div>
                        : <button onClick={()=>{setAdding(u.uid);setAmount(10);}}
                            style={{background:"rgba(96,165,250,0.1)",border:"1px solid rgba(96,165,250,0.3)",
                              borderRadius:0,padding:"4px 10px",color:"#6b7280",
                              fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
                            + Add Credits
                          </button>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
      }
      <div style={{marginTop:16}}>
        <button onClick={loadCredits}
          style={{background:"none",border:"1px solid #23405f",borderRadius:0,
            padding:"6px 14px",color:"#8ea2bf",fontFamily:"inherit",fontSize:10,cursor:"pointer"}}>
          ↻ Refresh
        </button>
      </div>
    </div>
  );
}

function AdminPanel({firebaseUser, userRecord, onExit}) {
  const [users,     setUsers]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [syncing,   setSyncing]   = useState(false);
  const [err,       setErr]       = useState("");
  const [form,      setForm]      = useState({email:"",pass:"",name:"",months:1,role:"user"});
  const [creating,  setCreating]  = useState(false);
  const [showCreate,setShowCreate]= useState(false);
  const [msg,       setMsg]       = useState("");
  const [adminTab,  setAdminTab]  = useState("users");
  const [plans,     setPlans]     = useState([]);
  const [plansMsg,  setPlansMsg]  = useState("");
  const [payments,  setPayments]  = useState([]);

  useEffect(()=>{
    apiFetch('/payment/plans').then(d=>setPlans(d.plans||[])).catch(()=>{});
    apiFetch('/payment/history').then(d=>setPayments(d.payments||[])).catch(()=>{});
  },[]);

  const savePlans = async()=>{
    try {
      await apiFetch('/payment/plans',{method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({plans,currency:'INR'})});
      setPlansMsg('✓ Plans saved');
      setTimeout(()=>setPlansMsg(''),2000);
    } catch(e){ setPlansMsg('Error: '+e.message); }
  };

  useEffect(()=>{ loadUsers(); },[]);

  async function loadUsers() {
    setLoading(true); setErr("");
    try { setUsers(await fbGetAllUsers()); }
    catch(e) { setErr("Failed to load: "+e.message+". Check Firestore rules — admin needs read access to all users."); }
    finally { setLoading(false); }
  }

  // Sync: create Firestore records for Firebase Auth users that are missing them
  async function syncAuthUsers() {
    setSyncing(true); setMsg("");
    try {
      const incomplete = users.filter(u=>!u.expiresAt||!u.email);
      const autoCreated = users.filter(u=>u.autoCreated);
      setMsg(`✓ ${users.length} users in Firestore. ${autoCreated.length} auto-created from Firebase Console (review roles/expiry). ${incomplete.length} may need attention.

💡 Tip: Users added via Firebase Console auto-get a 30-day 'user' role record on first login. To pre-set their role, create them here using the + button instead.`);
    } catch(e) { setMsg("Error: "+e.message); }
    finally { setSyncing(false); }
  }

  async function createUser(e) {
    e.preventDefault();
    setCreating(true); setErr(""); setMsg("");
    try {
      const expiresAt = new Date();
      expiresAt.setMonth(expiresAt.getMonth() + parseInt(form.months));
      const expStr = expiresAt.toISOString().split("T")[0]; // YYYY-MM-DD
      await fbCreateUser(form.email, form.pass, form.name, expStr, form.role);
      setMsg(`✓ User ${form.email} created. Expires: ${expStr}`);
      setForm({email:"",pass:"",name:"",months:1,role:"user"});
      setShowCreate(false);
      await loadUsers();
    } catch(e) { setErr(e.message); }
    finally { setCreating(false); }
  }

  async function extendUser(uid, months) {
    const user = users.find(u=>u.id===uid);
    const base = user?.expiresAt ? new Date(user.expiresAt) : new Date();
    if(base < new Date()) base.setTime(new Date().getTime());
    base.setMonth(base.getMonth()+months);
    const newExp = base.toISOString().split("T")[0];
    await fbUpdateUser(uid, {expiresAt: newExp});
    setMsg(`✓ Extended ${user?.email} to ${newExp}`);
    await loadUsers();
  }

  async function toggleUser(uid, active) {
    await fbUpdateUser(uid, {active: !active});
    await loadUsers();
  }

  async function removeUser(uid, email) {
    if(!window.confirm(`Remove ${email}? (Firestore record only — contact Firebase to delete auth user)`)) return;
    await fbDeleteUser(uid);
    await loadUsers();
  }

  async function setUserRole(uid, role) {
    await fbUpdateUser(uid, {role});
    setMsg(`✓ Role updated to "${role}"`);
    await loadUsers();
  }

  const inp = {background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,padding:"7px 10px",
    color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none"};

  return (
    <div style={{minHeight:"100vh",background:"#07101c",fontFamily:"'JetBrains Mono',monospace"}}>
      {/* Header */}
      <div style={{background:"#0b1624",borderBottom:"1px solid #1a2c44",padding:"10px 20px",
        display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:16}}>🛡</span>
          <div>
            <div style={{fontSize:11,fontWeight:700,color:"#e7edf5",letterSpacing:1}}>ADMIN PANEL</div>
            <div style={{fontSize:12,color:"#8ea2bf"}}>RABTRADE · User Management</div>
          </div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={()=>setShowCreate(!showCreate)}
            style={{background:showCreate?"rgba(0,212,170,0.15)":"#0f1d30",border:"1px solid "+(showCreate?"#e8b339":"#23405f"),
              borderRadius:0,padding:"6px 14px",color:showCreate?"#e8b339":"#e7edf5",fontFamily:"inherit",fontSize:10,cursor:"pointer",fontWeight:700}}>
            + ADD USER
          </button>
          <button onClick={loadUsers} disabled={loading}
            style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,padding:"6px 14px",
              color:"#8ea2bf",fontFamily:"inherit",fontSize:10,cursor:"pointer"}}>
            {loading?"⟳":"↻"} REFRESH
          </button>
          <button onClick={onExit}
            style={{background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,padding:"6px 14px",
              color:"#8ea2bf",fontFamily:"inherit",fontSize:10,cursor:"pointer"}}>
            → OPEN APP
          </button>
        </div>
      </div>

      <div style={{padding:"20px",maxWidth:900,margin:"0 auto"}}>
        {msg&&<div style={{background:"rgba(63,185,80,0.1)",border:"1px solid #3fb950",borderRadius:0,padding:"10px 14px",marginBottom:16,fontSize:11,color:"#2ed47a"}}>{msg}</div>}
        {err&&(
          <div style={{background:"rgba(248,81,73,0.1)",border:"1px solid #f85149",borderRadius:0,padding:"12px 14px",marginBottom:16,fontSize:11,color:"#ff5b6c"}}>
            <div style={{fontWeight:700,marginBottom:4}}>⚠ {err}</div>
            {err.includes("rules")||err.includes("permission")||err.includes("Missing")?(
              <div style={{color:"#8ea2bf",marginTop:6,lineHeight:1.7}}>
                Fix: Go to Firebase Console → Firestore → Rules and make sure your admin account has read access to all users.<br/>
                Your role in Firestore must be <strong style={{color:"#49c2e4"}}>"admin"</strong> — check your user document.
              </div>
            ):null}
          </div>
        )}

        {/* Admin Tab nav */}
        <div style={{display:"flex",gap:0,borderBottom:"2px solid #1a2c44",marginBottom:20}}>
          {[["users","👥 Users"],["plans","💳 Plans"],["credits","⚡ Credits"],["payments","📊 Payments"]].map(([id,label])=>(
            <button key={id} onClick={()=>setAdminTab(id)}
              style={{background:"transparent",border:"none",
                borderBottom:`2px solid ${adminTab===id?"#e8b339":"transparent"}`,
                padding:"8px 18px",color:adminTab===id?"#e8b339":"#8ea2bf",
                fontFamily:"inherit",fontSize:11,fontWeight:adminTab===id?700:400,cursor:"pointer"}}>
              {label}
            </button>
          ))}
        </div>

        {/* ── USERS TAB ── */}
        {adminTab==="users"&&<>

        {/* Create user form */}
        {showCreate&&(
          <div style={{background:"#0b1624",border:"1px solid #92400e",borderRadius:0,padding:"18px",marginBottom:20}}>
            <div style={{fontSize:10,color:"#e8b339",fontWeight:700,letterSpacing:1,marginBottom:14}}>CREATE NEW USER</div>
            <form onSubmit={createUser}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10}}>
                <div>
                  <div style={{fontSize:12,color:"#8ea2bf",marginBottom:4}}>FULL NAME</div>
                  <input {...inp} style={{...inp,width:"100%",boxSizing:"border-box"}} required
                    value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="John Doe"/>
                </div>
                <div>
                  <div style={{fontSize:12,color:"#8ea2bf",marginBottom:4}}>EMAIL</div>
                  <input {...inp} type="email" style={{...inp,width:"100%",boxSizing:"border-box"}} required
                    value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} placeholder="user@email.com"/>
                </div>
                <div>
                  <div style={{fontSize:12,color:"#8ea2bf",marginBottom:4}}>PASSWORD</div>
                  <input {...inp} type="password" style={{...inp,width:"100%",boxSizing:"border-box"}} required minLength={6}
                    value={form.pass} onChange={e=>setForm(p=>({...p,pass:e.target.value}))} placeholder="min 6 chars"/>
                </div>
              </div>
              <div style={{display:"flex",gap:10,alignItems:"flex-end"}}>
                <div>
                  <div style={{fontSize:12,color:"#8ea2bf",marginBottom:4}}>ACCESS DURATION</div>
                  <select value={form.months} onChange={e=>setForm(p=>({...p,months:e.target.value}))}
                    style={{...inp,width:130}}>
                    <option value="1">1 Month</option>
                    <option value="3">3 Months</option>
                    <option value="6">6 Months</option>
                    <option value="12">1 Year</option>
                    <option value="120">Lifetime (10y)</option>
                  </select>
                </div>
                <div>
                  <div style={{fontSize:12,color:"#8ea2bf",marginBottom:4}}>ROLE</div>
                  <select value={form.role} onChange={e=>setForm(p=>({...p,role:e.target.value}))}
                    style={{...inp,width:100}}>
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <button type="submit" disabled={creating}
                  style={{background:creating?"#1a2c44":"linear-gradient(135deg,#92400e,#3fb950)",border:"none",
                    borderRadius:0,padding:"9px 20px",color:creating?"#8ea2bf":"#000",
                    fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:creating?"not-allowed":"pointer"}}>
                  {creating?"CREATING...":"CREATE USER"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Users table */}
        <div style={{background:"#0b1624",border:"1px solid #1a2c44",borderRadius:0,overflow:"hidden"}}>
          <div style={{padding:"12px 16px",borderBottom:"1px solid #1a2c44",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontSize:10,color:"#8ea2bf",letterSpacing:1}}>USERS ({users.length})</span>
            <button onClick={loadUsers} style={{background:"none",border:"none",color:"#23405f",cursor:"pointer",fontSize:11}}>↻ Refresh</button>
          </div>
          {loading ? (
            <div style={{padding:"30px",textAlign:"center",color:"#8ea2bf",fontSize:11}}>Loading...</div>
          ) : users.length===0 ? (
            <div style={{padding:"30px",textAlign:"center",color:"#8ea2bf",fontSize:11}}>No users yet. Create one above.</div>
          ) : (
            <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}><table style={{width:"100%",borderCollapse:"collapse",fontSize:10,minWidth:400}}>
              <thead>
                <tr style={{borderBottom:"1px solid #1a2c44"}}>
                  {["Name","Email","Role","Expires","Status","Actions"].map(h=>(
                    <th key={h} style={{padding:"8px 12px",textAlign:"left",color:"#8ea2bf",fontWeight:600,letterSpacing:1}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u=>{
                  const days = daysRemaining(u);
                  const valid = isAccessValid(u);
                  return (
                    <tr key={u.id} style={{borderBottom:"1px solid #161b22",opacity:u.active?1:0.5}}>
                      <td style={{padding:"10px 12px",color:"#e7edf5",fontWeight:600}}>
                        {u.displayName||"—"}
                        {u.autoCreated&&<span style={{fontSize:9,marginLeft:5,background:"rgba(161,98,7,0.15)",color:"#49c2e4",padding:"1px 5px",borderRadius:0,border:"1px solid rgba(161,98,7,0.3)"}}>Console</span>}
                      </td>
                      <td style={{padding:"10px 12px",color:"#8ea2bf"}}>{u.email}</td>
                      <td style={{padding:"8px 12px"}}>
                        {/* 3-role selector */}
                        <div style={{display:"flex",gap:2}}>
                          {[
                            ["admin",  "#49c2e4", "Admin"],
                            ["trader", "#6b7280", "Trader"],
                            ["viewer", "#8ea2bf", "Viewer"],
                          ].map(([role,color,label])=>(
                            <button key={role}
                              onClick={()=>u.role!==role&&setUserRole(u.id,role)}
                              style={{
                                background:u.role===role?`${color}22`:"transparent",
                                border:`1px solid ${u.role===role?color:"#23405f"}`,
                                borderRadius:0,padding:"2px 6px",
                                color:u.role===role?color:"#5a728f",
                                fontFamily:"inherit",fontSize:11,fontWeight:700,
                                cursor:u.role===role?"default":"pointer",
                              }}>
                              {label}
                            </button>
                          ))}
                        </div>
                      </td>
                      <td style={{padding:"10px 12px",color:days<7?"#ff5b6c":days<30?"#e8b339":"#2ed47a",fontWeight:600}}>
                        {u.expiresAt||"Lifetime"} {days!==null&&`(${days}d)`}
                      </td>
                      <td style={{padding:"10px 12px"}}>
                        <span style={{padding:"2px 7px",borderRadius:0,fontSize:12,fontWeight:700,
                          background:valid?"rgba(22,163,74,0.15)":"rgba(220,38,38,0.15)",
                          color:valid?"#2ed47a":"#ff5b6c"}}>
                          {valid?"ACTIVE":"EXPIRED/BLOCKED"}
                        </span>
                      </td>
                      <td style={{padding:"8px 12px"}}>
                        <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                          <button onClick={()=>extendUser(u.id,1)}
                            style={{background:"rgba(63,185,80,0.1)",border:"1px solid #3fb950",borderRadius:0,
                              padding:"2px 7px",color:"#2ed47a",fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
                            +1m
                          </button>
                          <button onClick={()=>extendUser(u.id,3)}
                            style={{background:"rgba(63,185,80,0.1)",border:"1px solid #3fb950",borderRadius:0,
                              padding:"2px 7px",color:"#2ed47a",fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
                            +3m
                          </button>
                          <button onClick={()=>extendUser(u.id,12)}
                            style={{background:"rgba(63,185,80,0.1)",border:"1px solid #3fb950",borderRadius:0,
                              padding:"2px 7px",color:"#2ed47a",fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
                            +1y
                          </button>
                          {/* Scan limit override */}
                          {u.role!=="admin"&&(
                            <select
                              value={u.scanLimit??''}
                              onChange={async e=>{
                                const v = e.target.value===''?null:parseInt(e.target.value);
                                await fbUpdateUser(u.id,{scanLimit:v});
                                setMsg(`✓ Scan limit set to ${v??'default'} for ${u.email}`);
                                await loadUsers();
                              }}
                              style={{background:"#0b1624",border:"1px solid #1a2c44",borderRadius:0,
                                padding:"2px 4px",color:"#8ea2bf",fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
                              <option value="">📡 Default</option>
                              <option value="10">10/day</option>
                              <option value="20">20/day</option>
                              <option value="50">50/day</option>
                              <option value="100">100/day</option>
                              <option value="999">Unlimited</option>
                            </select>
                          )}
                          {/* AI Access toggle */}
                          {u.role!=="admin"&&(
                            <button
                              onClick={async()=>{
                                const newVal = !u.ai_access;
                                await fbUpdateUser(u.id,{ai_access:newVal});
                                setMsg(`✓ AI Access ${newVal?"enabled":"disabled"} for ${u.email}`);
                                await loadUsers();
                              }}
                              title={u.ai_access?"Revoke AI Access":"Grant AI Access"}
                              style={{background:u.ai_access?"rgba(96,165,250,0.15)":"rgba(100,116,139,0.08)",
                                border:`1px solid ${u.ai_access?"#e8b339":"#23405f"}`,
                                borderRadius:0,padding:"2px 7px",
                                color:u.ai_access?"#49c2e4":"#5a728f",
                                fontFamily:"inherit",fontSize:12,cursor:"pointer",fontWeight:u.ai_access?700:400}}>
                              {u.ai_access?"🧠 AI ✓":"🧠 AI"}
                            </button>
                          )}
                          <button onClick={()=>toggleUser(u.id, u.active)}
                            style={{background:u.active?"rgba(248,81,73,0.1)":"rgba(63,185,80,0.1)",
                              border:"1px solid "+(u.active?"#ff5b6c":"#2ed47a"),borderRadius:0,
                              padding:"2px 7px",color:u.active?"#ff5b6c":"#2ed47a",
                              fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
                            {u.active?"BLOCK":"UNBLOCK"}
                          </button>
                          <button onClick={()=>removeUser(u.id,u.email)}
                            style={{background:"rgba(248,81,73,0.05)",border:"1px solid #23405f",borderRadius:0,
                              padding:"2px 7px",color:"#ff5b6c",fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
                            DEL
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          )}
        </div>
        </>}

        {/* ── PLANS TAB ── */}
        {adminTab==="plans"&&(
          <div>
            <div style={{fontSize:11,color:"#8ea2bf",marginBottom:16}}>
              Set subscription plans. Changes take effect immediately for all users.
            </div>
            {plansMsg&&<div style={{padding:"8px 12px",borderRadius:0,marginBottom:12,fontSize:11,
              background:"rgba(63,185,80,0.1)",border:"1px solid #3fb950",color:"#2ed47a"}}>{plansMsg}</div>}
            <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
              {plans.map((plan,idx)=>(
                <div key={plan.id} style={{background:"#0b1624",border:"1px solid #1a2c44",
                  borderRadius:0,padding:"14px 16px"}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr",gap:8,alignItems:"end"}}>
                    <div>
                      <div style={{fontSize:12,color:"#8ea2bf",marginBottom:4}}>NAME</div>
                      <input value={plan.name}
                        onChange={e=>{const p=[...plans];p[idx]={...p[idx],name:e.target.value};setPlans(p);}}
                        style={{width:"100%",background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,
                          padding:"6px 8px",color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none",boxSizing:"border-box"}}/>
                    </div>
                    <div>
                      <div style={{fontSize:12,color:"#8ea2bf",marginBottom:4}}>PRICE (₹)</div>
                      <input type="number" value={plan.price}
                        onChange={e=>{const p=[...plans];p[idx]={...p[idx],price:parseInt(e.target.value)||0};setPlans(p);}}
                        style={{width:"100%",background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,
                          padding:"6px 8px",color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none",boxSizing:"border-box"}}/>
                    </div>
                    <div>
                      <div style={{fontSize:12,color:"#8ea2bf",marginBottom:4}}>DAYS</div>
                      <input type="number" value={plan.days}
                        onChange={e=>{const p=[...plans];p[idx]={...p[idx],days:parseInt(e.target.value)||1};setPlans(p);}}
                        style={{width:"100%",background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,
                          padding:"6px 8px",color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none",boxSizing:"border-box"}}/>
                    </div>
                    <div>
                      <div style={{fontSize:12,color:"#8ea2bf",marginBottom:4}}>TYPE</div>
                      <select value={plan.type}
                        onChange={e=>{const p=[...plans];p[idx]={...p[idx],type:e.target.value};setPlans(p);}}
                        style={{width:"100%",background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,
                          padding:"6px 8px",color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none"}}>
                        <option value="fixed">Fixed</option>
                        <option value="pertrade">Per Trade</option>
                      </select>
                    </div>
                    <div style={{display:"flex",gap:6,alignItems:"center"}}>
                      <label style={{display:"flex",alignItems:"center",gap:4,fontSize:10,color:"#8ea2bf",cursor:"pointer"}}>
                        <input type="checkbox" checked={!!plan.popular}
                          onChange={e=>{const p=[...plans];p[idx]={...p[idx],popular:e.target.checked};setPlans(p);}}/>
                        Popular
                      </label>
                      <button onClick={()=>setPlans(plans.filter((_,i)=>i!==idx))}
                        style={{background:"none",border:"1px solid rgba(248,81,73,0.3)",borderRadius:0,
                          padding:"4px 8px",color:"#ff5b6c",fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>
                        ✕
                      </button>
                    </div>
                  </div>
                  <div style={{marginTop:8}}>
                    <div style={{fontSize:12,color:"#8ea2bf",marginBottom:4}}>DESCRIPTION</div>
                    <input value={plan.desc||""}
                      onChange={e=>{const p=[...plans];p[idx]={...p[idx],desc:e.target.value};setPlans(p);}}
                      style={{width:"100%",background:"#0f1d30",border:"1px solid #23405f",borderRadius:0,
                        padding:"6px 8px",color:"#e7edf5",fontFamily:"inherit",fontSize:11,outline:"none",boxSizing:"border-box"}}
                      placeholder="Brief description shown to users"/>
                  </div>
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setPlans([...plans,{id:"plan_"+Date.now(),name:"New Plan",
                desc:"",price:999,days:30,type:"fixed",popular:false}])}
                style={{background:"rgba(96,165,250,0.1)",border:"1px solid rgba(96,165,250,0.3)",
                  borderRadius:0,padding:"8px 16px",color:"#6b7280",fontFamily:"inherit",
                  fontSize:11,cursor:"pointer"}}>
                + Add Plan
              </button>
              <button onClick={savePlans}
                style={{background:"rgba(0,212,170,0.15)",border:"1px solid #92400e",
                  borderRadius:0,padding:"8px 20px",color:"#e8b339",fontFamily:"inherit",
                  fontSize:11,fontWeight:700,cursor:"pointer"}}>
                💾 Save Plans
              </button>
            </div>
          </div>
        )}

        {/* ── CREDITS TAB ── */}
        {adminTab==="credits"&&(
          <CreditAdminPanel/>
        )}

        {/* ── PAYMENTS TAB ── */}
        {adminTab==="payments"&&(
          <div>
            <div style={{fontSize:11,color:"#8ea2bf",marginBottom:16}}>
              Payment history — {payments.length} total transactions
            </div>
            {payments.length===0
              ? <div style={{textAlign:"center",padding:40,color:"#5a728f"}}>No payments yet</div>
              : <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                    <thead>
                      <tr style={{background:"#0b1624",borderBottom:"1px solid #1a2c44"}}>
                        {["Date","User","Plan","Amount","Payment ID"].map(h=>(
                          <th key={h} style={{padding:"8px 12px",color:"#8ea2bf",
                            fontSize:12,letterSpacing:1,fontWeight:700,textAlign:"left"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((p,i)=>(
                        <tr key={i} style={{borderBottom:"1px solid #161b22"}}>
                          <td style={{padding:"8px 12px",color:"#8ea2bf",fontSize:10}}>
                            {p.paid_at?.slice(0,10)||"—"}
                          </td>
                          <td style={{padding:"8px 12px",color:"#e7edf5"}}>{p.uid?.slice(0,8)}...</td>
                          <td style={{padding:"8px 12px"}}>
                            <span style={{padding:"2px 8px",borderRadius:0,fontSize:12,fontWeight:700,
                              background:"rgba(0,212,170,0.1)",color:"#e8b339"}}>
                              {p.plan_name}
                            </span>
                          </td>
                          <td style={{padding:"8px 12px",color:"#2ed47a",fontWeight:700}}>
                            ₹{p.amount}
                          </td>
                          <td style={{padding:"8px 12px",color:"#5a728f",fontSize:12,fontFamily:"inherit"}}>
                            {p.payment_id}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
            }
          </div>
        )}

      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MY STRATEGIES — User-defined scan builder
// Strategies stored in Firestore: users/{uid}/strategies/{id}
// ══════════════════════════════════════════════════════════════════════════════

const STRAT_COL = (uid) => collection(_db, "users", uid, "strategies");
const STRAT_DOC = (uid, id) => doc(_db, "users", uid, "strategies", id);

async function fbStratSave(uid, strat) {
  if(!uid||!strat?.id) return;
  await setDoc(STRAT_DOC(uid, strat.id), {...strat, updatedAt: serverTimestamp()}, {merge:true});
}
async function fbStratLoad(uid) {
  try {
    const q   = query(STRAT_COL(uid), orderBy("createdAt","desc"));
    const snap= await getDocs(q);
    return snap.docs.map(d=>({...d.data(), id:d.id}));
  } catch { return []; }
}
async function fbStratDelete(uid, id) {
  await deleteDoc(STRAT_DOC(uid, id));
}

// Operators available in the rule builder
const OPERATORS = [
  {value:">",         label:"> greater than"},
  {value:"<",         label:"< less than"},
  {value:">=",        label:"≥ greater or equal"},
  {value:"<=",        label:"≤ less or equal"},
  {value:"==",        label:"= equals"},
  {value:"cross_above", label:"↑ crosses above"},
  {value:"cross_below", label:"↓ crosses below"},
];

const DEFAULT_RULE = () => ({
  id: Math.random().toString(36).slice(2),
  left: {id:"rsi", params:{period:14}},
  operator: "<",
  right: {type:"number", value:30, id:"ema", params:{period:50}},
});

// A single param input (int or float)
function ParamInput({param, value, onChange}) {
  return (
    <label style={{display:"flex",alignItems:"center",gap:4,fontSize:10,color:"#8ea2bf"}}>
      <span style={{minWidth:40}}>{param.label}</span>
      <input
        type="number" min={param.min} max={param.max} step={param.type==="float"?0.5:1}
        value={value ?? param.default}
        onChange={e=>onChange(param.type==="float"?parseFloat(e.target.value):parseInt(e.target.value))}
        style={{width:52,background:"#07101c",border:"1px solid #23405f",borderRadius:0,
          padding:"2px 4px",color:"#e7edf5",fontFamily:"inherit",fontSize:10,textAlign:"right"}}
      />
    </label>
  );
}

// Indicator picker + params
function IndicatorPicker({registry, value, onChange, label}) {
  const entry = registry[value?.id] || {};
  const params = value?.params || {};

  return (
    <div style={{display:"flex",flexDirection:"column",gap:4}}>
      {label && <span style={{fontSize:9,color:"#5a728f",letterSpacing:1,textTransform:"uppercase"}}>{label}</span>}
      <select
        value={value?.id||""}
        onChange={e=>onChange({id:e.target.value, params: Object.fromEntries(
          (registry[e.target.value]?.params||[]).map(p=>[p.name, p.default])
        )})}
        style={{background:"#07101c",border:"1px solid #23405f",borderRadius:0,
          padding:"4px 6px",color:"#e7edf5",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>
        {Object.entries(registry).map(([k,v])=>(
          <option key={k} value={k}>{v.label}</option>
        ))}
      </select>
      <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
        {(entry.params||[]).map(p=>(
          <ParamInput key={p.name} param={p}
            value={params[p.name]??p.default}
            onChange={v=>onChange({...value, params:{...params,[p.name]:v}})}
          />
        ))}
      </div>
    </div>
  );
}

// One rule row
function RuleRow({rule, registry, onChange, onRemove, index, total}) {
  const S = {
    row:{background:"#07101c",border:"1px solid #1a2c44",borderRadius:0,padding:"10px 12px",
         display:"flex",flexDirection:"column",gap:8,position:"relative"},
    badge:{fontSize:9,color:"#8ea2bf",letterSpacing:1,fontWeight:700},
    opSel:{background:"#07101c",border:"1px solid #e8b339",borderRadius:0,
           padding:"5px 8px",color:"#49c2e4",fontFamily:"inherit",fontSize:11,cursor:"pointer",
           fontWeight:700},
    rmBtn:{position:"absolute",top:8,right:8,background:"none",border:"none",
           color:"#475569",fontSize:14,cursor:"pointer",lineHeight:1,padding:2},
  };
  return (
    <div style={S.row}>
      {total>1&&<button style={S.rmBtn} onClick={onRemove} title="Remove rule">✕</button>}
      <span style={S.badge}>RULE {index+1}</span>
      <div style={{display:"flex",flexWrap:"wrap",gap:12,alignItems:"flex-start"}}>
        {/* LEFT side */}
        <IndicatorPicker registry={registry} value={rule.left} label="Indicator"
          onChange={v=>onChange({...rule, left:v})}/>

        {/* OPERATOR */}
        <div style={{display:"flex",flexDirection:"column",gap:4}}>
          <span style={{fontSize:9,color:"#5a728f",letterSpacing:1,textTransform:"uppercase"}}>Operator</span>
          <select value={rule.operator}
            onChange={e=>onChange({...rule, operator:e.target.value})}
            style={S.opSel}>
            {OPERATORS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {/* RIGHT side — number or indicator */}
        <div style={{display:"flex",flexDirection:"column",gap:4}}>
          <span style={{fontSize:9,color:"#5a728f",letterSpacing:1,textTransform:"uppercase"}}>Compare to</span>
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            <button onClick={()=>onChange({...rule,right:{...rule.right,type:"number"}})}
              style={{background:rule.right.type==="number"?"#0f1d30":"none",
                border:"1px solid #0f1d30",borderRadius:0,padding:"3px 8px",
                color:rule.right.type==="number"?"#fff":"#49c2e4",fontFamily:"inherit",
                fontSize:10,cursor:"pointer"}}>Value</button>
            <button onClick={()=>onChange({...rule,right:{...rule.right,type:"indicator"}})}
              style={{background:rule.right.type==="indicator"?"#0f1d30":"none",
                border:"1px solid #0f1d30",borderRadius:0,padding:"3px 8px",
                color:rule.right.type==="indicator"?"#fff":"#49c2e4",fontFamily:"inherit",
                fontSize:10,cursor:"pointer"}}>Indicator</button>
          </div>
          {rule.right.type==="number" ? (
            <input type="number" value={rule.right.value??0}
              onChange={e=>onChange({...rule,right:{...rule.right,value:parseFloat(e.target.value)}})}
              style={{width:80,background:"#07101c",border:"1px solid #23405f",borderRadius:0,
                padding:"5px 8px",color:"#e7edf5",fontFamily:"inherit",fontSize:12}}/>
          ) : (
            <IndicatorPicker registry={registry}
              value={{id:rule.right.id||"ema", params:rule.right.params||{}}}
              onChange={v=>onChange({...rule,right:{...rule.right,...v}})}/>
          )}
        </div>
      </div>
    </div>
  );
}

// Strategy builder / editor modal
function StrategyBuilder({registry, initial, onSave, onCancel}) {
  const isNew = !initial?.id;
  const [name, setName]           = useState(initial?.name||"");
  const [combinator, setCombinator] = useState(initial?.combinator||"AND");
  const [rules, setRules]         = useState(initial?.rules?.length ? initial.rules : [DEFAULT_RULE()]);
  const [saving, setSaving]       = useState(false);
  const [err, setErr]             = useState("");

  const updateRule = (idx,r) => setRules(rs=>rs.map((x,i)=>i===idx?r:x));
  const removeRule = idx      => setRules(rs=>rs.filter((_,i)=>i!==idx));
  const addRule    = ()       => setRules(rs=>[...rs, DEFAULT_RULE()]);

  const S = {
    overlay:{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:9000,
             display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"20px 12px",overflow:"auto"},
    modal:{background:"#0f172a",border:"1px solid #1a2c44",borderRadius:12,
           width:"100%",maxWidth:700,padding:"20px",display:"flex",flexDirection:"column",gap:16},
    title:{fontSize:15,fontWeight:700,color:"#e7edf5",letterSpacing:0.5},
    label:{fontSize:11,color:"#8ea2bf",marginBottom:2},
    input:{width:"100%",background:"#07101c",border:"1px solid #23405f",borderRadius:0,
           padding:"8px 10px",color:"#e7edf5",fontFamily:"inherit",fontSize:13,boxSizing:"border-box"},
    btn:(col)=>({background:col,border:"none",borderRadius:0,padding:"8px 18px",color:"#fff",
                 fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer"}),
  };

  return (
    <div style={S.overlay} onClick={e=>{if(e.target===e.currentTarget) onCancel();}}>
      <div style={S.modal}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={S.title}>{isNew?"New Strategy":"Edit Strategy"}</span>
          <button onClick={onCancel} style={{background:"none",border:"none",color:"#5a728f",
            fontSize:18,cursor:"pointer"}}>✕</button>
        </div>

        {/* Name */}
        <div>
          <div style={S.label}>Strategy Name</div>
          <input style={S.input} placeholder="e.g. RSI Oversold + Trend" value={name}
            onChange={e=>setName(e.target.value)}/>
        </div>

        {/* Combinator */}
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <span style={S.label}>Match</span>
          {["AND","OR"].map(c=>(
            <button key={c} onClick={()=>setCombinator(c)}
              style={{...S.btn(combinator===c?"#1d4ed8":"#1a2c44"),
                border:`1px solid ${combinator===c?"#e8b339":"#23405f"}`}}>
              {c}
            </button>
          ))}
          <span style={{fontSize:11,color:"#5a728f"}}>
            {combinator==="AND" ? "all rules must pass" : "any rule must pass"}
          </span>
        </div>

        {/* Rules */}
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {rules.map((r,i)=>(
            <RuleRow key={r.id} rule={r} registry={registry} index={i} total={rules.length}
              onChange={v=>updateRule(i,v)} onRemove={()=>removeRule(i)}/>
          ))}
          <button onClick={addRule}
            style={{background:"none",border:"1px dashed #0f1d30",borderRadius:7,
              padding:"8px",color:"#49c2e4",fontFamily:"inherit",fontSize:11,cursor:"pointer"}}>
            + Add Rule
          </button>
        </div>

        {err && <div style={{color:"#ff5b6c",fontSize:11}}>{err}</div>}

        {/* Actions */}
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button onClick={onCancel} style={S.btn("#1a2c44")}>Cancel</button>
          <button disabled={saving} onClick={async()=>{
            if(!name.trim()){setErr("Name is required"); return;}
            if(!rules.length){setErr("Add at least one rule"); return;}
            setSaving(true); setErr("");
            try {
              await onSave({
                id: initial?.id || crypto.randomUUID(),
                name: name.trim(),
                combinator,
                rules,
                createdAt: initial?.createdAt || new Date().toISOString(),
              });
            } catch(e){ setErr(e.message); }
            setSaving(false);
          }} style={S.btn("#1d4ed8")}>
            {saving ? "Saving…" : "Save Strategy"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Runner modal — pick watchlist + timeframe, show results
function StrategyRunner({strategy, onClose, uid}) {
  const [watchlist, setWatchlist] = useState("NIFTY 50");
  const [timeframe, setTimeframe] = useState("1day");
  const [running,   setRunning]   = useState(false);
  const [results,   setResults]   = useState(null);
  const [err,       setErr]       = useState("");

  const allWatchlists = {...WATCHLISTS, ...FO_WATCHLISTS};
  const TF_OPTIONS = [
    {v:"15min",l:"15 Min"},{v:"1hour",l:"1 Hour"},
    {v:"1day",l:"1 Day"},{v:"1week",l:"1 Week"},
  ];

  const run = async () => {
    setRunning(true); setErr(""); setResults(null);
    try {
      const symbols = allWatchlists[watchlist] || WATCHLISTS["NIFTY 50"];
      const exchange = watchlist==="MCX"?"MCX":"NSE";
      const data = await apiFetch("/strategy/scan", {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          symbols, exchange, timeframe,
          rules: strategy.rules,
          combinator: strategy.combinator,
        }),
      });
      setResults(data);
    } catch(e){ setErr(e.message); }
    setRunning(false);
  };

  const S = {
    overlay:{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:9100,
             display:"flex",alignItems:"flex-start",justifyContent:"center",
             padding:"20px 12px",overflow:"auto"},
    modal:{background:"#0f172a",border:"1px solid #1a2c44",borderRadius:12,
           width:"100%",maxWidth:640,padding:20,display:"flex",flexDirection:"column",gap:14},
    sel:{background:"#07101c",border:"1px solid #23405f",borderRadius:0,
         padding:"6px 8px",color:"#e7edf5",fontFamily:"inherit",fontSize:12,cursor:"pointer"},
    th:{padding:"6px 8px",fontSize:9,color:"#5a728f",letterSpacing:1,textAlign:"left",
        borderBottom:"1px solid #1a2c44",fontWeight:700},
    td:{padding:"7px 8px",fontSize:12,color:"#e7edf5",borderBottom:"1px solid #1a2c44"},
  };

  return (
    <div style={S.overlay} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={S.modal}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontSize:15,fontWeight:700,color:"#e7edf5"}}>
            ▶ Run: <span style={{color:"#49c2e4"}}>{strategy.name}</span>
          </span>
          <button onClick={onClose} style={{background:"none",border:"none",
            color:"#5a728f",fontSize:18,cursor:"pointer"}}>✕</button>
        </div>

        {/* Rule summary */}
        <div style={{background:"#07101c",borderRadius:0,padding:"8px 10px",
          border:"1px solid #1a2c44",fontSize:10,color:"#8ea2bf",lineHeight:1.7}}>
          <span style={{color:"#49c2e4",fontWeight:700}}>{strategy.combinator} </span>
          {strategy.rules.map((r,i)=>(
            <span key={i}>{i>0&&<span style={{color:"#475569"}}> · </span>}
              <span style={{color:"#e7edf5"}}>{r.left?.id?.toUpperCase()}</span>
              <span style={{color:"#f59e0b"}}> {r.operator} </span>
              <span style={{color:"#e7edf5"}}>
                {r.right?.type==="indicator"?r.right.id?.toUpperCase():r.right?.value}
              </span>
            </span>
          ))}
        </div>

        {/* Config row */}
        <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"flex-end"}}>
          <div>
            <div style={{fontSize:10,color:"#5a728f",marginBottom:3}}>Watchlist</div>
            <select style={S.sel} value={watchlist} onChange={e=>setWatchlist(e.target.value)}>
              {Object.keys(allWatchlists).map(k=>(
                <option key={k} value={k}>{k} ({(allWatchlists[k]||[]).length})</option>
              ))}
            </select>
          </div>
          <div>
            <div style={{fontSize:10,color:"#5a728f",marginBottom:3}}>Timeframe</div>
            <select style={S.sel} value={timeframe} onChange={e=>setTimeframe(e.target.value)}>
              {TF_OPTIONS.map(o=><option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
          <button onClick={run} disabled={running}
            style={{background:running?"#1a2c44":"#1d4ed8",border:"none",borderRadius:0,
              padding:"8px 20px",color:"#fff",fontFamily:"inherit",fontSize:12,
              fontWeight:700,cursor:running?"default":"pointer"}}>
            {running ? "Scanning…" : "▶ Run Scan"}
          </button>
        </div>

        {err && <div style={{color:"#ff5b6c",fontSize:11,padding:"6px 0"}}>{err}</div>}

        {/* Results */}
        {results && (
          <div>
            <div style={{fontSize:11,color:"#5a728f",marginBottom:8}}>
              <span style={{color:"#2ed47a",fontWeight:700}}>{results.count} match{results.count!==1?"es":""}</span>
              {" "}out of {results.scanned} scanned · {results.timeframe}
            </div>
            {results.count===0 ? (
              <div style={{textAlign:"center",padding:"24px",color:"#475569",fontSize:12}}>
                No symbols matched this strategy on the selected watchlist/timeframe.
              </div>
            ) : (
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                  <thead>
                    <tr>
                      {["SYMBOL","PRICE","RULES","INDICATORS"].map(h=>(
                        <th key={h} style={S.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.matches.map(m=>(
                      <tr key={m.symbol}>
                        <td style={{...S.td,fontWeight:700,color:"#49c2e4"}}>{m.symbol}</td>
                        <td style={S.td}>₹{(m.ltp||0).toLocaleString("en-IN",{maximumFractionDigits:2})}</td>
                        <td style={S.td}>
                          <span style={{color:"#2ed47a",fontWeight:700}}>{m.rules_matched}</span>
                          <span style={{color:"#475569"}}>/{m.rules_total}</span>
                        </td>
                        <td style={S.td}>
                          <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                            {Object.entries(m.indicators||{}).map(([k,v])=>(
                              <span key={k} style={{fontSize:9,background:"#1a2c44",
                                borderRadius:0,padding:"1px 5px",color:"#8ea2bf"}}>
                                {k.toUpperCase()} {v}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Main Strategies tab
function StrategiesTab({uid}) {
  const [registry,   setRegistry]   = useState({});
  const [strategies, setStrategies] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [builder,    setBuilder]    = useState(null);   // null | strategy object (new/edit)
  const [runner,     setRunner]     = useState(null);   // null | strategy to run
  const [deleting,   setDeleting]   = useState(null);
  const [toast,      setToast]      = useState("");

  const showToast = (msg) => { setToast(msg); setTimeout(()=>setToast(""),3000); };

  // Load registry + strategies on mount
  useEffect(()=>{
    apiFetch("/indicators").then(d=>setRegistry(d.indicators||{})).catch(()=>{});
    if(uid) fbStratLoad(uid).then(s=>{ setStrategies(s); setLoading(false); });
    else setLoading(false);
  },[uid]);

  const handleSave = async (strat) => {
    await fbStratSave(uid, strat);
    setStrategies(prev => {
      const idx = prev.findIndex(s=>s.id===strat.id);
      return idx>=0 ? prev.map(s=>s.id===strat.id?strat:s) : [strat,...prev];
    });
    setBuilder(null);
    showToast("Strategy saved ✓");
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    await fbStratDelete(uid, id);
    setStrategies(prev=>prev.filter(s=>s.id!==id));
    setDeleting(null);
    showToast("Deleted");
  };

  const S = {
    wrap:{padding:"16px 14px",display:"flex",flexDirection:"column",gap:16,maxWidth:800,margin:"0 auto"},
    header:{display:"flex",justifyContent:"space-between",alignItems:"center"},
    title:{fontSize:16,fontWeight:700,color:"#e7edf5",letterSpacing:0.5},
    newBtn:{background:"#1d4ed8",border:"none",borderRadius:7,padding:"8px 16px",
            color:"#fff",fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer"},
    card:{background:"#0f172a",border:"1px solid #1a2c44",borderRadius:0,padding:"14px 16px",
          display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12},
    cardTitle:{fontSize:13,fontWeight:700,color:"#e7edf5",marginBottom:4},
    pill:(c)=>({fontSize:9,background:`${c}22`,border:`1px solid ${c}44`,color:c,
               borderRadius:0,padding:"1px 6px",fontWeight:700,letterSpacing:0.5}),
    iconBtn:(c)=>({background:"none",border:`1px solid ${c}`,borderRadius:0,
                   padding:"5px 10px",color:c,fontFamily:"inherit",fontSize:11,
                   cursor:"pointer",fontWeight:700}),
  };

  if(loading) return (
    <div style={{...S.wrap,alignItems:"center",justifyContent:"center",padding:"60px 0",color:"#5a728f"}}>
      Loading strategies…
    </div>
  );

  return (
    <div style={{overflowY:"auto",flex:1}}>
      <div style={S.wrap}>
        {toast && (
          <div style={{position:"fixed",bottom:90,left:"50%",transform:"translateX(-50%)",
            background:"#2ed47a",color:"#fff",borderRadius:0,padding:"8px 18px",
            fontSize:12,fontWeight:700,zIndex:9999,pointerEvents:"none"}}>
            {toast}
          </div>
        )}

        <div style={S.header}>
          <div>
            <div style={S.title}>📐 My Strategies</div>
            <div style={{fontSize:11,color:"#5a728f",marginTop:2}}>
              Build custom scan rules — save, run on any watchlist
            </div>
          </div>
          <button style={S.newBtn} onClick={()=>setBuilder({})}>+ New Strategy</button>
        </div>

        {/* Indicator chips — quick reference */}
        {Object.keys(registry).length>0 && (
          <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
            <span style={{fontSize:10,color:"#475569",alignSelf:"center",marginRight:4}}>
              Available:</span>
            {Object.entries(registry).map(([k,v])=>(
              <span key={k} style={{fontSize:9,background:"#07101c",border:"1px solid #1a2c44",
                borderRadius:0,padding:"2px 7px",color:"#5a728f"}}>
                {v.label}
              </span>
            ))}
          </div>
        )}

        {/* Strategy list */}
        {strategies.length===0 ? (
          <div style={{textAlign:"center",padding:"48px 0",color:"#475569",fontSize:13}}>
            <div style={{fontSize:32,marginBottom:12}}>📐</div>
            No strategies yet. Create one to get started.
            <br/>
            <button style={{...S.newBtn,marginTop:16}}
              onClick={()=>setBuilder({})}>Create your first strategy</button>
          </div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {strategies.map(s=>(
              <div key={s.id} style={S.card}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={S.cardTitle}>{s.name}</div>
                  {/* Rule summary pills */}
                  <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:8}}>
                    <span style={S.pill(s.combinator==="AND"?"#e8b339":"#f59e0b")}>
                      {s.combinator}
                    </span>
                    <span style={{fontSize:10,color:"#475569",alignSelf:"center"}}>
                      {s.rules?.length||0} rule{s.rules?.length!==1?"s":""}
                    </span>
                  </div>
                  {/* Rules text */}
                  <div style={{fontSize:10,color:"#5a728f",lineHeight:1.8,fontFamily:"monospace"}}>
                    {(s.rules||[]).slice(0,3).map((r,i)=>(
                      <div key={i}>
                        <span style={{color:"#49c2e4"}}>{r.left?.id}</span>
                        {(r.left?.params?.period||r.left?.params?.fast) &&
                          <span style={{color:"#475569"}}>
                            ({Object.values(r.left?.params||{}).join(",")})
                          </span>}
                        <span style={{color:"#f59e0b"}}> {r.operator} </span>
                        <span style={{color:"#e7edf5"}}>
                          {r.right?.type==="indicator"
                            ? `${r.right.id}(${Object.values(r.right?.params||{}).join(",")})`
                            : r.right?.value}
                        </span>
                      </div>
                    ))}
                    {(s.rules?.length||0)>3 &&
                      <span style={{color:"#475569"}}>+{s.rules.length-3} more…</span>}
                  </div>
                </div>
                {/* Action buttons */}
                <div style={{display:"flex",flexDirection:"column",gap:6,flexShrink:0}}>
                  <button style={S.iconBtn("#2ed47a")} onClick={()=>setRunner(s)}>▶ Run</button>
                  <button style={S.iconBtn("#49c2e4")} onClick={()=>setBuilder(s)}>✎ Edit</button>
                  <button style={S.iconBtn("#ff5b6c")}
                    disabled={deleting===s.id}
                    onClick={()=>handleDelete(s.id)}>
                    {deleting===s.id?"…":"🗑"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {builder!==null && (
        <StrategyBuilder registry={registry} initial={builder}
          onSave={handleSave} onCancel={()=>setBuilder(null)}/>
      )}
      {runner && (
        <StrategyRunner strategy={runner} uid={uid} onClose={()=>setRunner(null)}/>
      )}
    </div>
  );
}

// ── Keyboard shortcuts ────────────────────────────────────────────────────────
// D = Dashboard, E = Equity, M = MCX, P = Positions, A = AI, V = Virtual
function useKeyboardShortcuts(setActiveTab) {
  useEffect(()=>{
    const handler = (e) => {
      const tag = document.activeElement?.tagName;
      if(["INPUT","TEXTAREA","SELECT"].includes(tag)) return;
      const k = e.key.toUpperCase();
      if(e.ctrlKey||e.metaKey||e.altKey) return;
      const map = { D:"dashboard", E:"equity", M:"mcx", P:"positions", A:"ai", V:"virtual" };
      if(map[k]) { setActiveTab(map[k]); e.preventDefault(); }
    };
    window.addEventListener("keydown", handler);
    return ()=>window.removeEventListener("keydown", handler);
  },[setActiveTab]);
}

// ══════════════════════════════════════════════════════════════════════════════
// MOBILE APP VIEW
// Lightweight mobile-first UI for Android/Chrome PWA.
// Receives state from App as props — no duplicate data fetching.
// Features: Signal stream, quick scan, positions summary, push notif bell.
// ══════════════════════════════════════════════════════════════════════════════
const MOB_SCAN_LABELS = {1:"TSI Cross",2:"RSI Extreme",3:"Vol+RSI",4:"EMA Cross",5:"Patterns",8:"D/S Zone"};
const MOB_ALL_SCANS   = [1,2,3,4,5,8];

// Market hours check in IST (Mon–Fri). NSE equity: 09:15–15:30.
// MCX commodities trade a long evening session → 09:00–23:00.
function _isMarketOpenIST(market="NSE") {
  try {
    const ist = new Date(new Date().toLocaleString("en-US", {timeZone:"Asia/Kolkata"}));
    const day = ist.getDay();                 // 0 Sun … 6 Sat
    if(day===0 || day===6) return false;
    const mins = ist.getHours()*60 + ist.getMinutes();
    if(market==="MCX") return mins >= (9*60)    && mins <= (23*60);      // 09:00–23:00
    return mins >= (9*60+15) && mins <= (15*60+30);                       // NSE 09:15–15:30
  } catch(e) { return true; }                 // fail open if TZ unsupported
}

function VpsEngineSettings({ authed, showToast }) {
  const [vpsCfg,    setVpsCfg]    = useState(null);   // /vps-scanner/config
  const [vpsStatus, setVpsStatus] = useState(null);
  useEffect(()=>{
    if(!authed) return;
    const load=()=>{
      apiFetch("/vps-scanner/config").then(d=>setVpsCfg(d.config||{})).catch(()=>{});
      apiFetch("/vps-scanner/status").then(d=>setVpsStatus(d)).catch(()=>{});
    };
    load();
    const t=setInterval(load, 60000);
    return ()=>clearInterval(t);
  },[authed]);
  const saveVps = useCallback(async (patch)=>{
    const next = {...(vpsCfg||{}), ...patch};
    setVpsCfg(next);
    try{
      await apiFetch("/vps-scanner/config",{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify(next)});
      showToast?.("VPS updated", "ok");
    }catch(e){ showToast?.("VPS save failed: "+(e.message||""), "error"); }
  }, [vpsCfg, showToast]);
  const [tgTesting, setTgTesting] = useState(false);
  const [tgTestMsg, setTgTestMsg] = useState(null);
  const testVpsTg = useCallback(async ()=>{
    setTgTesting(true); setTgTestMsg(null);
    try{
      const r = await apiFetch("/vps-scanner/tg-test",{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({bot_token:(vpsCfg&&vpsCfg.kite_tg_token)||"", chat_id:(vpsCfg&&vpsCfg.kite_tg_chat)||""})});
      setTgTestMsg(r.ok ? `✅ Sent via ${r.source} — check Telegram now` : `⚠ ${r.error||"failed"}`);
    }catch(e){ setTgTestMsg("⚠ "+(e.message||"failed")); }
    finally{ setTgTesting(false); }
  }, [vpsCfg]);
  if(!authed) return null;
  return (
            <div style={{background: vpsCfg&&vpsCfg.auto_trade?"rgba(248,81,73,0.06)":"rgba(73,194,228,0.05)",
              border:`1px solid ${vpsCfg&&vpsCfg.auto_trade?"rgba(248,81,73,0.4)":"rgba(73,194,228,0.3)"}`,
              borderRadius:10,padding:"14px"}}>
              <div style={{fontSize:12,fontWeight:700,color:"#49c2e4",marginBottom:2}}>🖥️ VPS Engine (24/7)</div>
              <div style={{fontSize:10,color:"#8ea2bf",marginBottom:8}}>
                Scans, validates and alerts on the server — even when this app is closed.
              </div>
              <div style={{fontSize:9,color:"#2ed47a",marginBottom:10,padding:"7px 9px",borderRadius:8,background:"rgba(46,212,122,0.06)",border:"1px solid rgba(46,212,122,0.25)",lineHeight:1.5}}>
                🔬 <b>Signal Engine v2</b> (equity · MCX · delta): a trade fires only when a <b>volume spike</b> + candle direction + <b>order-book imbalance</b> all agree, plus a context — <b>D/S zone</b>, <b>TSI cross</b>, or <b>RSI extreme</b>. Entry is pulled to the strongest bid/ask in the depth.
              </div>
              {vpsCfg===null ? (
                <div style={{fontSize:11,color:"#5a728f"}}>Loading…</div>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {[["kite_enabled","📈 Scan NSE on VPS"],["mcx_enabled","🏭 Scan MCX on VPS"],["delta_enabled","Δ Scan Delta / crypto (24/7)"]].map(([key,label])=>(
                    <div key={key} style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                      <span style={{fontSize:12,color:"#e7edf5"}}>{label}</span>
                      <button onClick={()=>saveVps({[key]:!vpsCfg[key]})}
                        style={{background:vpsCfg[key]?"rgba(46,212,122,0.15)":"transparent",
                          border:`1px solid ${vpsCfg[key]?"#2ed47a":"#23405f"}`,borderRadius:20,
                          padding:"4px 14px",color:vpsCfg[key]?"#2ed47a":"#8ea2bf",
                          fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                        {vpsCfg[key]?"● ON":"○ OFF"}
                      </button>
                    </div>
                  ))}

                  {/* NSE spread mode — bull/bear call/put spreads by IV regime */}
                  {vpsCfg.kite_enabled && (
                    <div style={{marginTop:2,padding:"8px",borderRadius:6,
                      background:vpsCfg.nse_spread_mode?"rgba(163,113,247,0.1)":"transparent",
                      border:`1px solid ${vpsCfg.nse_spread_mode?"#a371f7":"#23405f"}`}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                        <div>
                          <div style={{fontSize:12,color:"#e7edf5",fontWeight:700}}>🎚️ Spread mode (equity/index)</div>
                          <div style={{fontSize:9,color:"#8ea2bf"}}>
                            {vpsCfg.nse_spread_mode?"Bull/bear call/put spread by IV — defined risk":"Naked CE/PE buy (click to use spreads)"}
                          </div>
                        </div>
                        <button onClick={()=>saveVps({nse_spread_mode:!vpsCfg.nse_spread_mode})}
                          style={{background:vpsCfg.nse_spread_mode?"rgba(163,113,247,0.2)":"transparent",
                            border:`1px solid ${vpsCfg.nse_spread_mode?"#a371f7":"#23405f"}`,borderRadius:20,
                            padding:"4px 12px",color:vpsCfg.nse_spread_mode?"#a371f7":"#8ea2bf",
                            fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                          {vpsCfg.nse_spread_mode?"● ON":"○ OFF"}
                        </button>
                      </div>
                      {vpsCfg.nse_spread_mode&&(
                        <div style={{display:"flex",gap:6,marginTop:8,alignItems:"center",flexWrap:"wrap"}}>
                          <span style={{fontSize:9,color:"#8ea2bf"}}>Width (strikes):</span>
                          {[1,2,3,4,5].map(n=>{
                            const on=(vpsCfg.nse_spread_strikes||3)===n;
                            return (
                              <button key={n} onClick={()=>saveVps({nse_spread_strikes:n})}
                                style={{width:28,padding:"4px",borderRadius:6,fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer",
                                  border:`1px solid ${on?"#a371f7":"#23405f"}`,background:on?"rgba(163,113,247,0.15)":"transparent",
                                  color:on?"#a371f7":"#5a728f"}}>{n}</button>
                            );
                          })}
                        </div>
                      )}
                      {vpsCfg.nse_spread_mode&&(
                        <div style={{display:"flex",gap:6,marginTop:8,alignItems:"center",flexWrap:"wrap"}}>
                          <span style={{fontSize:9,color:"#8ea2bf"}}>Fill wait/leg:</span>
                          {[[60,"1m"],[120,"2m"],[150,"2.5m"],[240,"4m"],[300,"5m"]].map(([s,l])=>{
                            const on=(Number(vpsCfg.nse_spread_fill_wait)||150)===s;
                            return (
                              <button key={s} onClick={()=>saveVps({nse_spread_fill_wait:s})}
                                style={{padding:"4px 8px",borderRadius:6,fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer",
                                  border:`1px solid ${on?"#a371f7":"#23405f"}`,background:on?"rgba(163,113,247,0.15)":"transparent",
                                  color:on?"#a371f7":"#5a728f"}}>{l}</button>
                            );
                          })}
                        </div>
                      )}
                      {vpsCfg.nse_spread_mode&&(
                        <div style={{fontSize:9,color:"#5a728f",marginTop:6,lineHeight:1.5}}>Legs placed as marketable limits (~2% through) so they fill instead of resting — fewer cancellations.</div>
                      )}
                    </div>
                  )}

                  {/* Min signal strength (Equity + MCX) */}
                  {(vpsCfg.kite_enabled || vpsCfg.mcx_enabled) && (
                    <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                      <span style={{fontSize:10,color:"#8ea2bf"}}>Equity+MCX min strength — trade ⭐≥</span>
                      {[7,8,9,10].map(n=>{
                        const on=(Number(vpsCfg.min_signal_strength)||8)===n;
                        return (
                          <button key={n} onClick={()=>saveVps({min_signal_strength:n})}
                            style={{padding:"4px 10px",borderRadius:16,fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer",
                              border:`1px solid ${on?"#2ed47a":"#23405f"}`,background:on?"rgba(46,212,122,0.15)":"transparent",
                              color:on?"#2ed47a":"#5a728f"}}>{n}</button>
                        );
                      })}
                    </div>
                  )}

                  {/* Dynamic TP for STRONG signals — let winners run on the trailing SL */}
                  <div style={{marginTop:2,padding:"7px 9px",background:"#0b1624",border:"1px solid #1a2c44",borderRadius:6}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:6,flexWrap:"wrap"}}>
                      <span style={{fontSize:10,color:"#8ea2bf"}}>🚀 Dynamic TP on strong signals (let winners run)</span>
                      <button onClick={()=>saveVps({dynamic_tp_strong:!(vpsCfg.dynamic_tp_strong!==false)})}
                        style={{padding:"4px 12px",borderRadius:16,fontFamily:"inherit",fontSize:10,fontWeight:800,cursor:"pointer",
                          border:`1px solid ${(vpsCfg.dynamic_tp_strong!==false)?"#2ed47a":"#23405f"}`,
                          background:(vpsCfg.dynamic_tp_strong!==false)?"rgba(46,212,122,0.15)":"transparent",
                          color:(vpsCfg.dynamic_tp_strong!==false)?"#2ed47a":"#5a728f"}}>
                        {(vpsCfg.dynamic_tp_strong!==false)?"● ON":"○ OFF"}
                      </button>
                    </div>
                    <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap",marginTop:6}}>
                      <span style={{fontSize:10,color:"#8ea2bf"}}>STRONG when ⭐≥</span>
                      {[8,9,10,11].map(n=>{
                        const on=(Number(vpsCfg.strong_signal_score)||9)===n;
                        return (
                          <button key={n} onClick={()=>saveVps({strong_signal_score:n})}
                            style={{padding:"4px 10px",borderRadius:16,fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer",
                              border:`1px solid ${on?"#a371f7":"#23405f"}`,background:on?"rgba(163,113,247,0.18)":"transparent",
                              color:on?"#a371f7":"#5a728f"}}>{n}</button>
                        );
                      })}
                    </div>
                    <div style={{fontSize:9,color:"#5a728f",marginTop:5,lineHeight:1.5}}>Strong trades don't book the fixed TP — the TP locks in as a hard floor and the trailing SL rides for max profit. Equity (MIS), MCX, options &amp; Delta; GTT/CNC keep fixed TP.</div>
                  </div>

                  {/* Delta (crypto) timeframe — 5m / 15m / 1h */}
                  {vpsCfg.delta_enabled && (
                    <div style={{marginTop:2}}>
                      {/* Delta trade mode — SEPARATE from the NSE/MCX master switch */}
                      <div style={{fontSize:10,color:"#8ea2bf",marginBottom:4}}>Δ Delta trade mode (own switch — not tied to NSE/MCX)</div>
                      <div style={{display:"flex",gap:6,marginBottom:6}}>
                        <button onClick={()=>saveVps({delta_paper_trade:!vpsCfg.delta_paper_trade, delta_auto_trade:!vpsCfg.delta_paper_trade?false:vpsCfg.delta_auto_trade})}
                          style={{flex:1,padding:"7px",borderRadius:6,fontFamily:"inherit",fontSize:11,fontWeight:800,cursor:"pointer",
                            border:`1px solid ${vpsCfg.delta_paper_trade?"#60a5fa":"#23405f"}`,background:vpsCfg.delta_paper_trade?"rgba(96,165,250,0.14)":"transparent",
                            color:vpsCfg.delta_paper_trade?"#60a5fa":"#5a728f"}}>
                          {vpsCfg.delta_paper_trade?"📝 PAPER ON":"📝 Paper OFF"}
                        </button>
                        <button onClick={()=>saveVps({delta_auto_trade:!vpsCfg.delta_auto_trade, delta_paper_trade:!vpsCfg.delta_auto_trade?false:vpsCfg.delta_paper_trade})}
                          style={{flex:1,padding:"7px",borderRadius:6,fontFamily:"inherit",fontSize:11,fontWeight:800,cursor:"pointer",
                            border:`1px solid ${vpsCfg.delta_auto_trade?"#ff5b6c":"#23405f"}`,background:vpsCfg.delta_auto_trade?"rgba(248,81,73,0.18)":"transparent",
                            color:vpsCfg.delta_auto_trade?"#ff5b6c":"#5a728f"}}>
                          {vpsCfg.delta_auto_trade?"🤖 AUTO ON":"🤖 Auto OFF"}
                        </button>
                      </div>
                      <div style={{fontSize:9,marginBottom:8,padding:"6px 8px",borderRadius:6,
                        background:vpsCfg.delta_auto_trade?"rgba(248,81,73,0.07)":vpsCfg.delta_paper_trade?"rgba(96,165,250,0.07)":"#0f1d30",
                        border:`1px solid ${vpsCfg.delta_auto_trade?"rgba(248,81,73,0.2)":vpsCfg.delta_paper_trade?"rgba(96,165,250,0.25)":"#23405f"}`,
                        color:vpsCfg.delta_auto_trade?"#ff8a95":vpsCfg.delta_paper_trade?"#60a5fa":"#8ea2bf"}}>
                        {vpsCfg.delta_auto_trade?"⚠ LIVE — real Delta orders placed automatically on GO."
                          :vpsCfg.delta_paper_trade?"📝 PAPER — every GO signal tracked (strength gate is LIVE-only). Unlimited — never uses your live cap."
                          :"Alerts only — no paper or live trades recorded. Turn on Paper to start tracking."}
                      </div>
                      <div style={{fontSize:10,color:"#8ea2bf",marginBottom:4}}>Δ Delta timeframe · top 5 crypto (BTC ETH SOL BNB XRP)</div>
                      <div style={{display:"flex",gap:6}}>
                        {["5m","15m","1h"].map(tf=>{
                          const on=(vpsCfg.delta_timeframe||"15m")===tf;
                          return (
                            <button key={tf} onClick={()=>saveVps({delta_timeframe:tf})}
                              style={{flex:1,padding:"6px",borderRadius:6,fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer",
                                border:`1px solid ${on?"#a371f7":"#23405f"}`,background:on?"rgba(163,113,247,0.15)":"transparent",
                                color:on?"#a371f7":"#5a728f"}}>{tf}</button>
                          );
                        })}
                      </div>
                      {/* Buy options vs futures */}
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:8}}>
                        <div>
                          <div style={{fontSize:11,color:"#e7edf5",fontWeight:700}}>Buy options (no liquidation)</div>
                          <div style={{fontSize:9,color:"#8ea2bf"}}>
                            {(vpsCfg.delta_trade_options!==false)?"BUY→call, SELL→put · BTC/ETH · uses margin":"Trades leveraged futures (liquidation risk)"}
                          </div>
                        </div>
                        <button onClick={()=>saveVps({delta_trade_options:!(vpsCfg.delta_trade_options!==false)})}
                          style={{background:(vpsCfg.delta_trade_options!==false)?"rgba(46,212,122,0.15)":"rgba(255,91,108,0.08)",
                            border:`1px solid ${(vpsCfg.delta_trade_options!==false)?"#2ed47a":"#ff5b6c"}`,borderRadius:20,
                            padding:"4px 12px",color:(vpsCfg.delta_trade_options!==false)?"#2ed47a":"#ff8a95",
                            fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                          {(vpsCfg.delta_trade_options!==false)?"● OPTIONS":"○ FUTURES"}
                        </button>
                      </div>
                      {/* Per-coin instrument override: BTC / ETH → option or future */}
                      <div style={{display:"flex",gap:8,marginTop:6,flexWrap:"wrap"}}>
                        {[["BTC","delta_btc_instrument"],["ETH","delta_eth_instrument"]].map(([coin,key])=>{
                          const _dflt=(vpsCfg.delta_trade_options!==false)?"option":"future";
                          const isOpt=((vpsCfg[key]||_dflt)+"").toLowerCase().startsWith("opt");
                          return (
                            <button key={coin} onClick={()=>saveVps({[key]:isOpt?"future":"option"})}
                              style={{flex:1,minWidth:120,padding:"6px 10px",borderRadius:8,cursor:"pointer",
                                fontFamily:"inherit",fontSize:10,fontWeight:700,
                                background:isOpt?"rgba(46,212,122,0.12)":"rgba(232,179,57,0.12)",
                                border:`1px solid ${isOpt?"#2ed47a":"#e8b339"}`,color:isOpt?"#2ed47a":"#e8b339"}}>
                              {coin}: {isOpt?"OPTION":"FUTURE"}
                            </button>
                          );
                        })}
                      </div>
                      <div style={{fontSize:9,color:"#5a728f",marginTop:4,lineHeight:1.5}}>
                        Per-coin for BTC/ETH. OPTION = defined risk, no liquidation. FUTURE = perpetual (leverage/liquidation risk; needed for the BTC Diwali fee cashback).
                      </div>
                      {(vpsCfg.delta_trade_options!==false) && (
                        <div style={{display:"flex",gap:6,marginTop:6,alignItems:"center",flexWrap:"wrap"}}>
                          <span style={{fontSize:10,color:"#8ea2bf"}}>Expiry (Delta = daily; use Nearest):</span>
                          {[[0,"Nearest"],[7,"7d+"],[14,"14d+"]].map(([d,l])=>{
                            const on=(vpsCfg.delta_option_min_days||0)===d;
                            return (
                              <button key={d} onClick={()=>saveVps({delta_option_min_days:d})}
                                style={{padding:"4px 10px",borderRadius:16,fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer",
                                  border:`1px solid ${on?"#a371f7":"#23405f"}`,background:on?"rgba(163,113,247,0.15)":"transparent",
                                  color:on?"#a371f7":"#5a728f"}}>{l}</button>
                            );
                          })}
                        </div>
                      )}
                      {/* Balance % per trade + max trades/day */}
                      <div style={{display:"flex",gap:6,marginTop:6,alignItems:"center",flexWrap:"wrap"}}>
                        <span style={{fontSize:10,color:"#8ea2bf"}}>Balance % / trade:</span>
                        {[10,25,50,100].map(m=>{
                          const on=(vpsCfg.delta_margin_pct||10)===m;
                          return (
                            <button key={m} onClick={()=>saveVps({delta_margin_pct:m})}
                              style={{padding:"4px 8px",borderRadius:16,fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer",
                                border:`1px solid ${on?"#e8b339":"#23405f"}`,background:on?"rgba(232,179,57,0.15)":"transparent",
                                color:on?"#e8b339":"#5a728f"}}>{m}%</button>
                          );
                        })}
                      </div>
                      <div style={{display:"flex",gap:6,marginTop:6,alignItems:"center",flexWrap:"wrap"}}>
                        <span style={{fontSize:10,color:"#8ea2bf"}}>Max trades/day:</span>
                        {[3,5,10,20].map(n=>{
                          const on=(vpsCfg.delta_max_trades||3)===n;
                          return (
                            <button key={n} onClick={()=>saveVps({delta_max_trades:n})}
                              style={{padding:"4px 8px",borderRadius:16,fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer",
                                border:`1px solid ${on?"#2ed47a":"#23405f"}`,background:on?"rgba(46,212,122,0.15)":"transparent",
                                color:on?"#2ed47a":"#5a728f"}}>{n}</button>
                          );
                        })}
                      </div>
                      <div style={{display:"flex",gap:6,marginTop:6,alignItems:"center",flexWrap:"wrap"}}>
                        <span style={{fontSize:10,color:"#8ea2bf"}}>Min strength — trade ⭐≥</span>
                        {[6,7,8,9].map(n=>{
                          const on=(Number(vpsCfg.delta_min_strength)||7)===n;
                          return (
                            <button key={n} onClick={()=>saveVps({delta_min_strength:n})}
                              style={{padding:"4px 10px",borderRadius:16,fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer",
                                border:`1px solid ${on?"#49c2e4":"#23405f"}`,background:on?"rgba(73,194,228,0.15)":"transparent",
                                color:on?"#49c2e4":"#5a728f"}}>{n}</button>
                          );
                        })}
                      </div>
                      <div style={{fontSize:9,color:"#5a728f",marginTop:4,lineHeight:1.5}}>
                        Buying options = you pay the premium (defined risk, no liquidation). Sizing uses this % of your Delta balance; Delta's 50/100/200× leverage applies to futures/selling, not long options.
                      </div>
                    </div>
                  )}

                  {/* Master auto-trade — applies to the VPS */}
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                    marginTop:4,paddingTop:8,borderTop:"1px solid #1a2c44"}}>
                    <div>
                      <div style={{fontSize:12,fontWeight:800,color:vpsCfg.auto_trade?"#ff8a95":"#e7edf5"}}>🤖 Auto-Trade on VPS</div>
                      <div style={{fontSize:9,color:"#8ea2bf"}}>
                        {vpsCfg.auto_trade?"Places REAL orders on validated GO + sets TP/SL":"Scan + validate + alert only"}
                      </div>
                    </div>
                    <button onClick={()=>saveVps({auto_trade:!vpsCfg.auto_trade})}
                      style={{background:vpsCfg.auto_trade?"rgba(248,81,73,0.2)":"transparent",
                        border:`1px solid ${vpsCfg.auto_trade?"#ff5b6c":"#23405f"}`,borderRadius:20,
                        padding:"5px 16px",color:vpsCfg.auto_trade?"#ff5b6c":"#8ea2bf",
                        fontFamily:"inherit",fontSize:11,fontWeight:800,cursor:"pointer"}}>
                      {vpsCfg.auto_trade?"● ON":"○ OFF"}
                    </button>
                  </div>

                  {/* Paper mode — record trades on VPS, place no real orders */}
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <div>
                      <div style={{fontSize:12,fontWeight:700,color:vpsCfg.paper_trade?"#60a5fa":"#e7edf5"}}>📝 Paper mode</div>
                      <div style={{fontSize:9,color:"#8ea2bf"}}>
                        {vpsCfg.paper_trade?"Records paper trades on the VPS (no real orders)":"Off = live orders when Auto-Trade is on"}
                      </div>
                    </div>
                    <button onClick={()=>saveVps({paper_trade:!vpsCfg.paper_trade})}
                      style={{background:vpsCfg.paper_trade?"rgba(96,165,250,0.2)":"transparent",
                        border:`1px solid ${vpsCfg.paper_trade?"#60a5fa":"#23405f"}`,borderRadius:20,
                        padding:"5px 16px",color:vpsCfg.paper_trade?"#60a5fa":"#8ea2bf",
                        fontFamily:"inherit",fontSize:11,fontWeight:800,cursor:"pointer"}}>
                      {vpsCfg.paper_trade?"● ON":"○ OFF"}
                    </button>
                  </div>

                  {/* Validation pipelines */}
                  <div style={{display:"flex",gap:6,marginTop:2}}>
                    <button onClick={()=>saveVps({tech_validate:!(vpsCfg.tech_validate!==false)})}
                      style={{flex:1,padding:"6px",borderRadius:6,fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
                        border:`1px solid ${(vpsCfg.tech_validate!==false)?"#2ed47a":"#23405f"}`,
                        background:(vpsCfg.tech_validate!==false)?"rgba(46,212,122,0.12)":"transparent",
                        color:(vpsCfg.tech_validate!==false)?"#2ed47a":"#8ea2bf"}}>
                      {(vpsCfg.tech_validate!==false)?"✓ ":""}Tech+Validate
                    </button>
                    <button onClick={()=>saveVps({ai_validate:!vpsCfg.ai_validate})}
                      style={{flex:1,padding:"6px",borderRadius:6,fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
                        border:`1px solid ${vpsCfg.ai_validate?"#a78bfa":"#23405f"}`,
                        background:vpsCfg.ai_validate?"rgba(163,113,247,0.15)":"transparent",
                        color:vpsCfg.ai_validate?"#a78bfa":"#8ea2bf"}}>
                      {vpsCfg.ai_validate?"✓ ":""}AI+Validate
                    </button>
                  </div>

                  {/* Timeframe + min signals — control what the VPS scans/trades */}
                  <div style={{marginTop:4}}>
                    <div style={{fontSize:10,color:"#8ea2bf",marginBottom:4}}>Scan timeframe</div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                      {["5min","15min","30min","1hour","1day"].map(tf=>{
                        const on=(vpsCfg.kite_timeframe||"15min")===tf;
                        return (
                          <button key={tf} onClick={()=>saveVps({kite_timeframe:tf, mcx_timeframe:tf})}
                            style={{padding:"5px 10px",borderRadius:16,fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer",
                              border:`1px solid ${on?"#49c2e4":"#23405f"}`,background:on?"rgba(73,194,228,0.15)":"transparent",
                              color:on?"#49c2e4":"#5a728f"}}>{tf}</button>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{marginTop:6}}>
                    <div style={{fontSize:10,color:"#8ea2bf",marginBottom:4}}>Min scan signals to trade</div>
                    <div style={{display:"flex",gap:6}}>
                      {[1,2,3,4].map(n=>{
                        const on=(vpsCfg.kite_min_signals||2)===n;
                        return (
                          <button key={n} onClick={()=>saveVps({kite_min_signals:n})}
                            style={{flex:1,padding:"6px",borderRadius:6,fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer",
                              border:`1px solid ${on?"#e8b339":"#23405f"}`,background:on?"rgba(232,179,57,0.15)":"transparent",
                              color:on?"#e8b339":"#5a728f"}}>{n}</button>
                        );
                      })}
                    </div>
                    <div style={{fontSize:9,color:"#5a728f",marginTop:3}}>Lower = more trades (1–2 recommended). Higher = stricter.</div>
                  </div>

                  {/* Trade options (CE/PE) vs the stock itself */}
                  {vpsCfg.auto_trade && (
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:2}}>
                      <div>
                        <div style={{fontSize:11,color:"#e7edf5",fontWeight:700}}>Buy options (CE/PE)</div>
                        <div style={{fontSize:9,color:"#8ea2bf"}}>
                          {(vpsCfg.nse_trade_options!==false)?"BUY→ATM CE, SELL→ATM PE · exit on underlying TP/SL":"Trades the stock itself"}
                        </div>
                      </div>
                      <button onClick={()=>saveVps({nse_trade_options:!(vpsCfg.nse_trade_options!==false)})}
                        style={{background:(vpsCfg.nse_trade_options!==false)?"rgba(46,212,122,0.15)":"transparent",
                          border:`1px solid ${(vpsCfg.nse_trade_options!==false)?"#2ed47a":"#23405f"}`,borderRadius:20,
                          padding:"4px 14px",color:(vpsCfg.nse_trade_options!==false)?"#2ed47a":"#8ea2bf",
                          fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                        {(vpsCfg.nse_trade_options!==false)?"● ON":"○ OFF"}
                      </button>
                    </div>
                  )}

                  {/* Equity product — only when trading the stock (options use NRML) */}
                  {vpsCfg.auto_trade && (vpsCfg.nse_trade_options===false) && (
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:2}}>
                      <span style={{fontSize:11,color:"#8ea2bf"}}>Equity product</span>
                      <div style={{display:"flex",border:"1px solid #23405f",borderRadius:6,overflow:"hidden"}}>
                        {["MIS","CNC"].map(p=>(
                          <button key={p} onClick={()=>saveVps({equity_product:p})}
                            style={{padding:"4px 12px",border:"none",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer",
                              background:(vpsCfg.equity_product||"MIS")===p?"rgba(0,212,170,0.2)":"transparent",
                              color:(vpsCfg.equity_product||"MIS")===p?"#00d4aa":"#5a728f"}}>{p}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {vpsCfg.auto_trade && (vpsCfg.nse_trade_options===false) && (vpsCfg.equity_product||"MIS")==="MIS" && (
                    <div style={{fontSize:9,color:"#e8b339"}}>MIS = intraday, TP/SL tracked server-side (no GTT). Use CNC for GTT OCO.</div>
                  )}

                  {/* Server truth — confirms what the VPS is actually running */}
                  {vpsStatus&&(
                    <div style={{fontSize:9,marginTop:4,padding:"5px 7px",background:"#0b1624",borderRadius:6,
                      color:"#49c2e4",lineHeight:1.6}}>
                      ⚙️ VPS is running: <b>{vpsStatus.timeframe||"?"}</b> · min <b>{vpsStatus.min_signals}</b> signal
                      {(vpsStatus.timeframe && (vpsStatus.timeframe!==(vpsCfg.kite_timeframe||"15min")))
                        ? " · ⚠ not saved yet — tap Save" : ""}
                      <div style={{color:"#5a728f",fontWeight:400}}>
                        (This is separate from the Signals-tab Quick Scan timeframe.)
                      </div>
                    </div>
                  )}

                  {/* Per-market last-scan times (NSE · MCX · Delta) — Delta runs 24/7 */}
                  {vpsStatus&&(()=>{
                    const fmt=(ts)=>(ts&&ts>0)?new Date(ts*1000).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"}):"—";
                    const ago=(ts)=>{ if(!ts||ts<=0||!vpsStatus.server_now) return ""; const m=Math.max(0,Math.round((vpsStatus.server_now-ts)/60)); return m<1?" (now)":` (${m}m)`; };
                    const rows=[];
                    if(vpsCfg.kite_enabled)  rows.push(["📡 NSE",fmt(vpsStatus.last_kite_scan),"#2ed47a"]);
                    if(vpsCfg.mcx_enabled)   rows.push(["🛢️ MCX",fmt(vpsStatus.last_mcx_scan),"#e8b339"]);
                    if(vpsCfg.delta_enabled) rows.push(["Δ Delta",fmt(vpsStatus.delta_last_scan)+ago(vpsStatus.delta_last_scan),"#49c2e4"]);
                    return rows.length?(
                      <div style={{fontSize:10,marginTop:6,padding:"6px 8px",background:"#0b1624",borderRadius:6,display:"flex",flexWrap:"wrap",gap:10}}>
                        <span style={{color:"#5a728f"}}>Last scan:</span>
                        {rows.map(([l,t,c])=>(<span key={l} style={{color:c,fontWeight:700}}>{l} <span style={{color:"#8ea2bf",fontWeight:400}}>{t}</span></span>))}
                      </div>
                    ):null;
                  })()}

                  {/* Status line */}
                  <div style={{fontSize:9,color:"#5a728f",marginTop:4,lineHeight:1.5}}>
                    NSE: {(vpsCfg.kite_watchlist||[]).length>0?`${(vpsCfg.kite_watchlist||[]).length} custom`:"all F&O (default)"}
                    {" · "}MCX: {(vpsCfg.mcx_watchlist||[]).length>0?`${(vpsCfg.mcx_watchlist||[]).length} custom`:"14 commodities (default)"}
                  </div>

                  {/* Zerodha session expired warning — #1 reason for "no scans today" */}
                  {vpsStatus&&vpsStatus.kite_session_ok===false&&(
                    <div style={{marginTop:6,padding:"7px 9px",background:"rgba(255,91,108,0.12)",
                      border:"1px solid #ff5b6c",borderRadius:6,color:"#ff5b6c",fontWeight:700,fontSize:11}}>
                      ⚠️ Zerodha session expired — scanner is idle. Log in to Kite to resume today's scan / auto-trade.
                    </div>
                  )}

                  {/* SELL CE/PE daily index ideas — alerts only, never auto-trades */}
                  <div style={{marginTop:8,paddingTop:8,borderTop:"1px solid #1a2c44"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                      <span style={{fontSize:12,fontWeight:800,color:"#e8b339"}}>💰 Sell CE/PE Daily Ideas</span>
                      <button onClick={()=>saveVps({sell_alerts_enabled:!vpsCfg.sell_alerts_enabled})}
                        style={{background:vpsCfg.sell_alerts_enabled?"rgba(232,179,57,0.2)":"transparent",
                          border:`1px solid ${vpsCfg.sell_alerts_enabled?"#e8b339":"#23405f"}`,borderRadius:20,
                          padding:"4px 14px",color:vpsCfg.sell_alerts_enabled?"#e8b339":"#8ea2bf",
                          fontFamily:"inherit",fontSize:10,fontWeight:800,cursor:"pointer"}}>
                        {vpsCfg.sell_alerts_enabled?"● ON":"○ OFF"}
                      </button>
                    </div>
                    <div style={{fontSize:9,color:"#8ea2bf",marginTop:4,lineHeight:1.5}}>
                      Daily 09:40 IST: OTM strike to <b>sell</b> for NIFTY/BANKNIFTY/FINNIFTY/MIDCPNIFTY/SENSEX (nearest weekly), beyond the IV expected-move, ~{vpsCfg.sell_min_roi_pct||3}% ROI/wk on margin, with IV + OI + AI news read. <b style={{color:"#e8b339"}}>Idea only — never auto-trades.</b>
                    </div>
                    <VpsSellIdeas />
                  </div>

                  {/* Telegram creds + test */}
                  <div style={{marginTop:6,paddingTop:8,borderTop:"1px solid #1a2c44"}}>
                    <div style={{fontSize:10,fontWeight:700,color:"#49c2e4",marginBottom:4}}>🔔 Telegram for VPS alerts</div>
                    <input placeholder="Bot token" value={(vpsCfg.kite_tg_token)||""}
                      onChange={e=>setVpsCfg(p=>({...p,kite_tg_token:e.target.value}))}
                      style={{width:"100%",boxSizing:"border-box",background:"#0f1d30",border:"1px solid #23405f",
                        borderRadius:6,padding:"6px 8px",color:"#e7edf5",fontFamily:"inherit",fontSize:11}}/>
                    <input placeholder="Chat ID" value={(vpsCfg.kite_tg_chat)||""}
                      onChange={e=>setVpsCfg(p=>({...p,kite_tg_chat:e.target.value}))}
                      style={{width:"100%",boxSizing:"border-box",background:"#0f1d30",border:"1px solid #23405f",
                        borderRadius:6,padding:"6px 8px",color:"#e7edf5",fontFamily:"inherit",fontSize:11,marginTop:4}}/>
                    <div style={{display:"flex",gap:6,marginTop:6}}>
                      <button onClick={()=>saveVps({kite_tg_token:vpsCfg.kite_tg_token||"",kite_tg_chat:vpsCfg.kite_tg_chat||""})}
                        style={{flex:1,padding:"7px",borderRadius:6,border:"1px solid #23405f",background:"rgba(0,212,170,0.1)",
                          color:"#00d4aa",fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer"}}>Save</button>
                      <button onClick={testVpsTg} disabled={tgTesting}
                        style={{flex:1,padding:"7px",borderRadius:6,border:"1px solid rgba(73,194,228,0.4)",background:"rgba(73,194,228,0.1)",
                          color:"#49c2e4",fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                        {tgTesting?"Sending…":"Send test"}</button>
                    </div>
                    {tgTestMsg&&<div style={{fontSize:9,marginTop:5,color:tgTestMsg.startsWith("✅")?"#2ed47a":"#ff5b6c"}}>{tgTestMsg}</div>}
                  </div>

                  {/* Live TP/SL tracker */}
                  <div style={{marginTop:6,paddingTop:8,borderTop:"1px solid #1a2c44"}}>
                    <VpsTpSlTracker authed={authed} />
                  </div>

                  {/* Paper trades log (mobile has no Virtual tab) */}
                  <div style={{marginTop:6,paddingTop:8,borderTop:"1px solid #1a2c44"}}>
                    <VpsPaperLog authed={authed} />
                  </div>
                </div>
              )}
            </div>

  );
}

const MobileApp = memo(({
  uid, userRole, fbRecord,
  authed, onLogout,
  scanResults=[], livePositions=[],
  livePrices={}, indexQuotes={},
  onQuickScan, scanning=false,
  onTrade, showToast,
  atEnabled=false, onToggleAT=null,
  pendingAlerts=0,
}) => {
  const [mTab,       setMTab]      = useState("signals");
  const [notifPerm,  setNotifPerm] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [pushEnabled, setPushEnabled] = useState(!!localStorage.getItem("fcm_token"));

  // ── Scan settings (persisted) ──────────────────────────────────────────────
  const [mActiveScan, setMActiveScan] = useLocalStorage('mob_active_scans', MOB_ALL_SCANS);
  const [mMinSignals, setMMinSignals] = useLocalStorage('mob_min_signals', 1);
  // Scan timeframe — shared with desktop via the same 'eq_tf_unified' key, so the
  // Quick Scan uses whatever is selected here.
  const [mTimeframe, setMTimeframe] = useLocalStorage('eq_tf_unified', '15min');

  // ── Market selector ─────────────────────────────────────────────────────────
  // "NSE" = equity / F&O options  |  "MCX" = commodity futures
  const [mMarket, setMMarket] = useLocalStorage('mob_market', 'NSE');

  // ── Auto-scan — re-run Quick Scan every 3 min during market hours ───────────
  const [mAutoScan, setMAutoScan] = useLocalStorage('mob_autoscan', false);
  const onScanRef = useRef(null);

  // ── Order mode selector ────────────────────────────────────────────────────
  // "MIS" = equity intraday  |  "FNO" = options/futures NRML
  const [mOrderMode, setMOrderMode] = useLocalStorage('mob_order_mode', 'MIS');

  // ── Mobile Auto Trade ──────────────────────────────────────────────────────
  const [mAtIntra, setMAtIntra] = useLocalStorage('mob_at_intra', false);
  const [mAtFno,   setMAtFno]   = useLocalStorage('mob_at_fno',   false);
  const [mAtLog,   setMAtLog]   = useState([]);  // recent AT activity
  const prevScanRef = useRef(null);
  const [fnoBusy, setFnoBusy] = useState(null);  // sig._key currently resolving an ATM option

  // ── Pre-entry validation (mobile) ──────────────────────────────────────────
  const [mValidations, setMValidations] = useState({});   // symbol -> validate result
  const [mValidating,  setMValidating]  = useState(false);


  // ── Resolve the ATM option contract for an F&O order (mirrors desktop) ──────
  // BUY signal  → buy ATM CE ;  SELL signal → buy ATM PE.
  // Calls /fo/find_atm which picks the ATM strike by liquidity, then opens the
  // order ticket pre-filled with the real NFO contract, strike and premium.
  const handleFnoTrade = async (sig) => {
    if(!onTrade) return;
    const isBuy = sig.type === "BUY";
    setFnoBusy(sig._key);
    try {
      const res = await apiFetch("/fo/find_atm", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          symbol:       sig.symbol,
          exchange:     "NSE",
          signal_side:  isBuy ? "BUY" : "SELL",
          option_type:  isBuy ? "CE"  : "PE",
          min_days:     7,
          preferred_strike: null,
          action:       "BUY",     // we buy the option to express the view
          target_expiry: "",
        })
      });
      if(!res.found) {
        showToast?.(`No ${isBuy?"CE":"PE"} contract found: ${res.reason||"check expiry"}`, "error");
        return;
      }
      const contract = res.contract || {};
      const optLtp = parseFloat(contract.option_ltp || 0);
      const optOI  = parseInt(contract.option_oi || 0);
      const undLtp = parseFloat(contract.underlying_ltp || 0);
      if(optLtp <= 0 && optOI <= 0) {
        // If the underlying price is also 0, the broker session can't fetch live
        // quotes (Kite access token expires every morning) — tell the user to re-login.
        if(undLtp <= 0)
          showToast?.("Live prices unavailable — re-login to Zerodha (Kite token likely expired).", "error");
        else
          showToast?.(`${contract.tradingsymbol}: no price/liquidity — strike ${contract.strike} too far OTM`, "error");
        return;
      }
      onTrade({
        symbol:          contract.tradingsymbol,
        exchange:        contract.exchange || "NFO",
        side:            "BUY",
        price:           optLtp,
        qty:             contract.lot_size || 1,
        product:         "NRML",
        instrumentType:  isBuy ? "CE" : "PE",
        suggestedStrike: contract.strike,
        contractInfo:    contract,
        source:          "mob_fno",
      });
    } catch(e) {
      showToast?.("F&O lookup failed: " + e.message, "error");
    } finally {
      setFnoBusy(null);
    }
  };

  // ── Resolve the active MCX futures contract for an order ────────────────────
  // MCX is traded as futures (e.g. CRUDEOIL25JULFUT). Resolves the near-month
  // contract (skips <7d expiry) via /fo/futures-symbol, then opens the ticket.
  const handleMcxTrade = async (sig, product) => {   // product: "MIS" | "NRML"
    if(!onTrade) return;
    const busyKey = `mcx:${sig._key}:${product}`;
    setFnoBusy(busyKey);
    try {
      const res = await apiFetch("/fo/futures-symbol", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ symbol: sig.symbol, exchange: "MCX" })
      });
      if(!res.found) {
        showToast?.(`No MCX futures for ${sig.symbol}: ${res.reason||"check symbol"}`, "error");
        return;
      }
      onTrade({
        symbol:         res.tradingsymbol,
        exchange:       res.exchange || "MCX",
        side:           sig.type,
        price:          res.ltp || sig.price || 0,
        qty:            res.lot_size || 1,
        product:        product,
        instrumentType: "FUT",
        source:         "mob_mcx",
      });
    } catch(e) {
      showToast?.("MCX lookup failed: " + e.message, "error");
    } finally {
      setFnoBusy(null);
    }
  };

  // ── Place a MARKET order directly (used by auto-trade for hands-off firing) ──
  const placeDirect = async ({tradingsymbol, exchange, side, qty, product}) => {
    return apiFetch("/order/place", {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({
        tradingsymbol, exchange,
        transaction_type: side,
        quantity:         qty,
        order_type:       "MARKET",
        product,
        market_protection: 0.5,
        tag:               "RABT-MOB-AT",
      })
    });
  };

  const isAdmin  = userRole === "admin";
  const canTrade = userRole === "admin" || userRole === "trader";

  // ── Group signals by symbol+type, filter by active scans + min signals ─────
  const allSignals = useMemo(()=>{
    const map = {};
    (scanResults||[]).forEach(r=>{
      const filtSigs = (r.signals||[]).filter(s => (mActiveScan||MOB_ALL_SCANS).includes(s.scan));
      if(!filtSigs.length) return;
      filtSigs.forEach(sig=>{
        const key = `${r.symbol}-${sig.type}`;
        if(!map[key]) map[key] = {
          symbol:   r.symbol,    type:     sig.type,
          price:    r.ltp || 0,  score:    r.compositeScore || 0,
          oiPCR:    r.oiPCR || 1, oiSignal: r.oiSignal || "NEUTRAL",
          bulkScore:r.bulkScore || 0,
          scans: [], boosted:false, bulkBoost:false, oiBoost:false, _key:key,
        };
        map[key].scans.push(MOB_SCAN_LABELS[sig.scan] || `#${sig.scan}`);
        if(sig.boosted)   map[key].boosted   = true;
        if(sig.bulkBoost) map[key].bulkBoost = true;
        if(sig.oiBoost)   map[key].oiBoost   = true;
      });
    });
    // Only show signals for the selected market: MCX mode shows commodities,
    // NSE mode shows everything else. Prevents stale NSE cards lingering in MCX.
    const MCX_SET = new Set(MCX_SYMBOLS.map(s=>s.toUpperCase()));
    const inMarket = (sym)=> mMarket==="MCX"
      ? MCX_SET.has((sym||"").toUpperCase())
      : !MCX_SET.has((sym||"").toUpperCase());
    return Object.values(map)
      .filter(s => s.scans.length >= (mMinSignals || 1))
      .filter(s => inMarket(s.symbol))
      .sort((a,b)=>{
        const d = Math.abs(b.score) - Math.abs(a.score);
        return d !== 0 ? d : (a.type==="BUY" ? -1 : 1);
      });
  },[scanResults, mActiveScan, mMinSignals, mMarket]);

  // ── Validate all current signals: GO / WAIT / SKIP per symbol ──────────────
  const runMValidateAll = useCallback(async ()=>{
    if(allSignals.length===0){ showToast?.("Run Quick Scan first", "error"); return; }
    setMValidating(true);
    try{
      const items = allSignals.map(s=>({ symbol:s.symbol, exchange:mMarket,
        timeframe:mTimeframe, direction:s.type, signal_price:s.price||null }));
      const out = await apiFetch("/scan/validate/batch", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ items }),
      });
      const map={}; (out.results||[]).forEach(r=>{ if(!r.error) map[r.symbol]=r; });
      setMValidations(map);
      const go=Object.values(map).filter(r=>r.verdict==="GO").length;
      showToast?.(`Validated ${Object.keys(map).length} · ${go} GO`, "ok");
    }catch(e){ showToast?.("Validate failed: "+(e.message||""), "error"); }
    finally{ setMValidating(false); }
  }, [allSignals, mMarket, mTimeframe, showToast]);

  // ── Mobile Auto Trade: fires when scanResults changes and AT is on ─────────
  useEffect(()=>{
    if(!canTrade) return;
    if(!mAtIntra && !mAtFno) return;
    if(scanResults === prevScanRef.current || !scanResults?.length) return;
    prevScanRef.current = scanResults;

    const buySignals  = allSignals.filter(s=>s.type==="BUY");
    const sellSignals = allSignals.filter(s=>s.type==="SELL");
    const toFire = [...buySignals, ...sellSignals].slice(0, 3); // cap 3 auto-orders per scan

    toFire.forEach(async(sig)=>{
      try {
        if(mMarket==="MCX") {
          // MCX auto-trade: resolve the active futures contract and place a
          // MARKET order DIRECTLY (hands-off). MIS if intraday AT, else NRML.
          const res = await apiFetch("/fo/futures-symbol",{
            method:"POST", headers:{"Content-Type":"application/json"},
            body: JSON.stringify({ symbol: sig.symbol, exchange: "MCX" })
          });
          if(!res.found){ showToast?.(`AT-MCX: no futures for ${sig.symbol}`, "error"); return; }
          const prod = mAtIntra ? "MIS" : "NRML";
          await placeDirect({tradingsymbol:res.tradingsymbol, exchange:res.exchange||"MCX",
                             side:sig.type, qty:res.lot_size||1, product:prod});
          const msg = `🤖 AT-MCX ${prod} ${sig.type} ${res.tradingsymbol}`;
          setMAtLog(p=>[{msg,ts:Date.now()},...p].slice(0,10)); showToast?.(msg);
          return;
        }
        if(mAtIntra) {
          // Equity MIS — MARKET order placed directly with 0.5% protection
          await placeDirect({tradingsymbol:sig.symbol, exchange:"NSE",
                             side:sig.type, qty:1, product:"MIS"});
          const msg = `🤖 AT-MIS ${sig.type} ${sig.symbol} @ ₹${sig.price.toFixed(0)}`;
          setMAtLog(p=>[{msg,ts:Date.now()},...p].slice(0,10));
          showToast?.(msg);
        }
        if(mAtFno) {
          // F&O: resolve the ATM option, then place a MARKET NRML order DIRECTLY.
          const isBuy = sig.type === "BUY";
          const res = await apiFetch("/fo/find_atm",{
            method:"POST", headers:{"Content-Type":"application/json"},
            body: JSON.stringify({
              symbol: sig.symbol, exchange:"NSE",
              signal_side: isBuy?"BUY":"SELL", option_type: isBuy?"CE":"PE",
              min_days: 7, preferred_strike: null, action:"BUY", target_expiry:"",
            })
          });
          if(!res.found){ showToast?.(`AT-FNO: no ${isBuy?"CE":"PE"} for ${sig.symbol}`, "error"); return; }
          const ct = res.contract || {};
          const optLtp = parseFloat(ct.option_ltp||0), optOI = parseInt(ct.option_oi||0);
          if(optLtp<=0 && optOI<=0){ showToast?.(`AT-FNO: no live price for ${ct.tradingsymbol} — re-login to Kite`, "error"); return; }
          await placeDirect({tradingsymbol:ct.tradingsymbol, exchange:ct.exchange||"NFO",
                             side:"BUY", qty:ct.lot_size||1, product:"NRML"});
          const msg = `🤖 AT-FNO BUY ${ct.tradingsymbol} @ ₹${optLtp.toFixed(1)}`;
          setMAtLog(p=>[{msg,ts:Date.now()},...p].slice(0,10)); showToast?.(msg);
        }
      } catch(e) {
        showToast?.(`AT failed ${sig.symbol}: ${e.message}`, "error");
      }
    });
  },[scanResults]); // eslint-disable-line

  // ── Auto-scan: re-run Quick Scan every 3 min during market hours ────────────
  // Keep a ref to the latest onQuickScan so the interval isn't torn down on every
  // parent re-render (onQuickScan is a new function each render).
  useEffect(()=>{ onScanRef.current = onQuickScan; }, [onQuickScan]);
  useEffect(()=>{
    if(!mAutoScan) return;
    const tick = ()=>{ if(_isMarketOpenIST(mMarket)) onScanRef.current?.(); };
    const t0 = setTimeout(tick, 1500);          // first scan shortly after enabling
    const iv = setInterval(tick, 3*60*1000);    // then every 3 minutes
    return ()=>{ clearTimeout(t0); clearInterval(iv); };
  },[mAutoScan, mMarket]);

  // ── Enable push notifications ──────────────────────────────────────────────
  const enablePush = async () => {
    if(!uid) return;
    try {
      const perm = await Notification.requestPermission();
      setNotifPerm(perm);
      if(perm === "granted") { await _registerPush(uid); setPushEnabled(true); showToast?.("🔔 Push enabled!"); }
    } catch(e) { showToast?.("Push failed: "+e.message,"error"); }
  };

  // ── Position P&L summary ───────────────────────────────────────────────────
  const posSummary = useMemo(()=>{
    let totalPnl=0, count=0;
    (livePositions||[]).filter(p=>p.quantity!==0).forEach(p=>{
      const ltp = livePrices[p.tradingsymbol] || p.last_price || 0;
      totalPnl += (ltp - p.average_price) * p.quantity; count++;
    });
    return {totalPnl, count};
  },[livePositions, livePrices]);

  const niftyQ = indexQuotes["NSE:NIFTY 50"]  || indexQuotes["NSE:NIFTY50"]  || null;
  const bankQ  = indexQuotes["NSE:NIFTY BANK"] || indexQuotes["NSE:BANKNIFTY"]|| null;
  const clr    = (v)=> v>0?"#34d399":v<0?"#ff5b6c":"#8ea2bf";
  const fmtPnl = (v)=>`${v>=0?"+":""}₹${Math.abs(v).toFixed(0)}`;

  const buyCount  = allSignals.filter(s=>s.type==="BUY").length;
  const sellCount = allSignals.filter(s=>s.type==="SELL").length;

  return (
    <div style={{fontFamily:"'JetBrains Mono',monospace",background:"#07101c",
      height:"100dvh",display:"flex",flexDirection:"column",color:"#e8edf3",
      overflow:"hidden",WebkitFontSmoothing:"antialiased"}}>

      <style>{`
        * { box-sizing:border-box; -webkit-tap-highlight-color:transparent; }
        input,button,select { font-family:inherit; touch-action:manipulation; }
        ::-webkit-scrollbar { width:3px; }
        ::-webkit-scrollbar-thumb { background:#23405f; border-radius:2px; }
        @keyframes mobPulse { 0%,100%{opacity:1} 50%{opacity:.5} }
      `}</style>

      {/* ── TOP BAR ─────────────────────────────────────────────────────────── */}
      <div style={{background:"#0d1117",borderBottom:"1px solid #21262d",
        padding:"10px 14px env(safe-area-inset-top,0) 14px",
        display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
        <span style={{fontSize:20,lineHeight:1}}>📈</span>
        <div style={{flex:1}}>
          <div style={{fontSize:12,fontWeight:700,color:"#00d4aa",letterSpacing:0.5}}>RAB Signals</div>
          <div style={{display:"flex",gap:10,marginTop:2}}>
            {niftyQ && <span style={{fontSize:9,color:clr(niftyQ.change||0)}}>
              NIFTY {(niftyQ.last_price||0).toFixed(0)} <span>{(niftyQ.change||0)>=0?"+":""}{(niftyQ.change||0).toFixed(1)}</span>
            </span>}
            {bankQ && <span style={{fontSize:9,color:clr(bankQ.change||0)}}>
              BNF {(bankQ.last_price||0).toFixed(0)}
            </span>}
          </div>
        </div>

        {/* AT active indicator */}
        {(mAtIntra||mAtFno) && canTrade && (
          <div style={{fontSize:9,fontWeight:700,padding:"2px 7px",borderRadius:10,
            background:"rgba(161,115,247,0.15)",border:"1px solid rgba(161,115,247,0.4)",
            color:"#a371f7",animation:"mobPulse 2s infinite"}}>
            🤖 AT
          </div>
        )}

        {/* P&L pill */}
        {posSummary.count>0 && (
          <div style={{fontSize:10,fontWeight:700,padding:"3px 8px",borderRadius:10,
            background:`${clr(posSummary.totalPnl)}18`,border:`1px solid ${clr(posSummary.totalPnl)}44`,
            color:clr(posSummary.totalPnl)}}>
            {fmtPnl(posSummary.totalPnl)}
          </div>
        )}

        {/* Signals badge (other tabs) */}
        {allSignals.length>0 && mTab!=="signals" && (
          <div style={{fontSize:9,fontWeight:700,padding:"2px 7px",borderRadius:10,
            background:"rgba(232,179,57,0.15)",border:"1px solid rgba(232,179,57,0.35)",
            color:"#e8b339",animation:"mobPulse 2s infinite"}}>
            {allSignals.length} 📡
          </div>
        )}

        <button onClick={onLogout} style={{background:"none",border:"none",color:"#8ea2bf",
          fontSize:16,cursor:"pointer",padding:"4px",lineHeight:1}}>⏏</button>
      </div>

      {/* ── CONTENT ─────────────────────────────────────────────────────────── */}
      <div style={{flex:1,overflow:"hidden",display:"flex",flexDirection:"column"}}>

        {/* ══ SIGNALS TAB ═══════════════════════════════════════════════════ */}
        {mTab==="signals" && (
          <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>

            {/* Market toggle — NSE (equity/F&O) vs MCX (commodity futures) */}
            <div style={{padding:"10px 14px 0",display:"flex",gap:8,flexShrink:0,alignItems:"center"}}>
              <div style={{display:"flex",borderRadius:6,overflow:"hidden",border:"1px solid #23405f",flex:1}}>
                {[["NSE","📈 NSE"],["MCX","🏭 MCX"]].map(([mk,label])=>(
                  <button key={mk} onClick={()=>setMMarket(mk)}
                    style={{flex:1,padding:"7px 10px",border:"none",fontFamily:"inherit",
                      fontSize:11,fontWeight:700,cursor:"pointer",
                      background:mMarket===mk?(mk==="MCX"?"rgba(232,179,57,0.2)":"rgba(0,212,170,0.2)"):"transparent",
                      color:mMarket===mk?(mk==="MCX"?"#e8b339":"#00d4aa"):"#5a728f"}}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Scan button + order mode toggle */}
            <div style={{padding:"8px 14px 6px",display:"flex",gap:8,flexShrink:0,alignItems:"center"}}>
              <button onClick={onQuickScan} disabled={scanning}
                style={{flex:1,padding:"10px",borderRadius:8,fontFamily:"inherit",
                  fontWeight:700,fontSize:12,cursor:scanning?"default":"pointer",
                  background:scanning?"rgba(0,212,170,0.05)":"rgba(0,212,170,0.12)",
                  border:`1px solid ${scanning?"rgba(0,212,170,0.2)":"#00d4aa"}`,
                  color:scanning?"#5a728f":"#00d4aa",
                  animation:scanning?"mobPulse 1.5s infinite":"none"}}>
                {scanning ? "⟳ Scanning…" : `⚡ Quick Scan ${mMarket==="MCX"?"· MCX":""}`}
              </button>
              {/* Auto-scan toggle */}
              <button onClick={()=>setMAutoScan(v=>!v)} title="Auto-scan every 3 min during market hours"
                style={{padding:"8px 12px",borderRadius:8,fontFamily:"inherit",fontWeight:700,fontSize:11,
                  cursor:"pointer",flexShrink:0,
                  border:`1px solid ${mAutoScan?"#e8b339":"#23405f"}`,
                  background:mAutoScan?"rgba(232,179,57,0.15)":"transparent",
                  color:mAutoScan?"#e8b339":"#5a728f"}}>
                {mAutoScan?"⟳ AUTO":"AUTO"}
              </button>
              {/* Order mode toggle — NSE only (MCX uses its own futures buttons) */}
              {canTrade && mMarket==="NSE" && (
                <div style={{display:"flex",borderRadius:6,overflow:"hidden",border:"1px solid #23405f",flexShrink:0}}>
                  {[["MIS","Intraday"],["FNO","F&O"]].map(([mode,label])=>(
                    <button key={mode} onClick={()=>setMOrderMode(mode)}
                      style={{padding:"6px 10px",border:"none",fontFamily:"inherit",
                        fontSize:10,fontWeight:700,cursor:"pointer",
                        background:mOrderMode===mode?"rgba(0,212,170,0.2)":"transparent",
                        color:mOrderMode===mode?"#00d4aa":"#5a728f"}}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Auto-scan status */}
            {mAutoScan && (
              <div style={{padding:"0 14px 4px",flexShrink:0}}>
                <span style={{fontSize:10,fontWeight:700,color:_isMarketOpenIST(mMarket)?"#e8b339":"#5a728f"}}>
                  {_isMarketOpenIST(mMarket)
                    ? `⟳ Auto-scan ON · every 3 min · ${mMarket==="MCX"?"MCX 9am–11pm":"NSE 9:15–3:30"}`
                    : `⏸ Auto-scan paused — ${mMarket} market closed`}
                </span>
              </div>
            )}

            {/* Summary row */}
            {allSignals.length>0 && (
              <div style={{padding:"0 14px 6px",display:"flex",gap:8,flexShrink:0,alignItems:"center"}}>
                {buyCount>0 && <span style={{fontSize:10,color:"#34d399",fontWeight:700}}>▲ {buyCount} BUY</span>}
                {sellCount>0 && <span style={{fontSize:10,color:"#ff5b6c",fontWeight:700}}>▼ {sellCount} SELL</span>}
                <span style={{fontSize:10,color:"#5a728f",marginLeft:"auto"}}>
                  min {mMinSignals} · {mActiveScan.length}/{MOB_ALL_SCANS.length} types
                </span>
              </div>
            )}

            {/* Validate All — re-check every signal live before entry */}
            {allSignals.length>0 && (
              <div style={{padding:"0 14px 8px",flexShrink:0}}>
                <button onClick={runMValidateAll} disabled={mValidating}
                  style={{width:"100%",padding:"9px",borderRadius:8,
                    border:"1px solid rgba(56,189,248,0.5)",background:"rgba(56,189,248,0.12)",
                    color:"#38bdf8",fontFamily:"inherit",fontWeight:700,fontSize:12,
                    cursor:mValidating?"wait":"pointer"}}>
                  {mValidating ? "⟳ Validating…" : "🔎 Validate All Signals"}
                </button>
                {Object.keys(mValidations).length>0 && (
                  <div style={{display:"flex",gap:10,justifyContent:"center",marginTop:6,fontSize:10,fontWeight:700}}>
                    <span style={{color:"#2ed47a"}}>GO {Object.values(mValidations).filter(r=>r.verdict==="GO").length}</span>
                    <span style={{color:"#f59e0b"}}>WAIT {Object.values(mValidations).filter(r=>r.verdict==="WAIT").length}</span>
                    <span style={{color:"#ff5b6c"}}>SKIP {Object.values(mValidations).filter(r=>r.verdict==="SKIP").length}</span>
                  </div>
                )}
              </div>
            )}

            {/* Signal cards */}
            <div style={{flex:1,overflowY:"auto",padding:"0 14px 80px",display:"flex",flexDirection:"column",gap:8}}>
              {allSignals.length===0 ? (
                <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",
                  justifyContent:"center",gap:12,padding:"60px 0",color:"#8ea2bf",textAlign:"center"}}>
                  <div style={{fontSize:36}}>{mMarket==="MCX"?"🏭":"📡"}</div>
                  <div style={{fontSize:13}}>
                    {mMarket==="MCX" ? "No MCX commodity signals yet." : "No signals yet."}
                  </div>
                  <div style={{fontSize:11}}>
                    {scanResults.length>0
                      ? (mMarket==="MCX"
                          ? "Scanned commodities but no signals passed your filters. Lower Min Signals in Settings, or ensure your Zerodha (Kite) session is live for MCX prices."
                          : `${scanResults.length} stocks scanned — try lowering Min Signals or enabling more scan types in Settings`)
                      : (mMarket==="MCX"
                          ? "Tap Quick Scan to scan MCX commodities (CRUDEOIL, GOLD, SILVER, …)."
                          : "Tap Quick Scan to check for opportunities.")}
                  </div>
                </div>
              ) : allSignals.map(sig=>{
                const isBuy  = sig.type==="BUY";
                const c      = isBuy?"#34d399":"#ff5b6c";
                const bg     = isBuy?"rgba(52,211,153,0.06)":"rgba(255,91,108,0.06)";
                const border = isBuy?"rgba(52,211,153,0.25)":"rgba(255,91,108,0.25)";
                const scorePct = Math.round(Math.abs(sig.score)*100);

                // Determine order params based on selected mode
                const orderParams = mOrderMode==="FNO"
                  ? {symbol:sig.symbol, exchange:"NSE", side:sig.type, price:sig.price,
                     qty:1, product:"NRML", isFno:true, source:"mob_fno"}
                  : {symbol:sig.symbol, exchange:"NSE", side:sig.type, price:sig.price,
                     qty:1, product:"MIS", source:"mob_mis"};

                return (
                  <div key={sig._key} style={{background:bg,border:`1px solid ${border}`,
                    borderRadius:10,padding:"12px",display:"flex",flexDirection:"column",gap:8}}>

                    {/* Header */}
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:16,fontWeight:700,color:c,letterSpacing:0.5}}>
                        {isBuy?"▲":"▼"} {sig.symbol}
                      </span>
                      <span style={{fontSize:10,background:`${c}18`,border:`1px solid ${c}44`,
                        color:c,padding:"2px 7px",borderRadius:4,fontWeight:700}}>
                        {sig.type}
                      </span>
                      <span style={{marginLeft:"auto",fontSize:12,color:"#e7edf5",fontWeight:600}}>
                        ₹{sig.price.toFixed(2)}
                      </span>
                    </div>

                    {/* Validation verdict (after Validate All) */}
                    {mValidations[sig.symbol] && (()=>{
                      const v = mValidations[sig.symbol];
                      const vc = v.verdict==="GO"?"#2ed47a":v.verdict==="WAIT"?"#f59e0b":"#ff5b6c";
                      const vi = v.verdict==="GO"?"✓":v.verdict==="WAIT"?"⏸":"✕";
                      const reason = (v.ai&&v.ai.reason) || (v.rule_reasons&&v.rule_reasons[0]) || "";
                      return (
                        <div style={{background:"#0b1624",borderLeft:`3px solid ${vc}`,
                          borderRadius:6,padding:"7px 9px",display:"flex",flexDirection:"column",gap:3}}>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <span style={{fontSize:12,fontWeight:800,color:vc}}>{vi} {v.verdict}</span>
                            {v.suggested_entry!=null && <span style={{fontSize:10,color:"#8ea2bf"}}>entry ₹{v.suggested_entry}</span>}
                            {v.rr!=null && <span style={{fontSize:10,color:"#8ea2bf"}}>RR {v.rr}</span>}
                          </div>
                          {reason && <div style={{fontSize:10,color:"#b9c6d8",lineHeight:1.4}}>{reason}</div>}
                        </div>
                      );
                    })()}

                    {/* Scan type chips */}
                    <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                      {sig.scans.map((name,i)=>(
                        <span key={i} style={{fontSize:9,padding:"2px 7px",borderRadius:10,
                          background:`${c}12`,border:`1px solid ${c}33`,color:c,fontWeight:600}}>
                          {name}
                        </span>
                      ))}
                    </div>

                    {/* Score + badges */}
                    <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                      {/* Score bar */}
                      <div style={{display:"flex",alignItems:"center",gap:5}}>
                        <div style={{width:60,height:4,borderRadius:2,background:"rgba(255,255,255,0.07)"}}>
                          <div style={{width:`${Math.min(100,scorePct)}%`,height:"100%",
                            borderRadius:2,background:c}}/>
                        </div>
                        <span style={{fontSize:9,color:"#8ea2bf"}}>{scorePct}%</span>
                      </div>
                      {sig.oiPCR!==1 && <span style={{fontSize:9,
                        color:sig.oiSignal==="BULLISH"?"#34d399":sig.oiSignal==="BEARISH"?"#ff5b6c":"#8ea2bf"}}>
                        PCR {sig.oiPCR.toFixed(2)} {sig.oiSignal==="BULLISH"?"🐂":sig.oiSignal==="BEARISH"?"🐻":""}
                      </span>}
                      {sig.boosted   && <span style={{fontSize:9,color:"#ffd700"}}>⚡News+</span>}
                      {sig.bulkBoost && <span style={{fontSize:9,color:"#a78bfa"}}>🏦Bulk</span>}
                      {sig.oiBoost   && <span style={{fontSize:9,color:"#38bdf8"}}>OI+</span>}
                    </div>

                    {/* Order buttons */}
                    {canTrade && onTrade && mMarket==="NSE" && (
                      <div style={{display:"flex",gap:6}}>
                        {/* MIS intraday */}
                        <button
                          onClick={()=>onTrade({...orderParams, product:"MIS", isFno:false, source:"mob_mis"})}
                          style={{flex:1,padding:"8px 6px",borderRadius:7,border:`1px solid ${c}66`,
                            background:`${c}15`,color:c,fontFamily:"inherit",
                            fontWeight:700,fontSize:11,cursor:"pointer"}}>
                          {isBuy?"🟢":"🔴"} MIS {isBuy?"BUY":"SELL"}
                        </button>
                        {/* F&O NRML — resolves ATM option (strike + premium) first */}
                        <button
                          onClick={()=>handleFnoTrade(sig)}
                          disabled={fnoBusy===sig._key}
                          style={{flex:1,padding:"8px 6px",borderRadius:7,
                            border:"1px solid rgba(73,194,228,0.4)",
                            background:"rgba(73,194,228,0.1)",color:"#49c2e4",fontFamily:"inherit",
                            fontWeight:700,fontSize:11,cursor:fnoBusy===sig._key?"default":"pointer",
                            opacity:fnoBusy===sig._key?0.6:1}}>
                          {fnoBusy===sig._key ? "⟳ Finding ATM…" : "🎯 F&O NRML"}
                        </button>
                      </div>
                    )}
                    {/* MCX futures order buttons — resolve active contract first */}
                    {canTrade && onTrade && mMarket==="MCX" && (
                      <div style={{display:"flex",gap:6}}>
                        {[["MIS","Intraday"],["NRML","Positional"]].map(([prod,lbl])=>{
                          const busyKey = `mcx:${sig._key}:${prod}`;
                          const busy = fnoBusy===busyKey;
                          return (
                            <button key={prod}
                              onClick={()=>handleMcxTrade(sig, prod)}
                              disabled={busy}
                              style={{flex:1,padding:"8px 6px",borderRadius:7,
                                border:`1px solid ${isBuy?"rgba(52,211,153,0.4)":"rgba(255,91,108,0.4)"}`,
                                background:isBuy?"rgba(52,211,153,0.1)":"rgba(255,91,108,0.1)",
                                color:c,fontFamily:"inherit",fontWeight:700,fontSize:11,
                                cursor:busy?"default":"pointer",opacity:busy?0.6:1}}>
                              {busy ? "⟳ Finding…" : `🏭 ${prod} FUT ${isBuy?"BUY":"SELL"}`}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ══ POSITIONS TAB ════════════════════════════════════════════════ */}
        {mTab==="positions" && (
          <div style={{flex:1,overflowY:"auto",padding:"14px"}}>
            {livePositions.filter(p=>p.quantity!==0).length===0 ? (
              <div style={{textAlign:"center",padding:"60px 0",color:"#8ea2bf",fontSize:13}}>
                No open positions
              </div>
            ) : livePositions.filter(p=>p.quantity!==0).map((p,i)=>{
              const ltp    = livePrices[p.tradingsymbol] || p.last_price || 0;
              const pnl    = (ltp - p.average_price) * p.quantity;
              const pnlPct = p.average_price?((ltp-p.average_price)/p.average_price*100):0;
              const c = pnl>=0?"#34d399":"#ff5b6c";
              return (
                <div key={i} style={{background:"rgba(255,255,255,0.02)",
                  border:"1px solid #21262d",borderRadius:10,padding:"12px",
                  marginBottom:8,display:"flex",flexDirection:"column",gap:5}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontWeight:700,fontSize:13}}>{p.tradingsymbol}</span>
                    <span style={{fontSize:10,color:p.quantity>0?"#34d399":"#ff5b6c"}}>
                      {p.quantity>0?"LONG":"SHORT"} {Math.abs(p.quantity)}
                    </span>
                    <span style={{marginLeft:"auto",fontSize:13,fontWeight:700,color:c}}>
                      {fmtPnl(pnl)}
                      <span style={{fontSize:9,marginLeft:4}}>({pnlPct>=0?"+":""}{pnlPct.toFixed(1)}%)</span>
                    </span>
                  </div>
                  <div style={{display:"flex",gap:12,fontSize:10,color:"#5a728f"}}>
                    <span>Avg ₹{p.average_price?.toFixed(2)}</span>
                    <span>LTP ₹{ltp.toFixed(2)}</span>
                    <span style={{marginLeft:"auto"}}>{p.product||""}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ══ SETTINGS TAB ════════════════════════════════════════════════ */}
        {mTab==="settings" && (
          <div style={{flex:1,overflowY:"auto",padding:"14px",display:"flex",flexDirection:"column",gap:12}}>

            {/* ── VPS ENGINE (runs 24/7 on server) ────────────────── */}
            <VpsEngineSettings authed={authed} showToast={showToast} />

            {/* ── Scan Type Selection ──────────────────────────────────── */}
            <div style={{background:"rgba(0,212,170,0.04)",border:"1px solid rgba(0,212,170,0.2)",
              borderRadius:10,padding:"14px"}}>
              <div style={{fontSize:12,fontWeight:700,color:"#00d4aa",marginBottom:10}}>
                📡 Scan Types
              </div>
              <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                {MOB_ALL_SCANS.map(id=>{
                  const active = (mActiveScan||[]).includes(id);
                  return (
                    <button key={id}
                      onClick={()=>{
                        const next = active
                          ? (mActiveScan||[]).filter(x=>x!==id)
                          : [...(mActiveScan||[]), id];
                        if(next.length>0) setMActiveScan(next);
                      }}
                      style={{padding:"6px 12px",borderRadius:20,border:`1px solid ${active?"#00d4aa":"#23405f"}`,
                        background:active?"rgba(0,212,170,0.15)":"transparent",
                        color:active?"#00d4aa":"#5a728f",fontFamily:"inherit",
                        fontSize:11,fontWeight:700,cursor:"pointer"}}>
                      {MOB_SCAN_LABELS[id]}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Scan Timeframe ──────────────────────────────────────── */}
            <div style={{background:"rgba(56,189,248,0.05)",border:"1px solid rgba(56,189,248,0.25)",
              borderRadius:10,padding:"14px"}}>
              <div style={{fontSize:12,fontWeight:700,color:"#38bdf8",marginBottom:10}}>
                ⏱️ Scan Timeframe
              </div>
              <div style={{fontSize:11,color:"#8ea2bf",marginBottom:10}}>
                Candle interval the Quick Scan runs on. Shorter = more intraday signals; longer = swing/positional.
              </div>
              <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                {["1min","5min","10min","15min","30min","1hour","1day"].map(tf=>{
                  const active = mTimeframe===tf;
                  return (
                    <button key={tf} onClick={()=>setMTimeframe(tf)}
                      style={{padding:"7px 12px",borderRadius:20,
                        border:`1px solid ${active?"#38bdf8":"#23405f"}`,
                        background:active?"rgba(56,189,248,0.15)":"transparent",
                        color:active?"#38bdf8":"#5a728f",fontFamily:"inherit",
                        fontSize:11,fontWeight:700,cursor:"pointer"}}>
                      {tf}
                    </button>
                  );
                })}
              </div>
              <div style={{marginTop:8,fontSize:10,color:"#5a728f"}}>
                Currently scanning on: <span style={{color:"#38bdf8",fontWeight:700}}>{mTimeframe}</span>
              </div>
            </div>

            {/* ── Min Signals ─────────────────────────────────────────── */}
            <div style={{background:"rgba(232,179,57,0.05)",border:"1px solid rgba(232,179,57,0.2)",
              borderRadius:10,padding:"14px"}}>
              <div style={{fontSize:12,fontWeight:700,color:"#e8b339",marginBottom:10}}>
                🎯 Min Signals for BUY / SELL
              </div>
              <div style={{fontSize:11,color:"#8ea2bf",marginBottom:10}}>
                Only show stocks with at least this many scan types triggering.
              </div>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                {[1,2,3,4].map(n=>(
                  <button key={n} onClick={()=>setMMinSignals(n)}
                    style={{flex:1,padding:"10px 0",borderRadius:7,
                      border:`1px solid ${mMinSignals===n?"#e8b339":"#23405f"}`,
                      background:mMinSignals===n?"rgba(232,179,57,0.15)":"transparent",
                      color:mMinSignals===n?"#e8b339":"#5a728f",fontFamily:"inherit",
                      fontSize:13,fontWeight:700,cursor:"pointer"}}>
                    {n}
                  </button>
                ))}
              </div>
              <div style={{marginTop:8,fontSize:10,color:"#5a728f"}}>
                Currently: {allSignals.length} signals ({scanResults.length} stocks scanned)
              </div>
            </div>

            {/* ── Auto Trade (admin / trader only) ────────────────────── */}
            {canTrade && (
              <div style={{background:"rgba(161,115,247,0.05)",border:"1px solid rgba(161,115,247,0.25)",
                borderRadius:10,padding:"14px"}}>
                <div style={{fontSize:12,fontWeight:700,color:"#a371f7",marginBottom:10}}>
                  🤖 Auto Trade
                </div>
                <div style={{fontSize:11,color:"#8ea2bf",marginBottom:12}}>
                  Auto-place orders after each Quick Scan. Equity MIS fires silently; F&O opens order ticket for confirmation.
                </div>

                {/* Equity Intraday AT */}
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,fontWeight:700,color:"#e7edf5"}}>Equity MIS (Intraday)</div>
                    <div style={{fontSize:10,color:"#5a728f"}}>MARKET order, MIS product, NSE</div>
                  </div>
                  <button onClick={()=>setMAtIntra(v=>!v)}
                    style={{padding:"7px 16px",borderRadius:20,border:"none",
                      background:mAtIntra?"rgba(52,211,153,0.2)":"rgba(255,255,255,0.06)",
                      color:mAtIntra?"#34d399":"#5a728f",fontFamily:"inherit",
                      fontWeight:700,fontSize:11,cursor:"pointer"}}>
                    {mAtIntra?"ON ●":"OFF ○"}
                  </button>
                </div>

                {/* F&O AT */}
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,fontWeight:700,color:"#e7edf5"}}>F&O Options (NRML)</div>
                    <div style={{fontSize:10,color:"#5a728f"}}>Opens order ticket — you confirm</div>
                  </div>
                  <button onClick={()=>setMAtFno(v=>!v)}
                    style={{padding:"7px 16px",borderRadius:20,border:"none",
                      background:mAtFno?"rgba(73,194,228,0.2)":"rgba(255,255,255,0.06)",
                      color:mAtFno?"#49c2e4":"#5a728f",fontFamily:"inherit",
                      fontWeight:700,fontSize:11,cursor:"pointer"}}>
                    {mAtFno?"ON ●":"OFF ○"}
                  </button>
                </div>

                {/* AT log */}
                {mAtLog.length>0 && (
                  <div style={{marginTop:10,padding:"8px",background:"rgba(0,0,0,0.3)",
                    borderRadius:6,maxHeight:80,overflowY:"auto"}}>
                    {mAtLog.map((l,i)=>(
                      <div key={i} style={{fontSize:10,color:"#a371f7",marginBottom:2}}>{l.msg}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Push Notifications ──────────────────────────────────── */}
            <div style={{background:"rgba(73,194,228,0.05)",border:"1px solid rgba(73,194,228,0.2)",
              borderRadius:10,padding:"14px"}}>
              <div style={{fontSize:12,fontWeight:700,color:"#49c2e4",marginBottom:8}}>
                🔔 Push Notifications
              </div>
              {notifPerm==="granted" && pushEnabled ? (
                <div style={{fontSize:11,color:"#34d399"}}>✓ Enabled — signal & TP/SL alerts active</div>
              ) : notifPerm==="denied" ? (
                <div style={{fontSize:11,color:"#ff5b6c"}}>✗ Blocked — allow in Chrome settings, then reload</div>
              ) : (
                <button onClick={enablePush}
                  style={{width:"100%",padding:"10px",borderRadius:7,border:"1px solid #49c2e4",
                    background:"rgba(73,194,228,0.1)",color:"#49c2e4",fontFamily:"inherit",
                    fontWeight:700,fontSize:12,cursor:"pointer"}}>
                  Enable Push Notifications
                </button>
              )}
            </div>

            {/* ── Install as App ───────────────────────────────────────── */}
            <div style={{background:"rgba(0,212,170,0.04)",border:"1px solid rgba(0,212,170,0.15)",
              borderRadius:10,padding:"14px"}}>
              <div style={{fontSize:12,fontWeight:700,color:"#00d4aa",marginBottom:6}}>📱 Install as App</div>
              <div style={{fontSize:11,color:"#8ea2bf",lineHeight:1.7}}>
                Chrome → ⋮ menu → "Add to home screen"<br/>
                RAB Signals will appear as an app icon on your Android.
              </div>
            </div>

            {/* ── Account ─────────────────────────────────────────────── */}
            <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid #21262d",borderRadius:10,padding:"14px"}}>
              <div style={{fontSize:11,color:"#8ea2bf",lineHeight:1.8}}>
                <div>User: <span style={{color:"#f0f6fc"}}>{fbRecord?.email||uid}</span></div>
                <div>Role: <span style={{color:"#f0f6fc",textTransform:"capitalize"}}>{userRole}</span></div>
                {fbRecord?.expiresAt && <div>Access until: <span style={{color:"#f0f6fc"}}>{fbRecord.expiresAt}</span></div>}
              </div>
            </div>

          </div>
        )}
      </div>

      {/* ── BOTTOM TAB BAR ──────────────────────────────────────────────────── */}
      <div style={{background:"#0d1117",borderTop:"1px solid #21262d",
        display:"flex",justifyContent:"space-around",flexShrink:0,
        paddingBottom:"env(safe-area-inset-bottom, 6px)"}}>
        {[
          {id:"signals",   icon:"📡", label:"Signals",   badge:allSignals.length},
          {id:"positions", icon:"📊", label:"Positions", badge:posSummary.count},
          {id:"settings",  icon:"⚙️", label:"Settings",  badge:0},
        ].map(t=>(
          <button key={t.id} onClick={()=>setMTab(t.id)}
            style={{background:"none",border:"none",flex:1,padding:"10px 0 6px",
              display:"flex",flexDirection:"column",alignItems:"center",gap:2,
              cursor:"pointer",position:"relative",
              borderTop:`2px solid ${mTab===t.id?"#00d4aa":"transparent"}`,
              color:mTab===t.id?"#00d4aa":"#8b949e",fontFamily:"inherit",
              WebkitTapHighlightColor:"transparent"}}>
            <span style={{fontSize:20,lineHeight:1}}>{t.icon}</span>
            <span style={{fontSize:9,fontWeight:700,letterSpacing:0.5}}>{t.label}</span>
            {t.badge>0 && (
              <span style={{position:"absolute",top:6,right:"calc(50% - 18px)",
                background:"#ff5b6c",color:"#fff",borderRadius:10,
                fontSize:8,fontWeight:700,padding:"1px 5px",minWidth:14,textAlign:"center",
                animation:"mobPulse 2s infinite"}}>
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
});


export default function App() {
  // ── Mobile detection ───────────────────────────────────────────────────────
  const isMobileDevice = typeof window !== "undefined" && window.innerWidth <= 768;

  // ── Firebase Auth state ────────────────────────────────────────────────────
  const [fbUser,     setFbUser]     = useState(null);    // Firebase user object
  const [fbRecord,   setFbRecord]   = useState(null);    // Firestore user record
  const [fbLoading,  setFbLoading]  = useState(true);    // initial auth check

  // ── Role system ────────────────────────────────────────────────────────────
  // admin  → full access: AT engine, manual trade, signals, all tabs
  // trader → signals + manual trade with own Kite API (no AT)
  // viewer → signals only, read-only, uses admin's live price feed (no Kite needed)
  const userRole     = fbRecord?.role || "viewer";
  const isAdmin      = userRole === "admin";
  const isTrader     = userRole === "trader";
  const isViewer     = userRole === "viewer";
  const canTrade     = isAdmin || isTrader;
  const canSignal    = true;
  // AI access: admin always has it; traders get it if admin enabled ai_access on their record
  const hasAIAccess  = isAdmin || (fbRecord?.ai_access === true);

  // ── Scan limits per role ────────────────────────────────────────────────────
  // Admin   → unlimited scans
  // Trader  → 50 scans/day (configurable by admin via Firestore scanLimit field)
  // Viewer  → 20 scans/day (signal-only, read Kite feed)
  const ROLE_SCAN_LIMITS = { admin: 0, trader: 50, viewer: 20 };  // 0 = unlimited
  const scanLimitFromRole = ROLE_SCAN_LIMITS[userRole] ?? 20;
  // Admin can override per-user limit via Firestore fbRecord.scanLimit
  const scanLimit = isAdmin ? 0 : (fbRecord?.scanLimit ?? scanLimitFromRole);

  const [scanDailyCount, setScanDailyCount] = useLocalStorage('nse_pro_scan_daily',
    {date:'', count:0});

  const checkScanLimit = useCallback(()=>{
    if(scanLimit === 0) return true; // unlimited
    const today = new Date().toISOString().slice(0,10);
    const rec   = scanDailyCount.date === today ? scanDailyCount : {date:today, count:0};
    if(rec.count >= scanLimit) return false;
    setScanDailyCount({date:today, count:rec.count+1});
    return true;
  },[scanLimit, scanDailyCount, setScanDailyCount]);

  const scansUsedToday = useMemo(()=>{
    const today = new Date().toISOString().slice(0,10);
    return scanDailyCount.date === today ? scanDailyCount.count : 0;
  },[scanDailyCount]);

  // ── Failsafe: never stay on loading screen more than 8 seconds ────────────
  useEffect(()=>{
    const t = setTimeout(()=>setFbLoading(false), 8000);
    return ()=>clearTimeout(t);
  },[]);
  const [keysReady,  setKeysReady]  = useState(false);   // API keys entered
  const [showAdmin,  setShowAdmin]  = useState(false);
  const [dupSession, setDupSession] = useState(false);   // duplicate session warning

  // ── Duplicate session detection via BroadcastChannel ──────────────────────
  useEffect(()=>{
    if(!("BroadcastChannel" in window)) return;
    const SESSION_ID = `session_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const ch = new BroadcastChannel("rabtrade_session");
    // Announce this tab to others
    ch.postMessage({type:"HELLO", id:SESSION_ID});
    ch.onmessage = (e)=>{
      if(e.data.type==="HELLO" && e.data.id!==SESSION_ID){
        // Another tab just opened — warn this tab
        setDupSession(true);
        // Tell the new tab there's already an active session
        ch.postMessage({type:"ALREADY_ACTIVE", id:SESSION_ID});
      }
      if(e.data.type==="ALREADY_ACTIVE" && e.data.id!==SESSION_ID){
        setDupSession(true);
      }
      if(e.data.type==="TAKEOVER"){
        // Another tab explicitly took over — this tab is now secondary
        setDupSession(true);
      }
    };
    return ()=>ch.close();
  }, []);

  // ── Firebase auth listener ─────────────────────────────────────────────────
  useEffect(()=>{
    const unsub = onAuthStateChanged(_auth, async(user)=>{
      if(user) {
        try {
          // 5s timeout on Firestore user fetch — don't block login if Firestore is slow
          let rec = await Promise.race([
            fbGetUser(user.uid),
            new Promise((_,rej)=>setTimeout(()=>rej(new Error("Firestore timeout")),5000))
          ]);

          // User added via Firebase Console has no Firestore record yet
          // Auto-create one so they can log in immediately
          if(!rec) {
            const expires = new Date(Date.now()+30*86400000).toISOString().split('T')[0];
            await setDoc(doc(_db,"users",user.uid),{
              email:       user.email,
              displayName: user.displayName||user.email.split('@')[0],
              role:        "user",
              expiresAt:   expires,
              plan:        "monthly",
              createdAt:   serverTimestamp(),
              active:      true,
              autoCreated: true,
            });
            rec = await fbGetUser(user.uid);
          }

          if(rec && isAccessValid(rec)) {
            setFbUser(user); setFbRecord(rec);
            const role = rec.role || "viewer";
            const isAdminUser = role === "admin";

            // ── Register FCM push token (non-blocking) ──────────────────────
            // Runs in background — won't block login flow
            setTimeout(()=>_registerPush(user.uid), 2000);

            // ── Auto-restore API sessions for ANY user ──────────────────────
            // Try to restore Kite keys from sessionStorage (works for admin + any Zerodha user)
            const kKey = sessionStorage.getItem("kite_key");
            const kSec = sessionStorage.getItem("kite_secret");
            const brokerMode = sessionStorage.getItem("broker_mode");

            if(kKey && kSec) {
              // Zerodha user (admin or trader) — re-register keys
              try {
                const ctrl = new AbortController();
                const tid  = setTimeout(()=>ctrl.abort(), 5000);
                await fetch(`${API_BASE}/auth/register-keys`,{
                  method:"POST",headers:{"Content-Type":"application/json"},
                  body:JSON.stringify({uid:user.uid,api_key:kKey,api_secret:kSec}),
                  signal: ctrl.signal,
                });
                clearTimeout(tid);
                // Delta keys (admin only typically)
                const dKey = sessionStorage.getItem("delta_key");
                const dSec = sessionStorage.getItem("delta_secret");
                const dPlat = sessionStorage.getItem("delta_platform")||"india";
                if(dKey && dSec) {
                  const ctrl2 = new AbortController();
                  const tid2  = setTimeout(()=>ctrl2.abort(), 5000);
                  await fetch(`${DELTA_API_BASE}/delta/auth/register-keys`,{
                    method:"POST",headers:{"Content-Type":"application/json"},
                    body:JSON.stringify({uid:user.uid,api_key:dKey,api_secret:dSec,platform:dPlat}),
                    signal: ctrl2.signal,
                  });
                  clearTimeout(tid2);
                }
                setKeysReady(true);
              } catch {} // timeout or network error — user re-authenticates manually
            } else if(brokerMode) {
              // Already picked a broker (deeplink/Kotak/etc.) — go straight to app
              setKeysReady(true);
            }
            // else: ApiKeySetup will show broker picker, onComplete sets keysReady
          } else if(rec?.active===false) {
            await signOut(_auth); // explicitly deactivated
          } else {
            // Access expired — show subscription page (don't sign out)
            setFbUser(user); setFbRecord(rec);
          }
        } catch {
          // Firestore timeout or network error — let user in anyway, show limited view
          setFbUser(user); setFbRecord(null);
        }
      } else {
        setFbUser(null); setFbRecord(null); setKeysReady(false);
      }
      setFbLoading(false);  // always called — app never hangs on loading screen
    });
    return unsub;
  },[]);

  async function handleFbLogin(user, rec) {
    setFbUser(user); setFbRecord(rec);
  }

  async function handleFbLogout() {
    sessionStorage.clear();
    await signOut(_auth);
    setFbUser(null); setFbRecord(null); setKeysReady(false);
    setAuthed(false);
  }

  const uid = fbUser?.uid || "";
  useEffect(()=>{ window._uid = uid; },[uid]); // expose for Firestore saves inside tab components

  // ── Load user settings from Firestore on login ────────────────────────────
  useEffect(()=>{
    if(!uid) return;
    fbGetSettings(uid).then(saved=>{
      if(!saved) return;
      // Only restore settings that aren't already in localStorage
      // Never reload — just write to localStorage silently, React will pick up on next render
      const restore = (lsKey, val) => {
        if(!val) return;
        try {
          // Only write if localStorage doesn't already have this key
          if(!localStorage.getItem(lsKey)) {
            localStorage.setItem(lsKey, JSON.stringify(val));
          }
        } catch {}
      };
      restore('nse_pro_eq_at_cfg',    saved.eq_at_cfg);
      restore('nse_pro_fo_at_cfg',    saved.fo_at_cfg);
      restore('nse_pro_mcx_at_cfg',   saved.mcx_at_cfg);
      restore('nse_pro_mcx_at_mode',  saved.mcx_at_mode);
      restore('nse_pro_delta_at_cfg', saved.delta_at_cfg);
      restore('nse_pro_delta_at_mode',saved.delta_at_mode);
      restore('nse_pro_ai_min_conf',  saved.ai_min_conf);
      restore('nse_pro_ai_margin_pct',saved.ai_margin_pct);
      restore('nse_pro_ai_max_daily', saved.ai_max_daily);
      restore('nse_pro_signal_params',saved.signal_params);
      // Telegram config: restore to uid-keyed keys so each user has isolation
      if(uid && saved.tg_token) restore(`nse_pro_tg_token_${uid}`, saved.tg_token);
      if(uid && saved.tg_chat)  restore(`nse_pro_tg_chat_${uid}`,  saved.tg_chat);
      restore('nse_pro_vps_scan',     saved.vps_scan);
      // NO window.location.reload() — was breaking Kite login flow
    }).catch(()=>{});
  },[uid]);

  // ── Disclaimer popup for non-admin users (once per session) ──────────────
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(()=>{
    return sessionStorage.getItem("rabt_disclaimer_accepted") === "1";
  });

  const [authed,setAuthed]               = useState(false);
  const [authChecked,setAuthChecked]     = useState(false);
  const [kiteUserName, setKiteUserName]  = useState("");
  const [tokenSaved,   setTokenSaved]    = useState(false);
  const [activeTab,setActiveTab]         = useState(
    new URLSearchParams(window.location.search).get("tab") || "dashboard"
  );
  useKeyboardShortcuts(setActiveTab); // D/E/M/P/A/V shortcuts
  const [scanResults,setScanResults]     = useState([]);
  const [alerts,setAlerts]               = useState([]);
  const [seenKeys,setSeenKeys]           = useState(new Set());
  const [activeScan,setActiveScan]       = useLocalStorage('nse_pro_active_scan', [1,2,3,4,5,8]);
  // Migration: ensure all scan groups are present for existing users
  // (localStorage keeps old value — this merges any newly added groups)
  useEffect(()=>{
    setActiveScan(p=>{
      if(!Array.isArray(p)) return [1,2,3,4,5,8,9,10,11,12];
      let n = [...p];
      [5,8,9,10,11,12].forEach(g=>{ if(!n.includes(g)) n.push(g); });
      return n;
    });
  },[]);
  const [trades,setTrades]               = useLocalStorage('nse_pro_trades', []);

  // ── Firebase Trade Journal — persists ALL app trades across devices ────────
  const [journal,    setJournal]    = useState([]);   // loaded from Firebase
  const [journalLoaded, setJournalLoaded] = useState(false);

  // Load journal on login
  useEffect(()=>{
    if(!uid || !authed || journalLoaded) return;
    setJournalLoaded(true);
    fbJournalLoad(uid).then(entries=>{
      setJournal(entries);
    }).catch(()=>{});
  },[uid, authed, journalLoaded]);

  // Record a NEW trade entry (called at order placement)
  const journalEntry = useCallback((params)=>{
    if(!uid) return null;
    // Only record if order was actually placed (has order_id or explicit allowNoId)
    // Skip cancelled / rejected / pending orders
    const status = (params.orderStatus||params.status||"").toUpperCase();
    if(status && !["COMPLETE","OPEN","TRIGGER PENDING",""].includes(status)) return null;
    // Skip if no order_id and not explicitly allowed (prevents recording failed AT attempts)
    if(!params.orderId && !params.allowNoId) return null;
    const entry = buildJournalEntry(params);
    setJournal(prev=>[entry, ...prev.filter(e=>e.id!==entry.id)]);
    fbJournalSave(uid, entry).catch(()=>{});
    return entry.id;
  },[uid]);

  // Close a journal entry — called when TP/SL fires or manual exit
  const journalClose = useCallback((journalId, exitPrice, reason="MANUAL")=>{
    if(!uid || !journalId) return;
    setJournal(prev=>prev.map(e=>{
      if(e.id !== journalId) return e;
      const { pnl, roi } = calcJournalPnL(e, exitPrice);
      const closed = { ...e, status:"CLOSED",
        exitTime: new Date().toISOString(),
        exitPrice: parseFloat(exitPrice||0),
        exitReason: reason, pnl, roi };
      fbJournalClose(uid, journalId, {
        exitTime: closed.exitTime, exitPrice: closed.exitPrice,
        exitReason: reason, pnl, roi, status:"CLOSED",
        signals: e.signals||"" });
      return closed;
    }));
  },[uid]);

  // ── Watcher format converter (frontend → VPS backend) ────────────────────────
  const toServerFmt = useCallback((w)=>({
    watcher_id:      w.id,
    symbol:          w.symbol,
    exchange:        w.exchange||"NSE",
    side:            w.side,
    qty:             w.qty,
    entry_price:     w.entryPrice||0,
    product:         w.product||"MIS",
    tp_enabled:      w.tpEnabled||false,
    tp_price:        parseFloat(w.tpPrice||0),
    sl_enabled:      w.slEnabled||false,
    sl_price:        parseFloat(w.slPrice||0),
    trail_enabled:   w.trailEnabled||false,
    trail_pct:       parseFloat(w.trailPct||1.5),
    high_water_mark: parseFloat(w.highWaterMark||w.entryPrice||0),
    low_water_mark:  parseFloat(w.lowWaterMark||w.entryPrice||0),
    tick_size:       parseFloat(w.tickSize||0.05),
  }),[]);

  // ── Cross-device sync: Trades + Watchers ─────────────────────────────────────
  // On login, load trades from Firebase and run the full watcher sync chain:
  //   1. Load cloud watchers from Firebase
  //   2. Sync to VPS (send local WATCHING, receive ALL VPS active watchers)
  //   3. Merge: VPS is authoritative → Firebase fills gaps → local-only kept
  // This ensures watchers are consistent regardless of which device placed the trade.
  const watcherSyncDone = useRef(false);
  useEffect(()=>{
    if(!uid || !authed) return;
    watcherSyncDone.current = false;

    // ── Load trades from Firebase ──
    fbGetAllTrades(uid).then(cloudTrades=>{
      if(cloudTrades.length > 0) {
        setTrades(prev=>{
          const localIds = new Set(prev.map(t=>t.id));
          const merged   = [...prev];
          cloudTrades.forEach(t=>{ if(!localIds.has(t.id)) merged.push(t); });
          return merged.sort((a,b)=>new Date(b.exitTime||b.entryTime||0)-new Date(a.exitTime||a.entryTime||0));
        });
      }
    }).catch(()=>{});

    // ── Full watcher sync chain (sequential — not racing) ──
    (async()=>{
      try {
        // Step 1: Load cloud watchers from Firebase
        let cloudWatchers = [];
        try { cloudWatchers = await fbGetWatchers(uid); } catch {}

        // Step 2: Sync to VPS — send local WATCHING watchers, get ALL VPS active
        const localActive = watchers.filter(w=>w.status==="WATCHING");
        let vpsWatchers = [];
        try {
          const syncRes = await apiFetch("/watcher/sync", {
            method:"POST",
            headers:{"Content-Type":"application/json"},
            body: JSON.stringify({watchers: localActive.map(toServerFmt)})
          });
          vpsWatchers = (syncRes?.watchers || []).filter(w=>w.status==="WATCHING"||w.status==="PENDING_FILL");
        } catch {}

        // Step 3: Check for VPS-fired exits (trades that hit TP/SL while offline)
        let vpsCompleted = [];
        try {
          const statusRes = await apiFetch("/watcher/status");
          vpsCompleted = statusRes?.completed || [];
        } catch {}

        // Step 4: Build merged watcher list — VPS is source of truth
        setWatchers(prev=>{
          const merged = new Map(); // id → watcher (deduped)

          // First: add all VPS active watchers (highest authority)
          vpsWatchers.forEach(vw=>{
            const id = vw.watcher_id||vw.id;
            merged.set(id, {
              id,
              symbol:       vw.symbol,
              exchange:     vw.exchange||"NSE",
              side:         vw.side,
              entryPrice:   vw.entry_price||vw.entryPrice||0,
              qty:          vw.qty||1,
              product:      vw.product||"NRML",
              tpEnabled:    vw.tp_enabled||vw.tpEnabled||false,
              tpPrice:      vw.tp_price||vw.tpPrice||0,
              tpPct:        vw.tpPct||vw.tp_pct||2,
              slEnabled:    vw.sl_enabled||vw.slEnabled||false,
              slPrice:      vw.sl_price||vw.slPrice||0,
              slPct:        vw.slPct||vw.sl_pct||1,
              trailEnabled: vw.trail_enabled||vw.trailEnabled||false,
              trailPct:     vw.trail_pct||vw.trailPct||1.5,
              highWaterMark:vw.high_water_mark||vw.highWaterMark||vw.entry_price||vw.entryPrice||0,
              tickSize:     vw.tick_size||vw.tickSize||0.05,
              instrumentType: vw.instrumentType||vw.instrument_type||"",
              status:       vw.status==="PENDING_FILL" ? "PENDING_FILL" : "WATCHING",
              createdAt:    vw.createdAt||new Date().toISOString(),
              source:       vw.source||"VPS",
              orderId:      vw.orderId||vw.order_id||null,
            });
          });

          // Second: add Firebase cloud watchers not already from VPS
          cloudWatchers.forEach(cw=>{
            const id = cw.id||cw.watcher_id;
            if(id && !merged.has(id) && cw.status==="WATCHING") {
              merged.set(id, cw);
            }
          });

          // Third: keep local-only WATCHING watchers (just placed this session)
          prev.forEach(lw=>{
            const id = lw.id||lw.watcher_id;
            if(id && !merged.has(id) && lw.status==="WATCHING") {
              merged.set(id, lw);
            }
          });

          // Fourth: apply VPS-fired exits (TP/SL hit while offline)
          vpsCompleted.forEach(vc=>{
            const vcId = vc.watcher_id||vc.id;
            if(merged.has(vcId)){
              const hit = (vc.status||"").includes("TP") ? "TP" : "SL";
              merged.set(vcId, {
                ...merged.get(vcId),
                status:       "TRIGGERED",
                triggeredType: hit,
                triggeredAt:  vc.triggeredAt||new Date().toISOString(),
                exitPrice:    vc.exit_price||null,
                pnlEst:       vc.pnl_est||null,
                exitOrderId:  vc.exit_order_id||null,
                firedByVPS:   true,
              });
              showToast(
                `${hit==="TP"?"🟢 TP":"🔴 SL"} ${vc.symbol||""} @ ₹${vc.exit_price||"?"} [fired while offline]`,
                hit==="TP"?"ok":"error"
              );
            }
          });

          // Keep non-WATCHING watchers from prev (TRIGGERED, CANCELLED history)
          prev.forEach(pw=>{
            const id = pw.id||pw.watcher_id;
            if(id && !merged.has(id) && pw.status!=="WATCHING") {
              merged.set(id, pw);
            }
          });

          const result = [...merged.values()];
          console.log(`[WatcherSync] Merged: ${vpsWatchers.length} VPS + ${cloudWatchers.length} Cloud + ${localActive.length} Local → ${result.filter(w=>w.status==="WATCHING").length} active`);
          return result;
        });

        watcherSyncDone.current = true;
      } catch(e) {
        console.warn("[WatcherSync] Full sync failed:", e);
        watcherSyncDone.current = true;
      }
    })();
  }, [uid, authed]);

  // Bulk-save all trades to Firestore on any change
  useEffect(()=>{
    if(!uid || !authed || trades.length === 0) return;
    const timer = setTimeout(()=>{
      fbSaveAllTrades(uid, trades).catch(()=>{});
    }, 3000);
    return ()=>clearTimeout(timer);
  },[uid, authed, trades]);

  // Save new closed trades to Firestore
  const addTrade = useCallback((trade)=>{
    const enriched = {
      ...trade,
      id: trade.id || `trade_${Date.now()}`,
      exitTime: trade.exitTime || new Date().toISOString(),
      pnl: trade.action==="BUY"
        ? (trade.exitPrice - trade.entryPrice) * trade.quantity
        : (trade.entryPrice - trade.exitPrice) * trade.quantity,
      roi: trade.entryPrice > 0
        ? (((trade.exitPrice - trade.entryPrice) / trade.entryPrice) *
           (trade.action==="BUY" ? 1 : -1) * 100).toFixed(2)
        : 0,
    };
    setTrades(prev=>[enriched, ...prev.slice(0, 499)]);
    if(uid) fbSaveTrade(uid, enriched).catch(()=>{});
  }, [uid]);
  const [positions,setPositions]         = useState(null);
  const [holdings,setHoldings]           = useState(null);
  const [margins,setMargins]             = useState(null);
  const [indexQuotes,setIndexQuotes]     = useState({});
  const [posLoading,setPosLoading]       = useState(false);
  const [showSettings,setShowSettings]   = useState(false);
  // ── User broker selection (reactive) ─────────────────────────────────────
  const [userBroker, setUserBrokerState] = useLocalStorage('rabt_broker', 'zerodha');
  // Keep module-level var in sync for functions like openInBroker that run outside React
  useEffect(()=>{ _userBroker = userBroker; },[userBroker]);
  const selectBroker = (b) => { setUserBroker(b); setUserBrokerState(b); };

  // ── Kite access token for non-admin client-side order placement ──────────
  // Non-admin traders call https://api.kite.trade directly from the browser.
  // We cache the access_token (+ api_key) in sessionStorage so it survives re-renders.
  const [kiteClientKey,   setKiteClientKey]   = useState(()=>sessionStorage.getItem("kite_client_key")||"");
  const [kiteClientToken, setKiteClientToken] = useState(()=>sessionStorage.getItem("kite_client_token")||"");

  // Fetch access_token from VPS when non-admin trader becomes authed
  useEffect(()=>{
    if(!authed || isAdmin || userBroker !== "zerodha") return;
    if(kiteClientToken) return; // already have it
    apiFetch("/user/kite-access-token")
      .then(d=>{
        if(d.ok && d.access_token) {
          setKiteClientToken(d.access_token);
          setKiteClientKey(d.api_key);
          sessionStorage.setItem("kite_client_token", d.access_token);
          sessionStorage.setItem("kite_client_key", d.api_key);
        }
      }).catch(()=>{});
  },[authed, isAdmin, userBroker, kiteClientToken]);

  // ── Effective authed: true when user has an active broker API session ──
  // Zerodha: user must have their own Kite API key + whitelisted server IP
  // Kotak Neo: user must have completed Neo login
  // If authed=true, user can place orders directly via API
  const effectiveAuthed = authed;

  // ── Per-user Telegram config (uid-keyed so each user has own config) ────────
  const tgTokenKey = uid ? `nse_pro_tg_token_${uid}` : 'nse_pro_tg_token';
  const tgChatKey  = uid ? `nse_pro_tg_chat_${uid}`  : 'nse_pro_tg_chat';
  const [tgToken,setTgToken]             = useLocalStorage(tgTokenKey, '');
  // Clear AI auto-fired state on a new day so fresh signals can fire
  useEffect(()=>{
    const today = new Date().toISOString().slice(0,10);
    const stored = localStorage.getItem('nse_pro_ai_fired_date');
    if(stored !== today) {
      localStorage.setItem('nse_pro_ai_fired_date', today);
      // Clear all autoFired state — handled per-tab inside useAIPanel
    }
  },[]);

  const [vpsScanCfg, setVpsScanCfg]      = useLocalStorage('nse_pro_vps_scan', {
    kite_enabled: false, kite_watchlist: [], kite_timeframe: "15min",
    delta_enabled: false, delta_watchlist: ["BTCUSD","ETHUSD","SOLUSDT_PERP"],
    delta_max_trades: 3, delta_min_confidence: 80,
    delta_margin_pct: 10, delta_min_trade_usd: 10,
  });
  const [vpsScanStatus, setVpsScanStatus] = useState(null);
  const [tgChat,setTgChat]               = useLocalStorage(tgChatKey, '');
  const [pushEnabled,setPushEnabled]     = useLocalStorage('nse_pro_push_enabled', false);
  const [tgMinSignals, setTgMinSignals]  = useLocalStorage('nse_pro_tg_min_signals', 1);
  const [tgCooldownMins, setTgCooldownMins] = useLocalStorage('nse_pro_tg_cooldown', 60); // mins between same alert
  const alertFiredRef = useRef({}); // {`${symbol}_${type}` → timestamp}
  const [toast,setToast]                 = useState(null);
  const [orderTicket,setOrderTicket]     = useState(null); // {symbol,exchange,side,price,qty}
  const [optModal,   setOptModal]        = useState(null); // {symbol, ltp} for QuickOptionsModal
  const [watchers,setWatchers]           = useLocalStorage('nse_pro_watchers', []);
  // Mobile scan state — must be at top level (hooks rules: no conditional useState)
  const [mobileScanning, setMobileScanning] = useState(false);

  // Virtual trading — ref callback filled by VirtualTab, called by other tabs
  const addVirtualRef = useRef(null);
  const addVirtualTrade = useCallback((params)=>{
    if(addVirtualRef.current) return addVirtualRef.current(params);
  },[]);

  // ── VPS engine status — so desktop knows the server is auto-trading and does
  // NOT also fire its own auto-trade (would double the order). ─────────────────
  const [vpsStatus, setVpsStatus] = useState(null);
  useEffect(()=>{
    if(!authed) return;
    const load=()=>apiFetch("/vps-scanner/status").then(setVpsStatus).catch(()=>{});
    load(); const t=setInterval(load, 60000);
    return ()=>clearInterval(t);
  },[authed]);
  const vpsAutoTradeOn = !!(vpsStatus && vpsStatus.auto_trade);
  // Auto-cancel WATCHING watchers older than 1 trading day (positions closed overnight)
  // MIS positions auto-square off at 3:30 PM so any WATCHING watcher from yesterday is stale
  useEffect(()=>{
    const now       = new Date();
    const todayStr  = now.toISOString().slice(0,10); // YYYY-MM-DD
    setWatchers(prev=>prev.map(w=>{
      if(w.status!=="WATCHING") return w;
      const created = new Date(w.createdAt||0);
      const createdDate = created.toISOString().slice(0,10);
      // MIS: cancel if created on a different date (auto squared off)
      if(w.product==="MIS" && createdDate !== todayStr) {
        return {...w, status:"CANCELLED_POSITION_CLOSED",
                triggeredType:"AUTO_SQUAREOFF", triggeredAt:now.toISOString()};
      }
      // Any watcher older than 5 days — cancel
      const ageDays = (now - created) / 864e5;
      if(ageDays > 5) {
        return {...w, status:"CANCELLED_STALE", triggeredAt:now.toISOString()};
      }
      return w;
    }));
  },[]);
  // positionNotes: symbol -> {source, signal, confidence, summary, reasons, tab, firedAt}
  // Stores signal logic behind each auto-opened trade for display in Positions tab
  const [positionNotes, setPositionNotes] = useLocalStorage('nse_pro_pos_notes', {});
  // Auto-purge position notes older than 2 days (they're only relevant while trade is open)
  useEffect(()=>{
    const cutoff = Date.now() - 2*24*60*60*1000;
    setPositionNotes(prev=>{
      const filtered = Object.fromEntries(
        Object.entries(prev).filter(([,v])=>{
          const t = new Date(v.firedAt||0).getTime();
          return t > cutoff;
        })
      );
      return filtered;
    });
  },[]);
  const addPositionNote = useCallback((symbol, note)=>{
    setPositionNotes(prev=>({...prev,
      [symbol.toUpperCase()]: {...note, firedAt: new Date().toISOString()}
    }));
  },[]);

  // ── Continuous watcher sync: push changes to VPS + Firebase on every update ──
  // Only runs AFTER initial sync is done (watcherSyncDone flag prevents race)
  const prevWatchersRef = useRef(watchers);
  useEffect(()=>{
    if(!authed || !uid || !watcherSyncDone.current) return;
    // Skip if watchers haven't actually changed (prevents infinite loop)
    if(prevWatchersRef.current === watchers) return;
    prevWatchersRef.current = watchers;

    // Debounce: push changes to VPS + Firebase after 2s of stability
    const timer = setTimeout(()=>{
      const active = watchers.filter(w=>w.status==="WATCHING");
      // Push to VPS (non-blocking)
      apiFetch("/watcher/sync", {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({watchers: active.map(toServerFmt)})
      }).catch(()=>{});
      // Push to Firebase (for cross-device display)
      fbSaveWatchers(uid, watchers).catch(()=>{});
    }, 2000);
    return ()=>clearTimeout(timer);
  }, [authed, uid, watchers]);
  const [signalParams, setSignalParams]  = useLocalStorage('nse_pro_signal_params', DEFAULT_SIGNAL_PARAMS);

  // ── Auto-save all settings to Firestore whenever they change ──────────────
  // Only runs when user is fully authenticated (not during key setup)
  const saveSettingsTimeout = useRef(null);
  useEffect(()=>{
    if(!uid || !authed) return;  // wait for full auth — don't save during key setup
    clearTimeout(saveSettingsTimeout.current);
    saveSettingsTimeout.current = setTimeout(()=>{
      const eq_at   = (() => { try{ return JSON.parse(localStorage.getItem('nse_pro_eq_at_cfg')||'{}'); }catch{ return {}; } })();
      const fo_at   = (() => { try{ return JSON.parse(localStorage.getItem('nse_pro_fo_at_cfg')||'{}'); }catch{ return {}; } })();
      const mcx_at  = (() => { try{ return JSON.parse(localStorage.getItem('nse_pro_mcx_at_cfg')||'{}'); }catch{ return {}; } })();
      const mcx_m   = (() => { try{ return JSON.parse(localStorage.getItem('nse_pro_mcx_at_mode')||'"futures"'); }catch{ return 'futures'; } })();
      const dlt_at  = (() => { try{ return JSON.parse(localStorage.getItem('nse_pro_delta_at_cfg')||'{}'); }catch{ return {}; } })();
      const dlt_m   = (() => { try{ return JSON.parse(localStorage.getItem('nse_pro_delta_at_mode')||'"perp"'); }catch{ return 'perp'; } })();
      const ai_conf = (() => { try{ return JSON.parse(localStorage.getItem('nse_pro_ai_min_conf')||'75'); }catch{ return 75; } })();
      const ai_marg = (() => { try{ return JSON.parse(localStorage.getItem('nse_pro_ai_margin_pct')||'10'); }catch{ return 10; } })();
      const ai_day  = (() => { try{ return JSON.parse(localStorage.getItem('nse_pro_ai_max_daily')||'5'); }catch{ return 5; } })();
      const sp      = (() => { try{ return JSON.parse(localStorage.getItem('nse_pro_signal_params')||'{}'); }catch{ return {}; } })();
      // FIX: was reading the non-uid localStorage keys (always empty since TG
      // config is stored uid-keyed) — Firebase synced blank tg_token/tg_chat,
      // so mobile devices never received the Telegram config. Use state directly.
      const tg_tok  = tgToken || '';
      const tg_ch   = tgChat  || '';
      const vps     = (() => { try{ return JSON.parse(localStorage.getItem('nse_pro_vps_scan')||'{}'); }catch{ return {}; } })();
      fbSaveSettings(uid, {
        eq_at_cfg:    eq_at,
        fo_at_cfg:    fo_at,
        mcx_at_cfg:   mcx_at,
        mcx_at_mode:  mcx_m,
        delta_at_cfg: dlt_at,
        delta_at_mode:dlt_m,
        ai_min_conf:  ai_conf,
        ai_margin_pct:ai_marg,
        ai_max_daily: ai_day,
        signal_params:sp,
        tg_token:     tg_tok,
        tg_chat:      tg_ch,
        vps_scan:     vps,
      }).catch(()=>{});
    }, 3000);
    return ()=>clearTimeout(saveSettingsTimeout.current);
  },[uid, authed, signalParams, tgToken, tgChat, vpsScanCfg]);

  // ── Background engine state ──────────────────────────────────────────────
  // Equity (unified intraday + investment + F&O)
  const [equityEngineEnabled, setEquityEngineEnabled] = useState(false);
  const [equityEngineCfg,     setEquityEngineCfg]     = useState({});
  // MCX
  const [mcxEngineEnabled, setMcxEngineEnabled] = useState(false);
  const [mcxEngineCfg,     setMcxEngineCfg]     = useState({});
  const [mcxAtMode,        setMcxAtMode]         = useState("futures");
  const [mcxEngineFired,   setMcxEngineFired]   = useState({});
  const [mcxEngineLog,     setMcxEngineLog]     = useState([]);
  // Delta
  const [deltaEngineEnabled, setDeltaEngineEnabled] = useState(false);
  const [deltaEngineCfg,     setDeltaEngineCfg]     = useState({});
  const [deltaEngineFired,   setDeltaEngineFired]   = useState({});
  const [deltaEngineLog,     setDeltaEngineLog]     = useState([]);
  const [deltaAuthed,        setDeltaAuthed]         = useState(false);

  const showToast=(msg,type="ok")=>{setToast({msg,type});setTimeout(()=>setToast(null),3500);};
  const [showVpsScan, setShowVpsScan] = useState(false);
  const [aiApiStatus,  setAiApiStatus]  = useState(null);
  const [tradeCredits,  setTradeCredits]  = useState(null);
  const [showSubPage,   setNeedsSubPage]  = useState(false);

  // Fetch credits if user is on per-trade plan
  useEffect(()=>{
    if(!authed || !fbRecord) return;
    if(fbRecord.plan==="pertrade") {
      apiFetch('/credits/balance').then(d=>setTradeCredits(d.credits||0)).catch(()=>{});
    }
  },[authed, fbRecord]);

  // Check Gemini API status on mount and every 10 min
  useEffect(()=>{
    if(!authed) return;
    const check = ()=>apiFetch('/ai/status')
      .then(d=>setAiApiStatus(d.ok ? true : (d.action||d.error||false)))
      .catch(()=>setAiApiStatus('Connection error'));
    check();
    const t = setInterval(check, 600000); // every 10 min
    return ()=>clearInterval(t);
  },[authed]);

  // Poll VPS scanner status every 60s when authed
  useEffect(()=>{
    if(!authed) return;
    const poll = ()=>apiFetch("/vps-scanner/status")
      .then(s=>setVpsScanStatus(s)).catch(()=>{});
    poll();
    const t = setInterval(poll, 60000);
    return ()=>clearInterval(t);
  },[authed]);

  const openTrade = useCallback((ticket)=>setOrderTicket(ticket),[]);

  // Auto-TP watcher engine — runs in background every 5s
  // Telegram sender — stable ref-based, no circular dependency
  const tgChatRef  = useRef(tgChat);
  const tgTokenRef = useRef(tgToken);
  useEffect(()=>{ tgChatRef.current=tgChat; tgTokenRef.current=tgToken; },[tgChat,tgToken]);
  const sendTgAlert = useCallback(async(msg, extra={})=>{
    const chat = tgChatRef.current;
    const tok  = tgTokenRef.current;
    if(!chat) return;
    // Telegram is restricted: apiFetch drops this unless _tgAllow is set — only the
    // validate-scan flow ({validation:true}) and TP/SL exits ({allow:true}) pass it.
    try { await apiFetch("/alert/telegram",{method:"POST",headers:{"Content-Type":"application/json"},
      _tgAllow: !!(extra.validation || extra.allow),
      body:JSON.stringify({message:msg, bot_token:tok||"", chat_id:chat})}); }
    catch {}
  },[]);

  useAutoTPWatcher({watchers, setWatchers, showToast, tgToken, tgChat, onJournalClose:journalClose, sendTgAlert, isAdmin});

  // addAutoTP: adds watcher locally AND immediately syncs to VPS
  // This ensures TP/SL fires even if browser closes
  const addAutoTP = useCallback(async(w)=>{
    // Auto TP/SL watcher: admin only — trader users use GTT (broker-native, SEBI compliant)
    if(!isAdmin) {
      showToast("TP/SL watcher is admin-only. Use GTT in Kite for trader accounts.", "warn");
      return;
    }
    // 1. Add to local state immediately (UI shows it)
    setWatchers(prev=>[w,...prev]);
    // 2. Immediately register on VPS — don't wait for sync effect
    if(!w.tpEnabled && !w.slEnabled) return; // nothing to watch
    try {
      await apiFetch("/watcher/add", {
        method: "POST",
        headers: {"Content-Type":"application/json"},
        body: JSON.stringify({
          watcher_id:      w.id || `atp-${Date.now()}`,
          symbol:          w.symbol,
          exchange:        w.exchange || "NSE",
          side:            w.side,
          qty:             w.qty || 1,
          entry_price:     w.entryPrice || 0,
          product:         w.product || "MIS",
          tp_enabled:      w.tpEnabled || false,
          tp_price:        parseFloat(w.tpPrice || 0),
          sl_enabled:      w.slEnabled || false,
          sl_price:        parseFloat(w.slPrice || 0),
          trail_enabled:   w.trailEnabled || false,
          trail_pct:       parseFloat(w.trailPct || 1.5),
          high_water_mark: parseFloat(w.highWaterMark || w.entryPrice || 0),
          low_water_mark:  parseFloat(w.lowWaterMark || w.entryPrice || 0),
          tick_size:       parseFloat(w.tickSize || 0.05),
          // Zone-aware TP/SL — pass D/S zone boundaries to VPS
          demand_zone:     w.demandZone || null,
          supply_zone:     w.supplyZone || null,
          zone_touched:    w.zoneTouched || 'none',
        })
      });
    } catch(e) {
      console.warn("Watcher add to VPS failed:", e.message);
      // Still works locally — sync effect will retry
    }
  },[isAdmin, showToast]);

  // auth — 3s timeout so login screen shows immediately if backend is slow
  // Keep uid in module-level ref so apiFetch/deltaFetch can use it
  useEffect(()=>{ setCurrentUID(uid); },[uid]);

  // ── Listen for "Connect Kite" button in broker overlay ────────────────────
  useEffect(()=>{
    const h = () => setShowSettings(true);
    window.addEventListener("rabt-show-settings", h);
    return () => window.removeEventListener("rabt-show-settings", h);
  },[]);

  const checkAuth=useCallback(async()=>{
    if(!uid) { setAuthed(false); setAuthChecked(true); return; }
    try{
      const controller = new AbortController();
      const tid = setTimeout(()=>controller.abort(), 3000);
      const res = await fetch(`${API_BASE}/auth/status`, {
        signal: controller.signal,
        headers: await _authHeadersFor(uid)
      });
      clearTimeout(tid);
      const data = await res.json();
      setAuthed(data.authenticated);
      if(data.authenticated){
        setKiteUserName(data.user_name||"");
        setTokenSaved(data.token_saved||false);
      }
    }catch{ setAuthed(false); }
    finally{ setAuthChecked(true); }
  },[uid]);
  useEffect(()=>{ if(uid) checkAuth(); },[checkAuth, uid]);

  // Non-admin users (viewer / trader) don't own a Kite session — the backend
  // transparently falls back to the admin's Kite via get_kite_or_none().
  // Once Firebase record loads and we know the role, grant authed=true so all
  // scan guards, live-quote loops, and button disabled states work normally.
  useEffect(()=>{
    if(!authChecked || !fbRecord) return;
    const role = fbRecord.role || "viewer";
    if(role !== "admin") {
      setAuthed(true);
    }
  },[fbRecord, authChecked]);

  // ── Handle return from Kite OAuth (direct navigation flow) ────────────────
  useEffect(()=>{
    const returnUid = sessionStorage.getItem("kite_login_uid");
    if(!returnUid || !uid || returnUid !== uid) return;
    // Just returned from Kite — clear flags and re-check auth
    sessionStorage.removeItem("kite_login_uid");
    sessionStorage.removeItem("kite_login_return");
    // Poll for auth (Kite callback may take a moment to register)
    let count = 0;
    const poll = setInterval(async()=>{
      count++;
      try {
        const res = await fetch(`${API_BASE}/auth/status`,{headers: await _authHeadersFor(uid)});
        const data = await res.json();
        if(data.authenticated){
          clearInterval(poll);
          checkAuth();
          return;
        }
      } catch {}
      if(count > 10) { clearInterval(poll); checkAuth(); }
    }, 1000);
    return ()=>clearInterval(poll);
  },[uid]);

  const handleLogin=()=>{
    if(!uid) return;
    const loginUrl = `${API_BASE}/login?uid=${uid}`;
    const w = window.open(loginUrl, "_blank", "width=800,height=700,scrollbars=yes");
    if(!w || w.closed || typeof w.closed === 'undefined') {
      // Popup blocked — direct navigation fallback
      sessionStorage.setItem("kite_login_return", window.location.href);
      sessionStorage.setItem("kite_login_uid", uid);
      window.location.href = loginUrl;
      return;
    }
    let pollCount = 0;
    const poll = setInterval(async()=>{
      pollCount++;
      try {
        const res = await fetch(`${API_BASE}/auth/status`,{headers: await _authHeadersFor(uid)});
        const data = await res.json();
        if(data.authenticated){
          clearInterval(poll);
          checkAuth();
          // For non-admin traders: fetch fresh access_token after Kite login
          if(userRole !== "admin" && userBroker === "zerodha"){
            apiFetch("/user/kite-access-token").then(d=>{
              if(d.ok && d.access_token){
                setKiteClientToken(d.access_token);
                setKiteClientKey(d.api_key);
                sessionStorage.setItem("kite_client_token", d.access_token);
                sessionStorage.setItem("kite_client_key", d.api_key);
              }
            }).catch(()=>{});
          }
          return;
        }
      } catch {}
      if(pollCount > 90) clearInterval(poll);
    }, 2000);
  };

  // fetch portfolio data
  const fetchPortfolio=useCallback(async()=>{
    if(!authed)return;
    setPosLoading(true);
    try{
      const [pos,hold,mar]=await Promise.all([apiFetch("/portfolio/positions"),apiFetch("/portfolio/holdings"),apiFetch("/user/margins")]);
      setPositions(pos);setHoldings(hold);setMargins(mar);
      window._mcxMargins = mar; // used by McxTab for position sizing

      // ── Auto-close stale OPEN trades that no longer exist in Kite ──────────
      // Compares open trades in our ledger against actual Kite positions
      const openSymbols = new Set(
        (pos?.net||[])
          .filter(p=>parseInt(p.quantity||0)!==0)
          .map(p=>p.tradingsymbol.toUpperCase())
      );
      if(openSymbols.size > 0) {
        setTrades(prev=>prev.map(t=>{
          if(t.status!=="OPEN") return t;
          const sym = (t.instrument||t.symbol||t.strategy||"").toUpperCase()
                        .replace(/\d{2}[A-Z]{3}\d{2,4}.*/,"")  // strip expiry
                        .replace(/(CE|PE|FUT)$/,"");             // strip type
          // If no position matches this trade's base symbol → auto-close it
          const stillOpen = [...openSymbols].some(s=>s.startsWith(sym) || s===sym);
          if(!stillOpen) {
            return {...t,
              status:   "CLOSED",
              exitDate: new Date().toISOString().slice(0,10),
              exitPrice:t.ltp || t.entryPrice,
              exitReason:"AUTO_CLOSED_NO_POSITION",
            };
          }
          return t;
        }));
      }
    }catch(e){showToast(e.message,"error");}
    finally{setPosLoading(false);}
  },[authed]);

  useEffect(()=>{if(authed){fetchPortfolio();const t=setInterval(fetchPortfolio,60000);return()=>clearInterval(t);}},[authed,fetchPortfolio]);

  // index quotes — runs for ALL logged-in users (viewers use admin's Kite session via backend fallback)
  const fetchIndex=useCallback(async()=>{
    if(!uid && !authed)return;   // must be Firebase-logged-in OR Kite-authed
    try{
      const nseSyms = Object.values(INDEX_QUOTES_MAP).map(s=>`NSE:${s}`).join(",");
      const bseSyms = Object.values(BSE_INDEX_MAP).join(",");
      const allSyms = [nseSyms, bseSyms].join(",");
      const q = await apiFetch(`/quotes?symbols=${encodeURIComponent(allSyms)}`);
      setIndexQuotes(q);
    }catch{}
  },[uid, authed]);
  useEffect(()=>{
    if(uid||authed){
      fetchIndex();
      const t=setInterval(fetchIndex,30000);
      return()=>clearInterval(t);
    }
  },[uid, authed, fetchIndex]);

  // fire alerts
  // ── Shared Telegram sender — used by signals, AT, TP/SL ─────────────────────
  // Use refs so sendTgAlert never creates circular hook dependency

  const onFireAlerts=useCallback(async(newAlerts)=>{
    if(newAlerts.length===0) return;
    // Each user's tgChat stores their OWN Telegram chat ID.
    // Scan signal alerts fire for whoever has tgChat configured.
    // AT/TP-SL alerts (admin auto-trades) fire separately via the AT engine
    // and only reach admin's tgChat — not visible to other users.

    // ── Market hours gate — no alerts after market close ─────────────────────
    const _now2 = new Date();
    const _day  = _now2.getDay();
    const _mins = _now2.getHours()*60 + _now2.getMinutes();
    const _isWeekend = _day===0 || _day===6;
    const _nseOpen = !_isWeekend && _mins >= 555 && _mins <= 930;  // 9:15–15:30
    const _mcxOpen = !_isWeekend && _mins >= 540 && _mins <= 1410; // 9:00–23:30
    const _hasNSE  = newAlerts.some(a=>!a.exchange||a.exchange==='NSE'||a.exchange==='NFO');
    const _hasMCX  = newAlerts.some(a=>a.exchange==='MCX');
    if(_hasNSE && !_nseOpen && !_hasMCX) return;
    if(_hasMCX && !_mcxOpen && !_hasNSE) return;
    if(_hasNSE && !_nseOpen && _hasMCX && !_mcxOpen) return;
    // Filter out closed-market exchanges per alert
    const marketFiltered = newAlerts.filter(a=>{
      const ex = (a.exchange||'NSE').toUpperCase();
      if(ex==='MCX') return _mcxOpen;
      return _nseOpen; // NSE/NFO/default
    });
    if(marketFiltered.length===0) return;

    // ── Mixed-signal filter: if a symbol has BOTH BUY and SELL don't alert ───
    const _symDir = {};
    marketFiltered.forEach(a=>{
      if(!_symDir[a.symbol]) _symDir[a.symbol]={buy:0,sell:0};
      if(a.signal.type==='BUY')  _symDir[a.symbol].buy++;
      if(a.signal.type==='SELL') _symDir[a.symbol].sell++;
    });
    const cleanAlerts = marketFiltered.filter(a=>{
      const d = _symDir[a.symbol];
      return !(d.buy>0 && d.sell>0); // skip mixed signals for same symbol
    });
    if(cleanAlerts.length===0) return;

    const now = Date.now();
    // Minimum 5 min cooldown regardless of user setting to prevent spam
    const cooldownMs = Math.max(5, tgCooldownMins||60) * 60 * 1000;

    // Filter by min signals — group alerts by symbol and count
    const symGroups = {};
    cleanAlerts.forEach(a=>{
      if(!symGroups[a.symbol]) symGroups[a.symbol]={signals:[],price:a.price,zone:a.zone,timeframe:a.timeframe};
      symGroups[a.symbol].signals.push(a.signal);
      if(a.zone && !symGroups[a.symbol].zone) symGroups[a.symbol].zone = a.zone;
    });

    // Apply: min signals filter + cooldown dedup (per symbol+direction, not per scan type)
    const qualified = cleanAlerts.filter(a=>{
      // Must meet min signals threshold
      if((symGroups[a.symbol]?.signals?.length||0) < (tgMinSignals||1)) return false;
      // Dedup key: symbol + direction only (not per scan type — prevents same-symbol spam)
      const key = `${a.symbol}_${a.signal.type}_${a.timeframe||""}`;
      const lastFired = alertFiredRef.current[key] || 0;
      if(now - lastFired < cooldownMs) return false;
      return true;
    });

    if(qualified.length===0) return;

    // Mark as fired (dedup tracking — same key format as filter above)
    const seenSymbols = new Set();
    qualified.forEach(a=>{
      const key = `${a.symbol}_${a.signal.type}_${a.timeframe||""}`;
      if(!seenSymbols.has(key)){
        alertFiredRef.current[key] = now;
        seenSymbols.add(key);
      }
    });

    // Push notifications
    if(pushEnabled) qualified.forEach(a=>
      Notification.permission==="granted"&&new Notification(
        isAdmin
          ? `${a.signal.type==="BUY"?"▲":"▼"} ${a.symbol} · ${a.signal.label}`
          : `📊 ${a.symbol} · Technical Signal`,
        {body:`₹${a.price} · ${a.signal.label}`}
      )
    );

    // Telegram — group buys and sells with F&O option details
    if(tgChat) {
      const sentSymbols = new Set();
      const buys  = qualified.filter(a=>{ if(a.signal.type!=="BUY" ||sentSymbols.has(a.symbol)) return false; sentSymbols.add(a.symbol); return true; });
      const sells = qualified.filter(a=>{ if(a.signal.type!=="SELL"||sentSymbols.has(a.symbol)) return false; sentSymbols.add(a.symbol); return true; });

      // Fetch ATM option for each symbol in parallel — with liquidity filter
      const fetchATM = async(symbol, ltp, optType) => {
        try {
          const res = await apiFetch(`/fo/find_atm`, {
            method: 'POST',
            headers: {'Content-Type':'application/json'},
            body: JSON.stringify({
              symbol,
              exchange:    'NSE',
              option_type: optType,   // CE or PE
              signal_side: optType==='CE'?'BUY':'SELL',
              min_days:    3,
            })
          });
          if(!res || res.error || !res.found) return null;
          const c = res.contract;
          if(!c || !c.tradingsymbol) return null;
          const optLtp = parseFloat(c.option_ltp||0);
          // ── Liquidity filter: skip non-liquid / wide-spread options ──────────
          if(optLtp < 0.5) return null; // too cheap / non-liquid
          const bid = parseFloat(c.bid||0);
          const ask = parseFloat(c.ask||0);
          if(bid>0 && ask>0) {
            const spread = ask - bid;
            const spreadPct = spread / optLtp;
            if(spreadPct > 0.15) return null; // spread > 15% of premium — non-liquid
          }
          const oi = parseInt(c.oi||c.open_interest||0);
          if(oi > 0 && oi < 1000) return null; // very low OI — skip
          const tp     = optLtp>0 ? (optLtp*1.20).toFixed(1) : null;  // 20% TP
          const sl     = optLtp>0 ? (optLtp*0.80).toFixed(1) : null;  // 20% SL
          return { sym:c.tradingsymbol, ltp:optLtp, strike:c.strike, expiry:c.expiry||"", tp, sl };
        } catch { return null; }
      };

      // Build lines — admin gets full BUY/SELL + F&O details + zones
      // Non-admin gets signal analysis only (no direction advice — SEBI compliance)
      const buildLine = async(a, isBuy) => {
        const grp  = symGroups[a.symbol];
        const cnt  = grp?.signals?.length||1;
        const tf   = a.timeframe ? ` [${a.timeframe}]` : "";
        const ltp  = parseFloat(a.price||0);
        // Collect ALL signal labels for this symbol
        const allLabels = (grp?.signals||[a.signal]).map(s=>s.label).filter(Boolean);
        const signalList = allLabels.length>1
          ? allLabels.map((l,i)=>`   ${i+1}. ${l}`).join("\n")
          : `   ${allLabels[0]||a.signal.label}`;
        // S/D zone from scan result
        const zoneData = grp?.zone || a.zone;
        let zoneLine = "";
        if(zoneData) {
          const parts = [];
          if(zoneData.demand) parts.push(`Demand ₹${zoneData.demand.low}–₹${zoneData.demand.high}`);
          if(zoneData.supply) parts.push(`Supply ₹${zoneData.supply.low}–₹${zoneData.supply.high}`);
          if(parts.length) zoneLine = `\n   📐 ${parts.join(" · ")}`;
        }
        // Zone-based TP/SL: BUY → supply=TP, demand=SL; SELL → demand=TP, supply=SL
        let tpSlLine = "";
        if(zoneData && ltp > 0) {
          const isBuySignal = (a.signal?.type||"").toUpperCase()==="BUY" || isBuy;
          const tpZone = isBuySignal ? zoneData.supply : zoneData.demand;
          const slZone = isBuySignal ? zoneData.demand : zoneData.supply;
          const tp = tpZone ? (isBuySignal ? parseFloat(tpZone.high) : parseFloat(tpZone.low)) : null;
          const sl = slZone ? (isBuySignal ? parseFloat(slZone.low)  : parseFloat(slZone.high)) : null;
          if(tp||sl) tpSlLine = `\n   🎯 TP ₹${tp||"–"} · 🛑 SL ₹${sl||"–"}`;
        }

        if(!isAdmin) {
          return `📊 <b>${a.symbol}</b> @ ₹${ltp}${tf} · ${cnt} signal${cnt>1?"s":""}\n${signalList}${zoneLine}${tpSlLine}\n   <i>Technical signal detected — analyse before acting</i>`;
        }

        const emoji = isBuy ? "🟢" : "🔴";
        const dir   = isBuy ? "▲ BUY" : "▼ SELL";
        const optType = isBuy ? "CE" : "PE";
        const optLabel = isBuy ? "BUY CE" : "BUY PE";

        let base = `${emoji} <b>${dir} ${a.symbol}</b> @ ₹${ltp}${tf} · ${cnt} signal${cnt>1?"s":""}\n${signalList}${zoneLine}${tpSlLine}`;

        // Try to get ATM option (admin only)
        if(ltp > 0) {
          const atm = await fetchATM(a.symbol, ltp, optType);
          if(atm && atm.ltp > 0) {
            base += `\n\n   📋 <b>${optLabel}: ${atm.sym}</b>`;
            base += `\n   Strike ₹${atm.strike} · Option LTP ₹${atm.ltp}`;
            if(atm.tp) base += `\n   🎯 TP ₹${atm.tp} · 🛑 SL ₹${atm.sl}`;
          }
        }
        return base;
      };

      const lineResults = await Promise.all([
        ...buys.map(a=>buildLine(a, true)),
        ...sells.map(a=>buildLine(a, false)),
      ]);
      const lines = lineResults.filter(Boolean);
      if(lines.length===0) return;
      const time = new Date().toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"});
      const cdNote = ` · cooldown ${tgCooldownMins}m`;
      const msg = `<b>📡 RABTRADE Signal</b> · ${time}${cdNote}\n\n${lines.join("\n\n")}`;
      sendTgAlert(msg);
    }
  },[isAdmin,pushEnabled,tgToken,tgChat,tgMinSignals,tgCooldownMins,sendTgAlert]);

  // Background scan + AT engines — run regardless of active tab
  // Must be after onFireAlerts and addAutoTP are defined
  useBackgroundEngines({
    authed, deltaAuthed,
    signalParams,
    onAddAutoTP:  addAutoTP,
    onShowToast:  showToast,
    mcxEnabled:   mcxEngineEnabled,
    mcxCfg:       mcxEngineCfg,
    mcxAtMode,
    mcxFired:     mcxEngineFired,
    setMcxFired:  setMcxEngineFired,
    setMcxAtLog:  setMcxEngineLog,
    deltaEnabled: deltaEngineEnabled,
    deltaCfg:     deltaEngineCfg,
    deltaFired:   deltaEngineFired,
    setDeltaFired: setDeltaEngineFired,
    setDeltaLog:  setDeltaEngineLog,
  });

  // ── Firebase auth gates ────────────────────────────────────────────────────
  if(fbLoading) return (
    <div style={{fontFamily:"inherit",background:"#07101c",height:"100vh",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:12,color:"#e7edf5"}}>
      <div style={{fontSize:28,animation:"spin 1s linear infinite",display:"inline-block"}}>⟳</div>
      <div style={{color:"#8ea2bf",fontSize:12}}>Loading...</div>
      <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );

  // Not logged into Firebase
  if(!fbUser || !fbRecord) return <LoginScreen onLogin={handleFbLogin}/>;

  // ── T&C gate — show once on first login, skip for admin ───────────────────
  if(!isAdmin && !fbRecord.termsAccepted) return (
    <TermsPage
      fbUser={fbUser}
      fbRecord={fbRecord}
      onAccept={async()=>{
        await fbUpdateUser(fbUser.uid, {
          termsAccepted:    true,
          termsAcceptedAt:  new Date().toISOString(),
          termsVersion:     "1.0",
        });
        const rec = await fbGetUser(fbUser.uid);
        setFbRecord(rec);
      }}
      onLogout={handleFbLogout}
    />
  );
  if(showAdmin && fbRecord.role==="admin")
    return <AdminPanel firebaseUser={fbUser} userRecord={fbRecord}
      onExit={()=>setShowAdmin(false)}/>;

  // API keys not yet entered this session
  // Viewers don't need Kite API — they use admin's price feed (read-only)
  const skipApiKey = isViewer || userRole === "user";
  if(!keysReady && !skipApiKey) return (
    <ApiKeySetup
      firebaseUser={fbUser}
      userRecord={fbRecord}
      isAdmin={isAdmin}
      onComplete={()=>{
        if(!isAdmin) sessionStorage.setItem("broker_mode", getUserBroker());
        setKeysReady(true);
        checkAuth();
      }}
      onBrokerChange={b=>{ setUserBroker(b); setUserBrokerState(b); }}
      onLogout={handleFbLogout}
      onSkipAsViewer={()=>setKeysReady(true)}
    />
  );

  // ── Disclaimer popup for non-admin users ─────────────────────────────────
  if(!isAdmin && !disclaimerAccepted) return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:99999,
      display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'IBM Plex Sans',sans-serif"}}>
      <div style={{width:440,background:"#0b1624",border:"1px solid #e8b339",borderRadius:0,
        padding:"32px",boxShadow:"0 12px 48px rgba(0,0,0,0.8)"}}>

        {/* Header */}
        <div style={{textAlign:"center",marginBottom:20}}>
          <div style={{fontSize:32,marginBottom:8}}>⚠️</div>
          <div style={{fontSize:16,fontWeight:700,color:"#e8b339",letterSpacing:1}}>DISCLAIMER</div>
          <div style={{fontSize:10,color:"#5a728f",marginTop:4}}>Please read carefully before proceeding</div>
        </div>

        {/* Disclaimer body */}
        <div style={{background:"rgba(232,179,57,0.06)",border:"1px solid rgba(232,179,57,0.2)",
          borderRadius:0,padding:"18px",marginBottom:20}}>
          <div style={{fontSize:12,color:"#e7edf5",lineHeight:2.0}}>
            <strong style={{color:"#e8b339"}}>RAB Trade</strong> is a <strong>scan provider</strong> application.
          </div>
          <div style={{fontSize:12,color:"#e7edf5",lineHeight:2.0,marginTop:8}}>
            You can create your own custom scans. Based on your scan settings, the app will show results.
            The <strong>decision to trade is entirely yours</strong> and taken at <strong style={{color:"#ff5b6c"}}>your own risk</strong>.
          </div>
          <div style={{fontSize:12,color:"#e7edf5",lineHeight:2.0,marginTop:8}}>
            RAB Trade is <strong>not responsible</strong> for any profit or loss arising from trades
            placed based on scan results or signals shown in this app.
          </div>
          <div style={{fontSize:11,color:"#ff5b6c",lineHeight:2.0,marginTop:12,
            borderTop:"1px solid rgba(232,179,57,0.2)",paddingTop:12,fontWeight:600}}>
            RAB Trade = Scan Provider Only. Trade at your own risk.
          </div>
        </div>

        {/* Accept button */}
        <button onClick={()=>{
            sessionStorage.setItem("rabt_disclaimer_accepted","1");
            setDisclaimerAccepted(true);
          }}
          style={{width:"100%",background:"linear-gradient(135deg,#92400e,#e8b339)",
            border:"none",borderRadius:0,padding:"14px",
            color:"#07101c",fontFamily:"inherit",fontSize:13,fontWeight:700,
            cursor:"pointer",letterSpacing:1}}>
          I UNDERSTAND & ACCEPT
        </button>

        <div style={{textAlign:"center",marginTop:14}}>
          <button onClick={handleFbLogout}
            style={{background:"none",border:"none",color:"#5a728f",fontSize:10,
              cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>
            Logout
          </button>
        </div>
      </div>
    </div>
  );

  // Subscription gate — show payment page if expired or no subscription
  const needsSubscription = (fbRecord && (!fbRecord.expiresAt || isExpired(fbRecord))) || showSubPage;
  if(needsSubscription && !showAdmin) return (
    <SubscriptionPage
      fbUser={fbUser}
      fbRecord={fbRecord}
      onActivated={async()=>{
        const rec = await fbGetUser(fbUser.uid);
        setFbRecord(rec);
        setNeedsSubPage(false);
        // Refresh credits after payment
        if(rec?.plan==='pertrade')
          apiFetch('/credits/balance').then(d=>setTradeCredits(d.credits||0)).catch(()=>{});
      }}
      onLogout={handleFbLogout}
    />
  );

  // Kite not authenticated yet (keys entered but not Kite-logged-in)
  if(!authChecked) return (
    <div style={{fontFamily:"inherit",background:"#07101c",height:"100vh",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:12,color:"#e7edf5"}}>
      <div style={{fontSize:28,animation:"spin 1s linear infinite",display:"inline-block"}}>⟳</div>
      <div style={{color:"#8ea2bf",fontSize:12}}>Loading...</div>
      <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
  // Non-admin users (trader/viewer) don't need VPS Kite auth — they use deeplink mode
  // Only admin needs Kite connected for scan data + auto-trade
  if(!authed && isAdmin) return (
    <div style={{fontFamily:"'JetBrains Mono',monospace",background:"#07101c",height:"100vh",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{width:400,background:"#0b1624",border:"1px solid #1a2c44",borderRadius:12,padding:"32px 28px",textAlign:"center"}}>
        <div style={{fontSize:32,marginBottom:12}}>🔗</div>
        <div style={{fontSize:14,fontWeight:700,color:"#e7edf5",marginBottom:8}}>Connect Kite Account</div>
        <div style={{fontSize:11,color:"#8ea2bf",marginBottom:24,lineHeight:1.8}}>
          Your API keys are saved.<br/>Connect your Zerodha Kite account to start trading.
        </div>
        <button onClick={handleLogin}
          style={{background:"linear-gradient(135deg,#ff6b35,#f0c040)",border:"none",borderRadius:7,
            padding:"13px 32px",color:"#000",fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer"}}>
          🔗 LOGIN WITH KITE
        </button>
        <div style={{marginTop:16}}>
          <button onClick={handleFbLogout} style={{background:"none",border:"none",color:"#8ea2bf",
            fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>
            ← Logout / Change Account
          </button>
        </div>
      </div>
    </div>
  );

  // ── Mobile view (≤768px) — lightweight signal+position app ─────────────────
  // (mobileScanning state declared at top of App to avoid hooks-order violation)
  const showToastMobile = (msg, type="info")=>setToast({msg,type,ts:Date.now()});
  if(isMobileDevice && authed) {
    // FIX: useLocalStorage stores values JSON-encoded (quotes included). Raw
    // localStorage.getItem returned '"15min"' / '"MCX"' — the backend didn't
    // recognise the quoted timeframe and silently fell back to 1day (same
    // results on every TF), and market==="MCX" never matched (MCX scanned NSE).
    const _lsGet = (k, dflt)=>{
      try{
        const v = localStorage.getItem(k);
        if(v===null) return dflt;
        try{ return JSON.parse(v); }catch(e){ return v; }  // legacy raw values
      }catch(e){ return dflt; }
    };
    // Telegram alert for mobile scans — same channel as desktop alerts
    const tgNotifyMobile = (results, tfLabel, marketLabel)=>{
      try{
        const hits = (results||[])
          .filter(r=>!r.error && (r.signals||[]).length >= (tgMinSignals||1))
          .slice(0, 10);
        if(hits.length===0) return;
        const lines = hits.map(r=>{
          const sigs = r.signals||[];
          const buy  = sigs.some(s=>s.type==="BUY");
          const sell = sigs.some(s=>s.type==="SELL");
          const dir  = buy&&sell ? "⚠ MIXED" : buy ? "▲ BUY" : "▼ SELL";
          return `<b>${r.symbol}</b> ${dir} @ ₹${r.price||r.ltp||"?"}\n${sigs.map(s=>s.label).join(" · ")}`;
        });
        sendTgAlert(`<b>📱 Mobile Scan</b> · ${tfLabel} · ${marketLabel}\n${hits.length} signal${hits.length>1?"s":""}\n\n${lines.join("\n\n")}`);
      }catch(e){}
    };
    // Async scan job — same pattern as desktop. The old direct POST /scan hit
    // Cloudflare's 100s timeout on big watchlists; the gateway's timeout error
    // has no CORS headers, so the browser reported it as a CORS block.
    const runScanJob = async(payload)=>{
      const startR = await apiFetch("/scan/start",{method:"POST",
        headers:{"Content-Type":"application/json"}, body:JSON.stringify(payload)});
      const jobId = startR?.job_id;
      if(!jobId) throw new Error("Server did not return a job ID");
      const t0 = Date.now();
      while(Date.now()-t0 < 300000){
        await new Promise(r=>setTimeout(r,3000));
        const p = await apiFetch(`/scan/result/${jobId}`);
        if(p?.status==="done")  return p.result;
        if(p?.status==="error") throw new Error(p.error||"Scan failed on server");
      }
      throw new Error("Scan timed out (5 min)");
    };
    const handleQuickScan = ()=>{
      if(mobileScanning) return;

      const market = _lsGet("mob_market", "NSE");
      const tfSel  = _lsGet("eq_tf_unified", "15min");

      // ── MCX branch — scan commodity futures ──────────────────────────────
      if(market==="MCX") {
        const mcxSyms = MCX_SYMBOLS.slice(0, 50);
        setMobileScanning(true);
        runScanJob({
            symbols:      mcxSyms,
            exchange:     "MCX",
            timeframe:    tfSel,
            tech_weight:  0.7,
            news_enabled: false,
        }).then(r=>{
          if(r?.results) setScanResults(r.results);
          const sigCount = r?.results?.filter(x=>(x.signals||[]).length>0).length||0;
          tgNotifyMobile(r?.results, tfSel, "MCX");
          showToastMobile(sigCount>0
            ? `✓ ${sigCount} MCX signal${sigCount!==1?'s':''} on ${mcxSyms.length} commodities`
            : "MCX scan complete — no signals right now");
        }).catch(e=>showToastMobile("MCX scan failed: "+e.message,"error"))
        .finally(()=>setMobileScanning(false));
        return;
      }

      // Use the exact same localStorage keys as desktop EquityTab so
      // the mobile scan mirrors whatever the user configured on desktop.
      let selectedLists = ['NIFTY 50'];
      let customListsM = {};
      try{ const v=localStorage.getItem("eq_selected_lists"); if(v) selectedLists=JSON.parse(v)||['NIFTY 50']; }catch(e){}
      try{ const v=localStorage.getItem("eq_custom_lists");   if(v) customListsM =JSON.parse(v)||{};          }catch(e){}

      // Rebuild the same ALL_LISTS used by EquityTab (WATCHLISTS/FO_WATCHLISTS are module-level)
      const ALL_LISTS_M = {
        'NIFTY 50':    WATCHLISTS['NIFTY 50'],    'NIFTY BANK':  WATCHLISTS['NIFTY BANK'],
        'NIFTY IT':    WATCHLISTS['NIFTY IT'],     'NIFTY MIDCAP':WATCHLISTS['NIFTY MIDCAP'],
        'NIFTY PHARMA':WATCHLISTS['NIFTY PHARMA'],'NIFTY AUTO':  WATCHLISTS['NIFTY AUTO'],
        'NIFTY FMCG':  WATCHLISTS['NIFTY FMCG'],  'NIFTY METAL': WATCHLISTS['NIFTY METAL'],
        'NSE F&O ALL': WATCHLISTS['NSE F&O ALL'],
        'INDEX FUT':   ['NIFTY 50','BANKNIFTY','FINNIFTY','MIDCPNIFTY','SENSEX'],
        'NIFTY F&O':   FO_WATCHLISTS['NIFTY F&O'],'BANK F&O':    FO_WATCHLISTS['BANK F&O'],
        'FIN F&O':     FO_WATCHLISTS['FIN F&O'],  'MIDCAP F&O':  FO_WATCHLISTS['MIDCAP F&O'],
        'STOCK F&O':   FO_WATCHLISTS['STOCK F&O'],
      };
      const seen = new Set();
      // Cap raised 50 → 200: the async job pattern removed the timeout limit,
      // so mobile can scan full watchlists (F&O ALL etc.) same as desktop.
      const activeSyms = selectedLists
        .flatMap(n => customListsM[n] || ALL_LISTS_M[n] || [])
        .filter(s=>{ if(seen.has(s)) return false; seen.add(s); return true; })
        .slice(0, 200);

      if(activeSyms.length === 0){
        showToastMobile("No watchlist configured. Open desktop app to set up your watchlist.", "error");
        return;
      }

      // Use same timeframe the user set on desktop (default 15min)
      const tf = tfSel;   // already JSON-decoded above
      let signal_params = {};
      try{ const v=localStorage.getItem("nse_pro_signal_params"); if(v) signal_params=JSON.parse(v)||{}; }catch(e){}

      setMobileScanning(true);
      runScanJob({
          symbols:      activeSyms,
          exchange:     "NSE",
          timeframe:    tf,
          signal_params,
          tech_weight:  0.7,
          news_enabled: false,
      }).then(r=>{
        if(r?.results) setScanResults(r.results);
        const sigCount = r?.results?.filter(x=>(x.signals||[]).length>0).length||0;
        tgNotifyMobile(r?.results, tf, "NSE");
        showToastMobile(sigCount>0
          ? `✓ ${sigCount} signal${sigCount!==1?'s':''} found on ${activeSyms.length} symbols`
          : "Scan complete — no signals right now");
      }).catch(e=>showToastMobile("Scan failed: "+e.message,"error"))
      .finally(()=>setMobileScanning(false));
    };
    // Build price map from positions (they embed last_price)
    const mobilePriceMap = {};
    (positions?.net||[]).forEach(p=>{if(p.tradingsymbol) mobilePriceMap[p.tradingsymbol]=p.last_price||0;});
    return (
      <>
        <MobileApp
          uid={uid}
          userRole={userRole}
          fbRecord={fbRecord}
          authed={authed}
          onLogout={handleFbLogout}
          scanResults={scanResults}
          livePositions={positions?.net||[]}
          livePrices={mobilePriceMap}
          indexQuotes={indexQuotes}
          onQuickScan={handleQuickScan}
          scanning={mobileScanning}
          onTrade={(params)=>setOrderTicket(params)}
          showToast={showToastMobile}
          atEnabled={equityEngineEnabled}
          onToggleAT={()=>setEquityEngineEnabled(v=>!v)}
        />
        {/* Toast overlay for mobile */}
        {toast&&(
          <div style={{position:"fixed",top:16,left:"50%",transform:"translateX(-50%)",
            zIndex:9999,padding:"10px 20px",borderRadius:8,
            background:toast.type==="error"?"#f85149":"#238636",
            color:"#fff",fontSize:12,fontWeight:700,
            boxShadow:"0 4px 20px rgba(0,0,0,0.5)",
            maxWidth:"90vw",textAlign:"center",pointerEvents:"none"}}>
            {toast.msg}
          </div>
        )}
        {/* Order ticket modal — needed for MIS/F&O place order from signal cards */}
        {orderTicket&&<OrderTicket
          ticket={orderTicket}
          onClose={()=>setOrderTicket(null)}
          onSuccess={(msg)=>showToastMobile(msg)}
          onAddAutoTP={addAutoTP}
          authed={authed}
          userRole={userRole}
          userBroker={userBroker}
          onAddVirtual={addVirtualTrade}
          kiteClientKey={kiteClientKey}
          kiteClientToken={kiteClientToken}
        />}
      </>
    );
  }

  return (
    <div style={{fontFamily:"'JetBrains Mono','Fira Code',monospace",background:"#07101c",height:"100dvh",display:"flex",flexDirection:"column",color:"#5c3d2e",overflow:"hidden"}}>
      <link href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@300;400;600;700&display=swap" rel="stylesheet"/>
      <style>{`
        * { -webkit-tap-highlight-color: transparent; box-sizing: border-box; }
        input, select, textarea, button { touch-action: manipulation; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: #0d1117; }
        ::-webkit-scrollbar-thumb { background: #23405f; border-radius: 2px; }
        select option { background: #0d1117; color: #f0f6fc; }
        @media (max-width: 480px) {
          input, select, textarea { font-size: 16px !important; }
        }
      `}</style>

      {toast&&(
        <div style={{position:"fixed",top:12,left:"50%",transform:"translateX(-50%)",zIndex:400,padding:"8px 18px",borderRadius:8,background:toast.type==="error"?"#f85149":"#3fb950",color:"#fff",fontSize:12,fontWeight:700,boxShadow:"0 4px 20px rgba(0,0,0,0.4)"}}>
          {toast.msg}
        </div>
      )}

      {dupSession&&(
        <div style={{position:"fixed",top:0,left:0,right:0,zIndex:500,background:"rgba(248,130,0,0.97)",padding:"10px 16px",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <span style={{fontSize:14}}>⚠️</span>
          <span style={{flex:1,fontSize:11,fontWeight:700,color:"#000"}}>RAB Trade is open in another tab or device. Auto-trade engines may fire duplicate orders!</span>
          <button onClick={()=>setDupSession(false)} style={{background:"#000",border:"none",borderRadius:5,padding:"4px 12px",color:"#fff",fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer"}}>I understand, continue</button>
        </div>
      )}

      {orderTicket&&<OrderTicket ticket={orderTicket} onClose={()=>setOrderTicket(null)} onSuccess={(msg)=>showToast(msg)} onAddAutoTP={addAutoTP} authed={authed} userRole={userRole} userBroker={userBroker} onAddVirtual={addVirtualTrade} kiteClientKey={kiteClientKey} kiteClientToken={kiteClientToken}/>}
      {optModal&&<QuickOptionsModal symbol={optModal.symbol} ltp={optModal.ltp} onTrade={openTrade} onClose={()=>setOptModal(null)}/>}
      {showSettings&&<SettingsModal onClose={()=>setShowSettings(false)} tgToken={tgToken} setTgToken={setTgToken} tgChat={tgChat} setTgChat={setTgChat} pushEnabled={pushEnabled} setPushEnabled={setPushEnabled} uid={uid} userRole={userRole}/>}

      {/* Index Ticker Strip */}
      <div style={{background:"#090d13",borderBottom:"1px solid #161b22",padding:"4px 12px",display:"flex",gap:0,alignItems:"center",overflowX:"auto",flexShrink:0,scrollbarWidth:"none"}}>
        {[["NIFTY 50","NIFTY 50"],["BANKNIFTY","NIFTY BANK"],["FINNIFTY","NIFTY FIN SERVICE"],["MIDCAP","NIFTY MIDCAP SELECT"],["SENSEX","SENSEX"],["BANKEX","BANKEX"],["NIFTY IT","NIFTY IT"],["NIFTY AUTO","NIFTY AUTO"]].map(([label,key])=>{
          const q = indexQuotes[key] || indexQuotes[`BSE:${key}`];
          if(!q) return (<div key={label} style={{padding:"2px 12px",borderRight:"1px solid #161b22",minWidth:90,flexShrink:0}}><div style={{fontSize:8,color:"#484f58",letterSpacing:1}}>{label}</div><div style={{fontSize:10,color:"#484f58"}}>--</div></div>);
          const price = q.last_price || q.ltp || 0;
          const chg = q.net_change ?? q.change ?? 0;
          const pct = q.change_percent ?? q.pct ?? 0;
          const up = chg >= 0;
          return (<div key={label} style={{padding:"2px 12px",borderRight:"1px solid #161b22",minWidth:100,flexShrink:0}}><div style={{fontSize:8,color:"#8b949e",letterSpacing:1}}>{label}</div><div style={{fontSize:11,fontWeight:700,color:up?"#3fb950":"#f85149"}}>{price.toLocaleString("en-IN",{maximumFractionDigits:0})}</div><div style={{fontSize:8,color:up?"#3fb950":"#f85149"}}>{up?"+":""}{(chg||0).toFixed(0)} ({up?"+":""}{(pct||0).toFixed(2)}%)</div></div>);
        })}
        <div style={{marginLeft:"auto",padding:"2px 8px",fontSize:8,color:"#484f58",flexShrink:0}}>{new Date().toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}</div>
      </div>

      {/* Header */}
      <div style={{background:"#0d1117",borderBottom:"1px solid #21262d",padding:"8px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:30,height:30,borderRadius:7,background:"linear-gradient(135deg,#00d4aa,#0070f3)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14}}>📊</div>
          <div><div style={{fontSize:13,fontWeight:700,color:"#f0f6fc"}}>RAB TRADE</div><div style={{fontSize:9,color:"#8b949e",letterSpacing:2}}>KITE LIVE</div></div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {/* AI Status indicator */}
          <div title={aiApiStatus===true?"Gemini AI Connected":typeof aiApiStatus==="string"?aiApiStatus:"Checking AI..."}
            style={{width:8,height:8,borderRadius:"50%",flexShrink:0,
              background:aiApiStatus===true?"#3fb950":aiApiStatus===null?"#484f58":"#f85149",
              boxShadow:aiApiStatus===true?"0 0 6px #3fb950":aiApiStatus===null?"none":"0 0 6px #f85149",
              animation:aiApiStatus===null?"pulse 1.5s infinite":"none"}}/>
          <span style={{fontSize:9,color:aiApiStatus===true?"#3fb950":aiApiStatus===null?"#484f58":"#f85149",fontWeight:700,letterSpacing:0.5}}>
            {aiApiStatus===true?"AI":"AI ✗"}
          </span>
          <button onClick={()=>setShowSettings(true)} style={{background:"#161b22",border:"1px solid #30363d",borderRadius:6,padding:"5px 10px",color:(tgToken&&tgChat)||pushEnabled?"#00d4aa":"#8b949e",fontFamily:"inherit",fontSize:12,cursor:"pointer"}}>🔔</button>
          {kiteUserName&&<span style={{fontSize:10,color:"#8b949e",maxWidth:90,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={kiteUserName}>👤 {kiteUserName}</span>}
          {fbRecord&&<span style={{fontSize:9,color:"#8b949e",maxWidth:80,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{fbRecord.displayName||""}</span>}
          {daysRemaining(fbRecord)!==null&&<span style={{fontSize:9,padding:"2px 5px",borderRadius:4,background:daysRemaining(fbRecord)<7?"rgba(248,81,73,0.15)":daysRemaining(fbRecord)<30?"rgba(210,130,0,0.1)":"rgba(63,185,80,0.08)",color:daysRemaining(fbRecord)<7?"#f85149":daysRemaining(fbRecord)<30?"#d28200":"#3fb950"}}>{daysRemaining(fbRecord)}d</span>}
          {fbRecord?.role==="admin"&&(<button onClick={()=>setShowAdmin(true)} style={{background:"rgba(163,113,247,0.12)",border:"1px solid #a371f7",borderRadius:5,padding:"4px 8px",color:"#a371f7",fontFamily:"inherit",fontSize:9,cursor:"pointer",fontWeight:700}}>🛡 ADMIN</button>)}
          <button onClick={handleFbLogout} style={{background:"none",border:"1px solid #30363d",borderRadius:6,padding:"5px 8px",color:"#8b949e",fontFamily:"inherit",fontSize:10,cursor:"pointer"}} title="Logout">OUT</button>
        </div>
      </div>

      {/* Tab content */}
      <div style={{flex:1,overflow:"hidden",display:"flex",flexDirection:"column",position:"relative"}}>
        <div style={{flex:1,overflow:"auto",display:activeTab==="dashboard"?"flex":"none",flexDirection:"column"}}><DashboardTab positions={positions} holdings={holdings} margins={margins} scanResults={scanResults} alerts={alerts} trades={trades} indexQuotes={indexQuotes}/></div>
        <div style={{flex:1,overflow:"hidden",display:activeTab==="equity"?"flex":"none",flexDirection:"column"}}><EquityTab isAdmin={isAdmin} canTrade={canTrade} scanLimit={scanLimit} scansUsed={scansUsedToday} checkScanLimit={checkScanLimit} userBroker={userBroker} results={scanResults} setResults={setScanResults} alerts={alerts} setAlerts={setAlerts} seenKeys={seenKeys} setSeenKeys={setSeenKeys} activeScan={activeScan} setActiveScan={setActiveScan} indexQuotes={indexQuotes} authed={authed} onFireAlerts={onFireAlerts} tgToken={tgToken} tgChat={tgChat} pushEnabled={pushEnabled} onTrade={openTrade} onAddAutoTP={addAutoTP} onShowToast={showToast} signalParams={signalParams} setSignalParams={setSignalParams} bgEngineEnabled={equityEngineEnabled} setBgEngineEnabled={setEquityEngineEnabled} setBgEngineCfg={setEquityEngineCfg} margins={margins} setOptModal={setOptModal} uid={uid} onAddNote={(n)=>{setPositionNotes(p=>({...p,[n.symbol]:[...(p[n.symbol]||[]),n]}))}} onAddVirtual={addVirtualTrade} watchers={watchers} onJournalEntry={journalEntry} tgMinSignals={tgMinSignals} setTgMinSignals={setTgMinSignals} sendTgAlert={sendTgAlert} vpsAutoTradeOn={vpsAutoTradeOn}/></div>
        <div style={{flex:1,overflow:"hidden",display:activeTab==="mcx"?"flex":"none",flexDirection:"column"}}><McxTab isAdmin={isAdmin} canTrade={canTrade} scanLimit={scanLimit} scansUsed={scansUsedToday} checkScanLimit={checkScanLimit} onTrade={openTrade} onAddAutoTP={addAutoTP} onShowToast={showToast} authed={authed} signalParams={signalParams} setSignalParams={setSignalParams} bgEngineEnabled={mcxEngineEnabled} setBgEngineEnabled={setMcxEngineEnabled} setBgEngineCfg={setMcxEngineCfg} setBgAtMode={setMcxAtMode} bgLog={mcxEngineLog} setBgLog={setMcxEngineLog} onAddVirtual={addVirtualTrade} onAddNote={(n)=>{setPositionNotes(p=>({...p,[n.symbol]:[...(p[n.symbol]||[]),n]}))}} onJournalEntry={journalEntry} onFireAlerts={onFireAlerts} sendTgAlert={sendTgAlert} vpsAutoTradeOn={vpsAutoTradeOn}/></div>
        <div style={{flex:1,overflow:"auto",display:activeTab==="positions"?"flex":"none",flexDirection:"column"}}><PositionsTab positions={positions} holdings={holdings} loading={posLoading} onRefresh={fetchPortfolio} scanResults={scanResults} activeScan={activeScan} trades={trades} onSaveAsStrategy={(legs)=>{setTrades(prev=>[...legs,...prev]);showToast(`✓ Saved`)}} onTrade={openTrade} watchers={watchers} setWatchers={setWatchers} showToast={showToast} positionNotes={positionNotes} setPositionNotes={setPositionNotes} journal={journal} journalClose={journalClose} uid={uid} userRole={userRole} onJournalEntry={journalEntry} tgChat={tgChat}/></div>
        <div style={{flex:1,overflow:"hidden",display:activeTab==="customscan"?"flex":"none",flexDirection:"column"}}><CustomScanTab signalParams={signalParams} authed={authed} onShowToast={showToast} fbUser={fbUser} isAdmin={isAdmin} userRole={userRole} indexQuotes={indexQuotes} onTrade={openTrade} canTrade={canTrade} scanResults={scanResults} positions={positions} setSignalParams={setSignalParams} journal={journal} hasAIAccess={hasAIAccess} watchers={watchers} addVirtualRef={addVirtualRef} sendTgAlert={sendTgAlert}/></div>
        <div style={{flex:1,overflow:"hidden",display:activeTab==="delta"?"flex":"none",flexDirection:"column"}}><DeltaTab isAdmin={isAdmin} canTrade={canTrade} signalParams={signalParams} setSignalParams={setSignalParams} bgEngineEnabled={deltaEngineEnabled} setBgEngineEnabled={setDeltaEngineEnabled} setBgEngineCfg={setDeltaEngineCfg} bgLog={deltaEngineLog} setBgLog={setDeltaEngineLog} onSetDeltaAuthed={setDeltaAuthed} onShowToast={showToast} onAddNote={(n)=>{setPositionNotes(p=>({...p,[n.symbol]:[...(p[n.symbol]||[]),n]}))}} onJournalEntry={journalEntry} onFireAlerts={onFireAlerts} sendTgAlert={sendTgAlert} onAddVirtual={addVirtualTrade} vpsAutoTradeOn={vpsAutoTradeOn}/></div>
        <div style={{flex:1,overflow:"hidden",display:activeTab==="virtual"?"flex":"none",flexDirection:"column"}}><VirtualTab scanResults={scanResults} authed={authed} signalParams={signalParams} indexQuotes={indexQuotes} addVirtualRef={addVirtualRef} uid={uid}/></div>
        <div style={{flex:1,overflow:"auto",display:activeTab==="vps"?"block":"none"}}>
          <div style={{maxWidth:560,margin:"0 auto",padding:"14px"}}>
            <VpsEngineSettings authed={authed} showToast={showToast} />
          </div>
        </div>
        <div style={{flex:1,overflow:"auto",display:activeTab==="vpstrade"?"block":"none"}}>
          <div style={{maxWidth:760,margin:"0 auto",padding:"14px",display:"flex",flexDirection:"column",gap:12}}>
            {/* VpsPaperLog already renders VpsSummary + VpsAiReport at the top, so don't repeat them */}
            <VpsTpSlTracker authed={authed} />
            <VpsPaperLog authed={authed} />
          </div>
        </div>
      </div>

      {/* VPS engine status — shows on desktop so you don't double-trade */}
      {vpsStatus&&(vpsStatus.auto_trade||vpsStatus.kite_running||vpsStatus.mcx_running||vpsStatus.delta_running)&&(
        <div style={{background:vpsStatus.auto_trade?"rgba(248,81,73,0.12)":"rgba(73,194,228,0.1)",
          borderTop:`1px solid ${vpsStatus.auto_trade?"#ff5b6c":"#49c2e4"}`,padding:"3px 12px",
          display:"flex",gap:10,alignItems:"center",flexShrink:0,flexWrap:"wrap",fontSize:9,fontWeight:700}}>
          <span style={{color:"#49c2e4",letterSpacing:1}}>🖥️ VPS</span>
          {vpsStatus.kite_running&&<span style={{color:"#3fb950"}}>Scan NSE</span>}
          {vpsStatus.mcx_running&&<span style={{color:"#d28200"}}>Scan MCX</span>}
          {vpsStatus.delta_running&&<span style={{color:"#a371f7"}}>Scan Δ{vpsStatus.delta_mode==="paper"?" 📝":vpsStatus.delta_mode==="auto"?" 🤖":""}{vpsStatus.delta_paper_open>0?` ${vpsStatus.delta_paper_open}`:""}</span>}
          <span style={{color:vpsStatus.auto_trade?"#ff5b6c":"#8b949e"}}>
            🤖 Auto-Trade {vpsStatus.auto_trade?"ON":"OFF"}
          </span>
          {vpsStatus.auto_trade&&<span style={{color:"#ff8a95"}}>· desktop auto-trade disabled (no double orders)</span>}
        </div>
      )}

      {/* BG engine status */}
      {(equityEngineEnabled||mcxEngineEnabled||deltaEngineEnabled)&&(<div style={{background:"#0a0e17",borderTop:"1px solid #21262d",padding:"3px 12px",display:"flex",gap:8,alignItems:"center",flexShrink:0,flexWrap:"wrap"}}><span style={{fontSize:8,color:"#8b949e",fontWeight:700,letterSpacing:1}}>BG ENGINES:</span>
        {[[equityEngineEnabled,"⚡ Equity","#3fb950",()=>setEquityEngineEnabled(false)],[mcxEngineEnabled,"🏭 MCX","#d28200",()=>setMcxEngineEnabled(false)],[deltaEngineEnabled,"Δ Delta","#a371f7",()=>setDeltaEngineEnabled(false)]].map(([on,label,color,stop])=>on&&(<div key={label} style={{display:"flex",gap:4,alignItems:"center",background:`${color}18`,border:`1px solid ${color}44`,borderRadius:10,padding:"1px 8px"}}><span style={{fontSize:8,color,fontWeight:700}}>● {label}</span><button onClick={stop} style={{background:"none",border:"none",color,fontSize:9,cursor:"pointer",padding:"0 2px",lineHeight:1}}>✕</button></div>))}
      </div>)}

      {/* Bottom nav */}
      <div style={{background:"#0d1117",borderTop:"1px solid #21262d",display:"flex",justifyContent:"space-around",padding:"6px 0 env(safe-area-inset-bottom, 8px)",flexShrink:0,overflowX:"auto"}}>
        {TABS.map(t=>(<button key={t.id} onClick={()=>setActiveTab(t.id)} style={{background:activeTab===t.id?"rgba(0,212,170,0.08)":"none",border:"none",borderTop:activeTab===t.id?"2px solid #00d4aa":"2px solid transparent",display:"flex",flexDirection:"column",alignItems:"center",gap:2,padding:"6px 10px",cursor:"pointer",color:activeTab===t.id?"#00d4aa":"#8b949e",fontFamily:"inherit",flexShrink:0,minWidth:44,minHeight:44,WebkitTapHighlightColor:"transparent",transition:"color 0.15s"}}><span style={{fontSize:18}}>{t.icon}</span><span style={{fontSize:8,fontWeight:700,letterSpacing:0.5}}>{t.label}</span></button>))}
      </div>
      <div style={{background:"#0d1117",textAlign:"center",fontSize:8,color:"#3a4756",padding:"1px 0 env(safe-area-inset-bottom, 2px)",flexShrink:0}}>build {APP_BUILD}</div>
    </div>
  );
}
